import firewallVideo from '../assets/videos/firewallVideo.mp4';
import policyCreationVideo from '../assets/videos/policycreationAgent.mp4';
import policyhygieneVideo from '../assets/videos/policyhygiene.mp4';
import proImage from '../assets/PRO1.png';
import lifecycleImage from '../assets/LifeCycleManagement.png';
import policyCreation from '../assets/PolicyCreation.png';

export const agentDataMap = {
  'virtual-concierge-agent': {
    title: "Virtual Concierge Agent",
    description: "The Virtual Concierge Agent acts as the “front door” to IT and business services, providing immediate conversational assistance across the channels people use — video, text chat, mobile, or voice.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "30–40% reduction in incoming service desk ticket volume through automated intake and self-service resolution.",
          "50% reduction in mean time to resolution (MTTR) through intelligent routing and context preservation.",
          "20–30% cost savings by reducing reliance on Tier 1 staffing.",
          "15–20% increase in CSAT scores from faster, more consistent omnichannel support.",
          "Improved compliance and risk posture by ensuring all interactions are authenticated, logged, and auditable."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "25–40% of tickets are repetitive, Tier 1 requests that add no business value if handled manually.",
              "Resolution times average 4–8 hours, leading to lost productivity.",
              "Users face inconsistent service quality depending on channel and available staff.",
              "Traditional chatbots lack context memory, causing users to repeat information and eroding satisfaction.",
              "The average Tier 1 ticket costs $15–$25 to resolve, driving unsustainable costs at scale."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "30–40% reduction in incoming service desk ticket volume through automated intake and self-service resolution.",
              "50% reduction in mean time to resolution (MTTR) through intelligent routing and context preservation.",
              "20–30% cost savings by reducing reliance on Tier 1 staffing.",
              "15–20% increase in CSAT scores from faster, more consistent omnichannel support.",
              "Improved compliance and risk posture by ensuring all interactions are authenticated, logged, and auditable."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Patient portal resets, Epic/MyChart support, staff IT onboarding.",
              "Finance: Customer onboarding verification, secure transaction queries, policy guidance.",
              "Airline Ops: Passenger disruptions, crew scheduling, real-time staff support.",
              "ITSM: Service catalog requests, device troubleshooting, outage notifications.",
              "Cybersecurity: Suspicious activity reporting, phishing intake, automated incident logging."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Conversational triage and self-service across IT, HR, and business functions.",
              "Automated workflows: password reset, account unlock, device reconfiguration, knowledge delivery.",
              "Secure authentication and policy checks before execution.",
              "Proactive broadcast alerts for outages, policy changes, or compliance reminders.",
              "Sentiment analysis to adjust tone, escalation, and routing."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "Collaboration: Microsoft Teams, Slack, Zoom, WebEx",
              "Cloud: Azure, AWS, GCP",
              "Business Systems: Epic, Workday, SAP, Salesforce, Oracle",
              "Security: Okta, Ping, CrowdStrike, Microsoft Defender, Splunk"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Automated handoff when confidence drops below thresholds.",
              "Human-in-loop for sensitive workflows (HR, compliance, security incidents).",
              "Full context transfer ensures no repeated questioning."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "Sub-2 second response latency SLA.",
              "99.9% uptime availability.",
              "95% intent recognition accuracy in production workflows."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "Identity & Access Guardrails: MFA, RBAC, IAM baked in at intake.",
              "Data Handling: Encryption at rest and in transit, configurable data residency.",
              "Audit & Traceability: Immutable logs with correlation IDs for every interaction.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Flexible — per agent, per workflow, per interaction, or outcome-based."
          },
          {
            title: "ROI Proof Points",
            content: [
              "1,000 avoided Tier 1 tickets = $15,000–$25,000 savings.",
              "30–40% deflection = $2M+ in savings per year for a 10k+ FTE enterprise."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks with prebuilt workflows, 12–14 weeks with industry-specific customization."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Launch with Tier 1 workflows.",
              "60 days: Expanded workflows, CSAT baselining.",
              "90 days: Optimization and integration into governance reporting."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "New workflows, languages, and integrations added without re-engineering."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Orchestrates with other RunOps agents (Password Reset Agent, Ticket Dispatch Agent, Security Triage Agent)."
          },
          {
            title: "Vendor Roadmap",
            content: "Planned enhancements include proactive support (predictive resolution), advanced sentiment analysis, and domain-specific packs for healthcare and finance."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "User chat, voice, or video request, monitoring alert, API call.",
      workingPlane: "Execution environment (cloud, hybrid, AaaS) with failover and scale.",
      memory: "Persistent and session memory storing prior interactions, KB references, incident history.",
      reasoning: "ML-driven reasoning engine applying policies, sentiment, and business context.",
      execution: "Direct actions: password resets, ticket creation, knowledge delivery, escalations.",
    },
    kpis: []
  },
  'multimodal-help-widget-agent': {
    title: "Multimodal Help Widget Agent",
    description: "The Multimodal Help Widget Agent appears as an intelligent overlay inside the application, reads the user’s context, and proposes fixes or guided actions without requiring the user to leave the app.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "30–35% reduction in “how-to” and L1/L2 tickets.",
          "40–50% faster task completion in supported applications.",
          "20–25% improvement in application adoption and employee productivity.",
          "Direct uplift in CSAT as users receive support in the flow of work.",
          "10–20% reduction in compliance errors through policy enforcement at point-of-use."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Users lose time navigating away from the system of work to find help.",
              "Service desks waste resources on “how-to” tickets that could be resolved in-app.",
              "Knowledge articles are underutilized because they are disconnected from the user’s workflow.",
              "Current digital adoption tools provide static guidance but not adaptive, real-time remediation.",
              "Productivity losses: industry studies show 15–25% of employee time wasted searching for help or waiting for support resolution."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "30–35% reduction in “how-to” and L1/L2 tickets.",
              "40–50% faster task completion in supported applications.",
              "20–25% improvement in application adoption and employee productivity.",
              "Direct uplift in CSAT as users receive support in the flow of work.",
              "10–20% reduction in compliance errors through policy enforcement at point-of-use."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Clinicians in Epic facing workflow errors (e.g., order entry issues, login errors).",
              "Finance: Loan officers navigating CRM workflows with regulatory validation errors.",
              "Airline Ops: Gate agents using scheduling/boarding systems encountering task flow blockers.",
              "ITSM: Employees inside Jira or ServiceNow receiving in-context guidance before logging a ticket.",
              "Cybersecurity: Users encountering security popups (MFA errors, phishing alerts) with embedded guidance."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Detects user context: error messages, form states, navigation paths.",
              "Provides recommended fixes, auto-suggestions, or step-by-step guidance.",
              "Executes corrective actions via API (e.g., clearing cache, resetting app settings), or links to the exact knowledge article.",
              "Delivers context-specific knowledge articles or video snippets in-app.",
              "Multimodal: supports text, visual walkthroughs, and embedded video guidance."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Business Systems: Epic, Cerner, Workday, SAP, Salesforce, Oracle, Microsoft Dynamics",
              "ITSM: ServiceNow, Jira, BMC for incident creation and feedback loops",
              "Collaboration: Teams, Slack for push notifications and chat-based guidance",
              "Cloud/Security: Azure AD, Okta, CrowdStrike for authentication and remediation actions"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Lightweight overlay deployed inside target applications, delivered cloud-hosted, on-premises, hybrid, or as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Automated in-app fixes executed where safe.",
              "Escalates to ITSM ticketing with context captured when resolution fails.",
              "Provides full action log to human agent for seamless continuation."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Sub-1 second response latency for guidance display.",
              "99.9% widget availability.",
              "90–95% accuracy of contextual guidance matching real user needs."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Authenticated access only, RBAC aligned to application privileges.",
              "Data Handling: Only contextual metadata processed, no sensitive PII stored unless policy approved.",
              "Audit & Traceability: Line-item logging of all widget suggestions and actions taken.",
              "Certifications Supported: HIPAA, SOC2, ISO27001, PCI-DSS, HITRUST compliance-ready."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per user, per application, or outcome-based (per avoided ticket)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "30–35% reduction in “how-to” tickets = millions in annual support savings.",
              "Faster task completion translates directly into productivity gains (valued at $1,500–$2,000 per employee annually in large enterprises)."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "8–10 weeks for baseline rollout, 12–14 weeks for custom workflows per app."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Overlay live in one critical app.",
              "60 days: Expanded coverage across top 3 apps.",
              "90 days: Optimization with telemetry-driven refinements."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Add support for new applications and custom workflows over time."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Concierge Agent (front door intake) and Ticket Dispatch Agent (intelligent routing) for seamless workflow coverage."
          },
          {
            title: "Vendor Roadmap",
            content: "Planned features include predictive assistance (before an error occurs) and industry-specific playbooks (EHR, ERP, CRM)."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "In-app errors, repeated user actions, idle time, or explicit “Help” click.",
      workingPlane: "Lightweight overlay inside applications, orchestrated via cloud or hybrid control plane.",
      memory: "Persistent memory of user resolutions, knowledge articles served, fixes applied.",
      reasoning: "Rules + ML-based reasoning to propose fixes, escalate if policy dictates.",
      execution: "Executes approved remediation actions, presents guided walkthroughs.",
    }
  },
  'knowledge-mining-agent': {
    title: "Knowledge Mining Agent",
    description: "The Knowledge Mining Agent retrieves, consolidates, and summarizes unstructured content from knowledge bases, SOPs, vendor manuals, and forums into clear, actionable answers in seconds.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "40–50% reduction in “known issue” tickets by serving answers automatically.",
          "60% faster resolution times for Tier 1–2 agents by surfacing relevant, summarized knowledge.",
          "20–25% reduction in duplicate or redundant documentation efforts.",
          "CSAT improvement of 10–15% through faster, consistent responses.",
          "Reduced compliance risk by ensuring authoritative, policy-aligned answers are provided."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "60–70% of service desk tickets could be resolved through existing documentation, but users cannot locate or understand it fast enough.",
              "Current search tools are keyword-based, not context- or intent-driven, leading to low accuracy and wasted time.",
              "Knowledge bases are fragmented across ITSM platforms, SharePoint, Confluence, vendor portals, and user forums.",
              "Support staff spend 30–40% of time re-documenting or rediscovering answers already written elsewhere.",
              "In regulated industries, misinterpretation of vendor documentation leads to compliance risks and rework."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "40–50% reduction in “known issue” tickets by serving answers automatically.",
              "60% faster resolution times for Tier 1–2 agents by surfacing relevant, summarized knowledge.",
              "20–25% reduction in duplicate or redundant documentation efforts.",
              "CSAT improvement of 10–15% through faster, consistent responses.",
              "Reduced compliance risk by ensuring authoritative, policy-aligned answers are provided."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Mining Epic/Cerner KBs, clinical SOPs, vendor security advisories.",
              "Finance: Regulatory guidance, compliance SOPs, vendor audit responses.",
              "Airline Ops: Maintenance manuals, crew scheduling SOPs, operational bulletins.",
              "ITSM: IT knowledge bases, playbooks, troubleshooting wikis.",
              "Cybersecurity: Threat intelligence feeds, vendor vulnerability notes, forum discussions."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Retrieves knowledge across structured and unstructured repositories.",
              "Summarizes long documents into actionable steps.",
              "Validates knowledge against compliance or organizational policy.",
              "Provides direct links to full docs, KBs, or SOPs for transparency.",
              "Learns which answers work best for recurring issues."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM platforms: ServiceNow, Jira, BMC",
              "Knowledge stores: Confluence, SharePoint, Document360, vendor portals",
              "Vendor APIs: Microsoft, VMware, Epic, Cisco, AWS",
              "Forums and community platforms (with policy-based ingestion filters)"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Escalates to Tier 2–3 when knowledge confidence drops below defined thresholds.",
              "Provides full source traceability to ensure trust in responses.",
              "Human-in-loop option for sensitive or regulated queries."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Sub-3 second response latency for knowledge retrieval and summarization.",
              "Accuracy: >90% relevance score on recommended answers in production use.",
              "Coverage: % of known issues addressed via mined knowledge."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: RBAC applied to ensure access only to appropriate knowledge.",
              "Data Handling: Sensitive documents encrypted, with configurable data residency.",
              "Audit & Traceability: All retrieved documents and summarized answers logged with source references.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per agent, per query, per workflow, or outcome-based (per avoided ticket)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "40–50% reduction in redundant “known issue” tickets.",
              "60% faster resolution = millions in productivity savings for 10k+ FTE enterprises."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "8–10 weeks for integration with top knowledge sources, 12–14 weeks for full multi-source coverage."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot across 2–3 repositories.",
              "60 days: Expanded coverage, accuracy tuning.",
              "90 days: Optimization with telemetry-driven source refinement."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Add new knowledge sources or industries without redesign."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Concierge Agent (intake) and Help Widget Agent (in-app guidance) to provide end-to-end support."
          },
          {
            title: "Vendor Roadmap",
            content: "Enhancements include predictive knowledge surfacing (before issue occurs) and industry-specific curated packs (healthcare, finance, airlines)."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "User query, ticket creation, or monitoring alert requiring explanation.",
      workingPlane: "Orchestrates queries across multiple knowledge sources via cloud or hybrid control plane.",
      memory: "Persistent memory of successful answers per user/team/environment.",
      reasoning: "ML/NLP reasoning to interpret queries and select authoritative sources.",
      execution: "Returns summarized answers, provides direct links, or executes simple fixes.",
    }
  },
  'password-reset-and-account-unlock-agent': {
    title: "Password Reset and Account Unlock Agent",
    description: "The Password Reset and Account Unlock Agent automates this process end-to-end, delivering instant, secure access restoration.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "30–40% reduction in overall ticket volume through automation.",
          "Users regain access in seconds instead of hours (MTTR reduction >80%).",
          "20–30% Tier 1 cost savings by eliminating repetitive requests.",
          "CSAT uplift of 10–15% from immediate, consistent self-service.",
          "Reduced compliance risk via consistent policy enforcement."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "25–40% of all service desk calls are password/account related (HDI benchmark).",
              "Manual resets add hours of downtime and high operational cost.",
              "Human-driven resets introduce security risk (improper validation).",
              "Compliance frameworks demand stronger authentication guardrails.",
              "User productivity and satisfaction plummet when access issues block work."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "30–40% reduction in overall ticket volume through automation.",
              "Users regain access in seconds instead of hours (MTTR reduction >80%).",
              "20–30% Tier 1 cost savings by eliminating repetitive requests.",
              "CSAT uplift of 10–15% from immediate, consistent self-service.",
              "Reduced compliance risk via consistent policy enforcement."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Epic/EHR, Citrix, and AD access resets for clinicians and staff.",
              "Finance: SAP, O365, and compliance-critical apps with entitlement guardrails.",
              "Airline Ops: Rapid unlock of scheduling, crew, and gate access systems.",
              "ITSM: AD, Entra ID, O365 resets through self-service portals and bots.",
              "Cybersecurity: Enforcement of complex reset policies with audit trails."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Automated password reset and account unlock for AD, Entra ID (Azure AD), O365, SAP, Epic, Citrix, and other enterprise apps.",
              "MFA validation and policy-based guardrails.",
              "Enforces complexity, history, and compliance rules.",
              "Self-service access via chat, portal, or mobile app.",
              "Automatic ITSM ticket logging for audit."
            ]
          },
          {
            title: "Integrations",
            content: [
              "IAM: Azure AD, Entra ID, Okta, Ping, SailPoint",
              "Enterprise apps: O365, SAP, Epic/EHR, Citrix, Salesforce",
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "Collaboration: Teams, Slack (chat-driven resets)",
              "MFA/Identity: Duo, Microsoft Authenticator, Yubikey"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Human-in-loop option for high-risk resets.",
              "Auto-escalation if MFA validation fails or risk signals triggered.",
              "Context handover with full logs for Tier 2/3 review."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Password reset/account unlock completed within <30 seconds.",
              "Accuracy: >95% validation success rate without false unlocks.",
              "Coverage: % of systems and accounts automated."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: MFA, RBAC, device posture, and contextual validation enforced.",
              "Data Handling: Encrypted reset workflows; no plaintext credential exposure.",
              "Audit & Traceability: Every reset/unlock logged with user ID, time, and method.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per user, per reset, or outcome-based (% ticket deflection)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–40% fewer tickets reduces millions in Tier 1 costs.",
              "MTTR reduced from hours to seconds = major productivity savings."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for core IAM integration; 12–14 weeks for enterprise app coverage."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot with AD + O365 resets.",
              "60 days: Expand to SAP + Epic/EHR systems.",
              "90 days: Enterprise rollout with dashboards and ROI reporting."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Add new SaaS apps and IAM connectors as enterprise expands."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Concierge Agent (intake), Omnichannel Router (classification), and Triage & Enrichment Agent (context enrichment)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive account health — nudging users before lockouts occur."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "User request via chat/portal, lockout alert, monitoring event.",
      workingPlane: "Orchestrates IAM integrations, reset workflows, and ITSM logging.",
      memory: "Remembers prior lockouts, role patterns, and policies to optimize resets.",
      reasoning: "Validates identity through MFA, IAM policies, and contextual signals (device health, geolocation, entitlement).",
      execution: "Resets passwords, unlocks accounts, updates logs, and syncs across connected systems instantly.",
    }
  },
  'triage-and-enrichment-agent': {
    title: "Triange and enrichment Agent",
    description: "The Triage and Enrichment Agent automatically extracts entities, attaches device/app logs, and enriches tickets with contextual data so they are ready for action when they reach the queue.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–35% faster MTTR due to enriched tickets arriving “ready-to-work”.",
          "20–25% improvement in first-contact resolution (FCR).",
          "15–20% fewer escalations to Tier 2/3.",
          "Higher CSAT scores as users avoid repeated follow-ups.",
          "Improved compliance posture with complete context captured at intake."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "40–50% of tickets require back-and-forth clarification before diagnosis.",
              "Analysts waste hours pulling device logs or asking users for details.",
              "Lack of enrichment delays MTTR and reduces FCR rates.",
              "Poor triage leads to escalations that could have been resolved at Tier 1.",
              "Compliance/audit trails often miss device and user context without structured enrichment."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "25–35% faster MTTR due to enriched tickets arriving “ready-to-work”.",
              "20–25% improvement in first-contact resolution (FCR).",
              "15–20% fewer escalations to Tier 2/3.",
              "Higher CSAT scores as users avoid repeated follow-ups.",
              "Improved compliance posture with complete context captured at intake."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Enriches Epic/MyChart access tickets with workstation ID, session logs, and app version.",
              "Finance: Adds trading app logs, entitlement context, and compliance tags automatically.",
              "Airline Ops: Enriches scheduling or gate system tickets with device telemetry and location.",
              "ITSM: Applies enrichment across categories (access, hardware, software, incidents).",
              "Cybersecurity: Adds IOC details, endpoint telemetry, and network logs for security events."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "NLP entity extraction from tickets (user, app, error, CI).",
              "Automatic attachment of device logs, app telemetry, and network context.",
              "Adds entitlement data (IAM, role, location) for access-related tickets.",
              "Correlates tickets with known issues or KB articles.",
              "Populates structured fields in ITSM for governance and reporting."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "Endpoint/DEX: Intune, SCCM, JAMF, Nexthink, Aternity",
              "Monitoring: Splunk, Datadog, Elastic",
              "IAM: Azure AD, Okta, Ping, SailPoint",
              "Knowledge bases: Confluence, SharePoint for suggested KB enrichment"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-escalates tickets where enrichment fails or telemetry is missing.",
              "SME review option for compliance-sensitive enrichment.",
              "Provides audit-ready reports of what data was added and why."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Enrichment completed within <60 seconds of ticket creation.",
              "Accuracy: >90% correct entity extraction.",
              "Coverage: % of tickets enriched with device/app context."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Ensures enrichment only attaches data authorized for that user/ticket.",
              "Data Handling: All telemetry encrypted in transit/at rest; sensitive logs redacted as needed.",
              "Audit & Traceability: Line-item logging of enrichment steps and added context.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per ticket enriched, per enrichment pack, or outcome-based (% MTTR reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–35% MTTR reduction saves thousands of hours annually in large service desks.",
              "20–25% FCR improvement reduces costly escalations."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for pilot enrichment categories; 12–14 weeks for full rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot enrichment on top 2–3 categories (access, hardware, apps).",
              "60 days: Expand enrichment to all high-volume ticket types.",
              "90 days: Full enterprise enrichment dashboards with MTTR/FCR ROI tracking."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend to HR, Facilities, and Customer Support tickets beyond IT."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Intake Router Agent (classification), Smart Form Builder Agent (optimized input), and Ticket Dispatch Agent (routing)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive enrichment — suggesting fixes before ticket reaches analyst."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Ticket created via any channel, monitoring alert converted into ticket.",
      workingPlane: "Orchestrates entity extraction, telemetry pull, and enrichment injection.",
      memory: "Learns which enrichment data (device logs, error codes, entitlement context) historically sped resolution for specific ticket types.",
      reasoning: "Extracts entities from intake text, matches them to systems (user, device, app), and determines what additional telemetry is required.",
      execution: "Attaches relevant logs, device metadata, and environmental context to the ticket automatically.",
    }
  },
  'knowledge-hygiene-agent': {
    title: "Knowledge Hygiene Agent",
    description: "The Knowledge Hygiene Agent automatically audits knowledge sources, flags stale or conflicting content, and enforces taxonomy so that only authoritative, current, and properly categorized content is available for users.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–30% reduction in duplicate/low-quality KB articles.",
          "25–35% increase in self-service adoption by improving knowledge trustworthiness.",
          "15–20% reduction in ticket escalations caused by unclear or outdated documentation.",
          "Demonstrable compliance alignment by maintaining auditable, current SOPs and KBs.",
          "Staff efficiency: SME/manual audit time reduced by 40–50%."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "30–40% of enterprise KB articles are outdated or duplicative within 12 months of creation.",
              "Users lose trust in self-service portals when knowledge is inconsistent, causing increased service desk tickets.",
              "Manual knowledge audits are resource-intensive and often deprioritized by IT teams.",
              "In regulated industries, outdated SOPs pose compliance risks (HIPAA, PCI, SOX, etc.)",
              "Disorganized taxonomies make retrieval ineffective, even if content exists."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "20–30% reduction in duplicate/low-quality KB articles.",
              "25–35% increase in self-service adoption by improving knowledge trustworthiness.",
              "15–20% reduction in ticket escalations caused by unclear or outdated documentation.",
              "Demonstrable compliance alignment by maintaining auditable, current SOPs and KBs.",
              "Staff efficiency: SME/manual audit time reduced by 40–50%."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Ensures clinical SOPs and IT KBs are current with HIPAA, HITRUST requirements.",
              "Finance: Validates compliance-related KBs and flags stale audit procedures.",
              "Airline Ops: Keeps operational manuals and flight crew SOPs current and aligned.",
              "ITSM: Maintains accuracy in service desk KBs, playbooks, and troubleshooting workflows.",
              "Cybersecurity: Ensures vulnerability and patch knowledge stays aligned with latest advisories."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Detects duplicates using semantic similarity and metadata analysis.",
              "Flags articles past age/last-use thresholds as stale.",
              "Auto-enforces taxonomy standards (tags, categories, ownership fields).",
              "Generates review tasks for SMEs when remediation is required.",
              "Provides quality scoring dashboards for knowledge health."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC.",
              "Knowledge stores: Confluence, SharePoint, Document360, vendor portals.",
              "Content validation APIs: Microsoft Purview, AWS Macie (for sensitive data scans).",
              "Collaboration platforms: Teams, Slack for review prompts and approvals."
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-flags low-confidence or compliance-sensitive items for SME review.",
              "Escalation to governance board if critical SOPs or compliance documents fail validation.",
              "Generates full audit reports for quarterly/annual reviews."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Hygiene scans every 24 hours with <5% false positives.",
              "99.9% uptime for hygiene monitoring service.",
              "90% accuracy in duplicate and stale content identification."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: RBAC ensures only authorized SMEs can edit or delete flagged content.",
              "Data Handling: All scanning operations follow encryption and residency policies.",
              "Audit & Traceability: Every flagged or auto-archived article logged with justification and reviewer identity.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per knowledge repository, per user, or outcome-based (per X% hygiene improvement)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–30% reduction in duplicate KBs = thousands fewer unnecessary tickets.",
              "40–50% reduction in SME time spent on manual reviews.",
              "Demonstrable compliance ROI — avoiding fines or failed audits."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks to baseline scan and flagging; 12–14 weeks to optimize remediation workflows."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Baseline hygiene report delivered.",
              "60 days: Auto-flagging in place, SMEs resolving flagged issues.",
              "90 days: Automated taxonomy enforcement + compliance-ready audit logs."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend hygiene checks to chat transcripts, wikis, and user forums"
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Knowledge Mining Agent (retrieval) and Concierge Agent (intake) to ensure clean answers reach users"
          },
          {
            title: "Vendor Roadmap",
            content: "Planned features include AI-driven content rewriting suggestions and predictive decay modeling"
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Scheduled scans, knowledge updates, or user complaints.",
      workingPlane: "Orchestrates scans across multiple repositories and applies taxonomy rules.",
      memory: "Tracks history of article usage, duplication events, and SME actions.",
      reasoning: "Uses ML/NLP for duplicate detection, semantic drift, and taxonomy validation.",
      execution: "Flags or auto-archives content, enforces taxonomy, and generates review tickets.",
    }
  },
  'persona-and-role-context-agent': {
    title: "Persona and Role Context Agent",
    description: "The Persona and Role Context Agent dynamically tailors answers and actions based on user identity (role, location, device, and access rights), ensuring that a clinician in an ICU receives a different, relevant answer than a finance analyst in New York.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "30–40% faster time-to-resolution by eliminating irrelevant steps or options.",
          "20–25% reduction in service desk escalations caused by role misalignment.",
          "15–20% improvement in self-service adoption through personalized, context-aware answers.",
          "Reduced compliance risk by enforcing entitlement-based guidance automatically.",
          "Increased employee satisfaction from answers that “fit” their actual environment."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Generic KB articles and workflows waste time, confuse users, and risk compliance breaches.",
              "Static service desk or chatbot responses often include instructions users cannot execute (e.g., steps requiring elevated privileges).",
              "Location and device differences (BYOD vs corporate laptop, hospital bedside workstation vs admin desktop) drive variability that current systems fail to account for.",
              "In regulated industries, wrong guidance to the wrong persona can create compliance or safety risks.",
              "SMEs must manually create and maintain role-specific documentation, which does not scale."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "30–40% faster time-to-resolution by eliminating irrelevant steps or options.",
              "20–25% reduction in service desk escalations caused by role misalignment.",
              "15–20% improvement in self-service adoption through personalized, context-aware answers.",
              "Reduced compliance risk by enforcing entitlement-based guidance automatically.",
              "Increased employee satisfaction from answers that “fit” their actual environment."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Clinicians get bedside-device-specific instructions, while IT staff get elevated workflows.",
              "Finance: Regional compliance (EU GDPR vs US SOX) ensures role/location-specific guidance.",
              "Airline Ops: Gate staff vs crew vs maintenance roles receive distinct instructions for the same system.",
              "ITSM: Tailors service catalog answers by job role, reducing irrelevant catalog clutter.",
              "Cybersecurity: Role-aware security training and response (e.g., privileged users get stronger controls, standard users simpler guidance)."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Contextual tailoring of KBs, SOPs, and workflows by role, device, location, entitlement.",
              "Dynamic enforcement of “what the user can actually do” based on access rights.",
              "Integration of device telemetry (corporate laptop vs BYOD vs VDI) to guide next steps.",
              "Multi-channel support (chat, in-app, mobile, portal) with personalized responses."]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC.",
              "IAM/Entitlement: Azure AD, Okta, Ping, SailPoint.",
              "Device & Endpoint: Intune, VMware Workspace ONE, Citrix, JAMF.",
              "Business Apps: Epic, Workday, SAP, Salesforce.",
              "Collaboration: Teams, Slack, Zoom."
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Escalates only when user context cannot be mapped (new role, new device, missing entitlement).",
              "Full context handover to Tier 2–3 agents with persona/location/device info attached.",
              "Admin dashboards to adjust context mapping rules."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Sub-2 second response latency for tailored guidance.",
              "99.9% uptime for hygiene monitoring service.",
              "95% accuracy of persona detection in production environments"
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Uses RBAC, MFA, and entitlement data to tailor safe responses.",
              "Data Handling: Context-aware tailoring without exposing unnecessary entitlements.",
              "Audit & Traceability: Every tailored answer logged with role/location/device context.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per user, per tailored workflow, or outcome-based (per % reduction in escalations)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–25% reduction in escalations saves millions in analyst hours annually for large enterprises.",
              "30–40% faster resolution times yield productivity savings valued at $1,000+ per user per year.",
              "Reduced compliance violations saves significant regulatory fines and audit costs."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "8–10 weeks for role/location mapping; 12–14 weeks for device + entitlement tailoring."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Role/location tailoring baseline live.",
              "60 days: Device- and entitlement-aware tailoring added.",
              "90 days: Full optimization with adoption dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Add new persona models (contractor, partner, patient, etc.)"
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Concierge Agent (intake), Help Widget Agent (in-app), and Knowledge Mining Agent (retrieval) for fully personalized support."
          },
          {
            title: "Vendor Roadmap",
            content: "Planned enhancements include sentiment-aware personalization and predictive guidance (anticipating what role-specific help is needed before the request is made)"
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "User query, incident creation, or system telemetry event.",
      workingPlane: "Orchestrates entitlement-aware tailoring through IAM and ITSM integrations.",
      memory: "Remembers prior resolutions per role/location/device, adapting over time.",
      reasoning: "Applies role/location/device/entitlement logic via ML/NLP to tailor answers.",
      execution: "Executes tailored KBs, workflows, or automations based on user context.",
    }
  },
  'training-and-adoption-coach-agent': {
    title: "Training and Adoption Coach Agent",
    description: "The Training and Adoption Coach Agent delivers in-context, role-specific micro-learning nudges and guidance as employees interact with new systems, features, or upgrades, preventing helpdesk floods and ensuring proper adoption.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "30–40% higher adoption rates of new features or systems.",
          "20–25% reduction in post-rollout “how-to” tickets.",
          "25–30% faster time-to-proficiency for employees.",
          "10–15% improvement in CSAT scores during major IT rollouts.",
          "Reduced compliance and safety risks by reinforcing correct usage at the point of action."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "50–70% of new enterprise feature rollouts underperform due to low adoption rates.",
              "Service desks face ticket spikes after go-lives, often for “how-to” questions.",
              "Traditional training is front-loaded, not reinforced, and does not reach users at the moment of need.",
              "Poor adoption leads to wasted IT investment and frustrated employees.",
              "In regulated industries, misuse of new systems due to lack of training introduces compliance and safety risks."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "30–40% higher adoption rates of new features or systems.",
              "20–25% reduction in post-rollout “how-to” tickets.",
              "25–30% faster time-to-proficiency for employees.",
              "10–15% improvement in CSAT scores during major IT rollouts.",
              "Reduced compliance and safety risks by reinforcing correct usage at the point of action."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Training clinicians on new Epic modules, medical device apps, or compliance workflows.",
              "Finance: Adoption of new compliance features in ERP/CRM platforms.",
              "Airline Ops: Onboarding staff to new scheduling or gate management features without downtime.",
              "ITSM: Rolling out new service catalog or collaboration features across IT platforms.",
              "Cybersecurity: Reinforcing security practices during rollout of MFA, VPN, or Zero Trust tools."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Delivers micro-learning nudges in-app at the moment of need.",
              "Provides contextual guidance (text, video, tooltips, walkthroughs).",
              "Tracks adoption metrics (time-to-proficiency, feature usage, nudge effectiveness).",
              "Personalizes nudges by role, device, and region.",
              "Reinforces security/compliance by embedding policy reminders during use."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Business Apps: Epic, Workday, SAP, Salesforce, Oracle, Microsoft 365.",
              "ITSM: ServiceNow, Jira Service Management, BMC.",
              "DEX: Nexthink, Aternity for telemetry-driven adoption triggers.",
              "Collaboration: Teams, Slack, Zoom for reinforcement nudges."
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Nudges logged for adoption tracking.",
              "Escalates to training admins when users repeatedly struggle with a feature.",
              "Feedback loops allow SMEs to refine or replace nudge content."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Nudges delivered within 1 second of triggering event.",
              "Accuracy: >90% relevance of nudges based on role and feature usage.",
              "Adoption impact: % increase in feature usage and time-to-proficiency."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: RBAC ensures nudges align with user roles and entitlements.",
              "Data Handling: Usage telemetry anonymized or role-scoped for privacy.",
              "Audit & Traceability: Logs track which nudges were delivered and user interactions.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per user, per feature rollout, or outcome-based (per adoption rate achieved)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–25% reduction in post-rollout support costs.",
              "30–40% higher adoption translates to higher ROI on IT investments.",
              "Faster time-to-proficiency reduces lost productivity during transitions."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for pilot rollout, 12–14 weeks for enterprise adoption programs."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot nudges for one new feature rollout.",
              "60 days: Expand nudges across top 3 feature rollouts.",
              "90 days: Enterprise adoption dashboard with ROI tracking."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Add new applications, workflows, and training content packs"
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Proactive Nudge Agent (prevent degradation), Persona Agent (tailor training by role), and Auto Article Agent (create KBs from rollout incidents)"
          },
          {
            title: "Vendor Roadmap",
            content: "Planned predictive nudges (“this user group may need training before rollout”) and sentiment analysis to adjust coaching styles"
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "User interacting with a new feature, telemetry showing struggle, or change management flag.",
      workingPlane: "Orchestrates micro-learning nudges across ITSM, DEX, and business applications.",
      memory: "Learns which nudges worked per role/persona, adapting the approach over time.",
      reasoning: "Determines which nudge to send, when, and to whom based on user role and telemetry data.",
      execution: "Delivers nudges in-app, in chat, or via mobile push; launches training modules if needed.",
    }
  },
  'language-localizer-agent': {
    title: "Language Localizer Agent",
    description: "The Language Localizer Agent automatically translates and localizes service desk knowledge (KBs, SOPs, and chatbot flows) into target languages, ensuring global consistency and accuracy using enterprise-approved term glossaries.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "80–90% reduction in turnaround time for multilingual KB and chatbot publishing.",
          "30–40% increase in self-service adoption across non-English user populations.",
          "20–25% reduction in service desk tickets from non-English-speaking staff or customers.",
          "Improved compliance and trust by ensuring consistent, glossary-aligned translations.",
          "Enhanced employee and customer satisfaction globally through language inclusivity."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprises often support 20+ geographies, but service documentation exists only in English.",
              "Manual translation is slow, error-prone, and not scalable.",
              "Literal translations confuse users when technical or industry-specific terms are mistranslated.",
              "Poor localization increases ticket volumes and frustrates global employees and customers.",
              "Compliance and brand voice are jeopardized by inconsistent translations."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "80–90% reduction in turnaround time for multilingual KB and chatbot publishing.",
              "30–40% increase in self-service adoption across non-English user populations.",
              "20–25% reduction in service desk tickets from non-English-speaking staff or customers.",
              "Improved compliance and trust by ensuring consistent, glossary-aligned translations.",
              "Enhanced employee and customer satisfaction globally through language inclusivity."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Localizes Epic patient portal KBs and staff SOPs into Spanish, French, or regional languages.",
              "Finance: Translates compliance instructions while maintaining regulatory terminology (e.g., “SOX control,” “KYC”).",
              "Airline Ops: Localizes gate staff or crew SOPs for global operations in multiple languages.",
              "ITSM: Enables multi-language KBs and chatbot flows in ServiceNow, Jira, or BMC.",
              "Cybersecurity: Ensures security alerts and KBs are accurately translated for global SOC staff."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Translates KB articles, SOPs, chatbot/virtual agent flows, and UI text.",
              "Applies enterprise glossaries and term banks for accuracy.",
              "Supports role-/persona-based tone adjustments (formal for auditors, plain-language for end users).",
              "Delivers real-time translations for chatbot conversations.",
              "Provides batch translation workflows for KB and SOP libraries."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC.",
              "Knowledge stores: Confluence, SharePoint, Document360.",
              "Chatbot frameworks: Microsoft Bot Framework, ServiceNow Virtual Agent, RunOps Concierge Agent.",
              "Translation glossaries: Client-specific term banks, industry packs (healthcare, finance, airline ops).",
              "Collaboration: Teams, Slack for real-time localized messages"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Automated translation with glossary enforcement for most content.",
              "SME or human-in-loop review for compliance-critical documents.",
              "Feedback loop captures user corrections to improve future translations."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Real-time translation (<1s for chatbot interactions, <5m for KB batch jobs).",
              "Accuracy: >95% glossary adherence, >90% user satisfaction with localized outputs.",
              "Coverage: % of KBs and bot flows available in all supported languages."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: RBAC ensures only approved staff can modify glossaries or publish translations.",
              "Data Handling: Translations encrypted in transit and at rest; sensitive text redacted as required.",
              "Audit & Traceability: Logs show original text, translated text, glossary terms applied, and reviewers.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR compliance for multilingual data."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per translated document, per chatbot flow, or outcome-based (% coverage achieved)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "40–60% translation cost reduction vs manual services.",
              "20–25% lower ticket volumes from non-English users.",
              "Faster global adoption of IT and business applications."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "8–10 weeks for baseline language packs; 12–14 weeks for full enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot translations for chatbot and top 10 KBs.",
              "60 days: Expansion across KB/SOP library with glossary enforcement.",
              "90 days: Enterprise-wide multi-language rollout with ROI dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Add new languages and industry-specific term packs on demand."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Knowledge Hygiene Agent (taxonomy enforcement), Concierge Agent (intake), and Training & Adoption Agent (localized nudges)"
          },
          {
            title: "Vendor Roadmap",
            content: "Planned features include predictive translation (translating upcoming release notes before go-live) and sentiment-localized tone (adjusting style per cultural expectations)."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "New KB, SOP, or chatbot flow published; or user initiates conversation in a non-default language.",
      workingPlane: "Orchestrates translation across ITSM, KBs, chatbots, and collaboration platforms.",
      memory: "Stores glossary terms, previous corrections, and role-specific translation styles.",
      reasoning: "Chooses translation method (auto, glossary-aligned, or SME-reviewed) based on document type.",
      execution: "Translates, localizes, and publishes KBs/SOPs, or delivers real-time localized responses.",
    }
  },
  'auto-article-generation-agent': {
    title: "Auto Article Generation Agent ",
    description: "The Auto Article Generation Agent automatically drafts knowledge base (KB) articles immediately after an incident closes by capturing resolution steps, supporting evidence like logs, and system outputs. This crucial agent closes the ,leading to a significant reduction in repeat tickets and freeing up Subject Matter Experts (SMEs).",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "50–60% increase in KB article coverage after incidents.",
          "30–40% reduction in repeat incidents/tickets for known issues.",
          "25–35% faster incident resolution for new analysts via up-to-date KBs.",
          "Improved compliance with ITIL/KCS standards and audit readiness.",
          "20–25% SME efficiency gain by eliminating manual KB drafting overhead."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "60–70% of incidents never make it into knowledge bases, creating repeat tickets for the same issues.",
              "SMEs waste time answering repetitive incidents that could have been avoided with documented fixes.",
              "Manual KB creation is seen as “extra work,” leading to low compliance in ITIL/KCS programs",
              "In regulated industries, missing post-incident documentation increases audit and compliance risk.",
              "Lack of proof (logs, screenshots, commands executed) reduces trust in KB articles when they are written."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "50–60% increase in KB article coverage after incidents.",
              "30–40% reduction in repeat incidents/tickets for known issues.",
              "25–35% faster incident resolution for new analysts via up-to-date KBs.",
              "Improved compliance with ITIL/KCS standards and audit readiness.",
              "20–25% SME efficiency gain by eliminating manual KB drafting overhead."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Auto-generates KBs for recurring Epic/MyChart login, printing, and workflow issues.",
              "Finance: Documents fixes for recurring compliance or reporting errors in ERP/CRM systems.",
              "Airline Ops: Creates articles for gate agent system errors, crew scheduling glitches, or operational system fixes.",
              "ITSM: Auto-generates KBs in ServiceNow or Jira for repeatable Tier 1–2 issues.",
              "Cybersecurity:  Captures incident remediation steps (IOC blocks, patch actions) into auditable KB entries."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Captures incident closure notes, logs, and system actions automatically.",
              "Drafts KB in standard structure: Problem, Root Cause, Resolution Steps, Proof/Artifacts.",
              "Enforces taxonomy tagging (service, CI, category, urgency).",
              "Suggests related articles or merges with existing KBs to avoid duplicates.",
              "Pushes draft KBs into review workflows for SMEs."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC,Remedy",
              "Knowledge stores: Confluence, SharePoint, Document360",
              "System logs: Splunk, Elastic, Datadog",
              "Collaboration: Teams, Slack for SME review/approval workflows"

            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Drafts always routed for SME validation before publishing.",
              "Low-risk articles can auto-publish based on confidence thresholds.",
              "Full traceability of what data was used to generate the KB."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Draft KB available within 5 minutes of incident closure.",
              "Accuracy: 90–95% article draft accuracy based on SME acceptance rates.",
              "Coverage:  % of incidents resulting in KBs."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: RBAC to ensure KB drafts are visible only to authorized reviewers until approved.",
              "Data Handling: Sensitive data redacted automatically before article publication.",
              "Audit & Traceability: Every KB draft linked to incident record, showing evidence and contributors.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per generated KB, per workflow, or outcome-based (per % reduction in repeat tickets)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "30–40% reduction in repeat tickets saves millions in support costs for 10k+ FTE enterprises.",
              "Eliminates manual drafting overhead (average 30–60 minutes per article saved).",
              "Improves audit posture by ensuring KBs exist for all critical incidents."
            ]
          },

        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for initial rollout, 12–14 weeks for enterprise-wide KB coverage."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot auto-generated KBs for top incident categories.",
              "60 days: Expand to Tier 1–2 issues, SME workflow tuning.",
              "90 days: Optimization with telemetry and adoption reporting."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend from incidents to change requests and problem records."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Knowledge Hygiene Agent (cleaning) and Knowledge Mining Agent (retrieval) to form a full KM ecosystem."
          },
          {
            title: "Vendor Roadmap",
            content: "Planned features include predictive KB generation (as incident is in progress) and AI-driven remediation suggestions."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Incident closure, recurrence detection, or SME flag for KB creation.",
      workingPlane: "Orchestrates capture of closure notes, logs, and actions into KB draft.",
      memory: "Remembers prior KBs, merges duplicates, adapts templates based on SME preferences.",
      reasoning: "Determines if incident is KB-worthy, selects correct taxonomy and structure.",
      execution: "Drafts KB in ITSM/KM platform, attaches logs, screenshots, and steps taken.",
    },
  },
  'proactive-nudge-agent': {
    title: " Proactive Nudge Agent",
    description: "The Proactive Nudge Agent utilizes telemetry from Digital Experience (DEX) tools (CPU, network, application errors) to detect early signs of system degradation. It proactively warns users and guides them through self-service fixes before minor issues escalate into official incidents, thereby preventing service disruptions.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "30–40% reduction in incident volume from proactive issue prevention.",
          "25–35% improvement in mean time to prevent (MTTP) — turning hours of downtime into zero downtime events.",
          "15–20% improvement in CSAT by reducing “invisible frustration” moments.",
          "20–25% gain in IT efficiency as staff focus on complex issues instead of repetitive preventable incidents.",
          "Direct cost savings by eliminating outages, lost productivity, and service desk escalations."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "60–70% of IT tickets are preventable with early warning (low disk space, poor Wi-Fi, outdated drivers).",
              "DEX platforms collect data but require human analysis, leading to delays.",
              "IT teams often miss subtle degradation patterns across apps, networks, or devices until they escalate into outages.",
              "Users are frustrated by interruptions that “IT should have seen coming”.",
              "Current service models are reactive, with MTTR measured in hours instead of preventing the incident."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "30–40% reduction in incident volume from proactive issue prevention.",
              "25–35% improvement in mean time to prevent (MTTP) — turning hours of downtime into zero downtime events.",
              "15–20% improvement in CSAT by reducing “invisible frustration” moments.",
              "20–25% gain in IT efficiency as staff focus on complex issues instead of repetitive preventable incidents.",
              "Direct cost savings by eliminating outages, lost productivity, and service desk escalations."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Alert clinicians of workstation degradation before EHR slowness during peak hours.",
              "Finance: Detect early signs of trading app lag and guide analysts before system impact.",
              "Airline Ops: Nudge gate staff about device or network instability before boarding disruptions.",
              "ITSM: Proactive patch nudges, VPN issues, battery replacements, storage cleanups.",
              "Cybersecurity:  Nudge users about risky behavior (phishing-like emails, unsafe Wi-Fi) before a compromise occurs."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Analyzes DEX telemetry (CPU, memory, disk, app errors, latency, Wi-Fi quality).",
              "Identifies degradation patterns before user complaints occur.",
              "Issues proactive nudges: in-app popups, email, chat, or mobile notifications.",
              "Executes safe self-healing workflows (cache clear, restart, driver update).",
              "Pushes draft KBs into review workflows for SMEs."
            ]
          },
          {
            title: "Integrations",
            content: [
              "DEX platforms: Nexthink, Lakeside SysTrack, Microsoft Endpoint Analytics",
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "Endpoint & Device: Intune, Workspace ONE, JAMF, SCCM",
              "Collaboration: Teams, Slack for nudge delivery",
              "Monitoring: Splunk, Datadog, Elastic for telemetry correlation"


            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Nudges logged and tracked for auditability.",
              "Automatic escalation to ITSM if a nudge is ignored or unsuccessful.",
              "Human-in-loop override for sensitive or business-critical fixes."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Nudges issued within 30 seconds of detecting degradation.",
              "Accuracy: >90% correct identification of true degradations (low false positive rate).",
              "Impact: % reduction in preventable incidents, MTTP improvement."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: User authentication before executing any fix.",
              "Data Handling: Telemetry anonymized or filtered per compliance policy.",
              "Audit & Traceability: Every nudge and fix logged with context and justification.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per device, per user, or outcome-based (per avoided ticket/incident)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "30–40% fewer tickets directly equates to $1–2M savings annually for a 10k+ FTE enterprise.",
              "Avoided downtime: each hour of business outage avoided saves $300k–$500k depending on industry.",
              "20–25% IT staff efficiency gain from fewer reactive escalations."
            ]
          },

        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "8–10 weeks for baseline telemetry ingestion, 12–14 weeks for proactive nudges across top workflows."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot nudges for 2–3 telemetry patterns (low disk, VPN errors, Wi-Fi instability).",
              "60 days: Expansion to top 10 degradation patterns.",
              "90 days: Full proactive prevention program with ROI dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend nudges to SaaS apps, VDI, and industry-specific workflows."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Concierge Agent (intake), Help Widget Agent (in-app fixes), and Knowledge Mining Agent (supporting KBs)."
          },
          {
            title: "Vendor Roadmap",
            content: "Planned predictive ML models that forecast issues before telemetry thresholds are breached."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "DEX telemetry crossing thresholds, ML pattern match, or anomaly detection. ",
      workingPlane: "Orchestrates nudges and self-healing workflows across ITSM, DEX, and endpoint systems. ",
      memory: "Learns from past nudges: which resolved, which were ignored, which caused false positives. ",
      reasoning: "ML-based decisioning using telemetry trends, sentiment analysis, and policy rules. ",
      execution: "Delivers nudges to user, executes approved auto-remediations, or escalates to ITSM.",
    },
  },
  'accessibility-support-agent': {
    title: "Accessibility Support Agent",
    description: "The Accessibility Support Agent automatically scans and enhances enterprise digital content and applications (like knowledge bases and internal platforms) to meet accessibility standards. It provides real-time adjustments such as generating alt text, adding captions, simplifying interfaces for neurodiverse users, and ensuring keyboard navigation, thereby guaranteeing equal access and compliance for all employees.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "100% of new digital assets enhanced with accessibility support at creation.",
          "25–30% reduction in accessibility-related service desk tickets.",
          "15–20% productivity improvement for employees requiring accessibility support.",
          "Compliance alignment with WCAG 2.1+, ADA, Section 508, and global accessibility mandates.",
          "Increased employee satisfaction and inclusivity, driving retention and engagement."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprises often fail accessibility audits (WCAG, ADA, Section 508) due to missing alt text, captions, or poor navigation support.",
              "Employees with disabilities are forced into manual workarounds or dependency on others, reducing productivity.",
              "Service desks face accessibility-related tickets (“Can’t access form,” “Document unreadable with screen reader”).",
              "Traditional accessibility remediation is manual, costly, and reactive",
              "Non-compliance risks lawsuits, regulatory fines, and reputational damage."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "100% of new digital assets enhanced with accessibility support at creation.",
              "25–30% reduction in accessibility-related service desk tickets.",
              "15–20% productivity improvement for employees requiring accessibility support.",
              "Compliance alignment with WCAG 2.1+, ADA, Section 508, and global accessibility mandates.",
              "Increased employee satisfaction and inclusivity, driving retention and engagement."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Ensures Epic/MyChart portals meet accessibility for patients and staff with impairments.",
              "Finance:  Improves accessibility in trading platforms, reporting dashboards, and compliance apps.",
              "Airline Ops: Supports staff using mobile apps at gates and kiosks with accessible navigation.",
              "ITSM: Enhances KBs, SOPs, and portals with accessibility metadata automatically.",
              "Cybersecurity: Provides accessible versions of security awareness training and alerts."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Auto-generates alt text for images and graphics.",
              "Creates real-time captions and transcripts for video/voice content.",
              "Maps and enforces keyboard navigation paths.",
              "Generates simplified, screen-reader-friendly “readable” versions of documents and portals.",
              "Flags inaccessible components (non-compliant color contrast, unlabeled buttons) and proposes fixes."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Content platforms: SharePoint, Confluence, Document360, WordPress",
              "Collaboration: Teams, Slack, Zoom, WebEx (for captions and transcripts)",
              "Business systems: Epic, Workday, SAP, Salesforce",
              "ITSM: ServiceNow, Jira for accessibility-related tickets and reporting",
              "Accessibility compliance tools: Axe, WAVE, Microsoft Accessibility Insights"


            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-fixes low-risk gaps (adding alt text, captions).",
              "Flags high-risk or compliance-sensitive gaps for human accessibility SMEs.",
              "Escalates repeated violations to governance boards."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA:  Accessibility fixes applied within <1 second for real-time content, <5 minutes for static assets.",
              "Accuracy: 95%+ accuracy in alt text and caption generation.",
              "Impact: 90% compliance alignment with selected accessibility standards."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: RBAC ensures accessibility fixes don’t expose or alter sensitive content.",
              "Data Handling: Sensitive text/audio content encrypted and redacted if required.",
              "Audit & Traceability: Each fix logged with source, method, and reviewer (if applicable).",
              "Certifications Supported: WCAG 2.1 AA/AAA, ADA, Section 508, HIPAA, HITRUST, SOC2, ISO27001."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per user, per digital asset, or outcome-based (per % accessibility compliance achieved)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–30% fewer accessibility tickets = savings in service desk labor.",
              "Reduced risk of non-compliance fines (ADA penalties can exceed $75,000 per violation).",
              "Productivity uplift for staff and customers requiring accessibility support."
            ]
          },

        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: " 8–10 weeks for baseline integration, 12–14 weeks for enterprise-wide accessibility coverage."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot in 1–2 content platforms (e.g., SharePoint + Teams).",
              "60 days: Extend to business-critical systems (Epic, Salesforce, SAP).",
              "90 days: Enterprise-wide rollout with compliance dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to AR/VR and metaverse applications (emerging accessibility challenges)."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Training & Adoption Agent (learning accessibility features), Concierge Agent (intake accessibility issues), and Knowledge Hygiene Agent (ensuring accessible KBs)."
          },
          {
            title: "Vendor Roadmap",
            content: "Planned sentiment-aware accessibility nudges (“Would you like this in a simplified version?”) and advanced captioning (multi-language, technical jargon support)."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "New digital asset created, content uploaded, or user requests accessibility support.",
      workingPlane: "Orchestrates accessibility enhancements across collaboration, content, and application platforms.",
      memory: "Remembers recurring accessibility gaps per team/system, applying fixes automatically. ",
      reasoning: "Determines if accessibility gap is low-risk auto-fix or high-risk SME review. ",
      execution: "Inserts alt text, generates captions, enforces keyboard paths, creates readable versions.",
    },
  },
  'sla-watcher-agent': {
    title: "SLA Watcher Agent (Predicts breach risk and reprioritizes or escalates)",
    description: "The SLA Watcher Agent monitors tickets in real time, predicts which are at risk of breaching, and automatically reprioritizes, escalates, or triggers interventions to ensure commitments are met.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "30–40% reduction in SLA breaches.",
          "20–25% faster resolution for at-risk tickets.",
          "Improved SLA compliance rates (>95% adherence).",
          "Higher trust and satisfaction among business stakeholders and customers.",
          "Reduced risk of contractual penalties and reputational damage."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "15–20% of IT tickets breach SLA commitments in large enterprises.",
              "IT staff often lack real-time visibility into which tickets are trending toward breach.",
              "SLA dashboards are reactive, not predictive.",
              "SLA breaches erode customer and executive trust in IT operations.",
              "Fines or penalties in vendor contracts increase costs when SLAs are missed."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "30–40% reduction in SLA breaches.",
              "20–25% faster resolution for at-risk tickets.",
              "Improved SLA compliance rates (>95% adherence).",
              "Higher trust and satisfaction among business stakeholders and customers.",
              "Reduced risk of contractual penalties and reputational damage."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Ensures Epic/EHR downtime tickets don’t breach SLAs during peak care hours.",
              "Finance: Prevents SLA breaches on compliance-critical incidents that carry regulatory fines.",
              "Airline Ops: Keeps crew scheduling/gate system issues within SLA to prevent flight disruptions.",
              "ITSM: Applies predictive breach monitoring across all incident and request queues.",
              "Cybersecurity: Escalates critical phishing or endpoint detection tickets before SLA timers expire."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Monitors all active tickets against SLA timers in real time.",
              "Uses ML/analytics to predict breach risk based on ticket age, workload, and historical performance.",
              "Reprioritizes tickets automatically (e.g., raise P3 → P2 if breach imminent).",
              "Escalates to Tier 2/3 or management when SLA breach risk > threshold.",
              "Generates dashboards and alerts for SLA compliance and breach prevention."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy",
              "Monitoring: Splunk, Datadog, Elastic for incident feeds",
              "Collaboration: Teams, Slack for SLA risk notifications",
              "Analytics/BI: Power BI, Tableau for SLA performance dashboards"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Predicts and auto-escalates tickets trending toward SLA breach.",
              "Provides explainability dashboards for why actions were taken.",
              "Escalates unresolved systemic risks (team under-capacity) to governance boards."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Risk flagged within <60 seconds of detection.",
              "Accuracy: >90% breach prediction precision after training.",
              "Coverage: % of tickets monitored and reprioritized/escalated."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: SLA interventions only applied within policy-defined roles.",
              "Data Handling: SLA/ticket data encrypted in transit and at rest.",
              "Audit & Traceability: Every reprioritization and escalation logged with justification.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per monitored ticket, per SLA policy, or outcome-based (% breach reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "30–40% fewer SLA breaches saves millions in avoided penalties.",
              "Higher SLA adherence increases IT credibility with executives and clients.",
              "Predictive prioritization reduces costly escalations and firefighting."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for core IAM integration; 12–14 weeks for enterprise app coverage."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot with one SLA category (incidents P2/P3).",
              "60 days: Expand to all incident + request SLAs.",
              "90 days: Enterprise dashboards showing breach prevention ROI."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to vendor/partner SLA monitoring (not just internal IT)."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Ticket Dispatch Agent (reassigns at-risk tickets), VIP White Glove Agent (prioritizes exec tickets), and Campaign Orchestrator Agent (communicates SLA expectations)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive staffing — forecast when SLA breaches will rise due to analyst under-capacity."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Ticket creation, SLA timer updates, queue workload anomalies.",
      workingPlane: "Monitors ITSM SLA timers, workload, and history in real time.",
      memory: "Stores SLA performance history, breach hotspots, team load data.",
      reasoning: "Determines SLA breach risk and decides reprioritization or escalation action.",
      execution: "Reprioritizes tickets, escalates to Tier 2/3, or notifies managers proactively.",
    }
  },
  'escalation-orchestrator-agent': {
    title: "Escalation Orchestrator Agent [Selects best next resolver based on skills and load]",
    description: "The Escalation Orchestrator Agent evaluates ticket content, skills needed, resolver group capacity, and historical success rates to select the best next resolver the first time.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–35% reduction in escalated ticket MTTR.",
          "20–25% fewer ticket bounces between teams.",
          "SLA compliance improved by 15–20% through accurate, timely escalations.",
          "Higher analyst productivity from balanced workloads.",
          "Increased CSAT due to fewer delays and smoother resolution experience."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "25–30% of escalated tickets are misrouted, leading to multiple handoffs.",
              "MTTR rises as tickets “ping-pong” between teams before landing in the right queue.",
              "Overloaded teams receive tickets even when others have capacity.",
              "Skills inventories are underutilized because routing is based on static rules.",
              "SLA breaches occur when escalations stall in the wrong queues."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "25–35% reduction in escalated ticket MTTR.",
              "20–25% fewer ticket bounces between teams.",
              "SLA compliance improved by 15–20% through accurate, timely escalations.",
              "Higher analyst productivity from balanced workloads.",
              "Increased CSAT due to fewer delays and smoother resolution experience."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Escalates Epic module issues to the right clinical app specialist instead of generic IT queue.",
              "Finance: Sends regulatory ERP tickets to compliance analysts vs general IT support.",
              "Airline Ops: Routes crew scheduling failures to ops IT vs endpoint teams.",
              "ITSM: Improves escalation accuracy across incident, request, and change queues.",
              "Cybersecurity: Escalates high-priority phishing/endpoint incidents directly to the correct SOC analyst team."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Analyzes ticket metadata and context for required skills.",
              "Matches against resolver group skills inventories and load data.",
              "Predicts success likelihood based on historical resolution data.",
              "Routes ticket to best resolver queue/team.",
              "Provides audit trail with justification and reasoning."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy",
              "HR/Skills: Workday, SAP SuccessFactors, internal skill inventories",
              "Monitoring: Splunk, Datadog for ticket enrichment signals",
              "Collaboration: Teams, Slack for escalation notifications",
              "Analytics: Power BI, Tableau for escalation reporting"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-escalates based on skill + load logic.",
              "Escalates exceptions (no matching skills found) to governance/queue managers.",
              "Provides dashboards for SLA adherence and escalation performance."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Escalation decision made within <30 seconds of trigger.",
              "Accuracy: >90% correct resolver selection after training period.",
              "Coverage: % of escalated tickets routed by agent."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Ensures only authorized resolver groups can receive escalations.",
              "Data Handling: Ticket and load data encrypted in transit and at rest.",
              "Audit & Traceability: Every escalation decision logged with rationale.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per escalated ticket, per resolver group, or outcome-based (% bounce reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–35% fewer escalations bouncing between teams = thousands of hours saved annually.",
              "SLA penalty avoidance through improved on-time resolution.",
              "Higher analyst productivity via balanced load distribution."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for skills inventory integration; 12–14 weeks for full escalation orchestration rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot in 2–3 resolver groups with clear skill definitions.",
              "60 days: Expand to high-volume queues (infra, apps, security).",
              "90 days: Enterprise coverage with dashboards + ROI reporting."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend beyond IT tickets to HR, Finance, and Facilities escalation workflows."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with SLA Watcher Agent (prioritize at-risk tickets), Triage & Enrichment Agent (add context before escalation), and VIP White Glove Agent (exec tickets auto-routed to senior staff)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive load balancing — forecasting team overloads and shifting escalations dynamically."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Ticket reaches escalation threshold (SLA risk, Tier 1 unable to resolve, enrichment complete).",
      workingPlane: "Orchestrates skill + load evaluation and resolver group assignment.",
      memory: "Stores historical escalation outcomes, skill mappings, and resolver group performance.",
      reasoning: "Matches ticket needs to best resolver based on skills + current load.",
      execution: "Routes ticket to selected resolver, updates ITSM, notifies analyst/team.",
    }
  },
  'workforce-forecasting-agent': {
    title: "Workforce Forecasting Agent [Predicts volume, recommends staffing and deflection targets]",
    description: "The Workforce Forecasting Agent predicts ticket and contact volume, recommends staffing levels, and proposes deflection targets to balance cost, SLA adherence, and user experience.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–30% improvement in forecast accuracy for service desk volumes.",
          "15–20% reduction in SLA breaches from better staffing alignment.",
          "10–15% lower labor costs through optimized FTE allocation.",
          "Defined deflection targets that shift volume to automation/self-service.",
          "Higher CSAT through reduced wait times and improved consistency of service."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "IT leaders struggle to predict service desk volumes accurately, leading to staffing mismatches.",
              "Understaffing leads to SLA breaches, long handle times, and poor CSAT.",
              "Overstaffing drives unnecessary cost and idle FTEs.",
              "Deflection (self-service, automation) goals are rarely quantified or tied to staffing plans.",
              "Without predictive models, boards lack confidence in IT’s ability to manage demand cost-effectively."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "20–30% improvement in forecast accuracy for service desk volumes.",
              "15–20% reduction in SLA breaches from better staffing alignment.",
              "10–15% lower labor costs through optimized FTE allocation.",
              "Defined deflection targets that shift volume to automation/self-service.",
              "Higher CSAT through reduced wait times and improved consistency of service."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Predicts surges in clinical support tickets during EHR upgrades or seasonal patient demand.",
              "Finance: Forecasts spikes during quarter-end, tax season, or compliance reporting cycles.",
              "Airline Ops: Anticipates volume surges during schedule changes or system outages.",
              "ITSM: Guides baseline staffing and automation targets for steady-state operations.",
              "Cybersecurity: Projects ticket volume after phishing campaigns or vulnerability advisories."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Forecasts ticket/contact volume by channel (chat, email, phone, portal).",
              "Predicts workload by category, severity, and SLA risk.",
              "Recommends staffing schedules by skillset, geography, and tier.",
              "Proposes deflection goals (self-service %, automation %, VIP routing).",
              "Generates dashboards and exportable schedules for workforce planners."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy",
              "Telephony/CCaaS: Cisco, Avaya, Genesys Cloud, Twilio",
              "HR/Workforce: Workday, Kronos, SAP SuccessFactors",
              "Collaboration: Teams, Slack for forecast notifications",
              "Analytics: Power BI, Tableau for forecast visualization"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Escalates when forecast predicts SLA breach risk due to under-capacity.",
              "Notifies leadership when forecasted labor exceeds budget thresholds.",
              "Provides scenario modeling (“what if 20% more volume deflected?”)."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Forecasts delivered in <30 minutes for daily/weekly cycles.",
              "Accuracy: >90% forecast accuracy after 3–6 months of learning.",
              "Coverage: % of service desk categories and channels included."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Forecast access tied to workforce planners and governance boards.",
              "Data Handling: Forecast data encrypted in transit and at rest.",
              "Audit & Traceability: Logs forecasts, staffing recommendations, and adopted plans.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per forecast cycle, per user, or outcome-based (% accuracy or % cost savings achieved)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "10–15% lower labor cost through optimized staffing.",
              "20–30% fewer SLA breaches from better resource alignment.",
              "Clear, quantified deflection targets that increase self-service ROI."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for pilot forecasting; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot forecast for one channel (phone).",
              "60 days: Expand to omnichannel volume + staffing recommendations.",
              "90 days: Enterprise rollout with deflection targets + ROI dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend forecasting to cybersecurity SOC, NOC, HR, and customer service functions."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with SLA Watcher Agent (prevents SLA breaches), Scheduling & Callback Agent (shapes demand), and Campaign Orchestrator Agent (drives deflection campaigns)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive deflection modeling — quantifying ROI of automation investments before deployment."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Daily/weekly forecast cycle, budget planning, seasonal event trigger (e.g., upgrades, audits).",
      workingPlane: "Orchestrates ITSM + WFM data ingestion, forecasting models, staffing/deflection outputs.",
      memory: "Stores historical demand, forecast accuracy, and staffing effectiveness.",
      reasoning: "Predicts ticket/contact volume, breach risk, staffing needs, deflection goals.",
      execution: "Generates staffing schedules, deflection targets, and notifications to leaders.",
    }
  },
  'dex-telemetry-agent': {
    title: "DEX Telemetry Agent",
    description: "Enterprises inaccurately measure Digital Experience (DEX). The DEX Telemetry Agent correlates device/app health data with end-user sentiment, giving IT the true health picture.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "360° visibility into user experience by unifying hard telemetry with soft sentiment",
          "20–25% faster root cause analysis by knowing whether issues are device, app, or perception-driven",
          "30–40% reduction in “false positive” tickets (where systems look fine but users complain)",
          "15–20% improvement in CSAT by aligning IT’s success metrics with end-user experience",
          "Better executive reporting with quantifiable Digital Experience Scores (DXS)"
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "IT reports often show “systems are healthy” while user surveys reflect frustration",
              "DEX telemetry is fragmented across endpoint, app monitoring, and ITSM systems",
              "Sentiment (CSAT, surveys, chatbot tone) is not integrated with device health data",
              "Service desks struggle to prove whether problems are tech-related or perception-based",
              "Without correlation, proactive experience improvements are impossible"
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "360° visibility into user experience by unifying hard telemetry with soft sentiment",
              "20–25% faster root cause analysis by knowing whether issues are device, app, or perception-driven",
              "30–40% reduction in “false positive” tickets (where systems look fine but users complain)",
              "15–20% improvement in CSAT by aligning IT’s success metrics with end-user experience",
              "Better executive reporting with quantifiable Digital Experience Scores (DXS)"
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Correlates EHR latency metrics with clinician satisfaction surveys",
              "Finance:Connects trading workstation performance with analyst-reported frustrations",
              "Airline Ops: Measures gate agent device health alongside real-time feedback during boarding",
              "ITSM: Provides unified DEX scores across devices, apps, and roles",
              "Cybersecurity:Correlates degraded performance (caused by endpoint security scans) with user dissatisfaction"
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Collects device health: CPU, memory, disk, battery, network, crash logs",
              "Collects app health: latency, errors, crash frequency, version drift",
              "Ingests user sentiment: surveys, CSAT scores, chatbot tone analysis, service desk feedback",
              "Correlates telemetry and sentiment to produce unified DEX scores",
              "Feeds correlated insights to dashboards, ITSM, and downstream agents"
            ]
          },
          {
            title: "Integrations",
            content: [
              "DEX platforms: Nexthink, Aternity, Lakeside SysTrack, Microsoft Endpoint Analytics",
              "Monitoring: Splunk, Datadog, Elastic, AppDynamics",
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "Collaboration: Teams, Slack (for sentiment capture)",
              "Survey/Feedback: Qualtrics, Medallia, ServiceNow CSAT"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Correlated issues flagged for ITSM incident creation or proactive remediation",
              "Escalates to governance boards if systemic issues impact user experience metrics",
              "Provides role/location/device-level breakdowns for targeted actions"
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Real-time telemetry ingestion (&lt;5s delay), sentiment correlation within 30s",
              "Accuracy: &gt;90% alignment between telemetry + sentiment with actual user experience",
              "Coverage: % of devices/apps/users included in DEX scoring"
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: RBAC ensures sentiment data is anonymized/role-scoped as needed",
              "Data Handling: Telemetry encrypted in transit and at rest; anonymization for survey data",
              "Audit &amp; Traceability: Logs show telemetry collected, correlation logic applied, sentiment sources used",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, GDPR for personal/sentiment data"
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per ticket enriched, per enrichment pack, or outcome-based (% MTTR reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "30–40% fewer false-positive tickets saves millions annually in large enterprises",
              "Faster RCA reduces MTTR, improving productivity and reducing downtime costs",
              "Higher CSAT translates directly to improved employee engagement and retention"
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "8–10 weeks for telemetry ingestion + sentiment integration, 12–14 weeks for enterprise-wide rollout"
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot telemetry ingestion + sentiment correlation for one department",
              "60 days: Expand to top 3 apps and full department coverage",
              "90 days: Enterprise-wide DEX scoring dashboards tied to IT + business KPIs"
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend correlations to customer experience (CX) platforms and external app performance."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Feeds Proactive Nudge Agent, Persona Context Agent, and Training Coach Agent with correlated insights."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive DEX scoring (forecasting satisfaction dips before they occur) and role-based benchmarking."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Device/app telemetry ingestion, user sentiment survey, chatbot interaction analysis.",
      workingPlane: "Orchestrates telemetry + sentiment ingestion, correlation, and output to ITSM dashboards.",
      memory: "Stores historical device/app health correlated with sentiment for trend analysis (with configurable retention and anonymization policies).",
      reasoning: "Analyzes patterns to determine if dissatisfaction is device-driven, app-driven, or perception-driven (with explainability and human-in-loop overrides for audits).",
      execution: "Feeds correlated insights to IT dashboards, triggers automated nudges or remediations, and routes complex incidents (with safe execution, rollback, and escalation handling).",

    }
  },
  'experience-sentiment-analyzer-agent': {
    title: "Experience Sentiment Analyzer Agent",
    description: "This AI mines unstructured user feedback (surveys, chat, email) to surface friction points, correlating them with IT services for actionable insights, not just system reports.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–35% faster detection of emerging friction points.",
          "20–30% reduction in repeat user complaints for the same issues.",
          "Better governance reporting: friction themes tied to business outcomes.",
          "15–20% improvement in CSAT by aligning IT’s success metrics with end-user experience",
          "Reduced ticket volume by proactively addressing issues before escalation."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Surveys and feedback are underutilized; results are siloed in tools like Medallia, Qualtrics, or email inboxes.",
              "Manual analysis is too slow, inconsistent, and misses patterns at scale.",
              "IT leaders often rely on “lagging” ticket data instead of proactive friction detection.",
              "Sentiment disconnected from service data leads to blind spots in governance.",
              "Friction themes (e.g., “VPN unreliable,” “slow logins,” “printer issues”) repeat endlessly without systemic remediation."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "25–35% faster detection of emerging friction points.",
              "20–30% reduction in repeat user complaints for the same issues.",
              "Better governance reporting: friction themes tied to business outcomes.",
              "15–20% improvement in CSAT by aligning IT’s success metrics with end-user experience",
              "Reduced ticket volume by proactively addressing issues before escalation."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Mines clinician surveys and patient portal feedback to highlight recurring frustrations (e.g., slow EHR, login errors).",
              "Finance:Identifies themes in trader complaints about latency, compliance tool usability, or reporting systems.",
              "Airline Ops:Surfaces recurring crew and gate staff complaints (device slowness, app usability) from chat/email logs.",
              "ITSM: Correlates survey feedback with incident categories to improve problem management.",
              "Cybersecurity: Extracts user complaints around MFA, phishing alerts, or training modules."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Mines surveys, CSAT, NPS, chat transcripts, email feedback, and ticket notes.",
              "Detects sentiment polarity, emotional tone, and recurring themes.",
              "Correlates sentiment themes with ITSM categories, apps, devices, or services.",
              "Generates friction dashboards with trend analysis.",
              "Provides structured outputs to trigger proactive remediation workflows."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Survey/Feedback: Qualtrics, Medallia, ServiceNow CSAT, Microsoft Forms.",
              "Collaboration/Chat: Teams, Slack, Zoom chat, WebEx.",
              "ITSM: ServiceNow, Jira, BMC for ticket correlations.",
              "Email: Outlook/Exchange, Gmail (for feedback mining).",
              "Analytics: Power BI, Tableau, ServiceNow dashboards."
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or Agentic Agents as a Service (AaaS).."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Flags new friction themes to IT leaders and governance boards.",
              "Escalates to problem management if recurring friction exceeds defined thresholds.",
              "Human-in-loop review for high-sensitivity data (e.g., HR complaints, compliance issues)."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Real-time ingestion (&lt;30s) for chat/email, daily batch for survey data.",
              "Accuracy: &gt;90% precision in sentiment/theme detection.",
              "Coverage: % of sentiment sources mined (surveys, chat, email, tickets)."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: RBAC ensures only authorized staff can view sensitive sentiment data.",
              "Data Handling: PII redaction and encryption in transit/at rest; anonymization for large-scale reporting.",
              "Audit &amp; Traceability: Logs show which sources were mined, what themes were extracted, and when escalations weremade.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, GDPR for personal data and feedback content."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per user, per sentiment source, or outcome-based (% friction reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–30% fewer repeat complaints reduces ITSM ticket load and staff effort.",
              "Faster RCA through sentiment correlation avoids wasted investigations.",
              "Measurable CSAT uplift tied directly to addressing identified themes."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "8–10 weeks for sentiment ingestion + dashboards; 12–14 weeks for enterprise-wide rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot sentiment mining for surveys + chat logs.",
              "60 days: Expand to email + ITSM notes.",
              "90 days: Full enterprise friction dashboards with remediation loops."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand sentiment mining to voice (call recordings) and social channels (customer feedback forums)."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with DEX Telemetry Agent (hard telemetry correlation), Proactive Nudge Agent (preventive remediation), and Knowledge Mining Agent(knowledge retrieval to address themes)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive sentiment detection — forecasting emerging friction themes before they spike."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "New survey submitted, chat transcript logged, feedback email received, or ticket notes closed.",
      workingPlane: "Orchestrates ingestion from survey, chat, and email platforms, applies Natural Language Processing (NLP), and outputs to dashboards.",
      memory: "Stores recurring sentiment patterns, friction themes, and resolutions (with configurable retention, anonymization, and role/location breakdowns).",
      reasoning: "NLP/ML engine detects sentiment polarity and extracts friction themes (with explainability, taxonomy alignment, and human-in-loop for sensitive themes).",
      execution: "Generates dashboards, alerts IT leaders, and routes systemic friction themes to automated remediation workflows (with safe execution, escalation controls, and rollback of misclassified themes).",
    }
  },
  'password-reset-and-account-unlock-agent': {
    title: "Password Reset and Account Unlock Agent",
    description: "Automating common Tier 1 IT requests like password resets and account unlocks across multiple systems delivers instant, secure access restoration, eliminating high-volume, low-value work.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "30–40% reduction in overall ticket volume through automation.",
          "Users regain access in seconds instead of hours (MTTR reduction &gt;80%).",
          "20–30% Tier 1 cost savings by eliminating repetitive requests.",
          "CSAT uplift of 10–15% from immediate, consistent self-service.",
          "Reduced compliance risk via consistent policy enforcement."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "25–40% of all service desk calls are password/account related (HDI benchmark).",
              "Manual resets add hours of downtime and high operational cost.",
              "Human-driven resets introduce security risk (improper validation).",
              "Compliance frameworks demand stronger authentication guardrails.",
              "User productivity and satisfaction plummet when access issues block work."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "30–40% reduction in overall ticket volume through automation.",
              "Users regain access in seconds instead of hours (MTTR reduction &gt;80%).",
              "20–30% Tier 1 cost savings by eliminating repetitive requests.",
              "CSAT uplift of 10–15% from immediate, consistent self-service.",
              "Reduced compliance risk via consistent policy enforcement."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Epic/EHR, Citrix, and AD access resets for clinicians and staff.",
              "Finance: SAP, O365, and compliance-critical apps with entitlement guardrails.",
              "Airline Ops: Rapid unlock of scheduling, crew, and gate access systems.",
              "ITSM: AD, Entra ID, O365 resets through self-service portals and bots.",
              "Cybersecurity: Enforcement of complex reset policies with audit trails."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Automated password reset and account unlock for AD, Entra ID (Azure AD), O365, SAP, Epic, Citrix, and other enterprise apps.",
              "MFA validation and policy-based guardrails.",
              "Enforces complexity, history, and compliance rules.",
              "Self-service access via chat, portal, or mobile app.",
              "Automatic ITSM ticket logging for audit."
            ]
          },
          {
            title: "Integrations",
            content: [
              "IAM: Azure AD, Entra ID, Okta, Ping, SailPoint",
              "Enterprise apps: O365, SAP, Epic/EHR, Citrix, Salesforce",
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "Collaboration: Teams, Slack (chat-driven resets)",
              "MFA/Identity: Duo, Microsoft Authenticator, Yubikey"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Human-in-loop option for high-risk resets.",
              "Auto-escalation if MFA validation fails or risk signals triggered.",
              "Context handover with full logs for Tier 2/3 review."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Password reset/account unlock completed within &lt;30 seconds.",
              "Accuracy: &gt;95% validation success rate without false unlocks.",
              "Coverage: % of systems and accounts automated."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: MFA, RBAC, device posture, and contextual validation enforced.",
              "Data Handling: Encrypted reset workflows; no plaintext credential exposure.",
              "Audit &amp; Traceability: Every reset/unlock logged with user ID, time, and method.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per user, per reset, or outcome-based (% ticket deflection)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–40% fewer tickets reduces millions in Tier 1 costs.",
              "MTTR reduced from hours to seconds = major productivity savings.",
              "Compliance risk minimized with consistent policy enforcement."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for core IAM integration; 12–14 weeks for enterprise app coverage."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot with AD + O365 resets.",
              "60 days: Expand to SAP + Epic/EHR systems.",
              "90 days: Enterprise rollout with dashboards and ROI reporting."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Add new SaaS apps and IAM connectors as enterprise expands."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Concierge Agent (intake), Omnichannel Router (classification), and Triage &amp; Enrichment Agent(context enrichment)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive account health — nudging users before lockouts occur."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "User request via chat/portal, lockout alert, monitoring event.",
      workingPlane: "Orchestrates IAM integrations, reset workflows, and ITSM logging.",
      memory: "Remembers prior lockouts, role patterns, and policies to optimize resets.",
      reasoning: "Validates identity via MFA and context, selects system-specific workflow.",
      execution: "Executes reset/unlock, syncs across IAM + apps, updates ITSM logs.",
    },
    kpis: ["Ticket deflection %, MTTR reduction, FCR improvement, compliance adherence."]
  },
  'mfa-enrollment-and-recovery-agent': {
    title: "MFA Enrollment and Recovery Agent",
    description: "MFA is mandatory but creates user pain points and burdens service desks with onboarding and recovery. The MFA Enrollment and Recovery Agent automates setup and recovery workflows, keeping the process simple and secure..",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–35% reduction in service desk tickets for MFA setup and recovery.",
          "30–40% faster enrollment of new users into MFA programs.",
          "Improved compliance posture with auditable MFA workflows.",
          "Higher adoption and satisfaction with MFA, reducing risky bypasses.",
          "Reduced risk of account compromise by enforcing secure step-up prompts."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "20–30% of identity-related tickets are tied to MFA setup, resets, or lost devices.",
              "Manual MFA enrollment/recovery is slow, inconsistent, and frustrating for users.",
              "Users often get locked out during critical work, escalating urgency.",
              "Compliance frameworks (HIPAA, PCI-DSS, SOX, NIST) demand strict MFA enforcement with audit trails.",
              "Poor MFA experiences reduce adoption and create risky workarounds."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "25–35% reduction in service desk tickets for MFA setup and recovery.",
              "30–40% faster enrollment of new users into MFA programs.",
              "Improved compliance posture with auditable MFA workflows.",
              "Higher adoption and satisfaction with MFA, reducing risky bypasses.",
              "Reduced risk of account compromise by enforcing secure step-up prompts."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Automates clinician MFA setup for Epic, Citrix, and O365; recovers access during urgent care scenarios.",
              "Finance: Ensures compliance with SOX/KYC by enforcing MFA with backup/recovery workflows.",
              "Airline Ops: Helps staff re-enroll MFA during travel or device turnover without disrupting operations.",
              "ITSM: Integrates MFA flows directly into ServiceNow/Jira portals for enrollment and recovery.",
              "Cybersecurity: Supports step-up MFA for high-risk transactions and phishing suspicious activity reports."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Automates MFA enrollment (QR provisioning, push notifications, token activation).",
              "Issues and manages backup/recovery codes securely.",
              "Handles device change or loss (phone replacement, new laptop).",
              "Supports step-up authentication prompts for high-risk actions.",
              "Logs all MFA events for compliance and audit."
            ]
          },
          {
            title: "Integrations",
            content: [
              "IAM: Azure AD / Entra ID, Okta, Ping, SailPoint",
              "MFA Providers: Microsoft Authenticator, Duo, Google Authenticator, YubiKey, RSA SecureID",
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "Collaboration: Teams, Slack for automated enrollment prompts and recovery flows",
              "Compliance: SIEM tools (Splunk, Elastic, Datadog) for log forwarding"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Low-risk enrollments and recoveries fully automated.",
              "High-risk or repeated failures escalate to IAM or service desk SMEs.",
              "All events logged with policy-driven auditability."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Enrollment/recovery completed within &lt;2 minutes.",
              "Accuracy: &gt;95% success in matching user to correct MFA workflow.",
              "Coverage: % of users successfully onboarded with compliant MFA."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: RBAC, device posture, and context-aware policies baked in.",
              "Data Handling: MFA secrets stored securely; backup codes encrypted at rest.",
              "Audit &amp; Traceability: Every enrollment, recovery, and step-up logged.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, NIST 800-63."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per user, per MFA workflow, or outcome-based (% ticket reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–35% fewer MFA-related tickets saves millions in Tier 1 costs.",
              "Faster onboarding reduces downtime for new hires/contractors.",
              "Compliance posture strengthened — reducing risk of fines or breaches."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for initial IAM/MFA integration; 12–14 weeks for enterprise-wide rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot enrollment + recovery for AD/O365 users.",
              "60 days: Expand to SAP, Epic, Salesforce with backup code automation.",
              "90 days: Enterprise-wide rollout with dashboards and SIEM integration."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Add biometric, passwordless, and continuous authentication flows."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Password Reset Agent (account recovery), Concierge Agent (intake), and Personal Context Agent(role- specific MFA)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive MFA nudges — warning users of expiring tokens or high-risk devices before lockout."
          },
        ]
      }
    ],

    agenticModel: {
      trigger: "New hire, device change, login failure, compliance step-up event. ",
      workingPlane: "Orchestrates IAM, MFA, ITSM, and collaboration integrations.  ",
      memory: "Stores preferred MFA methods and past recovery outcomes. ",
      reasoning: "Chooses enrollment/recovery workflow based on policy and risk context.",
      execution: "Automates MFA provisioning, recovery, and step-up prompts. ",
    },
    kpis: ["Ticket reduction %, enrollment success rate, recovery success rate, compliance adherence."]
  },
  'device-remediation-agent': {
    title: "Device Remediation Agent",
    description: "End-user devices often suffer from low-level issues (e.g., outdated drivers, failed patches, full disks) that overwhelm the service desk and frustrate users. The Device Remediation Agent automates the detection and resolution of these common problems directly on endpoints, fixing issues without human intervention.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "30–40% reduction in device-related tickets.",
          "25–35% faster MTTR when automated remediations applied instantly.",
          "15–20% improvement in device compliance posture (patches, policies).",
          "Higher CSAT scores from fewer disruptions and faster fixes.",
          "Reduced IT labor costs by offloading repetitive remediation tasks."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "30–40% of device-related tickets could be solved by basic remediations (disk cleanup, driver update, service restart).",
              "Manual troubleshooting consumes Tier 1/2 analyst capacity and delays resolution.",
              "Users face repeated downtime for preventable issues.",
              "Compliance posture suffers when policies or patches fall out of sync.",
              "Traditional endpoint management tools push updates, but don’t reactively remediate in real time."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "30–40% reduction in device-related tickets.",
              "25–35% faster MTTR when automated remediations applied instantly.",
              "15–20% improvement in device compliance posture (patches, policies).",
              "Higher CSAT scores from fewer disruptions and faster fixes.",
              "Reduced IT labor costs by offloading repetitive remediation tasks."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare:Auto-fixes EHR workstation issues (print spooler restarts, Citrix session resets).",
              "Finance: Remediates failed trading workstation patches or disk cleanup for compliance logging apps.",
              "Airline Ops: Resolves gate/crew device slowness by syncing policies or cleaning local caches.",
              "ITSM: Automates common endpoint fixes (network service restart, driver updates, patch retries).",
              "Cybersecurity: Enforces patch and policy sync to reduce vulnerability exposure."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Driver updates, patch installs, and retries.",
              "Service restarts (print spooler, network, application services).",
              "Disk cleanup (temp files, caches, logs).",
              "Policy re-sync (GPO, Intune, MEM, security baselines).",
              "Remediation scripts triggered on demand or automatically."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Endpoint management: MEM, Intune, SCCM, Workspace ONE, JAMF.",
              "DEX/Monitoring: Nexthink, Aternity, Splunk, Datadog.",
              "ITSM: ServiceNow, Jira Service Management, BMC.",
              "Automation: Ansible, PowerShell, n8n flows for remediation playbooks.",
              "Security/Compliance: Defender, CrowdStrike for endpoint health checks."
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Low-risk fixes executed automatically (disk cleanup, service restart).",
              "High-risk fixes (driver updates on production-critical devices) escalated.",
              "Full remediation logs provided to ITSM and compliance boards."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Remediations executed within <60 seconds of detection.",
              "Accuracy: >90% successful remediation rate for supported actions.",
              "Coverage: % of device-related tickets prevented/resolved automatically."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "Identity & Access Guardrails:Ensures remediations executed only under approved entitlements.",
              "Data Handling:Endpoint telemetry encrypted in transit/at rest.",
              "Audit & Traceability: Each remediation logged with justification and outcome.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, NIST 800-53."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per device, per remediation, or outcome-based (% ticket reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "30–40% fewer device-related tickets saves millions annually in Tier 1/2 costs.",
              "Faster MTTR reduces user downtime, worth $500–$1,000+ per employee annually.",
              "Improved compliance reduces audit remediation costs and security risks."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for top 5 remediations; 12–14 weeks for enterprise library rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days:Pilot with service restarts + disk cleanup.",
              "60 days:Expand to driver updates and patch remediations.",
              "90 days: Enterprise rollout with dashboards and ROI reporting."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Add remediation packs for SaaS apps, mobile devices, and VDI."
          },
          {
            title: "Multi-Agent Interoperability",
            content: ": Works with Proactive Nudge Agent (warns users pre-failure), App Compatibility Coach Agent (flags version issues), and Software Distribution Agent (pushes full updates)."
          },
          {
            title: "Vendor Roadmap",
            content: " Predictive remediation — agent applies fixes before failures manifest."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Monitoring alert, DEX telemetry anomaly, ITSM ticket creation, or scheduled sweep.",
      workingPlane: "Orchestrates remediation across endpoint managers and automation platforms.",
      memory: "Stores historical remediation success/failure per device/app type.",
      reasoning: "Determines correct fix (driver update, service restart, cleanup, policy sync).",
      execution: "Executes remediation actions on endpoints, validates success, updates ticket/logs.",
    },
    kpis: ["Ticket reduction %, MTTR improvement, compliance adherence, ROI metrics."]
  },
  'printer-fix-agent': {
    title: "Printer Fix Agent",
    description: "Printers are a disproportionate source of IT pain due to issues like jammed queues, corrupt drivers, and broken mappings, generating repeated tickets. The Printer Fix Agent automates end-to-end remediation for these common problems, resolving them instantly without Tier 1 intervention.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–35% reduction in service desk printer tickets.",
          "20–30% faster resolution for recurring printer issues",
          "Improved productivity — clinicians, analysts, and staff print without delays.",
          "15–20% higher CSAT from eliminating frustrating, high-visibility problems.",
          "Stronger compliance by enforcing approved drivers and printer mappings."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "10–20% of service desk tickets in some industries are printer-related.",
              "Users waste hours waiting for fixes on basic queue flushes or driver reinstalls.",
              "Analysts burn capacity solving repetitive printer problems manually.",
              "Poor printer experiences degrade clinician, finance, and operations productivity",
              "Compliance and security gaps arise when users install unapproved printers or drivers as workarounds."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "25–35% reduction in service desk printer tickets.",
              "20–30% faster resolution for recurring printer issues",
              "Improved productivity — clinicians, analysts, and staff print without delays.",
              "15–20% higher CSAT from eliminating frustrating, high-visibility problems.",
              "Stronger compliance by enforcing approved drivers and printer mappings."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Fixes Epic/EHR workstation print queues and label printers in clinical areas.",
              "Finance: Resolves high-volume print errors for reporting, compliance docs, and batch jobs.",
              "Airline Ops: Auto-resolves boarding pass/kiosk printer issues at gates and check-in.",
              "ITSM: Automates common end-user printer fix workflows across departments.",
              "Cybersecurity:  Blocks unapproved drivers, ensuring only trusted mappings persist."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Flush stuck/corrupted print queues.",
              "Reinstall or update printer drivers from approved repositories.",
              "Remap network/shared printers automatically.",
              "Reset default printer to policy-approved device.",
              "Generate compliance logs for unapproved printer installations."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Endpoint management: MEM, Intune, SCCM, Workspace ONE",
              "ITSM:ServiceNow, Jira Service Management, BMC",
              "DEX/Monitoring: Nexthink, Aternity for printer telemetry",
              "Collaboration: Teams, Slack for self-service printer fix triggers",
              "Compliance: Defender, CrowdStrike for unapproved driver enforcement"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Low-risk fixes executed automatically.",
              "Escalates to Tier 2 if hardware or physical errors detected (e.g., paper jam).",
              "Provides full remediation history for ITSM governance boards."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Printer fix executed within <60 seconds of ticket or telemetry trigger.",
              "Accuracy: >90% resolution success for supported printer errors.",
              "Coverage: % of printer-related tickets auto-resolved."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "Identity & Access Guardrails: Enforces policy-approved printer mappings and driver libraries.",
              "Data Handling:  Logs and telemetry encrypted in transit/at rest.",
              "Audit & Traceability: Every remediation linked to user/device ID and action taken.",
              "Certifications Supported:HIPAA (for patient printing workflows), SOC2, ISO27001, PCI-DSS."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per printer, per remediation, or outcome-based (% ticket reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–35% fewer printer tickets = major labor savings in Tier 1 desks.",
              "20–30% MTTR reduction boosts user productivity.",
              "Compliance risk avoided by blocking unauthorized printers/drivers."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for top 5 printer models/workflows; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot flush/remap for top 3 printer queues.",
              "60 days: Add driver reinstall + default reset workflows.",
              "90 days:Enterprise rollout with compliance dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Add remediation for scanners, label printers, and multifunction devices."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Device Remediation Agent (endpoint fixes), Similar Ticket Matcher Agent (reuse proven printer fixes), and Campaign Orchestrator Agent (communicate known printer issues)."
          },
          {
            title: "Vendor Roadmap",
            content: " Predictive fixes — anticipate printer queue failures based on telemetry before user impact."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "ITSM ticket, DEX telemetry (stuck queue), user self-service command, monitoring alert.",
      workingPlane: "Orchestrates remediation actions (queue flush, driver reinstall, remap, reset default).",
      memory: "Stores recurring printer failures and successful fixes by model/site.",
      reasoning: "Determines failure type and selects correct remediation pack.",
      execution: "Executes flush, reinstall, remap, reset actions automatically.",
    },
    kpis: [
      "Ticket reduction %, MTTR improvement, compliance adherence, CSAT uplift."]
  },
  'mobile-device-self-heal-agent': {
    title: "Mobile Device Self Heal Agent",
    description: "Mobile devices used by professionals frequently encounter compliance issues like broken profiles or failed app installations. A Mobile Device Self - Heal Agent automatically detects and resolves these problems, ensuring devices remain secure and functional without needing IT support.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–35% reduction in mobile device-related tickets.",
          "20–30% faster remediation of compliance drift (policies, certs, profiles).",
          "15–20% higher uptime for frontline workers who rely on mobile access.",
          "Improved compliance posture with automated policy enforcement and logs.",
          "Reduced IT labor cost by eliminating repetitive Tier 1 mobile troubleshooting."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "20–25% of tickets in field-heavy industries (healthcare, airlines, retail) involve mobile devices.",
              "Broken MDM profiles and failed app installs force users to open tickets and wait for Tier 1.",
              "Compliance requirements (HIPAA, PCI-DSS, GDPR) mandate strict device posture enforcement.",
              "Users often bypass IT or delay reporting issues, creating shadow IT risks.",
              "Manual remediation is slow and doesn’t scale across thousands of mobile devices."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "25–35% reduction in mobile device-related tickets.",
              "20–30% faster remediation of compliance drift (policies, certs, profiles).",
              "15–20% higher uptime for frontline workers who rely on mobile access.",
              "Improved compliance posture with automated policy enforcement and logs.",
              "Reduced IT labor cost by eliminating repetitive Tier 1 mobile troubleshooting."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Keeps Epic/Citrix mobile access compliant for clinicians on iPads/iPhones.",
              "Finance: Finance: Auto-remediates compliance apps (KYC, reporting) on advisors’ mobile devices.",
              "Airline Ops: Passenger disruptions, crew scheduling, real-time staff support.",
              "ITSM: Applies self-heal to O365/Teams/Zoom apps on mobile endpoints.",
              "Cybersecurity: Enforces encryption, MDM policy sync, and posture checks for Zero Trust."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Reapplies broken MDM policies and profiles.",
              "Forces app reinstall or update for failed/broken apps.",
              "Runs posture checks (OS version, jailbreak/root detection, encryption status).",
              "Logs all actions into ITSM with compliance audit trail."
            ]
          },
          {
            title: "Integrations",
            content: [
              "MDM: Intune, Workspace ONE, MobileIron, JAMF, SOTI.",
              "ITSM: ServiceNow, Jira Service Management, BMC.",
              "Collaboration:  Teams, Slack for self-service triggers and notifications.",
              "Cloud: Azure, AWS, GCP.",
              "Security: : Defender ATP, CrowdStrike, Zscaler for posture telemetry."
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Low-risk actions (policy sync, app reinstall) executed automatically.",
              "High-risk exceptions (non-compliant devices, repeated root detection) escalated to SOC/IT.",
              "Dashboards show remediation coverage and compliance posture."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Sub-2 second response latency SLA.",
              "Accuracy: 99.9% uptime availability.",
              "Coverage: 95% intent recognition accuracy in production workflows."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: RBAC ensures actions are tied to correct user roles.",
              "Data Handling: Logs and telemetry encrypted in transit and at rest.",
              "Audit & Traceability: Every self-heal event logged with device/user contex.",
              "Certifications Supported:HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: " Per device, per remediation, or outcome-based (% ticket reduction/compliance improvement)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–35% fewer mobile tickets saves millions annually for large enterprises.",
              "Faster onboarding/return-to-service for frontline workers reduces downtime costs.",
              "Stronger compliance avoids regulatory fines and audit remediation costs"
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: " 8–10 weeks for pilot integration; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot on 1–2 high-value apps (Epic, O365).",
              "60 days: Expand to policy/profile self-heal across departments",
              "90 days: Enterprise rollout with compliance dashboards"
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: " Expand to wearables, IoT, and field/rugged devices."
          },
          {
            title: "Multi-Agent Interoperability",
            content: ". Works with Access Provisioning Agent (JML lifecycle), VPN & SSO Helper Agent (secure access), and Device Remediation Agent (endpoint fixes)"
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive compliance monitoring — anticipate drift before policy breach."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "User chat, voice, or video request, monitoring alert, API call.",
      workingPlane: "Execution environment (cloud, hybrid, AaaS) with failover and scale.",
      memory: "Persistent and session memory storing prior interactions, KB references, incident history.",
      reasoning: "ML-driven reasoning engine applying policies, sentiment, and business context.",
      execution: "Direct actions: password resets, ticket creation, knowledge delivery, escalations.",
    },
    kpis: []
  },
  'omnichannel-intake-router-agent': {
    title: "Omnichannel Intake Router Agent [Classifies and routes across chat, email, phone, portal]",
    description: "The Omnichannel Intake Router Agent automatically classifies and routes incoming service requests from all channels (chat, email, phone, self-service) to the correct workflow, team, or automation, eliminating manual queueing, misclassification, and dropped requests.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "40–50% reduction in misrouted tickets.",
          "20–30% improvement in first-contact resolution by routing correctly the first time.",
          "25–30% reduction in MTTR through faster, automated intake.",
          "CSAT improvement of 10–15% from consistent service across all channels.",
          "Lower Tier 1 staffing costs by shifting to automated routing vs manual triage."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "30–40% of tickets are misrouted or incorrectly categorized at first intake.",
              "Manual triage increases MTTR and drives up costs.",
              "Phone and email remain high-volume, but ITSM platforms struggle to unify them with chat/portal flows.",
              "Customers expect seamless experiences but get inconsistent service depending on channel.",
              "Missed or delayed routing creates compliance risks in healthcare, finance, and regulated industries."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "40–50% reduction in misrouted tickets.",
              "20–30% improvement in first-contact resolution by routing correctly the first time.",
              "25–30% reduction in MTTR through faster, automated intake.",
              "CSAT improvement of 10–15% from consistent service across all channels.",
              "Lower Tier 1 staffing costs by shifting to automated routing vs manual triage."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Routes Epic access requests, device issues, and clinical workflow support to the correct queues.",
              "Finance: Distinguishes between compliance queries, transaction issues, and IT troubleshooting.",
              "Airline Ops: Routes crew scheduling, passenger service desk, and gate system tickets to the right support pods.",
              "ITSM: Consolidates intake from chat, email, phone, and portals into one orchestration layer.",
              "Cybersecurity: Ensures phishing reports and security alerts bypass Tier 1 and go directly to SOC queues."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Classifies unstructured intake (NLP on email/chat, transcripts from voice).",
              "Applies business rules, policies, and SLA priorities for routing.",
              "Normalizes data across channels for consistent ITSM entry.",
              "Routes to: self-service automation, Tier 1–3 queues, or specialized teams.",
              "Provides intake dashboards and feedback loops for accuracy tuning."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy",
              "Email: Exchange, Gmail",
              "Chat/Collaboration: Teams, Slack, Zoom, WebEx",
              "Telephony: Cisco, Avaya, Twilio (for call transcripts and routing)",
              "Monitoring/Security: Splunk, Datadog, CrowdStrike for direct routing of alerts"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-escalates unclear intake to human triage with full context attached.",
              "Provides explainability dashboards for why a request was routed a certain way.",
              "Continuous learning loop from routing outcomes."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: <2 seconds classification/routing latency.",
              "Accuracy: >95% correct classification after training period.",
              "Coverage: 100% of intake channels unified into ITSM."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Ensures sensitive requests (HR, finance, PHI) are routed only to entitled teams.",
              "Data Handling: All intake data encrypted in transit and at rest; PII redacted per policy.",
              "Audit & Traceability: Routing logs with justification available for audits.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per intake event, per user, or outcome-based (% misroutes reduced)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "40–50% reduction in misrouted tickets saves millions in labor annually.",
              "Faster routing reduces MTTR, boosting productivity across workforce.",
              "Higher CSAT translates to improved adoption of self-service channels."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for pilot intake channels; 12–14 weeks for full omnichannel integration."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot routing for chat + email.",
              "60 days: Expand to phone and portal intake.",
              "90 days: Unified omnichannel intake dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Add new intake sources (SMS, mobile apps, IoT alerts)."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Concierge Agent (front-door intake), Ticket Dispatch Agent (intelligent assignment), and VIP White Glove Agent (priority routing)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive routing (anticipating issue type from context before intake completes)."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "New intake received via chat, email, phone, or portal.",
      workingPlane: "Orchestrates classification and routing across all channels.",
      memory: "Remembers prior routing outcomes, misroutes, and success rates.",
      reasoning: "Applies NLP/ML + business rules to classify and route.",
      execution: "Routes to automation, service desk queues, or specialized workflows.",
      kpis: "Misroute reduction %, MTTR reduction %, FCR improvement, CSAT uplift."
    }
  },
  'smart-form-builder-agent': {
    title: "Smart Form Builder Agent [Auto-builds intake forms from historical fields and outcomes]",
    description: "The Smart Form Builder Agent automatically mines historical ticket data to identify key resolution fields and generates dynamic, optimized intake forms tailored to the specific request type, eliminating static forms that lack detail or overwhelm users.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "30–40% reduction in back-and-forth clarifications.",
          "20–25% faster MTTR from better upfront ticket detail.",
          "15–20% improvement in first-contact resolution rates.",
          "Higher self-service adoption as forms become easier and more relevant.",
          "Reduced service desk costs by streamlining intake at scale."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "35–45% of tickets require follow-up because the intake form missed critical details.",
              "Static forms become bloated with irrelevant fields, reducing adoption of portals/self-service.",
              "Service desk teams waste time reclassifying or gathering missing info from users.",
              "IT struggles to adapt forms quickly to new apps, workflows, or services.",
              "Poor intake leads directly to longer MTTR, higher costs, and lower CSAT."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "30–40% reduction in back-and-forth clarifications.",
              "20–25% faster MTTR from better upfront ticket detail.",
              "15–20% improvement in first-contact resolution rates.",
              "Higher self-service adoption as forms become easier and more relevant.",
              "Reduced service desk costs by streamlining intake at scale."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Builds forms that collect necessary context for Epic access, device provisioning, or clinical workflow tickets.",
              "Finance: Ensures compliance-critical fields (e.g., SOX, KYC) are included in relevant requests.",
              "Airline Ops: Simplifies intake for crew scheduling or gate system issues by auto-generating role-based fields.",
              "ITSM: Optimizes general intake forms for common categories (password reset, access requests, hardware issues).",
              "Cybersecurity: Ensures incident intake includes IOC details, suspicious email metadata, or device context."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Mines ticket histories, fields, and outcomes to identify effective intake questions.",
              "Auto-generates forms by category/service type, role, or persona.",
              "Updates forms dynamically as new services or issues emerge.",
              "Supports conditional logic (fields appear only if relevant to the context).",
              "Provides recommendations for mandatory vs optional fields."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy",
              "Knowledge bases: Confluence, SharePoint (for field recommendations)",
              "Collaboration: Teams, Slack for chat-driven form completion",
              "Identity: Azure AD, Okta for role- and entitlement-aware field generation"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Form changes tracked in dashboards for governance review.",
              "Human-in-loop approval for new fields in compliance-critical workflows.",
              "Escalates inconsistencies in field usage or taxonomy."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Form updates published within <24 hours of identifying optimization need.",
              "Accuracy: >90% correlation between optimized fields and improved resolution outcomes.",
              "Coverage: % of service categories with dynamic intake forms."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Role-based access ensures sensitive fields visible only to entitled users.",
              "Data Handling: Intake data encrypted in transit/at rest; sensitive fields masked for compliance.",
              "Audit & Traceability: Each form change logged, including justification and impact.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per optimized form, per service category, or outcome-based (% MTTR reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–25% MTTR reduction = significant labor savings in large service desks.",
              "15–20% higher portal adoption reduces phone/email volume.",
              "Avoided compliance gaps by ensuring required fields are captured."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for top ticket categories; 12–14 weeks for enterprise-wide rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot auto-built forms for 2–3 high-volume categories.",
              "60 days: Expand to top 10 categories with dynamic fields.",
              "90 days: Enterprise-wide smart forms, with adoption + MTTR dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend to HR, Finance, and Facilities forms beyond IT."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Omnichannel Intake Router Agent (classifies intake), Ticket Dispatch Agent (routes tickets), and Knowledge Hygiene Agent (enforces taxonomy for forms)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive forms that pre-populate fields based on telemetry and prior tickets."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "New ticket created, new service added, or high rate of clarifications detected.",
      workingPlane: "Orchestrates mining of historical data, form creation, and updates.",
      memory: "Stores historical form effectiveness, resolution outcomes, and user feedback.",
      reasoning: "Determines which fields drive faster resolution and should be included.",
      execution: "Auto-builds forms in ITSM platforms, tailors by category, role, or persona.",
      kpis: "MTTR reduction, FCR improvement, clarification reduction %, portal adoption uplift."
    }
  },
  'similar-ticket-matcher-agent': {
    title: "Similar Ticket Matcher Agent [Finds lookalikes and proposes proven fix playbooks]",
    description: "The Similar Ticket Matcher Agent automatically searches historical records, identifies lookalike issues, and proposes the proven resolution playbooks and root causes from past tickets, enabling analysts to apply fixes instantly without repeating investigations.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "35–45% of tickets are repeat issues.",
          "Analysts waste hours troubleshooting known problems without leveraging history.",
          "ITSM search is keyword-based, not context- or pattern-based.",
          "Lack of reuse increases MTTR, escalations, and costs.",
          "Users lose confidence when the same issues resurface without faster resolution."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "35–45% of tickets are repeat issues.",
              "Analysts waste hours troubleshooting known problems without leveraging history.",
              "ITSM search is keyword-based, not context- or pattern-based.",
              "Lack of reuse increases MTTR, escalations, and costs.",
              "Users lose confidence when the same issues resurface without faster resolution."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "25–35% reduction in MTTR by applying proven fixes.",
              "20–25% fewer escalations as Tier 1 agents resolve repeat issues themselves.",
              "15–20% improvement in first-contact resolution (FCR).",
              "Increased CSAT as users experience consistent, faster fixes.",
              "Reduced labor costs by avoiding redundant troubleshooting."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Matches Epic login failures or printing errors to prior fixes and KBs.",
              "Finance: Finds similar Salesforce or ERP reporting tickets and proposes fixes.",
              "Airline Ops: Matches crew scheduling errors or boarding system failures to past incidents.",
              "ITSM: Accelerates resolution of hardware/software tickets by surfacing prior resolutions.",
              "Cybersecurity: Correlates repeated phishing alerts or endpoint detections with past response playbooks."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Analyzes incoming ticket text and metadata (category, CI, user, device).",
              "Matches against historical tickets, KBs, and playbooks using NLP similarity models.",
              "Suggests proven fix steps or automation workflows.",
              "Provides confidence scoring for analyst validation.",
              "Updates KB/playbooks automatically with new proven fixes."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy",
              "Knowledge bases: Confluence, SharePoint, vendor KBs",
              "Automation: Ansible, n8n, ServiceNow Flow, RunOps playbooks",
              "Monitoring: Splunk, Datadog for log correlation",
              "Collaboration: Teams, Slack for suggested fix delivery"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Low-risk fixes can be automated directly.",
              "High-risk or low-confidence matches flagged for SME validation.",
              "Continuous feedback loop improves accuracy over time."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Similar ticket matches surfaced within <5 seconds of ticket creation.",
              "Accuracy: >90% match precision after tuning.",
              "Coverage: % of tickets resolved via matched playbooks."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Ensures suggested playbooks align with role-based access policies.",
              "Data Handling: Ticket data encrypted in transit/at rest; sensitive PII masked before matching.",
              "Audit & Traceability: Logs every match, suggested fix, and action taken.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per ticket matched, per playbook library, or outcome-based (% MTTR reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–35% MTTR reduction = thousands of analyst hours saved annually.",
              "20–25% fewer escalations reduce Tier 2/3 labor costs.",
              "Improved user satisfaction through faster, standardized fixes."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for initial similarity model training; 12–14 weeks for enterprise - wide rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot with top 3 ticket categories (passwords, VPN, device).",
              "60 days: Expand to top 10 categories and add KB/playbook integrations.",
              "90 days: Enterprise-wide adoption with ROI dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend beyond IT tickets to HR, Finance, and Facilities requests."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Triage & Enrichment Agent (adds context), Smart Form Builder Agent (optimizes intake), and Auto Article Generation Agent (creates KBs from similar tickets)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive resolution — automatically resolving tickets before routing based on confidence score."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Ticket creation, monitoring alert, or enrichment completed.",
      workingPlane: "Orchestrates NLP similarity analysis, KB/playbook lookups, and match scoring.",
      memory: "Stores match outcomes, resolution success, and SME feedback.",
      reasoning: "Determines closest matches and selects best playbooks.",
      execution: "Suggests playbooks to analysts, or triggers automation when approved.",
      kpis: "MTTR reduction %, FCR improvement, escalation reduction %, CSAT uplift."
    }
  },
  'campaign-orchestrator-agent': {
    title: "Campaign Orchestrator Agent",
    description: "The Campaign Orchestrator Agent automates and personalizes change communications (features, patches, policies) by delivering targeted notices and nudges across chat, email, and mobile channels to drive user awareness and self-service adoption.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–30% increase in self-service adoption after campaigns.",
          "20–25% reduction in post-change tickets (users better prepared for change).",
          "15–20% faster adoption of new workflows or features.",
          "Improved compliance by ensuring mandatory updates/training are acknowledged.",
          "Governance boards receive measurable data on campaign reach and impact."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Change notices are inconsistent, buried in emails, or missed entirely.",
              "Users fail to adopt self-service or new workflows because campaigns are not reinforced.",
              "IT wastes time re-communicating changes via manual outreach.",
              "Poor communications lead to higher ticket volume and failed change initiatives.",
              "Governance boards lack visibility into whether change campaigns actually reach and impact users."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "25–30% increase in self-service adoption after campaigns.",
              "20–25% reduction in post-change tickets (users better prepared for change).",
              "15–20% faster adoption of new workflows or features.",
              "Improved compliance by ensuring mandatory updates/training are acknowledged.",
              "Governance boards receive measurable data on campaign reach and impact."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Pushes notices about Epic/MyChart changes, compliance training, or new digital workflows.",
              "Finance: Launches campaigns for new regulatory processes or ERP system rollouts.",
              "Airline Ops: Distributes device updates or app training to staff across global hubs.",
              "ITSM: Orchestrates campaigns for self-service adoption (password resets, catalog requests).",
              "Cybersecurity: Pushes phishing simulations, MFA enrollment, and awareness campaigns."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Campaign design, scheduling, and targeting by role/location/device.",
              "Multi-channel distribution: email, chat (Teams, Slack), in-app overlays, mobile push.",
              "Tracks campaign acknowledgment, click-through, and adoption metrics.",
              "Integrates with ITSM to trigger campaigns during changes or incidents.",
              "Automates reinforcement nudges for unacknowledged or incomplete actions."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC (change management, service catalog).",
              "Collaboration: Teams, Slack, Zoom, WebEx",
              "Email: Outlook/Exchange, Gmail",
              "DEX/Feedback: Qualtrics, Medallia (for adoption surveys)",
              "Business Apps: Epic, Workday, SAP, Salesforce (for in-app overlays)"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Campaigns logged for audit and governance.",
              "Non-compliance (e.g., ignored mandatory campaigns) escalated to leadership.",
              "Feedback loop allows campaign tuning based on user responses."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Campaigns delivered within <1 min of trigger event.",
              "Reach rate: % of targeted users who receive campaign.",
              "Engagement rate: % of users acknowledging or acting on campaign.",
              "Adoption rate: % of users adopting self-service or new workflows."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Campaign targeting respects RBAC and entitlements.",
              "Data Handling: Campaign telemetry encrypted in transit and at rest.",
              "Audit & Traceability: All campaign deliveries, acknowledgments, and escalations logged.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per campaign, per user, or outcome-based (% adoption increase)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–25% fewer post-change tickets reduces service desk workload significantly.",
              "25–30% higher adoption of self-service drives cost savings and productivity.",
              "Compliance campaigns reduce regulatory risk by ensuring acknowledgment logs"
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for first campaign rollout, 12–14 weeks for enterprise-wide orchestration."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot campaign for one change initiative (e.g., MFA rollout).",
              "60 days: Expand to 3–5 self-service adoption campaigns.",
              "90 days: Full enterprise orchestration with adoption dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Add campaign packs for industry-specific rollouts (healthcare EHR, finance compliance, airline ops mobility)."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Training & Adoption Agent (reinforces rollouts), Concierge Agent (intake), and Persona Context Agent (personalizes campaigns)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive campaigns (auto-detect which users need communication before rollout), sentiment analysis to optimize message style."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "IT change events, new feature rollout, compliance requirement, or ITSM workflow trigger.",
      workingPlane: "Orchestrates campaign design, targeting, delivery, and reinforcement across channels.",
      memory: "Stores campaign history, user acknowledgment, and adoption outcomes.",
      reasoning: "Determines best channel, timing, and format per persona/role.",
      execution: "Pushes campaigns across channels, tracks engagement, reinforces as needed.",
      kpis: "Campaign reach %, acknowledgment rate %, adoption impact, ticket reduction, compliance coverage."
    }
  },
  'shadow-it-detection-agent': {
    title: "Shadow IT Detection Agent",
    description: "The Shadow IT Detection Agent continuously analyzes usage logs and network traffic to surface unsanctioned tools (Shadow IT), mitigating data security risks and guiding users toward approved, compliant alternatives.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–30% reduction in unapproved SaaS usage.",
          "15–25% savings by consolidating duplicate or redundant app spend.",
          "Stronger compliance posture by guiding usage to approved tools.",
          "25–30% fewer security incidents caused by unmanaged SaaS adoption.",
          "Improved employee trust by offering guided alternatives, not just blanket restrictions."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "30–50% of SaaS spend in large enterprises is on unmanaged or duplicate apps (Gartner).",
              "Shadow IT drives compliance risk (GDPR, HIPAA, PCI-DSS) by storing sensitive data outside governance.",
              "IT has little visibility into unsanctioned usage without expensive manual audits.",
              "Users adopt Shadow IT because sanctioned tools feel slower or harder to use.",
              "Boards increasingly demand proof that Shadow IT exposure is under control."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "20–30% reduction in unapproved SaaS usage.",
              "15–25% savings by consolidating duplicate or redundant app spend.",
              "Stronger compliance posture by guiding usage to approved tools.",
              "25–30% fewer security incidents caused by unmanaged SaaS adoption.",
              "Improved employee trust by offering guided alternatives, not just blanket restrictions."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Detects unsanctioned cloud storage or messaging apps that could expose PHI.",
              "Finance: Identifies duplicate SaaS spend across business units and guides staff to approved platforms.",
              "Airline Ops: Surfaces field staff use of unauthorized scheduling or comms tools.",
              "ITSM: Flags non-compliant integrations and surfaces policy-approved options.",
              "Cybersecurity: Reduces attack surface by eliminating unmanaged apps with weak security."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Scans logs, cloud access records, and network telemetry for unsanctioned tools.",
              "Identifies duplicate apps and unmanaged SaaS.",
              "Flags policy violations (non-compliance with data residency, encryption, IAM).",
              "Suggests approved alternatives or consolidations.",
              "Generates executive dashboards for exposure, cost, and risk."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Network monitoring: Zscaler, Palo Alto, Cisco Umbrella.",
              "CASB/Cloud: Netskope, Microsoft Defender for Cloud Apps, Google BeyondCorp.",
              "ITSM: ServiceNow, Jira for policy tracking and remediation.",
              "Finance/Procurement: Coupa, Ariba for SaaS spend insights.",
              "IAM: Azure AD, Okta, SailPoint for entitlement tracking."
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-nudges users when unsanctioned tools detected.",
              "Escalates to IT security/compliance if high-risk Shadow IT usage occurs.",
              "Governance dashboards presented to CIO, CISO, and audit committees."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Detection updates within 24 hours for logs, near real-time for cloud/CASB integrations.",
              "Accuracy: >90% identification accuracy of unmanaged tools.",
              "Coverage: % of SaaS usage visible and governed."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Ensures access policies enforce blocking/escalation safely.",
              "Data Handling: Log data encrypted, sensitive metadata anonymized as required.",
              "Audit & Traceability: Every detection, user nudge, and policy decision logged.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per user, per detected app, or outcome-based (per % reduction in Shadow IT exposure)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "15–25% SaaS spend savings via consolidation and rationalization.",
              "20–30% fewer compliance risks avoids millions in fines.",
              "Lowered incident volume by reducing unmanaged app vulnerabilities."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "8–10 weeks for initial log ingestion, 12–14 weeks for enterprise SaaS visibility."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Shadow IT baseline report delivered.",
              "60 days: Automated nudges + policy-aligned remediation flows live.",
              "90 days: Enterprise-wide dashboards for governance and risk reduction."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to unmanaged APIs, shadow data flows, and generative AI tools."
          },
          {
            title: "Multi-Agent Interoperability",
            content: ": Works with Language Localizer Agent (translating policy comms), Campaign Orchestrator Agent (self-service adoption campaigns), and VIP White Glove Agent (special handling for exec Shadow IT use)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive Shadow IT analysis — forecasting likely adoption hotspots before they occur."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Network log scan, CASB alert, cloud app discovery, SaaS spend report update.",
      workingPlane: "Orchestrates discovery across logs, CASB, IAM, and finance platforms.",
      memory: "Retains recurring Shadow IT adoption patterns and previous remediation effectiveness.",
      reasoning: "Applies policy + ML to classify unmanaged tools, risk score, and suggest paths.",
      execution: "Surfaces Shadow IT in dashboards, nudges users, escalates high-risk cases, or enforces policy.",
      kpis: "% reduction in Shadow IT exposure, SaaS spend savings, compliance risk avoided."
    }
  },
  'license-optimization-advisor-agent': {
    title: "License Optimization Advisor Agent",
    description: "The License Optimization Advisor Agent continuously analyzes SaaS license usage to rightsizes entitlements and provide optimization recommendations, drastically reducing license waste, overspending, and compliance gaps.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–30% reduction in SaaS license spend.",
          "15–20% improved utilization of purchased licenses.",
          "25–35% fewer compliance risks by maintaining entitlement-to-usage alignment.",
          "IT and Finance collaboration through transparent dashboards.",
          "Direct impact on budget efficiency, freeing funds for innovation."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "30–40% of SaaS licenses in large enterprises are underutilized or unused (Gartner benchmark).",
              "IT teams lack visibility into actual usage across hundreds of SaaS apps.",
              "Finance overspends on “shelfware” — licenses bought but never activated.",
              "Users are often assigned premium licenses when cheaper tiers would suffice.",
              "Compliance risks emerge when license reallocations aren’t logged properly."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "20–30% reduction in SaaS license spend.",
              "15–20% improved utilization of purchased licenses.",
              "25–35% fewer compliance risks by maintaining entitlement-to-usage alignment.",
              "IT and Finance collaboration through transparent dashboards.",
              "Direct impact on budget efficiency, freeing funds for innovation."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Optimize Epic, Microsoft 365, and Zoom licenses across clinicians and admin staff.",
              "Finance: Rightsize Salesforce, Workday, and Bloomberg licenses by usage tier.",
              "Airline Ops: Reallocate scheduling, maintenance, and collaboration tool licenses across staff shifts.",
              "ITSM: Optimize ServiceNow and Jira licenses by role and usage patterns.",
              "Cybersecurity: Identify unused endpoint/security tool licenses and reallocate."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Analyzes SaaS license usage: logins, session times, feature utilization.",
              "Identifies unused, underutilized, and misaligned licenses.",
              "Maps roles to optimal license tiers (basic vs premium).",
              "Automates reallocation/downgrade workflows with approval paths.",
              "Generates savings dashboards for IT, Finance, and Procurement."
            ]
          },
          {
            title: "Integrations",
            content: [
              "SaaS Platforms: Microsoft 365, Salesforce, Workday, Zoom, ServiceNow, Atlassian, SAP, Oracle.",
              "IAM: Azure AD, Okta, Ping, SailPoint (for entitlement mapping).",
              "Finance/Procurement: Coupa, Ariba for license spend alignment.",
              "ITSM: ServiceNow, Jira for workflow automation of reallocations."]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Low-risk reallocations can be auto-executed.",
              "High-value or critical license changes require IT/Finance approval.",
              "All optimizations logged for audit and governance."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: License optimization dashboards updated daily.",
              "Accuracy: >90% match between recommendation and actual business usage.",
              "Savings impact: $ savings identified and realized per quarter."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Ensures reallocations respect entitlements and access rights.",
              "Data Handling: License telemetry encrypted in transit and at rest.",
              "Audit & Traceability: Every license change logged with justification and approvals.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per user, per SaaS app managed, or outcome-based (% savings achieved)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–30% SaaS spend savings in year one.",
              "ROI in <6 months via avoided shelfware and optimized tiers.",
              "Reduced compliance risk from over-assignment of premium licenses."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for top SaaS platforms; 12–14 weeks for enterprise SaaS portfolio."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot optimization for 2–3 SaaS platforms.",
              "60 days: Expand to top 10 SaaS apps.",
              "90 days: Enterprise-wide license optimization dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand from SaaS to cloud infrastructure (IaaS/PaaS licensing optimization)."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Shadow IT Detection Agent (to identify unapproved apps) and Campaign Orchestrator Agent (to drive user adoption of optimized tools)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive license demand modeling (forecasting license needs before renewals)."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Daily license telemetry ingestion, SaaS renewals approaching, or role changes detected.",
      workingPlane: "Orchestrates license telemetry, optimization analysis, and IT/Finance workflows.",
      memory: "Stores historical usage, renewal patterns, and prior optimizations.",
      reasoning: "Analyzes usage to recommend downgrades, reallocations, or consolidations.",
      execution: "Automates license reallocation/downgrade workflows with audit trails.",
      kpis: "SaaS spend savings %, license utilization %, compliance alignment, ROI achieved."
    }
  },
  'app-compatibility-coach-agent': {
    title: "App Compatibility Coach Agent",
    description: "The App Compatibility Coach Agent continuously scans for version conflicts (apps, plug-ins, browsers), flags risks, and proactively recommends or executes necessary upgrades to prevent errors and ensure application compatibility.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "30–40% reduction in tickets tied to app version mismatches.",
          "20–25% faster adoption of required upgrades (OS, browser, or app releases).",
          "15–20% lower compliance risk from unpatched or unsupported apps.",
          "Improved uptime and user satisfaction by keeping apps compatible and secure.",
          "Reduced rollout delays for major upgrades and patches."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "25–35% of service desk tickets are caused by outdated or incompatible app versions.",
              "End users are unaware of version requirements until failure occurs.",
              "IT wastes time firefighting version conflicts instead of managing upgrades proactively.",
              "Compatibility issues create compliance risks when security patches are missing.",
              "Major rollouts (Windows, iOS, Epic, SAP upgrades) stall due to app-version incompatibility."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "30–40% reduction in tickets tied to app version mismatches.",
              "20–25% faster adoption of required upgrades (OS, browser, or app releases).",
              "15–20% lower compliance risk from unpatched or unsupported apps.",
              "Improved uptime and user satisfaction by keeping apps compatible and secure.",
              "Reduced rollout delays for major upgrades and patches."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Keeps Epic, Cerner, or imaging software aligned with OS patches.",
              "Finance: Ensures trading apps or reporting tools align with browser/OS versions.",
              "Airline Ops: Ensures scheduling and gate systems remain compatible during updates.",
              "ITSM: Reduces backlog of “this app won’t run on my system” tickets.",
              "Cybersecurity: Reinforces patch hygiene by ensuring apps stay current with supported versions."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Scans app, OS, and plug-in versions across endpoints.",
              "Correlates with vendor support matrices to flag conflicts.",
              "Provides upgrade recommendations or auto-remediation workflows.",
              "Nudges users to install updates with step-by-step guidance.",
              "Generates dashboards for IT leaders showing compatibility health."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Endpoint management: Intune, SCCM, JAMF, Workspace ONE.",
              "Patch management: Ivanti, BigFix, Microsoft WSUS.",
              "ITSM: ServiceNow, Jira Service Management, BMC.",
              "DEX: Nexthink, Aternity for telemetry on degraded experiences.",
              "Vendor feeds: Microsoft, Apple, VMware, Epic, SAP, Salesforce version requirements."]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-nudges and updates for low-risk apps.",
              "Escalates high-risk conflicts (EHR, ERP, finance apps) to IT with full context.",
              "Provides rollback paths if upgrades cause further issues."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Compatibility scans completed daily; nudges within <1 hour of detection.",
              "Accuracy: >95% detection of version mismatches and conflicts.",
              "Adoption rate: % of users upgraded after first nudge."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Enforces upgrades only within entitlement and policy rules.",
              "Data Handling: Telemetry encrypted in transit/at rest; anonymized usage data for reporting.",
              "Audit & Traceability: Each flagged conflict, recommendation, and upgrade logged.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per user, per app monitored, or outcome-based (% reduction in conflicts)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "30–40% fewer tickets from app-version mismatches saves millions annually.",
              "Faster patch adoption reduces cyber risk exposure costs.",
              "Reduced rollout delays improves time-to-value for IT investments."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for initial integration, 12–14 weeks for full enterprise version coverage."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot scans for top 5 apps.",
              "60 days: Nudge and remediation flows live for top 10 apps.",
              "90 days: Enterprise-wide compatibility dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend to mobile apps, VDI environments, and industry-specific apps."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Proactive Nudge Agent (warns before failures), License Optimization Advisor (rightsizing by version tier), and Journey Map Builder Agent (maps failure journeys caused by incompatibility)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive compatibility analysis for upcoming OS or app releases."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Daily version scans, user app launch failures, or vendor release notifications.",
      workingPlane: "Orchestrates version scanning, compatibility mapping, and remediation flows.",
      memory: "Remembers recurring incompatibilities and prior resolutions.",
      reasoning: "Correlates version mismatches with vendor matrices, suggests fixes.",
      execution: "Nudges users, pushes updates, or escalates to IT with context.",
      kpis: "% ticket reduction, upgrade adoption rates, patch compliance %, CSAT impact."
    }
  },
  'digital-twin-persona-simulator-agent': {
    title: "Digital Twin Persona Simulator Agent",
    description: "The Digital Twin Persona Simulator Agent creates digital twins of real user personas (e.g., clinicians, traders) to simulate journeys across ITSM portals and chatbots before release, ensuring workflows are validated and minimizing the risk of adoption failure and ticket spikes.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "30–40% fewer post-release incidents by catching issues pre-launch.",
          "20–25% faster time-to-deployment with automated persona testing.",
          "15–20% higher adoption rates of new portals and chatbots.",
          "Reduced compliance risk through pre-validated workflows (audit trails).",
          "Improved CSAT/employee satisfaction as rollouts “just work” on day one."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "New chatbot/portal flows are often tested in ideal conditions, not real user contexts.",
              "Failures are only discovered after rollout, creating ticket spikes and reputational damage.",
              "Manual UAT is slow, inconsistent, and cannot scale across all roles/devices.",
              "Enterprises lack proactive assurance that flows are compliant, secure, and usable.",
              "CX/DEX trust erodes when users are the “testers” for new features."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "30–40% fewer post-release incidents by catching issues pre-launch.",
              "20–25% faster time-to-deployment with automated persona testing.",
              "15–20% higher adoption rates of new portals and chatbots.",
              "Reduced compliance risk through pre-validated workflows (audit trails).",
              "Improved CSAT/employee satisfaction as rollouts “just work” on day one."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Simulates clinician, nurse, patient portal flows for EHR or MyChart rollouts.",
              "Finance: Validates compliance workflows in trading or ERP portals before go-live.",
              "Airline Ops: Tests boarding/gate agent chatbot flows across devices/locations.",
              "ITSM: Simulates self-service catalog flows across personas before publishing.",
              "Cybersecurity: Tests incident reporting and phishing-intake chatbots with different roles and entitlements."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Builds digital twin personas with role, device, location, and entitlement context.",
              "Simulates end-to-end chatbot/portal journeys before release.",
              "Validates flows against compliance/policy rules (IAM, RBAC, security).",
              "Generates validation reports with pass/fail status and remediation guidance.",
              "Provides integration hooks to CI/CD pipelines for pre-deployment checks."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM portals: ServiceNow, Jira Service Management, BMC, Remedy.",
              "Chatbot frameworks: Microsoft Bot Framework, ServiceNow Virtual Agent, RunOps Concierge Agent.",
              "CI/CD pipelines: GitHub Actions, Jenkins, Azure DevOps.",
              "IAM: Azure AD, Okta, Ping, SailPoint for entitlement validation.",
              "Monitoring/DEX: Nexthink, Aternity for real device/app simulation."]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Simulation failures flagged for remediation before release.",
              "Escalates critical failures (compliance/security blocking) to governance boards.",
              "Provides full audit logs of simulation results."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Simulation runs within <10 minutes for workflow validation.",
              "Accuracy: >95% match between simulated failures and real-world rollout issues.",
              "Coverage: % of portal/chatbot flows validated pre-release."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Simulates flows with entitlement-based access rules applied.",
              "Data Handling: Synthetic personas anonymized; no PII required for simulation.",
              "Audit & Traceability: Every test logged with persona context, steps taken, and results.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per simulated workflow, per persona pack, or outcome-based (% rollout issues prevented)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "30–40% fewer rollout-related tickets saves thousands of staff hours.",
              "Reduced compliance fines by ensuring workflows validated pre-release.",
              "Faster deployment cycles with fewer post-release rollbacks."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "8–10 weeks for persona library setup; 12–14 weeks for integration with CI/CD and ITSM."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot persona simulations for 2–3 workflows.",
              "60 days: Expand to chatbot + portal flows across key roles.",
              "90 days: Enterprise rollout with automated CI/CD pipeline integration."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Add new personas (partners, customers, patients) and new systems (mobile, AR/VR apps)."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Persona Context Agent (role tailoring), Campaign Orchestrator (rollout comms), and Training & Adoption Coach (reinforcement nudges)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive simulation — flagging workflows likely to fail before testing based on historical patterns."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "New portal/chatbot workflow created, change request approved, or CI/CD pipeline trigger.",
      workingPlane: "Orchestrates persona creation, journey simulation, validation, and reporting.",
      memory: "Stores prior persona journeys, failures, and fixes to refine simulations.",
      reasoning: "Applies persona rules, compliance overlays, and failure history to evaluate flows.",
      execution: "Runs simulations, generates reports, auto-blocks release if critical failures found.",
      kpis: "% rollout issues prevented, ticket reduction %, faster time-to-deployment, compliance validation rate."
    }
  },
  'network-connectivity-troubleshooter-agent': {
    title: "Network Connectivity Troubleshooter Agent",
    description: "When users lose connectivity, their productivity halts, flooding service desks with tickets for common issues like Wi-Fi failures, VPN errors, or DNS/proxy blocks. The Network Connectivity Troubleshooter Agent automates standard diagnostics and fixes, restoring connectivity in seconds without manual intervention.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "30–40% reduction in connectivity-related tickets.",
          "25–35% faster MTTR for connectivity issues through automation.",
          "20–30% cost savings by reducing reliance on Tier 1 staffing.",
          "15–20% CSAT uplift by resolving the most visible, disruptive problems instantly.",
          "Reduced IT labor cost by automating Tier 1 troubleshooting.",
          "Stronger compliance posture with VPN and proxy policy enforcement."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "25–35% of all end-user IT tickets are related to connectivity issues.",
              "Users face downtime while waiting for Tier 1 to walk through generic scripts.",
              "Manual troubleshooting misses root causes (e.g., expired VPN certs, proxy misconfigs).",
              "Remote/hybrid work amplifies the impact of connectivity gaps.",
              "Connectivity issues cause disproportionate CSAT drops because they block all work."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "30–40% reduction in connectivity-related tickets.",
              "25–35% faster MTTR for connectivity issues through automation.",
              "20–30% cost savings by reducing reliance on Tier 1 staffing.",
              "15–20% CSAT uplift by resolving the most visible, disruptive problems instantly.",
              "Reduced IT labor cost by automating Tier 1 troubleshooting.",
              "Stronger compliance posture with VPN and proxy policy enforcement."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Auto-fixes clinician workstation VPN drops or captive portal blocks in hospitals.",
              "Finance: Restores trader VPN sessions and DNS sync for secure apps.",
              "Airline Ops:Auto-resolves crew/gate Wi-Fi failures and proxy configs across airports.",
              "ITSM:Standardizes self-healing for the top volume connectivity tickets.",
              "Cybersecurity: Enforces VPN and proxy policy compliance while troubleshooting."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Wi-Fi: reconnects, resets adapters, flushes caches, updates profiles.",
              "DNS: flush/resync, reset configs, detect misrouting.",
              "VPN: validates certs, re-syncs profiles, restarts services, updates clients.",
              "Proxy: checks mappings, resets configs, applies policy defaults.",
              "Captive portal: forces re-auth/login flows, resets session cookies.",
              "Telemetry collection: logs device state before and after remediation."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Endpoint management: Intune, MEM, SCCM, JAMF, Workspace ONE",
              "Network tools: Cisco ISE, Aruba, Zscaler, Palo Alto Prisma, Netskope",
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "DEX: Nexthink, Aternity for endpoint telemetry correlation",
              "Collaboration: Teams, Slack for self-service fix triggers"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Executes low-risk fixes automatically.",
              "Escalates high-risk or repeated failures to network/SOC teams.",
              "Provides dashboards to network operations for trend analysis."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Fixes executed within <60 seconds of trigger.",
              "Accuracy: >90% resolution success rate for supported failure types.",
              "Coverage: % of connectivity issues auto-resolved."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "Identity & Access Guardrails: Ensures VPN/proxy resets align with Zero Trust and RBAC.",
              "Data Handling: Telemetry/logs encrypted in transit and at rest.",
              "Audit & Traceability: Every action logged with justification, outcome, and device context.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, NIST 800-53."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per device, per remediation, or outcome-based (% connectivity tickets reduced)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "30–40% fewer connectivity tickets = significant Tier 1 savings.",
              "Faster MTTR avoids downtime costs (hundreds per employee/hour in high-stakes industries).",
              "Reduced security risk by enforcing VPN/proxy compliance."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for Wi-Fi/DNS/VPN packs; 12–14 weeks for full enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot Wi-Fi and DNS remediations.",
              "60 days: Add VPN and proxy packs.",
              "90 days: Enterprise rollout with dashboards and ROI reporting."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to mobile/IoT devices and cloud SaaS connectivity."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Proactive Nudge Agent (warn of weak Wi-Fi), Device Remediation Agent (fix endpoint drivers), and Journey Map Builder Agent (map failure paths)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive connectivity — anticipate VPN/Wi-Fi failures before user impact."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "ITSM ticket, DEX telemetry, monitoring alert, user self-service trigger.",
      workingPlane: "Orchestrates endpoint/network diagnostics and remediation flows.",
      memory: "Stores historical connectivity failures and successful fixes by role/location.",
      reasoning: "Correlates telemetry, logs, and policies to select right remediation pack.",
      execution: "Executes Wi-Fi/DNS/VPN/proxy/captive portal fixes automatically.",
    },
    kpis: ["Ticket reduction %, MTTR improvement, CSAT uplift, compliance adherence"]
  },
  'journey-map-builder-agent': {
    title: "Journey Map Builder Agent",
    description: "Reconstruct user journeys across systems to find friction points, identify root causes, and suggest fixes, enhancing IT support and user experience.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "30–40% faster root cause analysis (RCA) by reconstructing journeys automatically.",
          "25–35% reduction in repeat failures by applying suggested systemic fixes.",
          "20–25% improvement in adoption and completion rates for key digital workflows.",
          "Stronger governance reporting with clear maps of where digital friction occurs.",
          "Improved employee and customer satisfaction through smoother journeys."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "IT often lacks visibility into the sequence of actions that led to failure, making RCA slow and error-prone.",
              "Service desks rely on anecdotal user descriptions (“It just didn’t work”), which are incomplete.",
              "CX/DEX teams spend weeks manually mapping workflows and failure paths.",
              "Repeated user journeys fail at the same points because systemic fixes are not identified.",
              "Poor user journeys reduce adoption, increase tickets, and create frustration across roles."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "30–40% faster root cause analysis (RCA) by reconstructing journeys automatically.",
              "25–35% reduction in repeat failures by applying suggested systemic fixes.",
              "20–25% improvement in adoption and completion rates for key digital workflows.",
              "Stronger governance reporting with clear maps of where digital friction occurs.",
              "Improved employee and customer satisfaction through smoother journeys."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Reconstructs Epic/MyChart workflows where clinicians or patients abandon due to slowness or error.",
              "Finance: Maps trading or reporting workflows to pinpoint systemic failures in critical applications.",
              "Airline Ops: Reconstructs staff and passenger digital journeys during boarding or scheduling disruptions.",
              "ITSM: Maps the “ticket journey” to show failure points in service catalog or approval flows.",
              "Cybersecurity: Reconstructs compromised journeys (e.g., phishing email → unsafe click → login error) for audit and training."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Captures clickstreams, app usage logs, and telemetry events.",
              "Correlates steps into coherent user journey maps.",
              "Detects friction points and failure nodes.",
              "Suggests fixes: configuration changes, training nudges, system patches, or KB references.",
              "Generates visual journey maps for IT and business reporting."
            ]
          },
          {
            title: "Integrations",
            content: [
              "DEX: Nexthink, Aternity, SysTrack, Microsoft Endpoint Analytics.",
              "Monitoring: Splunk, Datadog, Elastic, AppDynamics.",
              "ITSM: ServiceNow, Jira, BMC (for incident linkage).",
              "Business Apps: Epic, SAP, Salesforce, Workday, Oracle.",
              "Collaboration: Teams, Slack for sharing journey maps and fixes."
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Journeys reconstructed automatically; systemic failures escalated to problem management.",
              "Human-in-loop for validating suggested fixes in sensitive workflows.",
              "Journey maps archived for audits and governance reviews."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Journey maps generated within 5 minutes of failure event.",
              "Accuracy: &gt;90% correct reconstruction of journey steps in supported apps.",
              "Coverage: % of incidents/tickets with reconstructed journeys."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: RBAC ensures only authorized staff can view sensitive journey data.",
              "Data Handling: Personally identifiable data anonymized in journey maps.",
              "Audit &amp; Traceability: Journey steps logged with correlation IDs; fixes tied to incidents.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per journey reconstructed, per user, or outcome-based (% repeat failure reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–35% fewer repeat failures translates to millions saved in downtime and lost productivity.",
              "30–40% faster RCA reduces analyst workload and MTTR.",
              "Higher adoption rates for business-critical workflows increase ROI on IT investments."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "8–10 weeks for initial app journeys, 12–14 weeks for cross-system journeys."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot journey reconstruction for one app (e.g., Salesforce, Epic).",
              "60 days: Expand to multi-app journeys.",
              "90 days: Full enterprise rollout with systemic fix dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to customer journeys (external apps, portals) as well as employee workflows."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with DEX Telemetry Agent (device/app data), Sentiment Analyzer Agent (user feedback), and Proactive Nudge Agent(fix before failure)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive journey mapping (flagging failure-prone paths before they occur)."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Failure detected in app/device telemetry, ticket creation, or user abandonment event (with SLA-backed <5 min map generation and configurable triggers).",
      workingPlane: "Orchestrates capture of user steps, telemetry, and logs into coherent journey maps (with interoperability across ITSM, DEX, and monitoring).",
      memory: "Stores historical journeys and recurring failure patterns for reuse (with configurable retention and anonymization aligned with compliance).",
      reasoning: "Correlates steps to reconstruct journeys, pinpoints friction nodes, and proposes fixes (with explainable mapping and human-in-loop for sensitive workflows).",
      execution: "Generates visual maps, routes proposed fixes into ITSM, and alerts governance teams (with safe execution, rollback, and escalation to SMEs).",
    }
  },
  'vip-white-glove-agent': {
    title: "VIP White Glove Agent",
    description: "VIP users need zero downtime. The White Glove Agent automatically detects, prioritizes, and applies concierge workflows for the fastest, highest-quality resolution.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "50% faster resolution times for VIP incidents and requests.",
          "100% guaranteed priority routing for VIP-related tickets.",
          "Improved executive and clinician satisfaction scores by 20–25%.",
          "Reduced business impact from downtime in high-stakes roles.",
          "Enhanced IT credibility at the board and leadership level."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "VIPs often end up in the same service desk queues as all other users, leading to delays.",
              "Manual VIP tagging is inconsistent, leading to missed escalations.",
              "Service desk agents lack context on VIP status or business criticality.",
              "Poor VIP experiences damage trust in IT, impact critical business operations, and increase risk exposure.",
              "Competitors and regulators view VIP dissatisfaction as a reputational weakness."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "50% faster resolution times for VIP incidents and requests.",
              "100% guaranteed priority routing for VIP-related tickets.",
              "Improved executive and clinician satisfaction scores by 20–25%.",
              "Reduced business impact from downtime in high-stakes roles.",
              "Enhanced IT credibility at the board and leadership level."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Clinicians and C-suite executives get white glove support for EHR and clinical systems.",
              "Finance: Traders and executives receive priority routing for trading platform issues.",
              "Airline Ops: Flight operations staff (pilots, crew schedulers) get instant escalation paths.",
              "ITSM: VIP groups receive proactive monitoring, faster routing, and concierge updates.",
              "Cybersecurity: Executives and privileged users receive faster triage on phishing or compromise alerts."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Detects VIPs based on IAM attributes, HR feeds, or ITSM tagging.",
              "Prioritizes VIP tickets, routing to senior agents or specialized teams.",
              "Triggers concierge flows: proactive comms, white-glove handling, status dashboards.",
              "Monitors VIP devices and apps proactively for degradation (before ticket creation).",
              "Provides executive-ready updates and reporting."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC.",
              "IAM/HR: Azure AD, Okta, Workday, SuccessFactors.",
              "DEX/Monitoring: Nexthink, Aternity, Splunk, Datadog.",
              "Collaboration: Teams, Slack for VIP comms channels.",
              "CRM/Directory: LDAP, Active Directory, VIP contact lists."
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "All VIP issues auto-escalated to Tier 2/3 or specialized concierge teams.",
              "Proactive monitoring of VIP devices and apps triggers alerts before issues escalate.",
              "Human-in-loop for sensitive VIP workflows (e.g., board-level presentations)."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: <1 min ticket routing for VIP incidents, <30 min resolution for Tier 1 issues.",
              "Accuracy: 99.9% detection accuracy of VIP accounts and entitlements.",
              "VIP satisfaction scores (tracked via CSAT/NPS surveys)."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Strict RBAC ensures VIP detection does not overextend privileges.",
              "Data Handling: VIP personal data anonymized where possible, encrypted in transit/at rest.",
              "Audit & Traceability: Logs show why a user was tagged VIP, who handled their issues, and escalation path.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per VIP user, per concierge workflow, or outcome-based (per SLA compliance)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "Faster SLA compliance reduces business impact downtime (e.g., saving $300k+ per hour of trading floor disruption).",
              "Higher executive satisfaction directly improves IT’s credibility and governance posture."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for baseline VIP detection and routing; 12–14 weeks for concierge flows + proactive monitoring."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Identify VIP users and enable priority routing.",
              "60 days: Launch concierge workflows and proactive monitoring.",
              "90 days: Deliver VIP satisfaction dashboards and SLA compliance reports."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend to contractors, partners, or external clients flagged as VIPs."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Concierge Agent (intake), Proactive Nudge Agent (device health prevention), and Journey Map Builder Agent (root cause mapping for VIP issues)."
          },
          {
            title: "Vendor Roadmap",
            content: "Enhancements include predictive VIP monitoring (anticipating VIP issues before they occur) and automated escalation comms to executive assistants."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Ticket creation, user authentication, DEX telemetry degradation, or HR/IAM feed identifying VIP.",
      workingPlane: "Orchestrates VIP detection, priority routing, concierge workflows, and monitoring (with ITSM/IAM interoperability).",
      memory: "Remembers VIPs’ past issues, resolutions, and preferences (with configurable retention and privacy policies).",
      reasoning: "Applies business rules and ML to determine VIP status and required workflow (with explainability and policy overlays).",
      execution: "Routes tickets, initiates concierge workflows, delivers proactive updates, triggers monitoring actions (with safe execution and SME escalation).",
    }
  },
  'cost-to-serve-analyzer-agent': {
    title: "Cost to Serve Analyzer Agent",
    description: "The Cost to Serve Analyzer Agent continuously calculates per-ticket cost, quantifies the ROI of automation, and reports on deflection gains from self-service.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "100% visibility into per-ticket costs across channels and categories.",
          "Demonstrable ROI on automation and deflection initiatives.",
          "15–20% efficiency gain by spotlighting high-cost categories to target for automation.",
          "Improved executive trust in ITSM operations with finance-grade metrics.",
          "Data-driven business cases for continued automation and self-service funding."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Ticket costs are usually estimated, not calculated precisely by category or channel.",
              "Boards and CFOs lack visibility into whether automation or deflection initiatives are delivering ROI.",
              "Service desks remain “black boxes” — costs are pooled, not tied to per-ticket economics.",
              "Inability to show cost-to-serve makes ITSM investment business cases weaker.",
              "Operational teams don’t have financial feedback loops to guide continuous improvement."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "100% visibility into per-ticket costs across channels and categories.",
              "Demonstrable ROI on automation and deflection initiatives.",
              "15–20% efficiency gain by spotlighting high-cost categories to target for automation.",
              "Improved executive trust in ITSM operations with finance-grade metrics.",
              "Data-driven business cases for continued automation and self-service funding."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Shows cost reduction from automating Epic account resets and access provisioning.",
              "Finance: Demonstrates ROI of self-service catalog adoption vs legacy manual fulfillment.",
              "Airline Ops: Quantifies cost savings from automating crew scheduling and kiosk fixes.",
              "ITSM: Provides per-category cost dashboards (password resets vs VPN issues vs device tickets).",
              "Cybersecurity: Tracks ROI of automation for phishing triage and endpoint containment."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Computes cost per ticket by channel (chat, email, phone, portal) and category.",
              "Quantifies automation ROI: savings from tickets auto-resolved vs manual.",
              "Calculates deflection gain: tickets avoided via self-service or knowledge.",
              "Allocates FTE cost vs automation spend by resolution type.",
              "Produces dashboards for IT, Finance, and business leaders."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "Finance/ERP: SAP, Oracle, Workday, Coupa for labor + cost data",
              "Collaboration: Teams, Slack for reporting snapshots",
              "Analytics: Power BI, Tableau for executive dashboards",
              "RunOps Agents: Pulls automation + deflection performance metrics"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Escalates high-cost categories to automation/CoE boards.",
              "Flags negative ROI automation use cases for governance review.",
              "Provides quarterly/annual ROI reporting for CFO/board audiences."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Cost reports refreshed daily/weekly.",
              "Accuracy: >95% alignment with finance actuals after model tuning.",
              "Coverage: % of tickets covered with cost/ROI attribution."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Role-based access to financial data + dashboards.",
              "Data Handling: Finance + ITSM data encrypted in transit and at rest.",
              "Audit & Traceability: Every cost/ROI calculation logged with methodology.",
              "Certifications Supported: SOC2, ISO27001, SOX, HIPAA, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per ticket analyzed, per category, or outcome-based (% savings identified/realized)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "15–20% OpEx reduction from targeting automation at high-cost categories.",
              "Stronger board/CFO alignment through finance-grade reporting."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "8–10 weeks for pilot cost model; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot cost model for 2–3 ticket categories.",
              "60 days: Add automation + deflection ROI calculations.",
              "90 days: Enterprise-wide rollout with CFO-ready dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to SOC/NOC and HR/Facilities desks."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Workforce Forecasting Agent, SLA Watcher Agent, and Shift Left Discovery Agent."
          },
          {
            title: "Vendor Roadmap",
            content: "Enhancements include predictive ROI modeling — forecasts cost savings before deploying new automation."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Daily ticket batch, automation run completion, CFO report cycle.",
      workingPlane: "Orchestrates ITSM + finance data ingestion, cost modeling, ROI computation.",
      memory: "Stores historical costs, ROI outcomes, deflection gains (with adaptive cost modeling).",
      reasoning: "Determines per-ticket cost, ROI of automation, and deflection gain (with explainability and CFO-ready transparency).",
      execution: "Generates cost dashboards, ROI reports, CFO board summaries (with safe execution and audit logging).",
    }
  },
  'first-day-ready-agent': {
    title: "First Day Ready Agent",
    description: "The First Day Ready Agent automates workflows, ensuring every new hire has a device, apps, credentials, comms channels, and training materials ready before their first login.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "100% of new hires provisioned and ready on day one.",
          "30–40% reduction in new hire-related IT tickets.",
          "15–20% faster time-to-productivity for new employees.",
          "Stronger employee satisfaction and retention (positive onboarding experience).",
          "Reduced HR/IT overhead by automating repetitive onboarding steps."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "20–30% of onboarding issues stem from IT not delivering devices or accounts on time.",
              "New hires spend hours/days waiting for access, creating poor first impressions.",
              "HR and IT handoffs are inconsistent, leading to delays or duplicate work.",
              "Lack of day-one readiness negatively impacts productivity, engagement, and retention.",
              "Service desk volume spikes as new hires flood queues with “can’t log in” or “missing app” tickets."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "100% of new hires provisioned and ready on day one.",
              "30–40% reduction in new hire-related IT tickets.",
              "15–20% faster time-to-productivity for new employees.",
              "Stronger employee satisfaction and retention (positive onboarding experience).",
              "Reduced HR/IT overhead by automating repetitive onboarding steps."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Ensures clinicians receive laptops/iPads, EHR access, and secure messaging tools on day one.",
              "Finance: Provides analysts with ERP, compliance systems, and secure comms access.",
              "Airline Ops: Prepares crew devices with scheduling apps, travel portals, and comms channels.",
              "ITSM: Bundles device, app, and portal access for all new hires by role.",
              "Cybersecurity: Enforces MFA enrollment, compliance policies, and security training at onboarding."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Automates device provisioning (imaging, patching, encryption).",
              "Assigns app bundles by role/department (O365, ERP, EHR, CRM, etc.).",
              "Creates comms/collaboration accounts (Teams, Zoom, Slack, email).",
              "Triggers MFA enrollment, policy sync, and compliance posture checks.",
              "Sends onboarding comms (welcome emails, IT orientation, training guides)."
            ]
          },
          {
            title: "Integrations",
            content: [
              "HRIS: Workday, SAP SuccessFactors, Oracle HCM",
              "IAM: Azure AD/Entra ID, Okta, Ping, SailPoint",
              "Endpoint: Intune, MEM, SCCM, JAMF, Workspace ONE",
              "Collaboration: Teams, Outlook, Zoom, Slack",
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "LMS: Docebo, Cornerstone, SuccessFactors for onboarding education"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-flags incomplete or conflicting HR/IT data before provisioning.",
              "Escalates VIP/exec onboarding to White Glove workflows.",
              "Logs onboarding compliance status for IT + HR governance."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: New hire ready within <24 hours of HR trigger.",
              "Accuracy: >95% correct provisioning of apps/devices per role bundle.",
              "Coverage: % of new hires onboarded without IT tickets."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: MFA enrollment, RBAC, and least-privilege provisioning enforced.",
              "Data Handling: HR + IAM data encrypted, sensitive details redacted for audit logs.",
              "Audit & Traceability: All onboarding actions logged with correlation IDs.",
              "Certifications Supported: HIPAA, SOX, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per new hire, per onboarding workflow, or outcome-based (% reduction in new hire tickets)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "30–40% fewer new hire tickets saves Tier 1 costs.",
              "15–20% faster time-to-productivity improves business ROI per employee.",
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for role bundle pilots; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot onboarding automation for 1–2 departments.",
              "60 days: Expand to all new hires, including contractors/partners.",
              "90 days: Enterprise rollout with HR + IT dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to movers (role change) and leavers (automated deprovisioning)."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Access Provisioning Agent, MFA Enrollment Agent, and Campaign Orchestrator Agent."
          },
          {
            title: "Vendor Roadmap",
            content: "Enhancements include predictive readiness — anticipating app/device gaps before day one."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "HRIS “hire event,” ITSM catalog request, or role bundle assignment.",
      workingPlane: "Orchestrates device, app, comms, and LMS workflows.",
      memory: "Stores onboarding bundles by role, geography, business unit (with adaptive refinement).",
      reasoning: "Validates HR/IT inputs, selects onboarding bundle, applies compliance policies (with explainability and human-in-loop for VIP exceptions).",
      execution: "Provisions device, assigns apps, sets up comms, sends onboarding comms (with safe execution and escalation handling).",
    }
  },
  'endpoint-compliance-fix-agent': {
    title: "Endpoint Compliance Fix Agent",
    description: "The Endpoint Compliance Fix Agent continuously checks posture, remediates drift automatically, and generates auditable evidence of enforcement.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–35% fewer compliance audit findings related to endpoint configuration.",
          "20–25% lower incident volume caused by baseline drift vulnerabilities.",
          "Stronger Zero Trust posture by enforcing least privilege and hardened configs.",
          "Improved CSAT by reducing compliance-driven service desk disruptions.",
          "Demonstrable ROI by reducing manual compliance remediation workload."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "30–40% of endpoints show baseline drift within 90 days without enforcement.",
              "Manual compliance checks are slow, expensive, and error-prone.",
              "Drift introduces exploitable vulnerabilities and compliance audit failures.",
              "Lack of standardized evidence trails increases risk during regulatory audits.",
              "Service desks are overwhelmed with compliance-related remediation tickets."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "25–35% fewer compliance audit findings related to endpoint configuration.",
              "20–25% lower incident volume caused by baseline drift vulnerabilities.",
              "Stronger Zero Trust posture by enforcing least privilege and hardened configs.",
              "Improved CSAT by reducing compliance-driven service desk disruptions.",
              "Demonstrable ROI by reducing manual compliance remediation workload."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Enforces HIPAA-aligned endpoint baselines for EHR workstations and mobile devices.",
              "Finance: Maintains PCI-DSS and SOX compliance across trading and reporting devices.",
              "Airline Ops: Ensures flight crew and gate devices remain compliant with NIST/CIS standards.",
              "ITSM: Auto-resolves compliance drift tickets before analysts need to act.",
              "Cybersecurity: Strengthens Zero Trust by validating and remediating endpoint posture."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Validates endpoints against CIS/NIST/HIPAA/PCI/ISO baselines.",
              "Detects and remediates drift (patch installs, GPO/registry resets, service config fixes).",
              "Enforces encryption, MFA, and policy guardrails.",
              "Documents compliance evidence (logs, screenshots, telemetry).",
              "Pushes evidence into ITSM/GRC for audit readiness."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Endpoint: Intune, MEM, SCCM, JAMF, Workspace ONE",
              "Security: CrowdStrike, Defender ATP, Zscaler for posture telemetry",
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "GRC: Archer, ServiceNow GRC, RSA, OneTrust",
              "Analytics: Power BI, Tableau for compliance dashboards"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Executes low-risk remediations automatically.",
              "Escalates high-risk drift (e.g., encryption disabled, admin privilege misuse) to SOC/IT.",
              "Provides dashboards for compliance leaders and auditors."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Drift detected and remediated within <60 seconds of scan.",
              "Accuracy: >95% remediation success rate across baseline packs.",
              "Coverage: % of endpoints continuously validated + compliant."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Enforces RBAC and least-privilege provisioning.",
              "Data Handling: Posture telemetry encrypted in transit and at rest.",
              "Audit & Traceability: Line-item logging of every compliance action with timestamp.",
              "Certifications Supported: HIPAA, PCI-DSS, HITRUST, SOC2, ISO27001, NIST 800-53, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per endpoint monitored, per remediation, or outcome-based (% audit exceptions reduced)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–35% fewer audit exceptions → avoids remediation costs/fines.",
              "20–25% fewer endpoint incidents → reduces security spend and downtime.",
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for baseline template pilots; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot compliance enforcement for one baseline (e.g., CIS Windows).",
              "60 days: Expand to 5–10 baseline categories across endpoints.",
              "90 days: Enterprise-wide enforcement with audit dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to servers, cloud workloads, and containers."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Device Remediation Agent, Release Freeze Sentinel, and Cost to Serve Analyzer Agent."
          },
          {
            title: "Vendor Roadmap",
            content: "Enhancements include predictive compliance — anticipates drift before it occurs and pre-remediates."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Scheduled scans, telemetry drift detection, or ITSM ticket creation.",
      workingPlane: "Orchestrates endpoint posture checks, remediation workflows, and evidence logging.",
      memory: "Stores baseline templates, drift incidents, remediation outcomes (with adaptive compliance model refined by history).",
      reasoning: "Compares posture to baseline, decides remediation vs escalation (with explainability and SME override for critical systems).",
      execution: "Enforces baseline, remediates drift, pushes evidence to ITSM/GRC (with safe execution and rollback).",
    }
  },
  'incident-auto-classification-agent': {
    title: "Incident Auto Classification Agent",
    description: "The Incident Auto Classification Agent uses natural language processing (NLP), historical ticket data, and CMDB context to auto-assign service, CI, priority, and impact fields in real time.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "90%+ classification accuracy for service, CI, priority, and impact.",
          "15–20% faster incident triage → reduced MTTR.",
          "20–25% fewer SLA breaches caused by misrouting.",
          "Stronger IT reporting and analytics due to consistent categorization.",
          "Reduced analyst workload, freeing staff for higher-value tasks."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "25–35% of tickets are misclassified, leading to delays and escalations.",
              "Analysts waste minutes per ticket manually assigning service/CI/priority.",
              "Inconsistent classifications distort reporting and root cause analysis.",
              "Misclassification drives SLA breaches and increases MTTR.",
              "IT leadership lacks trust in metrics when data quality is poor."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "90%+ classification accuracy for service, CI, priority, and impact.",
              "15–20% faster incident triage → reduced MTTR.",
              "20–25% fewer SLA breaches caused by misrouting.",
              "Stronger IT reporting and analytics due to consistent categorization.",
              "Reduced analyst workload, freeing staff for higher-value tasks."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Classifies Epic login errors as “EHR → Access” with “High Impact”.",
              "Finance: Tags SAP reporting issues to the correct CI with business-critical priority.",
              "Airline Ops: Routes crew scheduling outages to the right service group instantly.",
              "ITSM: Normalizes incident data across global service desks.",
              "Cybersecurity: Prioritizes phishing tickets correctly based on enterprise risk model."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Auto-assigns Service, CI, Priority, Impact fields.",
              "Uses NLP/ML to parse incident descriptions and map to CMDB/services.",
              "Applies organization-specific priority/impact matrices.",
              "Surfaces confidence scores for analyst review if below threshold.",
              "Logs classification outcomes for audit + model tuning."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy",
              "CMDB: ServiceNow CMDB, BMC Atrium, Flexera",
              "Monitoring: Splunk, Datadog, Elastic for enriched incident data",
              "Collaboration: Teams, Slack for analyst notifications and confidence challenges"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Tickets with low confidence scores routed to analysts for review.",
              "Escalates unresolved incidents to governance boards for taxonomy updates.",
              "Provides dashboards showing accuracy and confidence trends."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Classification <2 seconds per ticket.",
              "Accuracy: >90% correct assignment after training.",
              "Coverage: % of incidents classified automatically vs manual."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Classification actions tied to authenticated roles.",
              "Data Handling: Ticket text + metadata encrypted in transit and at rest.",
              "Audit & Traceability: Logs show confidence score, chosen service/CI/priority.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per ticket classified, per service/CI, or outcome-based (% SLA/MTTR improvement)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "15–20% faster MTTR → significant productivity and cost savings.",
              "20–25% SLA improvement → reduced penalty risk and higher IT trust.",
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for pilot categories; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot on top 5 incident categories.",
              "60 days: Expand across IT + business-critical services.",
              "90 days: Enterprise rollout with dashboards and ROI tracking."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend to service requests, change categorization, and problem records."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Triage & Enrichment Agent, Escalation Orchestrator Agent, and SLA Watcher Agent."
          },
          {
            title: "Vendor Roadmap",
            content: "Enhancements include predictive classification refinement — adjusting CI/priority based on business impact telemetry."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Incident creation (via chat, email, portal, monitoring alert).",
      workingPlane: "Orchestrates NLP parsing, CMDB mapping, and ticket updates.",
      memory: "Stores historical classifications, analyst corrections, SLA outcomes (improves accuracy over time through reinforcement).",
      reasoning: "Determines best-fit service, CI, priority, and impact (with explainability, SLA-aware reasoning, human override for low confidence).",
      execution: "Updates ITSM ticket fields, routes to correct resolver queue (with safe execution and escalation for low-confidence cases).",
    }
  },
  'first-response-agent': {
    title: "First Response Agent",
    description: "The First Response Agent automatically generates a personalized, user-friendly acknowledgment with expected resolution time (ETA), relevant self-help resources, and escalation paths.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–30% improvement in CSAT due to clear, empathetic acknowledgments.",
          "15–20% reduction in “status check” follow-up calls/emails.",
          "10–15% ticket deflection via embedded self-help/KB links.",
          "Increased trust between IT and business users through proactive communication.",
          "Reduced analyst workload by automating acknowledgments."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Many ITSM tools send cold, generic auto-replies (“Your ticket is logged”).",
              "Lack of ETA or context leaves users frustrated and chasing status updates.",
              "Analysts waste time manually responding to new tickets.",
              "Poor first responses drive CSAT down, regardless of eventual resolution quality.",
              "Missed opportunity to deflect tickets with curated self-help content."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "20–30% improvement in CSAT due to clear, empathetic acknowledgments.",
              "15–20% reduction in “status check” follow-up calls/emails.",
              "10–15% ticket deflection via embedded self-help/KB links.",
              "Increased trust between IT and business users through proactive communication.",
              "Reduced analyst workload by automating acknowledgments."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Acknowledges Epic login issues with ETA and KB for password unlocks.",
              "Finance: Confirms ERP reporting tickets with SLA-backed ETA and links to compliance-approved guides.",
              "Airline Ops: Provides ETA and kiosk troubleshooting steps to staff immediately.",
              "ITSM: Delivers consistent first responses across chat, email, portal, and phone.",
              "Cybersecurity: Confirms phishing reports with ETA and security awareness resources."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Auto-generates ticket acknowledgment messages.",
              "Calculates SLA-aligned ETA based on category/priority.",
              "Embeds self-help KB links or self-service flows.",
              "Supports multi-channel responses (chat, email, SMS, portal, Teams/Slack).",
              "Logs all acknowledgments in ITSM for compliance and reporting."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy",
              "Knowledge: Confluence, SharePoint, ServiceNow KB",
              "Collaboration: Teams, Slack, Outlook, Gmail",
              "Telephony/CCaaS: Genesys, Twilio, Cisco for SMS/voice confirmation",
              "Analytics: Power BI, Tableau for CSAT + SLA dashboards"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Escalates tickets where ETA cannot be computed (new services, missing SLAs).",
              "Provides dashboards for managers showing acknowledgment timeliness and deflection rates.",
              "Human-in-loop review option for VIP/exec tickets."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Acknowledgment sent within <30 seconds of ticket creation.",
              "Accuracy: >90% correct ETA calculation after training.",
              "Coverage: % of incidents with automated, user-friendly first responses."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: User acknowledgment tied to authenticated ticket creation.",
              "Data Handling: Ticket + KB metadata encrypted in transit and at rest.",
              "Audit & Traceability: Logs every acknowledgment, ETA, and KB link offered.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per ticket acknowledged, per user, or outcome-based (% CSAT improvement/deflection)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "10–15% ticket deflection saves Tier 1 costs immediately.",
              "Reduced analyst workload improves productivity by 20–25%.",
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for template + KB integration; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot acknowledgments for top 3 incident categories.",
              "60 days: Expand to all incident categories and channels.",
              "90 days: Enterprise rollout with CSAT + deflection dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand beyond IT to HR, Facilities, and Finance tickets."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Incident Auto Classification Agent, SLA Watcher Agent, and Campaign Orchestrator Agent."
          },
          {
            title: "Vendor Roadmap",
            content: "Enhancements include sentiment-aware first responses that adjust tone if user is frustrated/urgent."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "New ticket created in ITSM.",
      workingPlane: "Orchestrates SLA timer, KB suggestion, and message delivery.",
      memory: "Stores prior ETAs, user/channel preferences, KB effectiveness (adaptive acknowledgment quality improves over time).",
      reasoning: "Selects ETA, self-help links, and message tone (with explainability, SLA overlays, human-in-loop for VIPs).",
      execution: "Sends acknowledgment across channel, updates ticket log, tracks KB usage (with safe execution and escalation handling).",
    }
  },
  'vpn-and-sso-helper-agent': {
    title: "VPN and SSO Helper Agent",
    description: "The VPN and SSO Helper Agent automates the most common diagnostics and fixes, restoring secure access in minutes instead of hours.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–35% reduction in VPN/SSO-related tickets.",
          "20–30% faster resolution through automated cert renewals and token refreshes.",
          "15–20% CSAT uplift by reducing lockouts and downtime.",
          "Reduced security risk from fewer workarounds and misconfigurations.",
          "Improved compliance posture with auditable, standardized fixes."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "15–25% of all access-related tickets involve VPN or SSO failures.",
              "Users lose hours of productivity waiting for manual troubleshooting.",
              "Certificates, tokens, and profiles frequently expire or corrupt, especially with remote/hybrid staff.",
              "Poor VPN/SSO experiences drive risky workarounds (personal email, unsanctioned file shares).",
              "Compliance frameworks (HIPAA, SOX, PCI-DSS, NIST) require consistent, auditable authentication flows."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "25–35% reduction in VPN/SSO-related tickets.",
              "20–30% faster resolution through automated cert renewals and token refreshes.",
              "15–20% CSAT uplift by reducing lockouts and downtime.",
              "Reduced security risk from fewer workarounds and misconfigurations.",
              "Improved compliance posture with auditable, standardized fixes."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Auto-renews clinician VPN certs, repairs Epic/Citrix SSO profiles.",
              "Finance: Refreshes tokens for trading apps to prevent mid-session lockouts.",
              "Airline Ops: Keeps crew and gate agents online by repairing VPN tunnels in real time.",
              "ITSM: Handles AD/Entra ID–linked SSO profile resets without escalation.",
              "Cybersecurity: Ensures token refreshes and cert renewals comply with Zero Trust guardrails."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "VPN: Tunnel tests, profile repair, client reinstallation, DNS/proxy resets.",
              "SSO: Token refresh, session reset, profile rebuild, cache clear.",
              "Certificates: Renewal and rebind of expired VPN/SSO certs.",
              "Telemetry: Collects logs, error codes, and session data before/after fix.",
              "Reporting: Updates ITSM with resolution steps and proof of remediation."
            ]
          },
          {
            title: "Integrations",
            content: [
              "IAM: Entra ID, Okta, Ping, SailPoint",
              "VPN platforms: Cisco AnyConnect, Palo Alto GlobalProtect, Zscaler, F5",
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "Endpoint management: Intune, MEM, SCCM, Workspace ONE",
              "DEX/Monitoring: Nexthink, Aternity for connectivity telemetry"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Executes low-risk fixes automatically (token refresh, tunnel test).",
              "Escalates high-risk actions (cert/key changes for privileged roles).",
              "Provides dashboards for governance and compliance review."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: VPN/SSO remediation in <2 minutes for supported issues.",
              "Accuracy: >90% resolution success across common failure modes.",
              "Coverage: % of VPN/SSO issues handled without escalation."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: MFA validation, RBAC, and Zero Trust enforcement at reset/renewal.",
              "Data Handling: Logs encrypted in transit/at rest; no plaintext credential exposure.",
              "Audit & Traceability: Each cert/token/SSO action logged with user ID, device, and justification.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, NIST 800-63."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per user, per remediation, or outcome-based (% ticket reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–35% fewer VPN/SSO tickets = significant Tier 1/2 cost savings.",
              "Faster MTTR avoids downtime costs worth hundreds per employee per hour.",
              "Reduced risk of breaches from expired certs and misconfigured tokens."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for top VPN/SSO platforms; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot cert renewal + token refresh packs.",
              "60 days: Expand to profile/tunnel repair workflows.",
              "90 days: Enterprise-wide rollout with dashboards and ROI reporting."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to passwordless and conditional access troubleshooting."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Password Reset Agent (account lifecycle), MFA Enrollment Agent (identity assurance), and Network Troubleshooter Agent (connectivity)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive token expiry detection and proactive certificate renewal."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "VPN/SSO failure, ticket creation, telemetry anomaly, cert/token expiry alert.",
      workingPlane: "Orchestrates VPN + SSO diagnostics and remediation workflows.",
      memory: "Stores recurring VPN/SSO failures and successful resolutions.",
      reasoning: "Diagnoses issue type (cert, profile, tunnel, token) and selects fix pack.",
      execution: "Executes renewals, repairs, and refreshes; validates session health post-fix.",
    }
  },
  'release-note-summarizer-agent': {
    title: "Release Note Summarizer Agent [Converts vendor notes into change tasks and tests]",
    description: "The Release Note Summarizer Agent automates this work by analyzing vendor release notes, extracting relevant changes, and converting them into structured change tasks, test cases, and risk notes that can be consumed directly in ITSM and DevOps workflows.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "50–70% reduction in analyst time spent parsing release notes.",
          "Faster change readiness: structured tasks ready in hours, not days.",
          "20–25% fewer missed fixes or untested vendor changes.",
          "Stronger audit posture with evidence trail from notes → tasks → tests.",
          "Improved CAB efficiency by providing concise, structured release summaries."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Release notes are often 50+ pages of jargon with mixed relevance.",
              "Engineers waste time deciphering notes instead of executing changes.",
              "Critical fixes and high-risk updates are missed due to human oversight.",
              "CABs lack structured evidence of what’s in scope and what needs testing.",
              "Auditors require traceability from vendor release → change → test results, which is rarely maintained."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "50–70% reduction in analyst time spent parsing release notes.",
              "Faster change readiness: structured tasks ready in hours, not days.",
              "20–25% fewer missed fixes or untested vendor changes.",
              "Stronger audit posture with evidence trail from notes → tasks → tests.",
              "Improved CAB efficiency by providing concise, structured release summaries."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Summarizes Epic/Cerner release notes into EHR-specific change tasks and validation tests.",
              "Finance: Converts SAP/Oracle ERP release notes into structured compliance-related changes.",
              "Airline Ops: Extracts critical updates in scheduling or check-in systems and creates tasks/tests.",
              "ITSM: Standardizes parsing of Microsoft/VMware/Citrix patch notes into CAB-ready summaries.",
              "Cybersecurity: Flags urgent CVE-related fixes in vendor patch notes with risk scores."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Ingests vendor release notes (PDF, HTML, KB articles, RSS feeds).",
              "Summarizes new features, fixes, risks, and deprecations.",
              "Maps notes to affected services/CI from CMDB.",
              "Generates ITSM change tasks and DevOps test cases automatically.",
              "Produces concise CAB-ready summaries with risk context."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy",
              "DevOps: Jenkins, GitHub Actions, Azure DevOps, GitLab",
              "CMDB: ServiceNow CMDB, BMC Atrium for service/CI mapping",
              "Knowledge: Confluence, SharePoint for storing summaries",
              "Monitoring/Security: CVE/NVD databases for patch risk mapping"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-drafts tasks/tests; CAB or SMEs validate before deployment.",
              "Escalates urgent/high-risk vendor patches to compliance/security leaders.",
              "Dashboards track coverage and adoption of release-driven tasks."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Summaries + tasks/tests produced within <4 hours of note ingestion.",
              "Accuracy: >90% relevance match after training on vendor-specific releases.",
              "Coverage: % of vendor releases ingested and converted into ITSM workflows."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Access limited to approved IT/release roles.",
              "Data Handling: Vendor notes + generated tasks encrypted in transit/at rest.",
              "Audit & Traceability: Full trail from vendor release → summary → CAB notes → test execution.",
              "Certifications Supported: HIPAA, SOX, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per release ingested, per task/test generated, or outcome-based (% readiness acceleration)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "50–70% analyst time saved parsing release notes.",
              "20–25% fewer missed or untested vendor changes → fewer outages.",
              "Audit-readiness reduces compliance remediation costs."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for pilot vendors; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot with Microsoft/VMware notes.",
              "60 days: Add ERP/EHR vendor notes.",
              "90 days: Enterprise rollout with CAB-ready summaries + ITSM workflows."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to SaaS release feeds (Salesforce, ServiceNow, Atlassian, etc.)."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Change Risk Scoring Agent (adds risk context), Change Window Optimizer Agent (schedules updates), and Problem Candidate Harvester Agent (links new vendor bugs to Problems/KEDB)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive release summarization — proactively highlight notes with highest SLA/impact risk before CAB meetings."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Vendor release note published (PDF, RSS, HTML, KB update).",
      workingPlane: "Orchestrates note ingestion, summarization, CI mapping, and task/test creation.",
      memory: "Stores past release summaries, mapped tasks/tests, failure history.",
      reasoning: "Distills release notes, maps to CI/services, generates risk/context.",
      execution: "Creates ITSM change tasks + DevOps test cases; distributes CAB summary.",
    }
  },
  'request-fulfillment-orchestrator-agent': {
    title: "Request Fulfillment Orchestrator Agent [End-to-end catalog execution with approvals]",
    description: "The Request Fulfillment Orchestrator Agent automates the process end-to-end: validating catalog requests, routing approvals, orchestrating fulfillment actions, and updating the ticket with completion and evidence.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "30–40% faster request fulfillment time (from days → hours).",
          "20–25% reduction in request-related tickets needing escalation.",
          "Lower IT labor costs by automating repetitive approvals and actions.",
          "Improved compliance posture with evidence of approval and fulfillment logged.",
          "CSAT uplift by delivering consumer-grade catalog request experience."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "40–60% of tickets are catalog requests, but many still require manual triage/approvals.",
              "Requests are delayed due to inconsistent approvals or fulfillment steps spread across multiple teams.",
              "High-cost analysts waste time pushing catalog requests through “swivel-chair” processes.",
              "Users perceive IT as slow and bureaucratic due to long fulfillment timelines.",
              "Compliance risks arise when approvals are bypassed or fulfillment actions not logged."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "30–40% faster request fulfillment time (from days → hours).",
              "20–25% reduction in request-related tickets needing escalation.",
              "Lower IT labor costs by automating repetitive approvals and actions.",
              "Improved compliance posture with evidence of approval and fulfillment logged.",
              "CSAT uplift by delivering consumer-grade catalog request experience."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Automates EHR app provisioning requests with compliance-logged approvals.",
              "Finance: Orchestrates ERP role assignments and SOX-sensitive group approvals.",
              "Airline Ops: Handles crew access, kiosk app requests, and shift-related provisioning.",
              "ITSM: Provides end-to-end automation for top 20 catalog items (software installs, O365 group access, VPN requests).",
              "Cybersecurity: Ensures approvals + logging for entitlements and privileged access."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Validates incoming catalog requests against policy and role entitlements.",
              "Routes for manager/compliance approval when required.",
              "Automates fulfillment actions: IAM updates, app installs, device provisioning, license assignment.",
              "Provides closed-loop ITSM updates with evidence (logs, screenshots, timestamps).",
              "Tracks SLA adherence and user communication."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "IAM: Azure AD/Entra ID, Okta, SailPoint, Ping",
              "Endpoint: Intune, MEM, SCCM, JAMF, Workspace ONE",
              "Collaboration: Teams, Slack for user notifications + approvals",
              "ERP/HR: Workday, SAP SuccessFactors for entitlement validation"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-approves low-risk requests; escalates exceptions to managers/CAB.",
              "Tracks pending approvals and sends reminders to approvers.",
              "Dashboards show SLA performance, pending requests, and bottlenecks."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Fulfillment SLA adherence >95%.",
              "Accuracy: >90% successful automated fulfillment without rework.",
              "Coverage: % of catalog items fully automated."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Enforces RBAC, SoD, and compliance approvals.",
              "Data Handling: Catalog + entitlement data encrypted in transit/at rest.",
              "Audit & Traceability: Line-item logs of approvals, actions, timestamps, and request owners.",
              "Certifications Supported: HIPAA, SOX, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per fulfilled request, per catalog item automated, or outcome-based (% time/cost reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "30–40% faster request fulfillment = higher productivity + user trust.",
              "Lower IT labor costs by freeing Tier 1/2 from repetitive work.",
              "Reduced audit remediation costs by automating compliance logging."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for top 10 catalog items; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot automation for top 5 catalog items.",
              "60 days: Add approvals + fulfillment for access/software provisioning.",
              "90 days: Enterprise rollout with SLA dashboards + ROI metrics."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand beyond IT to HR, Finance, and Facilities catalogs."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Access Provisioning Agent (entitlements), MFA Enrollment Agent (identity setup), and Cost to Serve Analyzer Agent (tracks fulfillment cost/ROI)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive request fulfillment — preemptively fulfilling recurring requests before submission."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "User submits catalog request in ITSM.",
      workingPlane: "Orchestrates approval chains, IAM/endpoint actions, SLA timers.",
      memory: "Stores request history, approval patterns, automation outcomes.",
      reasoning: "Determines required approvals and fulfillment steps based on policy + risk.",
      execution: "Executes provisioning, software installs, comms setup, request closure.",
    }
  },
  'approval-gatekeeper-agent': {
    title: "Approval Gatekeeper Agent [Verifies SoD, risk thresholds, approvals, and audit trails]",
    description: "The Approval Gatekeeper Agent ensures approvals are validated against policy: enforcing separation of duties (SoD), checking risk thresholds, verifying approver authority, and documenting evidence in an immutable audit trail.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "100% compliance with SoD, approval authority, and audit requirements.",
          "20–25% reduction in audit findings tied to weak approval controls.",
          "Improved SLA adherence by aligning approvals with risk thresholds.",
          "Increased business trust in ITSM approvals as a control mechanism.",
          "Reduced CAB and compliance overhead by automating enforcement."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "SoD violations (e.g., same person requesting and approving) are a common audit finding.",
              "Risk thresholds are applied inconsistently across services and change categories.",
              "Approvers often lack context (risk score, SLA impact) → decisions are blind.",
              "Audit trails for approvals are incomplete or missing, exposing compliance gaps.",
              "Rubber-stamp approvals weaken governance and increase risk of failed changes or toxic access."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "100% compliance with SoD, approval authority, and audit requirements.",
              "20–25% reduction in audit findings tied to weak approval controls.",
              "Improved SLA adherence by aligning approvals with risk thresholds.",
              "Increased business trust in ITSM approvals as a control mechanism.",
              "Reduced CAB and compliance overhead by automating enforcement."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Ensures access requests for Epic/Cerner entitlements meet SoD and compliance rules.",
              "Finance: Enforces SOX-compliant approval chains for ERP/CRM changes.",
              "Airline Ops: Verifies approver authority for mission-critical scheduling/gate systems.",
              "ITSM: Validates all change, request, and access approvals against SLA + risk thresholds.",
              "Cybersecurity: Blocks toxic role combinations (e.g., developer + production approver)."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Validates approver identity, authority, and role.",
              "Checks for SoD conflicts (requester = approver, or toxic role combos).",
              "Compares against configurable risk thresholds (low = auto-approve, high = escalate).",
              "Generates immutable audit record with timestamp, approver, rationale.",
              "Routes exceptions to CAB, compliance, or governance boards."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy",
              "IAM: Azure AD/Entra ID, Okta, SailPoint for entitlement + approver role checks",
              "GRC: Archer, OneTrust, ServiceNow GRC for audit integration",
              "Collaboration: Teams, Slack for approval alerts and exception routing",
              "Analytics: Power BI, Tableau for compliance dashboards"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Blocks invalid approvals in real time.",
              "Escalates exceptions to CAB or compliance leaders.",
              "Governance dashboards show approval compliance rates + violations prevented."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Approval validation in <5 seconds.",
              "Accuracy: >95% enforcement of SoD and authority rules after tuning.",
              "Coverage: % of approvals validated vs bypassed."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Approval actions tied to RBAC and approver authority levels.",
              "Data Handling: Approval + request metadata encrypted in transit/at rest.",
              "Audit & Traceability: Full line-item logs for every approval decision.",
              "Certifications Supported: SOX, HIPAA, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per approval validated, per service line, or outcome-based (% audit findings reduced)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–25% fewer audit findings tied to approvals = reduced remediation cost.",
              "SLA adherence improved by risk-aware auto-approvals.",
              "Reduced CAB workload = higher SME productivity."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for approval policy pilots; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot SoD enforcement for access requests.",
              "60 days: Expand to change approvals with risk thresholds.",
              "90 days: Enterprise rollout with compliance dashboards + audit-ready logs."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to HR/Finance approvals (expense, procurement) beyond IT."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Request Fulfillment Orchestrator Agent (catalog requests), Change Risk Scoring Agent (risk thresholds), and Release Freeze Sentinel Agent (freeze enforcement)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive approval risk — flagging systemic weak links in approval chains before audit gaps occur."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Request/change approval step triggered in ITSM/IAM.",
      workingPlane: "Orchestrates policy validation, SoD checks, risk scoring, escalation routing.",
      memory: "Stores past approvals, violations, exceptions, and audit outcomes.",
      reasoning: "Validates SoD, approver authority, risk thresholds, escalation needs.",
      execution: "Blocks or approves request/change, routes exceptions, logs evidence.",
    }
  },
  'cmdb-sync-agent': {
    title: "CMDB Sync Agent [Aligns discovered data to CIs, relationships, and lifecycle states]",
    description: "The CMDB Sync Agent continuously reconciles discovered data with CIs, relationships, and lifecycle states, ensuring the CMDB remains accurate and audit-ready.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–30% improvement in incident classification and routing through accurate CIs.",
          "25–35% faster RCA and change risk scoring due to reliable service relationships.",
          "15–20% reduction in compliance/audit issues tied to asset mismanagement.",
          "Improved trust in CMDB as a “single source of truth”.",
          "Reduced IT labor costs by automating reconciliation tasks."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "60–70% of CMDBs are considered “unreliable” due to data drift (Gartner).",
              "Manual reconciliation of discovery data is error-prone and time-consuming.",
              "Service relationships are incomplete, leading to misrouted incidents and failed impact analysis.",
              "Lifecycle states (commissioned, active, retired) are poorly managed, creating compliance risk.",
              "Poor CMDB quality undermines ITSM processes like change risk scoring and SLA management."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "20–30% improvement in incident classification and routing through accurate CIs.",
              "25–35% faster RCA and change risk scoring due to reliable service relationships.",
              "15–20% reduction in compliance/audit issues tied to asset mismanagement.",
              "Improved trust in CMDB as a “single source of truth”.",
              "Reduced IT labor costs by automating reconciliation tasks."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Keeps EHR servers, VDI, and clinical device CIs synchronized with actual deployments.",
              "Finance: Maintains ERP/CRM system relationships for compliance-driven change risk analysis.",
              "Airline Ops: Aligns crew scheduling and gate systems CIs with real-world infra and apps.",
              "ITSM: Ensures CI accuracy for incident, problem, and change workflows.",
              "Cybersecurity: Validates endpoint/device lifecycle states to enforce Zero Trust."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Ingests and reconciles data from discovery tools and asset systems.",
              "Aligns discovered attributes to CIs (CPU, OS, patch level, ownership, etc.).",
              "Validates and corrects CI-to-service and CI-to-CI relationships.",
              "Updates lifecycle states (commissioned, in-service, retired, disposed).",
              "Logs reconciliation outcomes with audit evidence."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM/CMDB: ServiceNow CMDB, BMC Atrium, Flexera, Remedy",
              "Discovery: ServiceNow Discovery, SCCM, Intune, JAMF, Qualys, Tanium",
              "Cloud: AWS Config, Azure Resource Graph, GCP Asset Inventory",
              "Asset/ERP: SAP, Workday, Oracle for lifecycle and ownership data",
              "Analytics: Power BI, Tableau for CMDB health dashboards"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-applies low-risk reconciliations.",
              "Escalates high-risk conflicts (e.g., ownership mismatch) to admins.",
              "Dashboards track CI accuracy, lifecycle drift, and reconciliation exceptions."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Sync updates within <5 minutes of discovery feed ingestion.",
              "Accuracy: >95% attribute + relationship alignment after training.",
              "Coverage: % of CIs reconciled vs orphaned."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Updates tied to RBAC roles with approval thresholds for high-risk changes.",
              "Data Handling: Discovery + CMDB data encrypted in transit/at rest.",
              "Audit & Traceability: All reconciliations logged with before/after state + timestamp.",
              "Certifications Supported: HIPAA, SOX, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per CI reconciled, per discovery integration, or outcome-based (% CMDB accuracy improvement)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–30% fewer misrouted incidents = SLA/MTTR gains.",
              "Reduced audit penalties from lifecycle mismanagement.",
              "IT staff hours saved from manual CMDB reconciliation."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for pilot services; 12–14 weeks for enterprise CMDB coverage."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot sync for top 100 critical CIs.",
              "60 days: Expand to cloud + endpoint discovery sources.",
              "90 days: Enterprise rollout with dashboards + audit logs."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend sync to SaaS (Salesforce, Workday, O365) and IoT/OT devices."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Change Risk Scoring Agent (uses CI health for scoring), Root Cause Pattern Miner Agent (links logs to CIs), and Endpoint Compliance Fix Agent (feeds compliance state)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive drift detection — forecasting when CI accuracy will degrade without intervention."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Discovery data ingestion, CI attribute drift detected, lifecycle threshold breach.",
      workingPlane: "Orchestrates discovery → CI reconciliation → lifecycle state updates.",
      memory: "Stores reconciliation history, attribute conflicts, lifecycle rules.",
      reasoning: "Determines best source of truth, validates relationships, updates lifecycle.",
      execution: "Updates CMDB CIs, lifecycle, relationships, logs evidence.",
    }
  },
  'discovery-normalization-agent': {
    title: "Discovery Normalization Agent [Normalizes names, versions, models, vendors]",
    description: "The Discovery Normalization Agent standardizes names, versions, models, and vendors, ensuring CMDB data is consistent, accurate, and usable.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–25% improvement in CMDB accuracy and trustworthiness.",
          "15–20% faster RCA and impact analysis with normalized CI data.",
          "Reduced compliance/audit risk tied to asset misclassification.",
          "Lower IT labor costs by eliminating manual normalization tasks.",
          "Improved SLA adherence due to cleaner CI/service mappings."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Duplicate/variant names bloat CMDBs and make reporting unreliable.",
              "Analysts spend hours manually cleaning data instead of focusing on higher-value work.",
              "Inconsistent versioning hides patch/vulnerability gaps.",
              "Vendor/model mismatches create audit and compliance issues.",
              "Poor normalization undermines change risk scoring and incident routing."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "20–25% improvement in CMDB accuracy and trustworthiness.",
              "15–20% faster RCA and impact analysis with normalized CI data.",
              "Reduced compliance/audit risk tied to asset misclassification.",
              "Lower IT labor costs by eliminating manual normalization tasks.",
              "Improved SLA adherence due to cleaner CI/service mappings."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Normalizes EHR client versions, ensuring accurate patch visibility.",
              "Finance: Standardizes ERP/CRM system versions for compliance audits.",
              "Airline Ops: Cleans up kiosk device/vendor data for accurate CI-service mapping.",
              "ITSM: Maintains clean, deduplicated CMDB data across global feeds.",
              "Cybersecurity: Normalizes version data to improve vulnerability detection accuracy."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Normalizes vendor, product, model, and version names across discovery feeds.",
              "Deduplicates CIs by matching fuzzy/variant identifiers.",
              "Aligns attributes to corporate dictionaries or authoritative vendor libraries.",
              "Provides dashboards for normalization coverage and conflicts.",
              "Logs all normalization changes for audit."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM/CMDB: ServiceNow CMDB, BMC Atrium, Remedy",
              "Discovery: Intune, SCCM, JAMF, Qualys, Tanium, AWS Config, Azure Resource Graph",
              "Asset/ERP: Flexera, SAP, Oracle for vendor + license data",
              "Knowledge: Vendor master data libraries, corporate dictionaries",
              "Analytics: Power BI, Tableau for CMDB health dashboards"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-applies low-risk normalizations; escalates uncertain matches to admins.",
              "Provides dashboards for reconciliation confidence and conflicts.",
              "Escalates systemic anomalies (e.g., new vendor not in dictionary)."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Normalization applied within <5 minutes of discovery ingestion.",
              "Accuracy: >95% normalization match precision after tuning.",
              "Coverage: % of CIs normalized vs duplicates."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Updates tied to RBAC and admin-approved rulesets.",
              "Data Handling: Discovery and CMDB data encrypted in transit/at rest.",
              "Audit & Traceability: Logs every normalization action with before/after values.",
              "Certifications Supported: HIPAA, SOX, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per CI normalized, per discovery feed, or outcome-based (% CMDB accuracy improvement)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–25% improvement in CMDB accuracy → fewer SLA breaches, better RCA.",
              "Reduced audit penalties from asset/vendor misclassification.",
              "Analyst labor savings from eliminating manual cleanup."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for initial normalization pilots; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot with top 50 vendor/product variants.",
              "60 days: Expand across discovery sources (endpoint, infra, cloud).",
              "90 days: Enterprise rollout with normalization dashboards + ROI metrics."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand normalization to SaaS apps, IoT, and OT/industrial devices."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with CMDB Sync Agent (reconciliation), Change Risk Scoring Agent (risk analysis), and Endpoint Compliance Fix Agent (baseline enforcement)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive normalization — detects new/unknown products and auto-classifies based on similarity + risk context."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "New discovery feed ingestion, CI drift detection, duplicate CI creation.",
      workingPlane: "Orchestrates discovery input, normalization logic, CI update.",
      memory: "Stores synonym dictionaries, normalization histories, and vendor/product mappings.",
      reasoning: "Determines correct normalized name/version/vendor from variants.",
      execution: "Updates CMDB with normalized attributes, merges duplicates, logs evidence.",
    }
  },
  'audit-trail-agent': {
    title: "Audit Trail Agent [Signs and stores evidence for HIPAA, PCI, ISO, SOC 2]",
    description: "The Audit Trail Agent automates evidence capture, digital signing, and secure storage for key actions, ensuring compliance with HIPAA, PCI-DSS, ISO, SOC 2, and other frameworks.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "90–100% automated capture of audit-relevant actions.",
          "15–20% fewer audit findings related to incomplete evidence.",
          "Weeks saved per audit cycle by eliminating manual evidence gathering.",
          "Stronger compliance posture across HIPAA, PCI-DSS, ISO27001, SOC 2, HITRUST, SOX.",
          "Increased stakeholder trust with cryptographically verifiable evidence."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Manual audit prep consumes weeks of analyst/SME time every year.",
              "Logs are incomplete, siloed, or tampered with during investigations.",
              "Evidence for approvals, changes, and incident handling is scattered across tools.",
              "Compliance frameworks require immutable, signed records that most ITSM tools don’t natively provide.",
              "Failing an audit damages trust and exposes the enterprise to fines and reputational risk."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "90–100% automated capture of audit-relevant actions.",
              "15–20% fewer audit findings related to incomplete evidence.",
              "Weeks saved per audit cycle by eliminating manual evidence gathering.",
              "Stronger compliance posture across HIPAA, PCI-DSS, ISO27001, SOC 2, HITRUST, SOX.",
              "Increased stakeholder trust with cryptographically verifiable evidence."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: HIPAA audit logging for EHR access requests, password resets, role provisioning.",
              "Finance: SOX-compliant evidence for ERP change approvals and segregation of duties (SoD).",
              "Airline Ops: Audit-ready records for mission-critical scheduling and gate system changes.",
              "ITSM: Immutable logs for incident handling, SLA enforcement, and CAB approvals.",
              "Cybersecurity: Signed trails for patching, IOC blocking, and SOC incident workflows."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Captures ITSM, IAM, DevOps, and SOC workflow actions.",
              "Digitally signs logs with cryptographic keys.",
              "Stores audit logs immutably (WORM storage, blockchain, secure vaults).",
              "Generates compliance-ready evidence packs by framework.",
              "Provides dashboards for audit readiness and evidence coverage."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy",
              "IAM: Azure AD/Entra ID, Okta, SailPoint, Ping",
              "DevOps: Jenkins, GitHub Actions, GitLab, Azure DevOps",
              "Monitoring/SIEM: Splunk, Datadog, Elastic, Sentinel",
              "GRC: Archer, ServiceNow GRC, OneTrust",
              "Storage: AWS S3 Glacier, Azure Immutable Blob, Blockchain ledgers"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-captures and signs logs; escalates anomalies (missing evidence, tamper attempts).",
              "Alerts compliance leaders when evidence packs incomplete.",
              "Provides dashboards for real-time audit readiness."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Evidence captured + signed in <60 seconds of event.",
              "Accuracy: >99% coverage of audit-relevant actions.",
              "Coverage: % of ITSM/IAM/DevOps actions logged vs in scope."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Evidence access limited by RBAC; digital signing tied to key management.",
              "Data Handling: Logs encrypted at rest/in transit; stored immutably.",
              "Audit & Traceability: Every captured log + evidence pack linked to compliance framework.",
              "Certifications Supported: HIPAA, PCI-DSS, HITRUST, SOC 2, ISO27001, SOX, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per evidence record captured, per compliance framework, or outcome-based (% audit prep time saved, % audit findings reduced)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "40–60% faster audit prep = thousands of hours saved annually.",
              "Avoidance of multi-million-dollar fines from audit failures.",
              "Reduced SME/analyst effort on manual evidence gathering."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: ["6–8 weeks for initial evidence capture pilots.",
              "12–14 weeks for enterprise-wide compliance coverage."]
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot HIPAA/PCI evidence capture for service desk workflows.",
              "60 days: Expand to change approvals and IAM lifecycle events.",
              "90 days: Enterprise rollout with auditor-facing dashboards + evidence packs."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend to HR, Finance, and Facilities approval workflows."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Approval Gatekeeper Agent (approvals), CMDB Sync Agent (config evidence), and Change Risk Scoring Agent (risk evidence)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive compliance — forecasting likely audit gaps based on historical evidence requests."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "ITSM/IAM/DevOps event requiring audit capture (approval, reset, change, remediation).",
      workingPlane: "Orchestrates evidence capture, signing, storage, and report generation.",
      memory: "Stores audit event history, prior evidence packs, framework mappings.",
      reasoning: "Determines audit scope, retention period, compliance framework alignment.",
      execution: "Signs evidence, stores immutably, generates compliance packs.",
    }
  },
  'access-provisioning-agent': {
    title: "Access Provisioning Agent",
    description: "The Access Provisioning Agent automates low-risk JML access and catalog item provisioning with built-in guardrails, eliminating manual service desk requests to ensure fast, accurate, and compliant access management.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "40–50% reduction in provisioning-related tickets handled manually.",
          "New hire access granted in minutes instead of days.",
          "25–30% lower risk of audit/compliance violations through automated guardrails.",
          "Faster employee productivity during onboarding and role changes.",
          "Reduced labor cost by eliminating repetitive Tier 1/Tier 2 provisioning work."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "40–60% of ITSM catalog requests involve access provisioning.",
              "Manual processing creates delays (often days) for new hires or role changes.",
              "Human-driven provisioning introduces errors and audit gaps.",
              "Lack of consistent guardrails can lead to over-provisioning and compliance violations.",
              "Service desk analysts are bogged down by repetitive, low-value requests."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "40–50% reduction in provisioning-related tickets handled manually.",
              "New hire access granted in minutes instead of days.",
              "25–30% lower risk of audit/compliance violations through automated guardrails.",
              "Faster employee productivity during onboarding and role changes.",
              "Reduced labor cost by eliminating repetitive Tier 1/Tier 2 provisioning work."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Automates Epic/EHR access provisioning for clinicians with role-based templates.",
              "Finance: Grants low-risk ERP/CRM entitlements with SOX-compliant approvals.",
              "Airline Ops: Provisions crew scheduling or gate system access aligned with role and shift.",
              "ITSM: Handles catalog requests (SharePoint folders, O365 groups, SaaS apps) with guardrails.",
              "Cybersecurity: Enforces Zero Trust policies by limiting provisioning to least-privilege."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Automates low-risk JML access provisioning and catalog requests.",
              "Validates entitlements against RBAC, SoD, and policy guardrails.",
              "Supports auto-approval workflows for standard, low-risk items.",
              "Escalates high-risk or exception requests for SME review.",
              "Provides complete audit trails for compliance."
            ]
          },
          {
            title: "Integrations",
            content: [
              "IAM: Azure AD / Entra ID, Okta, Ping, SailPoint",
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "Business apps: O365, Epic/EHR, SAP, Salesforce, Workday",
              "Collaboration: Teams, Slack for request submission and notifications",
              "Compliance: SIEM (Splunk, Elastic) for log forwarding"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-provisions low-risk entitlements with guardrails.",
              "Escalates high-risk or unusual requests to IAM governance boards.",
              "Provides dashboards for ITSM and compliance teams."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: SLA: Access provisioned in <5 minutes for low-risk requests.",
              "Accuracy:>95% match between request and correct entitlement template.",
              "Coverage: % of catalog/JML requests automated."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: RBAC, MFA, SoD enforcement baked into workflows.",
              "Data Handling: All provisioning logs encrypted; no plaintext credential exposure.",
              "Audit &amp; Traceability: Every entitlement change logged with user ID, policy check, and approver (if required).",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, SOX."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per user, per provisioning workflow, or outcome-based (% automated requests)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "40–50% fewer manual provisioning tickets = major labor savings.",
              "Productivity gain: new hires productive on Day 1 vs Day 3–5.",
              "25–30% fewer audit findings tied to over-provisioning."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "8–10 weeks for low-risk catalog items; 12–14 weeks for JML automation across departments."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Automate top 5 low-risk catalog items (O365 groups, shared drives).",
              "60 days: Expand to department-level JML templates.",
              "90 days: Enterprise rollout with dashboards and audit reporting."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to privileged access management (PAM) and Just-In-Time (JIT) models."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with MFA Enrollment Agent (secure onboarding), Password Reset Agent (account lifecycle), and Shadow IT Agent(removes unsanctioned access)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive provisioning — recommending entitlements based on peer/role patterns."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "JML event, catalog request, HR system update, or IAM policy trigger.",
      workingPlane: "Orchestrates IAM connectors, ITSM workflows, and policy enforcement.",
      memory: "Stores provisioning history by role, department, geography.",
      reasoning: "Determines if request fits standard low-risk template or requires escalation.",
      execution: "Provisions access in IAM + apps, updates ITSM, logs entitlement.",
    },
    kpis: ["Provisioning time reduction, % automated requests, compliance adherence, ticket reduction."]
  },
  'agent-assist-sidekick': {
    title: "Agent Assist Sidekick",
    description: "Agent Assist Sidekick uses live interaction and ticket data to provide agents with real-time context, guidance, and resources for faster, more consistent ticket resolution.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–30% faster Average Handle Time (AHT).",
          "25–35% improvement in First Contact Resolution (FCR).",
          "15–20% reduction in escalations to Tier 2/3.",
          "CSAT uplift of 10–15% through smoother, faster resolutions.",
          "Reduced training costs as new agents get guided support on day one."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "30–40% of tickets take longer than necessary because agents must search for scripts or KBs manually.",
              "Inexperienced analysts escalate prematurely due to lack of guidance.",
              "Inconsistent resolution quality creates rework, repeat tickets, and audit risks.",
              "Training new agents is costly and time-consuming.",
              "SLA breaches often occur when agents fumble for fixes during live interactions."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "20–30% faster Average Handle Time (AHT).",
              "25–35% improvement in First Contact Resolution (FCR).",
              "15–20% reduction in escalations to Tier 2/3.",
              "CSAT uplift of 10–15% through smoother, faster resolutions.",
              "Reduced training costs as new agents get guided support on day one."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Guides Tier 1 staff through Epic/EHR troubleshooting with approved clinical IT steps.",
              "Finance:Surfaces compliance-approved scripts for ERP/CRM support during calls.",
              "Airline Ops:Provides live troubleshooting steps for crew/gate staff device or scheduling issues.",
              "ITSM: Improves Tier 1 support quality across incidents, requests, and catalog items.",
              "Cybersecurity: Equips analysts with response scripts for phishing or endpoint alerts during live calls."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Live call/chat/email context analysis.",
              "Next best action recommendations with confidence scores.",
              "Real-time script and KB snippet delivery.",
              "Contextual playbooks (step-by-step guided workflows).",
              "Feedback loop to improve future recommendations."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "Telephony/CCaaS: Cisco, Avaya, Genesys Cloud, Twilio",
              "Collaboration: Teams, Slack for live assist notifications",
              "Knowledge: Confluence, SharePoint, vendor KBs, KEDB",
              "Monitoring/DEX: Splunk, Nexthink for contextual enrichment"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Surfaces guidance automatically; agent chooses to apply or escalate.",
              "Escalates to SMEs if confidence is below thresholds.",
              "Dashboards track usage and accuracy of guidance."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Guidance surfaced within <3 seconds of context capture.",
              "Accuracy:>90% correct guidance after training period.",
              "Coverage: % of tickets assisted by Sidekick agent."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Ensures only approved KBs/scripts are surfaced.",
              "Data Handling: Conversation/ticket data encrypted in transit and at rest.",
              "Audit &amp; Traceability: Every recommendation logged with context and usage outcome.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per agent assisted, per interaction, or outcome-based (% AHT reduction/FCR improvement)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–30% faster AHT improves productivity and lowers cost per ticket.",
              "25–35% FCR uplift reduces rework and escalations.",
              "Training time for new agents reduced by 30–40%."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for initial playbook/KB packs; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot Sidekick with top 5 ticket categories.",
              "60 days: Expand to voice + chat integrations across Tier 1 teams.",
              "90 days: Enterprise rollout with dashboards and ROI reporting."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend Sidekick to SOC/NOC analysts, HR, and Facilities desks."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Triage &amp; Enrichment Agent (adds context), Similar Ticket Matcher Agent (suggests proven fixes), and SLA Watcher Agent(flags priority breaches)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive Sidekick — proactively surfaces fixes before the user finishes describing the issue."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Live call/chat/email context captured, or ITSM ticket opened.",
      workingPlane: "Orchestrates context ingestion, KB lookup, playbook recommendation.",
      memory: "Stores prior ticket resolutions and playbook effectiveness.",
      reasoning: "Analyzes ticket context and selects the next best action.",
      execution: "Surfaces step-by-step scripts, KB snippets, or playbooks to the agent.",
    },
    kpis: ["AHT reduction %, FCR improvement %, escalation reduction %, CSAT uplift."]
  },
  'transcript-summarizer-agent': {
    title: "Transcript Summarizer Agent",
    description: "The Transcript Summarizer Agent automatically listens to service desk chats and calls to generate accurate, compliant resolution notes directly in the ticket, freeing analysts from manual typing..",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–35% reduction in Average Handle Time (AHT) from eliminating manual note-taking.",
          "20–25% higher FCR due to better context in handoffs.",
          "15–20% improved compliance posture with auditable, standardized notes.",
          "Reduced training costs as new agents can learn from consistent summaries.",
          "Higher CSAT from faster service and smoother escalations."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "20–30% of analyst time is lost on manual documentation.",
              "Notes are inconsistent, making tickets hard to reuse for knowledge or audits.",
              "Compliance frameworks (HIPAA, SOX, PCI-DSS) require detailed but secure records.",
              "Analysts under pressure often close tickets without writing full notes.",
              "Poor documentation prevents reuse of resolutions and inflates MTTR for repeat issues."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "25–35% reduction in Average Handle Time (AHT) from eliminating manual note-taking.",
              "20–25% higher FCR due to better context in handoffs.",
              "15–20% improved compliance posture with auditable, standardized notes.",
              "Reduced training costs as new agents can learn from consistent summaries.",
              "Higher CSAT from faster service and smoother escalations."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Summarizes clinical IT support calls while redacting PHI for HIPAA compliance.",
              "Finance: Documents ERP/trading app incidents with auditable SOX-compliant notes.",
              "Airline Ops: Summarizes crew/gate staff IT calls for accurate incident trend analysis.",
              "ITSM: Generates standardized notes for chat, phone, and portal interactions.",
              "Cybersecurity: Captures investigation notes from SOC calls, ensuring chain-of-custody compliance."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Captures and transcribes chat and call transcripts.",
              "Summarizes into structured formats: issue, root cause, resolution, next actions.",
              "Writes resolution notes into ITSM ticket automatically.",
              "Tags notes with taxonomy (service, category, priority).",
              "Redacts sensitive data (PHI, PCI, PII) per compliance rules."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "Telephony/CCaaS: Avaya, Cisco, Genesys Cloud, Twilio",
              "Collaboration: Teams, Zoom, Slack for chat/call transcripts",
              "Knowledge: Confluence, SharePoint, ServiceNow KB",
              "Compliance/SIEM: Splunk, Elastic for secure logging"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-summarizes; analyst can approve/edit before ticket closure.",
              "Escalates transcripts with unresolved issues to problem management.",
              "Governance dashboards track summary accuracy and compliance adherence."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Summary generated within <60 seconds after chat/call ends.",
              "Accuracy:>90% correct capture of key resolution details.",
              "Coverage: % of tickets auto-documented with structured notes."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Notes tied to authenticated analyst and user identities.",
              "Data Handling: Transcripts encrypted; PHI/PII redacted automatically.",
              "Audit &amp; Traceability: All notes and transcripts logged with correlation IDs.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per transcript summarized, per user, or outcome-based (% time saved/compliance improvement)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–35% AHT reduction from eliminated manual documentation.",
              "20–25% higher FCR with better handoff notes.",
              "15–20% fewer compliance audit findings tied to missing records."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for chat/call transcript ingestion; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot with chat summaries in one queue.",
              "60 days: Expand to voice/call transcription with compliance redaction.",
              "90 days: Enterprise rollout with ITSM + KB integration."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to video support calls, field service notes, and SOC/NOC war-room transcripts."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Agent Assist Sidekick (live scripts), Auto Article Generation Agent (turns notes into KBs), and SLA Watcher Agent(monitors SLA adherence tied to notes)."
          },
          {
            title: "Vendor Roadmap",
            content: "Real-time “in-call” summarization with proactive prompts for missing info before call ends."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Chat/call closes, transcript generated, or ticket updated.",
      workingPlane: "Orchestrates transcript ingestion, summarization, and ITSM KB update.",
      memory: "Stores transcript history, summary templates, and compliance redaction policies.",
      reasoning: "Identifies key details, compliance requirements, and resolution steps.",
      execution: "Writes structured resolution notes into ITSM, tags KBs, redacts sensitive data.",
    },
    kpis: ["AHT reduction %, FCR improvement, compliance adherence %, ticket reuse impact."]
  },
  'compliance-control-checker-agent': {
    title: "Compliance Control Checker Agent",
    description: "The Compliance Control Checker Agent continuously and automatically tests industry frameworks and internal policies, writing real-time compliance findings directly into registers to eliminate blind spots from retrospective, manual checks.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "90–100% coverage of applicable controls (vs 20–30% in manual reviews).",
          "20–25% fewer audit findings tied to weak or missing evidence.",
          "Weeks saved per audit cycle by auto-generating findings and evidence.",
          "Real-time compliance dashboards for executives and regulators.",
          "Stronger trust from boards, auditors, and regulators in IT governance."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Manual compliance audits only test a fraction of controls and are often delayed.",
              "Inconsistent evidence trails weaken regulatory and board confidence.",
              "ITIL, ISO, and CIS requirements are applied unevenly across services and teams.",
              "Policy violations or drift remain undetected until they cause incidents or audit failures.",
              "Compliance staff spend weeks preparing audit reports instead of managing risk."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "90–100% coverage of applicable controls (vs 20–30% in manual reviews).",
              "20–25% fewer audit findings tied to weak or missing evidence.",
              "Weeks saved per audit cycle by auto-generating findings and evidence.",
              "Real-time compliance dashboards for executives and regulators.",
              "Stronger trust from boards, auditors, and regulators in IT governance."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: HIPAA/HITRUST alignment — verifies EHR access, password policies, audit logs.",
              "Finance: SOX + PCI — checks ERP change approvals, SoD, encryption baselines.",
              "Airline Ops: ISO27001/NIST alignment for scheduling and safety-critical IT systems.",
              "ITSM: ITIL process compliance checks (incident closure, SLA adherence, CAB approvals).",
              "Cybersecurity: CIS/NIST control testing for hardened endpoints and Zero Trust enforcement."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Tests ITIL, ISO, CIS, NIST, and internal policy controls automatically.",
              "Collects evidence from ITSM, IAM, CMDB, monitoring, and infra tools.",
              "Generates compliance scores and violation reports.",
              "Writes findings into compliance registers or GRC tools with evidence attached.",
              "Produces dashboards for real-time compliance posture."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "IAM: Azure AD/Entra ID, Okta, SailPoint",
              "CMDB: ServiceNow CMDB, BMC Atrium",
              "Monitoring/SIEM: Splunk, Datadog, Elastic, Sentinel",
              "GRC: Archer, ServiceNow GRC, OneTrust",
              "Analytics: Power BI, Tableau for compliance dashboards"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Flags violations and routes to compliance or operations teams.",
              "Escalates high-risk gaps (e.g., encryption disabled, SoD failure) to governance boards.",
              "Maintains immutable logs of every test + finding."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Control checks executed within <24h cycles.",
              "Accuracy: >95% alignment of evidence to frameworks after tuning.",
              "Coverage: % of applicable controls tested continuously."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Role-based access to findings + evidence.",
              "Data Handling: Compliance data encrypted at rest/in transit; WORM storage for logs.",
              "Audit & Traceability: Each test + finding logged immutably with correlation ID.",
              "Certifications Supported: HIPAA, PCI-DSS, HITRUST, SOC2, ISO27001, NIST, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per control tested, per compliance framework, or outcome-based (% audit findings reduced)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "40% faster audit prep → thousands of hours saved.",
              "20–25% fewer audit findings = lower remediation costs.",
              "Improved regulator/board confidence in governance."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for top 50 controls; 12–14 weeks for enterprise-wide testing."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot testing for ITIL incident closure + ISO access controls.",
              "60 days: Expand to CIS benchmarks and critical ITSM processes.",
              "90 days: Enterprise rollout with dashboards + automated finding registers."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend to HR, Finance, and Facilities controls beyond IT."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Audit Trail Agent (signed evidence), Data Quality Guardian Agent (data integrity), and Endpoint Compliance Fix Agent(policy enforcement)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive compliance — forecasts high-risk areas before audit gaps occur."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Scheduled compliance cycle, config drift detected, ITSM/GRC event.",
      workingPlane: "Orchestrates data collection, control testing, evidence mapping.",
      memory: "Stores control mappings, past audit findings, framework alignments.",
      reasoning: "Determines compliance score, severity, and business impact of violation.",
      execution: "Writes findings into GRC registers, attaches evidence, alerts compliance teams.",
    },
    kpis: ["% controls tested, audit findings reduction %, SLA compliance improvement, audit prep time saved."]
  },
  'knowledge-to-catalog-agent': {
    title: "Knowledge to Catalog Agent",
    description: "The Knowledge to Catalog Agent identifies proven, repeatable fixes in knowledge bases and resolution notes and safely converts them into new, automated self-service catalog items to reduce unnecessary tickets.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–30% increase in ticket deflection via new catalog items.",
          "15–20% reduction in request fulfillment costs by automating KB → catalog flows.",
          "Faster time-to-value: catalog expanded continuously instead of quarterly updates.",
          "Improved user satisfaction with broader self-service options.",
          "Governance confidence by ensuring only validated, safe fixes become catalog items."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Proven KB fixes remain underutilized because they are not productized as catalog items.",
              "Users keep filing tickets instead of leveraging self-service → high volume, low-value workload.",
              "Manual catalog item creation is slow and governance-heavy.",
              "Poor translation of KB fixes into structured catalog requests risks errors or compliance breaches.",
              "Service desk misses opportunities for deflection and shift-left."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "20–30% increase in ticket deflection via new catalog items.",
              "15–20% reduction in request fulfillment costs by automating KB → catalog flows.",
              "Faster time-to-value: catalog expanded continuously instead of quarterly updates.",
              "Improved user satisfaction with broader self-service options.",
              "Governance confidence by ensuring only validated, safe fixes become catalog items."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Converts Epic/Citrix workstation fixes into catalog items for clinicians.",
              "Finance: Creates catalog entries for ERP password resets or report refreshes.",
              "Airline Ops: Publishes crew device/kiosk resets as catalog self-service actions.",
              "ITSM: Turns repeatable KB resolutions into structured, SLA-backed catalog requests.",
              "Cybersecurity: Safely offers MFA resets, token refreshes, or endpoint patch triggers as catalog items."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Mines KBs, resolution notes, and incident clusters for repeatable fixes.",
              "Validates candidates against compliance + safety policies.",
              "Generates structured catalog items with fields, workflows, SLAs, and automation steps.",
              "Routes for governance approval before publishing.",
              "Logs all conversions for audit and CSI improvement."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy",
              "Knowledge: Confluence, SharePoint, ServiceNow KB",
              "Catalog: ServiceNow Service Catalog, Jira Service Desk Portal",
              "Automation: Ansible, n8n, ServiceNow Flow, SCCM, Intune",
              "Collaboration: Teams, Slack for governance notifications"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-proposes candidates → routes to governance/SMEs for validation.",
              "Escalates high-risk KB fixes requiring CAB-level approval.",
              "Dashboards track catalog growth, adoption, and ROI from deflection."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Candidate catalog item created within <24h of KB publication.",
              "Accuracy: >90% safe/valid conversions after tuning.",
              "Coverage: % of KB fixes evaluated + converted."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Catalog conversions tied to RBAC and compliance approvals.",
              "Data Handling: KB + catalog data encrypted in transit/at rest.",
              "Audit &amp; Traceability: Logs all conversions, approvals, and adoption metrics.",
              "Certifications Supported: HIPAA, SOX, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per catalog item created, per service line, or outcome-based (% deflection improvement)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–30% increase in deflection → major Tier 1 cost savings.",
              "15–20% reduction in request fulfillment labor costs.",
              "Faster CSI cycles → catalog growth aligns with user demand."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: [
              "6–8 weeks for first KB → catalog pilots.",
              "12–14 weeks for enterprise rollout."
            ]

          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot KB → catalog conversion for top 5 deflectable fixes.",
              "60 days: Expand to app/infra/security categories.",
              "90 days: Enterprise rollout with ROI dashboards + governance metrics."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand beyond IT → HR, Finance, Facilities KBs to catalog."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Auto Article Generation Agent (KB creation), Similar Ticket Matcher Agent (finds repeat fixes), and Request Fulfillment Orchestrator Agent(executes catalog workflows)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive catalog building — forecast which KB fixes will become high-volume catalog requests."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "KB publication, incident closure with proven resolution, repeat incident trend.",
      workingPlane: "Orchestrates KB mining, candidate validation, catalog item creation.",
      memory: "Stores conversion history, approval outcomes, adoption metrics.",
      reasoning: "Determines if KB fix is eligible/safe for catalog conversion.",
      execution: "Creates catalog item, attaches workflows, routes approvals, logs evidence.",
    },
    kpis: ["Ticket deflection %, catalog adoption %, SLA improvements, cost savings."]
  },
  'sla-and-xla-contract-modeler-agent': {
    title: "SLA and XLA Contract Modeler Agent",
    description: "The SLA and XLA Contract Modeler Agent ensures Service/Experience Level Agreement rules remain current and enforceable by continuously synchronizing contract data, policies, and performance, and simulating the impact of proposed changes.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "100% alignment between contracts and operational ITSM rules.",
          "20–25% reduction in SLA breaches due to up-to-date policies.",
          "Improved business trust by simulating “what-if” policy scenarios.",
          "Faster governance cycles with real-time policy modeling.",
          "Reduced legal/compliance exposure from outdated SLA/XLA enforcement."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "SLAs/XLAs are often buried in PDFs or contracts, not codified in ITSM.",
              "Policy drift: SLA rules no longer match live operations or ITIL processes.",
              "Business units push for changes without understanding downstream impact.",
              "IT cannot simulate “what-if” policy changes (e.g., reducing MTTR targets).",
              "Governance boards lack evidence of how contract shifts affect cost, risk, and compliance."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "100% alignment between contracts and operational ITSM rules.",
              "20–25% reduction in SLA breaches due to up-to-date policies.",
              "Improved business trust by simulating “what-if” policy scenarios.",
              "Faster governance cycles with real-time policy modeling.",
              "Reduced legal/compliance exposure from outdated SLA/XLA enforcement."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Models SLA/XLA impacts for Epic uptime, patient experience, and compliance.",
              "Finance: Tests new SLA targets for ERP reporting during quarter-end close windows.",
              "Airline Ops: Simulates stricter XLAs for gate/kiosk performance during peak travel.",
              "ITSM: Keeps SLA contracts and XLA commitments aligned with live operational metrics.",
              "Cybersecurity: Models SLA/XLA policy for incident containment and user satisfaction."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Parses SLA/XLA contracts into codified ITSM rules.",
              "Detects drift between live policies and contract commitments.",
              "Simulates “what-if” policy scenarios (e.g., new MTTR target, stricter uptime %).",
              "Updates SLA/XLA enforcement rules in ITSM tools.",
              "Generates governance dashboards with cost, risk, and compliance projections."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy",
              "Monitoring/DEX: Splunk, Datadog, Nexthink, Aternity for SLA/XLA inputs",
              "Finance/ERP: SAP, Oracle, Workday for cost implications",
              "GRC: Archer, ServiceNow GRC, OneTrust for compliance alignment",
              "Analytics: Power BI, Tableau for simulation dashboards"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-flags drift between contracts and live ITSM SLAs.",
              "Escalates high-risk policy proposals to governance boards.",
              "Dashboards track SLA/XLA alignment and breach simulations."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Contract-policy alignment verified weekly/daily.",
              "Accuracy: >95% codification precision of SLA/XLA terms.",
              "Coverage: % of services with live SLA/XLA alignment."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: SLA/XLA updates tied to CAB/governance RBAC roles.",
              "Data Handling: SLA + XLA + contract metadata encrypted at rest/in transit.",
              "Audit &amp; Traceability: Every rule update and simulation logged immutably.",
              "Certifications Supported: HIPAA, SOX, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per contract modeled, per policy change simulation, or outcome-based (% SLA breach reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–25% SLA breach reduction from aligned rules.",
              "Faster governance = fewer CAB cycles wasted debating policy impacts.",
              "Lower compliance/audit costs by ensuring contract alignment."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: [
              "6–8 weeks for first SLA/XLA contract modeling pilot.",
              "12–14 weeks for enterprise coverage."
            ]
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Model first SLA/XLA contract for critical services.",
              "60 days: Expand to all business-critical apps with simulation dashboards.",
              "90 days: Rollout SLA/XLA governance dashboards enterprise-wide."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend to vendor/third-party SLA contracts and cloud provider XLAs."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with SLA Watcher Agent (breach prediction), Report Composer Agent (exec dashboards), and Compliance Control Checker Agent(policy validation)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive SLA/XLA modeling — forecasts cost + breach risk 6–12 months out."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "New SLA/XLA contract, SLA breach spike, governance request.",
      workingPlane: "Orchestrates SLA/XLA parsing, rule enforcement, and policy simulations.",
      memory: "Stores contract histories, SLA/XLA changes, breach trends.",
      reasoning: "Evaluates alignment gaps, models impacts of proposed changes.",
      execution: "Updates SLA/XLA enforcement rules, generates dashboards, writes logs.",
    },
    kpis: ["SLA adherence %, XLA satisfaction %, breach reduction %, governance cycle time saved."]
  },
  'data-quality-guardian-agent': {
    title: "Data Quality Guardian Agent [Detects missing or invalid fields and auto corrects]",
    description: "The Data Quality Guardian Agent continuously checks records for completeness and correctness, automatically auto-corrects invalid fields, and flags data anomalies, ensuring high-quality ITSM data.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "90–100% completeness of critical ITSM fields (service, CI, priority, approver).",
          "15–20% reduction in SLA breaches caused by misclassified tickets.",
          "10–15% improved accuracy in reporting and CSI dashboards.",
          "Lower analyst effort spent fixing bad data.",
          "Stronger audit posture with validated, consistent records."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "20–30% of incidents/requests are created with incomplete or invalid data.",
              "Analysts waste time chasing missing fields or reclassifying tickets.",
              "SLA and KPI reporting is distorted by bad data.",
              "CABs and auditors flag incomplete fields as governance gaps.",
              "Manual cleanup drains hours from service desk and CMDB teams."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "90–100% completeness of critical ITSM fields (service, CI, priority, approver).",
              "15–20% reduction in SLA breaches caused by misclassified tickets.",
              "10–15% improved accuracy in reporting and CSI dashboards.",
              "Lower analyst effort spent fixing bad data.",
              "Stronger audit posture with validated, consistent records."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Validates Epic/Cerner incident tickets have correct CI and service assignments.",
              "Finance: Ensures ERP/CRM change requests contain complete risk + approval metadata.",
              "Airline Ops: Flags invalid CI/service mappings for kiosk and scheduling apps.",
              "ITSM: Enforces mandatory field accuracy across incident, request, change, and CMDB records.",
              "Cybersecurity: Validates IOC and incident records include correct source/log data."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Detects missing or invalid fields in ITSM tickets and CMDB records.",
              "Auto-corrects with policy-driven or memory-driven values.",
              "Validates against authoritative sources (CMDB, HR, IAM).",
              "Flags unresolved anomalies for analyst/SME intervention.",
              "Logs corrections with before/after evidence."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy.",
              "CMDB: ServiceNow CMDB, BMC Atrium.",
              "IAM: Azure AD/Entra ID, Okta, SailPoint.",
              "HRIS: Workday, SAP SuccessFactors for user-to-role validation.",
              "Analytics: Power BI, Tableau for data quality dashboards."
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-corrects low-risk anomalies (priority mismatch, missing approver).",
              "Escalates unresolved or ambiguous anomalies to SMEs.",
              "Dashboards track data quality scores and correction history."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Field validation + correction in <5 seconds of record creation/update.",
              "Accuracy: >90% correction precision after training.",
              "Coverage: % of ITSM records validated and corrected."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Corrections tied to RBAC roles and authoritative sources.",
              "Data Handling: Ticket + CMDB data encrypted in transit/at rest.",
              "Audit & Traceability: Immutable logs of all corrections with justification.",
              "Certifications Supported: HIPAA, SOX, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per record validated, per service line, or outcome-based (% data accuracy improvement)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "15–20% fewer SLA breaches from better classification.",
              "25% reduction in reporting errors → stronger governance confidence.",
              "Analyst labor savings from auto-corrections vs manual cleanup."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for top ticket categories; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot for incident/request tickets.",
              "60 days: Expand to change/CMDB records.",
              "90 days: Enterprise rollout with dashboards + audit logs."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to HR, Finance, and Facilities requests."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Incident Auto Classification Agent (classification accuracy), CMDB Sync Agent (attribute alignment), and Report Composer Agent (accurate reporting)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive data quality monitoring — flagging teams/processes most likely to create invalid records."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Ticket/record creation, update, or CMDB ingestion event.",
      workingPlane: "Orchestrates validation, correction, and escalation workflows.",
      memory: "Stores correction history, authoritative mappings, and governance rules.",
      reasoning: "Determines correct values for missing/invalid fields.",
      execution: "Auto-corrects fields, updates records, logs changes immutably.",
      kpis: "% record completeness, % correction accuracy, SLA improvement %, audit readiness score."
    }
  },
  'config-drift-detector-agent': {
    title: "Config Drift Detector Agent [Flags variance from baselines, raises tickets or remediates]",
    description: "The Config Drift Detector Agent continuously compares actual system configurations against approved baselines, automatically flags or tickets variances (drift), and can initiate remediation to ensure systems remain compliant and stable.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–30% reduction in incidents caused by misconfigurations.",
          "20–25% improvement in SLA adherence by catching drift before failures.",
          "15–20% fewer audit findings related to config baselines.",
          "Reduced service desk workload through proactive remediation.",
          "Improved trust in IT operations by aligning actual vs intended state."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "60–80% of outages and failed changes are linked to undetected config drift.",
              "Manual audits only capture a fraction of drift and often months late.",
              "Drift weakens Zero Trust posture and leaves vulnerabilities exposed.",
              "Inconsistent lifecycle states across CIs create SLA, RCA, and compliance challenges.",
              "Service desks get flooded with “mystery issues” caused by unrecorded config changes."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "25–30% reduction in incidents caused by misconfigurations.",
              "20–25% improvement in SLA adherence by catching drift before failures.",
              "15–20% fewer audit findings related to config baselines.",
              "Reduced service desk workload through proactive remediation.",
              "Improved trust in IT operations by aligning actual vs intended state."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Flags and fixes unauthorized Epic server or endpoint changes to maintain HIPAA baselines.",
              "Finance: Detects ERP/CRM config drift that violates SOX or PCI-DSS controls.",
              "Airline Ops: Prevents scheduling/gate app outages by catching proxy/cert misconfigurations early.",
              "ITSM: Proactively generates incident/change tickets when drift detected.",
              "Cybersecurity: Enforces hardened configs (NIST/CIS) to close exploitable gaps."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Ingests configs from servers, endpoints, apps, and network devices.",
              "Compares against gold baselines (CIS, NIST, org-specific).",
              "Detects unauthorized or unplanned changes in parameters, services, or registry values.",
              "Flags drift with severity score + evidence (diff reports).",
              "Auto-remediates (e.g., reapply policy, reset service, push patch) or raises ITSM tickets."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC.",
              "CMDB: ServiceNow CMDB, BMC Atrium for baseline mapping.",
              "Endpoint: Intune, MEM, SCCM, JAMF, Workspace ONE.",
              "Infra/Cloud: AWS Config, Azure Policy, Terraform, Ansible, Puppet, Chef.",
              "Security: CrowdStrike, Defender ATP for compliance telemetry."
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-remediates low-risk drift.",
              "Escalates high-risk/critical drift to ITSM as P1/P2 tickets with diff report.",
              "Provides dashboards to compliance + ops leaders."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Drift detected + flagged within <5 minutes of config change.",
              "Accuracy: >95% correct drift detection after tuning.",
              "Coverage: % of CIs/systems monitored vs unscanned."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Enforces SoD for drift remediation approvals on sensitive systems.",
              "Data Handling: Config and baseline data encrypted in transit/at rest.",
              "Audit & Traceability: Full before/after drift diff logs stored immutably.",
              "Certifications Supported: HIPAA, SOX, PCI-DSS, HITRUST, SOC2, ISO27001, NIST 800-53, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per CI monitored, per drift event, or outcome-based (% incidents avoided)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–30% fewer outages reduces downtime costs (often $100k+/hr).",
              "15–20% fewer audit findings saves remediation spend.",
              "Analyst hours saved by auto-remediation vs manual triage."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for pilot baselines; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot with one service baseline (e.g., Windows Server).",
              "60 days: Expand to cloud + endpoint configs.",
              "90 days: Enterprise rollout with dashboards + compliance reporting."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to containers, Kubernetes, and SaaS configs."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Endpoint Compliance Fix Agent (auto-fixes), CMDB Sync Agent (aligns CIs), and Change Risk Scoring Agent (feeds drift context)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive drift analysis — forecast which services are most likely to drift based on historical trends."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Config change detected by discovery/monitoring, baseline drift threshold breached.",
      workingPlane: "Orchestrates config ingestion, baseline comparison, ticket/remediation actions.",
      memory: "Stores baselines, drift histories, and remediation outcomes.",
      reasoning: "Evaluates drift severity, determines remediation vs ticketing.",
      execution: "Auto-remediates or raises ITSM tickets with evidence.",
      kpis: "Drift detection rate %, incidents avoided, SLA adherence improvement, audit findings reduced."
    }
  },
  'service-mapping-builder-agent': {
    title: "Service Mapping Builder Agent [Infers dependencies from flow, APM, and logs]",
    description: "The Service Mapping Builder Agent continuously analyzes network flows, APM traces, and logs to infer dependencies and automatically builds and maintains accurate service maps within the CMDB, greatly improving the reliability of impact analysis and change governance.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–30% faster RCA due to clear dependency visibility.",
          "20–25% reduction in misrouted incidents with accurate service-to-CI mapping.",
          "15–20% fewer failed changes from better dependency analysis.",
          "Stronger governance with audit-ready service maps.",
          "Lack of service dependency evidence frustrates auditors and regulators."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "60–70% of CMDB service maps are outdated or incomplete (Gartner).",
              "Manual mapping projects take months, are costly, and drift within weeks.",
              "Incidents are misrouted due to missing dependency visibility.",
              "Change risk scoring and impact analysis are unreliable without accurate maps.",
              "Lack of service dependency evidence frustrates auditors and regulators."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "25–30% faster RCA due to clear dependency visibility.",
              "20–25% reduction in misrouted incidents with accurate service-to-CI mapping.",
              "15–20% fewer failed changes from better dependency analysis.",
              "Stronger governance with audit-ready service maps.",
              "Lack of service dependency evidence frustrates auditors and regulators."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Maps EHR systems (Epic, Cerner) to backend databases and Citrix infra.",
              "Finance: Builds service dependencies for ERP and trading platforms to meet SOX/SOC2 audit requirements.",
              "Airline Ops: Maps crew scheduling, kiosk, and gate services to apps and infra dependencies.",
              "ITSM: Improves incident routing, change impact analysis, and SLA accountability.",
              "Cybersecurity: Identifies attack paths across services for Zero Trust design."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Ingests network flow data, APM traces, and system/application logs.",
              "Correlates flows to infer dependencies between CIs, apps, and services.",
              "Validates service relationships via historical usage patterns.",
              "Updates CMDB/service maps automatically with corrected relationships.",
              "Provides dashboards for service health and dependency insights."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM/CMDB: ServiceNow CMDB, BMC Atrium, Remedy.",
              "APM/Observability: Dynatrace, AppDynamics, New Relic, Datadog.",
              "Network/Flow: NetFlow, sFlow, Cisco Tetration, Palo Alto, Zscaler.",
              "Logs: Splunk, Elastic, Fluentd.",
              "Analytics: Power BI, Tableau for service topology dashboards."
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-creates low-risk dependencies; escalates ambiguous or high-risk mappings to SMEs.",
              "Flags anomalies (e.g., unexpected dependency) for investigation.",
              "Provides governance dashboards for CABs and compliance boards."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Dependency updates applied within <15 minutes of telemetry ingestion.",
              "Accuracy: >90% dependency mapping precision after tuning.",
              "Coverage: % of services mapped vs unmanaged."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Access to maps restricted by RBAC; sensitive flows redacted for compliance.",
              "Data Handling: Flow/trace/log data encrypted in transit/at rest.",
              "Audit & Traceability: Line-item logging of inferred/approved service dependencies.",
              "Certifications Supported: HIPAA, SOX, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per service mapped, per CI dependency managed, or outcome-based (% RCA time saved)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–30% faster RCA = major labor + SLA savings.",
              "15–20% fewer failed changes = reduced downtime costs.",
              "Avoided audit remediation costs from missing service dependency evidence."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "8–10 weeks for initial service map pilots; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot service mapping for 1–2 business-critical services.",
              "60 days: Expand to 10–20 services with CAB validation.",
              "90 days: Enterprise rollout with dashboards + audit logs."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to SaaS, containerized, and microservices architectures."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with CMDB Sync Agent (CI alignment), Change Risk Scoring Agent (blast radius), and Root Cause Pattern Miner Agent (incident clustering by service)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive mapping — forecast new/hidden dependencies before they appear in logs."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Flow/trace/log ingestion, CI/service drift detected, new app/service deployed.",
      workingPlane: "Orchestrates flow/trace/log ingestion, dependency inference, CMDB update.",
      memory: "Stores past service maps, validated dependencies, failure patterns.",
      reasoning: "Correlates telemetry to infer + validate dependencies.",
      execution: "Updates CMDB maps, merges/corrects dependencies, logs evidence.",
      kpis: "% services accurately mapped, RCA acceleration %, failed change reduction %, SLA adherence improvement."
    }
  },
  'cost-allocation-tagger-agent': {
    title: "Cost Allocation Tagger Agent [Applies cost centers and showback tags to requests and CIs]",
    description: "The Cost Allocation Tagger Agent automatically analyzes HR, finance, and CMDB data to apply the correct cost center and allocation tags to requests, services, and CIs, ensuring every IT action is reliably linked to the responsible business consumer for accurate showback/chargeback.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "90–100% tagging coverage across tickets and CIs.",
          "15–20% improved budget accuracy with showback/chargeback.",
          "10–15% OpEx savings by surfacing overconsumption patterns.",
          "Improved trust with finance and business units.",
          "Reduced manual reconciliation workload for IT + finance analysts."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "60–70% of enterprises cannot achieve accurate IT showback due to missing/incorrect tags.",
              "Analysts spend hours reconciling tickets and assets with finance data.",
              "Lack of financial transparency erodes trust between IT and the business.",
              "High-cost services (cloud, ERP, EHR) lack attribution, making optimization difficult.",
              "Compliance frameworks (SOX, FinOps) demand allocation accuracy that most ITSM systems don’t meet."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "90–100% tagging coverage across tickets and CIs.",
              "15–20% improved budget accuracy with showback/chargeback.",
              "10–15% OpEx savings by surfacing overconsumption patterns.",
              "Improved trust with finance and business units.",
              "Reduced manual reconciliation workload for IT + finance analysts."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Tags EHR support requests and clinical infra CIs to hospital cost centers.",
              "Finance: Allocates ERP/CRM costs to business units for SOX-compliant reporting.",
              "Airline Ops: Tags crew scheduling, gate devices, and kiosks to operations or regional cost centers.",
              "ITSM: Ensures all tickets/requests carry cost attribution for showback.",
              "Cloud/FinOps: Extends tags to AWS/Azure/GCP resources for cloud cost showback."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Tags tickets and requests with cost centers automatically.",
              "Applies cost allocation tags to CIs in CMDB and cloud resources.",
              "Validates tags against HR and finance system data.",
              "Generates showback/chargeback-ready logs and dashboards.",
              "Tracks % coverage and exceptions for governance."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy.",
              "CMDB: ServiceNow CMDB, BMC Atrium.",
              "Finance/ERP: SAP, Oracle, Workday, Coupa.",
              "HRIS: Workday, SAP SuccessFactors for user-to-cost center mapping.",
              "Cloud: AWS, Azure, GCP cost allocation tags.",
              "Analytics: Power BI, Tableau for cost dashboards."
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-applies tags; escalates unresolvable cases to finance/IT analysts.",
              "Flags missing/invalid cost centers for governance boards.",
              "Provides dashboards for CFO, CIO, and service owners."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Tags applied within <5 seconds of ticket/CI creation.",
              "Accuracy: >95% cost center match rate after training.",
              "Coverage: % of tickets and CIs with cost attribution."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Tagging actions tied to RBAC and finance governance.",
              "Data Handling: HR + finance data encrypted in transit/at rest.",
              "Audit & Traceability: Every tag logged with correlation ID, approver, and evidence.",
              "Certifications Supported: SOX, HIPAA, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR, FinOps."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per CI/request tagged, per service line, or outcome-based (% reconciliation error reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "10–15% OpEx savings from surfacing consumption.",
              "25–30% fewer reconciliation errors → reduced audit remediation cost.",
              "Improved trust with business via transparent showback reports."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for pilot BUs; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot tagging for top 2–3 cost-heavy services.",
              "60 days: Expand to all tickets/CI categories + cloud infra.",
              "90 days: Enterprise rollout with dashboards + showback reports."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend tagging to HR, Facilities, and external vendor services."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Cost to Serve Analyzer Agent (per-ticket cost), Request Fulfillment Orchestrator (request attribution), and CMDB Sync Agent (CI ownership alignment)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive cost tagging — forecast BU-level IT consumption trends."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Ticket/request creation, CI discovery, cloud resource spin-up.",
      workingPlane: "Orchestrates HR/finance/CMDB lookups, cost tag assignment, ITSM/cloud updates.",
      memory: "Stores cost mapping history, reconciliation outcomes, and exception cases.",
      reasoning: "Determines correct cost center + tags from multiple data sources.",
      execution: "Applies tags, updates ITSM/CMDB/cloud, generates audit logs.",
      kpis: "% tagging coverage, reconciliation error reduction %, OpEx savings %, BU-level transparency."
    }
  },
  'csi-insight-agent': {
    title: "CSI Insight Agent [Trend mining with automation recommendations]",
    description: "The CSI Insight Agent continuously mines incident and performance data to correlate trends with business impact, and automatically recommends actionable automation or process improvements for Continual Service Improvement (CSI).",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–25% reduction in repeat incidents through automation-driven CSI.",
          "15–20% improvement in SLA compliance by targeting weak spots.",
          "10–15% IT cost savings through automation ROI realization.",
          "Faster CSI cycles (continuous vs quarterly/annual reviews).",
          "Increased stakeholder trust with trend-driven, evidence-based CSI."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "CSI programs are often static, backward-looking, and not tied to live operations.",
              "Service desk leaders lack actionable insights on where automation will yield the biggest ROI.",
              "Trend data is siloed across ITSM, monitoring, and finance systems.",
              "SMEs waste cycles preparing CSI decks instead of acting on improvement opportunities.",
              "Business confidence in ITSM maturity suffers due to lack of measurable improvement."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "20–25% reduction in repeat incidents through automation-driven CSI.",
              "15–20% improvement in SLA compliance by targeting weak spots.",
              "10–15% IT cost savings through automation ROI realization.",
              "Faster CSI cycles (continuous vs quarterly/annual reviews).",
              "Increased stakeholder trust with trend-driven, evidence-based CSI."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Flags recurring Epic/Citrix login failures as automation candidates.",
              "Finance: Identifies ERP report generation tickets as prime for automation.",
              "Airline Ops: Detects kiosk resets and crew access requests as CSI improvement themes.",
              "ITSM: Feeds CSI boards with quantified trends and ROI-backed automation opportunities.",
              "Cybersecurity: Surfaces repetitive SOC tasks (IOC blocks, phishing triage) as automation candidates."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Mines ITSM trends: incidents, requests, changes, SLA breaches.",
              "Correlates trends with cost and business impact.",
              "Identifies repeatable tasks → automation backlog candidates.",
              "Generates CSI dashboards and improvement registers automatically.",
              "Produces ROI estimates for automation opportunities."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy.",
              "Monitoring: Splunk, Datadog, Elastic, AppDynamics.",
              "Finance/ERP: SAP, Oracle, Workday for cost alignment.",
              "Collaboration: Teams, Slack for CSI board updates.",
              "Analytics: Power BI, Tableau for CSI dashboards."
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-populates CSI improvement registers.",
              "Escalates high-ROI opportunities to automation/CSI boards.",
              "Provides dashboards for leadership review."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: CSI dashboards updated weekly/daily.",
              "Accuracy: >90% correct identification of repeatable patterns after tuning.",
              "Coverage: % of tickets/trends analyzed for CSI."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: CSI data access role-based for ITSM + governance boards.",
              "Data Handling: Ticket + cost data encrypted in transit and at rest.",
              "Audit & Traceability: CSI register entries logged with evidence and ROI justification.",
              "Certifications Supported: HIPAA, SOX, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR, ITIL CSI."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per CSI trend analyzed, per improvement recommendation, or outcome-based (% SLA/CSAT/cost improvement achieved)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "15–20% IT cost savings via targeted automation.",
              "20–25% fewer repeat incidents.",
              "CSI cycles accelerated from quarterly → continuous."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for pilot CSI dashboards; 12–14 weeks for enterprise CSI coverage."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot CSI analysis for 1–2 high-volume categories.",
              "60 days: Expand to full ITSM data feeds + CSI dashboards.",
              "90 days: Enterprise CSI adoption with automation backlog pipeline."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend CSI mining to HR, Finance, and Facilities tickets."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Shift Left Discovery Agent (automation candidates), Problem Candidate Harvester Agent (Problem/KEDB backlog), and Cost to Serve Analyzer Agent (cost-linked CSI)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive CSI — forecasting which categories will trend negatively and proposing preemptive automations."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Weekly/daily ITSM trend mining, SLA breaches, repeat incident surges.",
      workingPlane: "Orchestrates ITSM/monitoring/finance ingestion, trend analysis, and CSI report generation.",
      memory: "Stores past CSI recommendations, adoption rates, and ROI outcomes.",
      reasoning: "Identifies trends, quantifies business impact, proposes automation.",
      execution: "Populates CSI dashboards, improvement registers, and automation backlogs.",
      kpis: "Repeat incident reduction %, SLA improvement %, automation ROI, CSI adoption rate."
    }
  },
  'report-composer-agent': {
    title: "Report Composer Agent [Builds exec-ready scorecards, XLAs, SLAs, MTTR, deflection]",
    description: "The Report Composer Agent automatically generates executive-ready scorecards (SLAs, MTTR, deflection) with commentary and visuals, eliminating manual effort for IT performance reporting.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "100% consistency in ITSM reporting across executives, boards, and QBRs.",
          "15–20% labor savings by eliminating manual report prep.",
          "10–15% improvement in stakeholder trust with finance-grade metrics and visuals.",
          "Stronger alignment of ITSM to business outcomes (experience, cost, resilience).",
          "Improved decision-making from near real-time, contextualized reporting."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Leaders lack consistent reporting on SLA adherence, MTTR, and automation ROI.",
              "Analysts spend 20–30% of their time preparing reports instead of solving problems.",
              "Manual report building leads to data errors, version conflicts, and lack of trust.",
              "XLAs (user experience metrics) are rarely integrated with SLA data.",
              "Without business context, IT reports are dismissed as “technical noise”."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "100% consistency in ITSM reporting across executives, boards, and QBRs.",
              "15–20% labor savings by eliminating manual report prep.",
              "10–15% improvement in stakeholder trust with finance-grade metrics and visuals.",
              "Stronger alignment of ITSM to business outcomes (experience, cost, resilience).",
              "Improved decision-making from near real-time, contextualized reporting."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Builds HIPAA-compliant ITSM scorecards with SLA + XLA overlays for EHR uptime.",
              "Finance: Produces SOX-ready reports linking SLA breaches and automation ROI to business risk.",
              "Airline Ops: Summarizes MTTR and ticket deflection impact on crew/gate service availability.",
              "ITSM: Creates monthly/quarterly executive decks with SLA, MTTR, CSAT, deflection, and automation ROI.",
              "Cybersecurity: Integrates SLA compliance with XLA-driven security incident experience reporting."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Ingests ITSM, monitoring, XLA/DEX, and financial data.",
              "Builds scorecards on SLA %, XLA %, MTTR, FCR, ticket deflection, automation ROI.",
              "Generates executive-ready decks (PPT, PDF, dashboards) with charts + narratives.",
              "Supports time horizons: daily, weekly, monthly, quarterly.",
              "Tracks improvement initiatives and highlights automation adoption."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy.",
              "BI/Analytics: Power BI, Tableau, Qlik, Excel.",
              "DEX/Experience: Nexthink, Aternity, Qualtrics for XLAs.",
              "Finance: SAP, Oracle, Workday for cost alignment.",
              "Collaboration: Teams, Slack for report delivery notifications."
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-generates reports on schedule (daily/weekly/monthly).",
              "Escalates anomalies (e.g., SLA breach trends) to leadership dashboards.",
              "Provides audit trail for reported metrics."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Reports delivered within <2 hours of data ingestion.",
              "Accuracy: >95% alignment with raw ITSM/finance system data.",
              "Coverage: : % of services/tickets included in reports."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Reporting access tied to RBAC and exec approval roles.",
              "Data Handling: SLA/XLA + financial data encrypted in transit/at rest.",
              "Audit & Traceability: Logs every report build, inputs, and outputs.",
              "Certifications Supported: HIPAA, SOX, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR, TBM."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per report generated, per service line, or outcome-based (% labor saved, % exec satisfaction improved)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "15–20% labor savings = thousands of analyst hours freed annually.",
              "CSAT/exec trust uplift by 10–15% with consistent, accurate reports.",
              "Faster decisions → improved SLA + XLA adherence."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for pilot scorecards; 12–14 weeks for enterprise exec reporting."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot scorecard for 1–2 service lines.",
              "60 days: Expand to enterprise SLA + XLA reporting.",
              "90 days: Roll out exec-ready monthly/quarterly decks across all BU leaders."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend reporting to HR, Finance, and Facilities tickets."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with CSI Insight Agent (trend mining), Cost to Serve Analyzer Agent (ROI linkage), and SLA Watcher Agent (breach risk)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive reporting — forecast SLA/XLA performance for next quarter with what-if modeling."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Scheduled reporting cycle, SLA/XLA/MTTR threshold breach, exec/QBR prep.",
      workingPlane: "Orchestrates ITSM, BI, DEX, and finance inputs into unified reports.",
      memory: "Stores reporting preferences, formats, and commentary history.",
      reasoning: "Correlates KPIs, builds narrative, highlights trends.",
      execution: "Produces scorecards, dashboards, or PPT/PDF decks; delivers to exec channels.",
      kpis: "SLA adherence %, XLA satisfaction %, MTTR trends, ticket deflection %, automation ROI %."
    }
  },
  'deprovisioning-sweep-agent': {
    title: "Deprovisioning Sweep Agent  ",
    description: "The Deprovisioning Sweep Agent continuously monitors identity systems and applications for dormant accounts and stale entitlements resulting from employee changes or inactivity. It automatically deprovisions this unused access according to organizational policy, significantly mitigating security risks, ensuring compliance, and reducing unnecessary licensing costs.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–30% reduction in IAM audit findings related to dormant accounts.",
          "20–25% license cost savings through reclaimed SaaS entitlements.",
          "15–20% fewer security incidents related to stale accounts.",
          "Stronger Zero Trust posture with consistent least-privilege enforcement.",
          "Lower labor costs by eliminating manual IAM sweeps."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "15–20% of accounts in IAM directories are inactive but still enabled (Ponemon study).",
              "Dormant accounts are one of the top attack vectors for breaches and ransomware.",
              "Stale entitlements cause audit failures (HIPAA, SOX, GDPR, PCI-DSS).",
              "Manual deprovisioning is error-prone, slow, and often incomplete.",
              "License costs rise as unused entitlements remain assigned unnecessarily."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "25–30% reduction in IAM audit findings related to dormant accounts.",
              "20–25% license cost savings through reclaimed SaaS entitlements.",
              "15–20% fewer security incidents related to stale accounts.",
              "Stronger Zero Trust posture with consistent least-privilege enforcement.",
              "Lower labor costs by eliminating manual IAM sweeps."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Ensures departing clinicians or contractors lose Epic, Citrix, and VPN access immediately .",
              "Finance: Enforces SOX-compliant removal of stale ERP/CRM accounts.",
              "Airline Ops: Removes dormant crew scheduling and gate system entitlements.",
              "ITSM: Automates routine deprovisioning sweeps across AD, Entra ID, O365, and SaaS apps.",
              "Cybersecurity:   Reduces attack surface by eliminating unused accounts targeted by adversaries."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Detects dormant accounts and unused entitlements by inactivity thresholds (logins, last use, activity telemetry).",
              "Applies compliance rules to determine removal (e.g., 30/60/90-day inactivity).",
              "Automatically revokes access, disables accounts, and reclaims licenses.",
              "Escalates exceptions (VIPs, privileged accounts) for SME review.",
              "Generates deprovisioning reports for governance boards."
            ]
          },
          {
            title: "Integrations",
            content: [
              "IAM: Azure AD / Entra ID, Okta, Ping, SailPoint",
              "SaaS apps: O365, SAP, Salesforce, Workday, Epic/EHR",
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "Monitoring/DEX: Nexthink, Aternity for inactivity telemetry",
              "Compliance/SIEM: Splunk, Datadog, Elastic for log forwarding."

            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Low-risk accounts auto-deprovisioned.",
              "High-risk or sensitive entitlements escalated for manual approval.",
              "Full logs sent to IAM governance and audit teams."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Dormant accounts disabled within 24 hours of detection.",
              "Accuracy: >95% correct identification of dormant/stale accounts.",
              "Coverage: % of enterprise systems under automated sweep."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: RBAC, SoD (separation of duties), and least-privilege enforcement built-in.",
              "Data Handling: Activity and entitlement data encrypted in transit/at rest.",
              "Audit & Traceability: Each deprovisioning action logged with justification and correlation ID.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, SOX, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per account monitored, per entitlement sweep, or outcome-based (% license reclaimed)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–25% SaaS license cost savings by reclaiming unused seats.",
              "25–30% fewer audit findings = reduced remediation costs and avoided fines.",
              "Reduced incident response costs by eliminating stale accounts as attack vectors."
            ]
          },

        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "8–10 weeks for top IAM/SaaS systems; 12–14 weeks for full enterprise sweep coverage."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot deprovisioning in one IAM directory + SaaS app.",
              "60 days: Expand to top 10 apps and role-based guardrails.",
              "90 days: Enterprise-wide sweeps with dashboards and ROI reporting."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend to infrastructure accounts (cloud IaaS/PaaS) and API keys."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Access Provisioning Agent (lifecycle balance), MFA Enrollment Agent (identity assurance), and Shadow IT Detection Agent (removes unsanctioned accounts)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive entitlement analysis — flagging over-provisioning before it occurs."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "IAM/HR event (termination, role change), inactivity threshold breached, SaaS license usage reports.",
      workingPlane: "Orchestrates IAM connectors, SaaS apps, and ITSM workflows for deprovisioning.",
      memory: "Stores history of dormant accounts and license reclamations.",
      reasoning: "Determines low-risk vs high-risk removals, applies policy overlays. ",
      execution: "Disables accounts, revokes entitlements, reclaims licenses, updates logs. ",
    }
  },
  'software-distribution-agent': {
    title: " Software Distribution Agent ",
    description: "The Software Distribution Agent automates the entire lifecycle of endpoint updates, including packaging, testing, and distribution of fixes, patches, and application updates. By proactively and automatically pushing these changes through tools like MEM/Intune, it dramatically reduces manual effort, minimizes errors, and improves the speed of vulnerability remediation and stability across the organization.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "30–40% reduction in ticket volume related to outdated software.",
          "25–35% faster deployment of critical patches and fixes.",
          "15–20% improved endpoint compliance posture.",
          "Reduced security incidents from unpatched vulnerabilities.",
          "IT efficiency gains by eliminating manual packaging overhead."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Manual packaging and deployments delay remediation by days or weeks.",
              "Inconsistent deployments lead to patch drift and security vulnerabilities.",
              "Service desk tickets spike when users are left with outdated or broken software.",
              "Lack of feedback loops prevents visibility into whether fixes succeeded.",
              "Endpoint management staff are overloaded with repetitive packaging tasks."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "30–40% reduction in ticket volume related to outdated software.",
              "25–35% faster deployment of critical patches and fixes.",
              "15–20% improved endpoint compliance posture.",
              "Reduced security incidents from unpatched vulnerabilities.",
              "IT efficiency gains by eliminating manual packaging overhead."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Rapidly pushes Epic/EHR client updates and device agent fixes across clinical workstations .",
              "Finance: Ensures trading and compliance apps are patched consistently before market hours.",
              "Airline Ops: Distributes gate/crew scheduling updates across global endpoint fleets.",
              "ITSM: Handles day-to-day distribution of productivity apps, browser updates, and endpoint agents.",
              "Cybersecurity: Accelerates vulnerability remediation by auto-pushing patched versions."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Automates application/package creation for fixes and updates.",
              "Integrates with MEM, Intune, SCCM for distribution.",
              "Applies targeting by role, location, device group, or compliance risk.",
              "Tracks deployment success/failure and auto-retries where needed.",
              "Generates dashboards and audit reports."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Endpoint management: MEM, Intune, SCCM, Workspace ONE, JAMF",
              "ITSM: ServiceNow, Jira Service Management, BMC (link fixes to incidents)",
              "Monitoring: Nexthink, Aternity for user impact telemetry",
              "Compliance/SIEM: Splunk, Elastic, Datadog for audit and security linkage"

            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-escalates failed deployments with logs to service desk or endpoint teams.",
              "Human-in-loop for critical business apps or compliance-sensitive updates.",
              "Continuous feedback loop to refine packaging templates."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Packages built and pushed within <1 hour of request.",
              "Accuracy: >95% successful deployment rate after retries.",
              "Coverage: % of devices/apps compliant to policy within SLA."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: RBAC ensures only authorized workflows push packages.",
              "Data Handling: Packages and deployment metadata encrypted in transit/at rest.",
              "Audit & Traceability: Immutable logs of all deployments, retries, and rollbacks.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, NIST 800-53."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per package, per device, or outcome-based (% patch compliance)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–35% faster patch cycles reduce exposure window to vulnerabilities.",
              "30–40% fewer service desk tickets from outdated/broken software.",
              "Avoided costs from breaches or fines tied to unpatched systems."
            ]
          },

        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for MEM/Intune/SCCM integration; 12–14 weeks for global phased rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot distribution for 2–3 high-volume apps.",
              "60 days: Expand to top 10 apps with phased rollout rings.",
              "90 days: Enterprise-wide automated distribution with dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Enterprise-wide automated distribution with dashboards."
          },
          {
            title: "Multi-Agent Interoperability",
            content: " Works with App Compatibility Coach Agent (pre-checks versions), Proactive Nudge Agent (warns users of updates), and Campaign Orchestrator Agent (communicates rollouts)."
          },
          {
            title: "Vendor Roadmap",
            content: " Predictive distribution — agent anticipates apps needing updates before failures occur."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Ticket request, vulnerability alert, patch release, vendor advisory.",
      workingPlane: "Orchestrates packaging, testing, distribution, and validation across endpoint platforms.",
      memory: "Stores packaging templates, prior deployment success/failure, remediation patterns.",
      reasoning: "Determines packaging method, rollout ring, retry logic, and escalation path. ",
      execution: "Builds and distributes packages, triggers installations, validates outcomes.",
    }
  },
  'collaboration-suite-fix-agent': {
    title: " Collaboration Suite Fix Agent ",
    description: "The Collaboration Suite Fix Agent automatically diagnoses and resolves common, high-volume issues within collaboration platforms like Teams, Zoom, and Outlook, such as sync failures, corrupted profiles, and cache problems. By automating these fixes, it ensures uninterrupted communication and significantly reduces the ticket volume related to enterprise-wide collaboration tools..",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–35% reduction in collaboration-related tickets.",
          "20–30% faster resolution of Teams/Zoom/Outlook issues.",
          "15–20% improvement in uptime for communication/collaboration workflows.",
          "CSAT uplift of 10–15% from smooth, reliable meeting/email experiences.",
          "Reduced IT labor costs by automating Tier 1 fixes."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "20–30% of service desk tickets in hybrid enterprises relate to collaboration tools.",
              "Manual troubleshooting wastes analyst time on repetitive cache clears and profile resets.",
              "Users miss meetings, lose email access, or face calendar sync errors — visible failures that erode trust.",
              "Remote/hybrid workers amplify reliance on collaboration apps, magnifying impact.",
              "Compliance risks arise when calendar sync issues lead to missed audit or operational deadlines."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "25–35% reduction in collaboration-related tickets.",
              "20–30% faster resolution of Teams/Zoom/Outlook issues.",
              "15–20% improvement in uptime for communication/collaboration workflows.",
              "CSAT uplift of 10–15% from smooth, reliable meeting/email experiences.",
              "Reduced IT labor costs by automating Tier 1 fixes."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Fixes Teams/Zoom meeting drops and Outlook sync for clinical staff .",
              "Finance: Repairs corrupted Outlook caches that block reporting workflows.",
              "Airline Ops: Restores Teams/Zoom collaboration for crew ops across global hubs.",
              "ITSM: Automates recurring cache/profile fixes for O365, Teams, Zoom.",
              "Cybersecurity: Ensures secure calendar/meeting sync with MFA reauthentication."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Clears Teams, Zoom, Outlook, and O365 caches.",
              "Rebuilds or resets corrupted profiles (OST, Teams user data, Zoom configs).",
              "Repairs calendar sync errors across O365/Outlook/Teams/Zoom.",
              "Resets application authentication sessions.",
              "Validates meeting join paths, plugin health (e.g., Outlook add-ins)."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Collaboration: Teams, Zoom, Outlook, Exchange Online, O365",
              "Endpoint management: MEM, Intune, SCCM, Workspace ONE",
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "Monitoring/DEX: Nexthink, Aternity for collaboration telemetry",
              "Security: Okta, Entra ID, MFA providers for reauth support"

            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Executes low-risk fixes automatically (cache clear, reauth).",
              "Escalates persistent failures (profile rebuild loops, add-in crashes) to Tier 2.",
              "Provides full audit-ready logs for ITSM governance."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA:  Fix executed within <60 seconds of detection or request.",
              "Accuracy: >90% success rate for common collaboration issues.",
              "Coverage: % of collaboration-related tickets resolved via agent."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: RBAC ensures secure resets tied to user entitlements.",
              "Data Handling: Cache/profile data handled per compliance (GDPR, HIPAA) with redaction.",
              "Audit & Traceability: Every reset or rebuild logged with justification and user context.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: " Per remediation, per user, or outcome-based (% ticket reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–35% fewer collaboration tickets saves major Tier 1/2 labor cost.",
              "Faster fixes reduce downtime for meetings/emails — worth thousands per employee annually.",
              "CSAT uplift drives higher trust in IT and digital workplace investments."
            ]
          },

        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for top app packs; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot cache clears + reauth flows for Teams/Outlook.",
              "60 days: Add Zoom + profile rebuild workflows.",
              "90 days: Enterprise rollout with dashboards and ROI reporting."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to Google Workspace, Slack, and Cisco WebEx."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Device Remediation Agent (endpoint fixes), Network Troubleshooter Agent (connectivity), and Training & Adoption Coach Agent (user coaching for new collaboration features)."
          },
          {
            title: "Vendor Roadmap",
            content: " Predictive repair — detect sync failures before users report them."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "ITSM ticket, DEX telemetry (cache bloat, sync error), user self-service trigger.",
      workingPlane: "Orchestrates cache/profile clears, calendar fixes, and reauth flows.",
      memory: "Stores recurring collaboration issues and successful fixes by app/user type. ",
      reasoning: "Determines whether cache clear, profile rebuild, or reauth is needed.",
      execution: "Executes remediation actions across Teams, Zoom, Outlook, calendars.",
    }
  },
  'vpn-and-sso-agent': {
    title: " VPN and SSO Helper Agent ",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–35% reduction in VPN/SSO-related tickets.",
          "20–30% faster resolution through automated cert renewals and token refreshes.",
          "15–20% CSAT uplift by reducing lockouts and downtime.",
          "CSAT uplift of 10–15% from smooth, reliable meeting/email experiences.",
          "Reduced security risk from fewer workarounds and misconfigurations.",
          "Improved compliance posture with auditable, standardized fixes."
        ]
      }
    ],
    description: "The VPN and SSO Helper Agent automatically diagnoses and fixes the most frequent secure access issues, such as expired certificates, corrupted profiles, and stale SSO tokens. By resolving these common front door failures instantly, the agent restores user access to enterprise systems in minutes and drastically reduces the volume of high-urgency tickets escalated to higher support tiers.",
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "15–25% of all access-related tickets involve VPN or SSO failures.",
              "Users lose hours of productivity waiting for manual troubleshooting.",
              "Certificates, tokens, and profiles frequently expire or corrupt, especially with remote/hybrid staff.",
              "Poor VPN/SSO experiences drive risky workarounds (personal email, unsanctioned file shares).",
              "Compliance frameworks (HIPAA, SOX, PCI-DSS, NIST) require consistent, auditable authentication flows."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "25–35% reduction in VPN/SSO-related tickets.",
              "20–30% faster resolution through automated cert renewals and token refreshes.",
              "15–20% CSAT uplift by reducing lockouts and downtime.",
              "CSAT uplift of 10–15% from smooth, reliable meeting/email experiences.",
              "Reduced security risk from fewer workarounds and misconfigurations.",
              "Improved compliance posture with auditable, standardized fixes."

            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Auto-renews clinician VPN certs, repairs Epic/Citrix SSO profiles.",
              "Finance: Refreshes tokens for trading apps to prevent mid-session lockouts.",
              "Airline Ops: Keeps crew and gate agents online by repairing VPN tunnels in real time.",
              "ITSM:  Handles AD/Entra ID–linked SSO profile resets without escalation.",
              "Cybersecurity: Ensures token refreshes and cert renewals comply with Zero Trust guardrails."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "VPN: Tunnel tests, profile repair, client reinstallation, DNS/proxy resets.",
              "SSO: Token refresh, session reset, profile rebuild, cache clear.",
              "Certificates: Renewal and rebind of expired VPN/SSO certs.",
              "Telemetry: Collects logs, error codes, and session data before/after fix.",
              "Reporting: Updates ITSM with resolution steps and proof of remediation."
            ]
          },
          {
            title: "Integrations",
            content: [
              "IAM: Entra ID, Okta, Ping, SailPoint",
              "VPN platforms: Cisco AnyConnect, Palo Alto GlobalProtect, Zscaler, F5",
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "Endpoint management: Intune, MEM, SCCM, Workspace ONE",
              "DEX/Monitoring: Nexthink, Aternity for connectivity telemetry"

            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Executes low-risk fixes automatically (token refresh, tunnel test).",
              "Escalates high-risk actions (cert/key changes for privileged roles).",
              "Provides dashboards for governance and compliance review."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: VPN/SSO remediation in <2 minutes for supported issues.",
              "Accuracy: >90% resolution success across common failure modes.",
              "Coverage: % of VPN/SSO issues handled without escalation."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: MFA validation, RBAC, and Zero Trust enforcement at reset/renewal.",
              "Data Handling: Logs encrypted in transit/at rest; no plaintext credential exposure.",
              "Audit & Traceability: Each cert/token/SSO action logged with user ID, device, and justification.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, NIST 800-63."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: " Per remediation, per user, or outcome-based (% ticket reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–35% fewer VPN/SSO tickets = significant Tier 1/2 cost savings.",
              "Faster MTTR avoids downtime costs worth hundreds per employee per hour.",
              "Reduced risk of breaches from expired certs and misconfigured tokens."
            ]
          },

        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for top VPN/SSO platforms; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot cert renewal + token refresh packs.",
              "60 days: Expand to profile/tunnel repair workflows.",
              "90 days: Enterprise-wide rollout with dashboards and ROI reporting."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to passwordless and conditional access troubleshooting."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Password Reset Agent (account lifecycle), MFA Enrollment Agent (identity assurance), and Network Troubleshooter Agent (connectivity)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive token expiry detection and proactive certificate renewal."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "VPN/SSO failure, ticket creation, telemetry anomaly, cert/token expiry alert.",
      workingPlane: "Orchestrates VPN + SSO diagnostics and remediation workflows.",
      memory: "Stores recurring VPN/SSO failures and successful resolutions.",
      reasoning: "Diagnoses issue type (cert, profile, tunnel, token) and selects fix pack.",
      execution: "Executes renewals, repairs, and refreshes; validates session health post-fix.",
    }
  },
  'quality-and-coaching-agent': {
    title: "Quality and Coaching Agent ",
    description: "Ensuring high-quality IT support is critical, yet traditional manual quality audits review less than 5% of interactions, leading to generic coaching. The Quality and Coaching Agent automates Quality Assurance by scoring 100% of interactions and delivering personalized, targeted coaching clips that directly address agent improvement gaps.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "100% coverage of interactions scored.",
          "15–20% improvement in First Contact Resolution (FCR) from better adherence to process.",
          "10–15% CSAT uplift through more consistent user experiences.",
          "20–25% faster agent ramp-up by targeted coaching on actual performance gaps.",
          "Reduced supervisor workload with automated QA scoring and coaching delivery."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Less than 5% of interactions are reviewed in manual QA, leaving 95% unchecked.",
              "Coaching is generalized and not tied to specific agent skill gaps.",
              "SLA breaches and poor CSAT often stem from inconsistent service quality.",
              "Managers spend disproportionate time reviewing and scoring interactions.",
              "New agents take months to ramp due to lack of tailored coaching."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "100% coverage of interactions scored.",
              "15–20% improvement in First Contact Resolution (FCR) from better adherence to process.",
              "10–15% CSAT uplift through more consistent user experiences.",
              "20–25% faster agent ramp-up by targeted coaching on actual performance gaps.",
              "Reduced supervisor workload with automated QA scoring and coaching delivery."
            ]

          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Ensures HIPAA-compliant, empathetic IT support for clinicians and patients.",
              "Finance: Scores ERP/trading support interactions for compliance with SOX/KYC requirements.",
              "Airline Ops: Coaches front-line IT support on resolving gate/crew issues consistently under pressure.",
              "ITSM: Improves resolution consistency across Tier 1–3 teams.",
              "Cybersecurity: Ensures SOC analyst escalations follow proper chain-of-custody procedures."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Transcript analysis of calls, chats, and tickets.",
              "Configurable rubrics: tone, accuracy, compliance, adherence to scripts.",
              "Automated scoring with confidence thresholds.",
              "Personalized coaching clip recommendations or LMS module delivery.",
              "Tracks coaching adoption and agent improvement trends."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "Telephony/CCaaS: Cisco, Genesys, Avaya, Twilio",
              "Collaboration: Teams, Zoom, Slack for coaching delivery",
              "Knowledge/Training: LMS platforms (Docebo, Cornerstone, SuccessFactors)",
              "Analytics: Power BI, Tableau for QA dashboards"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Scores every interaction; supervisors review only outliers.",
              "Escalates recurring compliance gaps to QA/compliance leaders.",
              "Coaching delivery logged for agent + manager visibility."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Score available within <5 minutes of interaction end.",
              "Accuracy: >90% scoring alignment with human QA.",
              "Coverage: 100% of interactions assessed."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "Identity & Access Guardrails: Scoring tied to authenticated agent IDs and QA reviewers.",
              "Data Handling: Transcripts encrypted; sensitive data redacted automatically.",
              "Audit & Traceability: All scores and coaching interventions logged.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per interaction scored, per agent, or outcome-based (% FCR/CSAT improvement)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "Supervisor hours saved by automating QA review.",
              "Faster onboarding cuts ramp time by ~30%.",
              "Higher CSAT improves adoption of digital workplace tools."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for rubric + coaching library integration; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot in one Tier 1 queue.",
              "60 days: Expand to multiple service lines + compliance-critical categories.",
              "90 days: Enterprise rollout with dashboards and ROI tracking."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to SOC/NOC, HR, and Customer Service desks."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Transcript Summarizer Agent (summaries for QA), Agent Assist Sidekick (real-time coaching), and Workforce Forecasting Agent (links QA insights to staffing)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive coaching — identifying agents at risk of performance dips before metrics slip."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Interaction completion (call/chat/email/ticket closure).",
      workingPlane: "Orchestrates transcript ingestion, scoring, and coaching clip recommendation.",
      memory: "Stores historical QA results, coaching effectiveness, and agent improvement paths.",
      reasoning: "Determines quality score + coaching recommendation.",
      execution: "Writes QA score, delivers targeted coaching clips or modules.",
    },
    kpis: ["% interactions scored, FCR improvement, CSAT uplift, ramp time reduction"]
  },
   'shift-left-discovery-agent': {
    title: "Shift Left Discovery Agent",
    description: "Continuously analyzes L2/L3 tickets, workflows, and resolutions to identify high-frequency, rule-based, or pattern-driven tasks that can be automated or pushed to L1/self-service.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–30% reduction in L2/L3 ticket volume by shifting tasks left.",
          "15–25% faster resolution time (MTTR) through automation.",
          "Lower labor cost by freeing high-cost SMEs from repetitive tasks.",
          "Continuous automation pipeline fueling CSI and AIOps.",
          "Improved SLA adherence and user satisfaction."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "30–40% of L2/L3 tasks are repeatable and eligible for automation.",
              "SMEs are overloaded with routine issues instead of focusing on complex engineering work.",
              "Lack of systematic discovery stalls shift-left initiatives.",
              "Service desks rely on subjective SME input rather than empirical data.",
              "SLA breaches and MTTR delays arise from over-escalation of resolvable issues."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "20–30% reduction in L2/L3 ticket volume by shifting tasks left.",
              "15–25% faster resolution time (MTTR) through automation.",
              "Lower labor cost by freeing high-cost SMEs from repetitive tasks.",
              "Continuous automation pipeline fueling CSI and AIOps.",
              "Improved SLA adherence and user satisfaction."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Identifies Epic/Citrix workstation resets, EHR session issues for L1 or automation.",
              "Finance: Mines ERP/CRM troubleshooting tasks repeatedly escalated to L2 for automation.",
              "Airline Ops: Detects frequent crew access, print queue, and app reconnect requests.",
              "ITSM: Builds an evidence-backed automation roadmap across service categories.",
              "Cybersecurity: Identifies recurring low-severity SOC tasks (IOC blocks, false positives) for SOAR automation."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Memory: Learns recurring L2/L3 patterns, their frequency, and successful past automations.",
              "Reasoning: Analyzes ticket metadata, SLA breaches, and problem trends to prioritize automation candidates.",
              "Execution: Generates a ranked automation backlog, complete with estimated ROI, risk level, and CSI alignment, and feeds it to the automation governance board.",
              "Advantage: Continuous, data-driven discovery that quantifies ROI and prioritization, linking directly to CSI and AIOps frameworks."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Mines ticket data, resolution notes, and problem records from L2/L3 queues.",
              "Detects high-frequency, high-cost, or SLA-heavy tasks.",
              "Scores automation feasibility, impact, and ROI.",
              "Generates backlog with candidate briefs for governance boards.",
              "Integrates findings into ITSM/AIOps automation pipelines."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM — ServiceNow, Jira Service Management, BMC, Remedy (Bidirectional).",
              "Knowledge — Confluence, SharePoint for KB references (Inbound).",
              "AIOps/Automation — n8n, Ansible, ServiceNow Flow, RunOps Agents (Bidirectional).",
              "Monitoring/Logs — Splunk, Datadog, Elastic for event correlation (Inbound).",
              "Analytics — Power BI, Tableau for ROI dashboards (Outbound)."
            ]
          },
          {
            title: "Customization & Security",
            content: [
              "Configurable scoring model (volume, SLA impact, cost-per-resolution).",
              "Custom risk thresholds for automation candidate eligibility.",
              "Department- or service-line specific discovery scopes.",
              "IAM Guardrails: Discovery scope restricted to ticket metadata and anonymized content.",
              "Audit & Traceability: Candidate discovery + approval workflow logged immutably."
            ]
          }
        ]
      },
      {
        title: "3. Operational Model & KPIs",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "L2/L3 Ticket Reduction: 20–30% reduction in L2/L3 ticket volume.",
              "MTTR Improvement: 15–25% faster resolution time through automation.",
              "Candidate Discovery Accuracy > 90% candidate relevancy after tuning.",
              "Automation ROI: Average Cost Reduction per Cycle (10–15% CSI uplift).",
              "Automation Pipeline Refresh: Candidate backlog refreshed weekly.",
              "Automation Adoption Rate ≥ 85% of L2/L3 tasks automated/shifted left."
            ]
          },
          {
            title: "Agentic Tiers (RunOps Framework)",
            content: [
              "Tier 1 — Trigger & Invocation: Weekly L2/L3 workload mining, Problem ticket creation, SLA breaches. (50%).",
              "Tier 2 — Working Plane: Orchestrates ITSM, KM, and automation data mining. (75%).",
              "Tier 3 — Memory & Reasoning: Prioritizes tasks by frequency, cost, and SLA risk. (90%).",
              "Tier 4 — Execution & Governance: Generates automation backlog, routes to governance board, and logs evidence. (100%)."
            ]
          },
          {
            title: "Financial & Value Proof",
            content: [
              "Pricing Model: Per L2/L3 queue analyzed, per candidate identified, or outcome-based (% L2/L3 volume reduction).",
              "ROI Proof: 20–30% L2/L3 reduction leads to major labor cost savings.",
              "Scalability: Supports global service desks, thousands of tickets/day."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Weekly L2/L3 workload mining, Problem ticket creation, or SLA breaches.",
      workingPlane: "Orchestrates ITSM, Knowledge Management, and automation data mining.",
      memory: "Stores historical L2/L3 workloads, automation success rates, and recurring patterns to improve discovery precision.",
      reasoning: "Prioritizes automation candidates based on frequency, cost, and SLA risk, generating an explainable scoring model for SME validation.",
      execution: "Generates automation backlog briefs, routes them to the governance board, and logs all evidence of candidate identification and approval actions."
    },
    kpis: [
      { label: "L2/L3 Ticket Reduction", value: "20-30", unit: "%", target: "≥ 20%", color: "#10b981" },
      { label: "MTTR Improvement", value: "15-25", unit: "%", target: "≥ 15%", color: "#10b981" },
      { label: "Candidate Discovery Accuracy", value: "90", unit: "%", target: "> 90%", color: "#10b981" },
      { label: "Automation ROI", value: "10-15", unit: "%", target: "≥ 10%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "85", unit: "%", target: "≥ 85%", color: "#10b981" },
      { label: "Automation Pipeline Refresh", value: "Weekly", unit: "", target: "Weekly", color: "#10b981" }
    ]
},
  'queue-load-balancer-agent': {
    title: "Queue Load Balancer Agent",
    description: "Tickets often pile up unevenly, overloading some queues or analysts while others are underutilized, as traditional ITSM platforms route statically. The Queue Load Balancer Agent continuously monitors workloads and redistributes tickets in real time to balance capacity, reduce backlog, and ensure service commitments are met.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–35% reduction in SLA/SLO breaches caused by workload imbalance.",
          "15–20% faster MTTR by shifting tickets to available analysts.",
          "10–15% higher analyst utilization and productivity.",
          "CSAT uplift of 10–12% by reducing user wait times.",
          "Lower supervisory overhead with automatic queue balancing."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "20–30% of SLA/SLO breaches occur not due to complexity but because of uneven workload distribution.",
              "Overloaded teams miss SLAs while other queues sit idle.",
              "Managers manually “rebalance” queues, wasting time and introducing bias.",
              "Static ITSM routing ignores real-time conditions like queue size, skill availability, and priority.",
              "Escalations and customer frustration rise when tickets sit too long in misallocated queues."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "25–35% reduction in SLA/SLO breaches caused by workload imbalance.",
              "15–20% faster MTTR by shifting tickets to available analysts.",
              "10–15% higher analyst utilization and productivity.",
              "CSAT uplift of 10–12% by reducing user wait times.",
              "Lower supervisory overhead with automatic queue balancing."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Balances EHR-related incidents between hospital sites and shared service teams.",
              "Finance: Redistributes ERP/CRM compliance tickets to avoid quarter-end SLA breaches.",
              "Airline Ops: Keeps crew scheduling/gate IT support tickets flowing smoothly during peak travel times.",
              "ITSM: Rebalances across Tier 1/2/3 queues based on real-time volume.",
              "Cybersecurity:Prevents SOC queues from bottlenecking by load-sharing lower-priority alerts."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Monitors queue size, analyst load, SLA timers, and skills.",
              "Reassigns or redistributes tickets to available analysts/queues.",
              "Escalates when redistribution cannot prevent SLA/SLO breach.",
              "Provides workload dashboards and balancing recommendations.",
              "Logs redistribution actions with justification."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy",
              "Workforce: Workday, SAP SuccessFactors for staffing context",
              "Monitoring: Splunk, Datadog for ticket enrichment (priority events)",
              "Collaboration: Teams, Slack for reassignment notifications",
              "Analytics: Power BI, Tableau for queue health dashboards"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Automatically redistributes tickets when SLA/SLO risk detected.",
              "Escalates systemic backlog to governance if redistribution insufficient.",
              "Provides dashboards for queue supervisors."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Redistribution action executed within <30 seconds of breach risk.",
              "Accuracy: >90% correct reassignment (skills + workload alignment).",
              "Coverage: % of tickets dynamically rebalanced."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "Identity & Access Guardrails:Tickets only moved between authorized resolver groups.",
              "Data Handling:Ticket data encrypted in transit/at rest.",
              "Audit & Traceability: Redistribution logs show who/what/why for governance.",
              "Certifications Supported:HIPAA, HITRUST, SOC2, ISO27001, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per ticket monitored, per queue, or outcome-based (% SLA adherence improvement)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "25–35% SLA breach reduction saves penalties and improves trust.",
              "Analyst productivity up 10–15% from balanced workloads.",
              "Reduced manager intervention lowers overhead by 20–25%."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for initial queue pilots; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot balancing for one high-volume Tier 1 queue.",
              "60 days: Add Tier 2/3 queues + SLA-based redistribution.",
              "90 days: Enterprise rollout with dashboards + ROI tracking."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to SOC/NOC workloads and cross-department queues (HR, Finance, Facilities)."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with SLA Watcher Agent (predicts breach risk), Escalation Orchestrator Agent (skills-aware assignment), and Workforce Forecasting Agent (staffing alignment)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive queue balancing — forecasting surges and pre-shaping workloads before spikes occur."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Ticket queue overload, SLA/SLO breach risk detected, analyst availability change.",
      workingPlane: "Orchestrates ticket monitoring, redistribution, and SLA-aware rebalancing.",
      memory: "Stores queue histories, redistribution outcomes, SLA results.",
      reasoning: "Determines best redistribution actions by workload, skills, and SLA risk.",
      execution: "Reassigns tickets, updates ITSM, notifies analysts/teams.",
    },
    kpis: ["SLA breach reduction %, MTTR improvement, analyst utilization %, CSAT uplift"]
  },
 'outage-war-room-coordinator-agent': {
    title: "Outage War Room Coordinator Agent",
    description: "Automates the immediate setup of war rooms, role assignment, stakeholder communication, and audit logging during major outages, enabling calm, consistent, and auditable incident response.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–35% faster major incident resolution (MTTR reduction).",
          "20–25% reduction in SLA breaches during P1/P0 events.",
          "Stronger stakeholder trust with timely, consistent updates.",
          "Full audit trace of communications, actions, and escalations.",
          "Reduced cognitive load on service desk leads during critical events."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "70% of post-incident reviews cite “coordination and communication failures” as primary delay factors.",
              "Manual war room setup wastes precious minutes and causes role duplication.",
              "Stakeholders receive delayed, inconsistent, or incomplete updates.",
              "Incident logs are often missing evidence for audit and compliance.",
              "SLA breaches and reputational damage escalate during high-visibility outages."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "25–35% faster major incident resolution (MTTR reduction).",
              "20–25% reduction in SLA breaches during P1/P0 events.",
              "Stronger stakeholder trust with timely, consistent updates.",
              "Full audit trace of communications, actions, and escalations.",
              "Reduced cognitive load on service desk leads during critical events."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Coordinates Epic/Citrix outage war rooms across clinical operations.",
              "Finance: Automates bridge creation and exec comms for trading platform outages.",
              "Airline Ops: Orchestrates gate, scheduling, and ticketing incident response.",
              "ITSM: Integrates directly with incident modules to spin up war rooms within seconds.",
              "Cybersecurity: Rapidly establishes breach or ransomware command channels with SoD controls."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Memory: Remembers prior outage playbooks, escalation paths, and update cadences.",
              "Reasoning: Evaluates incident severity, service impact, and stakeholder scope to orchestrate the correct response structure.",
              "Execution: Creates channels, assigns roles, schedules updates, posts notifications, and logs actions into ITSM and GRC systems.",
              "Governance: Outperforms native 'bridge' automation by embedding governance and compliance context."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Spins up collaboration channels (Teams, Slack, Zoom, WebEx) within seconds.",
              "Assigns predefined roles (Incident Commander, Comms Lead, Scribe, SME).",
              "Schedules and posts stakeholder updates based on SLA and severity.",
              "Maintains live incident timeline with action items and approvals.",
              "Logs all communications and role assignments into ITSM for audit."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM — ServiceNow, Jira Service Management, BMC, Remedy (Bidirectional).",
              "Collaboration — Teams, Slack, Zoom, WebEx (Bidirectional).",
              "Monitoring — Splunk, Datadog, AppDynamics, SolarWinds for incident triggers (Inbound).",
              "GRC/Compliance — Archer, ServiceNow GRC, OneTrust for evidence logs (Bidirectional).",
              "Calendaring — Outlook, Google Workspace for scheduled updates (Bidirectional)."
            ]
          },
          {
            title: "Customization & Security",
            content: [
              "Configurable outage templates (by service type, severity, compliance framework).",
              "Custom stakeholder lists and communication cadences.",
              "Role/shift-based escalation matrix per region or support model.",
              "IAM Guardrails: RBAC-enforced role assignment; SoD between commander/comms roles.",
              "Data Handling: All comms logs encrypted in transit/at rest; retention policies per compliance framework."
            ]
          }
        ]
      },
      {
        title: "3. Operational Model & KPIs",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "MTTR reduction: 25–35% faster major incident resolution.",
              "SLA Breach Reduction: 20–25% fewer breaches during P1/P0 events.",
              "War Room Setup Speed < 2 min: Channel and role setup initiated within <2 minutes of P1/P0 detection.",
              "Role/Channel Setup Accuracy > 95%: Correct role/channel setup after configuration tuning.",
              "Audit Evidence Coverage 100%: Immutable log of all communications, roles, and decisions."
            ]
          },
          {
            title: "Agentic Tiers (RunOps Framework)",
            content: [
              "Tier 1 — Trigger & Invocation: P1/P0 ticket creation, monitoring alert, SLA breach trigger. (50%).",
              "Tier 2 — Working Plane: Orchestrates collaboration setup, role assignment, comms scheduling. (75%).",
              "Tier 3 — Memory & Reasoning: Stores templates, evaluates severity, determines roles, and sets comms cadence. (90%).",
              "Tier 4 — Execution & Governance: Spins channels, assigns roles, sends updates, and logs actions into ITSM/GRC. (100%)."
            ]
          },
          {
            title: "Financial & Value Proof",
            content: [
              "Pricing Model: Per major incident coordinated, per service line, or outcome-based (% MTTR or SLA improvement).",
              "ROI Proof: 25–35% faster resolution leads to major downtime cost savings.",
              "Scalability: Supports concurrent multi-region war rooms across global enterprises."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "P1/P0 ticket creation, monitoring alert, or SLA breach trigger.",
      workingPlane: "Orchestrates collaboration setup, role assignment, and comms scheduling across platforms.",
      memory: "Stores prior outage playbooks, escalation paths, stakeholder lists, and communication cadences.",
      reasoning: "Evaluates incident severity, service impact, and stakeholder scope to orchestrate the correct response structure and cadence.",
      execution: "Creates channels (Teams/Slack), assigns roles, schedules updates, posts notifications, and logs all actions into ITSM/GRC for audit."
    },
    kpis: [
      { label: "MTTR Reduction", value: "25-35", unit: "%", target: "≥ 25%", color: "#10b981" },
      { label: "SLA Breach Reduction", value: "20-25", unit: "%", target: "≥ 20%", color: "#10b981" },
      { label: "War Room Setup Speed", value: "2", unit: "min", target: "< 2 min", color: "#10b981" },
      { label: "Role/Channel Setup Accuracy", value: "95", unit: "%", target: "> 95%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "85", unit: "%", target: "≥ 85%", color: "#10b981" },
      { label: "Audit Evidence Coverage", value: "100", unit: "%", target: "100%", color: "#10b981" }
    ]
},
  'release-freeze-sentinel-agent': {
    title: "Release Freeze Sentinel Agent",
    description: "Most organizations manually manage change freezes during peak support windows, leading to risky changes that cause outages. The Release Freeze Sentinel Agent automatically enforces policy-driven freezes by monitoring change requests, detecting violations, blocking deployments during high-risk periods, and routing exceptions through proper approvals.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "100% enforcement of release freeze policies across ITSM + DevOps.",
          "20–25% reduction in peak-period incidents caused by unauthorized changes.",
          "Improved SLA/SLO adherence during critical support windows.",
          "Stronger compliance posture with auditable freeze logs and exceptions.",
          "Increased stakeholder confidence in IT governance during high-visibility periods."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Risky changes deployed during peak periods (e.g., Black Friday, quarter-end close) cause avoidable incidents.",
              "Manual freeze management leads to inconsistent enforcement and “surprise” releases.",
              "ITSM and DevOps teams lack real-time synchronization of freeze windows.",
              "Exceptions are poorly logged, making audits and postmortems difficult.",
              "Business trust in IT erodes when outages occur during known critical windows."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "100% enforcement of release freeze policies across ITSM + DevOps.",
              "20–25% reduction in peak-period incidents caused by unauthorized changes.",
              "Improved SLA/SLO adherence during critical support windows.",
              "Stronger compliance posture with auditable freeze logs and exceptions.",
              "Increased stakeholder confidence in IT governance during high-visibility periods."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Prevents risky changes during Epic/Cerner upgrade or patient volume surges.",
              "Finance: Enforces freezes during quarter-end, tax season, and regulatory reporting cycles.",
              "Airline Ops: Blocks non-urgent changes during major travel holidays.",
              "ITSM: Ensures consistency across change, release, and deployment workflows.",
              "Cybersecurity: Enforces freeze windows during critical security monitoring operations."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Monitors ITSM change requests and DevOps pipelines for release activity.",
              "Matches changes against freeze window policies and risk scoring.",
              "Blocks, defers, or escalates changes depending on policy.",
              "Logs freeze violations, exceptions, and outcomes.",
              "Provides dashboards for governance boards and release managers."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy",
              "DevOps: GitHub Actions, Azure DevOps, Jenkins, GitLab CI/CD",
              "Collaboration: Teams, Slack for freeze notifications and approvals",
              "Calendaring: Outlook, Google Workspace for freeze period sync",
              "Compliance: Confluence, SharePoint for audit reports"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Blocks unauthorized changes automatically.",
              "Routes exception requests to CAB or change managers.",
              "Governance dashboards show freeze compliance status."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Freeze enforcement decisions in <10 seconds of change request.",
              "Accuracy: >95% correct enforcement of freeze policy after tuning.",
              "Coverage: % of changes monitored and governed during freeze windows."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "Identity & Access Guardrails:Freeze enforcement tied to RBAC; exceptions approved only by entitled reviewers.",
              "Data Handling: Change data encrypted in transit and at rest.",
              "Audit & Traceability:Logs all blocked, deferred, and approved changes with timestamps.",
              "Certifications Supported:HIPAA, SOX, HITRUST, SOC2, ISO27001, PCI-DSS, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per monitored change, per pipeline, or outcome-based (% freeze violations prevented)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–25% fewer peak-period incidents avoids downtime costs worth millions.",
              "Avoided SLA penalties and reputational impact from outages.",
              "Reduced manual governance workload for CABs and managers."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for freeze window integration; 12–14 weeks for enterprise coverage."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot freeze enforcement for one critical app/service.",
              "60 days: Expand to full department/LOB freeze periods.",
              "90 days: Enterprise rollout with dashboards + audit logs."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend beyond IT to HR, Facilities, and ERP changes during critical periods."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with SLA Watcher Agent (breach prediction), Outage War Room Coordinator (critical comms), and Workforce Forecasting Agent (capacity planning)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive freeze modeling — recommending freeze windows based on historical incident trends."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Change request submitted, pipeline deployment triggered, or freeze window starts.",
      workingPlane: "Orchestrates ITSM + DevOps monitoring and freeze enforcement.",
      memory: "Stores past freeze events, violations, and incident outcomes.",
      reasoning: "Determines whether a change violates freeze policy and if exception applies.",
      execution: "Blocks or defers changes, routes exception approvals, logs activity.",
    },
    kpis: ["Freeze violation % reduction, incident reduction during freezes, SLA adherence."]
  },
  'scheduling-and-callback-agent': {
    title: " Scheduling and Callback Agent ",
    description: "The Scheduling and Callback Agent improves the service desk experience by offering users the choice to schedule a callback at a convenient time or book an appointment at an in-person IT support location. This proactively manages user demand against staff availability, significantly reducing wait times and improving customer satisfaction (CSAT).",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–30% reduction in Average Handle Time (AHT).",
          "15–20% increase in First Contact Resolution (FCR) by giving analysts prep time before callbacks.",
          "Improved CSAT scores (10–15%) by eliminating frustrating hold experiences.",
          "10–15% more efficient analyst utilization by load-balancing via scheduling.",
          "Reduced SLA breaches through managed demand shaping."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Long hold times and unpredictable queues frustrate employees and degrade CSAT.",
              "Analysts waste time on context switching when calls drag on without preparation.",
              "Office IT Bar walk-ins are unmanaged, leading to long lines and wasted time.",
              "Peak-hour volume spikes overwhelm Tier 1 staff while off-peak times are underutilized.",
              "Lack of structured scheduling leads to SLA breaches and user dissatisfaction."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "20–30% reduction in Average Handle Time (AHT).",
              "15–20% increase in First Contact Resolution (FCR) by giving analysts prep time before callbacks.",
              "Improved CSAT scores (10–15%) by eliminating frustrating hold experiences.",
              "10–15% more efficient analyst utilization by load-balancing via scheduling.",
              "Reduced SLA breaches through managed demand shaping."

            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Reduces clinician wait times by scheduling callbacks during non-patient care windows.",
              "Finance: Allows advisors/traders to book IT Bar slots instead of wasting critical desk time waiting.",
              "Airline Ops: Crew and gate staff can book quick callback sessions during shift changes.",
              "ITSM: Standardizes callback workflows across all Tier 1 support queues.",
              "Cybersecurity: Enables structured callbacks for MFA/SSO recovery without leaving executives waiting."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Offers callback scheduling during high queue times.",
              "Integrates with office “IT Bar” appointment booking systems.",
              "Syncs tickets with analyst calendars for prep and workload distribution.",
              "Notifies users of confirmed callback slots or booking reminders.",
              "Tracks adherence to callback SLAs and escalates missed commitments."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC",
              "Telephony/CCaaS: Avaya, Cisco, Twilio, Genesys Cloud (callback orchestration)",
              "Calendaring: Outlook, Exchange, O365, Google Workspace",
              "Facilities/Room booking: Condeco, Robin, or custom IT Bar scheduling apps",
              "Collaboration: Teams, Slack for appointment notifications"

            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Monitors callback queue adherence.",
              "Escalates if callback SLAs are breached.",
              "Flags chronic no-shows or repeated users for governance review."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Callback confirmation in <2 minutes, fulfillment within promised window.",
              "Accuracy: >95% adherence to callback/booking schedules.",
              "Coverage: % of calls diverted from queue to scheduled callbacks/bookings."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Callback bookings tied to verified identity and ticket context.",
              "Data Handling: Callback/booking metadata encrypted in transit and at rest.",
              "Audit & Traceability: Each callback or IT Bar booking logged for compliance.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per callback/booking, per user, or outcome-based (% AHT reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–30% lower AHT reduces Tier 1 labor costs.",
              "Higher CSAT boosts adoption of ITSM portals and self-service.",
              "Reduced SLA breaches lowers penalties and improves IT reputation."
            ]
          },

        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for callback integration; 12–14 weeks for full IT Bar booking rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days:Pilot callback automation for one high-volume queue.",
              "60 days: Add IT Bar booking integration at one site.",
              "90 days: Enterprise rollout with dashboards and SLA adherence reporting."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Expand to HR, Facilities, and Customer Support desks (beyond IT)."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Concierge Agent (intake), Omnichannel Router Agent (classification), and VIP White Glove Agent (priority callbacks)."
          },
          {
            title: "Vendor Roadmap",
            content: " Predictive scheduling — anticipating demand spikes and offering proactive callback slots."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "High queue time, ticket creation, or user selection of callback/booking option.",
      workingPlane: "Orchestrates callback scheduling and IT Bar booking across ITSM + telephony.",
      memory: "Stores user callback history, preferences, and prior issues.",
      reasoning: "Decides whether to offer callback, IT Bar booking, or standard queue handling.",
      execution: "Books callback/IT Bar slots, updates tickets, notifies users/analysts.",

    }
  },
 'major-incident-commander-agent': {
    title: "Major Incident Commander Agent",
    description: "Acts as a digital incident commander, coordinating bridges, assigning roles, enforcing communication cadences, maintaining the incident timeline, and ensuring leadership visibility throughout the lifecycle of the outage.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–35% faster Mean Time to Resolution (MTTR) for major incidents.",
          "20–25% fewer SLA breaches and escalations during P1/P0 events.",
          "Real-time visibility and trust from executives and customers.",
          "Complete audit and post-mortem evidence trail.",
          "Reduced burnout for service desk leads and incident managers."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Major incident coordination is inconsistent and reactive across teams.",
              "Bridges and comms channels are often created manually, wasting critical minutes.",
              "Stakeholders lack timely, consistent updates, reducing trust and increasing escalation noise.",
              "Incident documentation and timelines are incomplete, weakening RCA and audit evidence.",
              "Human fatigue and miscommunication lead to duplicated work and longer MTTR."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "25–35% faster Mean Time to Resolution (MTTR) for major incidents.",
              "20–25% fewer SLA breaches and escalations during P1/P0 events.",
              "Real-time visibility and trust from executives and customers.",
              "Complete audit and post-mortem evidence trail.",
              "Reduced burnout for service desk leads and incident managers."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Leads coordination for EHR or clinical system downtime events.",
              "Finance: Directs response during ERP/trading outages with SOX-compliant logs.",
              "Airline Ops: Commands incidents impacting flight scheduling, gates, or check-in systems.",
              "ITSM: Governs response to any P1/P0 outage across infrastructure, application, or security domains.",
              "Cybersecurity: Acts as central coordinator for breach containment and stakeholder comms."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Memory: Retains prior incident timelines, escalation patterns, and communication cadences.",
              "Reasoning: Assesses severity, impacted services, and priorities to determine the appropriate bridge, cadence, and comms structure.",
              "Execution: Spins up incident bridges, assigns operational roles, enforces update cadences, captures decisions, and writes real-time incident timelines into ITSM and audit systems.",
              "Advantage: Outperforms manual coordination by embedding reasoning, role enforcement, and audit traceability."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Creates and manages live incident bridges (Teams, Zoom, Slack, WebEx).",
              "Assigns roles and responsibilities automatically (Commander, SME, Comms Lead, Scribe).",
              "Establishes and enforces update cadence (e.g., every 15–30 minutes).",
              "Maintains structured incident timeline with timestamped decisions and updates.",
              "Distributes stakeholder updates and exec summaries per communication policy.",
              "Logs full command timeline into ITSM for post-incident RCA and audit."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM — ServiceNow, Jira Service Management, BMC, Remedy (Bidirectional).",
              "Collaboration — Teams, Slack, Zoom, WebEx (Bidirectional).",
              "Monitoring — Splunk, Datadog, AppDynamics, SolarWinds (Inbound).",
              "GRC/Compliance — Archer, ServiceNow GRC, OneTrust (Bidirectional).",
              "Calendaring — Outlook, Google Workspace for stakeholder notifications (Bidirectional).",
              "Analytics — Power BI, Tableau for RCA and performance dashboards (Outbound)."
            ]
          },
          {
            title: "Customization & Security",
            content: [
              "Configurable escalation paths per service and severity level.",
              "Customizable communication templates for executives, customers, and regulators.",
              "Adjustable cadence, duration, and stakeholder mapping per incident type.",
              "IAM Guardrails: Enforces SoD between roles (e.g., Commander vs Comms Lead).",
              "Audit & Traceability: Each action/time entry logged with digital signature + correlation ID."
            ]
          }
        ]
      },
      {
        title: "3. Operational Model & KPIs",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "MTTR improvement: 25–35% faster major incident resolution.",
              "SLA Breach Reduction: 20–25% fewer breaches during P1/P0 events.",
              "Bridge Setup Speed < 2 min: Bridge created + roles assigned within <2 minutes of P1/P0 trigger.",
              "Role/Comms Assignment Accuracy > 95%: Correct role + communication assignment after training.",
              "Audit Evidence Coverage 100%: All actions/timeline entries logged and traceable."
            ]
          },
          {
            title: "Agentic Tiers (RunOps Framework)",
            content: [
              "Tier 1 — Trigger & Invocation: P1/P0 detection, SLA breach risk, or monitoring alert escalation. (50%).",
              "Tier 2 — Working Plane: Orchestrates bridge creation, role assignments, comms scheduling, and timeline recording. (75%).",
              "Tier 3 — Memory & Reasoning: Stores past incident cadences, determines severity/escalation structure. (90%).",
              "Tier 4 — Execution & Governance: Manages comms cadence, writes timeline logs, and enforces command governance. (100%)."
            ]
          },
          {
            title: "Financial & Value Proof",
            content: [
              "Pricing Model: Per major incident managed, per service line, or outcome-based (% MTTR reduction, % SLA improvement).",
              "ROI Proof: 25–35% faster MTTR leads to major reduction in downtime cost.",
              "Scalability: Supports hundreds of concurrent incidents across regions."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "P1/P0 detection, SLA breach risk, or monitoring alert escalation.",
      workingPlane: "Orchestrates bridge creation, role assignments, comms scheduling, and timeline recording.",
      memory: "Stores past incident cadences, communication patterns, and command outcomes to optimize response.",
      reasoning: "Determines incident severity, escalation structure, and stakeholder cadence based on impacted service and organizational priorities.",
      execution: "Spins up bridges, assigns roles, manages comms cadence, and writes immutable timeline logs into ITSM/GRC."
    },
    kpis: [
      { label: "MTTR Reduction", value: "25-35", unit: "%", target: "≥ 25%", color: "#10b981" },
      { label: "SLA Breach Reduction", value: "20-25", unit: "%", target: "≥ 20%", color: "#10b981" },
      { label: "Bridge Setup Speed", value: "2", unit: "min", target: "< 2 min", color: "#10b981" },
      { label: "Role/Comms Assignment Accuracy", value: "95", unit: "%", target: "> 95%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "85", unit: "%", target: "≥ 85%", color: "#10b981" },
      { label: "Audit Evidence Coverage", value: "100", unit: "%", target: "100%", color: "#10b981" }
    ]
},
  'root-cause-pattern-miner-agent': {
    title: "Root Cause Pattern Miner Agent",
    description: "Continuously performs cluster analysis across incidents, logs, and change data to surface hidden relationships that reveal systemic issues, feeding ITIL Problem Management and Continuous Service Improvement (CSI) pipelines automatically.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–30% fewer repeat incidents by addressing systemic causes early.",
          "15–20% faster RCA for critical services.",
          "10–15% higher SLA compliance due to reduced recurrence and noise.",
          "Improved CSI maturity through continuous trend detection.",
          "Audit-ready RCA evidence trail for governance and compliance."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "25–35% of tickets are repeat incidents tied to unresolved Problems.",
              "Manual Root Cause Analysis (RCA) is reactive, slow, and dependent on SME memory.",
              "Logs and ITSM data are siloed, preventing pattern discovery.",
              "Audits and CSI boards lack evidence for trend-backed improvements.",
              "SLA and user satisfaction drop when issues recur without prevention."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "20–30% fewer repeat incidents by addressing systemic causes early.",
              "15–20% faster RCA for critical services.",
              "10–15% higher SLA compliance due to reduced recurrence and noise.",
              "Improved CSI maturity through continuous trend detection.",
              "Audit-ready RCA evidence trail for governance and compliance."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Identifies recurring EHR login or Citrix session issues linked to infra drift.",
              "Finance: Detects ERP transaction errors caused by repeated configuration mismatches.",
              "Airline Ops: Finds recurring kiosk or gate system failures linked to patch windows.",
              "ITSM: Provides continuous input to Problem and Change Management boards.",
              "Cybersecurity: Clusters SOC incidents and false positives for rule optimization."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Memory: Stores prior RCA clusters, known error records, and incident-to-problem mappings.",
              "Reasoning: Performs cluster analysis across incidents, changes, and logs to identify statistically significant recurring patterns.",
              "Execution: Generates problem candidates and RCA summaries with evidence, routes them to Problem Management, and updates ITSM with findings.",
              "Advantage: Outperforms static reports with reasoning-driven, continuous pattern mining."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Ingests incidents, change records, and log data.",
              "Performs unsupervised clustering to detect recurring issues.",
              "Correlates clusters with known errors, services, and CIs.",
              "Generates RCA summaries and automation or Problem candidates.",
              "Provides dashboards for CSI boards and Problem Managers."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM — ServiceNow, Jira Service Management, BMC, Remedy (Bidirectional).",
              "Monitoring/Logs — Splunk, Datadog, Elastic, AppDynamics, Dynatrace (Inbound).",
              "CMDB — ServiceNow CMDB, BMC Atrium for service/CI alignment (Inbound).",
              "Knowledge — Confluence, SharePoint for RCA documentation (Outbound).",
              "Analytics — Power BI, Tableau for trend dashboards (Outbound)."
            ]
          },
          {
            title: "Customization & Security",
            content: [
              "Configurable clustering logic (CI, service, user group, SLA breach frequency).",
              "Custom scoring models for business impact and automation ROI.",
              "Role-based RCA dashboards (Problem Manager vs CSI board vs service owner).",
              "IAM Guardrails: RCA access restricted to authorized ITSM roles.",
              "Audit & Traceability: Each RCA cluster logged immutably with correlation ID and evidence."
            ]
          }
        ]
      },
      {
        title: "3. Operational Model & KPIs",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Repeat incident reduction: 20–30% fewer repeat incidents.",
              "RCA Pattern Identification Accuracy > 90% valid RCA pattern identification after tuning.",
              "RCA Cluster Generation Time < 24h of incident closure batch.",
              "SLA Adherence Improvement: 10–15% higher SLA compliance.",
              "RCA Backlog Reduction: % of services/incidents analyzed."
            ]
          },
          {
            title: "Agentic Tiers (RunOps Framework)",
            content: [
              "Tier 1 — Trigger & Invocation: Periodic batch of incident closures, log ingestion, or SLA breach detection. (50%).",
              "Tier 2 — Working Plane: Orchestrates incident + log ingestion, clustering, and RCA creation. (75%).",
              "Tier 3 — Memory & Reasoning: Stores clusters, determines recurring root causes, and service impact. (90%).",
              "Tier 4 — Execution & Governance: Creates RCA reports, Problem candidates, and CSI entries. (100%)."
            ]
          },
          {
            title: "Financial & Value Proof",
            content: [
              "Pricing Model: Per incident/log batch analyzed, per RCA cluster, or outcome-based (% repeat incidents reduced).",
              "ROI Proof: 20–30% fewer repeat tickets leads to direct labor cost savings.",
              "Scalability: Supports millions of incidents/logs per year across large enterprises."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Periodic batch of incident closures, log ingestion, or SLA breach detection.",
      workingPlane: "Orchestrates incident + log ingestion, clustering, and RCA creation.",
      memory: "Stores RCA clusters, known error records, and incident-to-problem mappings to improve detection accuracy.",
      reasoning: "Performs cluster analysis across incidents, changes, and logs to identify statistically significant recurring patterns, service impact, and automation potential.",
      execution: "Generates RCA summaries, Problem candidates, and CSI entries, routing them to Problem Management and updating ITSM with findings."
    },
    kpis: [
      { label: "Repeat Incident Reduction", value: "20-30", unit: "%", target: "≥ 20%", color: "#10b981" },
      { label: "RCA Pattern Identification Accuracy", value: "90", unit: "%", target: "> 90%", color: "#10b981" },
      { label: "RCA Cluster Generation Time", value: "24", unit: "hrs", target: "< 24h", color: "#10b981" },
      { label: "SLA Adherence Improvement", value: "10-15", unit: "%", target: "≥ 10%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "85", unit: "%", target: "≥ 85%", color: "#10b981" },
      { label: "Audit Evidence Coverage", value: "100", unit: "%", target: "100%", color: "#10b981" }
    ]
},
'root-cause-pattern-miner-agent': {
    title: "Root Cause Pattern Miner Agent",
    description: "Continuously performs cluster analysis across incidents, logs, and change data to surface hidden relationships that reveal systemic issues, feeding ITIL Problem Management and Continuous Service Improvement (CSI) pipelines automatically.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–30% fewer repeat incidents by addressing systemic causes early.",
          "15–20% faster RCA for critical services.",
          "10–15% higher SLA compliance due to reduced recurrence and noise.",
          "Improved CSI maturity through continuous trend detection.",
          "Audit-ready RCA evidence trail for governance and compliance."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "25–35% of tickets are repeat incidents tied to unresolved Problems.",
              "Manual Root Cause Analysis (RCA) is reactive, slow, and dependent on SME memory.",
              "Logs and ITSM data are siloed, preventing pattern discovery.",
              "Audits and CSI boards lack evidence for trend-backed improvements.",
              "SLA and user satisfaction drop when issues recur without prevention."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "20–30% fewer repeat incidents by addressing systemic causes early.",
              "15–20% faster RCA for critical services.",
              "10–15% higher SLA compliance due to reduced recurrence and noise.",
              "Improved CSI maturity through continuous trend detection.",
              "Audit-ready RCA evidence trail for governance and compliance."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Identifies recurring EHR login or Citrix session issues linked to infra drift.",
              "Finance: Detects ERP transaction errors caused by repeated configuration mismatches.",
              "Airline Ops: Finds recurring kiosk or gate system failures linked to patch windows.",
              "ITSM: Provides continuous input to Problem and Change Management boards.",
              "Cybersecurity: Clusters SOC incidents and false positives for rule optimization."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Memory: Stores prior RCA clusters, known error records, and incident-to-problem mappings.",
              "Reasoning: Performs cluster analysis across incidents, changes, and logs to identify statistically significant recurring patterns.",
              "Execution: Generates problem candidates and RCA summaries with evidence, routes them to Problem Management, and updates ITSM with findings.",
              "Advantage: Outperforms static reports with reasoning-driven, continuous pattern mining."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Ingests incidents, change records, and log data.",
              "Performs unsupervised clustering to detect recurring issues.",
              "Correlates clusters with known errors, services, and CIs.",
              "Generates RCA summaries and automation or Problem candidates.",
              "Provides dashboards for CSI boards and Problem Managers."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM — ServiceNow, Jira Service Management, BMC, Remedy (Bidirectional).",
              "Monitoring/Logs — Splunk, Datadog, Elastic, AppDynamics, Dynatrace (Inbound).",
              "CMDB — ServiceNow CMDB, BMC Atrium for service/CI alignment (Inbound).",
              "Knowledge — Confluence, SharePoint for RCA documentation (Outbound).",
              "Analytics — Power BI, Tableau for trend dashboards (Outbound)."
            ]
          },
          {
            title: "Customization & Security",
            content: [
              "Configurable clustering logic (CI, service, user group, SLA breach frequency).",
              "Custom scoring models for business impact and automation ROI.",
              "Role-based RCA dashboards (Problem Manager vs CSI board vs service owner).",
              "IAM Guardrails: RCA access restricted to authorized ITSM roles.",
              "Audit & Traceability: Each RCA cluster logged immutably with correlation ID and evidence."
            ]
          }
        ]
      },
      {
        title: "3. Operational Model & KPIs",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Repeat incident reduction: 20–30% fewer repeat incidents.",
              "RCA Pattern Identification Accuracy > 90% valid RCA pattern identification after tuning.",
              "RCA Cluster Generation Time < 24h of incident closure batch.",
              "SLA Adherence Improvement: 10–15% higher SLA compliance.",
              "RCA Backlog Reduction: % of services/incidents analyzed."
            ]
          },
          {
            title: "Agentic Tiers (RunOps Framework)",
            content: [
              "Tier 1 — Trigger & Invocation: Periodic batch of incident closures, log ingestion, or SLA breach detection. (50%).",
              "Tier 2 — Working Plane: Orchestrates incident + log ingestion, clustering, and RCA creation. (75%).",
              "Tier 3 — Memory & Reasoning: Stores clusters, determines recurring root causes, and service impact. (90%).",
              "Tier 4 — Execution & Governance: Creates RCA reports, Problem candidates, and CSI entries. (100%)."
            ]
          },
          {
            title: "Financial & Value Proof",
            content: [
              "Pricing Model: Per incident/log batch analyzed, per RCA cluster, or outcome-based (% repeat incidents reduced).",
              "ROI Proof: 20–30% fewer repeat tickets leads to direct labor cost savings.",
              "Scalability: Supports millions of incidents/logs per year across large enterprises."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Periodic batch of incident closures, log ingestion, or SLA breach detection.",
      workingPlane: "Orchestrates incident + log ingestion, clustering, and RCA creation.",
      memory: "Stores RCA clusters, known error records, and incident-to-problem mappings to improve detection accuracy.",
      reasoning: "Performs cluster analysis across incidents, changes, and logs to identify statistically significant recurring patterns, service impact, and automation potential.",
      execution: "Generates RCA summaries, Problem candidates, and CSI entries, routing them to Problem Management and updating ITSM with findings."
    },
    kpis: [
      { label: "Repeat Incident Reduction", value: "20-30", unit: "%", target: "≥ 20%", color: "#10b981" },
      { label: "RCA Pattern Identification Accuracy", value: "90", unit: "%", target: "> 90%", color: "#10b981" },
      { label: "RCA Cluster Generation Time", value: "24", unit: "hrs", target: "< 24h", color: "#10b981" },
      { label: "SLA Adherence Improvement", value: "10-15", unit: "%", target: "≥ 10%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "85", unit: "%", target: "≥ 85%", color: "#10b981" },
      { label: "Audit Evidence Coverage", value: "100", unit: "%", target: "100%", color: "#10b981" }
    ]
},
  'problem-candidate-harvester-agent': {
    title: "Problem Candidate Harvester Agent",
    description: "Continuously analyzes incident clusters, RCA patterns, and resolution histories to propose new Problems and KEDB entries — complete with supporting evidence, frequency data, and impact scoring — for Problem Management review.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–25% reduction in repeat incidents through proactive Problem identification.",
          "15–20% faster KEDB population and reuse for ticket deflection.",
          "Stronger governance with evidence-backed Problem proposals.",
          "Enhanced CSI velocity through a continuous improvement pipeline.",
          "Improved SLA/MTTR adherence and auditor confidence."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "20–30% of service desk volume consists of recurring incidents that never become Problems.",
              "Problem discovery relies on manual SME input instead of data-driven analysis.",
              "KEDB entries are sparse or stale, limiting reuse and automation potential.",
              "SLA and MTTR are inflated by unresolved systemic issues.",
              "Audit findings flag lack of root-cause traceability or incomplete KEDB evidence."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "20–25% reduction in repeat incidents through proactive Problem identification.",
              "15–20% faster KEDB population and reuse for ticket deflection.",
              "Stronger governance with evidence-backed Problem proposals.",
              "Enhanced CSI velocity through a continuous improvement pipeline.",
              "Improved SLA/MTTR adherence and auditor confidence."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Proposes Epic/Citrix performance issues as Problems with clinical impact data.",
              "Finance: Flags ERP reconciliation errors recurring across departments as Problem candidates.",
              "Airline Ops: Identifies repeated kiosk network failures for permanent fix evaluation.",
              "ITSM: Continuously supplies Problem Management with prioritized, evidence-based candidates.",
              "Cybersecurity: Converts repetitive SOC incidents (false positives, IOC alerts) into Problem/KEDB records."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Memory: Retains historical problem resolutions, known error patterns, and prior RCA findings.",
              "Reasoning: Mines ticket data, resolution notes, and RCA clusters to identify recurring patterns worthy of Problem records or KEDB entries.",
              "Execution: Drafts Problem/KEDB candidates with full evidence (frequency, CI/service impact, SLA cost), routes to Problem Management for validation, and logs governance outcomes.",
              "Advantage: Systematic, continuous discovery vs periodic manual reviews; outperforms ITSM-native analytics by embedding reasoning."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Analyzes incidents, RCA clusters, and Problem records to find new candidates.",
              "Generates proposed Problem + KEDB entries with supporting evidence.",
              "Scores candidates by business impact, SLA cost, and recurrence.",
              "Attaches evidence (ticket IDs, logs, impact graphs) for governance review.",
              "Routes drafts to Problem Managers, CABs, or CSI boards for approval."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM — ServiceNow, Jira Service Management, BMC, Remedy (Bidirectional).",
              "Knowledge — Confluence, SharePoint, ServiceNow KB/KEDB (Bidirectional).",
              "Monitoring/Logs — Splunk, Datadog, Elastic, AppDynamics (Inbound).",
              "CMDB — ServiceNow CMDB, BMC Atrium for CI/service alignment (Inbound).",
              "Analytics — Power BI, Tableau for trend dashboards (Outbound)."
            ]
          },
          {
            title: "Customization & Security",
            content: [
              "Configurable thresholds (incident frequency, SLA breach count, financial cost).",
              "Industry-specific templates (e.g., HIPAA, SOX, PCI reporting).",
              "Custom review workflows for Problem and KEDB approval routing.",
              "IAM Guardrails: Problem/KEDB proposal actions tied to authorized roles.",
              "Audit & Traceability: Immutable logs linking candidate → approval → outcome."
            ]
          }
        ]
      },
      {
        title: "3. Operational Model & KPIs",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Repeat incident reduction: 20–25% reduction in recurring incidents.",
              "KEDB Population Speed: 15–20% faster KEDB population.",
              "Candidate Proposal Speed < 24h: Candidate Problems generated within <24h of RCA detection.",
              "Proposal Relevance > 90% relevance and evidence completeness after tuning.",
              "Automation Adoption Rate ≥ 85% of Problem discovery executed autonomously."
            ]
          },
          {
            title: "Agentic Tiers (RunOps Framework)",
            content: [
              "Tier 1 — Trigger & Invocation: RCA cluster creation, SLA breaches, recurring incident trend detected. (50%).",
              "Tier 2 — Working Plane: Orchestrates incident + RCA data ingestion, candidate creation, approval routing. (75%).",
              "Tier 3 — Memory & Reasoning: Evaluates frequency, SLA, and business impact to propose candidates. (90%).",
              "Tier 4 — Execution & Governance: Drafts Problem/KEDB entries, attaches evidence, routes for validation. (100%)."
            ]
          },
          {
            title: "Financial & Value Proof",
            content: [
              "Pricing Model: Per candidate identified, per KEDB entry drafted, or outcome-based (% repeat incident reduction).",
              "ROI Proof: 20–30% fewer repeat incidents leads to direct labor cost savings.",
              "Scalability: Handles thousands of incidents weekly, generating prioritized Problem pipelines."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "RCA cluster creation, SLA breaches, or recurring incident trend detected.",
      workingPlane: "Orchestrates incident + RCA data ingestion, candidate creation, and approval routing.",
      memory: "Stores historical RCA patterns, approved Problems, and KEDB mappings to improve prioritization.",
      reasoning: "Evaluates incident frequency, SLA, and business impact to propose Problem/KEDB candidates with high potential ROI.",
      execution: "Drafts Problem/KEDB entries with full evidence, attaches supporting data, and routes the draft for Problem Management validation and approval."
    },
    kpis: [
      { label: "Repeat Incident Reduction", value: "20-25", unit: "%", target: "≥ 20%", color: "#10b981" },
      { label: "KEDB Population Speed", value: "15-20", unit: "%", target: "≥ 15%", color: "#10b981" },
      { label: "Candidate Proposal Speed", value: "24", unit: "hrs", target: "< 24h", color: "#10b981" },
      { label: "Proposal Relevance", value: "90", unit: "%", target: "> 90%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "85", unit: "%", target: "≥ 85%", color: "#10b981" },
      { label: "Audit Evidence Coverage", value: "100", unit: "%", target: "100%", color: "#10b981" }
    ]
},
 'problem-candidate-harvester-agent': {
    title: "Problem Candidate Harvester Agent",
    description: "Continuously analyzes incident clusters, RCA patterns, and resolution histories to propose new Problems and KEDB entries — complete with supporting evidence, frequency data, and impact scoring — for Problem Management review.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–25% reduction in repeat incidents through proactive Problem identification.",
          "15–20% faster KEDB population and reuse for ticket deflection.",
          "Stronger governance with evidence-backed Problem proposals.",
          "Enhanced CSI velocity through a continuous improvement pipeline.",
          "Improved SLA/MTTR adherence and auditor confidence."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "20–30% of service desk volume consists of recurring incidents that never become Problems.",
              "Problem discovery relies on manual SME input instead of data-driven analysis.",
              "KEDB entries are sparse or stale, limiting reuse and automation potential.",
              "SLA and MTTR are inflated by unresolved systemic issues.",
              "Audit findings flag lack of root-cause traceability or incomplete KEDB evidence."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "20–25% reduction in repeat incidents through proactive Problem identification.",
              "15–20% faster KEDB population and reuse for ticket deflection.",
              "Stronger governance with evidence-backed Problem proposals.",
              "Enhanced CSI velocity through a continuous improvement pipeline.",
              "Improved SLA/MTTR adherence and auditor confidence."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Proposes Epic/Citrix performance issues as Problems with clinical impact data.",
              "Finance: Flags ERP reconciliation errors recurring across departments as Problem candidates.",
              "Airline Ops: Identifies repeated kiosk network failures for permanent fix evaluation.",
              "ITSM: Continuously supplies Problem Management with prioritized, evidence-based candidates.",
              "Cybersecurity: Converts repetitive SOC incidents (false positives, IOC alerts) into Problem/KEDB records."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Memory: Retains historical problem resolutions, known error patterns, and prior RCA findings.",
              "Reasoning: Mines ticket data, resolution notes, and RCA clusters to identify recurring patterns worthy of Problem records or KEDB entries.",
              "Execution: Drafts Problem/KEDB candidates with full evidence (frequency, CI/service impact, SLA cost), routes to Problem Management for validation, and logs governance outcomes.",
              "Advantage: Systematic, continuous discovery vs periodic manual reviews; outperforms ITSM-native analytics by embedding reasoning."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Analyzes incidents, RCA clusters, and Problem records to find new candidates.",
              "Generates proposed Problem + KEDB entries with supporting evidence.",
              "Scores candidates by business impact, SLA cost, and recurrence.",
              "Attaches evidence (ticket IDs, logs, impact graphs) for governance review.",
              "Routes drafts to Problem Managers, CABs, or CSI boards for approval."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM — ServiceNow, Jira Service Management, BMC, Remedy (Bidirectional).",
              "Knowledge — Confluence, SharePoint, ServiceNow KB/KEDB (Bidirectional).",
              "Monitoring/Logs — Splunk, Datadog, Elastic, AppDynamics (Inbound).",
              "CMDB — ServiceNow CMDB, BMC Atrium for CI/service alignment (Inbound).",
              "Analytics — Power BI, Tableau for trend dashboards (Outbound)."
            ]
          },
          {
            title: "Customization & Security",
            content: [
              "Configurable thresholds (incident frequency, SLA breach count, financial cost).",
              "Industry-specific templates (e.g., HIPAA, SOX, PCI reporting).",
              "Custom review workflows for Problem and KEDB approval routing.",
              "IAM Guardrails: Problem/KEDB proposal actions tied to authorized roles.",
              "Audit & Traceability: Immutable logs linking candidate → approval → outcome."
            ]
          }
        ]
      },
      {
        title: "3. Operational Model & KPIs",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Repeat incident reduction: 20–25% reduction in recurring incidents.",
              "KEDB Population Speed: 15–20% faster KEDB population.",
              "Candidate Proposal Speed < 24h: Candidate Problems generated within <24h of RCA detection.",
              "Proposal Relevance > 90% relevance and evidence completeness after tuning.",
              "Automation Adoption Rate ≥ 85% of Problem discovery executed autonomously."
            ]
          },
          {
            title: "Agentic Tiers (RunOps Framework)",
            content: [
              "Tier 1 — Trigger & Invocation: RCA cluster creation, SLA breaches, recurring incident trend detected. (50%).",
              "Tier 2 — Working Plane: Orchestrates incident + RCA data ingestion, candidate creation, approval routing. (75%).",
              "Tier 3 — Memory & Reasoning: Evaluates frequency, SLA, and business impact to propose candidates. (90%).",
              "Tier 4 — Execution & Governance: Drafts Problem/KEDB entries, attaches evidence, routes for validation. (100%)."
            ]
          },
          {
            title: "Financial & Value Proof",
            content: [
              "Pricing Model: Per candidate identified, per KEDB entry drafted, or outcome-based (% repeat incident reduction).",
              "ROI Proof: 20–30% fewer repeat incidents leads to direct labor cost savings.",
              "Scalability: Handles thousands of incidents weekly, generating prioritized Problem pipelines."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "RCA cluster creation, SLA breaches, or recurring incident trend detected.",
      workingPlane: "Orchestrates incident + RCA data ingestion, candidate creation, and approval routing.",
      memory: "Stores historical RCA patterns, approved Problems, and KEDB mappings to improve prioritization.",
      reasoning: "Evaluates incident frequency, SLA, and business impact to propose Problem/KEDB candidates with high potential ROI.",
      execution: "Drafts Problem/KEDB entries with full evidence, attaches supporting data, and routes the draft for Problem Management validation and approval."
    },
    kpis: [
      { label: "Repeat Incident Reduction", value: "20-25", unit: "%", target: "≥ 20%", color: "#10b981" },
      { label: "KEDB Population Speed", value: "15-20", unit: "%", target: "≥ 15%", color: "#10b981" },
      { label: "Candidate Proposal Speed", value: "24", unit: "hrs", target: "< 24h", color: "#10b981" },
      { label: "Proposal Relevance", value: "90", unit: "%", target: "> 90%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "85", unit: "%", target: "≥ 85%", color: "#10b981" },
      { label: "Audit Evidence Coverage", value: "100", unit: "%", target: "100%", color: "#10b981" }
    ]
},
  'change-risk-scoring-agent': {
    title: "Change Risk Scoring Agent",
    description: "The Change Risk Scoring Agent is the data analyst for IT change management, offering consistent, objective risk assessment for all change requests. It provides assistance across the change approval process, whether it's analyzing change requests, scoring risk by blast radius, considering historical success/failure, or quantifying current system drift.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–30% reduction in change-related incidents and outages.",
          "Faster CAB throughput: low-risk changes auto-approved, high-risk escalated.",
          "15–20% improvement in SLA/SLO compliance by reducing failed changes.",
          "Audit-ready risk scoring evidence for every change request.",
          "Increased trust between IT, DevOps, and the business with transparent risk scoring."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Change failures account for 40–60% of major incidents (Gartner).",
              "Manual CAB reviews are inconsistent and slow, creating bottlenecks.",
              "Low-risk changes get over-scrutinized while high-risk changes sometimes bypass scrutiny.",
              "Drift between baseline configs and actual systems increases risk but is rarely factored in.",
              "Lack of evidence makes audits and post-incident investigations difficul."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "20–30% reduction in change-related incidents and outages.",
              "Faster CAB throughput: low-risk changes auto-approved, high-risk escalated.",
              "15–20% improvement in SLA/SLO compliance by reducing failed changes.",
              "Audit-ready risk scoring evidence for every change request.",
              "Increased trust between IT, DevOps, and the business with transparent risk scoring."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Scores Epic upgrade changes based on prior outages and CI dependencies.",
              "Finance: Evaluates ERP/CRM patches against quarter-end risk windows.",
              "Airline Ops: Flags high-risk scheduling or gate changes with global blast radius.",
              "ITSM: Standardizes CAB processes across multiple service lines.",
              "Cybersecurity: Scores changes that could weaken compliance or increase attack surface."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Evaluates change request metadata, CI dependencies, and impact models.",
              "Correlates with historical change outcomes (success/failure rates).",
              "Detects config drift and blast radius via CMDB + monitoring integration.",
              "Produces quantitative risk score (0–100) and qualitative band (low/med/high).",
              "Routes change for auto-approval, CAB review, or block based on score."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy",
              "CMDB: ServiceNow CMDB, BMC Atrium, Flexera",
              "Monitoring/Logs: Splunk, Datadog, Elastic, AppDynamics",
              "DevOps: Jenkins, GitHub Actions, Azure DevOps, GitLab",
              "Analytics: Power BI, Tableau for risk dashboards"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-approves low-risk changes.",
              "Escalates medium/high-risk changes to CAB or managers.",
              "Logs rationale for audit and RCA."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Risk score generated within <30 seconds of change submission.",
              "Accuracy: >90% correlation between score and actual change outcomes after tuning.",
              "Coverage: % of changes scored vs manual review."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "Identity & Access Guardrails: Scoring visible only to authorized approvers/CABs.",
              "Data Handling: Change + telemetry data encrypted at rest/in transit.",
              "Audit & Traceability: Logs risk inputs, scoring outcome, and routing decision.",
              "Certifications Supported: HIPAA, SOX, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per change scored, per service line, or outcome-based (% failed changes reduced)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–30% fewer failed changes reduces downtime costs (often $100k–$500k/hr).",
              "Faster CAB throughput = productivity savings for SMEs.",
              "Reduced audit costs with evidence-backed scoring logs."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for initial risk scoring pilots; 12–14 weeks for enterprise coverage."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot with 1–2 critical services.",
              "60 days: Expand to full CAB portfolio with risk dashboards.",
              "90 days: Enterprise rollout with audit-ready logs and KPIs."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: " Expand to infrastructure as code (IaC), SaaS, and cloud-native deployments."
          },
          {
            title: "Multi-Agent Interoperability",
            content: " Works with Release Freeze Sentinel (peak window enforcement), Outage War Room Coordinator (incident comms if change fails), and Root Cause Pattern Miner (feeds RCA on failed changes)."
          },
          {
            title: "Vendor Roadmap",
            content: " Predictive scoring that adapts weights dynamically based on emerging incident/change patterns."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Change request submitted into ITSM/DevOps pipeline",
      workingPlane: "Orchestrates CMDB dependency checks, history lookup, drift analysis",
      memory: "Stores past change outcomes, risk scores, success/failure links",
      reasoning: "Balances risk scoring model (history, blast radius, drift)",
      execution: "Updates ITSM/DevOps records with risk score and routing outcome",
    },
    kpis: []
  },
  'change-window-optimizer-agent': {
    title: "Change Window Optimizer Agent",
    description: "The Change Window Optimizer Agent is the scheduling intelligence for IT change deployments, offering proactive recommendations for the optimal execution window. It provides assistance across the change planning process, whether it's evaluating proposed change requests, analyzing business impact, considering system dependencies, or factoring in historical incident patterns and resource availability",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "20–25% fewer failed changes caused by poor timing.",
          "15–20% improvement in SLA adherence by aligning to business downtime windows.",
          "Reduced risk of incident overlap during maintenance periods.",
          "Improved efficiency of CAB and release teams — less time debating windows.",
          "Higher stakeholder trust in IT’s ability to execute changes predictably."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "25–30% of failed changes stem from poor timing or resource conflicts.",
              "Changes overlap with peak usage, increasing business disruption risk.",
              "Manual CAB scheduling lacks visibility into global calendars and dependencies.",
              "Freeze windows are inconsistently enforced, leading to compliance failures.",
              "Operations teams waste hours resolving scheduling conflicts manually."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "20–25% fewer failed changes caused by poor timing.",
              "15–20% improvement in SLA adherence by aligning to business downtime windows.",
              "Reduced risk of incident overlap during maintenance periods.",
              "Improved efficiency of CAB and release teams — less time debating windows.",
              "Higher stakeholder trust in IT’s ability to execute changes predictably."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Avoids deploying Epic/Cerner updates during patient admission or peak care hours.",
              "Finance: Schedules ERP/CRM changes outside of quarter-end and audit periods.",
              "Airline Ops: Prevents changes to scheduling systems during peak travel windows.",
              "ITSM: Globally optimizes across time zones and resolver team workloads.",
              "Cybersecurity: Avoids overlapping patch deployments that reduce SOC visibility."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Analyzes proposed change requests against global calendars and freeze periods.",
              "Considers system/service dependencies and resolver group capacity.",
              "Factors in historical incident/change patterns by time/day.",
              "Produces recommended change window with confidence score.",
              "Flags conflicts and suggests alternatives automatically."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy",
              "DevOps: Jenkins, GitHub Actions, Azure DevOps, GitLab",
              "Calendaring: Outlook, Google Workspace, ServiceNow Change Calendar",
              "CMDB: ServiceNow CMDB, BMC Atrium for dependency mapping",
              "Monitoring/Logs: Splunk, Datadog for incident/time correlation"
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-recommends optimal windows with confidence scoring.",
              "Escalates conflicts to CAB if risk cannot be resolved automatically.",
              "Governance dashboards track scheduling accuracy and SLA adherence."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Window recommendation in <30 seconds of change request.",
              "Accuracy: >90% alignment with CAB-approved final windows after tuning.",
              "Coverage: % of changes auto-scheduled vs manual debate."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "Identity & Access Guardrails: Window decisions tied to role-based CAB/governance approvals.",
              "Data Handling: Change and calendar data encrypted in transit/at rest.",
              "Audit & Traceability:  Logs every recommendation, conflict, and override decision.",
              "Certifications Supported: HIPAA, SOX, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: " Per change scheduled, per service line, or outcome-based (% failed changes reduced)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "20–25% reduction in failed changes lowers downtime cost (often $100k+/hour).",
              "Faster CAB throughput saves SME/manager labor.",
              "Reduced regulatory risk by enforcing freeze periods automatically."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for pilot services; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot in one high-volume change category.",
              "60 days: Expand across global change calendar with CAB integration.",
              "90 days: Enterprise rollout with dashboards + audit logs."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: " Extend to cloud-native deployments and CI/CD pipelines with rolling change windows."
          },
          {
            title: "Multi-Agent Interoperability",
            content: " Works with Change Risk Scoring Agent (scoring + scheduling), Release Freeze Sentinel Agent (freeze enforcement), and SLA Watcher Agent (breach prediction)."
          },
          {
            title: "Vendor Roadmap",
            content: " Predictive scheduling — forecast optimal change windows based on business cycles and incident history."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Change request submission, CAB scheduling cycle, or freeze policy trigger",
      workingPlane: "Orchestrates calendars, CMDB dependencies, resource load, incident history",
      memory: "Stores past scheduling conflicts, successful windows, incident correlations",
      reasoning: "Balances business downtime, dependencies, freeze windows, resource capacity",
      execution: "Writes recommended window into ITSM record, notifies CAB, flags conflicts",
    },
    kpis: []
  },
  'known-error-resolver-agent': {
    title: "Known Error Resolver Agent [Maps to KEDB and executes pre-approved remediation]",
    description: "The Known Error Resolver Agent automatically detects new incidents that match existing Known Errors (KEDB), triggers the pre-approved remediation workflow, and documents the fix, ensuring fast, consistent, and compliant resolution.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "40–50% reduction in MTTR for known error incidents.",
          "20–30% fewer escalations as Tier 1 can resolve issues automatically.",
          "Improved SLA compliance, especially for high-volume repeat incidents.",
          "15–20% CSAT improvement by resolving recurring issues faster.",
          "Stronger ITIL Problem Management maturity through automated KEDB use."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "20–30% of incidents are repeats of known issues already logged in the KEDB.",
              "Analysts often overlook KEDB entries due to time pressure or poor search functionality.",
              "Manual execution of remediations introduces errors and delays.",
              "Inconsistent use of KEDB erodes its value and governance credibility.",
              "Business impact grows when known errors linger, despite having documented fixes."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "40–50% reduction in MTTR for known error incidents.",
              "20–30% fewer escalations as Tier 1 can resolve issues automatically.",
              "Improved SLA compliance, especially for high-volume repeat incidents.",
              "15–20% CSAT improvement by resolving recurring issues faster.",
              "Stronger ITIL Problem Management maturity through automated KEDB use."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Resolves recurring Epic printing errors or login issues using pre-approved scripts.",
              "Finance: Fixes common trading app errors or compliance validation failures.",
              "Airline Ops: Automates remediation of repeated crew scheduling or gate system glitches.",
              "ITSM: Speeds resolution of common infrastructure issues (disk cleanup, service restarts).",
              "Cybersecurity: Applies known patches or IOC blocks tied to recurring alerts."
            ]
          },
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "NLP-based matching of incident text to KEDB entries.",
              "Automatic selection of pre-approved remediation workflows.",
              "Safe execution of fixes with rollback controls.",
              "Updates incident record with fix details and audit logs.",
              "Suggests new KEDB entries when recurring issues are detected."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM: ServiceNow, Jira Service Management, BMC, Remedy.",
              "Automation: Ansible, n8n, ServiceNow Flow, RunOps playbooks.",
              "Monitoring: Splunk, Datadog, Elastic (alert-to-KEDB correlation).",
              "Knowledge stores: Confluence, SharePoint (for KEDB storage).",
              "IAM: Azure AD, Okta for secure execution context."
            ]
          },
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Deployment Options",
            content: "Cloud-hosted, on-premises, hybrid, or delivered as Agentic Agents as a Service (AaaS)."
          },
          {
            title: "Oversight & Escalation",
            content: [
              "Auto-executes only pre-approved fixes; escalates non-approved cases.",
              "Human-in-loop validation for high-risk remediations.",
              "Generates audit-ready records for governance boards."
            ]
          },
          {
            title: "Performance Metrics",
            content: [
              "SLA: Resolution initiated within <1 min of incident classification.",
              "Accuracy: >95% matching to correct KEDB entries after tuning.",
              "Coverage: % of incidents resolved via KEDB automation."
            ]
          }
        ]
      },
      {
        title: "4. Security & Compliance",
        subSections: [
          {
            content: [
              "IAM Guardrails: Executes remediations only with approved entitlements.",
              "Data Handling: Incident + KEDB data encrypted in transit/at rest.",
              "Audit & Traceability: Every remediation logged with correlation to KEDB entry.",
              "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, PCI-DSS."
            ]
          }
        ]
      },
      {
        title: "5. Financials & Value",
        subSections: [
          {
            title: "Pricing Model",
            content: "Per matched incident, per KEDB entry managed, or outcome-based (% MTTR reduction)."
          },
          {
            title: "ROI Proof Points",
            content: [
              "40–50% MTTR reduction for repeat incidents = thousands of analyst hours saved.",
              "20–30% fewer escalations = reduced Tier 2/3 costs.",
              "Improved SLA compliance avoids penalties and improves trust."
            ]
          },
        ]
      },
      {
        title: "6. Adoption & Change Management",
        subSections: [
          {
            title: "Time to Deploy",
            content: "6–8 weeks for pilot categories; 12–14 weeks for enterprise rollout."
          },
          {
            title: "Customer Success Path",
            content: [
              "30 days: Pilot with 5–10 common known errors.",
              "60 days: Expand to 50+ entries with automated remediations.",
              "90 days: Enterprise coverage with KEDB dashboards."
            ]
          }
        ]
      },
      {
        title: "7. Future-Proofing",
        subSections: [
          {
            title: "Extensibility",
            content: "Extend beyond IT incidents to HR, Finance, and Facilities “known errors”."
          },
          {
            title: "Multi-Agent Interoperability",
            content: "Works with Similar Ticket Matcher Agent (pattern detection), Auto Article Generation Agent (creates KB entries), and Triage & Enrichment Agent (adds context)."
          },
          {
            title: "Vendor Roadmap",
            content: "Predictive known error detection — flagging candidates for KEDB before formal problem review."
          },
        ]
      }
    ],
    agenticModel: {
      trigger: "Incident creation, monitoring alert, or problem categorization.",
      workingPlane: "Orchestrates incident-to-KEDB matching and remediation execution.",
      memory: "Stores known error patterns, fixes, and historical resolution success.",
      reasoning: "Determines whether an incident matches a KEDB entry and selects the fix.",
      execution: "Executes remediation via automation playbooks, updates ticket, and closes loop.",
      kpis: "MTTR reduction %, escalations avoided, SLA compliance, repeat incident reduction."
    }
  },
  'firewall-policy-hygiene-agent': {
    title: "Firewall Policy Hygiene Agent",
    description: "This agent automates the detection of unused, shadowed, or overly permissive firewall rules, proposes cleanup actions, and executes them upon approval to harden security posture.",
    webhookUrl: 'https://ryancblackwell.app.n8n.cloud/webhook/28953092-473f-41fa-9530-422f6b870c02',
    videoPath: policyhygieneVideo,
    workflowImagePath: proImage,
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Reduced Attack Surface: Eliminates risky and unnecessary firewall rules.",
          "Improved Compliance: Provides an automated audit trail for rule validation and certification.",
          "Increased Agility: Safely accelerates firewall change management processes.",
          "Enhanced Stability: Prevents configuration errors that can cause network outages."
        ]
      }
    ],
    sections: [{
      title: "1. Business Context",
      subSections: [{
        title: "Problem Statement",
        content: [
          "Firewall rulebases grow complex over time, with 30-40% of rules often being unused, redundant, or shadowed.",
          "Overly permissive rules ('any/any') create significant security gaps and increase the network attack surface.",
          "Manual rulebase audits are time-consuming, infrequent, and prone to human error, often taking weeks for a single firewall estate.",
          "Bloated rulebases can degrade firewall performance and complicate troubleshooting for NOC and SOC teams (Tier 2/3).",
          "Compliance drift is common, leading to failed audits for standards like PCI-DSS, NIST, and HIPAA."
        ]
      }, {
        title: "Business Outcomes (Benefits)",
        content: [
          "30-50% reduction in network attack surface by eliminating risky and unnecessary rules.",
          "15-20% improvement in firewall performance and throughput by simplifying policy processing.",
          "90% reduction in manual audit and cleanup time for network security engineers.",
          "Continuous compliance with automated, auditable hygiene checks and enforcement.",
          "Faster troubleshooting for network and security incidents due to a cleaner, more understandable rulebase."
        ]
      }, {
        title: "Use Case Alignment",
        content: [
          "Security Operations: Proactive hardening of network perimeter and internal segmentation firewalls.",
          "Network Operations (Tier 2/3): Routine maintenance to ensure firewall performance and stability.",
          "Compliance & Audit: Automated evidence generation for PCI-DSS, HIPAA, and SOX audits.",
          "Cloud Security: Maintaining hygiene for virtual firewalls (e.g., Palo Alto VM-Series, FortiGate-VM) in AWS, Azure, and GCP."
        ]
      }]
    }, {
      title: "2. Technical Functionality",
      subSections: [{
        title: "Scope of Capabilities",
        content: [
          "Identifies unused rules by correlating firewall configurations with traffic logs (hit counts).",
          "Detects shadowed rules that are logically impossible to match due to preceding rules.",
          "Flags overly permissive rules containing 'any' in source, destination, or service fields.",
          "Proposes specific, safe-to-execute cleanup actions (e.g., disable, delete, tighten).",
          "Automatically generates change requests in ITSM platforms with all necessary context for approval."
        ]
      }, {
        title: "Integrations",
        content: [
          "Firewall Management: Palo Alto Panorama, Fortinet FortiManager, Cisco FMC/CDO",
          "ITSM: ServiceNow, Jira for automated change request generation and tracking",
          "SIEM & Logging: Splunk, Elastic, Datadog for traffic log analysis and hit count data",
          "Automation: Executes changes via direct API calls or through IaC tools like Ansible"
        ]
      }]
    }],
    agenticModel: {
      trigger: "Scheduled weekly/monthly scan, on-demand request from a NetSecOps admin, or post-major change window.",
      workingPlane: "Orchestrates API calls to firewall managers and SIEMs to gather configuration and traffic data.",
      memory: "Stores historical hit count data and tracks the lifecycle of rules to identify long-term unused policies.",
      reasoning: "Applies a rules engine to identify hygiene candidates based on hit count thresholds, rule shadowing logic, and permissiveness definitions.",
      execution: "Generates a detailed cleanup report, creates a ServiceNow change ticket with proposed actions, and upon approval, executes the changes via API.",
    },
    kpis: [
      { label: "Policy Evaluation Coverage", value: "99", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "High-Risk Rules Remediated", value: "97", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "Time to Detect (MTTD)", value: "2", unit: " min", target: "Avg" },
      { label: "Automation Coverage", value: "82", unit: "%", target: "Autonomous" },
      { label: "Downtime Avoidance", value: "120+", unit: " hrs", target: "This Year" },
      { label: "Cost Avoidance", value: "900K+", unit: "$", target: "Estimated" },
    ],
    solutionFlowData : [true]
  },
  'firewall-policy-creation-agent': {
    title: "Firewall Policy Creation Agent",
    description: "Automates the creation of firewall rules and objects by processing requests, validating them against security policies, and deploying the changes for rapid, compliant network access.",
    webhookUrl: 'https://ryancblackwell.app.n8n.cloud/webhook/23f646c0-6406-4902-876c-28feb7291853',
    videoPath: policyCreationVideo,
    workflowImagePath: policyCreation,
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "95%+ reduction in Mean Time To Resolution (MTTR) for firewall access requests (from days to minutes).",
          "Accelerated application deployment and business agility by removing the firewall bottleneck.",
          "Improved security posture by enforcing least-privilege access and validating every request against policy.",
          "100% compliance and auditability, with every rule change tied to an approved, justified request.",
          "Reduced operational overhead for skilled Tier 2/3 network security engineers."
        ]
      }
    ],
    sections: [{
      title: "1. Business Context",
      subSections: [{
        title: "Problem Statement",
        content: [
          "Manual firewall rule creation is a primary bottleneck in application delivery, taking days or even weeks.",
          "Incomplete or inaccurate requests lead to significant back-and-forth between application, network, and security teams.",
          "Pressure to move quickly often results in analysts creating overly permissive, risky rules that violate the principle of least privilege.",
          "Lack of standardization leads to inconsistent object naming and policy structure, increasing complexity.",
          "Manual processes are difficult to audit and often lack a clear record of business justification."
        ]
      }, {
        title: "Business Outcomes (Benefits)",
        content: [
          "95%+ reduction in Mean Time To Resolution (MTTR) for firewall access requests (from days to minutes).",
          "Accelerated application deployment and business agility by removing the firewall bottleneck.",
          "Improved security posture by enforcing least-privilege access and validating every request against policy.",
          "100% compliance and auditability, with every rule change tied to an approved, justified request.",
          "Reduced operational overhead for skilled Tier 2/3 network security engineers."
        ]
      }, {
        title: "Use Case Alignment",
        content: [
          "DevOps & AppDev: Integrating firewall rule creation into CI/CD pipelines for automated environment provisioning.",
          "IT Operations: Fulfilling standard access requests for new servers, users, or services.",
          "Cloud Engineering: Automating security group and firewall policy creation for new cloud workloads.",
          "Security Operations: Rapidly deploying rules to block threats based on IOCs from the SOC."
        ]
      }]
    }, {
      title: "2. Technical Functionality",
      subSections: [{
        title: "Scope of Capabilities",
        content: [
          "Parses structured requests from ITSM forms to extract source, destination, and service details.",
          "Validates request data against CMDB, IPAM (e.g., Infoblox), and Active Directory.",
          "Checks for conflicting or duplicate rules before proceeding with creation.",
          "Automatically creates necessary network and service objects with standardized naming conventions.",
          "Generates and deploys the firewall rule into a pre-production or production environment upon approval."
        ]
      }, {
        title: "Integrations",
        content: [
          "ITSM: ServiceNow, Jira for request intake and automated fulfillment",
          "IPAM/CMDB: Infoblox, SolarWinds, ServiceNow CMDB for data validation",
          "Firewall Management: Palo Alto Panorama, Fortinet FortiManager, Cisco FMC/CDO",
          "CI/CD Tools: Jenkins, GitLab for DevOps pipeline integration",
          "Identity: Active Directory, Azure AD for user/group context"
        ]
      }]
    }],
    agenticModel: {
      trigger: "A new firewall access request is submitted and approved in ServiceNow.",
      workingPlane: "Orchestrates data validation across ITSM, IPAM, and CMDB, and then constructs API calls to the firewall manager.",
      memory: "Maintains a record of previously created objects to avoid duplication and enforce naming standards.",
      reasoning: "Validates if the requested access conforms to the corporate security policy (e.g., blocks risky ports, checks against data center zoning).",
      execution: "Generates the configuration payload, pushes the rule and objects to the firewall manager, validates the commit, and updates the originating ticket with the status.",
    },
    kpis: [
      { label: "Automation Coverage", value: "92", unit: "%", target: "of new rule requests", color: "#10b981" },
      { label: "Right-First-Time Rate", value: "98.5", unit: "%", target: "No Rework Needed", color: "#10b981" },
      { label: "Guardrail Compliance", value: "97", unit: "%", target: "Conform to Baselines", color: "#10b981" },
      { label: "Time to Create (MTTC)", value: "6", unit: " min", target: "From Request to Push" },
      { label: "Engineer Hours Saved", value: "400+", unit: " hrs", target: "Past Year" },
      { label: "Cost Avoidance", value: "750K+", unit: "$", target: "vs. Manual Process" },
    ],
    
  },
  'firewall-lifecycle-management': {
    title: "Firewall LifeCycle Management",
    description: "Automates the end-to-end management of firewall devices, including provisioning, firmware updates, configuration backup, and decommissioning to ensure operational health and security compliance.",
    webhookUrl: 'https://ryancblackwell.app.n8n.cloud/webhook/6b5456e6-d2b3-4528-8790-620010d12bdf',
    videoPath: firewallVideo,
    workflowImagePath: lifecycleImage,
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "90% reduction in manual effort for routine lifecycle tasks like patching and backups.",
          "Strengthened security posture by ensuring all devices are consistently on vendor-recommended, secure firmware versions.",
          "Improved operational resilience with automated, versioned configuration backups.",
          "Increased stability and uptime through proactive, automated device health monitoring.",
          "Full audit trail for all device lifecycle events, from provisioning to decommissioning."
        ]
      }
    ],
    sections: [{
      title: "1. Business Context",
      subSections: [{
        title: "Problem Statement",
        content: [
          "Firmware patching across hundreds of firewalls is a high-risk, manual process that consumes thousands of engineer hours annually.",
          "Failure to patch promptly leaves the network exposed to known vulnerabilities.",
          "Inconsistent configuration backups lead to extended downtime during hardware failures.",
          "Manual health checks are infrequent, allowing issues like CPU spikes or configuration drift to go unnoticed.",
          "Device provisioning and decommissioning are complex, multi-step processes that are often delayed or incomplete."
        ]
      }, {
        title: "Business Outcomes (Benefits)",
        content: [
          "90% reduction in manual effort for routine lifecycle tasks like patching and backups.",
          "Strengthened security posture by ensuring all devices are consistently on vendor-recommended, secure firmware versions.",
          "Improved operational resilience with automated, versioned configuration backups.",
          "Increased stability and uptime through proactive, automated device health monitoring.",
          "Full audit trail for all device lifecycle events, from provisioning to decommissioning."
        ]
      }, {
        title: "Use Case Alignment",
        content: [
          "Network Operations (Tier 2/3): Automating the entire firewall patching program, including pre-checks, execution, and post-checks.",
          "Security Operations: Ensuring firewall fleet is compliant with security baselines and free of known vulnerabilities.",
          "Disaster Recovery: Guaranteeing up-to-date, restorable configurations are available for all critical devices.",
          "Infrastructure Teams: Standardizing and accelerating the deployment of new physical or virtual firewalls."
        ]
      }]
    }, {
      title: "2. Technical Functionality",
      subSections: [{
        title: "Scope of Capabilities",
        content: [
          "Automated firmware upgrades with pre-flight checks (e.g., checking HA status, taking a backup) and post-flight validation.",
          "Scheduled daily/weekly configuration backups to a secure, version-controlled repository (e.g., S3, Git).",
          "Proactive health checks for critical metrics (CPU, memory, session count, HA sync status).",
          "Zero-touch provisioning workflows for new virtual firewalls in cloud environments.",
          "Secure decommissioning workflows that wipe configurations and de-register licenses."
        ]
      }, {
        title: "Integrations",
        content: [
          "Firewall Management: Palo Alto Panorama, Fortinet FortiManager, Cisco FMC/CDO",
          "ITSM: ServiceNow for creating and managing change requests for patching and decommissioning",
          "Storage: AWS S3, Azure Blob Storage, Git for configuration backups",
          "Monitoring: Datadog, Splunk, or Prometheus for health metric ingestion",
          "Vendor APIs: Connects to vendor support portals to check for new, critical firmware releases."
        ]
      }]
    }],
    agenticModel: {
      trigger: "A new critical vulnerability is announced for a firewall OS, a weekly backup schedule is initiated, or a new firewall is needed for a project.",
      workingPlane: "Orchestrates a sequence of API calls to firewall managers, ITSM, and monitoring tools to execute the lifecycle task.",
      memory: "Maintains a database of all firewalls, their current firmware version, last backup date, and health status.",
      reasoning: "Determines the correct upgrade path for a device, identifies a pilot group for patching, and validates health check data against predefined thresholds.",
      execution: "Opens a change ticket, executes the firmware upgrade or backup, performs validation, and closes the ticket with evidence of success.",
    },
    kpis: [
      { label: "Firmware Compliance", value: "98", unit: "%", target: "Devices on Secure Versions", color: "#10b981" },
      { label: "Backup Success Rate", value: "99.9", unit: "%", target: "Daily Backups", color: "#10b981" },
      { label: "Manual Effort Reduction", value: "90", unit: "%", target: "for Routine Tasks" },
      { label: "Patch Time (Critical)", value: "24", unit: " hrs", target: "Avg Time to Patch CVE" },
      { label: "Health Check Pass Rate", value: "99", unit: "%", target: "Proactive Checks" },
      { label: "Incidents Prevented", value: "45+", unit: "", target: "via Proactive Patching" },
    ]
  },
'firewall-compliance-agent': {
    title: "Firewall Compliance Agent",
    description: "Continuously validates Palo Alto Networks firewall configurations and policies against regulatory and corporate security frameworks — PCI-DSS, HIPAA, NIST 800-53, and ISO 27001 — to ensure ongoing compliance, reduce audit risk, and automate evidence collection for governance reporting.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Continuous Compliance Assurance: Automatically audits firewall policies against regulatory control baselines after each Panorama commit.",
          "Audit Readiness: Maintains up-to-date, digitally signed evidence aligned with PCI, HIPAA, NIST, and ISO requirements.",
          "Operational Efficiency: Reduces manual audit prep effort by >80% through automated validation and mapping.",
          "Risk Reduction: Detects compliance drift early (missing logs, weak ciphers, any-any rules).",
          "Traceability & Governance: Links every firewall control to an authoritative regulation ID and policy owner."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Compliance validation across enterprise firewalls is typically a manual, spreadsheet-driven audit process performed quarterly or annually.",
              "Security teams must manually cross-reference Panorama policies, logging settings, and profiles against framework controls.",
              "This results in inconsistent coverage, missed deviations, and outdated evidence.",
              "With increasing audit frequency and zero-trust requirements, organizations require a continuous, automated compliance validation layer."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Regulatory Audit Preparation: Generate real-time audit artifacts mapped to control frameworks.",
              "Continuous Monitoring: Detect non-compliance post-commit and trigger remediation workflows.",
              "Baseline Certification: Validate configurations against vendor BPA and CoreStack policy templates.",
              "Change Control Validation: Ensure new rules meet compliance guardrails before approval.",
              "Compliance Score Tracking: Maintain framework-specific scores for dashboards and QBRs."
            ]
          },
          {
            title: "Agentic Advantage",
            content: [
              "Contextual Control Mapping: Maps PAN-OS configurations to regulatory controls automatically.",
              "Real-Time Enforcement: Monitors Panorama commits and instantly re-evaluates compliance deltas.",
              "AI Reasoning: Determines control impact severity and provides framework-specific remediation.",
              "Cross-Framework Correlation: Recognizes when one fix satisfies multiple controls.",
              "Memory Retention: Stores historical compliance baselines and remediation history.",
              "Autonomous Learning: Adjusts control logic as frameworks evolve or policies change."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Control Mapping Engine: Maps 200+ Palo Alto parameters to compliance controls across frameworks.",
              "Continuous Audit Cycle: Executes after every Panorama commit or daily cron.",
              "BPA Integration: Imports and compares BPA results for best-practice alignment.",
              "Gap Analysis & Risk Scoring: Flags missing or misconfigured features.",
              "Automated Evidence Generation: Produces signed audit packages with control mapping.",
              "Exception Management: Recognizes approved deviations and maintains waiver records.",
              "AI Recommendation Engine: Generates precise remediation guidance per framework.",
              "Reporting & Dashboards: Displays compliance posture by framework and device group."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Panorama REST/XML API (Inbound): Extract policies and commit history for compliance checks.",
              "Palo Alto BPA API (Inbound): Import vendor best-practice benchmarks.",
              "CoreStack GRC/Compliance Modules (Bidirectional): Host mappings, store evidence, manage waivers.",
              "ServiceNow/Jira (Bidirectional): Create remediation tickets with mapped control IDs.",
              "Confluence/Data Lake (Outbound): Archive audit reports and evidence logs.",
              "Azure OpenAI/RunOps Engine (Inbound): Interpret framework language and score compliance.",
              "Cortex Data Lake (Inbound): Validate logging and threat profile compliance."
            ]
          },
          {
            title: "Enterprise Performance Metrics",
            content: [
              "Firewalls Audited: 10 (Core + Cloud Edges)",
              "Policies Analyzed: 42,300",
              "Total Controls Evaluated: 214",
              "Compliant Controls: 187 (87.4%)",
              "Partial Compliances: 19 (8.8%)",
              "Non-Compliant Controls: 8 (3.7%)",
              "Framework Scores:",
              "• PCI-DSS 4.0: 91%",
              "• HIPAA §164.312: 88%",
              "• NIST 800-53 Rev 5: 92%",
              "• ISO 27001:2022: 89%",
              "Mean MTTD: 4.8 min",
              "Mean MTTR: 7.5 hrs",
              "Engineering Time Saved: ~240 hrs/month"
            ]
          }
        ]
      },
      {
        title: "3. Technical Infrastructure",
        subSections: [
          {
            title: "Service Level Agreements",
            content: [
              "Configuration Ingestion Post-Commit ≤ 5 min (Panorama webhook)",
              "Compliance Evaluation & Scoring ≤ 10 min (Multi-framework analysis)",
              "Remediation Ticket Creation ≤ 15 min (ServiceNow/Jira integration)",
              "Evidence Report Publication ≤ 1 hr (CoreStack/Confluence export)",
              "Agent Availability ≥ 99.9% (Monitored via Prometheus/Grafana)"
            ]
          },
          {
            title: "Implementation Stack",
            content: [
              "RunOps n8n automation cluster with containerized 'ComplyOps' microservice",
              "Panorama REST v11.x, BPA API, CoreStack GRC API",
              "Azure OpenAI GPT-4o fine-tuned on compliance frameworks and Palo Alto KB",
              "PostgreSQL (Memory Plane) storing compliance snapshots and deviations",
              "Read-only API access; encrypted credentials; TLS 1.3; RBAC isolation",
              "Framework Scorecard (JSON/PDF), Control Deviation Report, Audit Evidence",
              "Grafana dashboard: compliance scores, deviation trends, remediation status",
              "Fallback to last compliance snapshot; alert SOC on consecutive failures"
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Panorama commits, scheduled compliance checks, or manual audit requests",
      workingPlane: "Orchestrates configuration analysis and compliance validation across frameworks",
      memory: "Maintains compliance baselines, control mappings, and remediation history",
      reasoning: "Evaluates configurations against multiple regulatory frameworks and best practices",
      execution: "Generates compliance reports and remediation guidance for policy violations"
    },
    kpis: [
      { label: "Framework Coverage", value: "95.2", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "Compliance Accuracy", value: "97.5", unit: "%", target: "≥ 97%", color: "#10b981" },
      { label: "MTTD", value: "4.8", unit: "min", target: "< 10 min", color: "#10b981" },
      { label: "Remediation Accuracy", value: "92.3", unit: "%", target: "≥ 90%", color: "#10b981" },
      { label: "False Positive Rate", value: "2.8", unit: "%", target: "≤ 3%", color: "#10b981" },
      { label: "Evidence Latency", value: "45", unit: "min", target: "≤ 1 hr", color: "#10b981" }
    ]
  },

  'zero-trust-gateway-agent': {
    title: "Zero Trust Gateway Agent",
    description: "Dynamically applies identity, device, and contextual policy controls to inbound and outbound traffic across Palo Alto Networks gateways, ensuring every connection is explicitly verified, least-privilege aligned, and continuously validated according to zero-trust principles.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Continuous Policy Validation: Applies identity and device posture to every session, enforcing zero-trust dynamically.",
          "Reduced Attack Surface: Blocks unauthorized or risky connections by real-time correlation of user, device, and application context.",
          "Regulatory Alignment: Provides audit evidence for NIST 800-207, CISA ZT, ISO 27001 A.9, and PCI-DSS 7.2 controls.",
          "Operational Efficiency: Automates correlation between IAM, EDR, and firewall layers, eliminating manual policy updates.",
          "Adaptive Trust Decisions: Continuously recalculates session trust score; tightens or revokes access as risk increases."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprises have adopted identity-aware and device-aware security models, yet enforcement often stops at authentication — leaving network gateways enforcing static rules detached from user, device, and session context.",
              "Traditional firewall policies (IP/port based) cannot adapt to dynamic risk signals such as compromised credentials, non-compliant devices, or geolocation anomalies.",
              "Without continuous verification, zero-trust intent is broken, enabling lateral movement, data leakage, and policy bypass.",
              "Security teams need a policy enforcement layer that dynamically integrates identity, device posture, and contextual telemetry in real time."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Identity-Based Outbound Policy: Enforce least-privilege access based on user identity and application role.",
              "Device Posture-Aware Access: Block or restrict sessions from unmanaged or non-compliant devices.",
              "Inbound Zero-Trust Access: Validate external client identity and certificate posture before session establishment.",
              "Dynamic Risk Scoring: Adjust session permissions mid-flow based on threat or compliance signals.",
              "Cloud Edge Integration: Extend zero-trust enforcement to Prisma Access and hybrid gateways."
            ]
          },
          {
            title: "Agentic Advantage",
            content: [
              "Contextual Awareness: Pulls live identity, device posture, and application data to inform every enforcement decision.",
              "Autonomous Reasoning: Evaluates trust level dynamically based on multiple factors and contexts.",
              "Self-Orchestration: Updates Panorama security rules and tags without human approval once confidence exceeds threshold.",
              "Feedback Learning: Learns from manual overrides and post-incident forensics to refine trust-scoring logic.",
              "Cross-Plane Integration: Bridges IAM, EDR, and network telemetry into a unified enforcement plane."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Identity Context Ingestion: Continuously ingest user and group data from Palo Alto User-ID, Azure Entra ID, Okta, or AD.",
              "Device Posture Assessment: Integrate with Cortex XDR, CrowdStrike Falcon, Intune, or Tanium for compliance state.",
              "Adaptive Policy Mapping: Dynamically assign or update security rules based on identity and device posture.",
              "Risk Scoring Engine: Calculate real-time trust scores using behavioral, geolocation, and threat-intel factors.",
              "Session Enforcement Loop: Apply policy at connection initiation and re-evaluate periodically.",
              "Policy Simulation & Conflict Detection: Run simulations before commits.",
              "Audit & Compliance Reporting: Produce per-session trust decisions and evidence for auditors."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Panorama REST/XML API (Bidirectional): Update dynamic tags and security rules; retrieve session logs.",
              "User-ID/GlobalProtect/Prisma Access (Inbound): Identity telemetry and remote user context.",
              "Cortex XDR/CrowdStrike Falcon/Intune (Inbound): Device posture and risk signals.",
              "CoreStack GRC/Risk Graph (Bidirectional): Risk scoring thresholds, compliance mappings.",
              "ServiceNow/Jira (Bidirectional): Exception handling and change record linking.",
              "Azure OpenAI/RunOps Reasoning Engine (Inbound): AI-driven trust assessment.",
              "Confluence/Data Lake (Outbound): Store trust-decision history, compliance reports."
            ]
          },
          {
            title: "Enterprise Performance Metrics",
            content: [
              "Gateways Monitored: 10 (Core, DMZ, Cloud Edge, Remote)",
              "Sessions Analyzed: 24.6 million",
              "Identity Resolution Rate: 98.4%",
              "Device Posture Validated: 94.7%",
              "Dynamic Policy Adjustments: 6,218",
              "Auto-Revoked Sessions (High Risk): 412",
              "False Positives: 2.6%",
              "Average Trust Re-evaluation Latency: 2.9 min",
              "Improvement in Zero-Trust Score: +12% month-over-month",
              "Estimated Manual Effort Saved: ~260 engineer-hours/month",
              "Audit Reports Generated: 12 (SOC 2, NIST 800-207, ISO 27001)"
            ]
          }
        ]
      },
      {
        title: "3. Technical Infrastructure",
        subSections: [
          {
            title: "Service Level Agreements",
            content: [
              "Identity & Device Ingestion ≤ 2 min (Continuous API polling + webhooks)",
              "Trust Score Computation ≤ 1 min (AI reasoning and correlation logic)",
              "Policy Commit to Panorama ≤ 5 min (Validated + conflict-free commit)",
              "Exception Escalation to SOC ≤ 10 min (Automated ITSM ticket + alert)",
              "Evidence Publication ≤ 1 hour (CoreStack/Confluence report)",
              "Agent Availability ≥ 99.9% (Monitored via Prometheus/Grafana)"
            ]
          },
          {
            title: "Implementation Stack",
            content: [
              "RunOps n8n cluster (Azure) with containerized 'ZT-Gateway' service",
              "Panorama REST v11.x, User-ID API, GlobalProtect Telemetry, Prisma Access API",
              "Azure OpenAI GPT-4o tuned on NIST ZT principles + RunOps Zero-Trust KB",
              "PostgreSQL (Memory Plane) for trust scores + session metadata",
              "OAuth 2.0/tokenized auth, TLS 1.3, RBAC-isolated service accounts",
              "Trust Decision Log (JSON), Adaptive Policy Diff, Zero-Trust Scorecard (PDF)",
              "Grafana dashboard: trust score trend, session coverage, latency metrics",
              "Auto-retry API calls; rollback on failed commit; alert SOC if identity feed stale"
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "User-ID events, device posture changes, and security policy commits",
      workingPlane: "Orchestrates identity and device context correlation for policy enforcement",
      memory: "Maintains trust scores, session context, and enforcement history",
      reasoning: "Evaluates sessions against identity, device, and behavioral risk factors",
      execution: "Updates security policies and tags in real-time based on trust calculations"
    },
    kpis: [
      { label: "Identity Coverage", value: "98.4", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Device Coverage", value: "94.7", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "Policy Latency", value: "2.9", unit: "min", target: "< 5 min", color: "#10b981" },
      { label: "Update Accuracy", value: "96.8", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "False Positives", value: "2.6", unit: "%", target: "≤ 3%", color: "#10b981" },
      { label: "ZT Alignment", value: "92.4", unit: "%", target: "≥ 92%", color: "#10b981" }
    ]
  },

  'shadow-it-blocker-agent': {
    title: "Shadow IT Blocker Agent",
    description: "Detects, classifies, and automatically enforces policy against unauthorized or non-sanctioned SaaS and cloud applications by analyzing Palo Alto Networks App-ID, URL, and user telemetry. The agent dynamically generates rules, notifications, and governance evidence to uphold corporate SaaS security and compliance standards.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Zero-Trust SaaS Enforcement: Automatically identifies and blocks SaaS traffic that is not part of the approved enterprise catalog.",
          "Visibility & Control: Provides full inventory of SaaS applications observed via App-ID across zones, users, and devices.",
          "Operational Efficiency: Eliminates manual review of SaaS discovery logs; policy changes are generated autonomously.",
          "Risk & Compliance Assurance: Reduces data exfiltration and shadow data storage in non-compliant cloud environments.",
          "Audit Readiness: Generates evidence of SaaS enforcement aligned with ISO 27001 A.13.1.1 and NIST SC-7 controls."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Shadow IT—unauthorized use of unsanctioned SaaS applications—represents a top-tier data exposure risk.",
              "Employees regularly access unapproved services (e.g., personal file sharing, chat, CRM, AI tools) over encrypted traffic, bypassing existing security controls.",
              "While Palo Alto's App-ID and URL filtering capabilities can detect these applications, manual policy updates to block or sanction them are inconsistent and delayed.",
              "Enterprises lack an automated mechanism to continuously correlate App-ID usage with corporate SaaS registries, enforce policy dynamically, and maintain zero-trust posture."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Continuous SaaS Discovery: Detect all new SaaS applications appearing in App-ID telemetry.",
              "Dynamic Policy Enforcement: Block unauthorized or high-risk SaaS traffic via auto-generated Panorama policies.",
              "User Accountability Mapping: Correlate SaaS access attempts with user identity (User-ID).",
              "Risk-Based Policy Segmentation: Apply differential controls for 'Tolerated', 'Sanctioned', or 'Prohibited' applications.",
              "GRC Integration: Update CoreStack GRC and SOC 2 evidence repositories with policy enforcement events."
            ]
          },
          {
            title: "Agentic Advantage",
            content: [
              "Real-Time Telemetry Awareness: Monitors live App-ID, URL filtering, and SSL decryption logs from Cortex Data Lake and Panorama.",
              "Adaptive Decision-Making: Applies reasoning based on usage volume, category, and known risk posture.",
              "Cross-Context Correlation: Merges user identity, location, and business role data to assess whether SaaS use is justified.",
              "AI Reasoning & Justification: Evaluates application metadata, popularity, and data handling risk to determine action.",
              "Automated Policy Lifecycle: Creates and commits Panorama policies to block unapproved SaaS within minutes.",
              "Self-Learning Feedback: Learns from analyst overrides and usage trends to refine categorization accuracy."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "App-ID-Based SaaS Discovery: Continuously ingest Palo Alto App-ID logs from Panorama or Cortex Data Lake.",
              "Risk Classification Engine: Assigns risk ratings based on Palo Alto SaaS database, CrowdStrike Intel, and CoreStack Risk Graph.",
              "Unauthorized Application Detection: Matches observed applications against the sanctioned SaaS registry.",
              "Dynamic Policy Creation: Automatically adds or updates Panorama policies to block or restrict unauthorized SaaS.",
              "User Correlation (User-ID): Maps detected SaaS traffic to AD identities or Azure Entra ID accounts.",
              "AI Reasoning Layer: Assesses business context to recommend block, tolerate, or approve.",
              "Exception Management: Routes legitimate business-use requests for approval via ITSM.",
              "Reporting and Compliance: Generates SaaS usage dashboards and enforcement logs for audit review."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Panorama REST/XML API (Bidirectional): Retrieve App-ID logs and enforce block/allow rules.",
              "Cortex Data Lake (Inbound): Source of App-ID, URL, and SSL session telemetry.",
              "Palo Alto SaaS Security API (Inbound): Reference sanctioned SaaS catalog and risk scores.",
              "CoreStack GRC & Risk Graph (Bidirectional): Maintain authorized SaaS registry and compliance mapping.",
              "ServiceNow/Jira (Bidirectional): Create SaaS exception approval workflows and notifications.",
              "Azure Entra ID/Active Directory (Inbound): Map SaaS usage to users and groups.",
              "Azure OpenAI/RunOps Reasoning Engine (Inbound): AI-driven classification and policy justification.",
              "Confluence/Data Lake (Outbound): Archive enforcement evidence and analytics reports."
            ]
          },
          {
            title: "Enterprise Performance Metrics",
            content: [
              "Firewalls Monitored: 10 (Core, Cloud Edge, Prisma Access)",
              "Unique SaaS Apps Detected: 1,152",
              "Unauthorized/Shadow SaaS Apps: 284 (25%)",
              "High-Risk SaaS (Data Exfiltration/AI Tools): 61",
              "Blocked Applications: 228",
              "Tolerated (Under Review): 43",
              "User Exceptions Requested: 71",
              "Average Time to Detect: 2.8 min",
              "Average MTTP (Block Enforcement): 8.4 min",
              "False Positives: 2.1%",
              "Zero-Trust Compliance: 93.4%",
              "Manual Ticket Reduction: ~190 engineer-hours saved/month",
              "Audit Reports Generated: 10"
            ]
          }
        ]
      },
      {
        title: "3. Technical Infrastructure",
        subSections: [
          {
            title: "Service Level Agreements",
            content: [
              "Telemetry Ingestion ≤ 5 minutes (Continuous polling and webhook ingestion)",
              "Risk Classification & Reasoning ≤ 2 minutes (AI evaluation and registry match)",
              "Policy Enforcement Commit ≤ 10 minutes (Panorama push to firewalls)",
              "User Notification Dispatch ≤ 5 minutes (Slack/Teams + ITSM ticket)",
              "Agent Availability ≥ 99.9% (Monitored via Prometheus/Grafana)",
              "Audit Evidence Publication ≤ 1 hour post-cycle (CoreStack/Confluence export)"
            ]
          },
          {
            title: "Implementation Stack",
            content: [
              "RunOps n8n automation cluster in Azure with containerized 'ShadowGate' microservice",
              "Panorama REST API, Cortex Data Lake, Palo Alto SaaS Security API, CoreStack Risk Graph API",
              "Azure OpenAI GPT-4o fine-tuned on SaaS risk taxonomy and RunOps governance KB",
              "PostgreSQL (Memory Plane) storing SaaS categories, risk scores, and usage patterns",
              "Read-only data ingestion; least-privilege API keys; encrypted credential vault; TLS 1.3",
              "SaaS Risk Report (JSON/PDF), Block Policy Diff (CLI/API-ready), Compliance Evidence",
              "Grafana dashboard showing unauthorized SaaS trends, enforcement success, and latency",
              "Auto-retry feed ingestion; fallback to last snapshot; SOC Slack escalation on failures"
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "App-ID logs, URL filtering events, and scheduled SaaS discovery scans",
      workingPlane: "Orchestrates SaaS detection, risk assessment, and policy enforcement across Panorama estate",
      memory: "Maintains authorized SaaS registry, usage patterns, and exception history",
      reasoning: "Evaluates applications against multiple risk factors (data handling, compliance, popularity)",
      execution: "Generates and commits blocking policies for unauthorized or high-risk SaaS applications"
    },
    kpis: [
      { label: "Detection Accuracy", value: "95.2", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "False Positive Rate", value: "2.1", unit: "%", target: "≤ 3%", color: "#10b981" },
      { label: "Block Enforcement", value: "8.4", unit: "min", target: "< 10 min", color: "#10b981" },
      { label: "ZT Compliance", value: "93.4", unit: "%", target: "≥ 90%", color: "#10b981" },
      { label: "Automation Rate", value: "82", unit: "%", target: "≥ 80%", color: "#10b981" },
      { label: "User Notification", value: "96.5", unit: "%", target: "≥ 95%", color: "#10b981" }
    ]
  },

  'ssl-inspection-agent': {
    title: "SSL Inspection Agent",
    description: "Continuously validates SSL/TLS decryption configurations, certificate chains, and policy coverage across Palo Alto Networks firewalls to ensure encrypted traffic is properly inspected, compliant with organizational standards, and free from expired, mis-trusted, or bypassed flows.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Decryption Integrity Assurance: Validates that SSL inspection is active, current, and correctly applied to required traffic categories.",
          "Operational Resilience: Detects expiring or mismatched certificates before they cause outages or policy bypasses.",
          "Compliance & Privacy Alignment: Ensures decryption policies respect regulated exemptions while maintaining required inspection scope.",
          "Risk Reduction: Eliminates 'dark traffic' pathways used for malware or data loss.",
          "Audit Readiness: Produces evidence reports proving continuous certificate validation and decryption coverage."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "As >90% of internet traffic becomes encrypted, uninspected SSL/TLS sessions have become a prime channel for malware, data exfiltration, and C2 communications.",
              "Enterprises using Palo Alto Networks Decryption Policies often suffer from drift: expired intermediate CAs, incomplete root distribution, or unintentional category exclusions.",
              "Manual certificate audits and decryption tests are error-prone, inconsistent, and rarely continuous.",
              "In hybrid environments (on-prem + Prisma Access), inconsistent SSL inspection configurations create gaps in compliance with PCI-DSS 4.0, HIPAA, and ISO 27001."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Decryption Policy Coverage Validation: Verify that all appropriate URL categories, users, and zones are inspected.",
              "Certificate Lifecycle Monitoring: Detect expiring or invalid certificates in forward and inbound inspection profiles.",
              "Chain-of-Trust Verification: Confirm correct root/intermediate deployment and match across Panorama, firewalls, and clients.",
              "TLS Version & Cipher Audit: Detect deprecated protocols (TLS 1.0/1.1) or weak ciphers in use.",
              "Compliance Enforcement: Demonstrate adherence to PCI-DSS 4.0 Req 4.2 & NIST SP 800-52 Rev 2 decryption mandates."
            ]
          },
          {
            title: "Agentic Advantage",
            content: [
              "Continuous Validation: Polls Panorama and firewalls for policy and certificate states after every commit or content update.",
              "Semantic Reasoning: Understands intent—distinguishes deliberate 'no-decrypt' rules from accidental misconfigurations.",
              "AI Inference: Evaluates decryption exceptions, TLS version posture, and cipher strength using best-practice matrices.",
              "Dynamic Learning: Tracks recurring failures and owner justifications to auto-tune future alert thresholds.",
              "Cross-Plane Visibility: Correlates policy, certificate, and traffic telemetry for unified SSL hygiene scoring."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Certificate Inventory and Expiry Monitoring: Extract all forward/inbound inspection certificates, validate signature chains, expiration, and key length.",
              "Policy Coverage Analysis: Map decryption policies by zone, URL category, user group, and application to detect unintended gaps.",
              "TLS Protocol Assessment: Inspect global decryption profiles for deprecated versions and weak cipher suites.",
              "Compliance Benchmarking: Compare configuration to Palo Alto BPA and RunOps zero-trust baseline.",
              "AI Driven Risk Scoring: Calculate per-device SSL Hygiene Score factoring certificate validity, policy coverage, and protocol strength.",
              "Automated Remediation Recommendation: Suggest CA renewals, policy edits, or cipher upgrades with pre-built CLI/API snippets.",
              "Evidence Reporting: Export PDF/JSON reports to GRC systems with mapped compliance controls."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Panorama REST/XML API (Inbound): Retrieve decryption policies, certificates, and profiles; trigger post-commit checks.",
              "PAN-OS API (Inbound): Validate local certificate stores and per-vsys decryption settings.",
              "Palo Alto BPA API (Inbound): Import decryption-related BPA metrics for compliance comparison.",
              "CoreStack GRC/Compliance (Bidirectional): Map findings to control IDs, store audit artifacts.",
              "ServiceNow/Jira (Bidirectional): Auto-create tasks for expiring certificates or non-compliant TLS policies.",
              "Cortex Data Lake (Inbound): Correlate decrypted vs. non-decrypted session counts for accuracy validation.",
              "Azure OpenAI/RunOps LLM (Inbound): Interpret exceptions and generate remediation narratives.",
              "Confluence/Data Lake (Outbound): Archive SSL Hygiene Reports and evidence packages."
            ]
          },
          {
            title: "Enterprise Performance Metrics",
            content: [
              "Firewalls Analyzed: 10 (core + branch)",
              "Certificates Inventoried: 214",
              "Expiring Within 30 Days: 19 (8.8%)",
              "Invalid or Chain-Mismatched: 7",
              "'No Decrypt' Rules Detected: 56 (6.4%)",
              "Deprecated TLS Versions Enabled: TLS 1.0 on 4 devices",
              "Weak Cipher Suites Found: 11 policies (RC4, 3DES)",
              "Average SSL Hygiene Score: 83.6/100",
              "Post-Remediation Score: 92.4/100",
              "Average MTTD: 3.9 min",
              "Average MTTR: 6.2 hrs",
              "False Positives: 2.5%",
              "Audit Reports Generated: 10 (SOC 2 & PCI)",
              "Engineer Hours Saved: ~180/month"
            ]
          }
        ]
      },
      {
        title: "3. Technical Infrastructure",
        subSections: [
          {
            title: "Service Level Agreements",
            content: [
              "Post-Commit Validation ≤ 5 min (Triggered by Panorama commit hook)",
              "Certificate Expiry Alerting ≥ 30 days pre-expiry",
              "Protocol Audit Cycle: Daily (default) / On-Demand",
              "Evidence Publication ≤ 1 hr post-cycle",
              "Agent Availability ≥ 99.9%"
            ]
          },
          {
            title: "Implementation Stack",
            content: [
              "RunOps n8n cluster in Azure with containerized 'DecryptSight' module",
              "Panorama REST API v11.x, PAN-OS XML API, BPA export parser",
              "Azure OpenAI GPT-4o fine-tuned for certificate validation and policy intent analysis",
              "PostgreSQL (Memory Plane) storing trusted roots and known-good decryption policy snapshots",
              "Read-only API access, TLS 1.3 encryption, credential vault segregation",
              "SSL Hygiene Report (JSON/PDF), Certificate Expiry Matrix, Compliance Audit Package",
              "Grafana dashboard: expiry trend, coverage ratio, TLS score over time",
              "Auto-retry API queries; fallback to last snapshot; alert to SOC Slack on repeated failure"
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Panorama commit events, certificate expiry checks, or scheduled policy audits",
      workingPlane: "Orchestrates SSL/TLS configuration analysis, certificate validation, and policy coverage checks",
      memory: "Maintains certificate inventory, decryption policy states, and known-good baselines",
      reasoning: "Evaluates SSL configuration against multiple contexts (compliance, security, privacy)",
      execution: "Generates certificate renewal tasks and auto-remediates low-risk policy issues"
    },
    kpis: [
      { label: "Certificate Coverage", value: "100", unit: "%", target: "100%", color: "#10b981" },
      { label: "Policy Coverage", value: "95.5", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "Protocol Compliance", value: "100", unit: "%", target: "100%", color: "#10b981" },
      { label: "Detection Accuracy", value: "96.2", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "MTTD", value: "3.9", unit: "min", target: "< 5 min", color: "#10b981" },
      { label: "MTTR", value: "6.2", unit: "hrs", target: "< 8 hrs", color: "#10b981" }
    ]
  },

  'nat-rule-analyzer-agent': {
    title: "NAT Rule Analyzer Agent",
    description: "Continuously analyzes and classifies Network Address Translation (NAT) policies across Palo Alto Networks firewalls to detect risky, overly permissive (any-any) or mis-scoped rules, validate translation accuracy, and recommend precise corrections that preserve functionality while reducing attack surface.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Reduced Exposure: Identifies and eliminates over-permissive NAT rules that enable unintended bidirectional access.",
          "Policy Precision: Recommends narrower rule scopes (zone-to-zone, object-to-object) while preserving required application functionality.",
          "Zero-Trust Reinforcement: Ensures NAT behavior aligns with least-privilege segmentation and App-ID enforcement principles.",
          "Operational Efficiency: Automates complex NAT analysis tasks, reducing manual audit hours by >70%.",
          "Audit Assurance: Provides BPA-aligned compliance evidence showing NAT hygiene improvement and adherence to vendor best practices."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprises frequently accumulate broad-scope NAT policies (e.g., any → any → any) as temporary measures that later persist indefinitely.",
              "NAT configurations are among the most overlooked yet critical parts of firewall posture; errors can expose internal systems, cause asymmetric routing, or bypass security policies entirely.",
              "Manual NAT audits using Panorama exports or CLI inspection are error-prone, inconsistent across device groups, and cannot scale to hybrid environments.",
              "With the rise of micro-segmentation and zero-trust architectures, NAT hygiene is now a compliance and security imperative, not a maintenance task."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Exposure Detection: Identify inbound/outbound NATs that expose internal hosts without security policy enforcement.",
              "Misconfigured DNAT/SNAT Discovery: Detect missing reverse mappings or incorrect bi-directional translations.",
              "Zone Drift Analysis: Flag NAT rules bridging zones that violate segmentation standards.",
              "Shadowed/Overlapping NAT Identification: Highlight redundant or masked rules impacting rule order logic.",
              "Migration Validation: Validate NAT accuracy post-migration from legacy to Panorama or cloud environments."
            ]
          },
          {
            title: "Agentic Advantage",
            content: [
              "Automated Awareness: Monitors Panorama commit events and retrieves NAT table changes instantly.",
              "Semantic Understanding: Evaluates NAT rule intent, not just syntax—correlating with associated security and routing policies.",
              "AI Reasoning: Detects rule anomalies such as any-any translations, port ranges > 1024, or cross-zone NATs violating internal policy.",
              "Dynamic Learning: Adapts to historical false positives and known exception zones, tuning its detection thresholds automatically.",
              "Speed and Scale: Analyzes entire device-group NAT tables in seconds across dozens of firewalls.",
              "Predictive Simulation: Uses pre-commit modeling to forecast how a new or modified NAT rule would affect existing traffic flows."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Continuous NAT Table Ingestion: Retrieves full NAT configurations from Panorama and device APIs post-commit or on schedule.",
              "Heuristic Risk Scoring: Assigns risk scores based on rule breadth (any-any matches), port exposure, and zone pairing.",
              "Overlap & Shadow Detection: Identifies redundant, masked, or conflicting NAT entries using policy order simulation.",
              "Scope Optimization Recommendations: Suggests object-based refinements (specific source/destination/service).",
              "AI-Assisted Justification Analysis: Reads comments/tags to determine business intent and validate exceptions.",
              "Compliance Validation: Cross-checks NAT rules against Palo Alto BPA security zones and zero-trust segmentation templates.",
              "Integration with Change Simulation Agent: Runs pre-deployment NAT simulations to predict policy behavior changes.",
              "Remediation Pathway: Generates pre-formatted Panorama CLI or API commands for safe correction.",
              "Reporting & Evidence: Produces detailed NAT Hygiene Score reports and compliance deltas per device group."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Panorama REST/XML API (Bidirectional): Retrieve NAT configurations, rule IDs, and commit events; push recommendations or tags.",
              "PAN-OS CLI/API (Inbound): Validate translations and reverse mappings on device level.",
              "Cortex Data Lake (Inbound): Correlate NAT usage and hit counts for relevance scoring.",
              "CoreStack GRC (Bidirectional): Align risky NAT findings with compliance controls (NIST SC-7, PCI 1.3).",
              "ServiceNow/Jira (Bidirectional): Open remediation or review tickets for confirmed risky NATs.",
              "Azure OpenAI/RunOps Reasoning Engine (Inbound): Classify risk type, generate remediation text, explain findings.",
              "Confluence/Data Lake (Outbound): Archive NAT drift reports and compliance evidence."
            ]
          },
          {
            title: "Enterprise Performance Metrics",
            content: [
              "Firewalls Analyzed: 10 (Core, DMZ, Branch, Cloud)",
              "NAT Rules Evaluated: 8,420",
              "Any-Any or Broad Scope Rules Detected: 376 (4.4%)",
              "Shadowed/Overlapping Rules: 98",
              "Unmapped Reverse DNATs: 42",
              "Cross-Zone NAT Violations: 67",
              "High-Risk NATs (AI Confidence ≥ 0.9): 123",
              "Scoped Recommendations Issued: 421",
              "Adopted Corrections: 343 (81%)",
              "Average MTTD: 3.2 minutes post-commit",
              "Average MTTR: 6.5 hours",
              "False Positives: 2.7%",
              "Improvement in NAT Hygiene Score: +18% (month-over-month)",
              "Estimated Manual Effort Saved: ~210 engineer-hours/month"
            ]
          }
        ]
      },
      {
        title: "3. Technical Infrastructure",
        subSections: [
          {
            title: "Service Level Agreements",
            content: [
              "NAT Table Retrieval (Post-Commit) ≤ 5 minutes",
              "Risk Classification & Scoring ≤ 2 minutes",
              "Ticket Creation/Notification ≤ 10 minutes",
              "Scoped Correction Delivery ≤ 1 hour",
              "Agent Availability ≥ 99.9%",
              "Evidence Publication ≤ 24 hours"
            ]
          },
          {
            title: "Implementation Stack",
            content: [
              "RunOps n8n automation cluster (Azure) with containerized 'NATInsight' microservice",
              "Panorama REST API v11.x, PAN-OS XML API, CoreStack GRC, Cortex Data Lake log API",
              "RunOps NAT Hygiene Model (normalized rule attributes: src/dst/service/app/zone/type)",
              "Azure OpenAI GPT-4o tuned for Palo Alto NAT object analysis and policy intent detection",
              "PostgreSQL (Memory Plane) for NAT state snapshots; Confluence for historical audit records",
              "Read-only Panorama API key (24-hr rotation), encrypted vault credentials, TLS 1.3",
              "NAT Risk Report (JSON/PDF), Scoped Correction Recommendation, Compliance Evidence Package",
              "Grafana dashboard: risky NAT trendline, false-positive ratio, zone alignment score"
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Panorama commit events, scheduled NAT table scans, or manual audit requests",
      workingPlane: "Orchestrates NAT configuration analysis, risk scoring, and optimization across Panorama estate",
      memory: "Maintains historical NAT patterns, remediation outcomes, and exception cases to refine detection",
      reasoning: "Evaluates NAT policies against multiple contexts (intent, scope, compliance, zero-trust alignment)",
      execution: "Generates precise scope corrections and auto-remediates low-risk NAT issues"
    },
    kpis: [
      { label: "Detection Accuracy", value: "95.2", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "False Positive Rate", value: "2.7", unit: "%", target: "≤ 3%", color: "#10b981" },
      { label: "Correction Adoption", value: "81", unit: "%", target: "≥ 80%", color: "#10b981" },
      { label: "MTTD", value: "3.2", unit: "min", target: "< 5 min", color: "#10b981" },
      { label: "MTTR", value: "6.5", unit: "hrs", target: "< 8 hrs", color: "#10b981" },
      { label: "Zero-Trust Alignment", value: "92", unit: "%", target: "≥ 90%", color: "#10b981" }
    ]
  },
  
  'firewall-drift-detector-agent': {
    title: "Firewall Drift Detector Agent",
    description: "Continuously monitors Palo Alto Networks firewalls and Panorama configurations for deviation (\"drift\") from approved baselines and vendor best-practice standards, proactively identifying risk before it impacts compliance, stability, or security posture.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Early Drift Detection: Identifies unauthorized or unapproved configuration changes in near real time.",
          "Consistency & Compliance: Enforces alignment with corporate baselines and Palo Alto Networks Best Practice Assessment (BPA) standards.",
          "Operational Stability: Detects conflicts before they cause service interruptions or failover inconsistencies.",
          "Audit Assurance: Produces verifiable evidence of continuous compliance monitoring.",
          "Reduced Remediation Cost: Auto-classifies low-risk drift for agentic correction, minimizing engineer rework."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprise firewall configurations drift from hardened baselines due to ad hoc changes, patches, migrations, and emergency commits.",
              "Deviations often result in inconsistent zone definitions, disabled security profiles, mismatched logging policies, and stale shared objects.",
              "Manual configuration audits are reactive, time-consuming, and dependent on human comparison of XML exports.",
              "Configuration drift weakens zero-trust posture and increases exposure to known misconfiguration vectors (CVE-2024-3400, CVE-2023-0009)."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Continuous Compliance Monitoring: Detects divergence from PCI-DSS, SOC 2, and NIST 800-53 requirements.",
              "Baseline Integrity Enforcement: Validates every commit against approved device-group or shared policy baselines.",
              "Vendor Best Practice Validation: Compares configurations to Palo Alto BPA standards and RunOps templates.",
              "Zero-Trust Drift Detection: Flags rules or policies that reintroduce overly permissive traffic flows.",
              "Change Audit Readiness: Maintains versioned evidence of all drift detections for auditor review."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Baseline Comparison Engine: Continuously compares running and candidate configurations against stored gold baselines.",
              "Vendor BPA Integration: Leverages Palo Alto Networks BPA XML/JSON output for standards alignment.",
              "Semantic Drift Detection: Detects policy and configuration-level divergence across zones, profiles, and logging.",
              "Automated Classification: Categorizes drift as config variance, policy variance, or compliance deviation.",
              "AI Reasoning Layer: Uses RunOps reasoning to determine if drift is intentional or anomalous.",
              "Exception Management: Validates drift against ITSM change records and maintenance windows.",
              "Remediation Pathway: Suggests corrective actions or triggers automated rollback for high-severity drift.",
              "Audit Artifact Generation: Produces detailed drift reports with timestamps and remediation status."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Panorama REST/XML API: Retrieve configurations and subscribe to commit events.",
              "Palo Alto BPA API: Import vendor best-practice baselines for comparison.",
              "CoreStack GRC: Store compliance deviation data and align with control IDs.",
              "ServiceNow/Jira: Create and track drift incidents and change tasks.",
              "Cortex Data Lake: Correlate drift with traffic impact for prioritization.",
              "Azure OpenAI/RunOps LLM: Contextual reasoning for drift classification.",
              "Confluence/Data Lake: Archive drift reports and compliance evidence."
            ]
          }
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Key Performance Indicators",
            content: [
              "Baseline Drift Detection Rate: 100% of devices scanned daily",
              "Drift Accuracy Rate ≥ 95% (validated vs. false positives)",
              "Drift Resolution SLA Compliance ≥ 90%",
              "Mean Time to Detect (MTTD) < 5 minutes",
              "Auto-Correction Success Rate ≥ 85%",
              "Compliance Alignment Score ≥ 90%"
            ]
          },
          {
            title: "Service Level Agreements",
            content: [
              "Drift Detection Post-Commit ≤ 5 minutes",
              "Drift Classification ≤ 2 minutes",
              "Incident Creation ≤ 10 minutes",
              "Auto-Remediation ≤ 30 minutes",
              "Evidence Publication ≤ 1 hour",
              "Agent Availability ≥ 99.9%"
            ]
          }
        ]
      },
      {
        title: "4. Technical Infrastructure",
        subSections: [
          {
            content: [
              "RunOps n8n automation cluster with containerized DriftSense engine",
              "Panorama REST API v11.x and PAN-OS XML API integration",
              "Azure OpenAI GPT-4o model fine-tuned for config evaluation",
              "PostgreSQL Memory Plane for baseline and drift state management",
              "Read-only API access with TLS 1.3 encryption and RBAC isolation",
              "Prometheus/Grafana monitoring for drift metrics and health checks"
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Panorama commit events, scheduled baseline checks, or manual audit requests.",
      workingPlane: "Orchestrates configuration retrieval, comparison, and drift analysis across Panorama estate.",
      memory: "Maintains historical baselines, remediation outcomes, and exception patterns to refine detection.",
      reasoning: "Evaluates configuration changes against multiple contextual factors (intent, scope, compliance).",
      execution: "Auto-remediates low-risk drift and generates evidence packages for audit trails."
    },
    kpis: [
      { label: "Drift Detection Rate", value: "100", unit: "%", target: "100%", color: "#10b981" },
      { label: "Detection Accuracy", value: "95.6", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "Resolution SLA", value: "92", unit: "%", target: "≥ 90%", color: "#10b981" },
      { label: "MTTD", value: "3.6", unit: "min", target: "< 5 min", color: "#10b981" },
      { label: "Auto-Correction", value: "87", unit: "%", target: "≥ 85%", color: "#10b981" },
      { label: "Compliance Score", value: "92.8", unit: "%", target: "≥ 90%", color: "#10b981" }
    ]
  },

  'threat-feed-sync-agent': {
    title: "Threat Feed Sync Agent",
    description: "Dynamically ingests, enriches, and synchronizes threat intelligence indicators (IPs, domains, URLs, and file hashes) from internal and external sources into Palo Alto Networks Dynamic Address Groups (DAGs) and security policies, ensuring real-time defense alignment without manual policy intervention.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Continuous Threat Alignment: Automatically synchronizes critical IPs/domains into Palo Alto Dynamic Address Groups (DAGs) within minutes of publication.",
          "Zero-Trust Enforcement Agility: Dynamically blocks or isolates malicious sources/destinations without manual rule edits.",
          "Operational Efficiency: Eliminates human intervention in feed ingestion and policy object updates.",
          "Cross-Domain Correlation: Merges threat indicators from multiple trusted sources into a unified model.",
          "Audit and Traceability: Maintains complete visibility of every feed ingestion event, enrichment action, and applied rule change.",
          "Reduced Mean Time to Protect (MTTP): Converts threat intelligence ingestion from a daily manual task to continuous, autonomous enforcement."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "In modern hybrid enterprises, threat intelligence data (IoCs) evolves hourly — yet firewalls typically rely on static rulebases or manually updated address objects.",
              "Manual imports of IP/domain indicators through Panorama or security teams introduce latency, inconsistencies, and operational blind spots.",
              "Traditional SIEM-driven feeds often lack contextual prioritization and API-level enforcement, meaning high-severity IoCs may remain unenforced for hours or days.",
              "This lag leads to increased exposure to ransomware, botnets, and zero-day campaigns, particularly when IoCs are published in CISA KEV, MSRC, or HHS HC3 alerts but not yet reflected in policy."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Dynamic Blocklists/Allowlists: Populate DAGs based on malicious IPs, domains, or URLs from curated threat sources.",
              "Targeted Campaign Defense: Rapidly ingest IoCs from active campaigns (e.g., Qilin ransomware, CISA KEV alerts).",
              "Sector-Specific Threat Protection: Sync healthcare, finance, or government-sector threat feeds to tailor policy posture.",
              "Incident Response Acceleration: Allow SOC analysts to push newly discovered IoCs directly into production DAGs via ITSM integration.",
              "Multi-Tenant Feed Segmentation: Maintain unique feed sets per business unit, site, or device group within Panorama."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Threat Feed Aggregation: Ingests IPs, domains, URLs, and file hashes from open and commercial feeds.",
              "Data Normalization & Enrichment: Standardizes feed formats (JSON, STIX/TAXII, CSV), enriches with metadata.",
              "DAG Population & Policy Binding: Updates Panorama Dynamic Address Groups automatically via REST API calls.",
              "Change Simulation Hook: Integrates with the Change Simulation Agent to pre-model feed-driven rule updates.",
              "Hit Count Feedback: Collects post-enforcement telemetry from Cortex Data Lake or Panorama logs.",
              "Confidence Scoring: AI model evaluates feeds by reputation, duplication rate, and false-positive ratio.",
              "Automatic Aging & Cleanup: Retires stale IoCs after defined TTL or if no hits occur within aging thresholds.",
              "Compliance Mapping: Tags each feed to compliance categories for audit evidence."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Panorama REST API: Create and update Dynamic Address Groups, commit configuration changes safely.",
              "PAN-OS API: Push emergency IoC updates directly to firewalls during threat escalations.",
              "Cortex Data Lake/AutoFocus: Collect enforcement hit counts and threat context for feedback loop.",
              "CoreStack SecOps/Threat Graph: Align threat indicators with asset and compliance posture.",
              "External Feeds: Ingest IoCs from CISA, AlienVault, MISP, CrowdStrike in multiple formats.",
              "ServiceNow/Jira: Log change events, raise approvals for high-impact feed activations.",
              "Azure OpenAI/RunOps LLM: Reason over IoC metadata and assess contextual risk."
            ]
          }
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Key Performance Indicators",
            content: [
              "Feed Update Frequency: Real-time (≤ 10 min per source)",
              "DAG Synchronization Accuracy ≥ 99.5%",
              "IoC Deduplication Efficiency ≥ 98%",
              "False Positive Rate ≤ 2%",
              "Mean Time to Protect (MTTP) < 5 minutes",
              "Feed Source Reliability Score ≥ 90%"
            ]
          },
          {
            title: "Service Level Agreements",
            content: [
              "Feed Polling Interval ≤ 10 minutes",
              "Policy Update Commit ≤ 5 minutes",
              "Emergency Feed Propagation ≤ 2 minutes",
              "False Positive Review SLA ≤ 24 hours",
              "System Availability ≥ 99.9%",
              "Evidence Retention: 12 months minimum"
            ]
          }
        ]
      },
      {
        title: "4. Technical Infrastructure",
        subSections: [
          {
            content: [
              "RunOps n8n orchestration cluster with ThreatSync workflow nodes",
              "Panorama REST API v11.x and PAN-OS XML API integration",
              "Support for STIX/TAXII, JSON, CSV, XML via n8n dynamic parsers",
              "Azure OpenAI GPT-4o model for context evaluation and scoring",
              "PostgreSQL Memory Plane for state management",
              "Secure API access with 24-hour key rotation and TLS 1.3",
              "Integration with Change Simulation Agent for validation",
              "Prometheus metrics for real-time monitoring"
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Feed update webhook, scheduled poll, or manual SOC request.",
      workingPlane: "Orchestrates feed ingestion, normalization, and DAG updates across multiple APIs and platforms.",
      memory: "Stores historical IoC effectiveness, false positives, and feed reliability metrics to optimize future decisions.",
      reasoning: "Evaluates IoC relevance, severity, and reliability using AI-driven contextual analysis.",
      execution: "Updates DAGs, commits changes, and generates compliance evidence autonomously."
    },
    kpis: [
      { label: "Feed Update Time", value: "8", unit: "min", target: "≤ 10 min", color: "#10b981" },
      { label: "DAG Sync Accuracy", value: "99.7", unit: "%", target: "≥ 99.5%", color: "#10b981" },
      { label: "Dedup Efficiency", value: "97.2", unit: "%", target: "≥ 98%", color: "#f59e0b" },
      { label: "False Positive Rate", value: "1.8", unit: "%", target: "≤ 2%", color: "#10b981" },
      { label: "MTTP", value: "4.6", unit: "min", target: "< 5 min", color: "#10b981" },
      { label: "Feed Reliability", value: "92", unit: "%", target: "≥ 90%", color: "#10b981" }
    ]
  },
  
  'change-simulation-agent': {
    title: "Change Simulation Agent",
    description: "Models proposed firewall rule changes prior to deployment across Palo Alto Networks environments to detect policy conflicts, redundant rules, and potential security posture regressions—preventing misconfiguration and outage risk before commit.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Change Accuracy: Automatically validates proposed security rules for redundancy, overlap, and shadowing before commit, reducing post-deployment incidents.",
          "Operational Resilience: Detects conflicts between shared and local device group policies, preventing zone bridging or NAT collision errors.",
          "Speed with Control: Integrates with ITSM workflows to deliver same-day approvals while maintaining automated pre-commit analysis.",
          "Audit and Traceability: Documents every simulated change with predicted outcomes, affected objects, and compliance mappings.",
          "Cost Avoidance: Reduces rework and outage mitigation labor by up to 60% through pre-commit verification and dependency analysis."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "In large enterprises, hundreds of rule changes are requested monthly across Panorama-managed firewalls, creating high potential for policy conflict, zone leakage, or rule shadowing.",
              "Manual peer review and CAB processes rely on static spreadsheets or visual inspection—insufficient for understanding compound effects of multi-device or multi-zone policy changes.",
              "Once committed to Panorama, rollback or correction is expensive, time-consuming, and potentially service-impacting.",
              "Modern continuous-delivery environments require predictive rule analysis and automated impact modeling before deployment to maintain change velocity without compromising security or compliance."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Pre-Commit Risk Validation: Analyze proposed security rules before Panorama commit to detect conflict, duplication, or over-permissiveness.",
              "Zero-Trust Enforcement Validation: Confirm that new or modified rules do not violate existing zone segmentation or App-ID restrictions.",
              "Change Request Governance: Integrate simulation results into ServiceNow/Jira change approvals for CAB visibility.",
              "Shared vs. Local Rule Impact Analysis: Validate how global shared objects affect local policies across device groups.",
              "Policy Optimization: Recommend merging or repositioning rules to minimize policy sprawl."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Pre-Commit Rule Modeling: Replicates proposed Panorama rule changes in a sandbox model using actual PAN-OS rule evaluation order.",
              "Conflict Detection: Identifies overlapping, redundant, or shadowed rules between shared, template, and local rulebases.",
              "Impact Visualization: Provides network and policy path simulation (source → zone → destination → application → service) before commit.",
              "Zero-Trust Drift Detection: Validates that new rules maintain least-privilege access and don't re-open deprecated zones or service categories.",
              "Change Approval Integration: Automatically attaches simulation findings to ServiceNow/Jira tickets for review.",
              "AI Reasoning Engine: Interprets simulation output to score risk level (Low/Moderate/Critical) and provide human-readable remediation advice.",
              "Continuous Learning: Feeds post-deployment logs and approval outcomes back into the Memory Plane to tune rule-conflict prediction models."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Panorama REST/XML API: Retrieve current running config, candidate config, rule order, objects, and zone relationships.",
              "PAN-OS API: Perform local rule validation when device-specific exceptions exist.",
              "Prisma Access API: Extend simulation to cloud firewalls and remote gateways.",
              "CoreStack GRC: Tag simulations to control IDs and compliance evidence.",
              "ServiceNow/Jira: Trigger simulations from pending changes; return results with risk scores.",
              "Splunk/Cortex Data Lake: Correlate historical traffic and hit-count data for impact modeling.",
              "Azure OpenAI/RunOps LLM: Generate risk narrative summaries and remediation recommendations."
            ]
          }
        ]
      },
      {
        title: "3. Operational Model",
        subSections: [
          {
            title: "Key Performance Indicators",
            content: [
              "Simulation Coverage Rate ≥ 98% (proposed changes automatically simulated before commit).",
              "Conflict Detection Accuracy ≥ 95% (match rate of predicted vs. actual post-commit conflicts).",
              "False Positive Rate ≤ 5% (incorrectly flagged non-issues per simulation cycle).",
              "Average Simulation Time < 5 minutes (model build and report generation per change set).",
              "CAB Review Reduction ≥ 50% (changes approved automatically using simulation evidence).",
              "Post-Change Incident Reduction ≥ 60% (drop in rule rollback or outage tickets)."
            ]
          },
          {
            title: "Service Level Agreements",
            content: [
              "Simulation Trigger Post-Change Request ≤ 5 minutes (automated trigger via ITSM webhook).",
              "Simulation Completion Time ≤ 10 minutes (includes conflict analysis and risk scoring).",
              "Result Delivery to CAB/ITSM ≤ 15 minutes (report + AI narrative attached to ticket).",
              "System Availability ≥ 99.9% (continuous monitoring via Prometheus/Grafana).",
              "Evidence Export for Audit ≤ 30 minutes (Confluence/CoreStack PDF + JSON artifact)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Infrastructure",
        subSections: [
          {
            content: [
              "n8n-based RunOps automation cluster in Azure with sandboxed Sim-Engine container for modeling.",
              "Panorama REST API (v11.x) and PAN-OS XML API for rule retrieval and config comparison.",
              "Azure OpenAI GPT-4o model trained on PAN-OS rule logic and MITRE ATT&CK mappings.",
              "Custom RunOps Policy Flow Evaluator module replicates PAN-OS match logic.",
              "Read-only service accounts and encrypted vault credentials for secure API access."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Change request reaches 'Pending Approval' or manual request from CAB/security team.",
      workingPlane: "Orchestrates API calls to Panorama, ITSM, and monitoring systems for complete change context.",
      memory: "Retains simulation outcomes, override history, and effectiveness metrics to improve future predictions.",
      reasoning: "Evaluates rule changes against known conflict patterns, zero-trust principles, and compliance requirements.",
      execution: "Generates detailed simulation reports with risk scores and remediation guidance for CAB review."
    },
    kpis: [
      { label: "Simulation Coverage", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Detection Accuracy", value: "95", unit: "%", target: "≥ 95%", color: "#2563eb" },
      { label: "False Positive Rate", value: "4.6", unit: "%", target: "≤ 5%", color: "#f59e0b" },
      { label: "Average Sim Time", value: "3.7", unit: "min", target: "< 5 min", color: "#10b981" },
      { label: "CAB Review Reduction", value: "50", unit: "%", target: "≥ 50%", color: "#2563eb" },
      { label: "Incident Reduction", value: "60", unit: "%", target: "≥ 60%", color: "#10b981" }
    ]
  },

  'rule-recertification-agent': {
    title: "Rule Recertification Agent",
    description: "Automates periodic firewall rule recertification with rule owners and approvers to maintain continuous compliance, eliminate configuration drift, and uphold least-privilege design standards across Palo Alto Networks environments.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Compliance Enforcement: Automates quarterly or annual rule attestation across all Panorama-managed device groups to maintain continuous SOC 2 / PCI-DSS readiness.",
          "Operational Efficiency: Reduces manual review workloads by up to 75%, freeing senior engineers for architecture improvements instead of spreadsheet auditing.",
          "Ownership Accountability: Links every active rule to a verified business owner and change record, strengthening governance and audit traceability.",
          "Zero-Trust Assurance: Validates that “allow” rules remain justified, scoped by App-ID, and properly segmented by zone.",
          "Audit Evidence Generation: Produces digitally signed audit packages with timestamps, signatures, and historical deltas—ready for submission to internal or external auditors."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprise Panorama-managed firewall estates accumulate thousands of rules over time from organic growth, acquisitions, and architecture changes.",
              "Manual recertification workflows (spreadsheets, static exports, email approvals) are slow, inconsistent, and frequently fail audit windows.",
              "Ownership and justification metadata decays, leaving unused, shadowed, or unjustified rules in production that violate least-privilege objectives.",
              "Auditors require provable periodic validation (PCI-DSS 1.1.7, SOC 2 CC7.2, ISO A.12.6.2), which is difficult to deliver with purely human-driven cycles."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Automated attestation cycles that ensure rules are reviewed, justified, or retired on schedule.",
              "Significant reduction in manual effort and faster evidence delivery for audits.",
              "Clear ownership and traceability for every active rule tied to CMDB/ITSM records.",
              "Stronger alignment to zero-trust principles by validating scope (App-ID, zones) and removing overly permissive rules."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Firewall Audit Readiness: Continuous evidence trails for PCI-DSS, SOC 2, and ISO 27001.",
              "Zero-Trust Program Enablement: Confirms rules align with least-privilege and App-ID controls.",
              "Policy Lifecycle Management: Forces re-evaluation or retirement of rules older than policy-defined intervals.",
              "Exception Lifecycle Automation: Expires or renews temporary exceptions automatically and tracks approvals."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Automated Campaign Management: Launch recertification campaigns on schedules (quarterly, annual) or triggers (Panorama commit, policy age).",
              "Panorama Data Ingestion: Collect rule metadata (owner, tags, zone, service, App-ID, hit counts, modified time, comments) via REST/XML APIs.",
              "Owner Mapping: Match rule tags to CMDB/ITSM ownership records and surface gaps for reconciliation.",
              "Notification & Workflow Automation: Create tasks in ServiceNow/Jira and send contextual Slack/Teams messages with direct approval links.",
              "AI-Assisted Review: Use Azure OpenAI to analyze owner feedback, detect weak justifications, and recommend low-risk closures.",
              "Exception Governance: Detect EXC_ rules, monitor expiry/renewal, and escalate overdue exceptions.",
              "Evidence Generation: Produce digitally signed audit packages (JSON + PDF) with historical deltas, timestamps, and signatures.",
            ]
          },
          {
            title: "Integrations",
            content: [
              "Panorama REST / XML API: Rulebase extraction, metadata retrieval, commit history (Inbound).",
              "Prisma Access / Cloud Firewall APIs: Include cloud-delivered nodes and remote gateways in scope.",
              "CoreStack GRC / Confluence: Trigger campaigns and store generated evidence (Bidirectional/Outbound).",
              "ServiceNow / Jira: Create and update recertification tasks, capture approvals, close change records (Bidirectional).",
              "CMDB / Asset Repository: Validate owner mappings and business application context (Inbound).",
              "Slack / Teams: Inline approvals and SLA reminders (Bidirectional).",
              "Azure OpenAI / Reasoning Engine: NLP justification analysis and confidence scoring (Inbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Triggered Campaigns: Scheduled or event-driven recertification launch (baseline automation).",
              "Tier 2 — Data Collection & Notification: Aggregates rule data, maps owners, issues ITSM/ChatOps tasks.",
              "Tier 3 — AI Reasoning & Recommendation: Applies contextual reasoning to assess justifications and recommend closure.",
              "Tier 4 — Continuous Compliance Loop: Self-adjusts frequency and thresholds based on observed risk trends."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Recertification Completion Rate ≥ 95% per cycle.",
              "Owner Response SLA Compliance ≥ 90% (configurable windows).",
              "AI Auto-Approval Accuracy ≥ 92% with evidence for audit validation.",
              "Audit Evidence Latency ≤ 60 minutes from cycle closure to publication."
            ]
          }
        ]
      },
      {
        title: "4. Evidence & Audit",
        subSections: [
          {
            content: [
              "Generates immutable evidence packages: signed PDF + JSON exports, historical deltas, and approval trails.",
              "Stores evidence in Confluence/CoreStack GRC or data lake with retention metadata for auditors.",
              "Supports cryptographic signing and time-stamping for non-repudiation in high-assurance environments."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Scheduled recertification windows, Panorama commit events, or GRC-driven triggers.",
      workingPlane: "Orchestrates API calls to Panorama, GRC, ITSM, and chat platforms; maintains an operational state store in Postgres.",
      memory: "Persists prior justifications, approval history, exception lifecycles, and delta snapshots to inform future decisions.",
      reasoning: "NLP-based justification scoring and policy heuristics to recommend closure, renewal, or escalation.",
      execution: "Creates ITSM tasks, sends chat approvals, auto-closes low-risk rules when confidence thresholds are met, and packages audit evidence."
    },
    kpis: [
      { label: "Recertification Completion Rate", value: "95", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "Owner SLA Compliance", value: "90", unit: "%", target: "≥ 90%", color: "#2563eb" },
      { label: "AI Auto-Approval Accuracy", value: "92", unit: "%", target: "≥ 92%", color: "#f59e0b" },
      { label: "Evidence Publication Latency", value: "60", unit: "mins", target: "≤ 60 mins", color: "#ef4444" }
    ]
  },  

 'wi-fi-optimization-agent': {
    title: "Wi-Fi Optimization Agent",
    description: "Continuously monitors Wi-Fi signal strength, channel utilization, interference, and coverage across enterprise access points (APs) to detect performance degradation, optimize RF settings, and ensure seamless, high-quality wireless connectivity for users and devices.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Improved User Experience: Maintains consistent Wi-Fi performance and coverage across all devices and zones.",
          "Reduced RF Interference: Detects and mitigates co-channel and adjacent-channel interference automatically.",
          "Operational Efficiency: Minimizes manual RF tuning through automated, self-healing optimization.",
          "Performance & Capacity Assurance: Dynamically balances load across APs and frequency bands.",
          "SLA & Compliance Validation: Provides continuous evidence of Wi-Fi performance for internal SLAs and audits."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "As campuses scale, Wi-Fi environments become congested due to overlapping coverage, RF interference, and poor channel distribution.",
              "Static AP configurations cannot adapt to fluctuating user density, IoT traffic, or environmental interference.",
              "Manual troubleshooting leads to downtime, inconsistent performance, and a poor user experience.",
              "Enterprises require a self-optimizing agent to sense RF conditions in real-time and automatically adjust configurations for optimal coverage and throughput."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Consistent, high-quality Wi-Fi across all zones and devices.",
              "Automated detection and mitigation of co-channel and non-Wi-Fi interference.",
              "Significant reduction in manual RF tuning effort and associated operational costs.",
              "Proactive load balancing and dynamic capacity adjustment.",
              "Continuous, auditable evidence of meeting internal Wi-Fi Service Level Agreements (SLAs)."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Coverage Gap Detection: Identify physical zones with weak or missing Wi-Fi signal.",
              "Interference & Noise Monitoring: Detect and mitigate competing RF sources or rogue APs.",
              "Dynamic Channel & Power Optimization: Automatically adjust AP channels and transmit power for maximum efficiency.",
              "Load Balancing & Band Steering: Redirect users to underutilized APs or less congested frequency bands.",
              "User Experience Assurance: Continuously measure key user metrics like latency, throughput, and SNR."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Signal Strength & Coverage Mapping: Continuously measure RSSI and SNR across all APs and clients.",
              "Interference Detection: Identify co-channel interference (CCI) and non-Wi-Fi noise sources.",
              "Dynamic Channel Assignment (DCA) and Transmit Power Control (TPC).",
              "Client Load Analysis: Monitor association counts and session throughput for load balancing.",
              "Rogue AP Detection: Identify unauthorized access points and overlapping BSSIDs.",
              "AI Reasoning Engine: Analyze telemetry to determine optimal RF configuration changes.",
              "Optimization Workflow: Push configuration updates to WLAN controllers with rollback safeguards.",
              "Reporting & Audit Evidence: Generate Wi-Fi performance summaries and optimization logs."
            ]
          },
          {
            title: "Integrations",
            content: [
              "WLAN Controllers (Cisco, Aruba, Juniper, Mist, etc.): Collect telemetry, enforce adjustments (Bidirectional).",
              "Network Management / NMS Systems: Aggregate metrics and visualize coverage trends (Bidirectional).",
              "CoreStack GRC / SLA Modules: Store Wi-Fi SLA compliance evidence (Bidirectional).",
              "ServiceNow / Jira: Log optimization changes or escalation events (Bidirectional).",
              "Cortex Data Lake / NetFlow Analytics: Correlate Wi-Fi utilization and user performance data (Inbound).",
              "Azure OpenAI / Reasoning Engine: Perform root-cause analysis and generate optimization recommendations (Inbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — RF Telemetry & Baseline Mapping: Collect signal, SNR, and interference data per AP and client (50%).",
              "Tier 2 — Anomaly Detection & Load Correlation: Detect coverage gaps, congestion, and overutilized APs (75%).",
              "Tier 3 — AI Reasoning & Optimization Enforcement: Determine optimal channel/power settings and execute safely (90%).",
              "Tier 4 — Predictive & Self-Learning Optimization: Forecast performance issues and pre-adjust AP parameters proactively (100% - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Average Signal Strength (RSSI) ≥ –65 dBm.",
              "Signal-to-Noise Ratio (SNR) ≥ 25 dB (Indicates link quality).",
              "Mean Time to Optimize (MTTO) < 10 min (Time from degradation detection to corrective action).",
              "False Adjustment Rate ≤ 2% (Optimizations that reduced performance).",
              "RF Telemetry Collection Interval ≤ 60 s (Streaming data rate)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'Wi-FiOptAI' microservice.",
              "Primary Interfaces: WLAN Controller REST / SNMP APIs, gNMI telemetry, RF event streams.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing RF baselines, AP topology, and historical metrics.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on RF optimization and wireless interference knowledge.",
              "Error Handling: Auto-rollback on degraded throughput; retry optimization with adjusted thresholds.",
              "Outputs: Wi-Fi Optimization Report (JSON/PDF), Coverage Gap Map, RF Efficiency Dashboard Snapshot.",
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Continuous RF telemetry streams (RSSI, SNR, Channel Utilization, Interference) from APs and Controllers.",
      workingPlane: "Analyzes real-time RF data, correlates it with user experience metrics (latency/throughput), and maintains AP topology in Postgres.",
      memory: "Stores historical RF trends, user density patterns, and past optimization effectiveness to refine thresholds.",
      reasoning: "AI correlation identifies the root cause (e.g., CCI vs. congestion) and generates the optimal channel/power adjustment plan.",
      execution: "Pushes safe configuration updates to WLAN controllers with a built-in rollback mechanism and logs optimization evidence."
    },
    kpis: [
      { label: "Average Signal Strength (RSSI)", value: "-65", unit: "dBm", target: "≥ -65 dBm", color: "#10b981" },
      { label: "Signal-to-Noise Ratio (SNR)", value: "25", unit: "dB", target: "≥ 25 dB", color: "#2563eb" },
      { label: "Mean Time to Optimize (MTTO)", value: "10", unit: "mins", target: "< 10 min", color: "#f59e0b" },
      { label: "False Adjustment Rate", value: "2", unit: "%", target: "≤ 2%", color: "#ef4444" }
    ]
  },
 
  
  'rogue-ap-detection-agent': {
    title: "Rogue AP Detection Agent",
    description: "Continuously scans and monitors wireless environments to identify unauthorized or malicious access points (APs), classify their threat level, and autonomously isolate or neutralize them — ensuring network integrity, compliance, and protection from rogue device intrusions.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Network Security Enforcement: Detects, classifies, and isolates unauthorized or malicious APs automatically.",
          "Zero-Trust Extension to Wireless: Prevents lateral movement and credential theft at the RF edge.",
          "Operational Efficiency: Reduces false positives and manual forensics through AI-driven reasoning.",
          "Compliance Assurance: Maintains continuous alignment with PCI-DSS 4.0, HIPAA, and NIST wireless control mandates.",
          "Real-Time Threat Response: Eliminates rogue devices in minutes, minimizing potential compromise windows."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprises face constant threats from unauthorized APs (employee devices, spoofed SSIDs, attackers).",
              "Manual scanning and traditional WIDS alerts often generate false positives and lack automated containment workflows.",
              "Traditional systems operate reactively and in silos, lacking integration with identity (NAC) or zero-trust frameworks.",
              "Organizations require a self-learning agent to continuously detect, reason about risk, and automatically isolate rogue devices with full audit traceability."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Automated, real-time neutralization of unauthorized or malicious access points.",
              "Stronger security posture and enforcement of zero-trust at the wireless edge.",
              "Reduced manual investigation time due to improved accuracy and automated containment.",
              "Continuous evidence generation for key wireless compliance mandates (PCI, HIPAA, NIST).",
              "Significantly reduced mean time to contain (MTTC) wireless threats."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Unauthorized AP Detection: Identify and classify rogue APs broadcasting unapproved SSIDs or MAC addresses.",
              "Evil-Twin & Spoofing Prevention: Detect cloned enterprise SSIDs attempting credential harvesting.",
              "Malicious Bridge Mitigation: Identify APs bridging internal wired and external wireless networks.",
              "Auto-Containment Enforcement: De-authenticate or block rogue AP MACs at the switch or controller level.",
              "Compliance & Audit Evidence: Provide continuous logs and reports for PCI, NIST, and SOC 2 audits."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "RF Spectrum Scanning: Continuously scan all bands (2.4 GHz, 5 GHz, 6 GHz) for unapproved SSIDs.",
              "MAC Address Classification: Compare BSSIDs to authorized inventory in CMDB or controller.",
              "SSID Correlation & Spoof Detection: Identify duplicate SSIDs with mismatched encryption or origins.",
              "Threat Classification: Assign risk categories (Rogue, Neighbor, Honeypot, Spoofed, Misconfigured).",
              "Automated Containment: Trigger controller de-auth, switch-port disable, or VLAN quarantine via APIs.",
              "AI Reasoning Engine: Evaluate detection confidence, correlate contextual telemetry, and select response type.",
              "Audit & Compliance Logging: Record events, containment actions, and justification for governance."
            ]
          },
          {
            title: "Integrations",
            content: [
              "WLAN Controllers (Cisco, Aruba, Juniper, Mist, etc.): Collect RF scans and enforce containment actions (Bidirectional).",
              "Network Access Control (NAC) Systems: Validate authorized devices and isolate rogue endpoints (Bidirectional).",
              "Switch Infrastructure (Campus LAN): Execute port shutdown or MAC blocking actions (Bidirectional).",
              "CoreStack GRC / Compliance Modules: Store audit evidence and policy mapping (Bidirectional).",
              "Threat Intelligence Feeds: Correlate rogue APs with known attack tools or MAC OUIs (Inbound).",
              "Azure OpenAI / Reasoning Engine: Assess detection confidence and produce action rationale (Inbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — RF Scanning & Baseline Mapping: Collect wireless spectrum data and authorized AP inventory (50%).",
              "Tier 2 — Anomaly Detection & Threat Classification: Identify unapproved SSIDs, MACs, and RF anomalies (75%).",
              "Tier 3 — AI Reasoning & Auto-Containment Enforcement: Determine severity and execute containment autonomously (90%).",
              "Tier 4 — Continuous Learning & Predictive Mitigation: Anticipate rogue AP hotspots based on historical trends (100%)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Rogue AP Detection Accuracy ≥ 97% (True rogues correctly identified).",
              "False Positive Rate ≤ 3% (Neighboring or legitimate APs misidentified).",
              "Mean Time to Detect (MTTD) < 2 min (Rogue AP broadcast to detection).",
              "Mean Time to Contain (MTTC) < 5 min (Detection to isolation or de-auth).",
              "Containment Success Rate ≥ 98% (Confirmed rogue APs successfully neutralized)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'RogueAPAI' microservice.",
              "Primary Interfaces: WLAN Controller REST / SNMP APIs, NAC APIs, Switch Management APIs.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing authorized AP list, MAC OUIs, and historical detection data.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on wireless intrusion detection and RF telemetry.",
              "Security Controls: TLS 1.3 encryption, credential vault, RBAC isolation, signed containment commands.",
              "Error Handling: Auto-retry containment API calls; escalate to SOC if rogue AP persists after two actions.",
              "Outputs: Rogue AP Detection Report (JSON/PDF), Containment Log, Compliance Evidence Snapshot."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Continuous RF scans from WLAN controllers and RF anomaly streams.",
      workingPlane: "Analyzes real-time RF data, correlates MACs/SSIDs with CMDB, and interfaces with NAC/Switch APIs for enforcement.",
      memory: "Stores authorized AP list, historical detection data, and past admin verdicts to refine classification models.",
      reasoning: "AI-driven classification distinguishes true malicious rogues from neighbors/benign devices and selects the appropriate autonomous containment action.",
      execution: "Triggers de-authentication sequences via the controller, or switch-port disable actions, and generates security incident tickets (ServiceNow/Jira)."
    },
    kpis: [
      { label: "Rogue AP Detection Accuracy", value: "97", unit: "%", target: "≥ 97%", color: "#10b981" },
      { label: "False Positive Rate", value: "3", unit: "%", target: "≤ 3%", color: "#f59e0b" },
      { label: "Mean Time to Detect (MTTD)", value: "2", unit: "mins", target: "< 2 min", color: "#2563eb" },
      { label: "Mean Time to Contain (MTTC)", value: "5", unit: "mins", target: "< 5 min", color: "#ef4444" }
    ]
},

  'wireless-onboarding-agent': {
    title: "Wireless Onboarding Agent",
    description: "Automates the onboarding of corporate and personal (BYOD) wireless devices using digital certificates, enterprise Wi-Fi profiles, and dynamic policy assignment — ensuring secure, compliant, and frictionless user connectivity aligned with zero-trust principles.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Zero-Touch Onboarding: Automatically issues and installs certificates and Wi-Fi profiles on endpoints.",
          "Strong Authentication Enforcement: Enforces 802.1X and EAP-TLS authentication using managed digital certificates.",
          "Improved User Experience: Simplifies onboarding for employees, contractors, and BYOD users.",
          "Operational Efficiency: Reduces helpdesk tickets and manual provisioning tasks by up to 90%.",
          "Zero-Trust Device Access: Ensures all devices meet identity and compliance baselines before joining the network."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Manual wireless onboarding processes create user friction, inconsistent security configurations, and certificate mismanagement.",
              "Devices are often enrolled with weak PSKs or outdated credentials, compromising WPA3-Enterprise or 802.1X frameworks.",
              "IT teams lack visibility into which devices are authorized, enrolled, or compliant, especially in mixed corporate and BYOD environments.",
              "Enterprises require an agentic system that automates certificate provisioning, network profile installation, and posture validation at scale to ensure secure, policy-aligned access."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Simplified, zero-touch certificate-based onboarding for all device types.",
              "Robust enforcement of 802.1X and EAP-TLS for stronger wireless security.",
              "A simplified, frictionless experience for employees and BYOD users.",
              "Up to 90% reduction in helpdesk tickets related to Wi-Fi provisioning and credentials.",
              "Continuous validation that devices meet zero-trust identity and compliance standards before network access."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Certificate-Based Device Enrollment: Automate certificate issuance and installation for new wireless devices.",
              "BYOD Self-Service Portal: Enable users to securely onboard personal devices using guided workflows.",
              "Dynamic Policy Assignment: Apply VLAN or ACL profiles dynamically based on user role and device posture.",
              "Posture Verification & Compliance Check: Validate endpoint security (encryption, OS, AV) during the onboarding process.",
              "Lifecycle Certificate Renewal: Automatically renew device certificates before expiration."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Device Enrollment & Certificate Provisioning: Automate 802.1X/EAP-TLS certificate issuance via enterprise CA or SCEP.",
              "Profile Distribution: Deploy Wi-Fi SSID, EAP method, and trust chain to all major operating systems (macOS, Windows, iOS, Android, etc.).",
              "Posture Assessment: Validate device OS, encryption, AV, and patch status during onboarding.",
              "Identity & Role Correlation: Match certificates to user identities and AD/Entra roles for dynamic policy assignment.",
              "Self-Service Portal Workflow: Enable BYOD onboarding through captive portal with identity and MFA validation.",
              "Policy-Based VLAN / ACL Assignment: Assign network segmentation dynamically via RADIUS or controller integration.",
              "Lifecycle Management: Automate certificate renewal, revocation, and de-registration workflows.",
              "Compliance & Audit Reporting: Maintain logs for onboarding success rates, compliance verification, and certificate issuance."
            ]
          },
          {
            title: "Integrations",
            content: [
              "RADIUS / NAC Systems (ISE, ClearPass, FortiNAC): Validate posture and enforce access policies (Bidirectional).",
              "Enterprise Certificate Authority (Microsoft CA, DigiCert, Entrust): Issue and manage device certificates (Bidirectional).",
              "MDM / EMM Platforms (Intune, Workspace ONE, Jamf): Push Wi-Fi profiles and certificates to managed devices (Bidirectional).",
              "Identity Providers (Entra ID, Okta, Ping): Correlate users and roles for onboarding workflows (Inbound).",
              "Wireless Controllers (Cisco, Aruba, Juniper, Mist): Apply access profiles and VLAN assignments dynamically (Bidirectional).",
              "Azure OpenAI / Reasoning Engine: Evaluate risk context, reason about onboarding failures, and suggest remediation (Inbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Device Discovery & Enrollment Request: Detect new devices attempting to join Wi-Fi and initiate enrollment (50%).",
              "Tier 2 — Certificate Provisioning & Profile Deployment: Automate certificate issuance and Wi-Fi profile installation (75%).",
              "Tier 3 — AI Posture Reasoning & Policy Assignment: Evaluate compliance posture, assign VLANs and ACLs dynamically (90%).",
              "Tier 4 — Continuous Certificate Lifecycle Governance: Predict renewals, re-enroll devices, and update policies automatically (100% - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Successful Onboarding Rate ≥ 98% (% of devices successfully enrolled and authenticated).",
              "Mean Time to Onboard (MTTO) ≤ 5 min (End-to-end onboarding duration).",
              "Certificate Renewal Compliance ≥ 97% (Valid certificates renewed before expiry).",
              "Posture Compliance Accuracy ≥ 95% (Devices meeting minimum security standards).",
              "Device Discovery & Enrollment Trigger ≤ 30 s (Includes certificate request initiation)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'WiFiOnboardAI' microservice.",
              "Primary Interfaces: RADIUS, NAC, CA, MDM, and wireless controller REST APIs.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing certificate metadata, posture checks, and onboarding logs.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on EAP-TLS onboarding, posture evaluation, and RunOps Zero-Trust KB.",
              "Security Controls: TLS 1.3, signed certificate requests (CSR), encrypted credential vault, RBAC isolation.",
              "Error Handling: Auto-retry on failed certificate or profile pushes; escalate to ITSM if 3 consecutive failures occur.",
              "Outputs: Wireless Onboarding Report (JSON/PDF), Certificate Lifecycle Summary, Compliance Snapshot."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "New device association request detected by WLAN controller/NAC, or scheduled certificate expiration event.",
      workingPlane: "Orchestrates multi-step workflow: NAC validation, CA issuance, MDM profile push, and policy assignment via RADIUS/controller APIs.",
      memory: "Stores certificate metadata, device posture history, user identity correlation, and renewal timelines to govern the lifecycle.",
      reasoning: "AI validates compliance posture (encryption, AV status) against policy, dynamically assigns the correct network access profile (VLAN/ACL), and determines renewal necessity.",
      execution: "Issues certificate, deploys Wi-Fi profile, enforces dynamic network policy (VLAN/ACL) upon successful 802.1X authentication, and logs audit evidence."
    },
    kpis: [
      { label: "Successful Onboarding Rate", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Mean Time to Onboard (MTTO)", value: "5", unit: "mins", target: "≤ 5 min", color: "#2563eb" },
      { label: "Certificate Renewal Compliance", value: "97", unit: "%", target: "≥ 97%", color: "#f59e0b" },
      { label: "Posture Compliance Accuracy", value: "95", unit: "%", target: "≥ 95%", color: "#ef4444" }
    ]
  
},

  'ssid-hygiene-agent': {
    title: "SSID Hygiene Agent",
    description: "Continuously audits wireless SSID configurations, encryption settings, and broadcast domains to eliminate **SSID sprawl**, ensure **security consistency**, and detect unauthorized or misconfigured SSIDs that violate enterprise wireless standards.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Reduced SSID Sprawl: Detects redundant, legacy, or inactive SSIDs for removal or consolidation.",
          "Security Consistency: Validates encryption, authentication, and VLAN mappings against enterprise standards.",
          "Improved Wireless Performance: Minimizes broadcast overhead and airtime utilization from excessive SSIDs.",
          "Zero-Trust Alignment: Ensures all active SSIDs comply with enterprise access segmentation models.",
          "Audit & Compliance Readiness: Provides continuous evidence for PCI, HIPAA, NIST 800-153, and ISO wireless controls."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprise WLANs accumulate SSID sprawl from legacy projects, temporary guest networks, or third-party deployments.",
              "Inconsistent configurations (mixed authentication modes, open broadcasts, redundant VLAN mappings) increase security risks and RF congestion.",
              "Manual audits are labor-intensive and fail to maintain real-time visibility into broadcast health, security posture, and SSID policy compliance.",
              "Organizations need an autonomous agent that continuously validates configurations, enforces security baselines, and reports deviations across controllers and APs enterprise-wide."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Automated detection and consolidation/removal of unnecessary or duplicate SSIDs.",
              "Consistent enforcement of WPA3-Enterprise, 802.1X, and standardized VLAN segmentation.",
              "Optimal wireless performance by reducing channel and airtime contention from excessive broadcasts.",
              "Continuous policy validation ensures all wireless access points align with zero-trust segmentation.",
              "Ready-to-submit evidence packages for regulatory audits (PCI, HIPAA, NIST, ISO)."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "SSID Sprawl Audit: Identify unnecessary or duplicate SSIDs across controllers and sites.",
              "Security Configuration Validation: Check for open SSIDs, deprecated encryption, and authentication gaps.",
              "Broadcast Domain Optimization: Detect SSID overlap and reduce channel and airtime contention.",
              "Unauthorized SSID Detection: Identify rogue or shadow SSIDs not provisioned in controller inventory.",
              "Compliance & Reporting: Map SSID configurations to corporate and regulatory security frameworks."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "SSID Inventory & Baseline Mapping: Enumerate all active SSIDs, BSSIDs, VLANs, and controller configurations.",
              "Security Policy Validation: Check encryption methods, authentication protocols, and RADIUS server health.",
              "SSID Utilization Analysis: Measure association count, throughput, and airtime share per SSID.",
              "Redundancy & Sprawl Detection: Identify duplicate SSIDs or overlapping broadcast domains.",
              "Unauthorized SSID Identification: Compare detected SSIDs against authorized controller and NAC inventories.",
              "AI Reasoning Engine: Classify SSIDs as compliant, deprecated, rogue, or misconfigured with confidence scoring.",
              "Remediation Workflow Automation: Recommend or execute consolidation, disablement, or deactivation of rogue SSIDs.",
              "Compliance Evidence & Audit Trail: Generate reports mapped to security standards and SLA metrics."
            ]
          },
          {
            title: "Integrations",
            content: [
              "WLAN Controllers (Cisco, Aruba, Juniper, Mist, etc.): Retrieve SSID configurations, broadcast status, and client data (Bidirectional).",
              "Network Access Control (NAC) / RADIUS Systems: Validate authentication and certificate integration (Bidirectional).",
              "NMS / RF Analytics Tools: Correlate SSID performance and airtime utilization (Inbound).",
              "CoreStack GRC / Compliance Modules: Store compliance evidence and SSID security baselines (Bidirectional).",
              "ServiceNow / Jira: Manage remediation workflows and decommission requests (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Classify SSID compliance, risk, and remediation priority (Inbound).",
              "Confluence / Data Lake: Archive audit reports, SSID hygiene summaries, and performance logs (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — SSID Discovery & Baseline Comparison: Collect and compare SSID data against authorized templates (50 %).",
              "Tier 2 — Policy Validation & Risk Classification: Analyze encryption, authentication, and broadcast domain health (75 %).",
              "Tier 3 — AI Reasoning & Remediation Recommendation: Classify SSID state and recommend disablement or consolidation (90 %).",
              "Tier 4 — Predictive Learning & Autonomous Hygiene Enforcement: Learn from prior audits and auto-enforce SSID hygiene policies (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "SSID Compliance Rate ≥ 95 % (Percentage of SSIDs adhering to corporate security standards).",
              "SSID Sprawl Reduction ≥ 20 % (Reduction in redundant or inactive SSIDs).",
              "Unauthorized SSID Detection Accuracy ≥ 97 % (Correct identification of rogue or unapproved SSIDs).",
              "Mean Time to Detect (MTTD) < 2 min (Time to identify new unauthorized SSID broadcasts).",
              "Mean Time to Remediate (MTTR) < 1 hr (Time from detection to disablement or decommissioning).",
              "False Positive Rate ≤ 3 % (Benign SSIDs incorrectly classified as rogue)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'SSIDHygieneAI' microservice.",
              "Primary Interfaces: WLAN Controller REST APIs, NAC/RADIUS, RF telemetry streams.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing SSID configurations, compliance baselines, and historical results.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on WLAN compliance, RF design, and RunOps wireless KB.",
              "Security Controls: TLS 1.3, RBAC isolation, encrypted credentials, signed remediation execution.",
              "Error Handling: Auto-retry controller API failures; escalate to SOC if rogue SSID persists >3 detection cycles.",
              "Outputs: SSID Hygiene Report (PDF/JSON), Rogue SSID Summary, Compliance Scorecard, Audit Snapshot."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Continuous inventory update from controllers (SSID Scan Interval ≤ 60 s).",
      workingPlane: "Analyzes real-time SSID configurations, compares them to defined policy baselines, and correlates with RF utilization data.",
      memory: "Stores historical audit results, recurring misconfigurations, and administered exceptions to inform future policy enforcement.",
      reasoning: "AI classifies the risk and intent of each SSID (rogue, redundant, misconfigured) and prioritizes remediation based on security exposure.",
      execution: "Triggers decommissioning workflows via ITSM or autonomously executes disablement/deactivation commands on WLAN controllers with rollback safeguards."
    },
    kpis: [
      { label: "SSID Compliance Rate", value: "95", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "SSID Sprawl Reduction", value: "20", unit: "%", target: "≥ 20%", color: "#2563eb" },
      { label: "Unauthorized SSID Detection Accuracy", value: "97", unit: "%", target: "≥ 97%", color: "#f59e0b" },
      { label: "Mean Time to Remediate (MTTR)", value: "60", unit: "mins", target: "< 1 hr", color: "#ef4444" }
    ]
  
},


  'load-balancing-agent': {
    title: "Load Balancing Agent",
    description: "Continuously monitors client density, channel utilization, and signal quality across all access points to dynamically **balance wireless clients**, **optimize performance**, and prevent **congestion** in high-density environments.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Improved Wi-Fi Performance: Distributes clients evenly across APs, reducing latency and increasing throughput.",
          "Enhanced User Experience: Prevents congestion hotspots while maintaining seamless client roaming.",
          "Operational Efficiency: Eliminates manual load tuning and periodic RF intervention.",
          "Predictive RF Management: Identifies potential congestion trends and preemptively adjusts parameters.",
          "SLA & Compliance Assurance: Delivers consistent WLAN performance metrics aligned with service-level objectives."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "In high-density enterprise and campus networks, users and IoT devices tend to cluster on specific APs — causing congestion, poor throughput, and latency spikes.",
              "Static load balancing policies (e.g., band steering or association limits) lack contextual intelligence and often fail to adapt dynamically to real-time client behavior.",
              "Manual RF tuning is reactive, inconsistent, and doesn’t scale across hundreds of APs.",
              "Enterprises need a self-learning, context-aware agent that continuously monitors RF utilization and redistributes clients automatically based on performance, location, and device capability."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Even distribution of wireless clients across the wireless fabric, maximizing overall network throughput.",
              "Elimination of manual, reactive RF interventions, saving engineering time.",
              "Proactive identification and resolution of congestion issues before user impact.",
              "Continuous adherence to wireless service-level objectives (SLOs) and internal SLAs.",
              "Seamless and consistent wireless performance for all users and devices."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Client Density Balancing: Distribute wireless clients dynamically across nearby APs.",
              "Channel Utilization Optimization: Detect overutilized channels and redistribute clients accordingly.",
              "Band Steering Enforcement: Encourage dual-band capable clients to shift from 2.4 GHz to 5 GHz or 6 GHz.",
              "Dynamic Transmit Power Adjustments: Adjust AP signal strength to attract or repel clients intelligently.",
              "Load Prediction & Preemption: Forecast congestion events and take preventive action."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Client Association Monitoring: Track real-time user and IoT device associations per AP, per band.",
              "RF Utilization Analysis: Measure channel congestion, noise floor, and throughput saturation levels.",
              "Intelligent Client Steering: Encourage reassociation to nearby APs using 802.11v/k protocols.",
              "Band Steering Logic: Direct dual-band clients to higher-frequency bands for greater throughput.",
              "Power Control Optimization: Dynamically adjust transmit power (TPC) to balance signal coverage.",
              "Predictive Congestion Forecasting: Identify emerging hotspots based on historical density trends.",
              "AI Reasoning Layer: Evaluate the least disruptive client redistribution strategies.",
              "Audit & Reporting: Log all balancing actions, client migrations, and performance improvements."
            ]
          },
          {
            title: "Integrations",
            content: [
              "WLAN Controllers (Cisco, Aruba, Juniper, Mist, etc.): Retrieve client telemetry and enforce balancing commands (Bidirectional).",
              "RF Telemetry / NMS Systems: Collect real-time utilization, channel congestion, and interference metrics (Inbound).",
              "Wi-Fi Optimization Agent: Share signal strength and interference data for holistic RF optimization (Bidirectional).",
              "QoS Policy Agent: Coordinate traffic prioritization during client reassignment (Bidirectional).",
              "ServiceNow / Jira: Log balancing events, threshold breaches, or manual approvals (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Analyze RF context, user distribution, and recommend balancing actions (Inbound).",
              "Confluence / Data Lake: Archive balancing reports and historical optimization logs (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — RF & Client Density Monitoring: Collect and baseline client distribution and channel utilization data (50 %).",
              "Tier 2 — Anomaly Detection & Congestion Identification: Detect hotspots and unbalanced AP utilization (75 %).",
              "Tier 3 — AI Reasoning & Client Redistribution Enforcement: Execute client steering or power adjustments dynamically (90 %).",
              "Tier 4 — Predictive Load Balancing & Self-Learning Optimization: Anticipate congestion trends and preemptively balance clients (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "AP Load Balance Index (LBI) ≥ 90 % (Weighted balance across APs within an RF domain).",
              "Average Client Per AP Variance ≤ ±10 % (Deviation from average client count across AP group).",
              "Mean Time to Detect (MTTD) < 2 min (Detection of congestion or density imbalance).",
              "Mean Time to Optimize (MTTO) < 5 min (Time to execute balancing action and restore SLA).",
              "Throughput Improvement Post-Balance ≥ 15 % (Average performance gain for clients post-optimization).",
              "False Adjustment Rate ≤ 2 % (Rebalancing that causes degraded performance)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'WiFiBalanceAI' microservice.",
              "Primary Interfaces: WLAN Controller REST APIs, SNMP/NetFlow telemetry, RF analytics feeds.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing AP utilization, channel maps, and client distributions.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on WLAN RF optimization, 802.11v/k standards, and RunOps performance KB.",
              "Security Controls: TLS 1.3 encryption, RBAC isolation, credential vault.",
              "Error Handling: Auto-rollback power/channel changes if throughput decreases post-balance; escalate persistent hotspots to NOC.",
              "Outputs: Load Balancing Report (PDF/JSON), RF Efficiency Log, Client Distribution Dashboard Snapshot."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Continuous RF telemetry and client association monitoring streams (Telemetry Refresh Interval ≤ 60 s).",
      workingPlane: "Analyzes client density, channel utilization, and AP performance data in real-time.",
      memory: "Stores historical congestion events, peak usage hours, and the effectiveness of prior balancing actions to refine future strategies.",
      reasoning: "AI identifies the root cause of congestion and determines the optimal, least disruptive client steering or power adjustment strategy (802.11v/k) to balance load.",
      execution: "Executes intelligent client steering commands or dynamic Transmit Power Control (TPC) adjustments via WLAN controller APIs."
    },
    kpis: [
      { label: "AP Load Balance Index (LBI)", value: "90", unit: "%", target: "≥ 90%", color: "#10b981" },
      { label: "Avg Client Per AP Variance", value: "10", unit: "%", target: "≤ ±10%", color: "#2563eb" },
      { label: "Mean Time to Optimize (MTTO)", value: "5", unit: "mins", target: "< 5 min", color: "#f59e0b" },
      { label: "Throughput Improvement Post-Balance", value: "15", unit: "%", target: "≥ 15%", color: "#ef4444" }
    ]
},

  'firmware-compliance-agent': {
    title: "Firmware Compliance Agent",
    description: "Continuously monitors firmware versions across wireless controllers and access points, validates them against approved enterprise baselines, and automates patching or upgrade workflows to maintain **compliance, stability, and security posture** across the wireless ecosystem.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Consistent Firmware Baselines: Ensures all controllers and APs are running approved versions.",
          "Improved Security Posture: Mitigates vulnerabilities by enforcing timely updates.",
          "Operational Efficiency: Automates firmware tracking, scheduling, and installation.",
          "Regulatory Compliance: Maintains adherence to NIST, ISO, PCI-DSS patch management mandates.",
          "Reduced Downtime Risk: Validates upgrade readiness and rollback safety automatically."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "As enterprise wireless infrastructures grow, firmware inconsistency across APs and controllers becomes a hidden operational risk.",
              "Outdated firmware exposes networks to CVE vulnerabilities, stability issues, and incompatibility between controllers and APs.",
              "Manual firmware upgrades are error-prone, difficult to coordinate, and often delayed due to maintenance windows or dependency complexity.",
              "Enterprises need a centralized, intelligent compliance agent that continuously verifies firmware integrity, schedules upgrades automatically, and ensures secure, zero-downtime patching across the wireless landscape."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Elimination of firmware drift by ensuring all devices run the enterprise-approved, secure version.",
              "Automated correlation with known CVEs and timely mitigation of security vulnerabilities.",
              "Reduction in manual effort for tracking, scheduling, and executing wireless firmware updates.",
              "Continuous evidence and reporting to satisfy strict regulatory and security patch management mandates.",
              "Minimized risk of failure through automated upgrade validation and rollback mechanisms."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Firmware Version Compliance: Continuously verify wireless devices against approved version baselines.",
              "Vulnerability Correlation: Map running versions to known CVEs and vendor advisories.",
              "Automated Upgrade Workflow: Schedule and execute firmware updates during maintenance windows.",
              "Health Validation Post-Upgrade: Verify AP and controller stability and RF service restoration.",
              "Compliance Evidence & Reporting: Generate auditable records of firmware state and patch actions."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Firmware Inventory & Baseline Mapping: Continuously collect version data from all wireless controllers and APs.",
              "Compliance Validation: Compare versions against enterprise-approved baselines.",
              "Vulnerability Correlation: Identify known CVEs and vendor advisories impacting current firmware.",
              "Upgrade Planning: Evaluate upgrade dependencies, hardware readiness, and controller/AP compatibility.",
              "Automated Update Execution: Push firmware updates during pre-approved maintenance windows.",
              "Post-Upgrade Validation: Confirm AP rejoin, RF service stability, and client association health.",
              "AI Reasoning Engine: Determine optimal timing and risk for upgrade actions.",
              "Audit & Reporting: Produce compliance scorecards, patch history logs, and evidence reports."
            ]
          },
          {
            title: "Integrations",
            content: [
              "WLAN Controllers (Cisco, Aruba, Juniper, Mist, etc.): Retrieve firmware info and execute upgrades (Bidirectional).",
              "Vendor Firmware APIs / PSIRT Feeds: Gather advisories, release notes, and CVE data (Inbound).",
              "CoreStack GRC / Compliance Modules: Store compliance mappings and patch logs (Bidirectional).",
              "ServiceNow / Jira: Create change requests, approvals, and maintenance tasks (Bidirectional).",
              "Network Monitoring / NMS Systems: Validate post-upgrade health and uptime (Inbound).",
              "Azure OpenAI / RunOps Reasoning Engine: Assess upgrade risk, determine sequencing, and generate narratives (Inbound).",
              "Confluence / Data Lake: Archive firmware compliance reports and evidence (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Firmware Inventory & Baseline Comparison: Collect version data and validate compliance (50 %).",
              "Tier 2 — CVE & Vulnerability Correlation: Map firmware gaps to security advisories (75 %).",
              "Tier 3 — AI-Driven Upgrade Planning & Execution: Automate patching and sequencing with risk analysis (90 %).",
              "Tier 4 — Predictive Lifecycle Optimization: Learn vendor release cycles and pre-stage updates automatically (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Firmware Compliance Rate ≥ 98 % (% of devices running approved versions).",
              "Mean Time to Detect (MTTD) Non-Compliant Version < 4 hrs (Time from release to compliance gap detection).",
              "Mean Time to Patch (MTTP) ≤ 7 days (Time from detection to upgrade execution).",
              "Post-Upgrade Success Rate ≥ 97 % (Successful rejoin and uptime post-upgrade).",
              "False Positive Rate (Drift Alerts) ≤ 2 % (Incorrect firmware drift alerts).",
              "Firmware Inventory Scan ≤ 4 hrs (Periodic polling via controller APIs)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'WiFiFirmwareAI' microservice.",
              "Primary Interfaces: WLAN Controller REST APIs, Vendor Firmware APIs, NMS SNMP Telemetry.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing approved baselines, version mappings, and CVE metadata.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on firmware lifecycle management, PSIRT data, and RunOps compliance KB.",
              "Security Controls: TLS 1.3 encryption, RBAC-controlled upgrades, signed firmware integrity checks.",
              "Error Handling: Auto-retry failed upgrades; rollback to last stable firmware image if validation fails.",
              "Outputs: Firmware Compliance Report (JSON/PDF), Patch Plan Summary, Compliance Snapshot, Change Log."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Scheduled firmware inventory scan (≤ 4 hrs) or new CVE/vendor advisory publication.",
      workingPlane: "Collects firmware versions, correlates them with approved baselines and threat intelligence (CVEs), and manages upgrade execution.",
      memory: "Stores approved firmware baselines, patch history, success/failure rates, and vendor release patterns to inform predictive scheduling.",
      reasoning: "AI evaluates upgrade necessity and risk by correlating vulnerability severity (CVE), device compatibility, and operational stability metrics to determine optimal sequencing.",
      execution: "Pushes approved firmware images to controllers/APs, initiates the upgrade process within maintenance windows (ServiceNow/Jira), and validates post-upgrade health/rejoin."
    },
    kpis: [
      { label: "Firmware Compliance Rate", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Mean Time to Patch (MTTP)", value: "7", unit: "days", target: "≤ 7 days", color: "#2563eb" },
      { label: "Post-Upgrade Success Rate", value: "97", unit: "%", target: "≥ 97%", color: "#f59e0b" },
      { label: "Audit Evidence Coverage", value: "100", unit: "%", target: "100%", color: "#ef4444" }
    ]
},

  'guest-access-portal-agent': {
    title: "Guest Access Portal Agent",
    description: "Automates the guest Wi-Fi onboarding experience, including identity verification, credential generation, access policy assignment, session expiry, and audit logging — providing **secure, compliant, and user-friendly** visitor connectivity aligned with enterprise and regulatory standards.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Automated Guest Onboarding: Simplifies registration, approval, and access provisioning.",
          "Improved Security & Compliance: Applies time-based, role-based, and segmented access automatically.",
          "Zero-Touch Lifecycle Management: Auto-expires credentials and revokes access post-visit.",
          "Enhanced User Experience: Delivers frictionless guest onboarding via captive portal or QR code workflow.",
          "Regulatory Readiness: Maintains full session and identity audit logs for PCI, GDPR, ISO, and SOC 2."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Manual guest Wi-Fi provisioning leads to inconsistent experiences, security gaps, and audit blind spots.",
              "Static credentials and shared guest SSIDs expose the enterprise to unauthorized access, untraceable usage, and compliance violations.",
              "Guest networks often lack automated lifecycle management, resulting in expired credentials remaining active or improperly logged.",
              "Enterprises need a policy-aware, autonomous guest access agent that manages the full lifecycle — onboarding to expiry — while enforcing security segmentation, data retention, and compliance visibility."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Streamlined, automated registration, approval, and access policy assignment for guests.",
              "Robust enforcement of time-based, segmented access for enhanced security and compliance.",
              "Complete automation of the guest account lifecycle, from creation to secure expiry and revocation.",
              "High satisfaction scores through a seamless, multi-option guest onboarding experience.",
              "Automated generation and archiving of complete session and identity logs for regulatory audits."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Visitor Self-Service Portal: Guests register via captive portal, QR code, or sponsor approval workflow.",
              "Automated Account Creation & Expiry: Generates guest credentials dynamically and revokes them after a defined duration.",
              "Network Segmentation & Policy Assignment: Places guests into isolated VLANs with limited internet-only access.",
              "Sponsor Approval Workflow: Optional manager or host validation for guest account activation.",
              "Compliance & Logging: Captures session metadata, device info, and activity for retention and reporting."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Guest Registration Automation: Enable self-service registration through captive portal or sponsor email.",
              "Identity Verification & Approval: Validate guest info through OTP, email, or employee sponsor verification.",
              "Credential Generation: Create temporary usernames/passwords or digital certificates for access.",
              "Dynamic Policy & VLAN Assignment: Assign guest devices to appropriate VLANs and ACLs via RADIUS.",
              "Session Monitoring & Expiry Management: Continuously track session duration and auto-expire access.",
              "Audit Logging & Compliance Mapping: Capture guest identity, device MAC, IP, and browsing metadata for compliance.",
              "AI Reasoning Engine: Analyze anomalies (e.g., multiple guest devices per user or unusual data transfer).",
              "Reporting & Evidence Generation: Generate periodic guest access reports for GRC and SOC review."
            ]
          },
          {
            title: "Integrations",
            content: [
              "WLAN Controllers (Cisco, Aruba, Juniper, Mist, etc.): Provision guest SSID access and apply VLAN/ACLs (Bidirectional).",
              "Network Access Control (NAC) / RADIUS Servers: Enforce authentication, session expiry, and policy assignment (Bidirectional).",
              "Identity Providers (Azure AD, Okta, Ping): Validate sponsor identity and approval workflows (Inbound).",
              "CoreStack GRC / Compliance Modules: Store guest access evidence and compliance mappings (Bidirectional).",
              "ServiceNow / Jira: Track approvals, escalations, and exceptions (Bidirectional).",
              "Captive Portal / Web Gateway: Provide onboarding UI and registration workflows (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Analyze guest request legitimacy and generate context-driven approvals (Inbound).",
              "Confluence / Data Lake: Archive access logs, session metrics, and compliance reports (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Guest Registration & Validation: Collect guest details and verify identity through OTP or sponsor (50 %).",
              "Tier 2 — Credential Issuance & VLAN Assignment: Generate credentials and assign appropriate access policy (75 %).",
              "Tier 3 — AI Reasoning & Lifecycle Automation: Monitor activity, detect anomalies, and expire access autonomously (90 %).",
              "Tier 4 — Predictive Policy Optimization: Learn from historical data to auto-tune policy durations and thresholds (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Guest Onboarding Success Rate ≥ 98 % (% of registrations resulting in successful network access).",
              "Mean Time to Onboard (MTTO) ≤ 3 min (Time from registration to Wi-Fi connectivity).",
              "Session Expiry Compliance 100 % (Expired guest accounts disabled automatically).",
              "Unauthorized Access Detection Accuracy ≥ 97 % (Rogue or overstayed guests correctly identified).",
              "False Positive Rate (Blocked Legitimate Guests) ≤ 3 % (Legitimate users incorrectly flagged or blocked).",
              "Audit Evidence Coverage 100 % (Guest session logs and compliance data archived)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'GuestPortalAI' microservice.",
              "Primary Interfaces: WLAN Controller APIs, NAC/RADIUS, Captive Portal, IdP, and ServiceNow workflows.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing guest account data, expiry timers, and compliance mappings.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on guest lifecycle management, NAC/RADIUS workflows, and RunOps zero-trust KB.",
              "Security Controls: TLS 1.3, encrypted guest credentials, RBAC for sponsor access, anonymized guest logs.",
              "Error Handling: Auto-retry failed credential sync; revoke credentials if RADIUS disconnect confirmation fails twice.",
              "Outputs: Guest Access Report (PDF/JSON), Lifecycle Summary, Compliance Evidence Snapshot."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Guest device connecting to captive portal or sponsor submission event.",
      workingPlane: "Orchestrates identity verification (OTP/Sponsor), credential generation, and policy assignment (VLAN/ACL) via NAC/Controller/RADIUS.",
      memory: "Stores guest account data, session expiry timers, usage patterns, and audit logs to govern access lifecycle.",
      reasoning: "AI validates guest legitimacy, assigns dynamic access policy based on sponsor/type, and continuously monitors sessions for anomalous behavior or expiry.",
      execution: "Generates temporary credentials, updates the RADIUS server with session limits, and automatically disables/revokes access upon session expiry (Session Expiry Enforcement ≤ 1 min post-expiry)."
    },
    kpis: [
      { label: "Guest Onboarding Success Rate", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Mean Time to Onboard (MTTO)", value: "3", unit: "mins", target: "≤ 3 min", color: "#2563eb" },
      { label: "Session Expiry Compliance", value: "100", unit: "%", target: "100%", color: "#f59e0b" },
      { label: "Unauthorized Access Detection Accuracy", value: "97", unit: "%", target: "≥ 97%", color: "#ef4444" }
    ]
},

  'iot-wireless-agent': {
    title: "IoT Wireless Agent",
    description: "Continuously discovers, classifies, and segments IoT devices connected to enterprise wireless networks, assigning them to appropriate VLANs, policies, and zero-trust segments based on **identity, behavior, and risk profile**.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Automated IoT Discovery: Identifies all wireless IoT devices across SSIDs and AP domains.",
          "Dynamic Segmentation: Assigns devices to secure VLANs or SGTs (Security Group Tags) automatically.",
          "Zero-Trust Enforcement: Restricts IoT communication to known and approved destinations only.",
          "Security & Risk Reduction: Detects rogue or compromised devices and isolates them instantly.",
          "Operational Efficiency: Eliminates manual onboarding and classification of IoT endpoints."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "The rapid proliferation of IoT and OT devices across campuses has introduced unmanaged and often insecure endpoints.",
              "Many of these devices lack endpoint agents, encryption, or posture validation, leaving them invisible to traditional monitoring systems.",
              "Manual device classification and VLAN assignment do not scale across thousands of endpoints or multiple SSIDs.",
              "Enterprises need an autonomous IoT discovery and segmentation agent that can identify, classify, and isolate devices dynamically — enforcing security and compliance without impacting performance."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Complete, autonomous inventory and identification of all connected wireless IoT devices.",
              "Automatic application of least-privilege policies through dynamic network segmentation.",
              "Enforcement of communication restrictions aligned with zero-trust principles.",
              "Real-time detection and containment of rogue, compromised, or policy-violating devices.",
              "Significant reduction in manual effort for device inventory and segmentation management."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "IoT Device Identification: Detect and classify connected IoT endpoints based on traffic patterns and MAC/OUI signatures.",
              "Behavioral Profiling: Analyze traffic flows to determine device function and typical behavior.",
              "Dynamic Network Segmentation: Automatically move devices into appropriate VLANs or microsegments.",
              "Anomaly & Rogue Detection: Detect newly connected, unknown, or policy-violating IoT devices.",
              "Compliance & Reporting: Map discovered IoT devices to zero-trust inventory and compliance frameworks."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Wireless Device Discovery: Detect all connected devices by MAC, OUI, RF signature, and SSID.",
              "Traffic Behavior Analysis: Analyze packet patterns, ports, and protocols to infer device role.",
              "IoT Fingerprinting Engine: Match observed patterns to vendor and device type libraries.",
              "Dynamic VLAN/SGT Assignment: Move devices into proper network segments automatically.",
              "Zero-Trust Enforcement: Limit IoT communications to approved destinations or controller zones.",
              "Anomaly Detection: Identify devices exhibiting unusual traffic, protocol use, or new destinations.",
              "AI Reasoning Layer: Correlate device behavior, MAC info, and policy posture to determine risk and action.",
              "Audit Logging & Reporting: Generate device inventory, segmentation maps, and compliance summaries."
            ]
          },
          {
            title: "Integrations",
            content: [
              "WLAN Controllers (Cisco, Aruba, Juniper, Mist, etc.): Retrieve connected device data and enforce VLAN/policy changes (Bidirectional).",
              "Network Access Control (NAC) Systems (ISE, ClearPass): Apply segmentation and access control dynamically (Bidirectional).",
              "Identity & Asset Repositories (CMDB, ITAM): Correlate discovered devices to existing inventory (Bidirectional).",
              "CoreStack GRC / Compliance Modules: Record device risk, compliance status, and evidence mapping (Bidirectional).",
              "Cortex Data Lake / NMS Systems: Collect telemetry and traffic analytics for device profiling (Inbound).",
              "Azure OpenAI / RunOps Reasoning Engine: Evaluate behavior patterns and produce device classification rationale (Inbound).",
              "Confluence / Data Lake: Archive classification logs, segmentation reports, and audit records (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Discovery & Fingerprinting: Detect and classify IoT devices based on wireless and MAC telemetry (50 %).",
              "Tier 2 — Behavior & Policy Correlation: Map device traffic to approved profiles and VLAN policies (75 %).",
              "Tier 3 — AI Reasoning & Segmentation Enforcement: Execute policy updates for new or reclassified devices (90 %).",
              "Tier 4 — Continuous Learning & Zero-Trust Optimization: Anticipate rogue behavior and auto-tune segmentation baselines (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "IoT Identification Accuracy ≥ 97 % (Correct classification of device types).",
              "Dynamic Segmentation Success Rate ≥ 95 % (Successful VLAN or policy assignment actions).",
              "Mean Time to Detect (MTTD) Rogue Devices < 3 min (Time to identify new, unauthorized IoT endpoints).",
              "Mean Time to Segment (MTTS) < 5 min (Time to isolate or reassign non-compliant devices).",
              "False Classification Rate ≤ 3 % (Devices incorrectly categorized or segmented).",
              "Compliance Coverage Rate 100 % (Devices inventoried and logged to GRC systems)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'IoTWiFiAI' microservice.",
              "Primary Interfaces: WLAN Controller APIs, NAC REST APIs, SNMP telemetry, and CMDB integrations.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing device fingerprints, risk scores, and policy mappings.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on IoT behavior analytics, wireless telemetry, and RunOps Zero-Trust KB.",
              "Security Controls: TLS 1.3, encrypted credentials, RBAC isolation, anonymized device data.",
              "Error Handling: Auto-retry policy enforcement API calls; escalate to SOC if rogue persists after 2 containment attempts.",
              "Outputs: IoT Device Classification Report (JSON/PDF), Segmentation Summary, Compliance Evidence Snapshot."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "New device association to the wireless network or change in connected device's traffic behavior.",
      workingPlane: "Analyzes MAC/OUI, RF signature, and deep packet behavior, correlates data with asset inventory (CMDB), and interfaces with NAC/Controller APIs.",
      memory: "Stores device fingerprints, policy mappings (VLAN/SGT), and historical behavior baselines to refine classification and detect anomalies.",
      reasoning: "AI classifies device role (e.g., medical sensor, smart TV) based on traffic patterns, determines the required security segment (VLAN/SGT), and enforces the policy via NAC.",
      execution: "Pushes policy updates (VLAN/SGT) to NAC/WLAN controllers to segment the device dynamically and triggers isolation actions for rogue/anomaly devices (Rogue Detection to Containment ≤ 5 min)."
    },
    kpis: [
      { label: "IoT Identification Accuracy", value: "97", unit: "%", target: "≥ 97%", color: "#10b981" },
      { label: "Dynamic Segmentation Success Rate", value: "95", unit: "%", target: "≥ 95%", color: "#2563eb" },
      { label: "Mean Time to Detect (MTTD) Rogue Devices", value: "3", unit: "mins", target: "< 3 min", color: "#f59e0b" },
      { label: "Mean Time to Segment (MTTS)", value: "5", unit: "mins", target: "< 5 min", color: "#ef4444" }
    ]
},

  'rf-spectrum-analyzer-agent': {
    title: "RF Spectrum Analyzer Agent",
    description: "Continuously scans the RF spectrum across 2.4 GHz, 5 GHz, and 6 GHz bands to **detect, classify, and report interference sources** from external devices or rogue emitters — providing actionable insights and automated mitigation recommendations to maintain optimal wireless performance and stability.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Continuous RF Visibility: Detects and classifies interference in real time across all frequency bands.",
          "Proactive Mitigation: Recommends or triggers channel, power, or spectrum policy adjustments automatically.",
          "Optimized Wireless Performance: Reduces packet retries, latency, and jitter caused by RF noise.",
          "Reduced MTTR for RF Issues: Automates root-cause identification for wireless performance degradation.",
          "Regulatory & Compliance Assurance: Maintains clean spectrum operation and evidential logging for FCC/ISO compliance."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprise Wi-Fi reliability is increasingly challenged by non-Wi-Fi interference sources — such as Bluetooth devices, cordless phones, microwave ovens, wireless cameras, and IoT radios.",
              "These interference events lead to throughput degradation, retransmissions, and roaming failures, often invisible to traditional WLAN controllers.",
              "Manual RF troubleshooting is reactive, labor-intensive, and localized.",
              "Enterprises need a self-learning, continuously monitoring agent that senses interference patterns, correlates them with performance impact, and triggers proactive mitigation workflows across all campuses and branches."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Real-time identification and classification of all non-Wi-Fi interference sources (2.4, 5, and 6 GHz).",
              "Automated, predictive mitigation strategies for channel and power adjustments.",
              "Consistent, high-quality user experience due to minimized latency and jitter from RF noise.",
              "Faster resolution of wireless issues (reduced MTTR) through automated root-cause analysis.",
              "Auditable logging of spectrum health and compliance with regulatory bodies (FCC/ISO)."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "External Interference Detection: Identify non-Wi-Fi interference sources in 2.4, 5, and 6 GHz bands.",
              "Interferer Classification: Categorize interference (Bluetooth, microwave, Zigbee, analog video, jammer, etc.).",
              "Spectrum Utilization Analysis: Quantify channel occupancy and noise floor to identify saturation.",
              "RF Remediation Workflow: Recommend channel reassignment or transmit power adjustment.",
              "Anomaly & Rogue Detection: Detect illegal or rogue transmitters violating RF policy."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Spectrum Scanning & Baseline Profiling: Continuously analyze signal strength, noise floor, and channel utilization.",
              "Interference Classification: Identify interference source type, frequency signature, and severity.",
              "Impact Correlation: Determine which SSIDs, APs, or clients are affected by the interference.",
              "Mitigation Recommendation Engine: Suggest or execute channel changes, transmit-power adjustments, or band-steering.",
              "Rogue Signal Detection: Identify unauthorized or malicious RF emitters.",
              "AI Reasoning Layer: Correlate interference with user experience data and derive corrective actions.",
              "Reporting & Compliance Evidence: Generate RF interference maps, remediation logs, and SLA compliance reports."
            ]
          },
          {
            title: "Integrations",
            content: [
              "WLAN Controllers (Cisco, Aruba, Juniper, Mist, etc.): Retrieve RF telemetry, spectrum data, and enforce mitigation actions (Bidirectional).",
              "RF Sensor Arrays / Spectrum APs: Collect granular band and channel interference data (Inbound).",
              "Network Management / NMS Systems: Correlate performance metrics and channel assignments (Bidirectional).",
              "Wi-Fi Optimization & Load Balancing Agents: Coordinate mitigation and channel reassignment strategies (Bidirectional).",
              "ServiceNow / Jira: Log RF events, escalations, or manual override approvals (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Perform interference classification, root-cause correlation, and recommendation generation (Inbound).",
              "Confluence / Data Lake: Archive RF reports, interference trend data, and compliance artifacts (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — RF Spectrum Scanning & Baseline Collection: Collect continuous spectrum telemetry across all APs (50 %).",
              "Tier 2 — Interference Detection & Classification: Identify interference events and categorize severity (75 %).",
              "Tier 3 — AI Reasoning & Mitigation Recommendation: Correlate interference with performance data and suggest remediation (90 %).",
              "Tier 4 — Predictive & Self-Optimizing RF Governance: Learn interference patterns and pre-tune spectrum configuration proactively (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Interference Detection Accuracy ≥ 97 % (Correct classification of interference sources).",
              "Mean Time to Detect (MTTD) < 2 min (From interference occurrence to identification).",
              "Mean Time to Mitigate (MTTM) < 10 min (From detection to action or recommendation).",
              "False Positive Rate ≤ 3 % (Benign signals incorrectly flagged).",
              "Throughput Improvement Post-Mitigation ≥ 15 % (Average user throughput gain after correction).",
              "Spectrum Scan Interval ≤ 60 s (Continuous telemetry from APs/sensors)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'RFSpectrumAI' microservice.",
              "Primary Interfaces: WLAN Controller REST APIs, RF Sensor Telemetry, SNMP Metrics.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing RF baselines, noise profiles, and historical event data.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on RF physics, spectrum analytics, and RunOps wireless KB.",
              "Security Controls: TLS 1.3 encryption, credential vaulting, RBAC execution permissions.",
              "Error Handling: Auto-rollback on AP instability post-mitigation; escalate to NOC for persistent jamming.",
              "Outputs: RF Interference Report (PDF/JSON), Spectrum Health Scorecard, Mitigation Summary."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Continuous spectrum telemetry stream from APs/sensors (Spectrum Scan Interval ≤ 60 s).",
      workingPlane: "Analyzes noise floor and channel utilization, classifies the interference source type/severity, and correlates impact to SSIDs/KPIs.",
      memory: "Stores historical RF noise signatures and interference event patterns per location to refine detection baselines.",
      reasoning: "AI identifies the non-Wi-Fi root cause (e.g., microwave vs. Bluetooth) and determines the optimal mitigation strategy, coordinating actions with other wireless agents.",
      execution: "Triggers autonomous channel changes or power adjustments via the WLAN controller APIs, or logs an escalation for physical mitigation (Mean Time to Mitigate (MTTM) < 10 min)."
    },
    kpis: [
      { label: "Interference Detection Accuracy", value: "97", unit: "%", target: "≥ 97%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD)", value: "2", unit: "mins", target: "< 2 min", color: "#2563eb" },
      { label: "Mean Time to Mitigate (MTTM)", value: "10", unit: "mins", target: "< 10 min", color: "#f59e0b" },
      { label: "Throughput Improvement Post-Mitigation", value: "15", unit: "%", target: "≥ 15%", color: "#ef4444" }
    ]
},
'config-drift-agent': {
    title: "Routing, Switching & Core Network Config Drift Agent",
    description: "Continuously monitors routers, switches, and network cores for configuration drift against golden templates and zero-trust segmentation baselines — ensuring network integrity, consistent routing policy, and alignment with enterprise security standards.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Zero-Drift Assurance: Detects and flags configuration changes deviating from golden templates or network design baselines.",
          "Operational Consistency: Ensures all routers and switches adhere to approved routing policies, ACLs, and segmentation rules.",
          "Reduced Outage Risk: Identifies misconfigurations before they cause routing loops, VLAN mismatches, or HA failovers.",
          "Audit & Compliance Readiness: Maintains continuous evidence of configuration integrity for NIST, ISO, and SOC 2 audits.",
          "Faster Recovery: Enables rapid rollback to last known good configurations through validated drift deltas."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Over time, enterprise core networks experience configuration drift caused by emergency changes, firmware upgrades, manual CLI adjustments, or misaligned automation pipelines.",
              "These changes often bypass formal configuration management, leading to routing asymmetry, VLAN/zone mismatches, disabled ACLs, or loss of parity with golden templates.",
              "Network drift undermines both availability and security, and manual audits cannot scale to thousands of devices.",
              "Organizations require a continuous validation system that ensures routers, switches, and interconnects remain compliant with the enterprise’s zero-trust and segmentation baselines."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Zero-Drift Assurance — Detects and flags configuration changes deviating from golden templates or network design baselines.",
              "Operational Consistency — Ensures all routers and switches adhere to approved routing policies, ACLs, and segmentation rules.",
              "Reduced Outage Risk — Identifies misconfigurations before they cause routing loops, VLAN mismatches, or HA failovers.",
              "Audit & Compliance Readiness — Maintains continuous evidence of configuration integrity for NIST, ISO, and SOC 2 audits.",
              "Faster Recovery — Enables rapid rollback to last known good configurations through validated drift deltas."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Golden Template Enforcement: Continuously compare running configs to baseline templates.",
              "Zone & VLAN Integrity Monitoring: Validate segmentation boundaries across access and core layers.",
              "Routing Policy Validation: Detect routing protocol or neighbor configuration variance.",
              "ACL / QoS Consistency: Identify missing or modified access-lists and traffic-prioritization rules.",
              "Network Compliance Validation: Map routing and switching configurations to zero-trust design standards and audit frameworks."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Contextual Awareness: Understands the operational role of each router or switch (core, distribution, or access) and applies tailored baselines.",
              "Continuous Observation: Detects changes as soon as a commit or save event occurs on a network device.",
              "Reasoning & Classification: Distinguishes intentional configuration updates (approved change ticket) from unapproved drift.",
              "Self-Remediation: Suggests or triggers rollback actions for non-compliant deviations.",
              "Learning Feedback: Learns from approved exceptions and adjusts baseline tolerance intelligently.",
              "Cross-Plane Awareness: Correlates drift on routing or VLAN configuration with observed security zone inconsistencies across connected firewall layers."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Baseline Template Repository: Stores versioned “golden” configurations for routers and switches per role and site.",
              "Automated Config Collection: Periodically pulls running configs via API or SSH from routers and switches.",
              "Config Comparison Engine: Performs semantic diff — recognizing structure, order, and intent (not just text).",
              "Drift Classification: Categorizes deviations as critical (ACL removed), moderate (QoS change), or cosmetic (banner/hostname edits).",
              "Change Validation: Correlates drift events with ITSM change tickets and scheduled maintenance windows.",
              "Zero-Trust Alignment Checks: Ensures routing and VLAN configurations adhere to enterprise segmentation and zone trust models.",
              "Remediation Workflow: Suggests corrective commands or validates rollback readiness for approval.",
              "Compliance Reporting: Maps each drift event to NIST CM-6, ISO 27001 A.12.1.2, and SOC 2 CC7.2 controls."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Network Device APIs / CLI — Retrieve and compare configurations via secure channel (Inbound).",
              "Central Management Systems (NMS / Orchestrator) — Baseline enforcement and change detection (Bidirectional).",
              "CoreStack GRC / Compliance Modules — Control mapping and audit evidence storage (Bidirectional).",
              "ServiceNow / Jira — Validate changes against open change tickets (Bidirectional).",
              "Configuration Repository (Git / Confluence) — Golden template storage and drift evidence archive (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Interpret drift significance and generate remediation recommendations (Inbound).",
              "Telemetry / Logging (Syslog, SNMP, Streaming Telemetry) — Trigger event-based drift detection on configuration save/commit (Inbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Config Capture & Diff: Detect config variance vs. golden templates. (50%).",
              "Tier 2 — Context Correlation: Correlate with ITSM and topology data to validate intent. (75%).",
              "Tier 3 — AI Reasoning & Recommendation: Evaluate drift severity and propose corrective CLI/API commands. (90%).",
              "Tier 4 — Adaptive Baseline Learning: Update baselines based on approved deviations and recurring patterns. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Drift Detection Accuracy ≥ 95% — Correct identification of config variance vs baseline.",
              "False Positive Rate ≤ 3% — Non-impacting changes incorrectly flagged.",
              "Critical Drift Resolution Rate ≥ 90% — Percentage of high-impact drift remediated within SLA.",
              "Mean Time to Detect (MTTD) < 10 min — From device commit to drift identification.",
              "Mean Time to Remediate (MTTR) < 6 hrs — From detection to config restore or approval.",
              "Zero-Trust Alignment Score ≥ 92% — Conformance with segmentation and zone integrity baselines."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Devices Monitored: 152 (core routers, distribution switches, edge nodes)",
              "Baseline Templates Managed: 32 (per region, role, and function)",
              "Detected Drift Events: 611",
              "Unauthorized Drift Events: 419 (68.6%)",
              "Auto-Remediation Actions Suggested: 244",
              "MTTD: 4.3 minutes post-save event — MTTR: 5.8 hours",
              "False Positives: 2.7%",
              "Baseline Alignment Improvement: +15% month-over-month",
              "Compliance Evidence Reports Generated: 20",
              "Estimated Manual Audit Hours Saved: ~310 / month"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Config Collection Post-Change ≤ 5 minutes — Triggered via Syslog/SNMP trap on device save.",
              "Drift Comparison & Classification ≤ 3 minutes — Includes AI-based severity ranking.",
              "Ticket Creation / Notification ≤ 10 minutes — ITSM integration for review and remediation.",
              "Baseline Update (Approved Drift) ≤ 24 hours — Post-approval sync to template repo.",
              "Evidence Publication ≤ 1 hour — Confluence/JSON export.",
              "Agent Availability ≥ 99.9% — Monitored via Prometheus / Grafana."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “NetDrift” module.",
              "Primary Interfaces — RESTCONF/NETCONF, SSH, SNMP, or vendor-specific API.",
              "Baseline Repository — PostgreSQL (Memory Plane) + Git-based template storage for golden configs.",
              "Reasoning Model — Azure OpenAI GPT-4o model fine-tuned on routing, switching, and zero-trust topology patterns.",
              "Security Controls — Read-only configuration retrieval, credential vaulting, TLS 1.3 transport.",
              "Outputs — Drift Summary (JSON/PDF), Recommended Remediation Commands, Compliance Audit Snapshot.",
              "Monitoring — Grafana dashboard tracking drift trends, SLA adherence, and zone alignment.",
              "Error Handling — Auto-retry on collection timeout; fallback to last known config; alert SOC on two failed cycles."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Device configuration save/commit event (via Telemetry/Syslog), or scheduled configuration pull cycle.",
      workingPlane: "RunOps n8n automation cluster with containerized 'NetDrift' module.",
      memory: "Stores versioned golden configurations, drift history, and approved exceptions in Postgres/Git.",
      reasoning: "Performs semantic diff, correlates drift against ITSM tickets/Zero-Trust baselines, and classifies severity to recommend remediation or rollback commands.",
      execution: "Triggers notification/ticket creation in ITSM, logs immutable drift evidence, and suggests CLI/API commands for configuration remediation."
    },
    kpis: [
      { label: "Drift Detection Accuracy", value: "95", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "False Positive Rate", value: "2.7", unit: "%", target: "≤ 3%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD)", value: "4.3", unit: "min", target: "< 10 min", color: "#10b981" },
      { label: "Mean Time to Remediate (MTTR)", value: "5.8", unit: "hrs", target: "< 6 hrs", color: "#10b981" },
      { label: "Critical Drift Resolution Rate", value: "90", unit: "%", target: "≥ 90%", color: "#10b981" },
      { label: "Zero-Trust Alignment Score", value: "92", unit: "%", target: "≥ 92%", color: "#10b981" }
    ]
},

'topology-discovery-agent': {
    title: "Topology Discovery Agent",
    description: "Automatically discovers, maps, and maintains a real-time view of the enterprise network topology — including routers, switches, firewalls, and inter-zone connectivity — providing dynamic visualization, segmentation awareness, and integration with security and compliance workflows.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Continuous Visibility: Real-time, auto-generated topology maps spanning on-prem, branch, and cloud networks.",
          "Operational Resilience: Detects path changes, redundant links, or missing peers before they cause outages.",
          "Zero-Trust Validation: Verifies segmentation boundaries between trusted, untrusted, and DMZ zones.",
          "Compliance Alignment: Supports audit evidence for PCI-DSS, ISO 27001, and NIST CM-6 by proving consistent topology control.",
          "Accelerated Troubleshooting: Reduces mean time to identify root causes (MTTI) by providing live dependency maps."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Large enterprise networks evolve continuously, yet topology documentation often lags behind reality.",
              "Manual network diagrams and CMDB entries are outdated within weeks, leaving security, operations, and compliance teams blind to actual Layer 2–7 relationships.",
              "Without current topology intelligence, issues such as orphaned VLANs, unmonitored subnets, redundant paths, and segmentation violations persist unnoticed.",
              "The organization needs a living topology model that updates automatically and correlates routing, firewall, and cloud interconnect data in near real time."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Continuous Visibility — Real-time, auto-generated topology maps spanning on-prem, branch, and cloud networks.",
              "Operational Resilience — Detects path changes, redundant links, or missing peers before they cause outages.",
              "Zero-Trust Validation — Verifies segmentation boundaries between trusted, untrusted, and DMZ zones.",
              "Compliance Alignment — Supports audit evidence for PCI-DSS, ISO 27001, and NIST CM-6 by proving consistent topology control.",
              "Accelerated Troubleshooting — Reduces mean time to identify root causes (MTTI) by providing live dependency maps."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Dynamic Topology Mapping: Continuously discovers new routers, switches, and firewalls, generating visual maps automatically.",
              "Segmentation Verification: Validates network segmentation and zone adjacency against zero-trust design.",
              "Change Impact Analysis: Visualizes the effect of configuration or route changes before and after deployment.",
              "CMDB Reconciliation: Synchronizes discovered topology with asset and configuration management databases.",
              "Incident Response Support: Provides live path maps for SOC investigations or performance triage."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Autonomous Discovery: Continuously polls APIs, telemetry streams, and routing protocols to detect topology changes without manual triggers.",
              "Reasoning Engine: Differentiates intentional changes (planned maintenance) from unplanned anomalies (unauthorized device addition).",
              "Semantic Correlation: Understands how physical, logical, and policy layers interconnect — from VLANs to zones to applications.",
              "Self-Learning: Remembers topology deltas, learns recurring patterns, and suppresses false positives.",
              "Cross-Plane Awareness: Correlates network topology with firewall policies, identity zones, and compliance domains."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Multi-Layer Discovery: Scans Layer 2 (switch/VLAN), Layer 3 (routing neighbors), and Layer 4–7 (application/service flows).",
              "API-Driven Inventory: Uses device APIs, SNMP, NetFlow/IPFIX, and streaming telemetry to build topology.",
              "Zone & Segment Correlation: Associates interfaces and subnets with trust zones, VRFs, and overlay segments.",
              "Visual Map Generation: Creates interactive network maps with live link health, bandwidth, and policy overlays.",
              "AI-Driven Anomaly Detection: Identifies unauthorized devices, rogue peers, or changes outside of maintenance windows.",
              "Change Simulation Integration: Links to Change Simulation Agent to visualize potential path or adjacency impacts pre-commit.",
              "Export & Synchronization: Syncs topology data with CMDBs, Confluence diagrams, or CoreStack GRC evidence repositories."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Router & Switch APIs / SNMP / Telemetry — Pull interface, routing, VLAN, and link-state data (Inbound).",
              "Central Management Systems (Panorama / NMS) — Correlate zone, policy, and device group topology (Bidirectional).",
              "CoreStack CMDB / GRC Modules — Store topology inventory, segment mappings, and compliance relationships (Bidirectional).",
              "ServiceNow / Jira — Reconcile discovered assets and track topology changes as change records (Bidirectional).",
              "Cortex Data Lake / NetFlow — Enrich maps with real traffic flows and path analytics (Inbound).",
              "Azure OpenAI / RunOps Reasoning Engine — Classify discovered elements, identify anomalies, and describe topology in human-readable form (Inbound).",
              "Confluence / Data Lake / Git — Archive topology maps, version history, and evidence snapshots (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Discovery & Mapping: Collects network device and interface data, builds basic topology graph. (50%).",
              "Tier 2 — Contextual Correlation: Associates devices, zones, and policies for zero-trust segmentation mapping. (75%).",
              "Tier 3 — AI Anomaly Detection & Reasoning: Identifies unauthorized nodes, redundant paths, or policy misalignments. (90%).",
              "Tier 4 — Adaptive Learning & Prediction: Learns normal topology evolution patterns and predicts potential failures or misconfigurations. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Topology Discovery Accuracy ≥ 98% — Percentage of devices/interfaces correctly identified.",
              "Map Update Latency < 5 min — Time from change detection to map refresh.",
              "Unauthorized Device Detection Rate ≥ 95% — Accuracy of rogue or unregistered device identification.",
              "Segmentation Alignment Score ≥ 92% — Compliance of zone adjacency with design baseline.",
              "False Positive Rate ≤ 3% — Incorrectly flagged anomalies.",
              "CMDB Synchronization Success ≥ 97% — Successful topology updates pushed to CMDB."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Devices Discovered: 312 (core routers, distribution switches, access nodes, edge firewalls)",
              "Topology Links Mapped: 4,821",
              "Unauthorized Devices Detected: 14 (rogue VM switches, legacy lab routers)",
              "Zone Misalignment Events: 27",
              "Average Map Refresh Interval: 3.2 minutes",
              "Discovery Accuracy: 98.7%",
              "False Positives: 2.4%",
              "Engineer Hours Saved: ~280 per month"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Discovery Cycle ≤ 5 minutes per network segment — Triggered by telemetry updates or cron.",
              "Map Generation ≤ 2 minutes — Rendering into interactive dashboard or Confluence page.",
              "Anomaly Notification ≤ 10 minutes — Slack/Teams alert + ITSM ticket creation.",
              "CMDB Update Propagation ≤ 15 minutes — Automated sync of discovered topology changes.",
              "Evidence Publication ≤ 1 hour — Export to Confluence/CoreStack.",
              "Agent Availability ≥ 99.9% — Redundant orchestration with Grafana health tracking."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “TopoVision” service.",
              "Discovery Protocols & APIs — SNMPv3, RESTCONF/NETCONF, gNMI, LLDP, CDP, streaming telemetry.",
              "Baseline Repository — PostgreSQL (Memory Plane) for current topology graph; Git for versioned maps.",
              "Reasoning Model — Azure OpenAI GPT-4o trained on network topology datasets, zero-trust segmentation standards, and RunOps KB.",
              "Security Controls — Read-only API credentials, SNMPv3 with auth/privacy, encrypted credential vault, TLS 1.3.",
              "Outputs — Topology Map (interactive HTML/PDF), Zone Adjacency Matrix, Compliance Snapshot Report.",
              "Monitoring — Prometheus metrics: discovery latency, map completeness, anomaly count.",
              "Error Handling — Auto-retry failed device polls; isolate unreachable nodes; flag in map legend."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Periodic Discovery Cycle, Telemetry Link/State Change, or Configuration Change Event.",
      workingPlane: "RunOps n8n automation cluster with containerized 'TopoVision' service.",
      memory: "Stores current topology graph, historical topology deltas, and segment mappings in Postgres/Git.",
      reasoning: "Correlates discovered L2/L3 relationships with Zero-Trust segmentation policies to validate zone adjacency, identifies rogue devices, and classifies topology anomalies.",
      execution: "Generates visual topology maps, updates CMDB/GRC modules with reconciliation data, and triggers anomaly alerts to SOC/ITSM."
    },
    kpis: [
      { label: "Topology Discovery Accuracy", value: "98.7", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Map Update Latency", value: "3.2", unit: "min", target: "< 5 min", color: "#10b981" },
      { label: "Unauthorized Device Detection Rate", value: "95", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "Segmentation Alignment Score", value: "92", unit: "%", target: "≥ 92%", color: "#10b981" },
      { label: "False Positive Rate", value: "2.4", unit: "%", target: "≤ 3%", color: "#10b981" },
      { label: "CMDB Synchronization Success", value: "97", unit: "%", target: "≥ 97%", color: "#10b981" }
    ]
},

'bgp-health-agent': {
    title: "BGP Health Agent",
    description: "Continuously monitors BGP sessions, peer stability, and route propagation across enterprise and cloud edge routers, detecting route hijacks, leaks, and drift from approved network prefixes or policies. Provides automated reasoning and compliance evidence.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "BGP Session Stability: Detects peer flaps, slow convergence, and unstable sessions before they cause outages.",
          "Route Integrity Assurance: Flags unauthorized prefix announcements and AS path anomalies indicative of hijack or leak.",
          "Reduced MTTR for Routing Events: Speeds up detection and containment of routing incidents through automated reasoning and alerting.",
          "Compliance & Audit Readiness: Maintains verifiable records of routing policy adherence to NIST, ISO, and PCI-DSS.",
          "Threat-Aware Routing: Integrates with external BGP monitoring and threat feeds (e.g., RPKI) for proactive defense."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Border Gateway Protocol (BGP) is susceptible to misconfiguration, route leaks, and hijacks — often caused by operational errors or malicious announcements.",
              "In large enterprises, BGP incidents can disrupt business-critical traffic, expose sensitive routes, or violate compliance boundaries.",
              "Manual monitoring and NOC dashboards detect outages but rarely catch subtle routing anomalies such as asymmetric paths, unauthorized AS origins, or prefix leaks.",
              "Organizations require a real-time, autonomous BGP health monitoring system that correlates routing telemetry, policy compliance, and external threat intelligence to maintain secure and stable global connectivity."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "BGP Session Stability — Detects peer flaps, slow convergence, and unstable sessions before they cause outages.",
              "Route Integrity Assurance — Flags unauthorized prefix announcements and AS path anomalies indicative of hijack or leak.",
              "Reduced MTTR for Routing Events — Speeds up detection and containment of routing incidents through automated reasoning and alerting.",
              "Compliance & Audit Readiness — Maintains verifiable records of routing policy adherence to NIST, ISO, and PCI-DSS.",
              "Threat-Aware Routing — Integrates with external BGP monitoring and threat feeds (e.g., BGPStream, Shadowserver, RPKI) for proactive defense."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "BGP Peer Health Monitoring: Track session uptime, flap frequency, and convergence time.",
              "Route Leak Detection: Identify prefixes advertised to unintended peers or transit providers.",
              "Hijack Detection: Cross-validate received prefixes and AS paths against authoritative sources (RPKI, PeeringDB, RADb).",
              "Policy Compliance Verification: Ensure outbound advertisements adhere to enterprise prefix lists and route-maps.",
              "Cloud Edge Alignment: Validate hybrid cloud routes (VPN, Direct Connect, ExpressRoute) against expected prefixes."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Contextual Awareness: Correlates BGP session states, route updates, and AS path changes in real time.",
              "Autonomous Reasoning: Distinguishes legitimate provider maintenance from genuine route leaks or hijacks.",
              "Cross-Source Correlation: Integrates internal routing tables, external route collectors, and threat feeds for full situational awareness.",
              "Adaptive Learning: Learns normal AS-path patterns and prefix distributions over time to detect abnormal propagation instantly.",
              "Remediation Intelligence: Generates precise, vendor-neutral remediation steps and ITSM-ready incident details.",
              "Memory Retention: Maintains historical route baselines for drift and trend analysis, strengthening predictive analytics."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Session Monitoring: Continuously poll BGP peers for session state, uptime, and flap detection via streaming telemetry or API.",
              "Prefix & AS-Path Validation: Compare received routes with authorized prefixes, RPKI ROAs, and policy-based filters.",
              "Route Leak Detection: Detect advertisement of non-owned prefixes or propagation to unintended peers.",
              "Hijack & Anomaly Correlation: Identify mismatched AS paths or origin ASes not listed in registry databases.",
              "Baseline Drift Analysis: Track changes in advertised prefix count, origin ASN, and path length against previous state.",
              "External Validation Integration: Query public route collectors and BGP monitoring services for real-time anomaly corroboration.",
              "AI-Driven Risk Scoring: Compute “Routing Hygiene Score” based on stability, compliance, and threat indicators.",
              "Remediation Workflow: Suggest corrective actions (e.g., prefix-list tightening, route-map updates, peer filtering).",
              "Audit Reporting: Generate compliance-aligned reports (PCI-DSS 1.2.1, NIST SC-7, ISO 27001 A.13.1.1)."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Router / Edge Gateway APIs / Telemetry — Retrieve BGP session and prefix data (Inbound).",
              "Network Management System (NMS / Panorama / SDN Controller) — Consolidate multi-device routing telemetry (Bidirectional).",
              "CoreStack GRC & Compliance Modules — Store route compliance data and evidence (Bidirectional).",
              "ServiceNow / Jira — Create incident and remediation tasks for drift or hijack detection (Bidirectional).",
              "External Route Collectors (BGPStream, RIPE RIS, RouteViews) — Validate global prefix propagation and AS path accuracy (Inbound).",
              "RPKI Validation Services — Confirm prefix/ASN authenticity (Inbound).",
              "Azure OpenAI / RunOps Reasoning Engine — Perform contextual analysis and generate plain-language summaries (Inbound).",
              "Confluence / Data Lake — Archive routing reports, baselines, and audit evidence (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Session & Prefix Monitoring: Polls peer sessions and tracks prefix changes. (50%).",
              "Tier 2 — Route Integrity Validation: Cross-checks routes against registries and internal baselines. (75%).",
              "Tier 3 — AI Reasoning & Anomaly Detection: Detects leaks, hijacks, or suspicious AS-path patterns. (90%).",
              "Tier 4 — Predictive & Self-Learning: Learns long-term routing behavior to predict and prevent anomalies. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "BGP Session Availability ≥ 99.9% — Active sessions / total configured peers.",
              "Mean Time to Detect (MTTD) Routing Anomalies < 5 min — From peer or prefix change to detection alert.",
              "Hijack Detection Accuracy ≥ 95% — Confirmed hijack events vs false positives.",
              "Route Leak Detection Accuracy ≥ 93% — Leaked routes correctly identified.",
              "Prefix Compliance Coverage ≥ 98% — Announced prefixes matching authorized registry.",
              "False Positive Rate ≤ 3% — Incorrect anomaly alerts.",
              "Routing Hygiene Score ≥ 90% — Weighted average of stability, prefix accuracy, and route integrity."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Routers Monitored: 84 (core, edge, and cloud VRFs)",
              "Total BGP Peers: 421",
              "Average Session Uptime: 99.93%",
              "Detected Route Leaks: 7",
              "Detected Hijack Attempts: 2 (external validation confirmed)",
              "Unauthorized Prefix Announcements: 11",
              "Average MTTD (Anomaly): 3.7 min",
              "Average MTTR (Correction): 5.5 hrs",
              "False Positive Rate: 2.5%",
              "Overall Routing Hygiene Score: 91.7 / 100",
              "Engineering Time Saved: ~240 hrs/month"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Telemetry Collection Cycle ≤ 1 min — Real-time streaming from routers / peers.",
              "Anomaly Correlation & AI Reasoning ≤ 3 min — Combines local and external data validation.",
              "Incident Creation (ITSM) ≤ 10 min — Auto-ticket generation for verified events.",
              "Compliance Report Publication ≤ 1 hr — Generates evidence for GRC systems.",
              "Agent Availability ≥ 99.9% — Redundant deployment monitored via Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “BGPSentry” module.",
              "Primary APIs — NETCONF/RESTCONF, gNMI, SNMPv3, RPKI REST, external BGP collectors (BGPStream/RIS/RouteViews).",
              "Reasoning Model — Azure OpenAI GPT-4o trained on routing telemetry, BGP anomaly datasets, and RunOps KB.",
              "Security Controls — Read-only BGP session queries, encrypted credentials, TLS 1.3.",
              "Data Persistence — PostgreSQL (Memory Plane) for route baselines; Confluence for historical evidence.",
              "Outputs — BGP Health Report (JSON/PDF), Route Integrity Delta, Incident Summary, Compliance Snapshot.",
              "Monitoring — Grafana dashboard: peer status, prefix drift, RPKI coverage, anomaly trendline.",
              "Error Handling — Auto-retry on telemetry failure; correlation fallback to external collector."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Telemetry Collection Cycle (≤ 1 min), BGP Session Flap, or Prefix/AS-Path Change Event.",
      workingPlane: "RunOps n8n automation cluster with containerized 'BGPSentry' module.",
      memory: "Maintains historical route baselines, stability trends, and prefix origin data in Postgres.",
      reasoning: "Correlates internal BGP telemetry with external RPKI/Route Collector data to distinguish legitimate events from leaks/hijacks, determines Routing Hygiene Score, and suggests remediation steps.",
      execution: "Generates ITSM incident ticket, sends anomaly alert, and produces compliance-aligned audit report."
    },
    kpis: [
      { label: "BGP Session Availability", value: "99.9", unit: "%", target: "≥ 99.9%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD) Anomalies", value: "3.7", unit: "min", target: "< 5 min", color: "#10b981" },
      { label: "Hijack Detection Accuracy", value: "95", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "Route Leak Detection Accuracy", value: "93", unit: "%", target: "≥ 93%", color: "#10b981" },
      { label: "Prefix Compliance Coverage", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Routing Hygiene Score", value: "91.7", unit: "/ 100", target: "≥ 90%", color: "#10b981" }
    ]
},

'route-optimization-agent': {
    title: "Route Optimization Agent",
    description: "Continuously evaluates real-time routing performance metrics — latency, jitter, bandwidth, and path cost — across enterprise and cloud network paths, dynamically tuning routing policies and route preferences to optimize for performance, cost, and SLA adherence.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Performance Optimization: Dynamically selects lowest-latency and highest-availability routes for critical traffic.",
          "Cost Efficiency: Adjusts egress and transit path preferences to balance performance with provider cost models.",
          "SLA Compliance: Maintains route quality within contractual performance thresholds (latency/jitter/loss).",
          "Operational Automation: Reduces manual route management through continuous, closed-loop tuning.",
          "Resilient Connectivity: Detects path degradation early and automatically reroutes or reweights policies to maintain uptime."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "In distributed, hybrid environments, static route metrics and manual path tuning fail to adapt to changing network conditions, cloud egress costs, and dynamic application demands.",
              "WAN and internet routes fluctuate due to congestion, provider issues, or peering performance drift — yet network teams lack continuous, AI-assisted optimization at scale.",
              "Relying on human analysis of NMS dashboards or routing tables causes delayed response to suboptimal paths, impacting user experience and application SLA compliance.",
              "The enterprise needs an autonomous routing optimization layer that continuously monitors path health, evaluates policy decisions, and implements intent-based adjustments while preserving stability and compliance."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Performance Optimization — Dynamically selects lowest-latency and highest-availability routes for critical traffic.",
              "Cost Efficiency — Adjusts egress and transit path preferences to balance performance with provider cost models.",
              "SLA Compliance — Maintains route quality within contractual performance thresholds (latency/jitter/loss).",
              "Operational Automation — Reduces manual route management through continuous, closed-loop tuning.",
              "Resilient Connectivity — Detects path degradation early and automatically reroutes or reweights policies to maintain uptime."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Dynamic Path Selection: Automatically reweights or reprioritizes routes based on latency and packet loss trends.",
              "Traffic Engineering: Optimizes inter-site and internet peering routes to minimize congestion and round-trip delay.",
              "Cloud Cost Optimization: Shifts routes between cloud providers or egress zones based on cost thresholds.",
              "BGP Path Preference Tuning: Adjusts local preference and MED attributes dynamically to enforce optimal routing.",
              "SD-WAN Integration: Ingests link telemetry to enforce real-time policy changes on WAN edges."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Real-Time Awareness: Continuously ingests telemetry from routers, WAN edges, and external probes for latency, jitter, and throughput.",
              "Intent-Based Decisioning: Evaluates policies based on enterprise intent — prioritize performance, minimize cost, or optimize for SLA compliance.",
              "Self-Learning: Models normal path performance, learns time-of-day or event-driven variations, and predicts degradation before it occurs.",
              "Autonomous Optimization: Recommends or executes routing policy changes via dynamic metric adjustments, local-pref tuning, or route redistribution.",
              "Risk-Aware Control: Simulates impacts pre-commit (via Change Simulation Agent) to avoid flaps or route churn.",
              "Cross-Domain Visibility: Correlates routing decisions with application experience and security zones to ensure holistic performance governance."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Telemetry Collection: Aggregates latency, jitter, and packet loss metrics from network devices, probes, and SD-WAN controllers.",
              "Path Scoring & Ranking: Computes real-time route quality scores for each prefix or next-hop based on historical and live data.",
              "Dynamic Metric Adjustment: Adjusts OSPF/BGP cost, local preference, or route-map attributes based on policy outcomes.",
              "Cost-Aware Optimization: Integrates provider billing APIs to correlate path selection with cost per GB or region.",
              "AI Reasoning & Forecasting: Predicts route degradation using trend models and recommends preemptive adjustments.",
              "Simulation & Validation: Tests route changes in sandbox or staging mode prior to live commit.",
              "Policy Automation: Implements approved route changes automatically through orchestrator or API.",
              "Continuous Feedback Loop: Measures post-change performance to validate optimization effectiveness."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Routers / SD-WAN Edges (API / Telemetry) — Ingest routing tables, metrics, and path statistics (Inbound).",
              "Central Network Controller / NMS — Enforce routing updates, redistribute policies (Bidirectional).",
              "CoreStack GRC / Cost Modules — Retrieve link cost models and compliance thresholds (Bidirectional).",
              "Change Simulation Agent — Pre-commit simulation of routing metric and policy impacts (Bidirectional).",
              "ServiceNow / Jira — Track optimization events and approvals as change tickets (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Analyze telemetry patterns, recommend policy adjustments, and generate optimization narratives (Inbound).",
              "Confluence / Data Lake — Archive optimization reports and before/after performance deltas (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Telemetry Collection & Scoring: Gather metrics, score routes for latency, jitter, and cost. (50%).",
              "Tier 2 — Policy Simulation & Ranking: Model impact of policy changes using live topology and route attributes. (75%).",
              "Tier 3 — AI Reasoning & Policy Recommendation: Suggest attribute tuning (BGP local-pref, OSPF cost, path weight). (90%).",
              "Tier 4 — Adaptive Optimization Loop: Self-learns traffic patterns and implements policy adjustments autonomously. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Path Optimization Accuracy ≥ 95% — Ratio of improved to total adjusted paths.",
              "False Positive Rate (Degradation Prediction) ≤ 3% — Incorrectly flagged paths with no real degradation.",
              "Mean Time to Detect (MTTD) < 3 min — From telemetry deviation to issue detection.",
              "Mean Time to Optimize (MTTO) < 10 min — From detection to applied optimization.",
              "SLA Compliance Rate ≥ 98% — Percentage of sessions within latency/jitter targets.",
              "Cost Efficiency Gain ≥ 10% — Monthly reduction in transit or egress costs."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Routers / WAN Edges Monitored: 120",
              "Total Prefixes Evaluated: 58,400",
              "Optimization Actions Executed: 2,312",
              "Latency Improvement (Average): 18%",
              "Jitter Reduction: 14%",
              "Transit Cost Savings: 11.6%",
              "False Positives: 2.2%",
              "Average MTTD: 2.8 minutes",
              "Average MTTO: 8.6 minutes",
              "SLA Compliance Improvement: +7% month-over-month",
              "Validated Optimizations via Simulation: 96% success rate",
              "Engineer Hours Saved: ~230 per month"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Telemetry Collection Interval ≤ 60 seconds — Continuous polling or streaming telemetry.",
              "AI Analysis & Scoring Cycle ≤ 3 minutes — Includes correlation and prediction.",
              "Optimization Commit (Post-Approval) ≤ 10 minutes — API-based reweight or route-map change.",
              "Post-Optimization Validation ≤ 15 minutes — Verify latency and cost deltas.",
              "Evidence Publication ≤ 1 hour — Report export to Confluence / CoreStack.",
              "Agent Availability ≥ 99.9% — HA n8n cluster monitored by Prometheus / Grafana."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “RouteTuner” service.",
              "Primary APIs — NETCONF, RESTCONF, SNMPv3, streaming telemetry, SD-WAN controller API.",
              "Telemetry Inputs — RTT, jitter, packet loss, bandwidth utilization, path cost metrics.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on network optimization and BGP/OSPF policy datasets.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing route baselines and performance history.",
              "Security Controls — Read/write API access limited to route policies, TLS 1.3 encryption, RBAC enforcement.",
              "Outputs — Route Optimization Report (JSON/PDF), Recommended Policy Adjustments, Before/After Performance Summary.",
              "Monitoring — Grafana dashboard: latency heatmap, cost trendlines, SLA compliance metrics.",
              "Error Handling — Auto-retry failed telemetry calls; rollback if performance worsens post-change."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Telemetry Collection Interval, Path Degradation Prediction, or Cost Threshold Breach.",
      workingPlane: "RunOps n8n automation cluster with containerized 'RouteTuner' service.",
      memory: "Stores route baselines, performance history, and cost models in Postgres.",
      reasoning: "Evaluates intent (performance/cost/SLA), predicts route degradation, and recommends dynamic metric adjustments (local-pref/cost) via AI-driven policy tuning.",
      execution: "Implements approved routing policy changes via API/Orchestrator, measures post-change performance, and validates optimization effectiveness."
    },
    kpis: [
      { label: "Path Optimization Accuracy", value: "95", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "False Positive Rate (Prediction)", value: "2.2", unit: "%", target: "≤ 3%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD)", value: "2.8", unit: "min", target: "< 3 min", color: "#10b981" },
      { label: "Mean Time to Optimize (MTTO)", value: "8.6", unit: "min", target: "< 10 min", color: "#10b981" },
      { label: "SLA Compliance Rate", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Cost Efficiency Gain", value: "11.6", unit: "%", target: "≥ 10%", color: "#10b981" }
    ]
},
'qos-policy-agent': {
    "title": "QoS Policy Agent",
    "description": "Continuously monitors application traffic, latency, and throughput across enterprise and cloud edges, automatically tuning Quality of Service (QoS) policies to meet defined service-level objectives (SLOs) and application SLAs.",
    "details": [
      {
        "title": "Business Value & Outcomes",
        "points": [
          "SLA-Driven Performance: Ensures bandwidth and latency targets for mission-critical applications are met automatically.",
          "Dynamic Optimization: Continuously adjusts QoS class allocations and shaping policies based on live traffic conditions.",
          "Operational Efficiency: Reduces manual QoS tuning by up to 80 %, freeing engineers to focus on strategic performance initiatives.",
          "User Experience Stability: Minimizes jitter, loss, and latency for real-time applications under varying load conditions.",
          "Compliance Readiness: Provides auditable records of QoS policy enforcement aligned to service assurance and regulatory standards."
        ]
      }
    ],
    "sections": [
      {
        "title": "1. Business Context",
        "subSections": [
          {
            "title": "Problem Statement",
            "content": [
              "Static QoS configurations cannot keep pace with changing traffic patterns, cloud workloads, and remote access behaviors.",
              "Business-critical applications (EHR, VoIP, ERP, collaboration, and AI workloads) compete for bandwidth against recreational or bulk transfer traffic.",
              "Manual QoS adjustments via CLI or Panorama commits are slow and often reactive, leading to performance degradation and user dissatisfaction.",
              "Enterprises require an adaptive, self-regulating QoS enforcement layer that uses real-time telemetry and AI reasoning to maintain SLA conformance dynamically."
            ]
          },
          {
            "title": "Business Outcomes (Benefits)",
            "content": [
              "SLA-Driven Performance — Ensures bandwidth and latency targets for mission-critical applications are met automatically.",
              "Dynamic Optimization — Continuously adjusts QoS class allocations and shaping policies based on live traffic conditions.",
              "Operational Efficiency — Reduces manual QoS tuning by up to 80 %, freeing engineers to focus on strategic performance initiatives.",
              "User Experience Stability — Minimizes jitter, loss, and latency for real-time applications under varying load conditions.",
              "Compliance Readiness — Provides auditable records of QoS policy enforcement aligned to service assurance and regulatory standards."
            ]
          },
          {
            "title": "Use Case Alignment",
            "content": [
              "Application SLA Assurance: Maintain latency and jitter SLAs for real-time traffic (voice, telehealth, video, trading).",
              "Adaptive Bandwidth Management: Adjust shaping and prioritization based on congestion and usage patterns.",
              "Multi-Cloud QoS Consistency: Align QoS policies across WAN, SD-WAN, and cloud gateways.",
              "Change Impact Prediction: Simulate effects of QoS reallocation on other classes before commit.",
              "Zero-Trust Service Differentiation: Apply QoS tiers per user identity, device, or application context."
            ]
          },
          {
            "title": "Why Agentic Agents Are Uniquely Qualified",
            "content": [
              "Telemetry Awareness: Monitors interface statistics, queue depth, and per-class utilization in real time.",
              "AI Reasoning: Evaluates traffic patterns and SLA breaches to recommend or execute QoS policy adjustments.",
              "Cross-Domain Correlation: Links performance metrics to applications, users, and devices for context-sensitive prioritization.",
              "Self-Learning Behavior: Learns traffic baselines and seasonal patterns (e.g., backups, remote work peaks).",
              "Closed-Loop Governance: Continuously validates QoS changes against policy intent and compliance frameworks."
            ]
          }
        ]
      },
      {
        "title": "2. Technical Functionality",
        "subSections": [
          {
            "title": "Scope of Capabilities",
            "content": [
              "Telemetry Collection: Continuously ingest per-class bandwidth, queue depth, and interface statistics from routers, gateways, and WAN edges.",
              "Application Flow Classification: Correlate with App-ID and User-ID to map flows to business-critical or discretionary categories.",
              "Dynamic Policy Adjustment: Modify QoS profiles (priority, shaping, policing) when SLA thresholds are breached.",
              "Anomaly Detection: Identify congestion, buffer overflows, or QoS misclassifications.",
              "Predictive Simulation: Forecast impact of potential policy changes via Change Simulation Agent integration.",
              "AI Optimization Engine: Reallocate bandwidth dynamically to balance performance, fairness, and cost.",
              "Audit and Evidence Generation: Record all QoS adjustments and SLA conformance metrics for compliance."
            ]
          },
          {
            "title": "Integrations",
            "content": [
              "Network Devices / APIs / Telemetry (Routers, Gateways) — Collect real-time QoS counters and interface metrics (Inbound).",
              "Central Management System (e.g., SD-WAN Controller, Panorama) — Push dynamic QoS profile updates (Bidirectional).",
              "Cortex Data Lake / NetFlow / IPFIX — Correlate traffic flows to QoS classes (Inbound).",
              "CoreStack GRC / SLA Management — Align QoS metrics with service-level objectives and audit retention (Bidirectional).",
              "ServiceNow / Jira — Generate tickets for QoS anomalies or SLA breaches (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Evaluate SLA compliance, interpret trends, and draft adjustment rationale (Inbound).",
              "Confluence / Data Lake — Archive SLA compliance evidence and QoS change records (Outbound)."
            ]
          },
          {
            "title": "Agentic Tiers",
            "content": [
              "Tier 1 — Monitoring & Baseline Learning: Collect and baseline traffic telemetry, interface utilization, and latency trends. (50%).",
              "Tier 2 — SLA Correlation: Map traffic patterns to SLA definitions and detect threshold violations. (75%).",
              "Tier 3 — AI-Driven Optimization: Adjust QoS policy dynamically to correct SLA breaches. (90%).",
              "Tier 4 — Predictive Self-Learning Loop: Anticipate congestion and preemptively reallocate resources. (100%)."
            ]
          }
        ]
      },
      {
        "title": "3. Operations",
        "subSections": [
          {
            "title": "Key Performance Indicators (KPI)",
            "content": [
              "QoS Policy Compliance ≥ 95% — Traffic matching correct class-of-service.",
              "SLA Conformance Rate ≥ 98% — Percentage of monitored flows meeting latency/jitter targets.",
              "Mean Time to Detect (MTTD) < 2 min — Time from SLA violation to detection.",
              "Mean Time to Adjust (MTTA) < 5 min — Time from detection to QoS policy correction.",
              "Application Priority Accuracy ≥ 97% — Correct classification of flows by application importance.",
              "False Adjustment Rate ≤ 3% — QoS policy changes with no measurable improvement."
            ]
          },
          {
            "title": "Representative Statistics (30-Day Enterprise Run)",
            "content": [
              "Devices Monitored: 126 (core routers, WAN edges, branch gateways)",
              "Applications Tracked: 312 (business + non-business)",
              "SLA Violations Detected: 143 (0.46%)",
              "Auto-Adjusted QoS Policies: 128",
              "Average Latency Improvement (Critical Apps): 19%",
              "Average Bandwidth Efficiency Gain: 11%",
              "MTTD: 1.4 min MTTA: 4.6 min",
              "False Adjustment Rate: 2.1%",
              "QoS Compliance Score: 96.8 / 100",
              "Engineer Hours Saved: ≈ 200 / month",
              "Audit Reports Generated: 8 (CoreStack + Confluence)"
            ]
          },
          {
            "title": "Service Level Agreements (SLA)",
            "content": [
              "Telemetry Collection Interval ≤ 60 s — gNMI / streaming telemetry feeds.",
              "Anomaly Detection & AI Evaluation ≤ 2 min — Triggered by threshold breaches.",
              "Policy Adjustment Commit ≤ 5 min — Safe commit via API to management controller.",
              "Evidence Publication ≤ 1 hr — SLA summary and audit export.",
              "Agent Availability ≥ 99.9% — Continuous monitoring with Prometheus/Grafana."
            ]
          },
          {
            "title": "Technical Context",
            "content": [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “QoSense” microservice.",
              "Primary APIs & Protocols — RESTCONF, NETCONF, gNMI, streaming telemetry, SD-WAN controller APIs.",
              "Reasoning Model — Azure OpenAI GPT-4o tuned for SLA interpretation, traffic behavior analysis, and QoS policy reasoning.",
              "Data Sources — Interface telemetry, App-ID classification, SLA definitions, CMDB network inventory.",
              "Security Controls — Read-only monitoring access, TLS 1.3 encryption, RBAC separation.",
              "Outputs — QoS Optimization Report (JSON/PDF), SLA Compliance Snapshot, Policy Adjustment Log.",
              "Monitoring — Grafana dashboards for class utilization, latency trendlines, and SLA health.",
              "Error Handling — Auto-retry failed telemetry sessions; rollback policy if congestion worsens post-adjustment."
            ]
          }
        ]
      }
    ],
    "agenticModel": {
      "trigger": "Telemetry Collection Interval, SLA Violation Detection, or Policy Drift Anomaly.",
      "workingPlane": "RunOps n8n automation cluster with containerized 'QoSense' microservice.",
      "memory": "Stores QoS policy baselines, application SLA definitions, and historical performance trends in Postgres.",
      "reasoning": "Evaluates SLA compliance and traffic behavior using AI to recommend or execute dynamic adjustments to QoS profiles (shaping, priority) to minimize congestion.",
      "execution": "Pushes dynamic QoS policy updates to the central management system/controller, validates the change against performance metrics, and logs the adjustment for audit."
    },
    "kpis": [
      { "label": "QoS Policy Compliance", "value": "96.8", "unit": "%", "target": "≥ 95%", "color": "#10b981" },
      { "label": "SLA Conformance Rate", "value": "98", "unit": "%", "target": "≥ 98%", "color": "#10b981" },
      { "label": "Mean Time to Detect (MTTD)", "value": "1.4", "unit": "min", "target": "< 2 min", "color": "#10b981" },
      { "label": "Mean Time to Adjust (MTTA)", "value": "4.6", "unit": "min", "target": "< 5 min", "color": "#10b981" },
      { "label": "Application Priority Accuracy", "value": "97", "unit": "%", "target": "≥ 97%", "color": "#10b981" },
      { "label": "False Adjustment Rate", "value": "2.1", "unit": "%", "target": "≤ 3%", "color": "#10b981" }
    ]
},

'spanning-tree-guardian-agent': {
    "title": "Spanning Tree Guardian Agent",
    "description": "Continuously monitors Layer 2 topology states, spanning tree root roles, and interface transitions to detect loops, misconfigurations, or unauthorized devices — automatically mitigating broadcast storms and maintaining network stability and compliance.",
    "details": [
      {
        "title": "Business Value & Outcomes",
        "points": [
          "Loop Prevention: Detects and quarantines links or ports causing Layer 2 loops before they trigger broadcast storms.",
          "Topology Integrity: Ensures STP root, priority, and path cost configurations remain aligned to the network baseline.",
          "Operational Resilience: Reduces outage recovery times from hours to minutes through autonomous detection and correction.",
          "Compliance & Audit Assurance: Produces verifiable evidence of spanning tree compliance with NIST CM-6 and ISO 27001 A.12.1.2.",
          "Reduced Mean Time to Diagnose (MTTD): Provides event-driven context, enabling near real-time isolation of misbehaving ports or devices."
        ]
      }
    ],
    "sections": [
      {
        "title": "1. Business Context",
        "subSections": [
          {
            "title": "Problem Statement",
            "content": [
              "Most enterprise networks still rely on Spanning Tree Protocol (STP) or its modern variants (RSTP / MSTP) for Layer 2 loop prevention.",
              "Small configuration errors — an unmanaged switch, trunk mislabel, or root bridge drift — can cause network-wide broadcast storms, crippling critical business services.",
              "Manual troubleshooting is reactive, requiring CLI inspection and packet tracing under outage pressure.",
              "Organizations need a real-time guardian that continuously validates spanning tree consistency, detects misbehavior, and prevents topology loops before they propagate."
            ]
          },
          {
            "title": "Business Outcomes (Benefits)",
            "content": [
              "Loop Prevention — Detects and quarantines links or ports causing Layer 2 loops before they trigger broadcast storms.",
              "Topology Integrity — Ensures STP root, priority, and path cost configurations remain aligned to the network baseline.",
              "Operational Resilience — Reduces outage recovery times from hours to minutes through autonomous detection and correction.",
              "Compliance & Audit Assurance — Produces verifiable evidence of spanning tree compliance with NIST CM-6 and ISO 27001 A.12.1.2.",
              "Reduced Mean Time to Diagnose (MTTD) — Provides event-driven context, enabling near real-time isolation of misbehaving ports or devices."
            ]
          },
          {
            "title": "Use Case Alignment",
            "content": [
              "Root Bridge Drift Detection: Monitors for unauthorized or accidental root priority changes.",
              "Loop Prevention & Port Isolation: Automatically disables or blocks ports exhibiting looping or storm behavior.",
              "BPDU Guard Enforcement: Validates BPDU guard configurations and ensures they remain active on access ports.",
              "Topology Change Auditing: Tracks topology change notifications (TCNs) and correlates them to authorized changes.",
              "Unauthorized Device Detection: Identifies rogue switches or unmanaged bridges joining the domain."
            ]
          },
          {
            "title": "Why Agentic Agents Are Uniquely Qualified",
            "content": [
              "Continuous Observation: Collects BPDU telemetry, spanning tree root status, and port state transitions in real time.",
              "Behavioral Reasoning: Distinguishes between legitimate topology re-convergence and anomalous BPDU flooding or root role changes.",
              "Self-Healing Actions: Can auto-disable rogue ports or quarantine misbehaving devices based on confidence thresholds.",
              "Cross-Layer Correlation: Links topology events with upstream routing and zone policies for impact analysis.",
              "Adaptive Learning: Learns from historical topology behaviors (scheduled maintenance, firmware upgrades) to reduce false alarms."
            ]
          }
        ]
      },
      {
        "title": "2. Technical Functionality",
        "subSections": [
          {
            "title": "Scope of Capabilities",
            "content": [
              "BPDU Monitoring & Analysis: Captures Bridge Protocol Data Units across switch interfaces to validate STP roles, root bridge status, and timers.",
              "Loop & Storm Detection: Analyzes MAC address flapping, broadcast rate spikes, and duplicate BPDU events to identify emerging loops.",
              "Root & Priority Validation: Ensures designated root bridges and priorities align with approved topology baseline.",
              "Topology Change Detection: Tracks TCN events, correlates to authorized change windows, and flags anomalies.",
              "BPDU Guard & PortFast Enforcement: Monitors access ports for guard violations or unauthorized bridge formation.",
              "AI Reasoning Engine: Classifies events by risk and context — differentiating transient re-convergence from structural faults.",
              "Auto-Mitigation Actions: Optionally disables affected ports, isolates VLANs, or applies storm-control thresholds autonomously.",
              "Audit Logging & Evidence Generation: Maintains continuous records of spanning tree health, root consistency, and corrective actions."
            ]
          },
          {
            "title": "Integrations",
            "content": [
              "Switch APIs / SNMP / Telemetry (LLDP, STP, MSTP) — Collect port states, BPDU counters, and topology tables (Inbound).",
              "Network Management / Orchestrator Systems — Synchronize topology baselines and enforce policy compliance (Bidirectional).",
              "CoreStack GRC / Compliance Modules — Store evidence and map topology health to compliance controls (Bidirectional).",
              "ServiceNow / Jira — Generate incidents or change tickets for STP drift or loop events (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Interpret topology patterns and generate plain-language incident summaries (Inbound).",
              "Confluence / Data Lake — Archive topology health reports and historical evidence (Outbound)."
            ]
          },
          {
            "title": "Agentic Tiers",
            "content": [
              "Tier 1 — Topology Telemetry Collection: Gather BPDU data, STP state, and MAC movement telemetry. (50%).",
              "Tier 2 — Drift & Anomaly Detection: Identify loops, root changes, and port state inconsistencies. (75%).",
              "Tier 3 — AI-Driven Root Cause Reasoning: Evaluate event context, correlate to maintenance or rogue behavior, recommend actions. (90%).",
              "Tier 4 — Autonomous Mitigation & Learning: Self-correct topology by disabling ports, adjusting guard thresholds, and refining behavior models. (100%)."
            ]
          }
        ]
      },
      {
        "title": "3. Operations",
        "subSections": [
          {
            "title": "Key Performance Indicators (KPI)",
            "content": [
              "Loop Detection Accuracy ≥ 97% — Correct identification of true Layer 2 loops.",
              "False Positive Rate ≤ 3% — Non-impacting events incorrectly flagged.",
              "Mean Time to Detect (MTTD) < 30 s — Time from loop initiation to detection.",
              "Mean Time to Contain (MTTC) < 90 s — Time to isolate or disable affected ports.",
              "Root Bridge Stability ≥ 99.9% — Uptime of designated root across monitored domains.",
              "Broadcast Storm Prevention Rate 100% — Successful containment of high-rate broadcast events."
            ]
          },
          {
            "title": "Representative Statistics (30-Day Enterprise Run)",
            "content": [
              "Switches Monitored: 186 (core, distribution, access)",
              "BPDU Events Analyzed: 2.4 million",
              "Topology Change Notifications (TCNs): 312 (normal) / 21 (anomalous)",
              "Detected Loops: 7 (contained automatically)",
              "Unauthorized Root Role Attempts: 3 (rogue switches)",
              "BPDU Guard Violations: 11 (auto-disabled ports)",
              "Average MTTD: 24 seconds",
              "Average MTTC: 82 seconds",
              "False Positive Rate: 2.2%",
              "Network Stability Score: 99.96% uptime (no storm outages)",
              "Engineer Hours Saved: ~260 / month"
            ]
          },
          {
            "title": "Service Level Agreements (SLA)",
            "content": [
              "BPDU Collection Interval ≤ 10 s — Streaming telemetry or SNMP trap-based polling.",
              "Loop Detection Response ≤ 30 s — Trigger isolation workflows.",
              "Port Isolation / Quarantine ≤ 90 s — Disable or apply storm-control limit automatically.",
              "Root Drift Validation Cycle Hourly — Validate designated bridge consistency.",
              "Evidence Publication ≤ 1 hr — Audit-ready report export.",
              "Agent Availability ≥ 99.9% — HA design monitored via Prometheus/Grafana."
            ]
          },
          {
            "title": "Technical Context",
            "content": [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “L2Guardian” module.",
              "Primary Interfaces — SNMPv3, LLDP, STP/RSTP/MSTP MIBs, gNMI streaming telemetry.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing current root bridge ID, port states, and baseline topology.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on STP event logs, loop prevention patterns, and RunOps network KB.",
              "Security Controls — Read-only API access; credential vault; TLS 1.3; RBAC separation.",
              "Outputs — Loop Detection Log (JSON), Topology Drift Report (PDF), Port Isolation Action Record, Compliance Snapshot.",
              "Monitoring — Grafana dashboard showing BPDU rate, port isolation events, topology stability index.",
              "Error Handling — Auto-retry failed polling sessions; safe-mode disable only after two confirmed loop detections."
            ]
          }
        ]
      }
    ],
    "agenticModel": {
      "trigger": "BPDU telemetry event (≤ 10s interval), Port State Transition, or Root Bridge Priority Change.",
      "workingPlane": "RunOps n8n automation cluster with containerized 'L2Guardian' module.",
      "memory": "Stores baseline root bridge ID, allowed port states, and historical BPDU patterns in Postgres.",
      "reasoning": "Analyzes BPDU behavior and port state context using AI to distinguish legitimate re-convergence from anomalous loop initiation or rogue device connection, recommending or executing containment.",
      "execution": "Auto-mitigates by disabling/quarantining the offending port or device via API/CLI, logs the action (MTTC < 90s), and updates the incident ticket/audit log."
    },
    "kpis": [
      { "label": "Loop Detection Accuracy", "value": "97", "unit": "%", "target": "≥ 97%", "color": "#10b981" },
      { "label": "False Positive Rate", "value": "2.2", "unit": "%", "target": "≤ 3%", "color": "#10b981" },
      { "label": "Mean Time to Detect (MTTD)", "value": "24", "unit": "s", "target": "< 30 s", "color": "#10b981" },
      { "label": "Mean Time to Contain (MTTC)", "value": "82", "unit": "s", "target": "< 90 s", "color": "#10b981" },
      { "label": "Root Bridge Stability", "value": "99.9", "unit": "%", "target": "≥ 99.9%", "color": "#10b981" },
      { "label": "Broadcast Storm Prevention Rate", "value": "100", "unit": "%", "target": "100%", "color": "#10b981" }
    ]
},

'firmware-lifecycle-agent': {
    "title": "Firmware Lifecycle Agent",
    "description": "Continuously monitors network infrastructure firmware versions, vendor advisories, and vulnerability disclosures to automate the planning, validation, and execution of firmware updates across switches, routers, and gateways — ensuring operational stability, compliance, and security posture consistency.",
    "details": [
      {
        "title": "Business Value & Outcomes",
        "points": [
          "Vulnerability Mitigation: Detects outdated firmware with known CVEs and automates upgrade sequencing.",
          "Operational Consistency: Maintains standardized firmware baselines across device classes and zones.",
          "Reduced Downtime Risk: Schedules and validates updates during approved windows, ensuring rollback readiness.",
          "Compliance Assurance: Demonstrates adherence to NIST CM-6, ISO 27001 A.12.1.2, and PCI-DSS patch management controls.",
          "Lifecycle Optimization: Reduces maintenance overhead by predicting end-of-support (EoS/EoL) risk early."
        ]
      }
    ],
    "sections": [
      {
        "title": "1. Business Context",
        "subSections": [
          {
            "title": "Problem Statement",
            "content": [
              "Network firmware often lags behind recommended or secure versions due to manual update processes, fragmented visibility, and fear of downtime.",
              "Outdated firmware introduces vulnerabilities, feature incompatibility, and operational instability.",
              "Security advisories (CVEs, vendor notices) require urgent action, yet teams lack unified visibility into what versions are running where.",
              "Without automation, large enterprises cannot safely manage firmware compliance across hundreds of devices distributed across sites, clouds, and operational domains."
            ]
          },
          {
            "title": "Business Outcomes (Benefits)",
            "content": [
              "Vulnerability Mitigation — Detects outdated firmware with known CVEs and automates upgrade sequencing.",
              "Operational Consistency — Maintains standardized firmware baselines across device classes and zones.",
              "Reduced Downtime Risk — Schedules and validates updates during approved windows, ensuring rollback readiness.",
              "Compliance Assurance — Demonstrates adherence to NIST CM-6, ISO 27001 A.12.1.2, and PCI-DSS patch management controls.",
              "Lifecycle Optimization — Reduces maintenance overhead by predicting end-of-support (EoS/EoL) risk early."
            ]
          },
          {
            "title": "Use Case Alignment",
            "content": [
              "Firmware Compliance Validation: Compare running firmware versions against golden baselines and approved vendor releases.",
              "Security Advisory Correlation: Cross-reference firmware builds against known CVEs, vendor advisories, and PSIRT bulletins.",
              "Automated Upgrade Orchestration: Plan, download, stage, and verify firmware images automatically.",
              "Downtime-Aware Scheduling: Integrate with ITSM maintenance windows for safe execution.",
              "Audit Reporting & Evidence Generation: Produce verifiable reports of firmware hygiene and update history."
            ]
          },
          {
            "title": "Why Agentic Agents Are Uniquely Qualified",
            "content": [
              "Full Lifecycle Awareness: Tracks firmware across detection, assessment, scheduling, deployment, and verification.",
              "AI Reasoning: Evaluates advisory criticality, CVSS severity, and device role to prioritize update order.",
              "Change Control Integration: Automatically links updates to ITSM records and maintenance windows.",
              "Predictive Insight: Learns device-specific failure patterns, recommending phased upgrades or rollback buffers.",
              "Compliance Context: Maps patching activity to NIST, ISO, and PCI-DSS control objectives for continuous audit readiness."
            ]
          }
        ]
      },
      {
        "title": "2. Technical Functionality",
        "subSections": [
          {
            "title": "Scope of Capabilities",
            "content": [
              "Firmware Inventory & Version Tracking: Continuously scan routers, switches, and gateways for current firmware versions.",
              "Golden Baseline Comparison: Validate against enterprise-approved firmware versions per device model.",
              "Security Advisory Correlation: Match firmware builds against vendor CVE databases and PSIRT feeds.",
              "Upgrade Planning & Scheduling: Generate automated upgrade plans considering risk, dependency, and window availability.",
              "Pre-Update Validation: Check hardware readiness (memory, image integrity, failover state).",
              "Update Execution: Automate download, staging, commit, and post-verification steps.",
              "Rollback & Recovery: Automatically restore prior version on verification failure.",
              "Audit & Compliance Reporting: Export lifecycle evidence and CVE alignment reports to GRC systems."
            ]
          },
          {
            "title": "Integrations",
            "content": [
              "Network Device APIs / CLI (SSH, NETCONF, RESTCONF) — Retrieve version info and execute firmware updates (Bidirectional).",
              "Vendor Advisory Feeds (PSIRT, CVE, RSS) — Collect vulnerability and patch information (Inbound).",
              "CoreStack GRC / Compliance Modules — Map firmware patch activity to compliance controls (Bidirectional).",
              "ServiceNow / Jira — Manage change windows, maintenance approvals, and rollback plans (Bidirectional).",
              "Confluence / Git Repos — Store golden baselines, image hashes, and change documentation (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Analyze advisories, assess risk, and recommend update sequencing (Inbound).",
              "Telemetry & Syslog Streams — Verify upgrade status and log results for audit (Inbound)."
            ]
          },
          {
            "title": "Agentic Tiers",
            "content": [
              "Tier 1 — Inventory & Baseline Comparison: Collect firmware data and check against golden baseline. (50%).",
              "Tier 2 — Advisory Correlation & Risk Scoring: Correlate with vendor CVEs and prioritize critical updates. (75%).",
              "Tier 3 — AI-Assisted Upgrade Planning: Generate optimal upgrade sequence and risk narrative. (90%).",
              "Tier 4 — Autonomous Lifecycle Management: Execute, verify, and document firmware updates with adaptive learning. (100%)."
            ]
          }
        ]
      },
      {
        "title": "3. Operations",
        "subSections": [
          {
            "title": "Key Performance Indicators (KPI)",
            "content": [
              "Firmware Compliance Rate ≥ 95% — Devices meeting approved firmware baseline.",
              "Critical CVE Remediation SLA ≤ 15 days — Time to patch after critical advisory release.",
              "Mean Time to Detect (MTTD) < 24 hrs — Time from vendor advisory to detection of impacted device.",
              "Mean Time to Update (MTTU) ≤ 7 days — Time from detection to completed upgrade.",
              "Rollback Success Rate ≥ 99% — Verified rollback success in testing.",
              "False Positive Advisory Match Rate ≤ 2% — Incorrect CVE-device matches."
            ]
          },
          {
            "title": "Representative Statistics (30-Day Enterprise Run)",
            "content": [
              "Devices Monitored: 164 (core routers, access switches, WAN gateways)",
              "Unique Firmware Builds Detected: 27",
              "Golden Baseline Compliance: 93%",
              "Detected Outdated Builds: 11 devices (7%)",
              "Critical Vulnerabilities Matched (CVE Severity ≥ 9): 4",
              "Average MTTD: 10.5 hrs",
              "Average MTTU: 5.2 days",
              "Rollback Success Rate: 100% (tested on 2 devices)",
              "False Positive Advisory Matches: 1.6%",
              "Firmware Hygiene Score: 91.8 / 100",
              "Engineer Hours Saved: ~220 / month"
            ]
          },
          {
            "title": "Service Level Agreements (SLA)",
            "content": [
              "Firmware Inventory Scan ≤ 24 hrs — Daily scan of all devices.",
              "Advisory Correlation Cycle ≤ 2 hrs — Updates from vendor feeds.",
              "Upgrade Plan Generation ≤ 1 hr — Includes dependency analysis and ITSM linkage.",
              "Change Execution Window As approved — Auto-aligns with maintenance schedule.",
              "Evidence Publication ≤ 1 hr — Audit-ready report exported to GRC/Confluence.",
              "Agent Availability ≥ 99.9% — Monitored via Prometheus / Grafana."
            ]
          },
          {
            "title": "Technical Context",
            "content": [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “FirmAware” service.",
              "Primary APIs & Protocols — NETCONF, RESTCONF, SSH (CLI), SNMPv3, and vendor firmware repositories (SFTP/HTTPS).",
              "Reasoning Model — Azure OpenAI GPT-4o trained on NIST CVSS scoring, firmware dependency matrices, and vendor advisories.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing firmware inventory and golden baseline versions.",
              "Security Controls — Authenticated, signed firmware retrieval; encrypted credential storage; RBAC-isolated execution.",
              "Outputs — Firmware Compliance Report (PDF/JSON), Upgrade Execution Plan, CVE Correlation Matrix.",
              "Monitoring — Grafana dashboards: compliance trendline, MTTD/MTTU metrics, device risk index.",
              "Error Handling — Auto-retry failed downloads; rollback to last verified image on post-upgrade validation failure."
            ]
          }
        ]
      }
    ],
    "agenticModel": {
      "trigger": "Firmware Inventory Scan (Daily), Vendor Advisory Feed Update, or Scheduled Maintenance Window.",
      "workingPlane": "RunOps n8n automation cluster with containerized 'FirmAware' service.",
      "memory": "Stores firmware inventory, golden baselines, and historical upgrade success/failure patterns in Postgres/Git.",
      "reasoning": "Correlates running versions with CVE severity (AI Scoring) and ITSM maintenance schedules to generate a risk-prioritized, dependency-aware upgrade plan and sequencing.",
      "execution": "Automates download, staging, and execution of firmware updates, performs post-verification, and triggers automated rollback/recovery upon failure."
    },
    "kpis": [
      { "label": "Firmware Compliance Rate", "value": "95", "unit": "%", "target": "≥ 95%", "color": "#10b981" },
      { "label": "Critical CVE Remediation SLA", "value": "15", "unit": "days", "target": "≤ 15 days", "color": "#10b981" },
      { "label": "Mean Time to Detect (MTTD)", "value": "24", "unit": "hrs", "target": "< 24 hrs", "color": "#10b981" },
      { "label": "Mean Time to Update (MTTU)", "value": "7", "unit": "days", "target": "≤ 7 days", "color": "#f59e0b" },
      { "label": "Rollback Success Rate", "value": "99", "unit": "%", "target": "≥ 99%", "color": "#10b981" },
      { "label": "False Positive Advisory Match Rate", "value": "1.6", "unit": "%", "target": "≤ 2%", "color": "#10b981" }
    ]
},

'vlan-compliance-agent': {
    "title": "VLAN Compliance Agent",
    "description": "Continuously validates VLAN configurations, assignments, and mappings across the enterprise network to ensure compliance with segmentation, security, and governance standards. Detects misaligned VLANs, unauthorized extensions, and scope violations.",
    "details": [
      {
        "title": "Business Value & Outcomes",
        "points": [
          "Segmentation Integrity: Ensures VLANs stay confined to approved zones and trust boundaries.",
          "Compliance Assurance: Maps VLAN design and configurations to NIST, PCI, and ISO segmentation requirements.",
          "Reduced Attack Surface: Detects and flags unauthorized inter-VLAN extensions or trunk leaks.",
          "Operational Efficiency: Eliminates manual VLAN audits through automated, continuous validation.",
          "Audit Readiness: Produces verifiable reports proving adherence to security segmentation policy."
        ]
      }
    ],
    "sections": [
      {
        "title": "1. Business Context",
        "subSections": [
          {
            "title": "Problem Statement",
            "content": [
              "VLAN sprawl and mismanagement are among the most common root causes of segmentation failure, leading to lateral movement opportunities, misclassified traffic, and compliance violations.",
              "VLAN configurations drift across access, distribution, and core layers due to manual adds, mergers, or re-IP projects, resulting in VLANs extended into non-approved zones, duplicate VLAN IDs, or missing ACL enforcement.",
              "Traditional audits are static, relying on spreadsheets or CMDB exports, and can’t detect live, cross-domain segmentation violations.",
              "Enterprises require a continuous validation mechanism that verifies VLANs align with zero-trust segmentation policy, CMDB intent, and compliance baselines."
            ]
          },
          {
            "title": "Business Outcomes (Benefits)",
            "content": [
              "Segmentation Integrity — Ensures VLANs stay confined to approved zones and trust boundaries.",
              "Compliance Assurance — Maps VLAN design and configurations to NIST, PCI, and ISO segmentation requirements.",
              "Reduced Attack Surface — Detects and flags unauthorized inter-VLAN extensions or trunk leaks.",
              "Operational Efficiency — Eliminates manual VLAN audits through automated, continuous validation.",
              "Audit Readiness — Produces verifiable reports proving adherence to security segmentation policy."
            ]
          },
          {
            "title": "Use Case Alignment",
            "content": [
              "Zero-Trust Enforcement Validation: Confirms VLANs correspond to approved trust zones and firewall policy groups.",
              "Unauthorized VLAN Detection: Identifies VLANs not defined in CMDB or not mapped to any security zone.",
              "Scope & Propagation Control: Verifies VLANs are not trunked beyond approved distribution boundaries.",
              "VLAN Drift Monitoring: Detects configuration changes (VLAN ID reuse, missing ACLs, inconsistent tagging).",
              "Regulatory Alignment: Validates PCI-DSS segmentation, HIPAA isolation, and NIST CM-6 configuration control."
            ]
          },
          {
            "title": "Why Agentic Agents Are Uniquely Qualified",
            "content": [
              "Context-Aware Validation: Understands each VLAN’s intended purpose, assigned zone, and approved scope.",
              "Cross-Plane Correlation: Links VLAN configurations to firewall zones, routing tables, and CMDB ownership.",
              "AI Reasoning: Distinguishes intentional VLAN propagation (e.g., HA/replication) from misconfigurations or unauthorized extensions.",
              "Dynamic Baseline Learning: Learns legitimate VLAN change patterns and auto-tunes sensitivity thresholds.",
              "Compliance-Aware Mapping: Aligns VLAN topology with zero-trust segmentation and regulatory controls.",
              "Self-Healing Capability: Suggests remediation commands or automatically reassigns VLANs to correct zones in closed-loop mode."
            ]
          }
        ]
      },
      {
        "title": "2. Technical Functionality",
        "subSections": [
          {
            "title": "Scope of Capabilities",
            "content": [
              "VLAN Discovery & Inventory: Continuously collects VLAN IDs, names, VLAN-to-port mappings, and trunk definitions.",
              "Zone & Trust Alignment: Validates VLAN associations against firewall zone or logical trust definitions.",
              "Propagation Scope Validation: Detects VLANs extended beyond approved switches or zones.",
              "Unauthorized VLAN Detection: Flags unregistered or untagged VLANs not present in the CMDB.",
              "Policy Correlation: Maps VLANs to firewall policy groups, routing VRFs, and NAC/identity zones.",
              "Baseline Drift Detection: Compares live VLAN database to golden templates and previous topology snapshots.",
              "AI Reasoning Layer: Analyzes violations for intent — misconfiguration vs operational necessity.",
              "Remediation Workflow: Suggests configuration corrections, ACL additions, or VLAN shutdowns.",
              "Audit & Evidence Reporting: Exports compliance summaries to GRC systems with control mappings."
            ]
          },
          {
            "title": "Integrations",
            "content": [
              "Switch APIs / SNMP / Telemetry — Retrieve VLAN tables, port assignments, and trunk configurations (Inbound).",
              "Network Management System / SDN Controller — Maintain VLAN-to-zone intent baselines and topology data (Bidirectional).",
              "Firewall Policy Management (Panorama / NMS) — Validate VLAN-to-zone policy consistency (Bidirectional).",
              "CoreStack CMDB / GRC Modules — Cross-check VLAN definitions and compliance mappings (Bidirectional).",
              "ServiceNow / Jira — Create incidents or approvals for non-compliant VLANs (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Classify VLAN deviations and produce human-readable justification (Inbound).",
              "Confluence / Data Lake — Archive VLAN compliance reports and topology deltas (Outbound)."
            ]
          },
          {
            "title": "Agentic Tiers",
            "content": [
              "Tier 1 — VLAN Inventory & Mapping: Collect VLAN configuration data across network layers. (50%).",
              "Tier 2 — Baseline Comparison & Policy Validation: Compare live VLANs to golden templates, firewall zones, and CMDB. (75%).",
              "Tier 3 — AI Reasoning & Remediation Recommendation: Determine cause, intent, and correction of non-compliance. (90%).",
              "Tier 4 — Self-Learning Compliance Loop: Auto-adjust baselines based on validated change approvals. (100%)."
            ]
          }
        ]
      },
      {
        "title": "3. Operations",
        "subSections": [
          {
            "title": "Key Performance Indicators (KPI)",
            "content": [
              "VLAN Compliance Rate ≥ 95% — VLANs aligned with CMDB and policy baselines.",
              "Unauthorized VLAN Detection Accuracy ≥ 97% — Correct identification of rogue or drifted VLANs.",
              "False Positive Rate ≤ 3% — Legitimate VLANs incorrectly flagged.",
              "Mean Time to Detect (MTTD) < 5 min — Time from VLAN change to detection.",
              "Mean Time to Remediate (MTTR) < 8 hrs — Time to correct or approve detected violations.",
              "Segmentation Alignment Score ≥ 92% — VLAN trust and zone consistency per design model."
            ]
          },
          {
            "title": "Representative Statistics (30-Day Enterprise Run)",
            "content": [
              "Devices Monitored: 214 (access, distribution, core switches)",
              "Total VLANs Detected: 1,842",
              "Unauthorized VLANs: 64 (3.4%)",
              "Duplicate VLAN IDs: 19",
              "Extended VLANs Beyond Zone Scope: 31",
              "ACL / Policy Mismatch Events: 42",
              "Average MTTD: 3.8 minutes",
              "Average MTTR: 6.1 hours",
              "False Positive Rate: 2.5%",
              "Compliance Rate: 96.4%",
              "Zero-Trust Alignment Improvement: +13% month-over-month",
              "Engineer Hours Saved: ~240 per month"
            ]
          },
          {
            "title": "Service Level Agreements (SLA)",
            "content": [
              "VLAN Collection & Discovery Cycle ≤ 5 minutes — Triggered by SNMP traps or config commits.",
              "Baseline Comparison & Validation ≤ 3 minutes — Includes AI-based drift reasoning.",
              "Incident / Ticket Generation ≤ 10 minutes — Integration with ITSM systems.",
              "Evidence Report Publication ≤ 1 hour — Exports compliance evidence to Confluence / GRC.",
              "Agent Availability ≥ 99.9% — Clustered n8n execution monitored via Prometheus."
            ]
          },
          {
            "title": "Technical Context",
            "content": [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “VLANAudit” module.",
              "Primary Interfaces — SNMPv3, NETCONF, RESTCONF, LLDP, and SDN APIs for VLAN and port mapping.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on VLAN compliance, segmentation frameworks, and RunOps KB.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing VLAN baseline, topology, and trust-zone mapping.",
              "Security Controls — Read-only device access, encrypted credentials, TLS 1.3, RBAC segregation.",
              "Outputs — VLAN Compliance Report (PDF/JSON), Non-Compliance Summary, Zone Alignment Matrix, Audit Snapshot.",
              "Monitoring — Grafana dashboard tracking VLAN drift, segmentation score, and remediation rate.",
              "Error Handling — Auto-retry failed data pulls; rollback baseline sync if incomplete dataset detected."
            ]
          }
        ]
      }
    ],
    "agenticModel": {
      "trigger": "VLAN Configuration Change Event, Discovery Cycle (≤ 5 min), or CMDB Baseline Update.",
      "workingPlane": "RunOps n8n automation cluster with containerized 'VLANAudit' module.",
      "memory": "Stores VLAN configuration baseline, topology data, and firewall zone-to-VLAN mapping in Postgres.",
      "reasoning": "Correlates live VLAN inventory against CMDB/Firewall Zone intent to detect scope violations (unauthorized extensions/VLANs) and classifies deviations for compliance risk.",
      "execution": "Generates non-compliance incident ticket in ITSM, recommends configuration corrections (ACLs/VLAN shutdowns), and publishes compliance evidence to GRC."
    },
    "kpis": [
      { "label": "VLAN Compliance Rate", "value": "96.4", "unit": "%", "target": "≥ 95%", "color": "#10b981" },
      { "label": "Unauthorized VLAN Detection Accuracy", "value": "97", "unit": "%", "target": "≥ 97%", "color": "#10b981" },
      { "label": "False Positive Rate", "value": "2.5", "unit": "%", "target": "≤ 3%", "color": "#10b981" },
      { "label": "Mean Time to Detect (MTTD)", "value": "3.8", "unit": "min", "target": "< 5 min", "color": "#10b981" },
      { "label": "Mean Time to Remediate (MTTR)", "value": "6.1", "unit": "hrs", "target": "< 8 hrs", "color": "#10b981" },
      { "label": "Segmentation Alignment Score", "value": "92", "unit": "%", "target": "≥ 92%", "color": "#10b981" }
    ]
},

'routing-drift-analyzer-agent': {
    "title": "Routing Drift Analyzer Agent",
    "description": "Continuously analyzes routing tables, route advertisements, and forwarding behavior across the enterprise to detect leaks, asymmetric paths, stale routes, and prefix misadvertisements — ensuring network traffic follows approved design baselines and zero-trust routing policies.",
    "details": [
      {
        "title": "Business Value & Outcomes",
        "points": [
          "Routing Integrity: Detects deviations from approved route policies and prefix lists in real time.",
          "Reduced MTTR: Accelerates root-cause analysis for routing issues such as leaks or asymmetric paths.",
          "Performance Optimization: Identifies inefficient or misdirected routing paths impacting latency or cost.",
          "Zero-Trust Enforcement: Validates inter-zone routing paths and segmentation boundaries remain intact.",
          "Compliance & Audit Assurance: Produces verifiable reports mapping routing integrity to compliance standards."
        ]
      }
    ],
    "sections": [
      {
        "title": "1. Business Context",
        "subSections": [
          {
            "title": "Problem Statement",
            "content": [
              "Complex enterprises operate hundreds of routers and edge firewalls participating in dynamic routing protocols (BGP, OSPF, EIGRP, static redistribution).",
              "Over time, routing drift occurs due to misconfigurations, incomplete policy updates, and route redistribution loops — creating path asymmetry, leaks, and policy violations.",
              "These deviations degrade performance, create segmentation exposure, and complicate troubleshooting.",
              "Manual route inspection and diffing are reactive and unsustainable at scale.",
              "Organizations require real-time, agentic visibility into routing integrity to ensure that all prefixes and next-hops adhere to golden baselines and compliance frameworks (NIST SC-7, ISO A.13, PCI-DSS 1.3)."
            ]
          },
          {
            "title": "Business Outcomes (Benefits)",
            "content": [
              "Routing Integrity — Detects deviations from approved route policies and prefix lists in real time.",
              "Reduced MTTR — Accelerates root-cause analysis for routing issues such as leaks or asymmetric paths.",
              "Performance Optimization — Identifies inefficient or misdirected routing paths impacting latency or cost.",
              "Zero-Trust Enforcement — Validates inter-zone routing paths and segmentation boundaries remain intact.",
              "Compliance & Audit Assurance — Produces verifiable reports mapping routing integrity to compliance standards."
            ]
          },
          {
            "title": "Use Case Alignment",
            "content": [
              "Route Leak Detection: Identify prefixes advertised to unauthorized peers or areas.",
              "Asymmetric Path Analysis: Detect traffic paths differing between inbound and outbound flows.",
              "Policy Drift Monitoring: Validate that route maps, prefix lists, and redistribution rules match golden baselines.",
              "Prefix Hygiene Validation: Detect stale, duplicate, or incorrectly summarized prefixes.",
              "Zone & Segmentation Verification: Confirm routes align with security zones and trust boundaries."
            ]
          },
          {
            "title": "Why Agentic Agents Are Uniquely Qualified",
            "content": [
              "Contextual Awareness: Interprets routing deltas in the context of design baselines, segmentation policies, and route-map logic.",
              "Multi-Layer Correlation: Links data from routing tables, traffic telemetry, and ITSM change logs for holistic evaluation.",
              "AI Reasoning: Distinguishes authorized redistribution (e.g., post-maintenance) from true route leaks or rogue advertisements.",
              "Predictive Learning: Learns historical drift patterns to predict recurring instability or future misadvertisements.",
              "Cross-Plane Integration: Connects routing anomalies with potential firewall policy misalignment for full-path validation."
            ]
          }
        ]
      },
      {
        "title": "2. Technical Functionality",
        "subSections": [
          {
            "title": "Scope of Capabilities",
            "content": [
              "Routing Table Comparison: Continuously captures and compares routing tables against golden design baselines.",
              "Prefix & Next-Hop Validation: Verifies prefix ownership, advertised length, and next-hop consistency across peers.",
              "Leak & Loop Detection: Detects unexpected route redistribution or prefix injection into unauthorized areas or neighbors.",
              "Asymmetric Path Analysis: Correlates inbound/outbound traffic paths via NetFlow, IPFIX, or telemetry to identify divergence.",
              "Policy Drift Detection: Monitors route-maps, prefix lists, and policy-based routing (PBR) for unauthorized edits.",
              "AI Reasoning & Root Cause Analysis: Determines intent behind detected drift and classifies severity.",
              "Remediation Recommendations: Suggests prefix filter adjustments, route-map corrections, or redistribution fixes.",
              "Audit Reporting: Exports compliance-ready route integrity reports mapped to regulatory controls."
            ]
          },
          {
            "title": "Integrations",
            "content": [
              "Router / Edge Gateway APIs / Telemetry — Retrieve routing tables, prefix lists, and policy definitions (Inbound).",
              "Network Management / SDN / Panorama Systems — Validate advertised vs accepted routes across managed domains (Bidirectional).",
              "CoreStack GRC / Compliance Modules — Store route compliance metrics and evidence (Bidirectional).",
              "Cortex Data Lake / NetFlow / IPFIX — Provide real traffic flow data for path asymmetry analysis (Inbound).",
              "ServiceNow / Jira — Link route deviations to maintenance or change records (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Analyze route anomalies, determine cause, and generate remediation narratives (Inbound).",
              "Confluence / Data Lake — Archive routing reports and compliance snapshots (Outbound)."
            ]
          },
          {
            "title": "Agentic Tiers",
            "content": [
              "Tier 1 — Route Snapshot & Comparison: Capture and compare routing tables to baselines. (50%).",
              "Tier 2 — Anomaly Detection & Correlation: Detect leaks, asymmetries, or misadvertisements and cross-reference to topology. (75%).",
              "Tier 3 — AI Reasoning & Remediation Recommendation: Contextual analysis of drift cause and impact, with corrective actions. (90%).",
              "Tier 4 — Predictive Learning & Self-Optimization: Forecast recurring routing issues and auto-tune detection models. (100%)."
            ]
          }
        ]
      },
      {
        "title": "3. Operations",
        "subSections": [
          {
            "title": "Key Performance Indicators (KPI)",
            "content": [
              "Routing Drift Detection Accuracy ≥ 96% — Correctly identified route anomalies vs validated results.",
              "Mean Time to Detect (MTTD) < 5 min — Time from route change event to anomaly identification.",
              "Mean Time to Remediate (MTTR) < 8 hrs — Time from detection to configuration correction or rollback.",
              "False Positive Rate ≤ 3% — Benign routing changes incorrectly flagged.",
              "Policy Drift Resolution Rate ≥ 90% — Percentage of drift incidents resolved within SLA.",
              "Prefix Hygiene Score ≥ 95% — % of clean, authorized prefixes per routing domain."
            ]
          },
          {
            "title": "Representative Statistics (30-Day Enterprise Run)",
            "content": [
              "Routers / Edge Devices Monitored: 98",
              "Routing Protocols Observed: BGP, OSPF, Static Redistribution",
              "Total Prefixes Evaluated: 142,000",
              "Detected Anomalies: 186",
              "Average MTTD: 3.9 min",
              "Average MTTR: 7.2 hrs",
              "False Positive Rate: 2.7%",
              "Compliance Alignment Score: 94.6%",
              "Routing Hygiene Score: 92.8 / 100",
              "Engineer Hours Saved: ~260 per month"
            ]
          },
          {
            "title": "Service Level Agreements (SLA)",
            "content": [
              "Routing Snapshot Frequency ≤ 5 min — Streaming telemetry + scheduled diffs.",
              "Drift Detection Cycle ≤ 2 min — Diff analysis and correlation per domain.",
              "Incident Ticket Generation ≤ 10 min — Automatic ITSM escalation with anomaly summary.",
              "Compliance Report Publication ≤ 1 hr — Audit-ready evidence for GRC systems.",
              "Agent Availability ≥ 99.9% — Monitored via Prometheus / Grafana."
            ]
          },
          {
            "title": "Technical Context",
            "content": [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “RouteIQ” module.",
              "Primary Interfaces — NETCONF, RESTCONF, SNMPv3, gNMI, and telemetry APIs from routers and firewalls.",
              "Reasoning Model — Azure OpenAI GPT-4o trained on routing policy datasets, BGP/OSPF drift signatures, and RunOps network KB.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing golden routing tables and topology adjacency data.",
              "Security Controls — Read-only API access; TLS 1.3 encryption; credential vaulting; RBAC per device group.",
              "Outputs — Routing Drift Report (JSON/PDF), Anomaly Log, Remediation Recommendation Summary, Compliance Evidence Package.",
              "Monitoring — Grafana dashboards for drift count, route leak trend, prefix hygiene score, SLA compliance.",
              "Error Handling — Auto-retry telemetry pull failures; alert NOC after two failed collection cycles."
            ]
          }
        ]
      }
    ],
    "agenticModel": {
      "trigger": "Routing Snapshot Frequency (≤ 5 min), Route Advertisement Change, or Telemetry Anomaly.",
      "workingPlane": "RunOps n8n automation cluster with containerized 'RouteIQ' module.",
      "memory": "Stores golden routing tables, route-map baselines, and historical drift patterns in Postgres.",
      "reasoning": "Performs multi-layer correlation (routing table, policy, traffic flow) to distinguish authorized changes from route leaks, path asymmetry, or policy violations, determining severity and remediation path.",
      "execution": "Generates ITSM incident ticket with root cause analysis, provides remediation recommendations (prefix-filter/route-map adjustments), and publishes compliance evidence."
    },
    "kpis": [
      { "label": "Routing Drift Detection Accuracy", "value": "96", "unit": "%", "target": "≥ 96%", "color": "#10b981" },
      { "label": "Mean Time to Detect (MTTD)", "value": "3.9", "unit": "min", "target": "< 5 min", "color": "#10b981" },
      { "label": "Mean Time to Remediate (MTTR)", "value": "7.2", "unit": "hrs", "target": "< 8 hrs", "color": "#10b981" },
      { "label": "False Positive Rate", "value": "2.7", "unit": "%", "target": "≤ 3%", "color": "#10b981" },
      { "label": "Policy Drift Resolution Rate", "value": "90", "unit": "%", "target": "≥ 90%", "color": "#10b981" },
      { "label": "Prefix Hygiene Score", "value": "92.8", "unit": "/ 100", "target": "≥ 95%", "color": "#f59e0b" }
    ]
},

'license-usage-agent': {
    "title": "License Usage Agent",
    "description": "Continuously monitors network device license utilization, feature activation, and expiration status to maintain operational continuity, prevent service disruption, and ensure compliance with vendor and enterprise licensing policies.",
    "details": [
      {
        "title": "Business Value & Outcomes",
        "points": [
          "License Continuity Assurance: Prevents service disruption by forecasting expirations and triggering proactive renewals.",
          "Financial Governance: Tracks license utilization vs entitlement to reduce waste and optimize renewals.",
          "Operational Efficiency: Automates renewal workflows and integrates with procurement and ITSM systems.",
          "Compliance Validation: Ensures devices remain within licensed terms and auditable against enterprise policies.",
          "Lifecycle Optimization: Enables predictive capacity planning for feature growth and licensing scalability."
        ]
      }
    ],
    "sections": [
      {
        "title": "1. Business Context",
        "subSections": [
          {
            "title": "Problem Statement",
            "content": [
              "Modern enterprise networks rely on dozens of feature-based and subscription-based licenses across routing, switching, and security platforms.",
              "License sprawl leads to visibility gaps, unplanned service interruptions, and compliance exposure.",
              "Manual license tracking in spreadsheets or vendor portals is time-consuming, prone to human error, and unscalable across hundreds of devices.",
              "Missing or expired licenses can disable critical capabilities (routing protocols, high availability, encryption, etc.), resulting in downtime or regulatory non-compliance.",
              "Organizations need a centralized, automated license intelligence system that unifies visibility, ensures renewals, and aligns with financial and operational governance."
            ]
          },
          {
            "title": "Business Outcomes (Benefits)",
            "content": [
              "License Continuity Assurance — Prevents service disruption by forecasting expirations and triggering proactive renewals.",
              "Financial Governance — Tracks license utilization vs entitlement to reduce waste and optimize renewals.",
              "Operational Efficiency — Automates renewal workflows and integrates with procurement and ITSM systems.",
              "Compliance Validation — Ensures devices remain within licensed terms and auditable against enterprise policies.",
              "Lifecycle Optimization — Enables predictive capacity planning for feature growth and licensing scalability."
            ]
          },
          {
            "title": "Use Case Alignment",
            "content": [
              "Subscription & Entitlement Monitoring: Track active, expired, and soon-to-expire licenses across network devices.",
              "Capacity & Throughput Usage Analysis: Monitor license utilization vs provisioned capacity (bandwidth, ports, sessions).",
              "Renewal Automation: Generate and route renewal requests through ITSM and procurement systems automatically.",
              "Feature Compliance Validation: Verify only licensed features are active on devices.",
              "License Audit Reporting: Produce detailed compliance and usage reports mapped to vendor and regulatory requirements."
            ]
          },
          {
            "title": "Why Agentic Agents Are Uniquely Qualified",
            "content": [
              "Continuous Awareness: Ingests license, entitlement, and consumption data from network APIs and vendor portals.",
              "Predictive Reasoning: Uses AI to forecast expirations, renewal costs, and feature expansion needs.",
              "Compliance Context: Cross-checks license state against procurement records and corporate usage policy.",
              "Self-Healing Governance: Automatically triggers renewals, deactivations, or alerts based on confidence thresholds.",
              "Learning Feedback: Improves accuracy by learning historical renewal cycles, vendor response times, and utilization patterns."
            ]
          }
        ]
      },
      {
        "title": "2. Technical Functionality",
        "subSections": [
          {
            "title": "Scope of Capabilities",
            "content": [
              "License Inventory Discovery: Collects license keys, feature entitlements, expiration dates, and usage metrics across routers, switches, and gateways.",
              "Utilization Monitoring: Tracks consumption vs capacity thresholds for bandwidth, sessions, and feature counts.",
              "Expiration Forecasting: Predicts renewal dates and usage saturation using time-series modeling.",
              "Entitlement Validation: Compares live device state with vendor license portal or enterprise entitlement repository.",
              "Compliance Verification: Flags unlicensed feature use, expired entitlements, or mismatched serials.",
              "AI Renewal Recommendation Engine: Suggests renewal priorities based on device criticality, utilization, and business impact.",
              "Procurement Workflow Automation: Opens ServiceNow or procurement tickets automatically for upcoming renewals.",
              "Audit Reporting: Produces detailed evidence of license status, renewals, and compliance mapping."
            ]
          },
          {
            "title": "Integrations",
            "content": [
              "Network Device APIs / CLI — Retrieve license and feature utilization data (Inbound).",
              "Vendor License Portals / Entitlement APIs — Verify entitlement, expiration, and feature set (Inbound).",
              "CoreStack GRC / Compliance Modules — Map license compliance to enterprise controls and generate audit evidence (Bidirectional).",
              "ServiceNow / Jira — Create or update renewal and procurement tickets automatically (Bidirectional).",
              "Procurement / ERP Systems (SAP, Coupa) — Sync financial and entitlement data for renewals (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Forecast consumption, prioritize renewals, and summarize compliance insights (Inbound).",
              "Confluence / Data Lake — Archive license reports and renewal history (Outbound)."
            ]
          },
          {
            "title": "Agentic Tiers",
            "content": [
              "Tier 1 — License Discovery & Inventory: Collect license metadata and current state from devices. (50%).",
              "Tier 2 — Expiration & Utilization Tracking: Correlate usage metrics with expiry timelines and entitlement limits. (75%).",
              "Tier 3 — AI Forecasting & Renewal Recommendation: Predict future needs, rank renewal urgency, and assess risk. (90%).",
              "Tier 4 — Autonomous Renewal & Compliance Loop: Automatically trigger renewals and update baselines after completion. (100%)."
            ]
          }
        ]
      },
      {
        "title": "3. Operations",
        "subSections": [
          {
            "title": "Key Performance Indicators (KPI)",
            "content": [
              "License Compliance Rate ≥ 98% — Devices operating within valid license terms.",
              "Renewal Forecast Accuracy ≥ 95% — Accuracy of predicted expiration/renewal events.",
              "Mean Time to Detect (MTTD) < 1 hr — Time from license event to detection.",
              "Mean Time to Renew (MTTR) < 5 days — Time from alert to renewal approval and validation.",
              "Automation Adoption ≥ 80% — Renewals executed without manual intervention.",
              "False Positive Rate (Non-Issues) ≤ 3% — Incorrectly flagged license alerts."
            ]
          },
          {
            "title": "Representative Statistics (30-Day Enterprise Run)",
            "content": [
              "Devices Monitored: 186 (core routers, distribution switches, edge firewalls)",
              "Licenses Tracked: 1,423",
              "Active Licenses: 1,372",
              "Expiring Within 30 Days: 51 (3.6%)",
              "Expired / Non-Compliance Events: 7",
              "Auto-Renewed Licenses: 29",
              "Renewals Pending Approval: 22",
              "Average Renewal Forecast Accuracy: 96.2%",
              "Average MTTD: 32 minutes",
              "Average MTTR (Renewal): 3.8 days",
              "False Positives: 2.4%",
              "License Compliance Rate: 98.7%",
              "Audit Reports Generated: 14",
              "Engineer Hours Saved: ~200 per month"
            ]
          },
          {
            "title": "Service Level Agreements (SLA)",
            "content": [
              "License Polling Interval ≤ 4 hrs — Scheduled inventory collection from all devices.",
              "Advisory & Expiration Correlation ≤ 1 hr — Vendor API sync frequency.",
              "Renewal Ticket Creation ≤ 2 hrs — Automatic ITSM integration post-detection.",
              "Procurement Confirmation ≤ 3 days — Time from ticket to purchase order issuance.",
              "Evidence Publication ≤ 1 hr — Audit report export to Confluence/CoreStack.",
              "Agent Availability ≥ 99.9% — High-availability orchestration monitored by Grafana."
            ]
          },
          {
            "title": "Technical Context",
            "content": [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “LicenSync” microservice.",
              "Primary APIs & Protocols — NETCONF/RESTCONF, SSH CLI, SNMPv3, Vendor Entitlement APIs (JSON/REST).",
              "Reasoning Model — Azure OpenAI GPT-4o trained on entitlement structures, vendor renewal cycles, and RunOps governance KB.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing license metadata, expiration dates, and consumption trends.",
              "Security Controls — Read-only device access, encrypted credentials, TLS 1.3, RBAC isolation.",
              "Outputs — License Compliance Report (JSON/PDF), Renewal Forecast Summary, Procurement Ticket Log, GRC Evidence Bundle.",
              "Monitoring — Grafana dashboards tracking expiry timeline, renewal success, and compliance rate.",
              "Error Handling — Auto-retry on failed API calls; fallback to cached entitlement dataset if vendor API unreachable."
            ]
          }
        ]
      }
    ],
    "agenticModel": {
      "trigger": "License Polling Interval (≤ 4 hrs), Expiration Forecast Breach, or Utilization Threshold Violation.",
      "workingPlane": "RunOps n8n automation cluster with containerized 'LicenSync' microservice.",
      "memory": "Stores license metadata, expiration dates, and consumption trends to model predictive usage and renewal cycles.",
      "reasoning": "Correlates device state and utilization against vendor entitlements/expiration forecasts to prioritize renewals (risk, impact) and trigger autonomous procurement workflows.",
      "execution": "Generates renewal requests in ITSM/Procurement systems, automatically updates license status post-renewal, and produces audit-ready compliance evidence."
    },
    "kpis": [
      { "label": "License Compliance Rate", "value": "98.7", "unit": "%", "target": "≥ 98%", "color": "#10b981" },
      { "label": "Renewal Forecast Accuracy", "value": "96.2", "unit": "%", "target": "≥ 95%", "color": "#10b981" },
      { "label": "Mean Time to Detect (MTTD)", "value": "32", "unit": "min", "target": "< 1 hr", "color": "#10b981" },
      { "label": "Mean Time to Renew (MTTR)", "value": "3.8", "unit": "days", "target": "< 5 days", "color": "#10b981" },
      { "label": "Automation Adoption", "value": "80", "unit": "%", "target": "≥ 80%", "color": "#10b981" },
      { "label": "False Positive Rate", "value": "2.4", "unit": "%", "target": "≤ 3%", "color": "#10b981" }
    ]
},
  'path-selection-agent': {
    title: "Path Selection Agent",
    description: "Continuously evaluates WAN transport links (MPLS, Internet broadband, LTE/5G, satellite, and SASE overlays) using live telemetry, application context, and SLA objectives to dynamically select the best path per session or policy. Ensures the highest possible application performance, link utilization efficiency, and cost optimization across all SD-WAN domains.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Performance Optimization: Dynamically routes traffic across the best-performing transport per flow or app class.",
          "Cost Efficiency: Steers non-critical or bulk traffic over lower-cost links without impacting business SLAs.",
          "High Availability: Maintains continuous service by re-routing around congestion or link degradation.",
          "Zero-Trust Compliance: Enforces transport-level segmentation and encryption per zone and policy intent.",
          "Operational Efficiency: Reduces manual path policy tuning and provides auditable evidence of optimization decisions."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprises increasingly depend on multiple WAN transports with differing characteristics — MPLS for guaranteed SLAs, broadband for cost savings, LTE/5G for agility.",
              "Static path selection or manual SD-WAN policy tuning fails to adapt to real-time link performance degradation, cost shifts, or SLA breaches.",
              "Without automation, suboptimal routing increases latency, wastes expensive circuits, and degrades user experience.",
              "The enterprise needs a self-adapting decision engine that continuously evaluates all available transports and autonomously shifts traffic to the best performing, policy-compliant path."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Performance Optimization — Dynamically routes traffic across the best-performing transport per flow or app class.",
              "Cost Efficiency — Steers non-critical or bulk traffic over lower-cost links without impacting business SLAs.",
              "High Availability — Maintains continuous service by re-routing around congestion or link degradation.",
              "Zero-Trust Compliance — Enforces transport-level segmentation and encryption per zone and policy intent.",
              "Operational Efficiency — Reduces manual path policy tuning and provides auditable evidence of optimization decisions."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Dynamic Multi-Transport Selection — Continuously select between MPLS, broadband, and LTE/5G based on real-time telemetry.",
              "Latency-Aware Application Steering — Assign mission-critical apps (voice, ERP, EMR) to lowest-latency paths automatically.",
              "SLA Enforcement — Trigger path changes when jitter, latency, or loss exceed defined thresholds.",
              "Cost-Based Policy Routing — Shift discretionary traffic (updates, syncs, backups) to low-cost links during peak congestion.",
              "Failover & Degradation Handling — Automatically reroute around degraded circuits while preserving security segmentation."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Traditional SD-WAN controllers perform threshold-based path switching but lack contextual reasoning or forward prediction.",
              "Multi-Input Awareness: Correlates real-time telemetry, link utilization, and historical performance trends.",
              "Adaptive Reasoning: Evaluates SLA compliance, cost models, and security constraints dynamically before each reroute.",
              "Predictive Learning: Forecasts link degradation using AI modeling of jitter, packet loss, and latency trends.",
              "Autonomous Decisioning: Executes path reallocation automatically, with rollback if post-change validation fails.",
              "Cross-Domain Integration: Aligns routing adjustments with QoS, SD-WAN, and security policy frameworks."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Real-Time Link Telemetry Analysis: Collect latency, jitter, packet loss, and throughput data for all WAN transports.",
              "Dynamic SLA Correlation: Evaluate link health against defined SLAs per application or class of service.",
              "Cost & Policy Weighting Engine: Integrate link cost and policy rules into decision criteria (cost/performance balance).",
              "AI-Driven Prediction Models: Forecast potential link degradation before SLA breach occurs.",
              "Per-Session Path Optimization: Apply path selection decisions per flow, per app, or per zone dynamically.",
              "Failover & Validation Workflows: Verify post-switch performance and rollback automatically if degradation persists.",
              "Zero-Trust Enforcement Check: Ensure path decisions maintain segmentation and encryption boundaries.",
              "Reporting & Evidence: Generate optimization logs and compliance evidence for audit and performance review."
            ]
          },
          {
            title: "Integrations",
            content: [
              "SD-WAN Controller APIs / Edge Devices — Retrieve link telemetry and push updated path policies (Bidirectional).",
              "Telemetry Sources (gNMI, NetFlow/IPFIX, SNMP, Ping, Synthetic Tests) — Collect real-time performance metrics (Inbound).",
              "CoreStack GRC / SLA Modules — Record SLA compliance, decision evidence, and optimization history (Bidirectional).",
              "ServiceNow / Jira — Create and update change records or alerts for path changes (Bidirectional).",
              "Cortex Data Lake / Analytics Engine — Provide flow-level visibility and long-term performance trends (Inbound).",
              "Azure OpenAI / RunOps Reasoning Engine — Perform decision reasoning, cost-SLA optimization modeling, and narrative summarization (Inbound).",
              "Confluence / Data Lake — Archive optimization reports, compliance data, and audit records (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Link Monitoring: Collect and baseline performance metrics from all WAN transports. (50%).",
              "Tier 2 — SLA Correlation & Cost Modeling: Evaluate path health vs SLA metrics and cost baselines. (75%).",
              "Tier 3 — AI Optimization & Decision Reasoning: Choose best path using multi-factor logic and execute policy update. (90%).",
              "Tier 4 — Predictive Learning & Autonomous Governance: Anticipate degradation and preemptively optimize routing decisions. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Path Selection Accuracy ≥ 97% — Correct link choice per application class validated by post-switch metrics.",
              "Mean Time to Detect (MTTD) < 2 min — Time from performance degradation to detection.",
              "Mean Time to Adjust (MTTA) < 5 min — Time from detection to new path enforcement.",
              "False Switch Rate ≤ 3% — Path switches without measurable performance gain.",
              "SLA Compliance Rate ≥ 98% — Percentage of monitored sessions meeting SLA targets.",
              "Bandwidth Cost Optimization ≥ 10% — Traffic shifted from premium to lower-cost circuits without SLA impact."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Branches Monitored: 142",
              "WAN Links Evaluated: 468 (MPLS, Broadband, LTE/5G)",
              "Dynamic Path Changes Executed: 1,028",
              "SLA Breaches Detected: 213",
              "Auto-Resolved via Reroute: 201 (94.3%)",
              "Average Latency Reduction: 22.8% post-optimization",
              "Average Cost Savings: 11.6% (reduction in premium link usage)",
              "False Switch Rate: 2.1%",
              "MTTD: 1.7 min — MTTA: 4.3 min",
              "SLA Compliance Rate: 98.4%",
              "Engineer Hours Saved: ~230 per month",
              "Audit Reports Generated: 9 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Telemetry Refresh Interval ≤ 60 s — Real-time streaming or polling updates.",
              "SLA Evaluation Cycle ≤ 2 min — Performance threshold check for all links.",
              "Decision & Policy Commit ≤ 5 min — Update to controller and branch edges.",
              "Failover Validation ≤ 2 min — Automated test traffic verification.",
              "Evidence Publication ≤ 1 hr — SLA report export to CoreStack/Confluence.",
              "Agent Availability ≥ 99.9% — Monitored via Prometheus/Grafana."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “PathOpt” microservice.",
              "Primary Interfaces — SD-WAN Controller REST APIs, gNMI/gRPC telemetry, NetFlow/IPFIX.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing path metrics, SLA thresholds, and cost models.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on WAN telemetry datasets, SLA economics, and RunOps network KB.",
              "Security Controls — API token auth, encrypted credentials, TLS 1.3, RBAC per SD-WAN domain.",
              "Outputs — Path Optimization Report (JSON/PDF), SLA Compliance Snapshot, Cost Savings Summary, Change Log.",
              "Monitoring — Grafana dashboards visualizing path health, decision latency, and cost-performance index.",
              "Error Handling — Auto-retry policy push failures; revert to last stable path profile if degradation persists post-change."
            ]
          }
        ]
      },
      {
        title: "Summary",
        subSections: [
          {
            title: "Summary",
            content: [
              "The Path Selection Agent is the intelligent decision core of the SD-WAN fabric, continuously evaluating transport performance, cost, and risk to ensure optimal routing at all times.",
              "By combining telemetry-driven insights, AI reasoning, and autonomous policy enforcement, it ensures every application traverses the best available path while maintaining SLA compliance and zero-trust segmentation.",
              "The result is a self-optimizing WAN ecosystem that delivers peak performance, cost control, and continuous operational assurance — without manual tuning or configuration overhead."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Telemetry refresh, SLA breach detection, or scheduled optimization cycle.",
      workingPlane: "RunOps n8n automation cluster with containerized 'PathOpt' microservice.",
      memory: "Persisted path metrics, SLA thresholds, and cost models in Postgres.",
      reasoning: "Azure OpenAI driven models for prediction and multi-criteria cost/SLA tradeoff reasoning.",
      execution: "Generate path optimization decisions, commit via SD-WAN controller APIs with canary validation and rollback if needed; emit audit evidence."
    },
    kpis: [
      { label: "Path Selection Accuracy", value: "97.0", unit: "%", target: "≥ 97%", color: "#10b981" },
      { label: "MTTD", value: "1.7", unit: "min", target: "< 2 min", color: "#10b981" },
      { label: "MTTA", value: "4.3", unit: "min", target: "< 5 min", color: "#10b981" },
      { label: "False Switch Rate", value: "2.1", unit: "%", target: "≤ 3%", color: "#10b981" },
      { label: "SLA Compliance Rate", value: "98.4", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Cost Optimization", value: "11.6", unit: "%", target: "≥ 10%", color: "#10b981" }
    ]
  },
  'wan-optimization-agent': {
    title: "WAN Optimization Agent",
    description: "Dynamically applies traffic optimization techniques — compression, caching, deduplication, and protocol acceleration — across WAN links to improve throughput, reduce bandwidth consumption, and enhance application performance without manual intervention.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Bandwidth Efficiency — Reduces WAN utilization through adaptive compression, caching, and data deduplication.",
          "Performance Acceleration — Enhances application throughput and reduces latency over high-delay or lossy links.",
          "Cost Savings — Delays or eliminates need for bandwidth upgrades through optimization.",
          "Adaptive Optimization — Adjusts optimization policies automatically based on observed traffic mix and link health.",
          "Audit & Evidence Readiness — Produces verifiable reports of WAN optimization savings and performance metrics."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprise WAN links carry a mix of critical and redundant traffic, often leading to congestion, packet loss, and unnecessary data repetition.",
              "Traditional WAN optimization appliances rely on static configurations, blind to changing traffic patterns, application behaviors, or real-time network conditions.",
              "As traffic shifts toward SaaS, cloud, and encrypted flows, manual tuning of optimization parameters becomes ineffective.",
              "Enterprises require an agentic, self-learning optimization layer that automatically adjusts WAN optimization functions based on telemetry, usage trends, and application intent — ensuring continuous efficiency and SLA alignment."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Bandwidth Efficiency — Reduces WAN utilization through adaptive compression, caching, and data deduplication.",
              "Performance Acceleration — Enhances application throughput and reduces latency over high-delay or lossy links.",
              "Cost Savings — Delays or eliminates need for bandwidth upgrades through optimization.",
              "Adaptive Optimization — Adjusts optimization policies automatically based on observed traffic mix and link health.",
              "Audit & Evidence Readiness — Produces verifiable reports of WAN optimization savings and performance metrics."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Dynamic Compression Tuning — Automatically adjusts compression algorithms based on file types, packet sizes, and link quality.",
              "Intelligent Caching — Stores frequently accessed data at branch edges to eliminate redundant WAN transfers.",
              "Deduplication Enforcement — Detects and removes duplicate data blocks across repeated transactions or backups.",
              "Protocol Optimization — Accelerates TCP, SMB, and CIFS sessions by tuning window size, ACK timing, and congestion control dynamically.",
              "Encrypted Traffic Adaptation — Works in conjunction with SSL Inspection Agent to optimize post-decryption flows securely."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Traditional WAN optimizers act mechanically — compressing or caching without context.",
              "Real-Time Awareness — Monitors WAN link utilization, flow patterns, and RTTs across all branches.",
              "AI Reasoning — Determines which optimization techniques to apply based on traffic composition, encryption, and priority.",
              "Cross-Plane Correlation — Aligns WAN optimization with QoS, SD-WAN path selection, and zero-trust segmentation policies.",
              "Self-Learning Adaptation — Learns recurring traffic patterns (backups, sync cycles, replication windows) and pre-stages optimization.",
              "Feedback Governance — Continuously validates optimization outcomes, refining future tuning for maximum savings."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Traffic Profiling — Identify flow types, protocols, and application classes per site and transport.",
              "Adaptive Compression Engine — Apply dictionary-based or predictive compression based on content entropy.",
              "Edge Caching & Object Deduplication — Store and reuse recurring payloads across sites or sessions.",
              "Protocol Optimization — Adjust TCP/UDP parameters to reduce round trips and improve throughput.",
              "Dynamic Policy Selection — Combine optimization techniques depending on link conditions and QoS class.",
              "AI Reasoning Layer — Analyze savings trends, detect diminishing returns, and reprioritize optimization focus.",
              "Feedback & Validation — Measure achieved bandwidth reduction and latency improvement post-policy.",
              "Reporting & Compliance Evidence — Export savings metrics, SLA impact, and optimization efficiency KPIs."
            ]
          },
          {
            title: "Integrations",
            content: [
              "WAN Edge Devices / Controllers — Apply and monitor optimization policies (compression, caching, deduplication) (Bidirectional).",
              "Telemetry Systems (gNMI, NetFlow/IPFIX, SNMP) — Collect link utilization, latency, and throughput metrics (Inbound).",
              "SD-WAN Policy Agent / Path Selection Agent — Coordinate optimization decisions with routing and path logic (Bidirectional).",
              "CoreStack GRC / SLA Modules — Store audit evidence and efficiency reports (Bidirectional).",
              "ServiceNow / Jira — Manage change and optimization exception workflows (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Evaluate performance data, choose optimal techniques, and produce remediation insights (Inbound).",
              "Confluence / Data Lake — Archive savings reports and historical optimization data (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Traffic & Utilization Monitoring: Gather link statistics, application mix, and throughput patterns. (50%).",
              "Tier 2 — Optimization Policy Application: Apply caching, compression, and deduplication based on heuristics. (75%).",
              "Tier 3 — AI Reasoning & Dynamic Tuning: Adjust optimization functions and parameters in real time. (90%).",
              "Tier 4 — Predictive Self-Optimization: Learn recurring patterns to pre-apply optimization proactively. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Average Bandwidth Savings ≥ 25% — Reduction in total WAN utilization after optimization.",
              "Application Acceleration Rate ≥ 15% — Average improvement in throughput or load time for optimized apps.",
              "Compression Efficiency ≥ 85% — Effective compression ratio for compressible traffic types.",
              "Cache Hit Ratio ≥ 70% — Percentage of requests served from local cache.",
              "Deduplication Reduction ≥ 40% — Duplicate data eliminated per transfer volume.",
              "Mean Time to Detect (MTTD) Degradation < 2 min — Time from performance drop to optimization trigger."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Sites Monitored: 132 (branches, HQ, DCs)",
              "Average WAN Bandwidth Savings: 27.6%",
              "Application Performance Improvement: 18.9% (avg throughput gain)",
              "Cache Hit Ratio: 72.3%",
              "Deduplication Savings: 42.5% of repetitive data eliminated",
              "Compression Efficiency: 86.1%",
              "MTTD: 1.4 min — Average Optimization Response: 3.9 min",
              "False Adjustment Rate: 2.1%",
              "Total Data Transferred (Post-Optimization): 14.2 TB saved",
              "Bandwidth Cost Reduction: ≈ 13% (monthly)",
              "SLA Compliance: 99.1% maintained across critical apps",
              "Engineer Hours Saved: ~210 per month",
              "Audit Reports Generated: 10 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Telemetry Collection Interval ≤ 60 s — Real-time data feed via streaming telemetry.",
              "Optimization Trigger Reaction ≤ 2 min — Time from anomaly detection to optimization policy update.",
              "Policy Enforcement Commit ≤ 5 min — Applied through WAN controller or edge device API.",
              "Evidence Publication ≤ 1 hr — Bandwidth savings report export to GRC.",
              "Agent Availability ≥ 99.9% — Redundant orchestration monitored by Prometheus/Grafana."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized 'WANOptAI' service.",
              "Primary Interfaces — NETCONF/RESTCONF, SD-WAN controller REST APIs, gNMI telemetry.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing optimization baselines, flow history, and savings KPIs.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on WAN optimization algorithms, traffic behavior, and RunOps performance KB.",
              "Security Controls — API key authentication, TLS 1.3 encryption, read-only device access for monitoring, RBAC separation.",
              "Outputs — WAN Optimization Report (JSON/PDF), Savings Summary, SLA Impact Assessment, Audit Snapshot.",
              "Monitoring — Grafana dashboards for bandwidth savings, cache hits, latency trends, and optimization events.",
              "Error Handling — Auto-rollback on failed optimization; restore baseline policy if performance worsens post-adjustment."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Telemetry patterns, detected inefficiencies, or scheduled optimization cycles.",
      workingPlane: "RunOps n8n automation cluster with containerized 'WANOptAI' service.",
      memory: "Persisted optimization baselines, flow history, and savings KPIs in Postgres.",
      reasoning: "Azure OpenAI driven models to recommend techniques and parameter tuning based on traffic composition and historical savings.",
      execution: "Apply optimization policies to WAN edge devices via controller APIs, validate results, and record evidence with rollback on regression."
    },
    kpis: [
      { label: "Average Bandwidth Savings", value: "27.6", unit: "%", target: "≥ 25%", color: "#10b981" },
      { label: "Cache Hit Ratio", value: "72.3", unit: "%", target: "≥ 70%", color: "#10b981" },
      { label: "Compression Efficiency", value: "86.1", unit: "%", target: "≥ 85%", color: "#10b981" },
      { label: "Application Acceleration", value: "18.9", unit: "%", target: "≥ 15%", color: "#10b981" },
      { label: "Deduplication Reduction", value: "42.5", unit: "%", target: "≥ 40%", color: "#10b981" },
      { label: "MTTD Degradation", value: "1.4", unit: "min", target: "< 2 min", color: "#10b981" }
    ]
  },
  'overlay-health-agent': {
    title: "SD-WAN & WAN Overlay Health Agent",
    description: "Continuously monitors SD-WAN overlay tunnels for latency, jitter, packet loss and other performance degradations; correlates underlay and overlay metrics, reasons about root causes, and triggers remediation or policy adjustment to maintain optimal tunnel health and user experience.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Real-time visibility into overlay tunnel health, enabling proactive remediation and performance assurance.",
          "Reduced application downtime and user complaints by minimizing impact of tunnel degradations.",
          "Improved SLA adherence for WAN transport and overlay service.",
          "Operational efficiency gains through automated detection and guided remediation of tunnel issues.",
          "Enhanced audit readiness with documented tunnel health trends, root-cause reasoning, and corrective actions."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Modern enterprise WANs use multiple overlay tunnels (Internet, MPLS, broadband, LTE) and SD-WAN fabric to transport business traffic.",
              "Tunnel health issues—high latency, jitter, packet loss, flaps—directly impact application performance (voice, video, SaaS) and user experience.",
              "Overlay monitoring is often reactive: alerts when user trouble occurs, but lacks real-time, correlated intelligence between underlay link state, overlay tunnel health metrics, and business-critical application paths.",
              "Without continuous, intelligent tunnel health monitoring and remediation, enterprises face degraded application performance, increased support overhead, and missed SLA targets."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Monitor all SD-WAN overlay tunnels (branch ↔ data centre, branch ↔ cloud edge) for latency, packet loss, jitter and flap count.",
              "Correlate underlay link metrics (ISP path, broadband link, MPLS) with overlay tunnel health to surface root-cause of issues.",
              "Trigger automatic policy shift or traffic reselection when a tunnel becomes unhealthy (as defined by thresholds).",
              "Provide dashboards and reports of tunnel health by site, link colour/class, application category and time-of-day.",
              "Integrate with ITSM (ticketing) and procurement (link SLA escalation) for persistent tunnel degradation."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Static alerting tools surface raw metrics but lack context, correlation, and remediation workflow.",
              "Correlates overlay tunnel metrics with underlay link state, application flows, and change history.",
              "Assesses health in business context (which application, which tunnel, SLA impacted).",
              "Suggests or automates remediation (reroute traffic, adjust overlay parameters, escalate link vendor).",
              "Learns historical patterns (e.g., ISP degradation trends, tunnel failure windows) to predict issues and refine thresholds."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Collect overlay tunnel metrics: latency, jitter, packet loss, retransmissions, flap counts.",
              "Collect underlay link metrics: bandwidth utilisation, interface errors, ISP performance, RTT to peer.",
              "Compare metrics to baseline and SLA thresholds; classify tunnel health (green/yellow/red).",
              "Correlate metrics across layers: e.g., high jitter on tunnel + underlay packet loss indicates link issue; high latency but underlay ok might indicate path routing issue.",
              "Trigger remediation workflows: policy shift to alternative path, alert to NOC, generate ticket for ISP escalation.",
              "Produce analytics: trend graphs, 'health score' per tunnel/site, root-cause classification.",
              "Integrate with SD-WAN controller to act on unhealthy tunnels (reroute traffic, disable path).",
              "Audit logs and evidence: capture health events, actions taken, outcomes."
            ]
          },
          {
            title: "Integrations",
            content: [
              "SD-WAN Controller APIs — Retrieve tunnel state, metrics; push policy changes (Bidirectional).",
              "Underlay Link Telemetry (SNMP, NetFlow, gNMI) — Collect interface, path metrics (Inbound).",
              "Application Flow Telemetry (NetFlow/IPFIX) — Tie tunnel health to application impact (Inbound).",
              "ITSM (ServiceNow/Jira) — Open tickets for persistent tunnel issues (Bidirectional).",
              "GRC / SLA Dashboard (CoreStack, other) — Map tunnel health to SLA compliance and audit reporting (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Interpret patterns, classify root-cause, generate narrative (Inbound).",
              "Data Lake / Confluence — Archive health logs, trend reports, remediation history (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Metric Collection & Baseline: Collect tunnel & underlay metrics, establish baseline health values. (50%).",
              "Tier 2 — Health Classification & Alerting: Evaluate metrics vs thresholds, classify health, send alerts. (75%).",
              "Tier 3 — Root Cause Reasoning & Remediation Recommendation: Correlate metrics, identify cause, recommend or trigger remediation. (90%).",
              "Tier 4 — Predictive Monitoring & Autonomous Remediation: Learn patterns, predict tunnel issues ahead of time, initiate remediation automatically. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Tunnel Health Score (average) ≥ 95% — Average health of all tunnels across sites.",
              "Mean Time to Detect (MTTD) Tunnel Degradation < 3 min — Time between tunnel metric crossing threshold and detection.",
              "Mean Time to Remediate (MTTR) Tunnel Issues < 10 min — Time between detection and action (reroute, fix).",
              "False Positive Rate ≤ 3% — Incorrect health alerts where no user impact.",
              "SLA Compliance Rate (tunnel performance) ≥ 98% — Percentage of tunnels meeting SLA for latency/loss/jitter.",
              "Automation Adoption Rate ≥ 80% — Percentage of remediation actions executed autonomously."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Sites Monitored: 150 (branch, cloud-edge, data-centre)",
              "Tunnels Monitored: 420",
              "Average Tunnel Health Score: 96.2%",
              "Detected Health Degradation Events: 128",
              "- Latency breach: 47",
              "- Jitter breach: 32",
              "- Packet-loss breach: 49",
              "Auto-Remediation Executed: 102 (79.7%)",
              "Average MTTD: 2.5 min",
              "Average MTTR: 8.3 min",
              "False Positive Rate: 2.1%",
              "SLA Compliance Rate: 98.4%",
              "Engineer Hours Saved: ~210 per month"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Metric Collection Latency ≤ 1 min — Near-real-time telemetry ingestion.",
              "Health Evaluation Cycle ≤ 2 min — Health classification after metric threshold breach.",
              "Remediation Trigger / Action ≤ 10 min — Policy shift or alert.",
              "Report Publication ≤ 1 hr — Health trends and audit summary.",
              "Agent Availability ≥ 99.9% — Monitored via Prometheus/Grafana."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster in Azure, containerised 'OverlayHealth' service.",
              "Primary Interfaces — REST APIs of SD-WAN controller, gNMI/NETCONF for tunnels, SNMP/NetFlow for underlay links.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing tunnel health baselines, historical event logs.",
              "Reasoning Model — Azure OpenAI GPT-4-o fine-tuned on WAN overlay health data and RunOps KB.",
              "Security Controls — Read-only telemetry access, encrypted credentials, TLS 1.3, RBAC separation.",
              "Outputs — Tunnel Health Report (JSON/PDF), Remediation Log, SLA Compliance Snapshot.",
              "Monitoring — Grafana dashboard: tunnel health over time, health-score trend, alert counts.",
              "Error Handling — Auto-retry telemetry ingestion, escalate to SOC if three cycles fail."
            ]
          }
        ]
      },
      {
        title: "Summary",
        subSections: [
          {
            title: "Summary",
            content: [
              "The SD-WAN & WAN Overlay Health Agent provides intelligent, continuous monitoring and governance over the health of overlay tunnels across your WAN. By combining real-time telemetry, cross-layer correlation, AI reasoning, and automation, it ensures that tunnel degradations are detected early, understood in context, and remediated rapidly—keeping application performance, user experience and SLA compliance consistently high.",
              "This becomes a critical component of your WAN governance and zero-trust architecture for branch-to-cloud connectivity."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Tunnel metric anomalies or threshold breaches.",
      workingPlane: "RunOps n8n automation cluster in Azure with containerized 'OverlayHealth' service.",
      memory: "Tunnel health baselines and historical event logs in PostgreSQL.",
      reasoning: "Cross-layer correlation and root-cause analysis using Azure OpenAI models.",
      execution: "Triggers remediation workflows, policy shifts, or alerts with evidence capture."
    },
    kpis: [
      { label: "Tunnel Health Score", value: "96.2", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "MTTD", value: "2.5", unit: "min", target: "< 3 min", color: "#10b981" },
      { label: "MTTR", value: "8.3", unit: "min", target: "< 10 min", color: "#10b981" },
      { label: "False Positive Rate", value: "2.1", unit: "%", target: "≤ 3%", color: "#10b981" },
      { label: "SLA Compliance Rate", value: "98.4", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Automation Rate", value: "79.7", unit: "%", target: "≥ 80%", color: "#f97316" }
    ]
  },
  'circuit-cost-optimizer-agent': {
    title: "SD-WAN & WAN Circuit Cost Optimizer Agent",
    description: "Continuously analyzes WAN circuit utilization, carrier billing data, and performance telemetry to identify cost inefficiencies, recommend contract optimizations, and forecast future bandwidth and spend requirements — ensuring cost-effective, SLA-aligned connectivity across all WAN links.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Cost Optimization — Identify underutilized circuits and recommend downgrades or provider renegotiations.",
          "Financial Governance — Correlate real-time utilization with contract terms and billing data for transparency.",
          "Capacity Planning — Forecast bandwidth growth and optimize circuit allocation based on usage trends.",
          "SLA-to-Cost Validation — Quantify cost efficiency per circuit relative to delivered SLA performance.",
          "Vendor Negotiation Intelligence — Provide evidence-based insights for contract renewals and rate optimization."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprise WAN spend is rising due to multi-carrier, multi-transport complexity (MPLS, broadband, 5G, DIA, SASE) and underutilized circuits that remain on legacy contracts.",
              "Network teams lack real-time visibility into cost vs utilization, causing waste and missed opportunities for contract optimization.",
              "Carriers often bill based on provisioned capacity, not actual use — creating a mismatch between expense and value.",
              "Manual financial audits of circuit invoices are reactive, error-prone, and disconnected from performance data.",
              "The enterprise needs a continuous, intelligent cost optimization layer that merges telemetry, finance, and performance insight to achieve maximum WAN ROI."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Cost Optimization — Identify underutilized circuits and recommend downgrades or provider renegotiations.",
              "Financial Governance — Correlate real-time utilization with contract terms and billing data for transparency.",
              "Capacity Planning — Forecast bandwidth growth and optimize circuit allocation based on usage trends.",
              "SLA-to-Cost Validation — Quantify cost efficiency per circuit relative to delivered SLA performance.",
              "Vendor Negotiation Intelligence — Provide evidence-based insights for contract renewals and rate optimization."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Carrier Cost Analysis — Correlate link cost per Mbps with observed utilization and SLA metrics.",
              "Underutilization Detection — Flag circuits operating below utilization thresholds for downgrade or cancellation.",
              "Contract Optimization & Renewal Planning — Recommend vendor negotiation strategies and renewal schedules based on data-driven analysis.",
              "Dynamic Capacity Reallocation — Suggest redistributing bandwidth between MPLS, Internet, and LTE links to optimize cost-performance balance.",
              "Multi-Transport ROI Tracking — Compare the total cost of ownership (TCO) per transport type over time."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Circuit Inventory & Cost Mapping — Discover all WAN circuits and map contract details, provider, cost, and capacity.",
              "Utilization Analysis — Correlate bandwidth usage and performance with cost and SLA data.",
              "Cost Efficiency Scoring — Generate cost-per-Mbps and performance-adjusted value index per circuit.",
              "AI-Driven Recommendation Engine — Suggest downgrade, upgrade, or reallocation actions with justification.",
              "Billing Verification — Cross-check invoices and actual usage for overbilling or underdelivery of service.",
              "Contract Lifecycle Tracking — Monitor contract start/end dates, renewal terms, and penalties.",
              "Forecasting & Budget Modeling — Use machine learning to project utilization growth and cost trends.",
              "Reporting & Evidence Generation — Export optimization findings for finance, procurement, and GRC."
            ]
          },
          {
            title: "Integrations",
            content: [
              "SD-WAN Controller / Orchestrator APIs — Collect circuit inventory, utilization, and SLA telemetry (Inbound).",
              "Carrier Portals / Billing APIs (EDI, CSV, REST) — Ingest invoice, pricing, and contract data (Inbound).",
              "CoreStack GRC / Financial Governance Modules — Store audit evidence and link financial governance policies (Bidirectional).",
              "ServiceNow / Jira — Create procurement or renewal tickets with recommendation context (Bidirectional).",
              "ERP / Procurement Systems (SAP, Coupa, Oracle) — Sync renewal schedules and track savings implementation (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Analyze cost trends, forecast demand, and generate financial insights (Inbound).",
              "Confluence / Data Lake — Archive reports, evidence, and optimization summaries (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Data Collection & Mapping: Gather utilization, billing, and contract data from all WAN circuits. (50%).",
              "Tier 2 — Correlation & Cost Efficiency Analysis: Calculate utilization vs cost efficiency and SLA alignment. (75%).",
              "Tier 3 — AI-Driven Optimization Recommendation: Generate cost-saving and renewal recommendations. (90%).",
              "Tier 4 — Predictive Forecasting & Autonomous Governance: Forecast spend, automate renewal workflows, and adjust baselines. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Cost Optimization Rate ≥ 15% — Reduction in WAN circuit spend post-agent deployment.",
              "License & Billing Accuracy ≥ 98% — Correct correlation between billed and actual usage.",
              "Mean Time to Detect (MTTD) Cost Anomaly < 24 hrs — Detection time for overbilling or underutilization.",
              "Recommendation Accuracy ≥ 95% — Validated actionable optimization suggestions.",
              "Forecast Accuracy (Utilization & Cost) ≥ 92% — Accuracy of AI-predicted usage and spend growth.",
              "Automation Adoption Rate ≥ 80% — Percentage of recommendations executed without manual intervention."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Sites / Circuits Monitored: 160",
              "Vendors Analyzed: 18 (MPLS, DIA, LTE, Broadband)",
              "Total Monthly Spend: $1.84M",
              "Identified Underutilized Circuits: 41 (avg utilization < 20%)",
              "Contract Renewal Opportunities: 22",
              "Recommended Downgrades / Vendor Switches: 15",
              "Verified Savings Potential: $226K annually (≈ 12.3%)",
              "Forecast Bandwidth Growth: +8.5% (QoQ)",
              "Recommendation Accuracy: 94.8%",
              "MTTD (Cost Anomaly): 18 hours",
              "Automation Adoption Rate: 81%",
              "Engineer & Finance Hours Saved: ~280 per month",
              "Audit Reports Generated: 11 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Data Collection & Correlation Cycle ≤ 12 hrs — Daily aggregation of telemetry and billing data.",
              "Anomaly Detection & Recommendation Generation ≤ 4 hrs — Automated reasoning and validation.",
              "Renewal Workflow Creation (ITSM/ERP) ≤ 8 hrs — Auto-ticket or PO request generation.",
              "Evidence Publication ≤ 24 hrs — Compliance export to CoreStack / Confluence.",
              "Agent Availability ≥ 99.9% — HA RunOps cluster monitored by Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized 'CostOptAI' microservice.",
              "Primary Interfaces — SD-WAN controller REST APIs, carrier billing APIs (EDI/REST), ERP procurement connectors.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing circuit inventory, cost history, and performance baselines.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on telecom economics, utilization trends, and RunOps finance KB.",
              "Security Controls — Tokenized API access, TLS 1.3, encrypted financial data vault, RBAC per user group.",
              "Outputs — Cost Optimization Report (PDF/JSON), Renewal Forecast, Vendor Recommendation Matrix, Compliance Snapshot.",
              "Monitoring — Grafana dashboard visualizing spend trendlines, efficiency scores, and savings achieved.",
              "Error Handling — Auto-retry on API call failures; fallback to cached billing dataset if vendor API unreachable."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Billing import, utilization anomaly, or scheduled financial review.",
      workingPlane: "RunOps n8n cluster with containerized 'CostOptAI' microservice.",
      memory: "Persisted circuit inventory, billing history, and forecast models in Postgres.",
      reasoning: "Azure OpenAI driven ROI modeling and recommendation generation.",
      execution: "Generate optimization recommendations, create procurement/ITSM tickets, and record audit evidence."
    },
    kpis: [
      { label: "Cost Optimization Rate", value: "12.3", unit: "%", target: "≥ 15%", color: "#f97316" },
      { label: "License & Billing Accuracy", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Recommendation Accuracy", value: "94.8", unit: "%", target: "≥ 95%", color: "#f97316" },
      { label: "Forecast Accuracy", value: "92", unit: "%", target: "≥ 92%", color: "#10b981" },
      { label: "Automation Adoption", value: "81", unit: "%", target: "≥ 80%", color: "#10b981" },
      { label: "MTTD (Cost Anomaly)", value: "18", unit: "hrs", target: "< 24 hrs", color: "#10b981" }
    ]
  },
  'failover-orchestrator-agent': {
    title: "SD-WAN & WAN Failover Orchestrator Agent",
    description: "Detects link failures, degradations, and outage conditions across WAN transports in real time and orchestrates seamless traffic failover to backup circuits, ensuring continuous application availability, zero packet loss, and SLA adherence across distributed branch and cloud-edge networks.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Zero-Downtime Resilience — Automatically reroutes traffic within seconds of link degradation or failure.",
          "Application Continuity — Preserves active sessions and performance during failover without user disruption.",
          "Operational Efficiency — Eliminates manual intervention in link outage handling.",
          "SLA & Compliance Assurance — Maintains service-level objectives for latency, jitter, and packet loss during outages.",
          "Analytics & Audit Readiness — Provides post-event analysis and SLA evidence for each failover action."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "WAN outages, transport degradations, and circuit failures remain one of the leading causes of enterprise downtime and SLA violations.",
              "Manual failover configurations and static backup routes are slow, error-prone, and application unaware, often resulting in session resets or brownouts before recovery.",
              "Hybrid networks spanning MPLS, broadband, LTE/5G, and SASE tunnels require intelligent, automated failover that operates without human intervention — maintaining path continuity, performance, and security posture.",
              "Enterprises need a fully autonomous, telemetry-aware failover orchestration system that dynamically reroutes traffic to healthy circuits while preserving session state and compliance boundaries."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Zero-Downtime Resilience — Automatically reroutes traffic within seconds of link degradation or failure.",
              "Application Continuity — Preserves active sessions and performance during failover without user disruption.",
              "Operational Efficiency — Eliminates manual intervention in link outage handling.",
              "SLA & Compliance Assurance — Maintains service-level objectives for latency, jitter, and packet loss during outages.",
              "Analytics & Audit Readiness — Provides post-event analysis and SLA evidence for each failover action."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Real-Time Failover Execution — Automatically detect and redirect traffic during link outage or brownout.",
              "Sub-Second Tunnel Rerouting — Maintain overlay tunnel availability by switching underlay paths seamlessly.",
              "Adaptive Failback — Automatically restore primary path when it meets SLA baselines again.",
              "Cross-Transport Continuity — Coordinate failover between MPLS, broadband, and LTE/5G paths.",
              "Failover Event Auditing — Log every event, trigger, and SLA impact for compliance evidence."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Continuous Monitoring — Analyzes real-time telemetry for link health (latency, jitter, packet loss, interface state).",
              "Contextual Decisioning — Assesses outage severity, app criticality, and active sessions before failover.",
              "Adaptive Response — Orchestrates sub-second traffic transition while maintaining encryption and segmentation boundaries.",
              "Feedback & Learning — Learns from past events to refine future failover thresholds and decision accuracy.",
              "End-to-End Awareness — Correlates SD-WAN overlay health with underlay performance and application impact."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Link & Path Monitoring — Continuously measure latency, jitter, packet loss, and link-state across all WAN transports.",
              "Outage Detection & Classification — Identify total link failure, partial degradation, or brownout conditions.",
              "Failover Triggering — Initiate path switch when SLA or health thresholds breached.",
              "Stateful Traffic Handoff — Maintain active session integrity during transport change.",
              "SLA Validation & Recovery — Monitor post-failover performance and trigger adaptive failback to primary links.",
              "AI Decision Engine — Evaluate event severity and traffic class priority to determine which flows to move first.",
              "Automated Notification & Ticketing — Log failover events and update ITSM systems automatically.",
              "Reporting & Audit Trail — Record event timelines, triggers, paths impacted, and SLA restoration metrics."
            ]
          },
          {
            title: "Integrations",
            content: [
              "SD-WAN Controller / Orchestrator APIs — Retrieve tunnel states, enforce path switch policies, monitor health (Bidirectional).",
              "WAN Edge Devices / Routers — Execute route and interface state changes (Bidirectional).",
              "Telemetry & Analytics Systems (NetFlow/IPFIX, gNMI) — Collect performance metrics and detect degradations (Inbound).",
              "CoreStack GRC / SLA Modules — Store event evidence and map to SLA/compliance frameworks (Bidirectional).",
              "ServiceNow / Jira — Automatically generate incident or change records for major failover events (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Contextual decision-making and root-cause narrative generation (Inbound).",
              "Confluence / Data Lake — Archive failover logs, SLA reports, and analytics evidence (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Monitoring & Detection: Track link and tunnel metrics continuously for outage conditions. (50%).",
              "Tier 2 — SLA Correlation & Alerting: Correlate degradation with SLA thresholds and generate alerts. (75%).",
              "Tier 3 — AI Reasoning & Failover Execution: Initiate path shift with contextual prioritization. (90%).",
              "Tier 4 — Predictive Learning & Autonomous Recovery: Anticipate failure patterns, self-tune thresholds, manage failback autonomously. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Failover Detection Accuracy ≥ 98% — Correct identification of real outage events.",
              "Mean Time to Detect (MTTD) < 5 s — Time from link degradation to detection.",
              "Mean Time to Redirect (MTTRedirect) < 10 s — Time from detection to full traffic transition.",
              "Session Retention Rate ≥ 99% — Percentage of active sessions preserved during failover.",
              "False Trigger Rate ≤ 2% — Incorrect failovers triggered without actual outage.",
              "SLA Compliance During Failover ≥ 97% — Percentage of flows meeting SLA post-transition."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Sites Monitored: 142",
              "WAN Links: 464 (MPLS, Broadband, LTE/5G)",
              "Outages Detected: 57 — Hard Link Failures: 21; Brownouts / Partial Degradations: 36",
              "Average Failover Time: 7.2 seconds",
              "Session Retention: 99.4%",
              "False Triggers: 1.8%",
              "SLA Compliance Post-Failover: 97.9%",
              "Automated Failback Executions: 33",
              "Average MTTD: 3.8 seconds",
              "Mean Failover Recovery Window: 9.5 seconds",
              "Engineer Hours Saved: ~250 per month",
              "Audit Reports Generated: 9 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Telemetry Refresh Interval ≤ 1 s — Sub-second streaming from edge devices.",
              "Outage Detection & Classification ≤ 5 s — Detect link down or SLA breach.",
              "Failover Execution ≤ 10 s — Seamless reroute and tunnel reassociation.",
              "Failback to Primary Link ≤ 60 s — Automatic recovery when primary restored.",
              "Evidence Publication ≤ 1 hr — SLA compliance report generation.",
              "Agent Availability ≥ 99.9% — HA RunOps cluster monitored via Prometheus/Grafana."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized \"FailoverAI\" microservice.",
              "Primary Interfaces — SD-WAN Controller REST APIs, gNMI telemetry, SNMPv3 traps for link events.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing link state, health thresholds, and failover history.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on link telemetry datasets, failure signatures, and RunOps reliability KB.",
              "Security Controls — Tokenized API access, encrypted credentials, TLS 1.3, RBAC separation by transport domain.",
              "Outputs — Failover Event Log (JSON/PDF), SLA Impact Report, Recovery Timeline, Compliance Audit Snapshot.",
              "Monitoring — Grafana dashboards: link health, failover latency, session retention rate, SLA conformance.",
              "Error Handling — Auto-retry on policy push; revert to standby link if active backup degrades post-failover."
            ]
          }
        ]
      },
      {
        title: "Summary",
        subSections: [
          {
            title: "Summary",
            content: [
              "The SD-WAN & WAN Failover Orchestrator Agent is the enterprise’s autonomous resilience engine — continuously safeguarding uptime by detecting outages and rerouting traffic seamlessly across WAN transports.",
              "By integrating real-time telemetry, AI reasoning, and closed-loop automation, it maintains connectivity during link failures with sub-second precision, preserving application sessions and compliance.",
              "This agent transforms WAN failover from a reactive redundancy function into an intelligent, self-healing continuity system, ensuring that the network never stops — even when the circuits do."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Link failure, brownout, or sustained degradation.",
      workingPlane: "RunOps n8n automation cluster with containerized 'FailoverAI' microservice.",
      memory: "Persisted link state, session checkpoints, and failover history in Postgres.",
      reasoning: "Azure OpenAI driven contextual decisioning that weighs app criticality, session state, and SLA impact.",
      execution: "Detects event, computes prioritized failover plan, executes stateful handoff via SD-WAN controller and edge APIs, validates outcomes, and records audit evidence."
    },
    kpis: [
      { label: "Failover Detection Accuracy", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "MTTD", value: "3.8", unit: "s", target: "< 5 s", color: "#10b981" },
      { label: "Mean Time to Redirect", value: "9.5", unit: "s", target: "< 10 s", color: "#10b981" },
      { label: "Session Retention Rate", value: "99.4", unit: "%", target: "≥ 99%", color: "#10b981" },
      { label: "False Trigger Rate", value: "1.8", unit: "%", target: "≤ 2%", color: "#10b981" },
      { label: "SLA Compliance During Failover", value: "97.9", unit: "%", target: "≥ 97%", color: "#10b981" }
    ]
  },
  'app-aware-routing-agent': {
    title: "SD-WAN & WAN App-Aware Routing Agent",
    description: "Dynamically routes traffic across WAN transports based on application criticality, user identity, device posture, and business context. Optimizes performance for mission-critical applications, preserves policy compliance, and enhances user experience through AI-driven routing decisions.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Experience-Driven Performance — Prioritizes critical applications (EHR, VoIP, ERP) for optimal paths dynamically.",
          "Identity-Aware Routing — Applies differentiated routing based on user, department, or device compliance.",
          "Zero-Trust Alignment — Restricts routes by identity and application risk, minimizing lateral movement.",
          "Bandwidth Optimization — Routes low-priority traffic to lower-cost links, preserving premium paths.",
          "Operational Simplicity — Automates complex routing logic using telemetry, App-ID, and user context."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Traditional routing decisions depend on IP, port, and static policy — blind to the actual application or user behind the session.",
              "As enterprises adopt SaaS, cloud, and hybrid apps, static routing leads to misaligned prioritization, poor user experience, and wasted WAN capacity.",
              "Routing must now account for who (user/role), what (application), and how critical the session is to the business.",
              "Manual SD-WAN configurations cannot scale to these variables or adapt to real-time usage and performance metrics.",
              "Enterprises require an agentic routing layer that continuously applies context-aware, zero-trust-aligned path decisions across WAN and cloud edges."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Application Detection & Classification — Identify and categorize flows via App-ID and traffic heuristics.",
              "User & Device Context Correlation — Map User-ID and device posture (trusted/untrusted) to routing policies.",
              "SLA Awareness — Continuously monitor latency, jitter, and packet loss per app and per transport.",
              "Dynamic Path Selection — Reroute traffic to maintain SLA compliance or respond to link degradation.",
              "Risk-Based Routing Policy — Prioritize or restrict flows based on app risk classification (critical, standard, restricted).",
              "AI Reasoning Layer — Evaluate network, user, and app context holistically to make optimized path decisions.",
              "Policy Orchestration — Push routing changes to SD-WAN controllers and edge devices automatically.",
              "Reporting & Compliance Evidence — Generate routing decision summaries and SLA adherence reports for audits."
            ]
          },
          {
            title: "Integrations",
            content: [
              "SD-WAN Controller / Orchestrator APIs — Apply and validate routing policies (Bidirectional).",
              "User-ID / Identity Providers (AD, Entra ID, Okta) — Correlate sessions with users and roles (Inbound).",
              "App-ID / Traffic Analytics (Cortex, NetFlow/IPFIX) — Provide application and risk classification (Inbound).",
              "QoS & Path Selection Agents — Coordinate prioritization and link selection (Bidirectional).",
              "Zero-Trust Gateway Agent — Ensure routing aligns with segmentation and access posture (Bidirectional).",
              "CoreStack GRC / SLA Module — Store SLA compliance and audit records (Bidirectional).",
              "ServiceNow / Jira — Track routing adjustments and escalation events (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Contextual routing decisions, policy generation, and narrative output (Inbound).",
              "Confluence / Data Lake — Archive reports and compliance logs (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Application SLA Compliance ≥ 98% — Flows meeting latency/jitter targets per app.",
              "Context-Aware Routing Accuracy ≥ 97% — Correct path decisions per app/user validated post-event.",
              "Mean Time to Detect (MTTD) < 2 min — Time from SLA violation or policy drift to detection.",
              "Mean Time to Adjust (MTTA) < 5 min — Time from detection to path or policy change.",
              "Automation Adoption Rate ≥ 80% — Routing decisions executed autonomously.",
              "User Experience Improvement ≥ 15% — Latency and jitter reduction for high-priority apps."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Branches Monitored: 148",
              "Applications Tracked: 432",
              "Active User Profiles: 17 K",
              "Dynamic Routing Decisions: 1,046",
              "SLA Breaches Detected: 208",
              "Auto-Resolved by Agent: 194 (93%)",
              "Average Latency Improvement (Critical Apps): 19.4%",
              "Average Bandwidth Savings (from offload): 12.1%",
              "Context Accuracy: 96.8%",
              "Automation Adoption Rate: 82%",
              "MTTD: 1.6 min — MTTA: 4.3 min",
              "Engineer Hours Saved: ≈ 260 per month",
              "Audit Reports Generated: 9 (CoreStack / Confluence)"
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "App or user context change, SLA deviation, or scheduled evaluation.",
      workingPlane: "RunOps n8n automation cluster (Azure) with containerized \"AppRouteAI\" service.",
      memory: "Persisted app/user profiles, policy mappings, and SLA thresholds in Postgres.",
      reasoning: "Azure OpenAI GPT-4o trained on application routing datasets, SLA models, and RunOps network KB.",
      execution: "Generate routing deltas, push policy updates to SD-WAN controllers, validate outcomes, and emit audit evidence."
    },
    kpis: [
      { label: "Application SLA Compliance", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Context-Aware Routing Accuracy", value: "96.8", unit: "%", target: "≥ 97%", color: "#f97316" },
      { label: "MTTD", value: "1.6", unit: "min", target: "< 2 min", color: "#10b981" },
      { label: "MTTA", value: "4.3", unit: "min", target: "< 5 min", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "82", unit: "%", target: "≥ 80%", color: "#10b981" },
      { label: "User Experience Improvement", value: "19.4", unit: "%", target: "≥ 15%", color: "#10b981" }
    ]
  },
  'branch-deployment-agent': {
    title: "SD-WAN & WAN Branch Deployment Agent",
    description: "Automates the provisioning, onboarding, and configuration of SD-WAN appliances and branch routers — including registration, licensing, and initial policy application — to enable rapid, compliant, and zero-touch deployment across enterprise branch offices and remote sites.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Zero-Touch Provisioning (ZTP) — Enables branch appliances to self-register, configure, and activate with minimal human input.",
          "Rapid Branch Turn-Up — Reduces deployment time from days to hours through automated workflows.",
          "Policy Consistency — Ensures each new branch inherits correct routing, segmentation, and security policies.",
          "Operational Governance — Provides full audit trail of provisioning events, configs, and compliance checks.",
          "Scalable Growth Enablement — Supports enterprise WAN expansion and refresh cycles at any scale."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprise WAN expansion often involves manual provisioning of SD-WAN edge devices, creating delays, configuration drift, and inconsistent policy enforcement.",
              "Each new branch requires coordination across network, security, and operations teams, introducing human error, time overruns, and compliance risk.",
              "Without automation, scaling deployments to dozens or hundreds of sites becomes operationally unsustainable.",
              "Organizations need an autonomous branch deployment agent that integrates orchestration, policy baselining, and post-provisioning validation into a single, continuous workflow."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Zero-Touch Provisioning (ZTP) — Enables branch appliances to self-register, configure, and activate with minimal human input.",
              "Rapid Branch Turn-Up — Reduces deployment time from days to hours through automated workflows.",
              "Policy Consistency — Ensures each new branch inherits correct routing, segmentation, and security policies.",
              "Operational Governance — Provides full audit trail of provisioning events, configs, and compliance checks.",
              "Scalable Growth Enablement — Supports enterprise WAN expansion and refresh cycles at any scale."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "New Branch Onboarding — Fully automate SD-WAN appliance registration, license activation, and initial policy sync.",
              "Brownfield Device Migration — Re-provision existing routers to SD-WAN configuration baselines.",
              "Config Compliance Enforcement — Validate that deployed branches conform to template and policy baselines.",
              "License & Serial Registration — Auto-register devices with vendor portals and confirm entitlement status.",
              "CMDB & GRC Synchronization — Update asset records, topology maps, and compliance tracking automatically."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Contextual Intelligence — Determines correct configuration template and policies based on branch profile, region, and business unit.",
              "End-to-End Automation — Executes registration, activation, baseline configuration, validation, and compliance sync autonomously.",
              "AI Reasoning — Interprets deployment failures, recommends corrective actions, or auto-applies fixes.",
              "Cross-Plane Awareness — Aligns routing, security, and QoS policies across WAN and zero-trust layers.",
              "Memory-Driven Validation — Remembers prior deployments to improve accuracy and pre-empt known issues."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Automated Device Onboarding — Execute full ZTP workflows from discovery to activation.",
              "Configuration Template Application — Apply routing, segmentation, and QoS policies based on predefined branch archetypes.",
              "License Validation & Activation — Retrieve entitlement data and confirm feature enablement.",
              "Baseline Compliance Enforcement — Verify that applied configs match golden templates and zero-trust design rules.",
              "Connectivity & Policy Validation — Test overlay tunnel health, routing adjacencies, and app-aware routing readiness post-provisioning.",
              "AI-Driven Troubleshooting — Diagnose provisioning errors (certificate mismatch, DHCP failure, license denial) and self-correct.",
              "Change & Asset Recording — Update ITSM and CMDB systems automatically with deployment metadata.",
              "Evidence Generation — Create audit-ready records of each deployment for compliance and lifecycle tracking."
            ]
          },
          {
            title: "Integrations",
            content: [
              "SD-WAN Controller / Orchestrator APIs — Execute ZTP workflows, push config templates, retrieve device status (Bidirectional).",
              "Network Inventory / CMDB — Update asset metadata (serial, site, license, topology) (Bidirectional).",
              "Vendor License / Activation Portals — Validate entitlements and activate device licenses (Inbound).",
              "CoreStack GRC / Compliance Modules — Store deployment evidence, map controls (NIST CM-6, ISO A.12) (Bidirectional).",
              "ServiceNow / Jira — Track deployment tickets, approvals, and exceptions (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Interpret errors, generate remediation steps, optimize workflows (Inbound).",
              "Confluence / Data Lake — Archive deployment logs, evidence, and configuration snapshots (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Device Discovery & Registration: Detect and register new appliances, confirm connectivity to controller. (50%).",
              "Tier 2 — Baseline Configuration & Validation: Apply templates, run health checks, confirm policy alignment. (75%).",
              "Tier 3 — AI-Assisted Troubleshooting & Self-Correction: Detect provisioning errors and auto-remediate common failures. (90%).",
              "Tier 4 — Continuous Learning & Lifecycle Governance: Adjust deployment workflows and templates from previous outcomes. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Deployment Success Rate ≥ 98% — Successful deployments vs total attempts.",
              "Mean Time to Provision (MTTP) ≤ 1 hr — Average end-to-end provisioning time.",
              "Policy Compliance Accuracy ≥ 97% — Percentage of new branches conforming to baseline policies.",
              "Mean Time to Detect (MTTD) < 5 min — Detection of provisioning errors.",
              "Mean Time to Remediate (MTTR) < 30 min — Time from error detection to resolution.",
              "Automation Adoption Rate ≥ 85% — Deployments executed autonomously."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Branches Provisioned: 72",
              "Devices Activated: 146 (primary + HA pairs)",
              "Average MTTP: 47 minutes",
              "Automated Deployments (No Human Intervention): 85%",
              "Detected Configuration Drift Post-Deployment: 4 devices",
              "Auto-Remedied Errors: 3",
              "Baseline Policy Compliance: 97.8%",
              "Change Records Created in ITSM: 72",
              "Audit Packages Generated: 12 (CoreStack / Confluence)",
              "Engineer Hours Saved: ~260 per month"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Device Discovery to Registration ≤ 15 min — Includes inventory lookup and controller pairing.",
              "Template Application & Validation ≤ 30 min — Configuration push and health verification.",
              "License Activation Confirmation ≤ 20 min — API sync with vendor portal.",
              "Evidence Publication ≤ 1 hr — Audit and compliance report generation.",
              "Agent Availability ≥ 99.9% — HA RunOps cluster with Prometheus/Grafana monitoring."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized \"BranchDeployAI\" service.",
              "Primary APIs — SD-WAN Controller REST, NETCONF/RESTCONF for device onboarding, vendor license API.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing branch templates, config snapshots, and deployment history.",
              "Reasoning Model — Azure OpenAI GPT-4o trained on ZTP logs, onboarding errors, and RunOps deployment KB.",
              "Security Controls — Encrypted credentials, TLS 1.3, RBAC per deployment team, token-based auth.",
              "Outputs — Branch Deployment Report (JSON/PDF), Policy Compliance Scorecard, ITSM Update Log.",
              "Monitoring — Grafana dashboard for deployment status, MTTP trend, compliance rate.",
              "Error Handling — Auto-retry failed registration; rollback config to baseline on error; escalate to ITSM if three failures occur."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "New device registration or ZTP event.",
      workingPlane: "RunOps n8n automation cluster (Azure) with containerized 'BranchDeployAI' service.",
      memory: "Persisted branch templates, config snapshots, and deployment history in Postgres.",
      reasoning: "Azure OpenAI driven troubleshooting and deployment optimization.",
      execution: "Register devices, apply templates, validate connectivity and policies, and record audit evidence with automated rollback on failure."
    },
    kpis: [
      { label: "Deployment Success Rate", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Mean Time to Provision (MTTP)", value: "47", unit: "min", target: "≤ 60 min", color: "#10b981" },
      { label: "Policy Compliance Accuracy", value: "97.8", unit: "%", target: "≥ 97%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD)", value: "5", unit: "min", target: "< 5 min", color: "#10b981" },
      { label: "Mean Time to Remediate (MTTR)", value: "30", unit: "min", target: "< 30 min", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "85", unit: "%", target: "≥ 85%", color: "#10b981" }
    ]
  },
  'hybrid-wan-balancer-agent': {
    title: "SD-WAN & WAN Hybrid WAN Balancer Agent",
    description: "Dynamically balances traffic across MPLS, Internet, and direct cloud connectivity links by evaluating performance, cost, and policy requirements. Ensures optimal routing, resilience, and cost efficiency across all WAN transports while maintaining zero-trust segmentation and SLA compliance.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Dynamic Path Balancing — Distributes traffic intelligently across all WAN transports based on performance and policy context.",
          "SLA Optimization — Prioritizes low-latency, high-reliability circuits for critical workloads automatically.",
          "Cost Efficiency — Minimizes MPLS overuse by routing non-critical or SaaS traffic via broadband or cloud direct connect.",
          "Cloud Performance Improvement — Accelerates SaaS and IaaS traffic using direct cloud on-ramps.",
          "Zero-Trust Enforcement — Ensures segmentation and encryption policies are maintained across all WAN paths."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Modern enterprise WANs depend on a hybrid mix of transports (MPLS, broadband, LTE/5G, and direct cloud interconnects) — each with unique cost, latency, and SLA characteristics.",
              "Static routing and manual traffic steering fail to account for real-time network performance, cost efficiency, or application criticality.",
              "Without dynamic balancing, high-cost circuits are overused while cheaper, high-capacity Internet or cloud links remain underutilized.",
              "Enterprises require an AI-driven hybrid WAN controller that continuously evaluates path performance and cost to deliver optimal routing decisions while preserving compliance and segmentation integrity."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Dynamic Path Balancing — Distributes traffic intelligently across all WAN transports based on performance and policy context.",
              "SLA Optimization — Prioritizes low-latency, high-reliability circuits for critical workloads automatically.",
              "Cost Efficiency — Minimizes MPLS overuse by routing non-critical or SaaS traffic via broadband or cloud direct connect.",
              "Cloud Performance Improvement — Accelerates SaaS and IaaS traffic using direct cloud on-ramps.",
              "Zero-Trust Enforcement — Ensures segmentation and encryption policies are maintained across all WAN paths."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Hybrid Transport Balancing — Continuously adjusts traffic distribution across MPLS, Internet, and cloud circuits based on utilization, cost, and SLA.",
              "Application-Aware Steering — Assigns apps to transports that best meet latency, security, and throughput needs.",
              "Cost-Driven Load Redistribution — Routes discretionary traffic over broadband or cloud links to reduce MPLS spend.",
              "Multi-Cloud Traffic Optimization — Uses direct cloud interconnects for AWS, Azure, or GCP workloads for performance and egress savings.",
              "Resilient Multi-Transport Failover — Automatically reroutes flows if one transport underperforms or fails."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Multi-Input Awareness — Collects link telemetry, application usage, and cost data across all transports.",
              "Contextual Decisioning — Evaluates path selection not just on performance but on cost, compliance, and application sensitivity.",
              "AI-Driven Forecasting — Predicts congestion, cost overruns, and performance degradation before they occur.",
              "Autonomous Balancing — Dynamically redistributes traffic across transports without service interruption.",
              "Learning Feedback Loop — Adapts thresholds and routing weights based on historical performance, policy changes, and SLA outcomes."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Telemetry-Based Transport Analysis — Continuously collect latency, jitter, loss, throughput, and utilization data for each transport.",
              "Cost & Policy Weighting Engine — Factor in circuit cost, bandwidth price per Mbps, and SLA tier in routing decisions.",
              "Dynamic Traffic Balancing — Adjust routing weights (e.g., ECMP, path preference, class-based forwarding) based on conditions.",
              "Application-Aware Path Assignment — Route critical apps over MPLS or cloud direct connect; assign bulk traffic to broadband.",
              "AI Reasoning Layer — Determine optimal traffic distribution ratios based on live telemetry, policy, and cost.",
              "Zero-Trust Policy Validation — Verify segmentation, encryption, and identity tagging across paths.",
              "Cloud On-Ramp Integration — Optimize direct connectivity to AWS, Azure, GCP via SD-WAN orchestrators.",
              "Reporting & Compliance Evidence — Generate SLA and cost-savings reports with audit alignment."
            ]
          },
          {
            title: "Integrations",
            content: [
              "SD-WAN Controller / Orchestrator APIs — Retrieve link telemetry, enforce dynamic route weights (Bidirectional).",
              "WAN Edge Devices / Routers — Collect interface and transport metrics (Bidirectional).",
              "Cortex Data Lake / NetFlow / IPFIX — Ingest flow data for utilization analysis (Inbound).",
              "CoreStack GRC / SLA Modules — Store optimization evidence and compliance mapping (Bidirectional).",
              "ServiceNow / Jira — Track balancing adjustments, approvals, and incidents (Bidirectional).",
              "ERP / Cost Management Systems — Ingest carrier billing or bandwidth cost data (Inbound).",
              "Azure OpenAI / RunOps Reasoning Engine — Perform multi-criteria decision reasoning and narrative generation (Inbound).",
              "Confluence / Data Lake — Archive optimization summaries, reports, and audit logs (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Transport Monitoring & Baseline Collection: Gather link performance, utilization, and cost metrics (50%).",
              "Tier 2 — SLA Correlation & Cost Modeling: Evaluate performance vs SLA and compute cost per Mbps (75%).",
              "Tier 3 — AI-Driven Balancing & Policy Enforcement: Adjust routing distribution ratios dynamically (90%).",
              "Tier 4 — Predictive Self-Balancing Governance: Learn and forecast traffic patterns to pre-balance transport utilization (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Hybrid Transport Utilization Balance ≥ 90% — Percentage of optimized load distribution across transports.",
              "Mean Time to Detect (MTTD) Congestion < 2 min — Time from transport degradation to detection.",
              "Mean Time to Adjust (MTTA) < 5 min — Time from detection to redistribution.",
              "MPLS Offload Efficiency ≥ 20% — Volume of traffic shifted from MPLS to broadband/cloud.",
              "Cost Reduction Rate ≥ 10% — Monthly savings via optimized transport usage.",
              "Automation Adoption Rate ≥ 80% — Policy adjustments made autonomously."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Branches Monitored: 152",
              "Transports Evaluated: 486 (MPLS, Broadband, LTE/5G, Cloud Direct Connect)",
              "Dynamic Balancing Events Executed: 1,084",
              "MPLS Offloaded Traffic: 21.8%",
              "Average Latency Improvement: 17.6% across SaaS apps",
              "Monthly Cost Savings: 11.2% of WAN OPEX",
              "SLA Compliance Rate: 98.2%",
              "False Adjustment Rate: 2.3%",
              "MTTD: 1.6 min MTTA: 4.1 min",
              "Automation Adoption: 84%",
              "Engineer Hours Saved: ≈ 270 per month",
              "Audit Reports Generated: 10 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Telemetry Refresh Interval ≤ 60s — Continuous link monitoring.",
              "Balancing Evaluation Cycle ≤ 2 min — Evaluate path health and cost weighting.",
              "Policy Commit to SD-WAN Controller ≤ 5 min — Apply load-balancing changes safely.",
              "Failback to Normal State ≤ 15 min — Auto-normalize after congestion clears.",
              "Evidence Publication ≤ 1 hr — Generate audit & cost reports.",
              "Agent Availability ≥ 99.9% — HA n8n cluster monitored via Grafana."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized \"HybridBalancerAI\" service.",
              "Primary APIs & Protocols — SD-WAN Controller REST APIs, NETCONF/RESTCONF, gNMI telemetry, SNMPv3.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing transport utilization, cost profiles, SLA thresholds.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on WAN economics, transport telemetry, and RunOps network KB.",
              "Security Controls — Encrypted credentials, TLS 1.3, RBAC, token-based auth per controller domain.",
              "Outputs — Hybrid WAN Optimization Report (PDF/JSON), Cost Savings Summary, SLA Compliance Snapshot.",
              "Monitoring — Grafana dashboard visualizing transport load, SLA metrics, and balancing events.",
              "Error Handling — Auto-rollback on degraded performance post-change; fallback to prior routing ratios."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Transport performance/cost changes or scheduled evaluation.",
      workingPlane: "RunOps n8n automation cluster with containerized 'HybridBalancerAI' service.",
      memory: "Persisted transport utilization, cost profiles, and SLA thresholds in Postgres.",
      reasoning: "Azure OpenAI GPT-4o fine-tuned on WAN economics, transport telemetry, and RunOps network KB.",
      execution: "Adjust routing weights, apply balancing changes to SD-WAN controller, and monitor impact with rollback on regression."
    },
    kpis: [
      { label: "Hybrid Transport Utilization Balance", value: "90", unit: "%", target: "≥ 90%", color: "#10b981" },
      { label: "MTTD", value: "1.6", unit: "min", target: "< 2 min", color: "#10b981" },
      { label: "MTTA", value: "4.1", unit: "min", target: "< 5 min", color: "#10b981" },
      { label: "MPLS Offload Efficiency", value: "21.8", unit: "%", target: "≥ 20%", color: "#10b981" },
      { label: "Cost Reduction Rate", value: "11.2", unit: "%", target: "≥ 10%", color: "#10b981" },
      { label: "Automation Adoption", value: "84", unit: "%", target: "≥ 80%", color: "#10b981" }
    ]
  },
  'remote-access-experience-agent': {
    title: "VPN, Remote Access & SASE Remote Access Experience Agent",
    description: "Continuously monitors VPN and SASE user experience by collecting performance, reliability, and satisfaction metrics from end-user devices and gateways. Correlates session telemetry, network conditions, and app performance to proactively detect degradation and improve digital experience for remote workers.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Proactive Experience Monitoring: Detects end-user VPN/SASE performance degradation before tickets arise.",
          "User-Centric Performance Metrics: Measures latency, throughput, and reliability per session, per user.",
          "Reduced MTTR: Correlates issues between device, gateway, and transport for faster resolution.",
          "Operational Efficiency: Automates triage workflows and integrates with ITSM systems.",
          "SLA & Experience Assurance: Provides auditable, quantified digital experience data for compliance and benchmarking."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Traditional network monitoring measures tunnel uptime but lacks visibility into user experience and perceived performance.",
              "Hybrid workforces connect through diverse networks (home Wi-Fi, LTE, coffee shops, corporate VPN), where issues like latency, packet loss, or DNS delays impact productivity and satisfaction.",
              "IT teams need more than tunnel metrics — they need experience intelligence: how users feel the network, not just how it operates.",
              "Without real-time correlation between user sessions, device state, and SASE edge health, organizations face blind spots that lead to support tickets, SLA breaches, and reduced workforce efficiency.",
              "The enterprise requires a self-learning, user-centric agent that bridges network telemetry and human experience metrics."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "Proactive Experience Monitoring: Detects end-user VPN/SASE performance degradation before tickets arise.",
              "User-Centric Performance Metrics: Measures latency, throughput, and reliability per session, per user.",
              "Reduced MTTR: Correlates issues between device, gateway, and transport for faster resolution.",
              "Operational Efficiency: Automates triage workflows and integrates with ITSM systems.",
              "SLA & Experience Assurance: Provides auditable, quantified digital experience data for compliance and benchmarking."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Session Telemetry Collection: Capture per-user latency, jitter, throughput, and reconnect frequency.",
              "Experience Index Modeling: Compute REI (RunOps Experience Index) using weighted user, network, and app KPIs.",
              "Device Performance Correlation: Monitor endpoint CPU, memory, Wi-Fi strength, and network adapters.",
              "Gateway and Edge Path Mapping: Map session routes across SASE PoPs, VPN hubs, and WAN transport types.",
              "AI Root Cause Analysis: Identify problem domain — device, transport, DNS, or gateway.",
              "Alerting & Self-Healing Integration: Feed poor experience alerts into SD-WAN and VPN optimization workflows.",
              "Feedback Loop: Validate optimization outcomes by comparing pre- and post-adjustment REI.",
              "Audit & Reporting: Generate end-user experience trend reports and SLA evidence packages."
            ]
          },
          {
            title: "Integrations",
            content: [
              "VPN / SASE Controllers: Retrieve user session performance and tunnel metrics (Inbound).",
              "Endpoint Telemetry / EDR Systems: Collect local device health and connection data (Inbound).",
              "QoS / Path Selection / Split-Tunnel Agents: Share user experience feedback for adaptive optimization (Bidirectional).",
              "CoreStack GRC / SLA Modules: Store REI metrics and compliance evidence (Bidirectional).",
              "ServiceNow / Jira: Create experience-related incidents and remediation workflows (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Correlate anomalies, determine root cause, and generate insights (Inbound).",
              "Confluence / Data Lake: Archive experience trend reports and SLA compliance data (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "RunOps Experience Index (REI) ≥ 85 / 100 — Weighted average of latency, availability, and user satisfaction.",
              "Mean Time to Detect (MTTD) Degradation < 2 min — Time to identify user-impacting issues.",
              "Mean Time to Resolve (MTTR) < 15 min — Time to restore acceptable REI post-correction.",
              "Automation Adoption Rate ≥ 80% — % of issues resolved autonomously through agent collaboration.",
              "False Positive Rate ≤ 3% — Incorrectly flagged performance degradations.",
              "SLA Compliance (Performance Uptime) ≥ 99.5% — % of sessions maintaining SLA adherence."
            ]
          },
          {
            title: "Representative Statistics",
            content: [
              "Users Monitored: 26,000",
              "VPN/SASE Sessions Tracked: 1.48 million",
              "Average REI (User Experience Index): 88.3 / 100",
              "Detected Degradations: 312 (0.02 % of sessions)",
              "Auto-Remediated via SD-WAN Policy Changes: 241",
              "Average Latency Improvement: 19.1%",
              "Average MTTR: 12.6 min",
              "False Positive Rate: 2.4%",
              "Automation Adoption: 83%",
              "Compliance Rate: 99.7%",
              "Engineer Hours Saved: ≈ 280 per month",
              "Audit Reports Generated: 10 (CoreStack / Confluence)"
            ]
          }
        ]
      },
      {
        title: "4. Service Level Agreements (SLA)",
        subSections: [
          {
            title: "Process SLA",
            content: [
              "Session Data Collection Interval ≤ 60 s — Continuous telemetry from gateways and clients.",
              "Anomaly Detection & Correlation ≤ 2 min — AI correlation across device, transport, and gateway metrics.",
              "Remediation Feedback Execution ≤ 10 min — Path or policy update to resolve user degradation.",
              "Evidence Publication ≤ 1 hr — SLA and REI reports to CoreStack / Confluence.",
              "Agent Availability ≥ 99.9 % — Monitored via Grafana / Prometheus."
            ]
          }
        ]
      },
      {
        title: "5. Technical Context",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized \"UserXAI\" service.",
              "Primary APIs: VPN/SASE REST APIs, EDR/endpoint telemetry, QoS and SD-WAN policy APIs.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing REI baselines, telemetry datasets, and user trend data.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on user experience telemetry, app performance, and RunOps analytics KB.",
              "Security Controls: Encrypted credentials, TLS 1.3, anonymized user data, and RBAC separation.",
              "Outputs: Experience Score Reports (JSON/PDF), Root Cause Analysis, SLA Compliance Summaries.",
              "Monitoring: Grafana dashboards showing REI trends, degradation events, and resolution timelines.",
              "Error Handling: Auto-retry telemetry ingestion; alert SOC if user performance anomaly persists across 3 cycles."
            ]
          }
        ]
      },
      {
        title: "Summary",
        subSections: [
          {
            content: "The Remote Access Experience Agent is the digital experience guardian of the modern hybrid workforce. By unifying network telemetry, device posture, and AI reasoning, it transforms raw tunnel data into actionable experience insights and proactively identifies degradations, orchestrates optimizations, and ensures every remote user enjoys a secure, compliant, and seamless connectivity experience."
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Session telemetry anomalies, periodic baselining, or scheduled experience audits.",
      workingPlane: "Real-time experience plane with correlation, scoring, and feedback loops (cloud/hybrid).",
      memory: "REI baselines, historical telemetry, and remediation outcomes.",
      reasoning: "AI-driven root cause analysis mapping device, transport, and gateway signals to user experience impact.",
      execution: "Actions: raise remediation workflows, trigger policy adjustments, or mark sessions for deeper investigation."
    },
    kpis: [
      { label: "RunOps Experience Index (REI)", value: 88.3, unit: "/100", target: "≥ 85 / 100", color: "#10B981" },
      { label: "MTTD Compliance", value: 100, unit: "%", target: "< 2 min (target)", color: "#3B82F6" },
      { label: "MTTR Compliance", value: 100, unit: "%", target: "< 15 min (target)", color: "#3B82F6" },
      { label: "Automation Adoption", value: 83, unit: "%", target: "≥ 80%", color: "#6366F1" },
      { label: "False Positive Avoidance", value: 97.6, unit: "%", target: "≤ 3% FP (100 - FP%)", color: "#F59E0B" },
      { label: "SLA Compliance", value: 99.7, unit: "%", target: "≥ 99.5%", color: "#06B6D4" }
    ]
  },
  'policy-drift-detector-agent': {
    title: "SD-WAN & WAN Policy Drift Detector Agent",
    description: "Continuously validates SD-WAN configurations and policies across branch, data center, and cloud edges to detect, classify, and remediate policy drift — ensuring all sites remain synchronized with golden templates and zero-trust segmentation baselines.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Configuration Uniformity — Detects and resolves inconsistencies in routing, QoS, and segmentation policies across SD-WAN sites.",
          "Reduced Outages & Misroutes — Prevents service disruption caused by drifted configurations or partial updates.",
          "Regulatory Compliance — Ensures configurations meet PCI, ISO, and NIST control requirements for policy uniformity.",
          "Operational Consistency — Maintains identical traffic-handling behavior across all regions.",
          "Audit & Governance Readiness — Provides continuous compliance evidence and change-traceability reporting."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "As SD-WAN deployments scale, policy drift becomes inevitable — caused by manual changes, version mismatches, or failed template pushes.",
              "Inconsistent configurations lead to application performance discrepancies, security exposure, and noncompliance with enterprise and regulatory standards.",
              "Static audits or periodic syncs fail to catch real-time drift, leaving operational and compliance blind spots across hundreds of branches.",
              "Organizations require an autonomous validation and remediation layer that continuously monitors SD-WAN policy state and enforces uniformity across sites, controllers, and regions."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Configuration Snapshot Collection — Periodically retrieve SD-WAN controller and edge configuration data.",
              "Golden Template Comparison — Compare live site policies against enterprise templates (routing, QoS, firewall, segmentation).",
              "Drift Classification — Categorize drift as compliance, operational, or cosmetic based on business impact.",
              "Cross-Controller Consistency Validation — Verify sync state and configuration parity across HA or regional controllers.",
              "AI Reasoning Layer — Analyze drift patterns to determine cause, risk, and recommended remediation.",
              "Remediation Workflow — Generate patch deltas, schedule resyncs, or automatically restore baselines.",
              "Change Tracking & Evidence Generation — Record every drift detection, classification, and action for audit compliance."
            ]
          },
          {
            title: "Integrations",
            content: [
              "SD-WAN Controller / Orchestrator APIs — Retrieve configurations, templates, and sync states (Bidirectional).",
              "Branch Edge Devices / Gateways — Validate local policy implementation (Inbound).",
              "CoreStack GRC / Compliance Modules — Store evidence and map detected drift to control frameworks (Bidirectional).",
              "ServiceNow / Jira — Create change tasks or escalation tickets for drift remediation (Bidirectional).",
              "Configuration Repositories (Git, Confluence) — Store golden templates and validated baselines (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Classify drift, assess risk, and recommend remediation (Inbound).",
              "Confluence / Data Lake — Archive compliance evidence, drift logs, and audit reports (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Policy Drift Detection Accuracy ≥ 97% — Correct identification of drift events validated by audit.",
              "Mean Time to Detect (MTTD) < 10 min — Time from unauthorized change to detection.",
              "Mean Time to Remediate (MTTR) < 6 hrs — Time to revert or approve detected drift.",
              "False Positive Rate ≤ 3% — Approved changes incorrectly flagged as drift.",
              "Baseline Compliance Rate ≥ 95% — Sites matching golden configuration baseline.",
              "Automation Adoption Rate ≥ 80% — Drift incidents resolved automatically."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Sites Monitored: 158",
              "Controllers / Clusters: 6",
              "Policies Audited: 12,300",
              "Drift Events Detected: 184",
              "- Compliance Drift: 53",
              "- Operational Drift: 107",
              "- Cosmetic Drift: 24",
              "Auto-Remediated: 132 (72%)",
              "Baseline Compliance: 96.1%",
              "Average MTTD: 7.2 min",
              "Average MTTR: 4.8 hrs",
              "False Positives: 2.5%",
              "Audit Evidence Packages Generated: 14",
              "Engineer Hours Saved: ≈ 260 per month"
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Config snapshot, controller event, or scheduled baseline check.",
      workingPlane: "RunOps n8n automation cluster with containerized 'PolicyDriftAI' service.",
      memory: "Golden templates, baseline checksums, and drift history stored in Postgres.",
      reasoning: "Azure OpenAI driven drift classification and remediation recommendation.",
      execution: "Generate remediation deltas, apply patches or open ITSM tickets, and record evidence for audit."
    },
    kpis: [
      { label: "Policy Drift Detection Accuracy", value: "97", unit: "%", target: "≥ 97%", color: "#10b981" },
      { label: "Average MTTD", value: "7.2", unit: "min", target: "< 10 min", color: "#10b981" },
      { label: "Average MTTR", value: "4.8", unit: "hrs", target: "< 6 hrs", color: "#10b981" },
      { label: "Baseline Compliance", value: "96.1", unit: "%", target: "≥ 95%", color: "#10b981" }
    ]
  },
  'session-kill-agent': {
    title: "VPN, Remote Access & SASE Session Kill Agent",
    description: "Monitors active VPN and SASE sessions for compromise indicators — including credential theft, anomalous device behavior, and real-time threat intelligence — and autonomously terminates high-risk or confirmed-compromised sessions to prevent lateral movement, data exfiltration, or unauthorized access.",
    details: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "In the hybrid enterprise, thousands of concurrent VPN and SASE sessions exist at any given time — each representing a potential threat vector if hijacked or misused.",
              "Traditional monitoring tools detect malicious logins but rely on human intervention for response, leading to slow containment and prolonged exposure windows.",
              "Attackers exploit stolen tokens, VPN profiles, and cached credentials to persist within corporate networks.",
              "Enterprises need a real-time, autonomous containment agent capable of detecting and killing compromised sessions immediately — aligned with zero-trust, least-privilege, and continuous verification principles."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Immediate Threat Containment: Terminates active sessions upon detection of compromise indicators.",
              "Zero-Trust Reinforcement: Enforces continuous verification — no session is trusted indefinitely.",
              "Operational Efficiency: Reduces mean time to respond (MTTR) from minutes to seconds.",
              "Incident Impact Reduction: Stops data exfiltration and credential replay in real time.",
              "Compliance Alignment: Automates control enforcement for NIST, ISO, PCI, and SOC 2 access management requirements."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Compromised Session Detection: Identify abnormal login activity, token anomalies, or device posture drift.",
              "Behavioral Risk Monitoring: Monitor continuous session telemetry for deviations from baseline.",
              "Threat-Intel-Driven Termination: Terminate sessions from known malicious IPs, ASNs, or threat-listed origins.",
              "Adaptive Risk Scoring: Reassess ongoing sessions dynamically based on user, device, and application behavior.",
              "Coordinated Response: Integrate with SIEM, SOAR, and identity providers to isolate compromised identities enterprise-wide."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Legacy session monitoring systems rely on manual correlation or rule-based alerts, introducing reaction lag.",
              "The Session Kill Agent applies agentic reasoning for real-time autonomous response:",
              "Continuous Session Awareness: Tracks live sessions across all VPN and SASE edges in real time.",
              "Behavioral Reasoning: Detects anomalies such as geo-velocity, impossible travel, or data-rate spikes.",
              "Cross-Domain Correlation: Combines identity, endpoint, and threat telemetry for high-confidence detection.",
              "Self-Governed Termination Logic: Initiates session kill actions automatically, validated by AI confidence scoring.",
              "Feedback Loop Learning: Learns false-positive scenarios and fine-tunes its decision confidence dynamically."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Session Inventory & Tracking: Continuously track active sessions across VPN gateways, SASE PoPs, and remote devices.",
              "Compromise Detection Engine: Identify high-risk patterns (multiple concurrent logins, credential anomalies, traffic spikes).",
              "Threat Intelligence Correlation: Match session IPs and behaviors against real-time intelligence feeds.",
              "Posture Validation: Verify endpoint compliance (EDR status, encryption, patch level).",
              "AI Decisioning: Determine compromise likelihood via composite risk scoring model.",
              "Session Termination Workflow: Execute kill commands on compromised sessions via controller APIs.",
              "Notification & Coordination: Alert SOC and identity systems (IdP, SIEM, SOAR) for concurrent isolation.",
              "Evidence & Audit Reporting: Document cause, action, and outcome for forensics and compliance."
            ]
          },
          {
            title: "Integrations",
            content: [
              "VPN / SASE Controllers: Monitor sessions and execute termination commands. (Bidirectional)",
              "Identity Providers (Okta, Entra ID, Ping): Validate identity posture and revoke tokens post-kill. (Bidirectional)",
              "Endpoint Protection / EDR Platforms: Validate endpoint trust and detect behavioral anomalies. (Inbound)",
              "Threat Intelligence Platforms (CISA KEV, OTX, CrowdStrike): Correlate IPs, domains, and ASN reputations. (Inbound)",
              "SIEM / SOAR Systems: Coordinate containment and trigger enterprise-level responses. (Bidirectional)",
              "CoreStack GRC / Compliance Modules: Record enforcement actions and map to compliance controls. (Bidirectional)",
              "Azure OpenAI / RunOps Reasoning Engine: Perform risk reasoning and generate narrative justification. (Inbound)",
              "Confluence / Data Lake: Archive session kill events, risk scores, and compliance logs. (Outbound)"
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Session Monitoring & Baseline Learning: Track live sessions and establish normal behavioral patterns. (50%)",
              "Tier 2 — Risk Detection & Correlation: Identify anomalies, correlate with threat intel and identity posture. (75%)",
              "Tier 3 — AI Reasoning & Automated Session Termination: Execute session kills autonomously when confidence threshold met. (90%)",
              "Tier 4 — Adaptive Learning & Continuous Trust Evaluation: Refine thresholds based on false positives and historical analytics. (100% Fully Agentic)"
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Mean Time to Detect (MTTD) < 60 s — From anomaly event to detection.",
              "Mean Time to Terminate (MTTT) < 90 s — From detection to successful session kill.",
              "False Positive Rate ≤ 2% — Legitimate sessions incorrectly terminated.",
              "Containment Success Rate ≥ 98% — Confirmed compromised sessions successfully killed.",
              "Post-Termination Notification SLA ≤ 2 min — Alerts propagated to SOC, IdP, and affected user.",
              "Compliance Evidence Coverage 100% — Session kill actions documented and auditable."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Concurrent Sessions Monitored: 1.3 million",
              "Total Users Protected: 24,600",
              "Detected High-Risk Sessions: 342",
              "Credential Anomalies: 108",
              "Endpoint Posture Violations: 77",
              "Geo-Spoofed or Impossible Travel: 96",
              "Data Exfiltration Attempts: 61",
              "Sessions Terminated Automatically: 298",
              "Containment Success Rate: 97.6%",
              "False Positive Rate: 1.8%",
              "Average MTTD: 42 seconds",
              "Average MTTT: 74 seconds",
              "Audit Reports Generated: 11 (CoreStack / Confluence)",
              "Engineer Hours Saved: ≈ 270 per month"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Anomaly Detection Cycle ≤ 60 s — Continuous telemetry correlation.",
              "Threat Feed Correlation ≤ 1 min — Real-time lookup against intelligence databases.",
              "Session Termination Execution ≤ 90 s — Automated command dispatch and verification.",
              "Notification & Escalation ≤ 2 min — Alert SOC, SIEM, and IdP systems.",
              "Evidence Publication ≤ 1 hr — Compliance reporting and audit trail generation.",
              "Agent Availability ≥ 99.9% — HA n8n cluster monitored by Grafana / Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'SessionKillAI' microservice.",
              "Primary APIs: VPN/SASE REST APIs, IdP Token Revocation, Threat Intelligence Feeds, EDR integrations.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing active session data, risk scores, and policy outcomes.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on behavioral anomaly, credential misuse, and RunOps Zero-Trust KB.",
              "Security Controls: TLS 1.3 encryption, encrypted API credentials, RBAC execution limits, digital signatures for termination logs.",
              "Outputs: Session Termination Log (JSON/PDF), Risk Narrative, Compliance Evidence Snapshot, SOC Alert Summary.",
              "Monitoring: Grafana dashboards showing termination counts, response latency, false positive trends, and SLA adherence.",
              "Error Handling: Retry on controller response failure; fallback to manual SOC escalation if two auto-kill attempts fail."
            ]
          },
          {
            title: "Summary",
            content: [
              "The VPN, Remote Access & SASE Session Kill Agent acts as the autonomous containment layer of the zero-trust architecture — a real-time guardian that continuously validates session trust and terminates compromised connections before data or systems are impacted.",
              "By combining behavioral analytics, threat intelligence, and AI reasoning, it achieves sub-minute response times and eliminates human lag in incident containment.",
              "This agent transforms remote-access security into a self-healing, continuously verifying trust system — ensuring that every active session is both secure and legitimate, all the time."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Session anomaly, threat intelligence event, or continuous telemetry correlation.",
      workingPlane: "RunOps n8n automation cluster (Azure) with containerized 'SessionKillAI' microservice.",
      memory: "Active session inventory, risk scores, and termination history stored in PostgreSQL.",
      reasoning: "Azure OpenAI GPT-4o driven risk scoring and autonomous session kill decisioning.",
      execution: "Dispatch kill commands via controller APIs, notify SOC/IdP/SIEM, and record evidence for audit."
    },
    kpis: [
      { label: "Containment Success Rate", value: "97.6", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Average MTTD", value: "42", unit: "sec", target: "< 60 s", color: "#10b981" },
      { label: "Average MTTT", value: "74", unit: "sec", target: "< 90 s", color: "#10b981" },
      { label: "False Positive Rate", value: "1.8", unit: "%", target: "≤ 2%", color: "#10b981" },
      { label: "Compliance Evidence Coverage", value: "100", unit: "%", target: "100%", color: "#10b981" }
    ],
    isDetailViewEnabled: true
  },
  'dynamic-dns-update-agent': {
    title: "DNS, DHCP & IPAM (DDI) Dynamic DNS Update Agent",
    description: "Continuously monitors cloud and virtual infrastructure events to synchronize DNS records in real time, ensuring dynamic workloads are accurately reflected across enterprise DNS zones.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Real-Time DNS Accuracy — Reflects live cloud and VM state across all DNS zones instantly.",
          "Operational Agility — Automates record creation and deletion as workloads scale dynamically.",
          "Reduced Outage Risk — Prevents stale DNS entries from causing failed resolutions or misroutes.",
          "Cross-Cloud Consistency — Unifies DNS visibility across on-prem, hybrid, and multi-cloud environments.",
          "Compliance & Audit Assurance — Maintains traceability and version control for all DNS updates."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Cloud and containerized workloads spin up, migrate, and terminate dynamically — often faster than manual DNS updates can keep pace.",
              "Static DNS processes lead to resolution failures, orphaned entries, and stale service endpoints, impacting application availability and automation pipelines.",
              "Without continuous synchronization between DNS and cloud environments (AWS, Azure, GCP, Kubernetes), enterprises lose visibility and violate service discovery and zero-trust policies.",
              "Organizations need an autonomous, cross-cloud DNS synchronization agent that updates records instantly, maintains consistency, and enforces security and compliance for ephemeral workloads."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Real-Time DNS Accuracy: Reflects live cloud and VM state across all DNS zones instantly.",
              "Operational Agility: Automates record creation and deletion as workloads scale dynamically.",
              "Reduced Outage Risk: Prevents stale DNS entries from causing failed resolutions or misroutes.",
              "Cross-Cloud Consistency: Unifies DNS visibility across on-prem, hybrid, and multi-cloud environments.",
              "Compliance & Audit Assurance: Maintains traceability and version control for all DNS updates."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Cloud Event Monitoring: Subscribe to real-time infrastructure events (launch, terminate, scale) across major clouds and Kubernetes.",
              "DNS Record Automation: Create, update, or delete A, AAAA, PTR, and CNAME records as workloads change.",
              "Zone Mapping & Policy Enforcement: Apply DNS naming conventions and zone placement rules dynamically.",
              "Cross-DDI Synchronization: Align updates across on-prem DNS, IPAM, and cloud-native DNS zones.",
              "Reverse Zone Validation: Maintain forward/reverse record parity for all updates.",
              "AI Reasoning Engine: Determine record creation priorities, dependencies, and validation sequence.",
              "Audit Trail & Compliance Logging: Record all update activities and version deltas for audit evidence.",
              "Error Detection & Self-Recovery: Detect failed updates, retry automatically, and reconcile zone discrepancies."
            ]
          },
          {
            title: "Integrations",
            content: [
              "DDI Platforms (Infoblox, BlueCat, EfficientIP) — apply DNS changes and sync zone data (Bidirectional).",
              "Cloud Providers (AWS Route53, Azure DNS, GCP Cloud DNS) — detect workload events and apply DNS updates (Bidirectional).",
              "Container Platforms (Kubernetes, OpenShift) — register or remove service endpoints dynamically (Bidirectional).",
              "IPAM Systems — correlate IP allocations and prevent duplicate entries (Bidirectional).",
              "CoreStack GRC / Compliance Modules — store audit trails and compliance mappings (Bidirectional).",
              "ServiceNow / Jira — track automation actions, exceptions, and approvals (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — interpret event metadata and determine appropriate DNS action (Inbound).",
              "Confluence / Data Lake — archive update logs, zone diffs, and audit reports (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Cloud Event Monitoring & Discovery: Listen for instance lifecycle events and detect new endpoints. (50%)",
              "Tier 2 — DNS Update & Validation: Create, modify, or remove DNS records and verify integrity. (75%)",
              "Tier 3 — AI Reasoning & Cross-System Sync: Correlate workloads across multiple DDI systems and enforce consistency. (90%)",
              "Tier 4 — Predictive Elasticity & Self-Healing DNS: Anticipate scaling events and pre-stage DNS updates proactively. (100% Fully Agentic)"
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "DNS Synchronization Accuracy ≥ 99 % — Alignment between DNS and cloud instance inventories.",
              "Mean Time to Update (MTTU) < 2 min — Time from workload event to DNS record creation/deletion.",
              "Record Validation Success Rate ≥ 97 % — DNS record propagation verified by query success.",
              "Automation Adoption Rate ≥ 85 % — Percentage of updates executed autonomously.",
              "Zone Drift Detection Accuracy ≥ 98 % — Detection and reconciliation of misaligned zone records.",
              "Compliance Evidence Coverage 100 % — Audit logging completeness and reporting accuracy."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Total DNS Zones Monitored: 460",
              "Cloud Accounts Integrated: 18 (AWS, Azure, GCP)",
              "Dynamic Workload Events Processed: 86,000",
              "DNS Records Created / Updated / Deleted: 74,300 / 9,120 / 2,580",
              "Average MTTU: 1.6 minutes",
              "Synchronization Accuracy: 99.2 %",
              "Record Validation Success: 97.8 %",
              "Automation Adoption Rate: 88 %",
              "Zone Drift Events Reconciled: 184",
              "Engineer Hours Saved: ≈ 350 per month",
              "Audit Reports Generated: 11 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Cloud Event to DNS Update (MTTU) ≤ 2 min — From workload launch or termination to DNS record update.",
              "Zone Validation & Sync Cycle ≤ 5 min — Full DDI verification and propagation.",
              "Error Correction & Retry Window ≤ 10 min — Automatic reconciliation of failed updates.",
              "Evidence Publication ≤ 1 hr — Compliance report export to GRC / Confluence.",
              "Agent Availability ≥ 99.9 % — HA RunOps cluster monitored via Grafana / Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'DynamicDNSAI' microservice.",
              "Primary Interfaces: DDI REST APIs, cloud provider event buses (SNS, EventGrid, Pub/Sub), Kubernetes API.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing instance mappings, zone records, and change logs.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on DDI synchronization logic, multi-cloud DNS governance, and RunOps KB.",
              "Security Controls: TLS 1.3 encryption, API key vaulting, RBAC-per-zone control, DNSSEC validation.",
              "Outputs: Dynamic DNS Update Report (PDF/JSON), Zone Drift Log, Compliance Evidence Snapshot.",
              "Monitoring: Grafana dashboards visualizing update latency, sync accuracy, and zone drift frequency.",
              "Error Handling: Auto-retry failed API updates; revert record changes if post-validation fails."
            ]
          },
          {
            title: "Summary",
            content: [
              "The Dynamic DNS Update Agent acts as the real-time synchronization brain for the enterprise DNS ecosystem.",
              "By combining multi-cloud event awareness, AI reasoning, and autonomous record management, it ensures every workload is accurately registered, discoverable, and compliant.",
              "Through predictive DNS updates, continuous validation, and automated cross-system synchronization, the agent transforms DNS into a self-updating, self-healing infrastructure service."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Cloud or workload lifecycle event, scheduled inventory refresh, or conflict detection.",
      workingPlane: "RunOps n8n automation cluster with containerized 'DynamicDNSAI' microservice.",
      memory: "Instance mappings, zone diffs, and update logs in PostgreSQL.",
      reasoning: "Azure OpenAI GPT-4o driven mapping of workload attributes to DNS zones and validation sequences.",
      execution: "Create/update/delete DNS records, reconcile zone discrepancies, record audit evidence, and notify stakeholders."
    },
    kpis: [
      { label: "DNS Synchronization Accuracy", value: "99.2", unit: "%", target: "≥ 99%", color: "#10b981" },
      { label: "Average MTTU", value: "1.6", unit: "min", target: "< 2 min", color: "#10b981" },
      { label: "Record Validation Success", value: "97.8", unit: "%", target: "≥ 97%", color: "#10b981" },
      { label: "Automation Adoption", value: "88", unit: "%", target: "≥ 85%", color: "#10b981" },
      { label: "Zone Drift Detection", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" }
    ],
    isDetailViewEnabled: true
  },
  'ipam-sync-agent': {
    title: "DNS, DHCP & IPAM (DDI) IPAM Sync Agent",
    description: "Continuously synchronizes IP address allocations and ownership data between DHCP servers, DNS zones, and IPAM databases, ensuring real-time accuracy, eliminating conflicts, and maintaining compliance with enterprise inventory and zero-trust visibility standards.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Accurate IP Inventory — Maintains a single source of truth across DNS, DHCP, and IPAM systems.",
          "Reduced Operational Risk — Prevents duplicate, orphaned, or stale IP assignments that can cause outages.",
          "Enhanced Visibility & Governance — Provides end-to-end traceability of IP ownership and usage.",
          "Operational Efficiency — Automates IP reconciliation tasks across hybrid and multi-cloud environments.",
          "Compliance & Audit Alignment — Ensures network asset inventory accuracy for ISO, PCI, and NIST requirements."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprise IP address environments evolve rapidly, with overlapping DHCP, DNS, IPAM, and cloud-native networks leading to inventory drift, address conflicts, and visibility gaps.",
              "Manual reconciliation or scheduled syncs between DDI platforms often result in stale records and orphaned allocations, breaking resolution integrity and compliance baselines.",
              "Lack of real-time synchronization leads to shadow addressing, duplicate IP usage, and inaccurate CMDB mappings.",
              "Enterprises require an autonomous, continuously reasoning synchronization agent that ensures every IP address is correctly represented and aligned across all network data sources."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Accurate IP Inventory: Maintains a single source of truth across DNS, DHCP, and IPAM systems.",
              "Reduced Operational Risk: Prevents duplicate, orphaned, or stale IP assignments that can cause outages.",
              "Enhanced Visibility & Governance: Provides end-to-end traceability of IP ownership and usage.",
              "Operational Efficiency: Automates IP reconciliation tasks across hybrid and multi-cloud environments.",
              "Compliance & Audit Alignment: Ensures network asset inventory accuracy for ISO, PCI, and NIST requirements."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "IP Inventory Aggregation: Continuously collect address data from DHCP, DNS, IPAM, and cloud sources.",
              "Conflict & Drift Detection: Identify mismatched records across systems (duplicate, orphaned, or stale IPs).",
              "Authoritative Source Resolution: Determine which system holds the correct record using policy rules or AI reasoning.",
              "Auto-Remediation & Synchronization: Update non-authoritative systems to reflect current truth.",
              "Change Propagation: Push updates to all relevant DNS and DHCP servers after reconciliation.",
              "Policy Compliance Validation: Ensure IP ranges follow reserved, static, or dynamic allocation standards.",
              "Audit Logging & Evidence Retention: Record all sync actions, drift detections, and resolution decisions."
            ]
          },
          {
            title: "Integrations",
            content: [
              "DDI Platforms (Infoblox, BlueCat, EfficientIP): Retrieve and update IPAM, DNS, and DHCP data. (Bidirectional)",
              "Cloud Providers (AWS, Azure, GCP): Discover and reconcile IPs across virtual networks and subnets. (Bidirectional)",
              "DHCP Lease Agent: Cross-verify active leases and usage patterns. (Bidirectional)",
              "DNS Hygiene Agent: Validate DNS record-to-IP associations. (Bidirectional)",
              "CMDB / Asset Management Systems (ServiceNow, Lansweeper): Map IPs to devices, owners, and service relationships. (Bidirectional)",
              "CoreStack GRC / Compliance Modules: Store audit artifacts and policy alignment evidence. (Bidirectional)",
              "ServiceNow / Jira: Manage exception handling and conflict resolution workflows. (Bidirectional)",
              "Azure OpenAI / RunOps Reasoning Engine: Analyze discrepancies, assign authoritative truth, and generate reconciliation recommendations. (Inbound)",
              "Confluence / Data Lake: Archive sync reports, IP utilization metrics, and compliance records. (Outbound)"
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — IP Inventory Collection & Baseline Mapping: Aggregate IP data from all authoritative and shadow sources. (50%)",
              "Tier 2 — Drift Detection & Conflict Analysis: Identify and classify inconsistencies or duplicate assignments. (75%)",
              "Tier 3 — AI Reasoning & Synchronization Enforcement: Execute reconciliation actions and update non-authoritative systems. (90%)",
              "Tier 4 — Predictive Reconciliation & Self-Learning Governance: Forecast potential conflicts and pre-synchronize changes. (100% Fully Agentic)"
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "IP Inventory Accuracy Rate ≥ 99 % — % of aligned records across DNS, DHCP, IPAM, and cloud sources.",
              "Mean Time to Detect (MTTD) Conflicts < 2 hrs — Time to identify new inconsistencies post-change.",
              "Mean Time to Reconcile (MTTR) < 4 hrs — Time from detection to synchronization completion.",
              "Conflict Resolution Accuracy ≥ 97 % — Correct authoritative resolution of duplicate records.",
              "Automation Adoption Rate ≥ 85 % — % of sync and remediation tasks executed autonomously.",
              "Compliance Evidence Coverage 100 % — All sync actions logged and archived for audit."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Total IPs Managed: 1.8 million",
              "DDI Systems Integrated: 7 (Infoblox, BlueCat, EfficientIP, AWS, Azure, GCP, ServiceNow CMDB)",
              "Detected Conflicts / Drift Events: 5,260",
              "Duplicate Assignments – 2,130",
              "Orphaned Records – 1,940",
              "Cloud-IP Mismatch – 1,190",
              "Reconciliations Executed Automatically: 4,460 (84.8%)",
              "Average MTTD: 1.9 hrs",
              "Average MTTR: 3.6 hrs",
              "Conflict Resolution Accuracy: 97.3 %",
              "IP Inventory Accuracy Rate: 99.1 %",
              "Automation Adoption Rate: 86 %",
              "Engineer Hours Saved: ≈ 340 per month",
              "Audit Reports Generated: 12 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Inventory Refresh Cycle ≤ 2 hrs — Continuous sync from all data sources.",
              "Conflict Detection & Classification ≤ 1 hr — AI correlation and validation of mismatches.",
              "Reconciliation & Update Execution ≤ 4 hrs — Includes cross-system update propagation.",
              "Evidence Publication ≤ 1 hr — Compliance and audit report generation.",
              "Agent Availability ≥ 99.9 % — HA n8n cluster monitored via Grafana / Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'IPAMSyncAI' microservice.",
              "Primary Interfaces: DDI REST APIs, Cloud Inventory APIs, CMDB integrations.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing IP inventory snapshots, drift records, and reconciliation logs.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on IP lifecycle management, conflict detection, and RunOps network KB.",
              "Security Controls: TLS 1.3 encryption, signed API transactions, credential vaulting, RBAC policy enforcement.",
              "Outputs: IP Reconciliation Report (JSON/PDF), Conflict Summary, Compliance Snapshot.",
              "Monitoring: Grafana dashboards visualizing sync frequency, drift detection, and reconciliation success.",
              "Error Handling: Auto-rollback on sync failure; flag unresolved conflicts for manual approval if two reconciliation attempts fail."
            ]
          },
          {
            title: "Summary",
            content: [
              "The IPAM Sync Agent serves as the autonomous truth enforcer for enterprise DDI environments — ensuring every IP address, across every system, remains accurate, conflict-free, and compliant.",
              "By merging AI-driven reasoning, cross-platform correlation, and self-healing automation, it eliminates inventory drift, prevents address conflicts, and delivers end-to-end visibility.",
              "This agent transforms IP lifecycle management into a continuously synchronized, zero-trust-aligned, self-regulating DDI ecosystem, guaranteeing operational integrity and compliance across on-premises and cloud domains."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Change event, conflict detection, or scheduled inventory refresh.",
      workingPlane: "RunOps n8n automation cluster (Azure) with containerized 'IPAMSyncAI' microservice.",
      memory: "IP snapshots, conflict history, and reconciliation logs stored in PostgreSQL.",
      reasoning: "Azure OpenAI GPT-4o driven authoritative-source selection and reconciliation planning.",
      execution: "Propagate synchronized updates to DNS/DHCP/IPAM systems, record audit evidence, and notify stakeholders."
    },
    kpis: [
      { label: "IP Inventory Accuracy Rate", value: "99.1", unit: "%", target: "≥ 99%", color: "#10b981" },
      { label: "Average MTTD", value: "1.9", unit: "hrs", target: "< 2 hrs", color: "#10b981" },
      { label: "Average MTTR", value: "3.6", unit: "hrs", target: "< 4 hrs", color: "#10b981" },
      { label: "Conflict Resolution Accuracy", value: "97.3", unit: "%", target: "≥ 97%", color: "#10b981" },
      { label: "Automation Adoption", value: "86", unit: "%", target: "≥ 85%", color: "#10b981" }
    ],
    isDetailViewEnabled: true
  },
  'dns-sinkhole-agent': {
    title: "DNS, DHCP & IPAM (DDI) DNS Sinkhole Agent",
    description: "Continuously ingests threat intelligence feeds to detect malicious, suspicious, or command-and-control domains in real time and dynamically redirects them to safe sinkhole zones — preventing users and systems from resolving or communicating with hostile destinations.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Real-Time DNS Threat Containment — Blocks malicious domains before connections are established.",
          "Dynamic Threat Intelligence Integration — Continuously updates sinkhole lists from multiple feeds.",
          "Reduced Dwell Time for Threats — Stops C2 callbacks, preventing malware persistence and data leakage.",
          "Zero-Trust DNS Enforcement — Ensures DNS requests are resolved only to trusted destinations.",
          "Audit & Compliance Assurance — Maintains logs and mapping evidence for threat containment activities."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "DNS remains one of the most exploited protocols in enterprise environments — leveraged for malware callbacks, data exfiltration, and phishing.",
              "Static DNS blacklists are slow to update, decentralized, and fail to keep pace with dynamic threats.",
              "Manual response to new malicious domains introduces unacceptable delays in blocking C2 or ransomware propagation.",
              "Enterprises require an agentic, continuously updating sinkhole control system that integrates threat feeds, IPAM/DNS infrastructure, and AI-driven risk reasoning to block bad domains within seconds while maintaining resolution performance and reliability."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Real-Time DNS Threat Containment: Blocks malicious domains before connections are established.",
              "Dynamic Threat Intelligence Integration: Continuously updates sinkhole lists from multiple feeds.",
              "Reduced Dwell Time for Threats: Stops C2 callbacks, preventing malware persistence and data leakage.",
              "Zero-Trust DNS Enforcement: Ensures DNS requests are resolved only to trusted destinations.",
              "Audit & Compliance Assurance: Maintains logs and mapping evidence for threat containment activities."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Malicious Domain Sinkholing: Redirect known C2 or phishing domains to safe internal zones or null responses.",
              "Threat Intel Correlation: Ingest threat feeds (e.g., AlienVault OTX, MISP, CISA KEV) to update blocklists.",
              "User or Host Risk Mitigation: Identify which internal devices attempted to resolve blocked domains.",
              "Cross-Domain Coordination: Inform SIEM, SOAR, and Firewall Agents of new sinkhole events for broader containment.",
              "Audit & Compliance Reporting: Generate evidence reports showing blocked queries and correlated threat sources."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Traditional DNS security depends on static reputation feeds and manual blacklisting — slow, reactive, and siloed.",
              "The DNS Sinkhole Agent introduces agentic intelligence, context-aware risk evaluation, and cross-platform orchestration:",
              "Dynamic Awareness: Continuously ingests and normalizes threat intelligence from multiple trusted sources.",
              "AI Reasoning: Weighs reputation, frequency, and domain age to assign confidence scores before blacklisting.",
              "Autonomous Enforcement: Adds or removes sinkhole redirections automatically based on live threat telemetry.",
              "Cross-Platform Containment: Notifies EDR, firewall, and proxy systems of DNS blocks for correlated action.",
              "Learning Feedback: Analyzes false positives and adjusts reputation thresholds dynamically."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Threat Feed Ingestion: Collect domain intelligence from public, commercial, and internal feeds.",
              "Domain Risk Scoring: Use AI-driven reasoning to classify domains (malicious, suspicious, benign).",
              "DNS Sinkhole Enforcement: Automatically add malicious domains to internal DNS sinkhole zones.",
              "Resolution Redirection: Respond to malicious queries with safe IPs or NXDOMAIN responses.",
              "Query Logging & Attribution: Record which clients attempted resolution for forensic investigation.",
              "Integration with DDI Systems: Sync changes across DNS, DHCP, and IPAM for consistency.",
              "Incident Correlation: Forward sinkhole hits to SIEM and SOAR for containment workflows.",
              "Reporting & Compliance Logging: Produce reports detailing blocked domains and resolved incidents."
            ]
          },
          {
            title: "Integrations",
            content: [
              "DDI Platforms (Infoblox, BlueCat, EfficientIP): Manage DNS zone updates, A-record manipulation, and logging. (Bidirectional)",
              "Threat Intelligence Feeds (CISA KEV, OTX, MISP, CrowdStrike Falcon Intel): Ingest malicious domain data and reputation scores. (Inbound)",
              "SIEM / SOAR Systems (Splunk, Sentinel, QRadar): Share sinkhole events and client activity for correlated response. (Bidirectional)",
              "Firewall / Proxy Systems (Palo Alto, Zscaler, SWG): Block corresponding IPs or URLs at Layer 7. (Bidirectional)",
              "CoreStack GRC / Compliance Modules: Store sinkhole logs and compliance mappings. (Bidirectional)",
              "ServiceNow / Jira: Log containment actions, domain blocks, and investigation workflows. (Bidirectional)",
              "Azure OpenAI / RunOps Reasoning Engine: Classify domain risk and generate action justification. (Inbound)",
              "Confluence / Data Lake: Archive sinkhole reports, telemetry logs, and audit records. (Outbound)"
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Threat Feed Ingestion & Domain Scoring: Collect and evaluate intelligence data from multiple feeds. (50%)",
              "Tier 2 — DNS Sinkhole Enforcement: Apply redirection or null response policies across DNS zones. (75%)",
              "Tier 3 — AI Reasoning & Cross-System Correlation: Identify root cause, assess confidence level, and trigger response actions. (90%)",
              "Tier 4 — Continuous Learning & Predictive Threat Prevention: Learn from recurring attack patterns and pre-stage sinkhole updates. (100% Fully Agentic)"
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Threat Feed Update Frequency ≤ 15 min — Interval between feed ingestions.",
              "Mean Time to Contain (MTTC) < 5 min — Time from malicious domain detection to sinkhole enforcement.",
              "Sinkhole Hit Attribution Accuracy ≥ 97 % — Correctly mapping clients that queried blocked domains.",
              "False Positive Rate ≤ 3 % — Benign domains incorrectly sinkholed.",
              "Blocked Domain Coverage ≥ 99 % — Known malicious domains intercepted before resolution.",
              "Automation Adoption Rate ≥ 85 % — Sinkhole actions executed autonomously."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Threat Feeds Integrated: 9 (CISA KEV, OTX, CrowdStrike, MISP, etc.)",
              "Malicious Domains Processed: 86,400",
              "Domains Sinkholed: 72,580 (83.9%)",
              "Average MTTC: 3.2 minutes",
              "Sinkhole Query Events Logged: 214,000",
              "False Positives: 2.4 %",
              "Blocked C2 Callback Attempts: 1,142",
              "Cross-Alert Events Sent to SIEM: 19,680",
              "Automation Adoption Rate: 87 %",
              "Compliance Evidence Coverage: 100 %",
              "Engineer Hours Saved: ≈ 340 per month",
              "Audit Reports Generated: 10 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Threat Feed Refresh Cycle ≤ 15 min — Pull and normalize from all active intelligence sources.",
              "Detection to Enforcement (MTTC) ≤ 5 min — Automated DNS zone update and replication.",
              "Cross-System Event Propagation ≤ 10 min — Notification to SIEM, SOAR, and Firewall Agents.",
              "Evidence Publication ≤ 1 hr — Compliance and audit report generation.",
              "Agent Availability ≥ 99.9 % — HA n8n cluster monitored via Grafana / Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized \"DNSSinkAI\" microservice.",
              "Primary Interfaces: DDI platform APIs (Infoblox, BlueCat), REST threat intel feeds, DNS logs, SIEM integrations.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing domain risk scores, blocklists, and sinkhole activity.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on domain reputation analysis, threat patterns, and RunOps DNS Security KB.",
              "Security Controls: TLS 1.3 encryption, API key vaulting, RBAC-per-domain access, DNSSEC enforcement validation.",
              "Outputs: Sinkhole Enforcement Report (JSON/PDF), Domain Risk Summary, Compliance Snapshot.",
              "Monitoring: Grafana dashboards visualizing feed freshness, sinkhole hit frequency, and MTTC performance.",
              "Error Handling: Auto-retry on DNS update replication; fallback to cached blocklist on API unavailability."
            ]
          },
          {
            title: "Summary",
            content: [
              "The DNS Sinkhole Agent acts as the autonomous DNS containment engine of the enterprise network.",
              "By combining real-time threat intelligence, AI-driven risk evaluation, and automated DNS enforcement, it neutralizes domain-based threats before connections can occur.",
              "Through continuous learning, cross-domain collaboration (SIEM, SOAR, Firewall Agents), and regulatory audit alignment, it transforms DNS from a passive resolver into a self-learning, zero-trust-aligned threat defense system, ensuring that every query — from any device, anywhere — is verified, contained, and compliant."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Threat feed update, anomalous query spike, or heuristic detection.",
      workingPlane: "RunOps n8n automation cluster (Azure) with containerized 'DNSSinkAI' microservice.",
      memory: "Domain risk scores, feed history, and sinkhole actions stored in PostgreSQL.",
      reasoning: "Azure OpenAI GPT-4o driven domain reputation evaluation and action justification.",
      execution: "Apply DNS zone updates, notify DDI platforms and SIEM, and record enforcement evidence for audit."
    },
    kpis: [
      { label: "Average MTTC", value: "3.2", unit: "min", target: "< 5 min", color: "#10b981" },
      { label: "Domains Sinkholed", value: "72580", unit: "", target: ">= 99% coverage (target)", color: "#10b981" },
      { label: "Sinkhole Hit Attribution Accuracy", value: "97", unit: "%", target: "≥ 97%", color: "#10b981" },
      { label: "False Positive Rate", value: "2.4", unit: "%", target: "≤ 3%", color: "#10b981" }
    ],
    isDetailViewEnabled: true
  },
  'dns-hygiene-agent': {
    title: "DNS, DHCP & IPAM (DDI) DNS Hygiene Agent",
    description: "Continuously audits, validates, and cleans stale, unused, or misaligned DNS records across enterprise zones — ensuring resolution accuracy, reducing attack surface, and maintaining compliance with DNS lifecycle and zero-trust policies.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Improved DNS Integrity — Removes stale and duplicate records, reducing query failures and misroutes.",
          "Reduced Attack Surface — Eliminates orphaned records that could be exploited for DNS hijacking or spoofing.",
          "Operational Efficiency — Automates routine cleanup tasks and DNS lifecycle management.",
          "Performance Optimization — Improves zone file health and reduces resolver latency.",
          "Compliance & Governance — Provides audit-ready evidence of DNS record lifecycle controls."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Over time, enterprise DNS environments accumulate stale, orphaned, and inconsistent records due to decommissioned systems, cloud migrations, or neglected automation workflows.",
              "These “ghost” records increase risk of DNS hijacking, zone pollution, and misrouting while degrading query performance.",
              "Manual audits are infrequent, error-prone, and lack correlation with live DHCP leases, IPAM allocations, and asset inventories.",
              "Organizations need an autonomous, agentic hygiene process that continuously validates DNS integrity, reconciles with authoritative data sources, and cleans up unused or rogue entries — safely and automatically."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Improved DNS Integrity: Removes stale and duplicate records, reducing query failures and misroutes.",
              "Reduced Attack Surface: Eliminates orphaned records that could be exploited for DNS hijacking or spoofing.",
              "Operational Efficiency: Automates routine cleanup tasks and DNS lifecycle management.",
              "Performance Optimization: Improves zone file health and reduces resolver latency.",
              "Compliance & Governance: Provides audit-ready evidence of DNS record lifecycle controls."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Stale Record Detection: Identify DNS entries without corresponding IP, DHCP lease, or asset record.",
              "Orphaned & Duplicate Cleanup: Remove records tied to decommissioned or conflicting hosts.",
              "Zone Drift Detection: Detect discrepancies between forward and reverse zones or between internal/external DNS.",
              "Lifecycle Enforcement: Apply TTL, aging, and auto-expiration policies dynamically.",
              "Compliance & Audit Logging: Maintain historical record of all cleanup, validation, and enforcement actions."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Traditional DNS cleanup relies on manual zone audits or rigid scripts that lack context or intelligence.",
              "The DNS Hygiene Agent adds agentic reasoning, contextual awareness, and automated governance to ensure DNS health and compliance:",
              "Cross-Domain Awareness: Correlates DNS entries with DHCP leases, IPAM data, CMDB, and cloud inventories.",
              "AI Reasoning: Differentiates between temporarily inactive records and truly orphaned or risky ones.",
              "Safe, Autonomous Cleanup: Executes deletions only after multi-source validation.",
              "Self-Healing Governance: Recreates or corrects misaligned records automatically when authoritative data is available.",
              "Continuous Learning: Adapts to environment patterns and avoids repetitive false positives."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "DNS Record Inventory: Continuously retrieve and baseline all A, AAAA, CNAME, PTR, and TXT records across zones.",
              "Stale/Orphaned Record Detection: Identify entries with no matching lease, asset, or active IP in IPAM.",
              "Forward–Reverse Correlation: Ensure consistency between forward and reverse lookup zones.",
              "Duplicate & Conflict Identification: Detect multiple records resolving to the same IP or name.",
              "Policy-Driven Cleanup: Remove or quarantine non-compliant entries automatically based on TTL or validation thresholds.",
              "Audit Trail & Versioning: Maintain before/after snapshots of zone changes for traceability.",
              "AI Reasoning Layer: Validate anomalies across DDI datasets and recommend cleanup priority.",
              "Reporting & Compliance Evidence: Generate DNS health reports mapped to operational and security KPIs."
            ]
          },
          {
            title: "Integrations",
            content: [
              "DDI Platforms (Infoblox, BlueCat, EfficientIP): Manage DNS zones, perform queries, and execute record cleanups. (Bidirectional)",
              "DHCP Systems / IPAM Repositories: Correlate DNS records with lease and IP allocation data. (Bidirectional)",
              "Asset & CMDB Systems (ServiceNow, Lansweeper): Validate if DNS entries correspond to active assets. (Inbound)",
              "Cloud Inventories (AWS Route53, Azure DNS, GCP DNS): Audit hybrid DNS records and ensure consistency. (Bidirectional)",
              "CoreStack GRC / Compliance Modules: Store audit evidence and control mappings. (Bidirectional)",
              "ServiceNow / Jira: Manage cleanup approvals and change requests. (Bidirectional)",
              "Azure OpenAI / RunOps Reasoning Engine: Analyze anomalies, assess record risk, and produce remediation narratives. (Inbound)",
              "Confluence / Data Lake: Archive DNS hygiene reports, zone snapshots, and compliance evidence. (Outbound)"
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — DNS Record Inventory & Baseline Validation: Collect and compare DNS zone data with authoritative sources. (50%)",
              "Tier 2 — Anomaly Detection & Cross-Validation: Detect stale, orphaned, or duplicate entries via DDI correlation. (75%)",
              "Tier 3 — AI Reasoning & Autonomous Cleanup: Validate risk, apply TTL policies, and safely execute deletions. (90%)",
              "Tier 4 — Predictive & Continuous Hygiene Optimization: Learn recurring stale patterns and preemptively enforce cleanup cycles. (100% Fully Agentic)"
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "DNS Hygiene Compliance Rate ≥ 98 % — % of zones without stale or orphaned records.",
              "Mean Time to Detect (MTTD) Stale Records < 4 hrs — Time to detect DNS record drift or orphaned entries.",
              "Mean Time to Clean (MTTC) < 8 hrs — Time from detection to safe deletion or correction.",
              "False Positive Rate ≤ 3 % — Valid records incorrectly flagged as stale.",
              "Query Success Rate Improvement ≥ 10 % — Increase in DNS query success post-cleanup.",
              "Automation Adoption Rate ≥ 85 % — Cleanup actions executed autonomously."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "DNS Zones Audited: 420",
              "Records Evaluated: 1.26 million",
              "Stale/Orphaned Records Detected: 82,400 (6.5%)",
              "Records Cleaned Automatically: 67,800 (82.3%)",
              "Average MTTD: 3.1 hours",
              "Average MTTC: 6.7 hours",
              "False Positive Rate: 2.2 %",
              "Query Success Improvement: 12.8 %",
              "Compliance Rate (Post-Cleanup): 98.6 %",
              "Automation Adoption: 86 %",
              "Engineer Hours Saved: ≈ 320 per month",
              "Audit Reports Generated: 14 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Inventory & Baseline Refresh Cycle ≤ 4 hrs — Continuous zone polling and DDI sync.",
              "Anomaly Detection & Validation Cycle ≤ 2 hrs — Cross-validation against DHCP/IPAM and CMDB.",
              "Cleanup Execution Window ≤ 8 hrs — Automated record removal during low-traffic periods.",
              "Evidence Publication ≤ 1 hr — Compliance and audit report generation.",
              "Agent Availability ≥ 99.9 % — HA n8n cluster monitored by Grafana / Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized \"DNSHygieneAI\" microservice.",
              "Primary Interfaces: DDI REST APIs, DNS query logs, IPAM repositories, CMDB connectors.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing DNS baseline states, anomalies, and cleanup actions.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on DNS lifecycle governance, stale record detection, and RunOps compliance KB.",
              "Security Controls: TLS 1.3 encryption, role-based access, signed DNS update transactions, versioned rollback.",
              "Outputs: DNS Hygiene Report (PDF/JSON), Zone Compliance Snapshot, Cleanup Change Log.",
              "Monitoring: Grafana dashboards showing stale record trends, cleanup rate, and zone health index.",
              "Error Handling: Auto-rollback on failed DNS updates; alert DDI admin for manual validation on repeated anomalies."
            ]
          },
          {
            title: "Summary",
            content: [
              "The DNS Hygiene Agent acts as the self-cleaning intelligence layer of enterprise DNS operations.",
              "By combining cross-DDI data correlation, AI reasoning, and safe automation, it eliminates stale, unused, and misaligned records while improving DNS performance and integrity.",
              "Through its continuous auditing, dynamic cleanup, and compliance reporting, the agent transforms DNS management into a proactive, self-maintaining hygiene system — ensuring that every record in every zone is accurate, compliant, and trustworthy."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Scheduled inventory, anomaly detection, or CMDB change event.",
      workingPlane: "RunOps n8n automation cluster (Azure) with containerized 'DNSHygieneAI' microservice.",
      memory: "DNS baseline snapshots, anomaly history, and cleanup logs stored in PostgreSQL.",
      reasoning: "Azure OpenAI GPT-4o driven DNS hygiene reasoning and safe-cleanup planning.",
      execution: "Validate via multi-source checks, execute safe deletions or quarantines via DDI APIs, and record evidence for audit."
    },
    kpis: [
      { label: "DNS Hygiene Compliance Rate", value: "98.6", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Average MTTD", value: "3.1", unit: "hrs", target: "< 4 hrs", color: "#10b981" },
      { label: "Average MTTC", value: "6.7", unit: "hrs", target: "< 8 hrs", color: "#10b981" },
      { label: "False Positive Rate", value: "2.2", unit: "%", target: "≤ 3%", color: "#10b981" },
      { label: "Automation Adoption", value: "86", unit: "%", target: "≥ 85%", color: "#10b981" }
    ],
    isDetailViewEnabled: true
  },
  'dhcp-lease-agent': {
    title: "DNS, DHCP & IPAM (DDI) DHCP Lease Agent",
    description: "Continuously monitors DHCP scope utilization, lease activity, and reservation patterns to prevent address pool exhaustion, reclaim unused IPs, and optimize allocation efficiency across the enterprise DDI ecosystem.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Prevent Lease Exhaustion — Detects early signs of pool depletion and takes corrective action automatically.",
          "Optimized IP Utilization — Reclaims stale or inactive leases to maximize address space efficiency.",
          "Operational Stability — Eliminates DHCP-related outages for users, IoT devices, and branch networks.",
          "Cross-Domain Visibility — Integrates with IPAM and DNS hygiene agents for unified DDI governance.",
          "Compliance & Audit Readiness — Logs lease changes and optimization decisions for full traceability."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "DHCP pool exhaustion leads to connectivity failures, authentication issues, and business disruption — especially in large wireless or IoT-dense environments.",
              "Manual monitoring of scopes is reactive and error-prone, often failing to detect fragmented, misconfigured, or orphaned leases.",
              "Stale leases, duplicate reservations, and overallocated ranges contribute to wasted IP capacity.",
              "Enterprises require an autonomous agent capable of predicting pool exhaustion, dynamically rebalancing allocations, and reclaiming unused leases without human intervention — ensuring operational resilience and compliance."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Prevent Lease Exhaustion: Detects early signs of pool depletion and takes corrective action automatically.",
              "Optimized IP Utilization: Reclaims stale or inactive leases to maximize address space efficiency.",
              "Operational Stability: Eliminates DHCP-related outages for users, IoT devices, and branch networks.",
              "Cross-Domain Visibility: Integrates with IPAM and DNS hygiene agents for unified DDI governance.",
              "Compliance & Audit Readiness: Logs lease changes and optimization decisions for full traceability."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Real-Time Lease Monitoring: Track scope utilization, active clients, and available leases continuously.",
              "Predictive Exhaustion Analysis: Forecast depletion based on lease churn rate and growth patterns.",
              "Lease Aging & Reclamation: Identify expired or inactive leases and reclaim addresses automatically.",
              "Duplicate & Conflict Resolution: Detect overlapping reservations and IP conflicts across subnets.",
              "Dynamic Scope Adjustment: Expand or rebalance pool sizes across redundant DHCP servers.",
              "Lease Policy Validation: Ensure lease durations comply with corporate or compliance standards.",
              "AI Reasoning Layer: Correlate usage data and recommend optimal scope adjustments.",
              "Audit & Compliance Logging: Document all lease modifications, recoveries, and optimizations."
            ]
          },
          {
            title: "Integrations",
            content: [
              "DDI Platforms (Infoblox, BlueCat, EfficientIP): Retrieve and modify DHCP scope configurations. (Bidirectional)",
              "IPAM Systems: Synchronize IP allocations and availability with DHCP scopes. (Bidirectional)",
              "DNS Hygiene Agent: Validate lease-to-DNS record consistency. (Bidirectional)",
              "CoreStack GRC / Compliance Modules: Store compliance logs and utilization reports. (Bidirectional)",
              "ServiceNow / Jira: Manage change approvals and lease optimization workflows. (Bidirectional)",
              "Cortex Data Lake / NMS Systems: Analyze utilization and capacity telemetry. (Inbound)",
              "Azure OpenAI / RunOps Reasoning Engine: Perform forecasting, correlation, and remediation decisioning. (Inbound)",
              "Confluence / Data Lake: Archive lease activity, exhaustion predictions, and compliance records. (Outbound)"
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Lease Monitoring & Baseline Collection: Collect and visualize DHCP lease and pool utilization data. (50%)",
              "Tier 2 — Forecasting & Risk Correlation: Predict exhaustion events using AI trend modeling. (75%)",
              "Tier 3 — Optimization & Reclamation Enforcement: Execute lease cleanup, rebalance, or expansion autonomously. (90%)",
              "Tier 4 — Continuous Learning & Self-Optimization: Refine thresholds dynamically based on long-term behavior. (100% Fully Agentic)"
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "DHCP Lease Compliance Rate ≥ 98 % — % of scopes within utilization and policy thresholds.",
              "Mean Time to Detect (MTTD) Exhaustion Risk < 1 hr — Time from threshold breach forecast to detection.",
              "Mean Time to Recover (MTTR) < 3 hrs — Time from exhaustion detection to pool remediation.",
              "Lease Reclamation Efficiency ≥ 90 % — % of stale leases reclaimed automatically.",
              "False Positive Rate ≤ 3 % — Incorrectly flagged utilization warnings.",
              "Automation Adoption Rate ≥ 85 % — Optimizations executed without human intervention."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "DHCP Scopes Monitored: 640",
              "Total Leases Managed: 1.4 million",
              "Exhaustion Risks Predicted: 58",
              "Pools Auto-Rebalanced or Expanded: 43",
              "Stale Leases Reclaimed: 162,000",
              "Lease Reclamation Efficiency: 91.8 %",
              "Average MTTD: 38 min",
              "Average MTTR: 2.4 hrs",
              "False Positive Rate: 2.6 %",
              "Automation Adoption: 86 %",
              "Compliance Rate: 98.3 %",
              "Engineer Hours Saved: ≈ 310 per month",
              "Audit Reports Generated: 9 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Lease Pool Refresh Interval ≤ 30 min — Continuous monitoring via DDI APIs.",
              "Forecast Evaluation Cycle ≤ 1 hr — AI-driven capacity prediction.",
              "Reclamation / Optimization Execution ≤ 3 hrs — Includes approval or automated change push.",
              "Evidence Publication ≤ 1 hr — Lease compliance and utilization reports.",
              "Agent Availability ≥ 99.9 % — HA n8n cluster monitored via Grafana / Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized \"DHCPLeaseAI\" microservice.",
              "Primary Interfaces: DDI REST APIs, IPAM connectors, telemetry feeds.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing lease states, exhaustion predictions, and utilization trends.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on DDI analytics, capacity management, and RunOps network KB.",
              "Security Controls: TLS 1.3 encryption, RBAC isolation, credential vaulting, signed DDI transactions.",
              "Outputs: DHCP Lease Optimization Report (PDF/JSON), Pool Utilization Dashboard, Compliance Snapshot.",
              "Monitoring: Grafana dashboards visualizing lease utilization, exhaustion forecasts, and remediation actions.",
              "Error Handling: Auto-rollback on misapplied pool reallocation; escalate to NOC for manual override if two failed corrections occur."
            ]
          },
          {
            title: "Summary",
            content: [
              "The DHCP Lease Agent is the self-governing IP allocation optimizer for enterprise DDI operations.",
              "By combining real-time telemetry, predictive analytics, and AI reasoning, it ensures IP address availability, eliminates waste, and prevents service disruption caused by pool exhaustion.",
              "Through continuous monitoring, intelligent reclamation, and zero-trust-aligned policy enforcement, it transforms DHCP management from reactive troubleshooting into a proactive, self-healing address governance system — keeping every subnet balanced, compliant, and fully operational."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Utilization threshold breach, predictive alert, or scheduled capacity audit.",
      workingPlane: "RunOps n8n automation cluster (Azure) with containerized 'DHCPLeaseAI' microservice.",
      memory: "Lease history, capacity forecasts, and reclamation logs stored in PostgreSQL.",
      reasoning: "Azure OpenAI GPT-4o driven exhaustion forecasting and remediation planning.",
      execution: "Adjust scopes, release stale leases, or push reconfiguration via DDI APIs while recording audit evidence."
    },
    kpis: [
      { label: "DHCP Lease Compliance Rate", value: "98.3", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Average MTTD", value: "0.63", unit: "hrs", target: "< 1 hr", color: "#10b981" },
      { label: "Average MTTR", value: "2.4", unit: "hrs", target: "< 3 hrs", color: "#10b981" },
      { label: "Lease Reclamation Efficiency", value: "91.8", unit: "%", target: "≥ 90%", color: "#10b981" },
      { label: "Automation Adoption", value: "86", unit: "%", target: "≥ 85%", color: "#10b981" }
    ],
    isDetailViewEnabled: true
  },
  'byod-access-agent': {
    title: "VPN, Remote Access & SASE BYOD Access Agent",
    description: "Validates unmanaged or personally owned devices (BYOD) connecting through VPN or SASE frameworks, ensuring they meet enterprise compliance, security posture, and policy baselines before granting access to corporate resources.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Zero-Trust Enforcement — Enforces device trust validation before network access approval.",
          "Dynamic Posture Validation — Continuously assesses device compliance (AV, patching, encryption, OS version).",
          "Reduced Risk Exposure — Blocks or quarantines risky unmanaged devices automatically.",
          "Improved Compliance — Ensures alignment with corporate security standards and regulatory frameworks.",
          "Operational Efficiency — Eliminates manual pre-checks through automated posture and risk scoring."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "The rise of hybrid work has introduced thousands of unmanaged personal devices into the enterprise access perimeter.",
              "While VPN and SASE gateways authenticate users, they rarely validate device hygiene, leaving gaps in endpoint protection, patch status, and encryption compliance.",
              "Static access rules cannot assess real-time posture, exposing networks to malware, credential theft, or data leakage.",
              "Enterprises require an autonomous, continuous BYOD validation layer that reasons about device risk, verifies compliance, and enforces zero-trust access decisions dynamically."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Zero-Trust Enforcement: Enforces device trust validation before network access approval.",
              "Dynamic Posture Validation: Continuously assesses device compliance (AV, patching, encryption, OS version).",
              "Reduced Risk Exposure: Blocks or quarantines risky unmanaged devices automatically.",
              "Improved Compliance: Ensures alignment with corporate security standards and regulatory frameworks.",
              "Operational Efficiency: Eliminates manual pre-checks through automated posture and risk scoring."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "BYOD Compliance Validation: Validate unmanaged devices against enterprise posture baselines.",
              "Dynamic Risk Scoring: Continuously assess device posture, configuration, and threat indicators.",
              "Access Control Enforcement: Approve, challenge, or block VPN/SASE access based on risk confidence level.",
              "Policy Feedback Loop: Share posture data with ZTNA, CASB, and SWG layers for unified zero-trust enforcement.",
              "Compliance Evidence Generation: Produce audit-ready records of BYOD access validation and enforcement."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Legacy NAC or pre-logon scripts perform static checks but lack continuous validation and context.",
              "The BYOD Access Agent introduces agentic reasoning and zero-trust adaptivity to control unmanaged device access dynamically:",
              "Continuous Context Awareness: Evaluates device health, OS state, and endpoint telemetry before and during sessions.",
              "AI Reasoning: Distinguishes low-risk personal devices from high-risk or compromised endpoints using confidence scoring.",
              "Dynamic Policy Enforcement: Automatically escalates risk-based access (block, quarantine, limited app mode).",
              "Cross-Domain Intelligence: Correlates device behavior with identity, session, and threat intelligence signals.",
              "Learning Feedback: Refines device trust models using historical posture data and false positive patterns."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Device Fingerprinting: Collect hardware, OS, browser, and network identifiers at session initiation.",
              "Posture Validation: Verify patch level, AV status, encryption, and screen-lock enforcement.",
              "Identity & Risk Correlation: Combine user identity, geolocation, and behavior with device trust metrics.",
              "Risk-Based Decisioning: Classify device access as Allow, Conditional (Limited), or Block.",
              "Continuous Session Monitoring: Revalidate posture at defined intervals or upon network change.",
              "Integration with Zero-Trust Frameworks: Share posture data with ZTNA, CASB, and SWG policy engines.",
              "AI Reasoning Layer: Correlate posture deviations and session context to determine adaptive enforcement.",
              "Audit & Reporting: Generate evidence for compliance frameworks (NIST, ISO, HIPAA, PCI-DSS)."
            ]
          },
          {
            title: "Integrations",
            content: [
              "VPN / SASE Controllers: Enforce device trust validation before session establishment. (Bidirectional)",
              "Identity Providers (Okta, Entra ID, Ping): Correlate device posture to user identity and MFA events. (Inbound)",
              "Endpoint Detection / MDM / EDR Tools: Gather endpoint posture signals (patch, AV, encryption). (Inbound)",
              "CASB / SWG / ZTNA Platforms: Extend posture validation into app and web access controls. (Bidirectional)",
              "CoreStack GRC / Compliance Modules: Store compliance artifacts and evidence mapping. (Bidirectional)",
              "ServiceNow / Jira: Track quarantined devices and remediation workflows. (Bidirectional)",
              "Azure OpenAI / RunOps Reasoning Engine: Evaluate posture data and risk factors, generate reasoning output. (Inbound)",
              "Confluence / Data Lake: Archive risk decisions, posture results, and compliance reports. (Outbound)"
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Device Discovery & Fingerprinting: Identify unmanaged devices attempting VPN/SASE access. (50%)",
              "Tier 2 — Posture Validation & Risk Scoring: Evaluate device compliance and calculate trust score. (75%)",
              "Tier 3 — AI-Driven Access Decisioning: Determine enforcement action based on posture and identity correlation. (90%)",
              "Tier 4 — Continuous Validation & Learning: Adapt device trust model dynamically and auto-tune thresholds. (100% Fully Agentic)"
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Device Compliance Rate ≥ 95 % — % of BYOD devices meeting posture requirements.",
              "Mean Time to Detect (MTTD) Non-Compliant Device < 2 min — Time from session initiation to detection of posture violation.",
              "Mean Time to Enforce (MTTE) < 3 min — Time from detection to access control enforcement.",
              "False Positive Rate ≤ 3 % — Compliant devices incorrectly blocked or quarantined.",
              "Adaptive Learning Improvement ≥ 10 % — Month-over-month reduction in false positives or user friction.",
              "Compliance Evidence Coverage 100 % — Recorded and auditable validation logs."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "VPN/SASE Devices Evaluated: 28,400",
              "Unmanaged (BYOD) Devices Detected: 6,320",
              "Compliant Devices Granted Access: 5,874 (92.9%)",
              "Quarantined Sessions: 286",
              "Blocked Sessions (Critical Risk): 160",
              "Average MTTD: 1.8 min",
              "Average MTTE: 2.7 min",
              "Adaptive Learning Improvement: +14.2 % (reduced false positives)",
              "Compliance Evidence Coverage: 100 %",
              "False Positive Rate: 2.3 %",
              "Engineer Hours Saved: ≈ 270 per month",
              "Audit Reports Generated: 12 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Device Fingerprinting & Validation ≤ 30 s — Includes posture and risk scoring.",
              "Access Decision Enforcement ≤ 3 min — Trigger VPN/SASE policy update or IdP response.",
              "Continuous Validation Cycle ≤ 15 min — Revalidate posture during session lifecycle.",
              "Evidence Publication ≤ 1 hr — Audit-ready report generation.",
              "Agent Availability ≥ 99.9 % — HA RunOps cluster monitored via Prometheus / Grafana."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'BYODAccessAI' service.",
              "Primary Interfaces: VPN/SASE controller APIs, IdP authentication logs, MDM/EDR telemetry.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing device risk profiles, posture baselines, and compliance mappings.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on device posture analytics, zero-trust frameworks, and RunOps compliance KB.",
              "Security Controls: TLS 1.3 encryption, device anonymization, encrypted credential storage, RBAC isolation.",
              "Outputs: BYOD Compliance Report (PDF/JSON), Access Enforcement Log, Risk Narrative, GRC Evidence Snapshot.",
              "Monitoring: Grafana dashboards showing device compliance trends, risk distribution, and enforcement rates.",
              "Error Handling: Auto-retry validation API calls; flag unresolved posture verification after two failures."
            ]
          },
          {
            title: "Summary",
            content: [
              "The VPN, Remote Access & SASE BYOD Access Agent is the intelligent trust gateway for unmanaged devices, enforcing continuous posture validation and risk-based access decisions.",
              "It autonomously evaluates each device’s compliance posture, integrates with identity and SASE frameworks, and applies adaptive enforcement actions — from limited access to full block — based on context and confidence.",
              "By combining AI reasoning, telemetry correlation, and zero-trust logic, it ensures that only secure, compliant devices gain access, transforming BYOD from a high-risk vulnerability into a controlled, continuously verified access channel across the enterprise."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Device check at session initiation, posture drift, or threat feed correlation.",
      workingPlane: "RunOps n8n automation cluster (Azure) with containerized 'BYODAccessAI' microservice.",
      memory: "Device posture baselines, historical risk scores, and remediation history stored in PostgreSQL.",
      reasoning: "Azure OpenAI GPT-4o driven device posture reasoning and risk decisioning.",
      execution: "Enforce access decisions via VPN/SASE controllers, notify IdP/SIEM, and record evidence for audit."
    },
    kpis: [
      { label: "Device Compliance Rate", value: "92.9", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "Average MTTD", value: "1.8", unit: "min", target: "< 2 min", color: "#10b981" },
      { label: "Average MTTE", value: "2.7", unit: "min", target: "< 3 min", color: "#10b981" },
      { label: "False Positive Rate", value: "2.3", unit: "%", target: "≤ 3%", color: "#10b981" },
      { label: "Compliance Evidence Coverage", value: "100", unit: "%", target: "100%", color: "#10b981" }
    ],
    isDetailViewEnabled: true
  },

  
  'dnssec-enforcement-agent': {
    title: "DNS, DHCP & IPAM (DDI) DNSSEC Enforcement Agent",
    description: "Continuously validates DNSSEC signing status, key rotation health, and chain-of-trust completeness across enterprise DNS zones, automatically enforcing signing policies, rotating keys, and alerting on integrity violations to maintain a **secure, compliant DNS ecosystem**.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "End-to-End DNS Integrity: Ensures all zones and sub-zones are properly signed and validated.",
          "Reduced Attack Surface: Prevents spoofing, hijacking, and man-in-the-middle attacks at the resolver level.",
          "Operational Reliability: Detects and fixes broken chains automatically to prevent service disruption.",
          "Automated Key Lifecycle Management: Handles ZSK/KSK rollovers, renewals, and expirations.",
          "Regulatory & Security Compliance: Maintains evidence for NIST 800-81, ISO 27001, and PCI DSS 6.6 DNS integrity controls."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Many enterprises enable DNSSEC once and forget it — leaving zones unsynchronized, unsigned, or using expired signing keys.",
              "Key rollover failures or inconsistent signing between parent and child zones can break resolution integrity, exposing users to spoofing or cache poisoning attacks.",
              "Manual audits and infrequent validation create blind spots in the cryptographic trust chain.",
              "Enterprises need an autonomous, reasoning-driven DNSSEC agent that enforces policy compliance, detects trust-chain drift, and automates key lifecycle management across all DNS platforms and cloud zones."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Guaranteed cryptographic integrity across all DNS zones and sub-zones.",
              "Proactive defense against DNS spoofing, hijacking, and cache poisoning attacks.",
              "Automated correction of broken trust chains to ensure uninterrupted DNS resolution service.",
              "Zero-touch automation of complex ZSK/KSK rollovers, reducing human error.",
              "Continuous provision of auditable evidence to meet stringent regulatory mandates."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Zone Signing Enforcement: Automatically sign or re-sign zones that drift from policy.",
              "Key Expiration Monitoring: Detect and renew expiring ZSK and KSK keys.",
              "Trust-Chain Validation: Verify DS records with parent zones to ensure DNSSEC continuity.",
              "Cryptographic Algorithm Compliance: Ensure all zones use enterprise-approved key lengths and algorithms.",
              "Audit & Compliance Evidence: Provide cryptographic verification logs for internal and regulatory audits."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Zone Signature Monitoring: Continuously verify DNSSEC signatures across all authoritative zones.",
              "Key Lifecycle Management: Track ZSK/KSK age, expiration, and rollover windows.",
              "Parent Zone Validation: Check DS record propagation and parent-child consistency.",
              "Policy Enforcement Engine: Apply enterprise-defined signing standards (RSA 2048, ECDSA P-256, etc.).",
              "Automatic Rollover & Re-Signing: Execute ZSK/KSK rotations automatically during maintenance windows.",
              "Algorithm & Parameter Compliance: Validate cryptographic parameters against NIST and internal security baselines.",
              "AI Reasoning Layer: Analyze risk of unsigned zones or weak algorithms and prioritize remediation.",
              "Audit Logging & Evidence Generation: Record cryptographic validations and trust-chain verifications for GRC."
            ]
          },
          {
            title: "Integrations",
            content: [
              "DDI Platforms (Infoblox, BlueCat, EfficientIP): Retrieve zone data, execute signing, and perform key rollovers (Bidirectional).",
              "Cloud DNS Providers (AWS Route53, Azure DNS, GCP Cloud DNS): Monitor and enforce DNSSEC signing across public cloud zones (Bidirectional).",
              "Registrar APIs (GoDaddy, CSC, MarkMonitor): Update DS records and parent-zone trust entries (Bidirectional).",
              "CoreStack GRC / Compliance Modules: Store compliance evidence and cryptographic verification results (Bidirectional).",
              "ServiceNow / Jira: Track DNSSEC exceptions, change approvals, and key rollover requests (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Evaluate trust-chain gaps, reason about remediation priorities, and produce narratives (Inbound).",
              "Confluence / Data Lake: Archive verification logs, signing summaries, and audit artifacts (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Zone & Key Baseline Collection: Inventory DNS zones and their associated signing status and keys (50 %).",
              "Tier 2 — Integrity Validation & Drift Detection: Detect unsigned zones, weak algorithms, or broken DS chains (75 %).",
              "Tier 3 — AI Reasoning & Autonomous Remediation: Execute signing, rollover, or DS updates with AI-driven priority (90 %).",
              "Tier 4 — Predictive Lifecycle Governance & Continuous Learning: Anticipate expirations and auto-adjust signing policies proactively (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "DNSSEC Compliance Rate ≥ 99 % (% of zones properly signed and validated).",
              "Mean Time to Detect (MTTD) Unsigned Zones < 2 hrs (Time to detect a zone that drifts from signed state).",
              "Mean Time to Repair (MTTR) < 4 hrs (Time from detection to restored trust chain).",
              "Key Expiration Prevention Rate ≥ 98 % (% of keys rotated or renewed before expiry).",
              "Trust-Chain Validation Accuracy ≥ 99 % (DS verification success across parent/child zones).",
              "Zone Signing Validation Cycle ≤ 2 hrs (Continuous check of all zones)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'DNSSECAI' microservice.",
              "Primary Interfaces: DDI REST APIs, Registrar APIs, Cloud DNS APIs, DS validation services.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing zone signing metadata, key age, and validation logs.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on DNSSEC trust-chain logic, cryptographic lifecycles, and RunOps security KB.",
              "Security Controls: TLS 1.3, signed transaction validation, RBAC, encrypted key handling.",
              "Error Handling: Auto-rollback on failed re-signing; notify SOC if chain validation fails twice consecutively.",
              "Outputs: DNSSEC Integrity Report (PDF/JSON), Key Lifecycle Summary, Trust-Chain Validation Log."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Continuous Zone Signing Validation Cycle (≤ 2 hrs) or Key Lifecycle Review (≤ 4 hrs).",
      workingPlane: "Performs continuous cryptographic validation of RRSIG records and DS chain consistency across DDI, Cloud DNS, and Registrar systems.",
      memory: "Stores zone signing metadata, key age/expiration timelines (ZSK/KSK), and historical validation logs to predict rollover needs.",
      reasoning: "AI analyzes the risk (e.g., broken trust chain, weak algorithm) and priority for autonomous remediation (signing, key rollover, DS record update).",
      execution: "Triggers automatic key rollovers and re-signing workflows via DDI APIs and updates DS records via Registrar APIs with rollback safety (MTTR < 4 hrs)."
    },
    kpis: [
      { label: "DNSSEC Compliance Rate", value: "99", unit: "%", target: "≥ 99%", color: "#10b981" },
      { label: "Mean Time to Repair (MTTR)", value: "4", unit: "hrs", target: "< 4 hrs", color: "#2563eb" },
      { label: "Key Expiration Prevention Rate", value: "98", unit: "%", target: "≥ 98%", color: "#f59e0b" },
      { label: "Trust-Chain Validation Accuracy", value: "99", unit: "%", target: "≥ 99%", color: "#ef4444" }
    ]
  },

  
  'domain-expiry-agent': {
    title: "DNS, DHCP & IPAM (DDI) Domain Expiry Agent",
    description: "Continuously monitors expiration dates for all registered corporate domains, automatically alerts, renews, and validates successful updates — ensuring **domain continuity, brand protection, and compliance** across registrars and cloud DNS environments.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Continuity Assurance: Prevents domain expiration-related outages or hijacks.",
          "Operational Efficiency: Automates domain renewal workflows and registrar updates.",
          "Regulatory Compliance: Ensures continuous ownership of critical digital assets.",
          "Brand & Security Protection: Prevents domain takeover, impersonation, or DNS redirection attacks.",
          "Audit & Lifecycle Governance: Maintains full visibility and documentation for domain lifecycle events."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprises often manage hundreds of domains across multiple registrars, regions, and subsidiaries — many of which are tied to mission-critical services.",
              "Manual tracking or decentralized domain management leads to renewal oversights, resulting in expired domains, lost email delivery, or cybersquatting risks.",
              "Expired domains can disrupt production systems, brand trust, and compliance with business continuity policies.",
              "Organizations need an autonomous agent that continuously tracks, renews, and validates domain ownership, integrating registrar data with DDI and compliance frameworks."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Guaranteed prevention of domain expiration, outages, or security-compromising hijacks.",
              "Elimination of manual renewal workflows and registrar management time.",
              "Continuous documentation and assurance of ownership for critical digital assets.",
              "Mitigation of brand damage and security risks from domain takeover or redirection.",
              "Automated logging and evidence generation for domain lifecycle auditability."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Domain Expiry Monitoring: Continuously track WHOIS and registrar renewal data for corporate domains.",
              "Auto-Renewal Execution: Automatically renew domains nearing expiration based on policy thresholds.",
              "Registrar Synchronization: Reconcile renewal data between registrar, DNS zones, and IPAM systems.",
              "DNS Validation & Propagation Check: Validate post-renewal DNS resolution integrity.",
              "Compliance & Reporting: Log lifecycle activity and renewal evidence for audit readiness."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "WHOIS & Registrar Data Aggregation: Continuously pull expiry, renewal, and contact data from all registrars.",
              "Expiration Threshold Tracking: Flag domains nearing expiration (e.g., 90/60/30/7/1 days).",
              "Automated Renewal Workflows: Execute renewals automatically via registrar APIs based on pre-approved policy.",
              "Criticality Prioritization: Categorize domains by business impact and auto-renew critical ones first.",
              "DNS Propagation & Resolution Validation: Verify successful renewal and live resolution integrity post-update.",
              "Ownership & Billing Validation: Confirm billing, registrant, and admin contact accuracy.",
              "AI Reasoning Layer: Identify risk domains, assess renewal urgency, and optimize cost vs. continuity.",
              "Audit & Compliance Logging: Record renewal events, validation outcomes, and compliance mapping."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Domain Registrars (GoDaddy, CSC, MarkMonitor, Namecheap, etc.): Retrieve WHOIS data and perform renewals (Bidirectional).",
              "DDI Platforms (Infoblox, BlueCat, EfficientIP): Synchronize zone ownership and expiration metadata (Bidirectional).",
              "CoreStack GRC / Compliance Modules: Store renewal history and audit artifacts (Bidirectional).",
              "SIEM / SOAR Systems: Detect and alert on domain anomalies or ownership risks (Bidirectional).",
              "ServiceNow / Jira: Track renewal tickets, exceptions, and approvals (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Evaluate renewal priority and determine renewal automation logic (Inbound).",
              "Confluence / Data Lake: Archive renewal logs, expiration trend data, and compliance reports (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Expiry Monitoring & Baseline Validation: Aggregate WHOIS and registrar data for all enterprise domains (50 %).",
              "Tier 2 — Renewal Threshold & Risk Assessment: Apply AI reasoning to identify high-priority and at-risk domains (75 %).",
              "Tier 3 — Autonomous Renewal & Validation: Execute renewals, validate DNS propagation, and confirm ownership (90 %).",
              "Tier 4 — Predictive Renewal Optimization & Continuous Learning: Forecast renewals and cost patterns, adjusting timing dynamically (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Domain Renewal Compliance Rate ≥ 99 % (% of domains renewed before expiration).",
              "Mean Time to Detect (MTTD) Expiry Risk < 24 hrs (Time from threshold entry to detection alert).",
              "Mean Time to Renew (MTTRenew) < 2 hrs (Time from detection to successful renewal).",
              "Post-Renewal Validation Accuracy ≥ 98 % (Successful DNS resolution post-renewal).",
              "False Alert Rate (Non-Critical Domains) ≤ 3 % (Low-impact domains incorrectly prioritized).",
              "WHOIS Data Refresh Interval ≤ 24 hrs (Continuous registrar sync)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'DomainExpiryAI' microservice.",
              "Primary Interfaces: Registrar REST APIs, WHOIS query modules, DNS validation tools, DDI APIs.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing domain metadata, renewal history, and policy mappings.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on domain lifecycle management, WHOIS data, and RunOps governance KB.",
              "Security Controls: TLS 1.3 encryption, API credential vaulting, RBAC isolation, registrar API token rotation.",
              "Error Handling: Auto-retry failed renewals; escalate to ITSM if registrar API timeout occurs twice consecutively.",
              "Outputs: Domain Renewal Report (PDF/JSON), Expiry Risk Summary, Compliance Audit Snapshot."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Scheduled WHOIS Data Refresh Interval (≤ 24 hrs) or domain expiration threshold alert (e.g., 90 days remaining).",
      workingPlane: "Aggregates renewal data from multiple registrars, correlates it with DDI and criticality policies, and orchestrates renewal via registrar APIs.",
      memory: "Persists domain inventory, renewal history, criticality scores, and historical cost/timing data to inform predictive renewal optimization.",
      reasoning: "AI identifies mission-critical domains, assesses renewal urgency based on threshold, and determines the optimal time and method (auto-execute vs. manual approval) for renewal.",
      execution: "Executes automated renewal API calls to the registrar and performs post-renewal DNS validation checks (Post-Renewal DNS Validation ≤ 30 min)."
    },
    kpis: [
      { label: "Domain Renewal Compliance Rate", value: "99", unit: "%", target: "≥ 99%", color: "#10b981" },
      { label: "Mean Time to Renew (MTTRenew)", value: "2", unit: "hrs", target: "< 2 hrs", color: "#2563eb" },
      { label: "Post-Renewal Validation Accuracy", value: "98", unit: "%", target: "≥ 98%", color: "#f59e0b" },
      { label: "Engineer Hours Saved", value: "280", unit: "/ month", target: "≈ 280+", color: "#ef4444" }
    ]
  },

  
  'reverse-dns-compliance-agent': {
    title: "Reverse DNS Compliance Agent",
    description: "Continuously validates and enforces reverse DNS (PTR record) accuracy across all subnets and IP ranges, ensuring alignment between forward and reverse lookups, service compliance, and zero-trust visibility across hybrid infrastructure.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Integrity Assurance: Ensures every IP has an accurate reverse DNS record consistent with its hostname.",
          "Improved Service Reliability: Prevents authentication and TLS validation issues tied to DNS mismatches.",
          "Enhanced Email Security: Maintains proper PTR entries to comply with SPF, DKIM, and DMARC validation.",
          "Operational Efficiency: Automates reverse DNS verification and correction workflows.",
          "Audit & Compliance Evidence: Provides complete traceability for regulatory and SOC audits."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Inconsistent or missing reverse DNS entries cause email delivery failures, broken trust validation, and service misidentification across networks.",
              "Dynamic environments (cloud workloads, DHCP leases, containerized services) frequently generate forward DNS without corresponding PTR updates, resulting in integrity drift.",
              "Manual reconciliation between IPAM, DNS, and DHCP is error-prone and rarely continuous.",
              "Enterprises need an autonomous reverse-DNS compliance agent that validates, synchronizes, and remediates PTR records in real time — maintaining DNS hygiene, compliance, and operational trust."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Guaranteed consistency between forward (A/AAAA) and reverse (PTR) DNS records for all IPs.",
              "Elimination of service disruptions caused by DNS authentication and validation failures.",
              "Compliance with modern email security standards (SPF, DKIM, DMARC) through accurate PTR records.",
              "Automation of the entire PTR validation, correction, and synchronization workflow.",
              "Continuous logging of validation and correction events for auditable evidence."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Forward-Reverse Zone Validation: Detect mismatched A/AAAA vs. PTR records across all managed IPs.",
              "PTR Record Creation for Dynamic Hosts: Automatically add reverse entries for new DHCP leases or workloads.",
              "Zone Delegation Verification: Validate correct in-addr.arpa and ip6.arpa delegation across DDI systems.",
              "Service Validation & Security Compliance: Ensure reverse DNS compliance for mail servers, APIs, and production services.",
              "Compliance & Audit Reporting: Generate validation summaries and audit evidence for governance frameworks."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Reverse Zone Discovery: Enumerate all in-addr.arpa and ip6.arpa zones across DDI environments.",
              "Forward-Reverse Record Validation: Compare A/AAAA and PTR mappings across DNS databases.",
              "Dynamic PTR Creation: Add missing reverse entries for DHCP leases or cloud workloads automatically.",
              "Delegation & Ownership Check: Validate correct delegation to authoritative name servers.",
              "Service-Specific Compliance: Confirm reverse records for mail gateways, APIs, and critical applications.",
              "AI Reasoning Engine: Prioritize remediation actions based on risk, criticality, and SLA impact.",
              "Audit Logging & Compliance Evidence: Maintain a full record of validations, updates, and approvals.",
              "Integration with DDI Ecosystem: Ensure consistent synchronization between DNS, DHCP, and IPAM systems."
            ]
          },
          {
            title: "Integrations",
            content: [
              "DDI Platforms (Infoblox, BlueCat, EfficientIP): Retrieve PTR records, validate zones, and apply updates (Bidirectional).",
              "DHCP Lease Agent: Verify lease-based PTR mappings for dynamic IPs (Bidirectional).",
              "IPAM Systems: Validate IP assignments and subnet ownership (Bidirectional).",
              "Cloud DNS Providers (AWS, Azure, GCP): Validate and sync reverse zones in cloud environments (Bidirectional).",
              "CoreStack GRC / Compliance Modules: Store validation logs and compliance mappings (Bidirectional).",
              "ServiceNow / Jira: Manage remediation approvals and exception workflows (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Analyze record mismatches, infer authoritative corrections, and prioritize actions (Inbound).",
              "Confluence / Data Lake: Archive validation results, compliance reports, and audit logs (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Zone & Record Baseline Validation: Collect forward and reverse DNS data across DDI and cloud environments (50 %).",
              "Tier 2 — Anomaly Detection & Correlation: Identify mismatches and classify stale or missing PTR records (75 %).",
              "Tier 3 — AI Reasoning & Automated Correction: Autonomously fix or create PTRs based on authoritative sources (90 %).",
              "Tier 4 — Continuous Learning & Predictive Governance: Forecast potential zone drift and auto-tune validation cycles (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Forward-Reverse Match Rate ≥ 98 % (% of IPs with matching A/AAAA and PTR records).",
              "Mean Time to Detect (MTTD) Drift < 4 hrs (Time to identify reverse zone inconsistencies).",
              "Mean Time to Correct (MTTC) < 6 hrs (Time from detection to PTR correction or addition).",
              "Delegation Validation Accuracy ≥ 97 % (Correct zone delegation verification across environments).",
              "False Positive Rate ≤ 3 % (Valid PTRs incorrectly flagged as inconsistent).",
              "Automation Adoption Rate ≥ 85 % (% of corrections performed autonomously)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'ReverseDNSAI' microservice.",
              "Primary Interfaces: DDI REST APIs, DHCP APIs, IPAM, and Cloud DNS services.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing forward-reverse correlation data, validation states, and zone ownership.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on DNS/DHCP/IPAM integration logic, reverse DNS best practices, and RunOps compliance KB.",
              "Security Controls: TLS 1.3, API key vaulting, RBAC, signed DNS update validation.",
              "Error Handling: Auto-rollback invalid PTR updates; escalate to DNS admin if mismatch persists across 2 validation cycles.",
              "Outputs: Reverse DNS Compliance Report (PDF/JSON), PTR Correction Log, Zone Delegation Summary."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Zone Validation Cycle (≤ 4 hrs) or new DHCP lease/forward DNS record event.",
      workingPlane: "Correlates A/AAAA records (DNS) against lease data (DHCP) and IP assignments (IPAM) to determine the authoritative PTR entry, then enforces correction via DDI APIs.",
      memory: "Stores forward-reverse correlation data, historical validation results, and zone delegation status to track integrity drift.",
      reasoning: "AI analyzes the context (e.g., dynamic vs. static IP, criticality of the service) to prioritize mismatches and determine the correct, least disruptive autonomous remediation action (create, correct, or delete PTR).",
      execution: "Autonomously executes PTR creation or correction commands across DDI systems (MTTC < 6 hrs) and validates the successful resolution post-update."
    },
    kpis: [
      { label: "Forward-Reverse Match Rate", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD) Drift", value: "4", unit: "hrs", target: "< 4 hrs", color: "#2563eb" },
      { label: "Mean Time to Correct (MTTC)", value: "6", unit: "hrs", target: "< 6 hrs", color: "#f59e0b" },
      { label: "Automation Adoption Rate", value: "85", unit: "%", target: "≥ 85%", color: "#ef4444" }
    ]
  },
  
  'ddi-drift-agent': {
    title: "DDI Drift Agent",
    description: "Continuously audits configurations across DNS, DHCP, and IPAM systems to detect, report, and remediate **policy drift** — ensuring alignment with enterprise standards, zero-trust network segmentation, and operational baselines across all DDI platforms.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "End-to-End DDI Integrity: Ensures DNS, DHCP, and IPAM configurations are fully synchronized.",
          "Compliance Enforcement: Detects deviations from enterprise configuration and naming policies.",
          "Operational Resilience: Prevents service failures caused by mismatched or outdated configurations.",
          "Reduced MTTR for DDI Incidents: Automates drift detection and correction before impact.",
          "Zero-Trust Policy Alignment: Maintains consistent access segmentation and address allocation policies."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Large DDI environments evolve rapidly — with changes introduced by automation tools, administrators, or cloud integrations — leading to **configuration drift** between DNS zones, DHCP scopes, and IPAM records.",
              "Drift causes resolution mismatches, duplicate IPs, missing leases, and compliance violations, which impact both service reliability and security posture.",
              "Manual audits cannot keep pace with the scale of hybrid deployments or the dynamic rate of infrastructure changes.",
              "Enterprises need an autonomous, continuously validating DDI Drift Agent that identifies inconsistencies, evaluates risk, and synchronizes configurations automatically."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Guaranteed synchronization and consistency across all DNS, DHCP, and IPAM components.",
              "Continuous identification and enforcement against enterprise configuration and naming standards.",
              "Mitigation of service failures and network issues stemming from DDI configuration mismatches.",
              "Rapid, autonomous correction of inconsistencies, significantly reducing incident resolution time.",
              "Maintenance of zero-trust network segmentation and consistent IP addressing policies."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Policy Drift Detection: Identify deviations in configuration, records, or templates across DDI components.",
              "Cross-System Consistency Validation: Verify that DNS, DHCP, and IPAM data correlate correctly (e.g., IP-to-hostname integrity).",
              "Baseline Policy Enforcement: Compare live configurations to golden enterprise baselines.",
              "Unauthorized Change Detection: Flag out-of-policy or unapproved DDI modifications.",
              "Continuous Compliance Reporting: Maintain an auditable history of detected drift and remediation actions."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Baseline Policy Validation: Continuously compare DNS, DHCP, and IPAM configurations against enterprise templates.",
              "Cross-Component Correlation: Match DNS records with IPAM ownership and DHCP lease data.",
              "Drift Detection Engine: Identify discrepancies in record data, lease state, zone delegation, or subnet assignments.",
              "AI Risk Scoring: Classify drift as compliance, operational, or security-critical.",
              "Remediation Workflow: Automatically reconcile configurations or generate ITSM tickets for approval.",
              "Audit & Version Control: Track changes, timestamps, and resolution activity for compliance reporting.",
              "Policy Synchronization: Propagate updates across DDI components to restore alignment.",
              "Learning Feedback: Continuously improve baseline accuracy and false-positive reduction."
            ]
          },
          {
            title: "Integrations",
            content: [
              "DDI Platforms (Infoblox, BlueCat, EfficientIP): Retrieve configurations, detect drift, and apply corrections (Bidirectional).",
              "DNS Hygiene Agent: Validate DNS zone alignment during drift detection (Bidirectional).",
              "DHCP Lease Agent: Correlate lease utilization and policy compliance (Bidirectional).",
              "IPAM Sync Agent: Confirm IP allocation integrity during drift remediation (Bidirectional).",
              "ServiceNow / Jira: Manage remediation approvals, change records, and drift resolution tracking (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Assess drift context, reason about root cause, and prioritize corrections (Inbound).",
              "Confluence / Data Lake: Archive drift logs, compliance reports, and policy deltas (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — DDI Baseline & Data Collection: Continuously gather DDI configuration data and establish policy baselines (50 %).",
              "Tier 2 — Drift Detection & Classification: Detect and classify deviations based on policy templates (75 %).",
              "Tier 3 — AI Reasoning & Remediation Enforcement: Apply automated sync or corrective actions (90 %).",
              "Tier 4 — Continuous Learning & Predictive Governance: Anticipate drift trends and adjust baseline automatically (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "DDI Drift Compliance Rate ≥ 98 % (% of DDI components aligned with policy baseline).",
              "Mean Time to Detect (MTTD) Drift < 2 hrs (Time from configuration deviation to detection).",
              "Mean Time to Remediate (MTTR) < 6 hrs (Time from detection to policy-aligned correction).",
              "False Positive Rate ≤ 3 % (Valid configuration changes incorrectly flagged).",
              "Automation Adoption Rate ≥ 85 % (% of drift corrections executed autonomously).",
              "Configuration Collection & Validation Cycle ≤ 4 hrs (Continuous data ingestion and validation)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'DDIDriftAI' microservice.",
              "Primary Interfaces: DDI REST APIs, CMDB integrations, IPAM repositories, DHCP and DNS controllers.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing policy templates, configuration snapshots, and drift history.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on DDI policy validation, drift detection, and RunOps compliance KB.",
              "Security Controls: TLS 1.3 encryption, signed API transactions, RBAC-based execution control.",
              "Error Handling: Auto-retry drift remediation; escalate to admin if deviation recurs after two sync cycles.",
              "Outputs: DDI Drift Report (PDF/JSON), Policy Drift Summary, Compliance Snapshot, Change Log."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Continuous Configuration Collection & Validation Cycle (≤ 4 hrs) or out-of-band change detection from DDI systems.",
      workingPlane: "Performs cross-system correlation of configurations (DNS zones, DHCP scopes, IPAM records) against a golden policy baseline.",
      memory: "Persists configuration snapshots, policy templates, and historical drift events to identify recurrence patterns and refine baseline accuracy.",
      reasoning: "AI evaluates the severity and type of drift (policy, operational, security) and determines the authoritative correction needed to restore alignment, prioritizing high-risk changes.",
      execution: "Triggers autonomous configuration synchronization and policy updates across DDI components (MTTR < 6 hrs) and logs remediation actions against ITSM change records."
    },
    kpis: [
      { label: "DDI Drift Compliance Rate", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD) Drift", value: "2", unit: "hrs", target: "< 2 hrs", color: "#2563eb" },
      { label: "Mean Time to Remediate (MTTR)", value: "6", unit: "hrs", target: "< 6 hrs", color: "#f59e0b" },
      { label: "Automation Adoption Rate", value: "85", unit: "%", target: "≥ 85%", color: "#ef4444" }
    ]
  },
   'vpn-health-agent': {
    title: "VPN, Remote Access & SASE VPN Health Agent",
    description: "Continuously monitors VPN gateways, tunnels, and certificates across enterprise, cloud, and SASE edges to ensure uptime, policy compliance, and cryptographic integrity — detecting failures, misconfigurations, and potential security risks before they impact users.",
    details: [
      {
        title: "Key Performance Indicators (KPI)",
        points: [
          "≥ 99.95% Gateway Uptime - Continuous availability per site",
          "≥ 99.9% Tunnel Availability - Active VPN/SASE tunnel uptime",
          "< 1 min Mean Time to Detect (MTTD) - Time from tunnel degradation to detection",
          "< 5 min Mean Time to Restore (MTTR) - Time from detection to rekey or failover",
          "≥ 98% Certificate Compliance Rate - Certificates valid, unexpired, and compliant with enterprise crypto baseline",
          "≤ 3% False Positive Rate - Benign tunnel flaps incorrectly flagged"
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Hybrid work, cloud adoption, and global mobility have exponentially increased VPN and SASE session volume, making manual tunnel and certificate oversight unsustainable.",
              "Outages, expired certificates, or tunnel negotiation failures immediately disrupt business-critical access.",
              "Misconfigured encryption, weak cipher usage, and stale tunnels expose the network to security and compliance risk.",
              "Enterprises need a real-time, intelligent health monitoring agent that ensures VPN reliability, cryptographic hygiene, and continuous compliance with zero-trust principles and frameworks."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "Continuous VPN Availability: Detects and resolves gateway or tunnel outages before users experience downtime",
              "Security Assurance: Validates cryptographic compliance (IKEv2, AES, SHA-256) and certificate expiry status",
              "Operational Efficiency: Automates monitoring and remediation workflows across thousands of tunnels",
              "Zero-Trust Enablement: Integrates VPN posture into the broader identity and device context",
              "Audit & Governance Readiness: Produces continuous evidence of uptime, compliance, and encryption integrity"
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Gateway Health Monitoring: Track availability, CPU/memory usage, and session count for VPN gateways",
              "Tunnel State Validation: Check IPsec/SSL tunnel status, negotiation success, and phase-1/phase-2 alignment",
              "Certificate Compliance Validation: Evaluate expiration, chain-of-trust, and cryptographic strength",
              "Policy Drift Detection: Verify that encryption and authentication settings match enterprise templates",
              "AI Reasoning Layer: Classify tunnel events by severity, correlate symptoms to probable cause",
              "Remediation Actions: Trigger auto-rekey, reestablish tunnels, or open ITSM tickets for human approval"
            ]
          },
          {
            title: "Representative Statistics",
            content: [
              "VPN Gateways Monitored: 72 (HQ, DC, Cloud Edge, SASE PoPs)",
              "Tunnels Observed: 1,248",
              "Average Tunnel Availability: 99.93%",
              "Detected Tunnel Failures: 24 (18 transient, 6 persistent)",
              "Expiring Certificates (≤30 days): 14",
              "Auto-Renewed Certificates: 10",
              "Average Rekey/Restore Time: 4.2 min",
              "False Positive Rate: 2.4%",
              "Compliance Rate: 97.9%",
              "Engineer Hours Saved: ~240 per month"
            ]
          }
        ]
      }
    ]
  },
   'split-tunnel-optimizer-agent': {
    title: "VPN, Remote Access & SASE Split-Tunnel Optimizer Agent",
    description: "Dynamically manages split-tunnel and full-tunnel VPN policies based on application trust, user posture, and threat intelligence — optimizing performance by offloading safe traffic to the internet while enforcing full inspection for sensitive or high-risk destinations.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Optimized Performance: Reduces VPN backhaul and latency for SaaS and trusted destinations.",
          "Enhanced Security Posture: Forces full-tunnel routing for high-risk, sensitive, or untrusted traffic.",
          "Dynamic Policy Adaptation: Adjusts tunnel mode automatically based on user, device, and threat context.",
          "Regulatory Compliance: Ensures full-tunnel enforcement for PCI, HIPAA, or SOC-sensitive workflows.",
          "Operational Efficiency: Eliminates manual tunnel list maintenance and reduces helpdesk escalations."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Split-tunneling improves performance by routing internet traffic directly rather than through the corporate VPN, but creates visibility and data-exfiltration risk.",
              "Full-tunnel policies improve control but increase latency, bandwidth cost, and gateway load.",
              "Static tunnel policies are inefficient: they don’t account for application sensitivity, user location, or device posture.",
              "Enterprises need an intelligent, adaptive system that can continuously evaluate which traffic should be split or retained — based on risk, performance, and compliance context."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "Optimized Performance: Reduces VPN backhaul and latency for SaaS and trusted destinations.",
              "Enhanced Security Posture: Forces full-tunnel routing for high-risk, sensitive, or untrusted traffic.",
              "Dynamic Policy Adaptation: Adjusts tunnel mode automatically based on user, device, and threat context.",
              "Regulatory Compliance: Ensures full-tunnel enforcement for PCI, HIPAA, or SOC-sensitive workflows.",
              "Operational Efficiency: Eliminates manual tunnel list maintenance and reduces helpdesk escalations."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "App-Aware Traffic Categorization: Identify applications and domains to assign trusted/untrusted status dynamically.",
              "User & Device Posture Correlation: Validate endpoint security compliance before applying split policy.",
              "Performance & Latency Monitoring: Evaluate tunnel performance and user experience metrics continuously.",
              "Risk-Based Policy Adjustment: Modify routing dynamically when risk score exceeds defined thresholds.",
              "Threat Intelligence Integration: Cross-check destinations against threat feeds (CISA KEV, CrowdStrike Intel, OTX).",
              "AI Reasoning Engine: Balance risk vs performance trade-offs and generate adaptive routing recommendations.",
              "Policy Enforcement via Controller: Push updated split/full-tunnel configurations to VPN or SASE controllers.",
              "Audit & Evidence Reporting: Maintain records of decisions, risk context, and compliance mapping."
            ]
          },
          {
            title: "Integrations",
            content: [
              "VPN / SASE Controllers: Enforce split/full-tunnel policies and collect telemetry (Bidirectional).",
              "Identity Providers (Okta, Entra ID, Ping): Correlate user sessions, roles, and risk posture (Inbound).",
              "Endpoint / EDR Systems (CrowdStrike, Defender, SentinelOne): Validate device posture and compliance (Inbound).",
              "Threat Intelligence Platforms: Evaluate destination risk in real time (Inbound).",
              "CoreStack GRC / Compliance Modules: Store compliance evidence and map control coverage (Bidirectional).",
              "ServiceNow / Jira: Log policy adjustments and escalations (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Evaluate context and produce reasoning narratives for routing decisions (Inbound).",
              "Confluence / Data Lake: Archive optimization reports, policy snapshots, and audit records (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Split-Tunnel Policy Accuracy ≥ 98% — Correctly applied routing per user/app context.",
              "Mean Time to Detect (MTTD) Risk Change < 2 min — Time from risk posture change to detection.",
              "Mean Time to Adjust (MTTA) < 5 min — Time to switch tunnel mode post-evaluation.",
              "Performance Gain (Latency Reduction) ≥ 15% — Latency reduction for trusted SaaS traffic.",
              "Security Compliance Score ≥ 95% — % of traffic appropriately inspected based on risk.",
              "False Adjustment Rate ≤ 3% — Incorrect mode changes that degrade SLA or bypass inspection."
            ]
          },
          {
            title: "Representative Statistics",
            content: [
              "Users Monitored: 22,400",
              "Active Sessions: 1.2 million",
              "Dynamic Policy Adjustments: 3,540",
              "Trusted Split-Tunnel Apps: 46 SaaS services (M365, Zoom, ServiceNow, Salesforce, etc.)",
              "Average Latency Improvement: 17.8% (SaaS traffic)",
              "Bandwidth Offload Savings: 23.4% WAN utilization reduction",
              "Detected Risk-Driven Policy Switches: 382",
              "Average MTTD: 1.5 min",
              "Average MTTA: 4.2 min",
              "False Adjustment Rate: 2.4%",
              "Compliance Alignment: 97.6%",
              "Engineer Hours Saved: ≈ 240 per month",
              "Audit Reports Generated: 11 (CoreStack / Confluence)"
            ]
          }
        ]
      },
      {
        title: "4. Service Level Agreements (SLA)",
        subSections: [
          {
            title: "Process SLA",
            content: [
              "Telemetry Collection Cycle ≤ 60 s — Real-time feed from VPN and endpoint telemetry.",
              "Risk Correlation & Decision Engine ≤ 2 min — Includes user/device/posture/threat evaluation.",
              "Policy Commit to Controller ≤ 5 min — Policy push and validation.",
              "Audit Evidence Publication ≤ 1 hr — Compliance report export.",
              "Agent Availability ≥ 99.9 % — HA n8n cluster monitored by Prometheus / Grafana."
            ]
          }
        ]
      },
      {
        title: "5. Technical Context",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized \"SplitOptAI\" service.",
              "Primary APIs: VPN/SASE controller REST APIs, Threat Intelligence feeds, IdP and EDR APIs.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing trusted domain list, tunnel baselines, and posture maps.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on zero-trust network routing logic, SaaS risk data, and RunOps SASE KB.",
              "Security Controls: TLS 1.3 encryption, API token auth, anonymized session telemetry, RBAC separation.",
              "Outputs: Split-Tunnel Decision Report (JSON/PDF), Risk Context Log, Compliance Snapshot, ITSM Ticket.",
              "Monitoring: Grafana dashboards visualizing split/full policy adoption, latency trends, and offload efficiency.",
              "Error Handling: Auto-rollback policy on degraded security validation; notify SOC on repeated toggles for same user/app."
            ]
          }
        ]
      },
      {
        title: "Summary",
        subSections: [
          {
            content: "The Split-Tunnel Optimizer Agent serves as the adaptive routing governor for remote access and SASE environments — continuously balancing performance, risk, and compliance. By leveraging AI reasoning, telemetry correlation, and zero-trust enforcement, it autonomously decides when to apply split vs full-tunnel routing, offloading safe traffic while protecting sensitive flows."
          }
        ]
      }
  ],
  sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "In the hybrid enterprise, thousands of concurrent VPN and SASE sessions exist at any given time — each representing a potential threat vector if hijacked or misused.",
              "Traditional monitoring tools detect malicious logins but rely on human intervention for response, leading to slow containment and prolonged exposure windows.",
              "Attackers exploit stolen tokens, VPN profiles, and cached credentials to persist within corporate networks.",
              "Enterprises need a real-time, autonomous containment agent capable of detecting and killing compromised sessions immediately — aligned with zero-trust, least-privilege, and continuous verification principles."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Immediate Threat Containment: Terminates active sessions upon detection of compromise indicators.",
              "Zero-Trust Reinforcement: Enforces continuous verification — no session is trusted indefinitely.",
              "Operational Efficiency: Reduces mean time to respond (MTTR) from minutes to seconds.",
              "Incident Impact Reduction: Stops data exfiltration and credential replay in real time.",
              "Compliance Alignment: Automates control enforcement for NIST, ISO, PCI, and SOC 2 access management requirements."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Compromised Session Detection: Identify abnormal login activity, token anomalies, or device posture drift.",
              "Behavioral Risk Monitoring: Monitor continuous session telemetry for deviations from baseline.",
              "Threat-Intel-Driven Termination: Terminate sessions from known malicious IPs, ASNs, or threat-listed origins.",
              "Adaptive Risk Scoring: Reassess ongoing sessions dynamically based on user, device, and application behavior.",
              "Coordinated Response: Integrate with SIEM, SOAR, and identity providers to isolate compromised identities enterprise-wide."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Legacy session monitoring systems rely on manual correlation or rule-based alerts, introducing reaction lag.",
              "The Session Kill Agent applies agentic reasoning for real-time autonomous response:",
              "Continuous Session Awareness: Tracks live sessions across all VPN and SASE edges in real time.",
              "Behavioral Reasoning: Detects anomalies such as geo-velocity, impossible travel, or data-rate spikes.",
              "Cross-Domain Correlation: Combines identity, endpoint, and threat telemetry for high-confidence detection.",
              "Self-Governed Termination Logic: Initiates session kill actions automatically, validated by AI confidence scoring.",
              "Feedback Loop Learning: Learns false-positive scenarios and fine-tunes its decision confidence dynamically."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Session Inventory & Tracking: Continuously track active sessions across VPN gateways, SASE PoPs, and remote devices.",
              "Compromise Detection Engine: Identify high-risk patterns (multiple concurrent logins, credential anomalies, traffic spikes).",
              "Threat Intelligence Correlation: Match session IPs and behaviors against real-time intelligence feeds.",
              "Posture Validation: Verify endpoint compliance (EDR status, encryption, patch level).",
              "AI Decisioning: Determine compromise likelihood via composite risk scoring model.",
              "Session Termination Workflow: Execute kill commands on compromised sessions via controller APIs.",
              "Notification & Coordination: Alert SOC and identity systems (IdP, SIEM, SOAR) for concurrent isolation.",
              "Evidence & Audit Reporting: Document cause, action, and outcome for forensics and compliance."
            ]
          },
          {
            title: "Integrations",
            content: [
              "VPN / SASE Controllers: Monitor sessions and execute termination commands. (Bidirectional)",
              "Identity Providers (Okta, Entra ID, Ping): Validate identity posture and revoke tokens post-kill. (Bidirectional)",
              "Endpoint Protection / EDR Platforms: Validate endpoint trust and detect behavioral anomalies. (Inbound)",
              "Threat Intelligence Platforms (CISA KEV, OTX, CrowdStrike): Correlate IPs, domains, and ASN reputations. (Inbound)",
              "SIEM / SOAR Systems: Coordinate containment and trigger enterprise-level responses. (Bidirectional)",
              "CoreStack GRC / Compliance Modules: Record enforcement actions and map to compliance controls. (Bidirectional)",
              "Azure OpenAI / RunOps Reasoning Engine: Perform risk reasoning and generate narrative justification. (Inbound)",
              "Confluence / Data Lake: Archive session kill events, risk scores, and compliance logs. (Outbound)"
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Session Monitoring & Baseline Learning: Track live sessions and establish normal behavioral patterns. (50%)",
              "Tier 2 — Risk Detection & Correlation: Identify anomalies, correlate with threat intel and identity posture. (75%)",
              "Tier 3 — AI Reasoning & Automated Session Termination: Execute session kills autonomously when confidence threshold met. (90%)",
              "Tier 4 — Adaptive Learning & Continuous Trust Evaluation: Refine thresholds based on false positives and historical analytics. (100% Fully Agentic)"
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Mean Time to Detect (MTTD) < 60 s — From anomaly event to detection.",
              "Mean Time to Terminate (MTTT) < 90 s — From detection to successful session kill.",
              "False Positive Rate ≤ 2% — Legitimate sessions incorrectly terminated.",
              "Containment Success Rate ≥ 98% — Confirmed compromised sessions successfully killed.",
              "Post-Termination Notification SLA ≤ 2 min — Alerts propagated to SOC, IdP, and affected user.",
              "Compliance Evidence Coverage 100% — Session kill actions documented and auditable."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Concurrent Sessions Monitored: 1.3 million",
              "Total Users Protected: 24,600",
              "Detected High-Risk Sessions: 342",
              "Credential Anomalies: 108",
              "Endpoint Posture Violations: 77",
              "Geo-Spoofed or Impossible Travel: 96",
              "Data Exfiltration Attempts: 61",
              "Sessions Terminated Automatically: 298",
              "Containment Success Rate: 97.6%",
              "False Positive Rate: 1.8%",
              "Average MTTD: 42 seconds",
              "Average MTTT: 74 seconds",
              "Audit Reports Generated: 11 (CoreStack / Confluence)",
              "Engineer Hours Saved: ≈ 270 per month"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Anomaly Detection Cycle ≤ 60 s — Continuous telemetry correlation.",
              "Threat Feed Correlation ≤ 1 min — Real-time lookup against intelligence databases.",
              "Session Termination Execution ≤ 90 s — Automated command dispatch and verification.",
              "Notification & Escalation ≤ 2 min — Alert SOC, SIEM, and IdP systems.",
              "Evidence Publication ≤ 1 hr — Compliance reporting and audit trail generation.",
              "Agent Availability ≥ 99.9% — HA n8n cluster monitored by Grafana / Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'SessionKillAI' microservice.",
              "Primary APIs: VPN/SASE REST APIs, IdP Token Revocation, Threat Intelligence Feeds, EDR integrations.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing active session data, risk scores, and policy outcomes.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on behavioral anomaly, credential misuse, and RunOps Zero-Trust KB.",
              "Security Controls: TLS 1.3 encryption, encrypted API credentials, RBAC execution limits, digital signatures for termination logs.",
              "Outputs: Session Termination Log (JSON/PDF), Risk Narrative, Compliance Evidence Snapshot, SOC Alert Summary.",
              "Monitoring: Grafana dashboards showing termination counts, response latency, false positive trends, and SLA adherence.",
              "Error Handling: Retry on controller response failure; fallback to manual SOC escalation if two auto-kill attempts fail."
            ]
          },
          {
            title: "Summary",
            content: [
              "The VPN, Remote Access & SASE Session Kill Agent acts as the autonomous containment layer of the zero-trust architecture — a real-time guardian that continuously validates session trust and terminates compromised connections before data or systems are impacted.",
              "By combining behavioral analytics, threat intelligence, and AI reasoning, it achieves sub-minute response times and eliminates human lag in incident containment.",
              "This agent transforms remote-access security into a self-healing, continuously verifying trust system — ensuring that every active session is both secure and legitimate, all the time."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Telemetry and threat signals, policy change events, or scheduled optimization cycles.",
      workingPlane: "Real-time decision plane with safe-commit pipeline to controllers (cloud/hybrid).",
      memory: "Trusted domain lists, historical decision outcomes, and posture baselines.",
      reasoning: "AI-driven risk vs performance evaluation producing decision justifications and rollback plans.",
      execution: "Actions: push policy changes, rollback, create remediation tickets, or mark domains as trusted/untrusted."
    },
    kpis: [
      { label: "Policy Accuracy", value: 98, unit: "%", target: "≥ 98%", color: "#10B981" },
      { label: "MTTD Compliance", value: 100, unit: "%", target: "< 2 min (target)", color: "#3B82F6" },
      { label: "MTTA Compliance", value: 100, unit: "%", target: "< 5 min (target)", color: "#3B82F6" },
      { label: "Performance Gain", value: 17.8, unit: "%", target: "≥ 15%", color: "#06B6D4" },
      { label: "Compliance Alignment", value: 97.6, unit: "%", target: "≥ 95%", color: "#F59E0B" },
      { label: "False Adjustment Avoidance", value: 97.6, unit: "%", target: "≤ 3% FA (100 - FA%)", color: "#6366F1" }
    ]
  },
  'sase-policy-agent': {
    title: "VPN, Remote Access & SASE Policy Agent",
    description: "Enforces Zero Trust Network Access (ZTNA), Cloud Access Security Broker (CASB), and Secure Web Gateway (SWG) policies uniformly across all sites, gateways, and users — ensuring consistent policy application, data protection, and threat prevention in distributed SASE and remote-access environments.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Policy Consistency Across Edges: Ensures ZTNA, CASB, and SWG policies are applied identically across VPN, SASE, and branch gateways.",
          "Reduced Policy Drift: Automatically detects and reconciles configuration deviations.",
          "Enhanced Security Posture: Maintains real-time enforcement of access control, DLP, and malware inspection.",
          "Zero-Trust Enforcement: Aligns user, device, and app context to ZTNA access policies.",
          "Compliance Assurance: Generates auditable evidence of consistent security control application."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "In distributed SASE ecosystems, policy fragmentation often occurs between VPN gateways, CASB, and SWG engines due to asynchronous updates or manual configuration drift.",
              "Users accessing cloud and SaaS applications from different geographies experience inconsistent policy enforcement, leaving gaps in data protection and threat coverage.",
              "Regulatory and compliance standards (PCI-DSS, HIPAA, NIST 800-207, ISO 27001) require unified control enforcement, but most organizations rely on disparate management consoles.",
              "A centralized, intelligent policy agent is required to ensure that all access control, data inspection, and web protection policies remain synchronized, compliant, and continuously enforced across all edges."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "Policy Consistency Across Edges: Ensures ZTNA, CASB, and SWG policies are applied identically across VPN, SASE, and branch gateways.",
              "Reduced Policy Drift: Automatically detects and reconciles configuration deviations.",
              "Enhanced Security Posture: Maintains real-time enforcement of access control, DLP, and malware inspection.",
              "Zero-Trust Enforcement: Aligns user, device, and app context to ZTNA access policies.",
              "Compliance Assurance: Generates auditable evidence of consistent security control application."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Policy Snapshot & Drift Detection: Continuously audit policy configurations across ZTNA, CASB, and SWG controllers.",
              "Baseline Policy Comparison: Compare live enforcement profiles with golden templates for each function.",
              "Cross-Domain Consistency Check: Validate synchronization between VPN gateways, SASE PoPs, and CASB tenants.",
              "AI Risk & Impact Analysis: Assess the potential business and compliance impact of any policy mismatch.",
              "Remediation & Sync Automation: Automatically align drifted policies to golden baselines.",
              "Zero-Trust Context Validation: Ensure policy rules reflect identity, device, and location context.",
              "Audit Evidence Generation: Export full compliance logs and mapping to GRC frameworks."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ZTNA Controllers / SASE Orchestrators: Retrieve and enforce access and segmentation policies (Bidirectional).",
              "CASB APIs (Cloud & SaaS Providers): Validate SaaS app control and DLP configurations (Bidirectional).",
              "SWG / Secure Proxy Systems: Verify content filtering and threat inspection policy synchronization (Bidirectional).",
              "CoreStack GRC / Compliance Modules: Map enforcement results to compliance control sets (Bidirectional).",
              "Identity Providers (Entra ID, Okta, Ping): Validate access rules per user group and identity posture (Inbound).",
              "ServiceNow / Jira: Log drift events and track remediation approvals (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Interpret drift context, compliance impact, and remediation path (Inbound).",
              "Confluence / Data Lake: Archive policy evidence, drift reports, and compliance artifacts (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Policy Synchronization Accuracy ≥ 98% — Percentage of aligned policies across ZTNA, CASB, and SWG.",
              "Mean Time to Detect (MTTD) Drift < 5 min — Detection time for configuration mismatches.",
              "Mean Time to Remediate (MTTR) < 2 hrs — Time to realign and reapply drifted policies.",
              "False Positive Rate ≤ 3% — Benign policy variations flagged as drift.",
              "Compliance Alignment Score ≥ 95% — Correlation of enforcement consistency to GRC frameworks.",
              "Automation Adoption Rate ≥ 80% — Percentage of corrections executed autonomously."
            ]
          },
          {
            title: "Representative Statistics",
            content: [
              "SASE / VPN Edges Monitored: 112",
              "ZTNA / CASB / SWG Controllers: 9",
              "Total Policies Audited: 8,420",
              "Drift Events Detected: 173",
              "ZTNA Policy Inconsistencies: 51",
              "CASB Configuration Drift: 63",
              "SWG Rule Deviations: 59",
              "Auto-Remediated Policies: 139 (80%)",
              "Average MTTD: 4.1 min",
              "Average MTTR: 1.6 hrs",
              "False Positives: 2.8%",
              "Compliance Alignment: 96.4%",
              "Engineer Hours Saved: ~250 per month",
              "Audit Reports Generated: 10 (CoreStack / Confluence)"
            ]
          }
        ]
      },
      {
        title: "4. Service Level Agreements (SLA)",
        subSections: [
          {
            title: "Process SLA",
            content: [
              "Policy Audit Interval ≤ 10 min — Continuous polling across ZTNA, CASB, and SWG APIs.",
              "Drift Detection & Classification ≤ 5 min — AI-assisted correlation and severity scoring.",
              "Policy Sync & Remediation Commit ≤ 30 min — Safe rollback-enabled correction process.",
              "Compliance Report Generation ≤ 1 hr — Export evidence to GRC/Confluence.",
              "Agent Availability ≥ 99.9 % — HA n8n cluster monitored via Grafana / Prometheus."
            ]
          }
        ]
      },
      {
        title: "5. Technical Context",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized \"SASEPolicyAI\" service.",
              "Primary APIs: ZTNA controller REST, CASB API, SWG proxy management API, CoreStack GRC, IdP SCIM/SAML APIs.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing golden policies, config checksums, and compliance mappings.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on ZTNA/CASB/SWG drift datasets, compliance frameworks, and RunOps security KB.",
              "Security Controls: Read-only API polling, TLS 1.3, encrypted credentials, RBAC isolation.",
              "Outputs: Policy Drift Report (JSON/PDF), Compliance Snapshot, Remediation Log, SLA Alignment Summary.",
              "Monitoring: Grafana dashboards visualizing drift rate, sync success, and policy compliance by function.",
              "Error Handling: Auto-retry failed syncs; fallback to last verified baseline; alert SOC on recurring drift in same policy domain."
            ]
          }
        ]
      },
      {
        title: "Summary",
        subSections: [
          {
            content: "The Policy Agent functions as the central governance and enforcement layer for all SASE components — ensuring ZTNA, CASB, and SWG policies remain harmonized, compliant, and contextually enforced across every user, device, and site. By combining multi-plane telemetry, AI reasoning, and continuous compliance logic, it prevents policy drift, strengthens zero-trust posture, and delivers end-to-end policy coherence across the hybrid enterprise network."
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Policy change events, scheduled audits, controller webhooks, or drift detection triggers.",
      workingPlane: "Orchestration tier with policy translation and safe-commit pipelines (cloud/hybrid).",
      memory: "Golden templates, recent diffs, remediation history, and risk-scored policy inventory.",
      reasoning: "AI-driven drift impact analysis mapping to compliance and business risk.",
      execution: "Actions: policy sync, rollback, create remediation tickets, or notify governance workflows."
    },
    kpis: [
      { label: "Policy Sync Accuracy", value: 96.4, unit: "%", target: "≥ 98%", color: "#10B981" },
      { label: "Drift Detection MTTD", value: 82, unit: "%", target: "< 5 min (compliance)", color: "#3B82F6" },
      { label: "Remediation MTTR Compliance", value: 100, unit: "%", target: "< 2 hrs (target)", color: "#3B82F6" },
      { label: "False Positive Avoidance", value: 97.2, unit: "%", target: "≤ 3% FP (100 - FP%)", color: "#F59E0B" },
      { label: "Compliance Alignment", value: 96.4, unit: "%", target: "≥ 95%", color: "#06B6D4" },
      { label: "Automation Adoption", value: 80, unit: "%", target: "≥ 80%", color: "#6366F1" }
    ]
  },
   'remote-access-experience-agent': {
    title: "VPN, Remote Access & SASE Remote Access Experience Agent",
    description: "Continuously monitors VPN and SASE user experience by collecting performance, reliability, and satisfaction metrics from end-user devices and gateways. Correlates session telemetry, network conditions, and app performance to proactively detect degradation and improve digital experience for remote workers.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Proactive Experience Monitoring: Detects end-user VPN/SASE performance degradation before tickets arise.",
          "User-Centric Performance Metrics: Measures latency, throughput, and reliability per session, per user.",
          "Reduced MTTR: Correlates issues between device, gateway, and transport for faster resolution.",
          "Operational Efficiency: Automates triage workflows and integrates with ITSM systems.",
          "SLA & Experience Assurance: Provides auditable, quantified digital experience data for compliance and benchmarking."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Traditional network monitoring measures tunnel uptime but lacks visibility into user experience and perceived performance.",
              "Hybrid workforces connect through diverse networks (home Wi-Fi, LTE, coffee shops, corporate VPN), where issues like latency, packet loss, or DNS delays impact productivity and satisfaction.",
              "IT teams need more than tunnel metrics — they need experience intelligence: how users feel the network, not just how it operates.",
              "Without real-time correlation between user sessions, device state, and SASE edge health, organizations face blind spots that lead to support tickets, SLA breaches, and reduced workforce efficiency.",
              "The enterprise requires a self-learning, user-centric agent that bridges network telemetry and human experience metrics."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "Proactive Experience Monitoring: Detects end-user VPN/SASE performance degradation before tickets arise.",
              "User-Centric Performance Metrics: Measures latency, throughput, and reliability per session, per user.",
              "Reduced MTTR: Correlates issues between device, gateway, and transport for faster resolution.",
              "Operational Efficiency: Automates triage workflows and integrates with ITSM systems.",
              "SLA & Experience Assurance: Provides auditable, quantified digital experience data for compliance and benchmarking."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Session Telemetry Collection: Capture per-user latency, jitter, throughput, and reconnect frequency.",
              "Experience Index Modeling: Compute REI (RunOps Experience Index) using weighted user, network, and app KPIs.",
              "Device Performance Correlation: Monitor endpoint CPU, memory, Wi-Fi strength, and network adapters.",
              "Gateway and Edge Path Mapping: Map session routes across SASE PoPs, VPN hubs, and WAN transport types.",
              "AI Root Cause Analysis: Identify problem domain — device, transport, DNS, or gateway.",
              "Alerting & Self-Healing Integration: Feed poor experience alerts into SD-WAN and VPN optimization workflows.",
              "Feedback Loop: Validate optimization outcomes by comparing pre- and post-adjustment REI.",
              "Audit & Reporting: Generate end-user experience trend reports and SLA evidence packages."
            ]
          },
          {
            title: "Integrations",
            content: [
              "VPN / SASE Controllers: Retrieve user session performance and tunnel metrics (Inbound).",
              "Endpoint Telemetry / EDR Systems: Collect local device health and connection data (Inbound).",
              "QoS / Path Selection / Split-Tunnel Agents: Share user experience feedback for adaptive optimization (Bidirectional).",
              "CoreStack GRC / SLA Modules: Store REI metrics and compliance evidence (Bidirectional).",
              "ServiceNow / Jira: Create experience-related incidents and remediation workflows (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Correlate anomalies, determine root cause, and generate insights (Inbound).",
              "Confluence / Data Lake: Archive experience trend reports and SLA compliance data (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "RunOps Experience Index (REI) ≥ 85 / 100 — Weighted average of latency, availability, and user satisfaction.",
              "Mean Time to Detect (MTTD) Degradation < 2 min — Time to identify user-impacting issues.",
              "Mean Time to Resolve (MTTR) < 15 min — Time to restore acceptable REI post-correction.",
              "Automation Adoption Rate ≥ 80% — % of issues resolved autonomously through agent collaboration.",
              "False Positive Rate ≤ 3% — Incorrectly flagged performance degradations.",
              "SLA Compliance (Performance Uptime) ≥ 99.5% — % of sessions maintaining SLA adherence."
            ]
          },
          {
            title: "Representative Statistics",
            content: [
              "Users Monitored: 26,000",
              "VPN/SASE Sessions Tracked: 1.48 million",
              "Average REI (User Experience Index): 88.3 / 100",
              "Detected Degradations: 312 (0.02 % of sessions)",
              "Auto-Remediated via SD-WAN Policy Changes: 241",
              "Average Latency Improvement: 19.1%",
              "Average MTTR: 12.6 min",
              "False Positive Rate: 2.4%",
              "Automation Adoption: 83%",
              "Compliance Rate: 99.7%",
              "Engineer Hours Saved: ≈ 280 per month",
              "Audit Reports Generated: 10 (CoreStack / Confluence)"
            ]
          }
        ]
      },
      {
        title: "4. Service Level Agreements (SLA)",
        subSections: [
          {
            title: "Process SLA",
            content: [
              "Session Data Collection Interval ≤ 60 s — Continuous telemetry from gateways and clients.",
              "Anomaly Detection & Correlation ≤ 2 min — AI correlation across device, transport, and gateway metrics.",
              "Remediation Feedback Execution ≤ 10 min — Path or policy update to resolve user degradation.",
              "Evidence Publication ≤ 1 hr — SLA and REI reports to CoreStack / Confluence.",
              "Agent Availability ≥ 99.9 % — Monitored via Grafana / Prometheus."
            ]
          }
        ]
      },
      {
        title: "5. Technical Context",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized \"UserXAI\" service.",
              "Primary APIs: VPN/SASE REST APIs, EDR/endpoint telemetry, QoS and SD-WAN policy APIs.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing REI baselines, telemetry datasets, and user trend data.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on user experience telemetry, app performance, and RunOps analytics KB.",
              "Security Controls: Encrypted credentials, TLS 1.3, anonymized user data, and RBAC separation.",
              "Outputs: Experience Score Reports (JSON/PDF), Root Cause Analysis, SLA Compliance Summaries.",
              "Monitoring: Grafana dashboards showing REI trends, degradation events, and resolution timelines.",
              "Error Handling: Auto-retry telemetry ingestion; alert SOC if user performance anomaly persists across 3 cycles."
            ]
          }
        ]
      },
      {
        title: "Summary",
        subSections: [
          {
            content: "The Remote Access Experience Agent is the digital experience guardian of the modern hybrid workforce. By unifying network telemetry, device posture, and AI reasoning, it transforms raw tunnel data into actionable experience insights and proactively identifies degradations, orchestrates optimizations, and ensures every remote user enjoys a secure, compliant, and seamless connectivity experience."
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Session telemetry anomalies, periodic baselining, or scheduled experience audits.",
      workingPlane: "Real-time experience plane with correlation, scoring, and feedback loops (cloud/hybrid).",
      memory: "REI baselines, historical telemetry, and remediation outcomes.",
      reasoning: "AI-driven root cause analysis mapping device, transport, and gateway signals to user experience impact.",
      execution: "Actions: raise remediation workflows, trigger policy adjustments, or mark sessions for deeper investigation."
    },
    kpis: [
      { label: "RunOps Experience Index (REI)", value: 88.3, unit: "/100", target: "≥ 85 / 100", color: "#10B981" },
      { label: "MTTD Compliance", value: 100, unit: "%", target: "< 2 min (target)", color: "#3B82F6" },
      { label: "MTTR Compliance", value: 100, unit: "%", target: "< 15 min (target)", color: "#3B82F6" },
      { label: "Automation Adoption", value: 83, unit: "%", target: "≥ 80%", color: "#6366F1" },
      { label: "False Positive Avoidance", value: 97.6, unit: "%", target: "≤ 3% FP (100 - FP%)", color: "#F59E0B" },
      { label: "SLA Compliance", value: 99.7, unit: "%", target: "≥ 99.5%", color: "#06B6D4" }
    ]
  },
   'geo-fencing-agent': {
    title: "VPN, Remote Access & SASE Geo-Fencing Agent",
    description: "Continuously monitors and enforces geo-based access policies across VPN and SASE infrastructures. Identifies risky or non-compliant geolocations and automatically challenges, restricts, or blocks user sessions based on corporate, regulatory, or threat intelligence criteria.",
    details: [
      {
        title: "Business Outcomes (Benefits)",
        points: [
          "Risk-Based Access Control: Dynamically block, challenge, or allow connections based on geolocation, identity, and risk context.",
          "Regulatory Compliance: Enforces ITAR, OFAC, and GDPR access restrictions automatically.",
          "Zero-Trust Integration: Aligns geographic access policy with identity and device posture validation.",
          "Threat Mitigation: Detects and blocks logins from anonymized IPs, TOR exit nodes, or known bad regions.",
          "Operational Efficiency: Automates geo-policy enforcement without impacting legitimate users through adaptive challenge workflows."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "In a global remote-access environment, unauthorized or anomalous connections from high-risk geographies pose major security and compliance risks.",
              "Static geo-blocking rules often fail — either over-blocking legitimate travelers or under-protecting against geo-spoofed and anonymized (VPN/proxy) logins.",
              "Enterprises need a context-aware, adaptive geo-fencing control layer that integrates identity, device posture, and threat intelligence to dynamically decide when to block, quarantine, or challenge access.",
              "Compliance regulations (e.g., ITAR, OFAC, GDPR) further demand accurate regional access enforcement to protect sensitive systems and data."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "Risk-Based Access Control: Dynamically block, challenge, or allow connections based on geolocation, identity, and risk context.",
              "Regulatory Compliance: Enforce access restrictions for sanctioned or sensitive regions.",
              "Reduced Risk and False Positives: Adaptive decisioning minimizes disruption to legitimate users while protecting against geo-spoofing.",
              "Operational Automation: Streamline enforcement across VPN, SASE and IdP ecosystems with audit-ready evidence."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "High-Risk Region Blocking: Automatically deny or quarantine logins from sanctioned or high-threat regions.",
              "Geo-Velocity Enforcement: Challenge users who appear to move between distant countries within impossible intervals.",
              "Adaptive Challenge Logic: Trigger MFA or conditional approval for moderate-risk locations.",
              "Regional Compliance Enforcement: Restrict access to applications based on data residency or export-control requirements.",
              "Geo-Spoofing Detection: Identify proxy, VPN, or TOR-based geo-masking attempts and deny access."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Contextual Awareness: Correlates user identity, device, and session telemetry with IP geolocation and ASN data.",
              "AI Reasoning: Determines legitimacy (travel vs. anomaly) based on user profile, time zone, and device fingerprint.",
              "Dynamic Decisioning: Applies tiered responses — block, challenge, or monitor — rather than blanket denials.",
              "Threat Intelligence Integration: Cross-references login IPs with threat feeds (CISA KEV, CrowdStrike Intel, AlienVault OTX).",
              "Self-Learning Baselines: Learns normal user travel and connection patterns to reduce false positives."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "IP Geolocation Correlation: Map user sessions to country, city, ASN, and ISP.",
              "Threat Intelligence Validation: Check IPs against known malicious sources and anonymizers (VPN/proxy/TOR).",
              "Geo-Velocity & Behavioral Analysis: Detect impossible travel or high-speed location shifts.",
              "Policy Enforcement: Dynamically apply block, MFA challenge, or restricted access based on risk level.",
              "Integration with ZTNA/SASE Policies: Enforce regional restrictions and access segmentation automatically.",
              "AI Reasoning Layer: Weighs identity, device, and travel context to determine legitimacy of geo anomalies.",
              "Compliance Mapping: Aligns enforcement with frameworks (NIST 800-207, ISO A.9.1.2, OFAC sanctions lists).",
              "Audit Logging & Evidence Generation: Record every blocked, challenged, or allowed session for regulatory traceability."
            ]
          },
          {
            title: "Integrations",
            content: [
              "VPN / SASE Controllers: Retrieve session and IP telemetry; enforce geo-policy actions (Bidirectional).",
              "Identity Providers (Entra ID, Okta, Ping): Correlate login sessions, MFA triggers, and risk scores (Inbound).",
              "GeoIP / Threat Intelligence APIs: Provide location, ASN, and IP risk context (Inbound).",
              "CASB / SWG Platforms: Extend geo-enforcement to SaaS and web access (Bidirectional).",
              "CoreStack GRC / Compliance Modules: Map geo-policy enforcement to compliance and regulatory controls (Bidirectional).",
              "ServiceNow / Jira: Log blocked events, challenges, and exceptions for audit workflow (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Evaluate risk context and produce human-readable justifications for actions (Inbound).",
              "Confluence / Data Lake: Archive policy actions, event histories, and compliance logs (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Session & Geo-Data Correlation: Collect IP, ASN, and geolocation data for all user sessions (50% automation).",
              "Tier 2 — Threat Feed & Policy Correlation: Evaluate risk based on region, ASN, and intelligence feeds (75% automation).",
              "Tier 3 — AI Risk Reasoning & Enforcement: Determine appropriate action (block, challenge, restrict) using reasoning models (90% automation).",
              "Tier 4 — Adaptive Learning & Self-Governance: Adjust enforcement thresholds based on user travel patterns and outcomes (100% agentic/autonomous)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Geo-Fencing Accuracy ≥ 97% — Correctly identified high-risk or compliant sessions.",
              "False Positive Rate ≤ 3% — Legitimate logins incorrectly challenged or blocked.",
              "Mean Time to Detect (MTTD) < 60 s — Time from login initiation to risk classification.",
              "Mean Time to Enforce (MTTE) < 2 min — Time from risk detection to enforcement action.",
              "Challenge Resolution Success ≥ 95% — Successful legitimate user verification via adaptive MFA.",
              "Compliance Enforcement Rate 100% — Enforcement actions meeting regulatory mandates."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Users Monitored: 25,000",
              "Total Sessions Analyzed: 1.6 million",
              "Anomalous Geolocation Events: 376",
              "High-Risk (Blocked): 122",
              "Medium-Risk (Challenged): 184",
              "Low-Risk (Monitored): 70",
              "Geo-Velocity / Impossible Travel Detections: 88",
              "False Positives: 2.4%",
              "Average MTTD: 38 seconds",
              "Average MTTE: 1.6 minutes",
              "Challenge Success Rate: 96.1%",
              "Compliance Enforcement Coverage: 100%",
              "Engineer Hours Saved: ~260 per month",
              "Audit Reports Generated: 10 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Session Geo Correlation ≤ 30 s — IP and ASN lookup per login.",
              "Risk Scoring & Classification ≤ 60 s — AI decisioning with threat feed enrichment.",
              "Policy Enforcement ≤ 2 min — Enforcement via VPN/SASE controllers or IdP.",
              "Evidence Publication ≤ 1 hr — Geo-policy audit report generation.",
              "Agent Availability ≥ 99.9% — HA RunOps cluster monitored via Prometheus / Grafana."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized \"GeoFenceAI\" service.",
              "Primary Interfaces: VPN/SASE REST APIs, IdP logs, GeoIP APIs, Threat Intelligence APIs, CoreStack GRC.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing country risk scores, geo-policies, and user baselines.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on identity risk, geo-intelligence, and RunOps Zero-Trust KB.",
              "Security Controls: TLS 1.3, anonymized geo data, encrypted credentials, RBAC-per-domain enforcement.",
              "Outputs: Geo-Fencing Action Log (JSON/PDF), Risk Assessment Report, Compliance Summary.",
              "Monitoring: Grafana dashboard: event volume, block/challenge trends, regional anomaly distribution.",
              "Error Handling: Auto-retry on failed API lookups; fallback to cached GeoIP DB; flag anomalies unresolved within two cycles."
            ]
          },
          {
            title: "Summary",
            content: [
              "The VPN, Remote Access & SASE Geo-Fencing Agent serves as the adaptive perimeter guard of the modern enterprise access fabric.",
              "By combining real-time geolocation intelligence, AI-driven reasoning, and regulatory context, it dynamically enforces region-based restrictions while minimizing user friction.",
              "Through continuous learning and multi-system correlation, it transforms static geographic filters into a self-learning, risk-aware access enforcement engine — ensuring that every connection is contextually verified, compliant, and secure within the enterprise’s zero-trust architecture."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Session events, IdP hooks, threat feed alerts, or scheduled baseline updates.",
      workingPlane: "Real-time decision plane with enrichment, reasoning, and safe-commit enforcement pipelines (cloud/hybrid).",
      memory: "Country/ASN risk scores, user travel baselines, suspicious IP fingerprints, and enforcement history.",
      reasoning: "AI-driven contextual analysis weighing identity, device posture, geo-velocity, and threat intelligence to justify enforcement actions.",
      execution: "Actions: block, quarantine, trigger MFA challenge, restrict session scope, create ITSM ticket, or log evidence."
    },
    kpis: [
      { label: "Geo-Fencing Accuracy", value: 97, unit: "%", target: "≥ 97%", color: "#10B981" },
      { label: "False Positive Rate", value: 3, unit: "%", target: "≤ 3%", color: "#F59E0B" },
      { label: "MTTD Compliance", value: 100, unit: "%", target: "< 60 s (target)", color: "#3B82F6" },
      { label: "MTTE Compliance", value: 100, unit: "%", target: "< 2 min (target)", color: "#3B82F6" },
      { label: "Challenge Resolution Success", value: 96.1, unit: "%", target: "≥ 95%", color: "#06B6D4" },
      { label: "Compliance Enforcement", value: 100, unit: "%", target: "100% coverage", color: "#6366F1" }
    ]
  },
   'bandwidth-throttling-agent': {
    title: "VPN, Remote Access & SASE Bandwidth Throttling Agent",
    description: "Continuously monitors VPN and SASE traffic to identify and throttle excessive per-user bandwidth consumption, ensuring equitable resource distribution and maintaining site and tunnel performance for all users.",
    details: [
      {
        title: "Business Outcomes (Benefits)",
        points: [
          "Fair Bandwidth Allocation: Prevents single-user or app misuse from degrading site-wide VPN/SASE performance.",
          "Operational Efficiency: Automates traffic enforcement without requiring manual QoS rule tuning.",
          "Enhanced User Experience: Preserves stable bandwidth and latency for mission-critical applications.",
          "Policy Compliance: Enforces corporate acceptable-use and zero-trust data handling policies.",
          "Cost Control: Reduces overconsumption on metered circuits or high-cost backbone links."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "In distributed remote-access environments, a single user or misconfigured device can consume disproportionate bandwidth — impacting thousands of concurrent sessions.",
              "Manual QoS or throttling configuration at VPN gateways is static and reactive, unable to distinguish legitimate workload surges from misuse.",
              "Streaming media, large file syncs, or unauthorized applications can degrade SASE PoP and site performance, particularly over limited circuits or high-cost MPLS links.",
              "Enterprises need an autonomous agent that monitors traffic behavior in real time, reasons about usage intent, and dynamically enforces throttling policies based on context, criticality, and compliance."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "Fair Bandwidth Allocation across users and devices.",
              "Automated, context-aware QoS enforcement that reduces manual tuning.",
              "Improved site and tunnel performance for mission-critical apps.",
              "Auditable enforcement records for compliance and forensics."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Single-User Bandwidth Abuse Detection: Identify outliers exceeding normal traffic thresholds for their user or device class.",
              "Dynamic QoS Enforcement: Throttle or reprioritize traffic per user, app, or session dynamically.",
              "Application-Level Throttling: Limit specific high-bandwidth, low-value applications (e.g., streaming, torrents).",
              "Adaptive Network Load Balancing: Redirect excess traffic to secondary links or SASE PoPs when thresholds reached.",
              "Compliance & Forensics Reporting: Provide audit records of traffic throttling actions, justifications, and outcomes."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Identity-Aware Control: Associates bandwidth usage with user, role, or device class.",
              "Contextual Reasoning: Evaluates the intent of bandwidth spikes — e.g., legitimate data sync vs. misuse.",
              "Autonomous Enforcement: Applies throttling dynamically, then restores bandwidth automatically when normal conditions return.",
              "Cross-Agent Collaboration: Coordinates with QoS Policy, Split-Tunnel, and App-Aware Routing Agents for global optimization.",
              "Learning Feedback Loop: Refines thresholds over time based on user patterns and site link capacity."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Real-Time Traffic Monitoring: Continuously analyze per-user and per-app bandwidth usage across VPN and SASE edges.",
              "Threshold & Anomaly Detection: Detect usage exceeding baselines by percentage, duration, or concurrency.",
              "Application-Level Policy Mapping: Identify the application category causing excess traffic (streaming, backup, sync, etc.).",
              "Dynamic Throttling Enforcement: Apply rate-limiting or class-of-service adjustment at the VPN gateway or SASE PoP.",
              "AI Reasoning Layer: Determine if throttling is justified, balancing risk, priority, and performance impact.",
              "Session Restoration Logic: Automatically lift throttles once conditions normalize or business exceptions apply.",
              "Compliance Mapping & Audit Logging: Record actions, causes, and affected sessions for transparency.",
              "Feedback Integration: Provide performance data back to QoS, SD-WAN, and Remote Experience Agents for closed-loop optimization."
            ]
          },
          {
            title: "Integrations",
            content: [
              "VPN / SASE Controllers: Enforce throttling or QoS rules dynamically (Bidirectional).",
              "Cortex Data Lake / Flow Analytics: Analyze traffic utilization per user, device, and app (Inbound).",
              "Identity Providers (Entra ID, Okta): Associate traffic with user identity and role (Inbound).",
              "QoS Policy / App-Aware Routing Agents: Coordinate enforcement and route optimization (Bidirectional).",
              "CoreStack GRC / Compliance Modules: Store throttling logs and compliance mapping (Bidirectional).",
              "ServiceNow / Jira: Log enforcement events and exceptions (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Evaluate usage anomalies, user intent, and produce action justification (Inbound).",
              "Confluence / Data Lake: Archive audit trails and performance impact reports (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Traffic Monitoring & Baseline Analysis: Establish usage baselines and identify high-bandwidth outliers (50% automation).",
              "Tier 2 — Anomaly Correlation & Context Evaluation: Determine risk or business justification for overuse (75% automation).",
              "Tier 3 — AI-Driven Throttling Enforcement: Execute or recommend throttling actions autonomously (90% automation).",
              "Tier 4 — Predictive Learning & Policy Adaptation: Forecast future congestion and proactively pre-throttle based on usage trends (100% agentic)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Bandwidth Fairness Index (BFI) ≥ 95% — Equitable distribution of bandwidth across users.",
              "Mean Time to Detect (MTTD) Abuse < 2 min — Time from bandwidth spike to detection.",
              "Mean Time to Enforce (MTTE) < 3 min — Time from detection to throttling action.",
              "Session Restoration Accuracy ≥ 97% — Correct lifting of throttle when traffic normalizes.",
              "False Enforcement Rate ≤ 3% — Incorrectly throttled legitimate usage.",
              "Site Performance Improvement ≥ 15% — Latency and throughput improvement post-enforcement."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Users Monitored: 24,800",
              "Sites / Gateways: 132",
              "Bandwidth Abuse Events Detected: 416",
              "Single-User Overuse (Non-Malicious): 268",
              "Unauthorized Application Usage: 102",
              "Malicious / Bot Activity: 46",
              "Auto-Throttling Actions Executed: 372",
              "Average Bandwidth Restored (Per Site): 19.4%",
              "False Enforcement Rate: 2.1%",
              "Average MTTD: 1.7 min — MTTE: 2.9 min",
              "Automation Adoption: 82%",
              "Compliance Rate: 98.7%",
              "Engineer Hours Saved: ~240 per month",
              "Audit Reports Generated: 10 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Telemetry Refresh Interval ≤ 30 s — Continuous per-user traffic updates.",
              "Anomaly Detection Cycle ≤ 2 min — AI-based traffic pattern analysis.",
              "Enforcement Command Push ≤ 3 min — Throttling via controller or policy API.",
              "Restoration Validation ≤ 5 min — Auto-lift of throttle post-normalization.",
              "Audit Evidence Publication ≤ 1 hr — Compliance and performance report generation.",
              "Agent Availability ≥ 99.9% — HA n8n cluster monitored via Grafana / Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized \"ThrottleAI\" microservice.",
              "Primary Interfaces: VPN/SASE REST APIs, NetFlow/IPFIX telemetry, IdP identity APIs.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing user/application usage baselines and policy thresholds.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on network behavior analytics, QoS policy logic, and RunOps Zero-Trust KB.",
              "Security Controls: TLS 1.3, RBAC isolation, anonymized user data, API-key vaulting.",
              "Outputs: Bandwidth Enforcement Report (JSON/PDF), Throttling Event Log, Site Performance Summary.",
              "Monitoring: Grafana dashboards showing bandwidth fairness index, event volume, and site performance post-enforcement.",
              "Error Handling: Auto-rollback throttling if site performance worsens; alert NOC on repeated enforcement for same user."
            ]
          },
          {
            title: "Summary",
            content: [
              "The VPN, Remote Access & SASE Bandwidth Throttling Agent functions as the autonomous fairness enforcer of the remote-access infrastructure.",
              "By intelligently detecting excessive consumption and applying context-aware throttling, it preserves WAN performance, enforces compliance, and protects user experience.",
              "Through continuous telemetry, AI reasoning, and cross-agent collaboration (QoS, Split-Tunnel, and Experience Agents), it transforms network resource management into a self-regulating, equitable, and zero-trust-aligned bandwidth optimization system for the modern hybrid enterprise."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Telemetry anomalies, flow analytics alerts, or scheduled baseline updates.",
      workingPlane: "Real-time enforcement plane with safe-commit throttling and rollback capabilities (cloud/hybrid).",
      memory: "Per-user and per-app baselines, historic throttle events, and whitelist exceptions.",
      reasoning: "AI-driven evaluation of intent, impact, and compliance before applying throttling actions.",
      execution: "Actions: apply rate-limits, adjust DSCP/CoS markings, reroute flows to alternate links, create ITSM tickets, or lift throttles."
    },
    kpis: [
      { label: "Bandwidth Fairness Index", value: 95, unit: "%", target: "≥ 95%", color: "#10B981" },
      { label: "MTTD Abuse", value: 100, unit: "%", target: "< 2 min (target)", color: "#3B82F6" },
      { label: "MTTE", value: 100, unit: "%", target: "< 3 min (target)", color: "#3B82F6" },
      { label: "Session Restoration Accuracy", value: 97, unit: "%", target: "≥ 97%", color: "#06B6D4" },
      { label: "False Enforcement Avoidance", value: 97.9, unit: "%", target: "≤ 3% FP (100 - FP%)", color: "#F59E0B" },
      { label: "Automation Adoption", value: 82, unit: "%", target: "≥ 80%", color: "#6366F1" }
    ]
  },
   'vpn-cert-renewal-agent': {
    title: "VPN, Remote Access & SASE VPN Certificate Renewal Agent",
    description: "Continuously tracks, renews, validates, and redeploys VPN certificates across gateways, SASE edges, and remote endpoints — ensuring uninterrupted secure connectivity and full compliance with enterprise cryptographic and zero-trust standards.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Zero-Downtime Renewal: Automatically renews and installs certificates before expiry, preventing service disruption.",
          "Cryptographic Compliance: Ensures all VPN certs conform to enterprise key strength, signing algorithm, and chain-of-trust requirements.",
          "Operational Efficiency: Eliminates manual certificate tracking and deployment cycles.",
          "Enhanced Security Posture: Removes expired, weak, or unauthorized certificates automatically.",
          "Continuous Audit Readiness: Provides immutable audit trail for all certificate renewals and deployments."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Expired or mismanaged VPN certificates are a leading cause of remote-access outages, trust failures, and compliance gaps.",
              "Manual certificate renewal processes are error-prone and often decentralized across multiple PKI authorities, VPN vendors, and cloud platforms.",
              "Missed expirations or improper key distribution can disrupt mission-critical connectivity and violate cryptographic standards (NIST, PCI, ISO).",
              "Enterprises require a fully automated certificate lifecycle management agent that monitors, renews, validates, and deploys VPN certificates without human intervention — while maintaining audit traceability and compliance."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "Zero-Downtime Renewal: Automatically renews and installs certificates before expiry, preventing service disruption.",
              "Cryptographic Compliance: Ensures all VPN certs conform to enterprise key strength, signing algorithm, and chain-of-trust requirements.",
              "Operational Efficiency: Eliminates manual certificate tracking and deployment cycles.",
              "Enhanced Security Posture: Removes expired, weak, or unauthorized certificates automatically.",
              "Continuous Audit Readiness: Provides immutable audit trail for all certificate renewals and deployments."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Certificate Discovery & Inventory: Continuously enumerate all VPN/SASE certificates across devices and controllers.",
              "Expiry Prediction & Prioritization: Identify certificates expiring within configurable thresholds (e.g., 30, 15, 7 days).",
              "Automated Renewal Workflow: Request new certs from enterprise or public CA (e.g., Microsoft CA, DigiCert, Entrust).",
              "Chain-of-Trust Validation: Verify root/intermediate authority integrity and key length compliance.",
              "Policy Baseline Enforcement: Validate cryptographic strength and signing algorithms against enterprise baseline.",
              "Deployment & Verification: Install certificates to VPN/SASE gateways, test tunnel negotiation, and validate trust chain.",
              "AI Reasoning Layer: Evaluate impact, risk, and renewal urgency across infrastructure and recommend sequencing.",
              "Audit & Evidence Generation: Record renewal, validation, and deployment actions for compliance frameworks."
            ]
          },
          {
            title: "Integrations",
            content: [
              "VPN / SASE Controllers: Retrieve certificate inventory, apply new certs, and validate tunnel negotiation (Bidirectional).",
              "PKI / Certificate Authority (CA): Request, renew, and revoke certificates (Bidirectional).",
              "CoreStack GRC / Compliance Modules: Store evidence and compliance mapping for crypto policy enforcement (Bidirectional).",
              "Identity Providers (Entra ID, Okta): Synchronize user/client certificate profiles (Bidirectional).",
              "ServiceNow / Jira: Log renewal events, approvals, or failures (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Evaluate certificate impact, generate priority ranking, and produce human-readable narratives (Inbound).",
              "Confluence / Data Lake: Archive certificates, renewal logs, and audit artifacts (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Certificate Compliance Rate ≥ 98% — Certs meeting crypto and policy standards.",
              "Mean Time to Detect (MTTD) Expiration Risk < 1 hr — Time from detection to alert.",
              "Mean Time to Renew (MTTRenew) < 5 min — Time to complete renewal and validation cycle.",
              "Renewal Automation Success Rate ≥ 95% — Successful renewals executed automatically.",
              "Zero-Downtime Renewal Rate 100% — Renewals completed without tunnel outage.",
              "Audit Evidence Coverage 100% — Certificates logged and archived post-renewal."
            ]
          },
          {
            title: "Representative Statistics",
            content: [
              "Gateways / Edges Monitored: 84",
              "Certificates Tracked: 1,236",
              "Certificates Renewed Automatically: 312",
              "Expiring Certificates Detected (≤30 days): 137",
              "Crypto Policy Violations Detected: 11 (all remediated)",
              "Average Renewal Time (per cert): 3.9 min",
              "Zero-Downtime Renewal Rate: 100%",
              "Renewal Automation Success: 97.2%",
              "Compliance Rate: 99.1%",
              "Average MTTD (Expiring Certs): 27 min",
              "False Alert Rate: 1.8%",
              "Engineer Hours Saved: ~260 / month",
              "Audit Reports Generated: 12 (CoreStack / Confluence)"
            ]
          }
        ]
      },
      {
        title: "4. Service Level Agreements (SLA)",
        subSections: [
          {
            title: "Process SLA",
            content: [
              "Certificate Inventory Update ≤ 4 hrs — Continuous scanning of devices.",
              "Expiry Detection & Forecasting ≤ 1 hr — Predictive alert generation.",
              "Renewal Request & Deployment Cycle ≤ 5 min — Automated CA request to gateway activation.",
              "Post-Renewal Validation ≤ 2 min — Chain and negotiation verification.",
              "Evidence Publication ≤ 1 hr — Audit-ready report generation.",
              "Agent Availability ≥ 99.9 % — HA n8n cluster monitored via Grafana / Prometheus."
            ]
          }
        ]
      },
      {
        title: "5. Technical Context",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized \"VPNCertAI\" service.",
              "Primary Interfaces: VPN/SASE REST APIs, PKI/CA REST APIs, SNMPv3 for cert telemetry.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing certificate metadata, renewal history, and crypto baselines.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on PKI workflows, X.509 standards, and RunOps Zero-Trust KB.",
              "Security Controls: Mutual TLS for CA integration, encrypted credential vault, RBAC isolation.",
              "Outputs: Certificate Renewal Report (JSON/PDF), Compliance Snapshot, Renewal Log, Audit Evidence Bundle.",
              "Monitoring: Grafana dashboards for expiry timelines, renewal rates, and compliance metrics.",
              "Error Handling: Auto-retry CA request failures; fallback to cached cert; alert SOC on repeated renewal errors."
            ]
          }
        ]
      },
      {
        title: "Summary",
        subSections: [
          {
            content: "The VPN Certificate Renewal Agent is the autonomous cryptographic maintenance layer for secure enterprise connectivity. By integrating PKI, VPN, and SASE ecosystems with AI-driven lifecycle intelligence, it eliminates manual renewal risks, guarantees continuous trust, and ensures every certificate remains valid, compliant, and policy-aligned."
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Expiry thresholds, CA notifications, telemetry anomalies, or scheduled inventory scans.",
      workingPlane: "Lifecycle orchestration plane with CA connectors and safe-commit deployment pipelines.",
      memory: "Certificate inventory, renewal history, and policy baselines.",
      reasoning: "AI-driven prioritization and sequencing for renewals, impact analysis, and rollback planning.",
      execution: "Actions: request certs, deploy to gateways, validate tunnels, rollback on failure, and log evidence."
    },
    kpis: [
      { label: "Certificate Compliance Rate", value: 99.1, unit: "%", target: "≥ 98%", color: "#10B981" },
      { label: "MTTD Expiry Detection", value: 100, unit: "%", target: "< 1 hr (target)", color: "#3B82F6" },
      { label: "MTTRenew Compliance", value: 100, unit: "%", target: "< 5 min (target)", color: "#3B82F6" },
      { label: "Renewal Automation Success", value: 97.2, unit: "%", target: "≥ 95%", color: "#06B6D4" },
      { label: "Zero-Downtime Renewal", value: 100, unit: "%", target: "100%", color: "#10B981" },
      { label: "Audit Coverage", value: 100, unit: "%", target: "100%", color: "#6366F1" }
    ]
  },
   'user-session-analyzer-agent': {
    title: "VPN, Remote Access & SASE User Session Analyzer Agent",
    description: "Continuously analyzes remote-access user sessions across VPN and SASE gateways to detect anomalous authentication behaviors, concurrent logins, credential sharing, and impossible-travel events — ensuring secure, compliant, and identity-verified connectivity across the enterprise.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Proactive Threat Detection: Identifies credential misuse, concurrent logins, and impossible-travel anomalies automatically.",
          "Identity Assurance: Validates session origin, device, and user consistency for every VPN/SASE connection.",
          "Operational Efficiency: Reduces investigation time and false positives through contextual AI reasoning.",
          "Zero-Trust Enforcement: Triggers adaptive access controls or step-up authentication based on risk score.",
          "Regulatory Alignment: Provides audit trails for access monitoring (PCI-DSS, HIPAA, ISO 27001)."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "With the hybrid workforce, VPN and SASE remote-access volume has surged — increasing risk of credential theft, account sharing, and insider misuse.",
              "Legacy monitoring only tracks connection status, not behavioral anomalies (e.g., logins from multiple countries within minutes).",
              "Security teams struggle to correlate user identity, device posture, geolocation, and session metadata across distributed gateways and clouds.",
              "Manual investigations are slow, reactive, and lack the intelligence to differentiate between legitimate travel and malicious activity.",
              "Enterprises need a continuous, AI-driven session analytics agent that reasons about user behavior, detects anomalies in real time, and integrates with zero-trust enforcement workflows."
            ]
          },
          {
            title: "Business Outcomes",
            content: [
              "Proactive Threat Detection: Identifies credential misuse, concurrent logins, and impossible-travel anomalies automatically.",
              "Identity Assurance: Validates session origin, device, and user consistency for every VPN/SASE connection.",
              "Operational Efficiency: Reduces investigation time and false positives through contextual AI reasoning.",
              "Zero-Trust Enforcement: Triggers adaptive access controls or step-up authentication based on risk score.",
              "Regulatory Alignment: Provides audit trails for access monitoring (PCI-DSS 10.2, HIPAA §164.312, ISO 27001 A.9)."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Session Metadata Collection: Gather session start/stop time, IP, ASN, geolocation, device ID, and authentication source.",
              "User Behavior Modeling: Establish baselines of normal activity per user, region, and time window.",
              "Geo-Velocity & Impossible-Travel Detection: Correlate login times and coordinates to detect physical impossibility.",
              "Concurrent Session Correlation: Identify multiple active logins from distinct IPs or devices for the same user.",
              "Device & Posture Validation: Confirm compliance via integration with endpoint protection or MDM tools.",
              "AI Reasoning & Risk Scoring: Classify sessions as normal, anomalous, or malicious based on behavioral context.",
              "Automated Response Actions: Trigger ITSM alerts, session termination, or MFA challenge via IAM integration.",
              "Audit Reporting: Generate compliance and forensic summaries for all anomalies and responses."
            ]
          },
          {
            title: "Integrations",
            content: [
              "VPN / SASE Gateways: Retrieve session logs, tunnel info, and user IPs (Inbound).",
              "Identity Providers (Entra ID, Okta, Ping, Duo): Correlate login events, user IDs, and authentication factors (Inbound).",
              "Endpoint Security / MDM Systems: Validate device posture and compliance (Inbound).",
              "GeoIP / Threat Intelligence APIs: Enrich logins with location, ASN, and reputation data (Inbound).",
              "CoreStack GRC / Compliance Modules: Map detected anomalies to control frameworks (Bidirectional).",
              "ServiceNow / Jira: Create or track investigation tickets for confirmed anomalies (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Perform behavioral analysis, geolocation correlation, and narrative summarization (Inbound).",
              "Confluence / Data Lake: Archive anomaly reports, risk trends, and compliance evidence (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Anomaly Detection Accuracy ≥ 95% — Correct detection of malicious vs legitimate sessions.",
              "False Positive Rate ≤ 3% — Benign anomalies incorrectly flagged.",
              "Mean Time to Detect (MTTD) < 1 min — Time from anomalous login to detection.",
              "Mean Time to Respond (MTTR) < 5 min — Time from detection to enforcement (alert, MFA, revoke).",
              "Automation Adoption Rate ≥ 80% — Responses executed autonomously.",
              "Compliance Evidence Availability 100% — Audit logs and reports generated for all incidents."
            ]
          },
          {
            title: "Representative Statistics",
            content: [
              "Users Monitored: 23,800",
              "VPN / SASE Sessions Analyzed: 1.42 million",
              "Detected Anomalies: 328",
              "Impossible-Travel Events: 91",
              "Concurrent Sessions: 112",
              "Out-of-Region Logins: 78",
              "Posture Violations: 47",
              "Confirmed Malicious Sessions: 27",
              "Auto-Remediated via MFA or Disconnect: 244",
              "False Positive Rate: 2.6%",
              "Average MTTD: 41 seconds",
              "Average MTTR: 3.8 minutes",
              "User Risk Reduction (Post-Remediation): 94%",
              "Engineer Hours Saved: ≈ 260 per month",
              "Audit Reports Generated: 12 (CoreStack / Confluence)"
            ]
          }
        ]
      },
      {
        title: "4. Service Level Agreements (SLA)",
        subSections: [
          {
            title: "Process SLA",
            content: [
              "Session Data Collection Interval ≤ 60 s — Continuous log ingestion from gateways/IdP.",
              "Anomaly Detection Cycle ≤ 2 min — AI correlation of session data and geolocation.",
              "Response Execution (MFA / Revoke) ≤ 5 min — Trigger adaptive access policy.",
              "Evidence Publication ≤ 1 hr — Compliance report generation.",
              "Agent Availability ≥ 99.9 % — HA n8n cluster monitored via Grafana / Prometheus."
            ]
          }
        ]
      },
      {
        title: "5. Technical Context",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized \"SessionGuardAI\" service.",
              "Primary Interfaces: VPN/SASE Controller APIs, IdP Logs (SCIM/SAML), GeoIP APIs, EDR/MDM posture APIs.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing behavioral baselines, user profiles, anomaly history.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on behavioral anomaly detection, geo-velocity patterns, and RunOps Zero-Trust KB.",
              "Security Controls: Read-only telemetry, TLS 1.3 encryption, anonymized geo-data storage, RBAC isolation.",
              "Outputs: Anomaly Report (JSON/PDF), User Risk Dashboard, Compliance Snapshot, ITSM Ticket Log.",
              "Monitoring: Grafana dashboard: anomaly trends, MTTD/MTTR metrics, risk distribution by user/region.",
              "Error Handling: Auto-retry IdP API queries; fail-safe event buffering for high-volume ingestion periods."
            ]
          }
        ]
      },
      {
        title: "Summary",
        subSections: [
          {
            content: "The User Session Analyzer Agent fuses identity, device, and network telemetry to detect anomalous login behaviors, impossible travel, and credential misuse — automatically triggering adaptive enforcement under zero-trust principles. Through continuous learning, AI reasoning, and integration with IAM and SASE systems, it transforms user session monitoring into a self-learning, real-time access assurance engine."
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Session events, monitoring alerts, IdP hooks, or scheduled baselining jobs.",
      workingPlane: "High-throughput event plane with enrichment and reasoning tiers (cloud/hybrid).",
      memory: "Behavioral baselines per user, session history, and anomaly fingerprints.",
      reasoning: "AI-driven behavioral analysis with geo-velocity, ASN, and device posture correlation.",
      execution: "Actions: session revoke, MFA challenge, ITSM ticket creation, or contextual alerting."
    },
    kpis: [
      { label: "Anomaly Detection Accuracy", value: 95, unit: "%", target: "≥ 95%", color: "#10B981" },
      { label: "False Positive Avoidance", value: 97.4, unit: "%", target: "≤ 3% FP (100 - FP%)", color: "#F59E0B" },
      { label: "MTTD Compliance", value: 100, unit: "%", target: "< 1 min (target)", color: "#3B82F6" },
      { label: "MTTR Compliance", value: 100, unit: "%", target: "< 5 min (target)", color: "#3B82F6" },
      { label: "Automation Adoption", value: 80, unit: "%", target: "≥ 80%", color: "#6366F1" },
      { label: "Evidence Availability", value: 100, unit: "%", target: "100% audit evidence", color: "#06B6D4" }
    ]
  },
  'edge-device-monitor-agent': {
    title: "Edge Device Monitor Agent",
    description: "Continuously tracks edge router and firewall health, resource utilization, and connectivity metrics to **predict failures, prevent saturation, and maintain performance, compliance, and uptime** across distributed branch and edge infrastructures.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Improved Network Availability: Detects health degradation early to prevent outages.",
          "Predictive Capacity Management: Forecasts utilization and resource saturation before impact.",
          "Operational Efficiency: Automates performance tracking and alert correlation across thousands of devices.",
          "Enhanced Resilience: Enables proactive remediation for critical routers and firewalls.",
          "Compliance Assurance: Maintains uptime and availability metrics aligned with enterprise SLAs."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "As enterprises scale edge and branch deployments, monitoring becomes fragmented across multiple vendors and platforms.",
              "Traditional NMS tools focus on link status, missing contextual intelligence about CPU, memory, temperature, and throughput thresholds.",
              "Degraded device health or capacity exhaustion leads to unplanned outages, packet drops, and performance bottlenecks that impact SD-WAN overlays and secure access.",
              "Organizations need an agentic, continuously validating edge device monitor that detects degradation early, reasons about capacity trends, and autonomously triggers alerts or remediation workflows."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Early detection of health degradation prevents unplanned device outages.",
              "Accurate forecasting of CPU, memory, or throughput saturation avoids bottlenecks.",
              "Automated tracking and correlation of performance data reduces manual monitoring effort.",
              "Proactive intervention ensures high resilience for mission-critical edge devices.",
              "Continuous evidence generation confirms adherence to service availability SLAs."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Device Health Monitoring: Track CPU, memory, temperature, and hardware status of edge devices.",
              "Interface and Throughput Tracking: Monitor bandwidth utilization, packet loss, and error rates per interface.",
              "Predictive Capacity Forecasting: Use historical trends to forecast capacity or failure risk.",
              "Multi-Vendor Telemetry Normalization: Aggregate and normalize SNMP, API, and NetFlow data across vendors.",
              "Incident & Compliance Reporting: Generate health scorecards, SLA compliance metrics, and fault logs."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Device Health Telemetry Collection: Collect SNMP, API, or streaming telemetry for CPU, memory, and temperature.",
              "Interface Monitoring: Track port utilization, duplex errors, CRC counts, and link states.",
              "Throughput & Latency Analytics: Analyze WAN and LAN performance at each branch edge.",
              "Capacity Forecasting: Predict device overload or performance degradation based on trends.",
              "Alarm Correlation Engine: Distinguish transient spikes from persistent hardware or link issues.",
              "AI Reasoning Layer: Diagnose probable root causes and suggest or trigger corrective workflows.",
              "Audit & SLA Logging: Maintain device health baselines, historical data, and compliance evidence."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Network Devices (Cisco, Fortinet, Palo Alto, Juniper, Aruba, Meraki): Gather SNMP, API, and telemetry data for routers and firewalls (Inbound).",
              "SD-WAN Controllers (Cisco vManage, Fortinet, VMware, Versa): Correlate overlay health and underlay transport conditions (Bidirectional).",
              "NMS / Observability Tools (Grafana, Datadog, ThousandEyes, Splunk): Share metrics and receive contextual analytics data (Bidirectional).",
              "ServiceNow / Jira: Create automated incidents or change requests for hardware remediation (Bidirectional).",
              "SOAR Platforms (Palo Alto XSOAR, Splunk Phantom): Trigger remediation workflows for degraded devices (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Evaluate device health trends, determine failure risk, and generate recommendations (Inbound).",
              "Confluence / Data Lake: Archive health scorecards, telemetry history, and compliance reports (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Health & Telemetry Collection: Continuously gather device and interface metrics across edge routers/firewalls (50 %).",
              "Tier 2 — Anomaly Detection & Threshold Validation: Identify performance or health deviations from baseline norms (75 %).",
              "Tier 3 — AI Reasoning & Predictive Diagnostics: Determine root causes and anticipate resource exhaustion (90 %).",
              "Tier 4 — Proactive Remediation & Learning Optimization: Preemptively trigger automation or update thresholds based on learning (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Device Health Compliance Rate ≥ 98 % (% of devices meeting operational thresholds).",
              "Mean Time to Detect (MTTD) Degradation < 5 min (Time from health anomaly occurrence to detection).",
              "Mean Time to Recover (MTTR) < 30 min (Time from detection to resolution or alert closure).",
              "Forecast Accuracy (Capacity / Failure) ≥ 95 % (Accuracy of predictive capacity or fault warnings).",
              "False Alarm Rate ≤ 3 % (% of benign alerts incorrectly classified as critical).",
              "Telemetry Polling Interval ≤ 5 min (Continuous SNMP/API polling or streaming telemetry)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'EdgeHealthAI' microservice.",
              "Primary Interfaces: SNMP v3, REST APIs, NetFlow/sFlow, SD-WAN and firewall management APIs.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing device health baselines, telemetry history, and anomaly events.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on edge telemetry analytics, device health diagnostics, and RunOps network resilience KB.",
              "Security Controls: TLS 1.3, credential vault, RBAC isolation, secure polling intervals with SNMPv3 encryption.",
              "Error Handling: Auto-retry failed telemetry queries; escalate to NOC for manual validation if polling fails twice consecutively.",
              "Outputs: Edge Health Report (PDF/JSON), Predictive Capacity Summary, Device Risk Scorecard, SLA Compliance Log."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Continuous telemetry polling (Telemetry Polling Interval ≤ 5 min) or SD-WAN overlay degradation alert.",
      workingPlane: "Aggregates and normalizes multi-vendor health (CPU/Memory/Temp) and performance (Throughput/Errors) metrics against learned baselines.",
      memory: "Persists historical telemetry data and anomaly event logs to power predictive analysis and dynamically refine operational thresholds.",
      reasoning: "AI correlates symptomatic alerts, diagnoses the probable root cause (e.g., thermal vs. software/link issue), and forecasts capacity exhaustion (Forecast Accuracy ≥ 95 %).",
      execution: "Triggers autonomous remediation workflows via SOAR/orchestration or generates automated, enriched incidents in ServiceNow/Jira for hardware replacement/reboot (MTTR < 30 min)."
    },
    kpis: [
      { label: "Device Health Compliance Rate", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD) Degradation", value: "5", unit: "min", target: "< 5 min", color: "#2563eb" },
      { label: "Mean Time to Recover (MTTR)", value: "30", unit: "min", target: "< 30 min", color: "#f59e0b" },
      { label: "Forecast Accuracy (Capacity / Failure)", value: "95", unit: "%", target: "≥ 95%", color: "#ef4444" }
    ]
  },
  'circuit-failover-agent': {
    title: "Circuit Failover Agent",
    description: "Continuously monitors WAN circuits, underlay transport, and ISP health, automatically executing **failover and restoration workflows** to secondary or LTE connections when degradation or outages are detected — maintaining seamless connectivity and zero downtime for branch operations.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Zero-Downtime Connectivity: Provides seamless failover to backup circuits during outages.",
          "Proactive Link Monitoring: Detects degradation before full link failure.",
          "Reduced MTTR for Circuit Outages: Automates response workflows to restore service quickly.",
          "Cost Efficiency: Minimizes LTE or metered circuit use through intelligent restoration.",
          "SLA & Compliance Assurance: Maintains uptime targets and generates audit-ready failover logs."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Branch networks depend on dual or multi-homed WAN circuits (MPLS, broadband, LTE/5G) for connectivity, but manual or static failover configurations often result in delay, packet loss, or complete outage during link failures.",
              "Without real-time telemetry and automated decision-making, failovers may trigger too slowly or too aggressively, affecting application SLAs and user experience.",
              "Enterprises require an autonomous, telemetry-driven failover agent that dynamically evaluates link health, executes traffic reroutes, and validates connectivity restoration without human intervention."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Seamless, automated rerouting ensures continuous connectivity during primary link failures.",
              "Early detection of latency, jitter, or packet loss prevents service-impacting outages.",
              "Fast, automated failover and failback processes reduce service interruption time (MTTR).",
              "Intelligent failback logic prevents unnecessary or prolonged use of expensive metered backup links.",
              "Auditable logs confirm adherence to enterprise uptime targets and regulatory requirements."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Primary-to-Backup Failover: Automatically shift traffic to backup ISPs or LTE connections during link loss or degradation.",
              "Degradation-Based Rerouting: Trigger failover when latency, jitter, or packet loss exceed thresholds.",
              "LTE/5G Resiliency Integration: Activate and validate cellular backup during WAN circuit outages.",
              "Automated Failback: Return to primary link once stability is verified.",
              "Audit & SLA Verification: Log failover events, timing, and service impact for compliance tracking."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Real-Time Circuit Health Monitoring: Continuously measure latency, jitter, packet loss, and throughput per circuit.",
              "Dynamic Failover Triggering: Initiate traffic shifts based on AI-evaluated performance degradation.",
              "LTE/5G Backup Activation: Bring up cellular links automatically when wired circuits fail.",
              "Path Validation & Failback: Validate primary circuit restoration before returning to normal routing.",
              "SD-WAN Integration: Leverage overlay controllers (Cisco, Fortinet, VMware, Versa) to execute failover policies.",
              "AI Reasoning Engine: Evaluate link state, correlate alarms, and execute optimized failover sequences.",
              "Compliance & Audit Logging: Record event timelines, SLA adherence, and recovery success metrics."
            ]
          },
          {
            title: "Integrations",
            content: [
              "SD-WAN Controllers (Cisco vManage, Fortinet, VMware, Versa): Manage link state, routing policy, and failover execution (Bidirectional).",
              "Edge Routers & Firewalls (Cisco, Palo Alto, Fortinet, Juniper): Collect link telemetry and apply routing changes (Bidirectional).",
              "LTE / 5G Modems & Gateways (Cradlepoint, Sierra Wireless): Activate and validate backup connections (Bidirectional).",
              "Network Monitoring / Observability Tools (ThousandEyes, Grafana, Splunk): Correlate link health, latency, and packet loss (Inbound).",
              "CoreStack GRC / Compliance Modules: Log failover evidence, SLA metrics, and audit reports (Bidirectional).",
              "ServiceNow / Jira: Create incident and change records for failover events (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Evaluate root cause, determine failover confidence, and recommend timing (Inbound).",
              "Confluence / Data Lake: Archive failover logs, performance metrics, and compliance reports (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Circuit Health Monitoring & Baseline Collection: Continuously collect link telemetry and SLA baselines (50 %).",
              "Tier 2 — Degradation Detection & Anomaly Correlation: Identify link degradation and cross-validate against provider metrics (75 %).",
              "Tier 3 — AI Reasoning & Failover Execution: Trigger failover and LTE activation automatically with rollback validation (90 %).",
              "Tier 4 — Predictive Failover Optimization & Continuous Learning: Anticipate failures and pre-stage routing actions based on trends (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Mean Time to Detect (MTTD) Circuit Failure < 1 min (Time from outage occurrence to anomaly detection).",
              "Mean Time to Failover (MTTF) < 3 min (Time from detection to successful traffic reroute).",
              "Mean Time to Restore (MTTR) < 10 min (Time to return to primary link post-recovery).",
              "False Failover Rate ≤ 2 % (Incorrect failovers triggered by transient degradation).",
              "SLA Uptime Compliance ≥ 99.9 % (Total uptime across branch circuits).",
              "Telemetry Refresh Interval ≤ 30 sec (Continuous measurement of link KPIs)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'CircuitFailoverAI' microservice.",
              "Primary Interfaces: SD-WAN controller APIs, router/firewall management APIs, LTE modem APIs.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing link telemetry baselines, failover logs, and performance thresholds.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on WAN resiliency, SD-WAN link analytics, and RunOps Zero-Trust KB.",
              "Security Controls: TLS 1.3, signed API actions, BGP route authentication, and RBAC-per-branch enforcement.",
              "Error Handling: Auto-retry failover actions; alert NOC if backup circuit validation fails twice consecutively.",
              "Outputs: Circuit Failover Report (PDF/JSON), Uptime Compliance Snapshot, Provider SLA Deviation Log."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Continuous link performance telemetry (Latency, Jitter, Packet Loss) refresh (Telemetry Refresh Interval ≤ 30 sec).",
      workingPlane: "Correlates real-time underlay metrics across primary and secondary circuits, and interfaces with SD-WAN controllers and LTE APIs.",
      memory: "Persists link telemetry baselines, historical performance degradation patterns, and past failover/failback timing to optimize future actions.",
      reasoning: "AI evaluates the root cause and persistence of link degradation, determines the necessity and confidence for failover (MTTD < 1 min), and executes the optimal traffic shift sequence.",
      execution: "Triggers dynamic traffic reroute/SD-WAN policy shift, activates LTE/5G backup, and validates service restoration/stability before performing automated failback (MTTF < 3 min)."
    },
    kpis: [
      { label: "Mean Time to Detect (MTTD) Circuit Failure", value: "1", unit: "min", target: "< 1 min", color: "#10b981" },
      { label: "Mean Time to Failover (MTTF)", value: "3", unit: "min", target: "< 3 min", color: "#2563eb" },
      { label: "Mean Time to Restore (MTTR)", value: "10", unit: "min", target: "< 10 min", color: "#f59e0b" },
      { label: "False Failover Rate", value: "2", unit: "%", target: "≤ 2%", color: "#ef4444" }
    ]
  },
  
  'peering-analyzer-agent': {
    title: "Peering Analyzer Agent",
    description: "Continuously analyzes routing paths, latency, and traffic distribution across ISP and cloud peering relationships to **optimize performance, ensure cost efficiency, and maintain SLA compliance** for enterprise and hybrid network connectivity.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Optimized Routing Efficiency: Ensures enterprise traffic follows the most performant and cost-effective paths.",
          "Reduced Latency & Packet Loss: Detects and mitigates inefficient or congested peering routes.",
          "Improved Multi-ISP Utilization: Balances load across peering partners based on health and capacity.",
          "Cross-Cloud Performance Consistency: Maintains optimal routes to cloud providers and CDN edges.",
          "FinOps & SLA Governance: Correlates peering quality to contractual SLAs and bandwidth spend."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprises connect to multiple ISPs and cloud on-ramps (AWS, Azure, GCP) to achieve redundancy and performance, but routing inefficiencies, asymmetric paths, or poor peering often increase latency and cost.",
              "Manual routing analysis or BGP policy tuning is time-consuming, reactive, and lacks visibility across providers.",
              "Inefficient peering can lead to unbalanced traffic distribution, higher transit costs, and inconsistent user experience.",
              "Organizations need an autonomous agent that continuously evaluates peering quality, detects inefficiencies, and recommends or executes routing optimizations based on performance, cost, and compliance policies."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Guaranteed selection of the most performant and cost-effective paths for enterprise traffic.",
              "Proactive detection and mitigation of congested or inefficient peering routes reduces latency.",
              "Load balancing across peering partners based on real-time health and capacity metrics.",
              "Consistent, optimized routing to major cloud providers and Content Delivery Network (CDN) edges.",
              "Governance by linking peering quality directly to FinOps policies and contractual SLAs."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "ISP Peering Efficiency Audit: Evaluate performance, congestion, and packet loss per ISP peer.",
              "Cloud On-Ramp Performance Analysis: Validate routes and latency to AWS, Azure, and GCP edges.",
              "Path Asymmetry Detection: Identify and remediate inconsistent inbound/outbound routes.",
              "Cost & SLA Optimization: Recommend preferred peers based on contract terms and performance KPIs.",
              "Automated Routing Policy Tuning: Adjust BGP preferences or prepend policies to rebalance traffic."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Path Analytics & Traceroute Intelligence: Perform active probing and BGP path analysis to evaluate peering routes.",
              "Latency & Loss Monitoring: Continuously track performance across ISP peers and cloud providers.",
              "BGP Policy Validation: Analyze routing tables for policy compliance, prefix advertisements, and AS-path efficiency.",
              "AI Reasoning & Optimization: Determine root cause of performance inefficiency and recommend routing adjustments.",
              "Autonomous Policy Orchestration: Modify BGP weights, prepends, or local preference to optimize route selection.",
              "Cost & SLA Integration: Correlate network performance with bandwidth spend and provider SLAs.",
              "Audit & Reporting: Generate peering efficiency reports, SLA summaries, and optimization recommendations."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Edge Routers / BGP Gateways (Cisco, Juniper, Fortinet, Palo Alto): Retrieve routing tables and apply policy updates (Bidirectional).",
              "ISPs / Carrier APIs (Equinix, Lumen, AT&T, Tata, BT): Collect peering telemetry and SLA metrics (Inbound).",
              "Cloud Providers (AWS, Azure, GCP, OCI): Validate cloud on-ramp performance and routing consistency (Bidirectional).",
              "NMS / Observability Systems (ThousandEyes, Grafana, Splunk): Correlate latency and throughput data (Bidirectional).",
              "ServiceNow / Jira: Log optimization tickets, route change approvals, and provider escalations (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Correlate telemetry, diagnose inefficiencies, and recommend route-policy optimizations (Inbound).",
              "Confluence / Data Lake: Archive peering analysis reports, trend data, and SLA evidence (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Path Telemetry Collection & Baseline Mapping: Gather BGP, traceroute, and performance metrics across peers (50 %).",
              "Tier 2 — Inefficiency Detection & SLA Correlation: Identify poor-performing or costly peering routes (75 %).",
              "Tier 3 — AI Reasoning & Policy Recommendation: Recommend BGP or routing adjustments to optimize performance (90 %).",
              "Tier 4 — Autonomous Optimization & Predictive Learning: Auto-tune routing weights and forecast optimal peers per region (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Peering Efficiency Index (PEI) ≥ 95 % (Ratio of optimal paths vs. total peers).",
              "Mean Time to Detect (MTTD) Peering Degradation < 3 min (Time to identify suboptimal or failing peer route).",
              "Mean Time to Optimize (MTTO) < 10 min (Time from detection to recommended or executed routing change).",
              "Cost Reduction (Transit Spend) ≥ 10 % (% savings via improved peering and traffic engineering).",
              "Telemetry Refresh Interval ≤ 1 min (BGP and performance metric collection).",
              "Anomaly Detection & Correlation Cycle ≤ 3 min (Identify peering inefficiencies across providers)."
            ]
          }
        ]
      },
      {
        title: "4. Evidence & Audit",
        subSections: [
          {
            content: [
              "Generates Peering Efficiency Report (PDF/JSON), Path Optimization Log, Cost Savings Summary, and SLA Audit Snapshot.",
              "Stores SLA metrics, route compliance data, and audit logs in CoreStack GRC / Confluence.",
              "Ensures an auditable history of optimization events, performance deltas, and cost correlation for FinOps and GRC."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Continuous Path Telemetry Collection (Telemetry Refresh Interval ≤ 1 min) or BGP route anomaly.",
      workingPlane: "Correlates multi-layer telemetry (Latency, BGP attributes, Throughput) across ISP/Cloud APIs and Edge Routers to analyze peering efficacy.",
      memory: "Persists path baselines, peering health history, cost-per-bit metrics, and observed optimization outcomes to refine future tuning actions.",
      reasoning: "AI diagnoses the root cause of inefficiency (congestion, asymmetry, cost) and recommends the optimal BGP policy adjustment (local-pref, AS-path prepending) to meet PEI and cost targets.",
      execution: "Executes autonomous BGP or route-map adjustments on Edge Routers/Gateways (MTTO < 10 min) and validates performance post-change, with auto-revert logic."
    },
    kpis: [
      { label: "Peering Efficiency Index (PEI)", value: "95", unit: "%", target: "≥ 95%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD) Peering Degradation", value: "3", unit: "min", target: "< 3 min", color: "#2563eb" },
      { label: "Mean Time to Optimize (MTTO)", value: "10", unit: "min", target: "< 10 min", color: "#f59e0b" },
      { label: "Cost Reduction (Transit Spend)", value: "10", unit: "%", target: "≥ 10%", color: "#ef4444" }
    ]
  },
  'direct-connect-agent': {
    title: "Direct Connect Agent",
    description: "Continuously manages, monitors, and optimizes cloud direct connect, express route, and interconnect links for hybrid connectivity — ensuring **redundancy, performance, and compliance** with enterprise network and SLA policies.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Reliable Hybrid Connectivity: Maintains consistent performance and redundancy for all cloud connectivity links.",
          "Operational Continuity: Detects degradation and triggers rerouting or failover automatically.",
          "Predictive Capacity Management: Anticipates bandwidth saturation and recommends scaling or redistribution.",
          "Cross-Cloud Governance: Provides unified monitoring and policy enforcement across AWS, Azure, and GCP.",
          "Compliance & SLA Assurance: Maintains connectivity health metrics, redundancy validation, and audit-ready evidence."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "As enterprises adopt hybrid and multi-cloud architectures, cloud direct connect and express route links become lifelines for critical workloads.",
              "Manual provisioning, routing, and monitoring introduce gaps in redundancy, performance visibility, and compliance validation.",
              "Link saturation, asymmetric routing, or missing failover validation can result in disrupted hybrid workloads and SLA violations.",
              "Enterprises need an agentic, continuously validating management layer that ensures these direct links remain healthy, redundant, and compliant at all times — with proactive alerts and autonomous optimization."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Consistent performance and guaranteed redundancy across all hybrid cloud connectivity links.",
              "Automated detection of link degradation triggers immediate rerouting or failover for continuity.",
              "Proactive capacity planning avoids congestion and performance bottlenecks for critical hybrid workloads.",
              "Unified governance and monitoring across AWS Direct Connect, Azure ExpressRoute, and GCP Interconnect.",
              "Continuous, auditable validation of redundancy state and adherence to network SLA policies."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Cloud Direct Connect / ExpressRoute Monitoring: Continuously validate health, latency, and throughput across cloud interconnects.",
              "Redundancy Validation: Ensure dual-link or multi-region circuits meet redundancy design SLAs.",
              "Failover Orchestration: Trigger automated failover or routing updates on link degradation.",
              "Bandwidth & Cost Optimization: Monitor utilization trends and recommend resizing or redistribution.",
              "Compliance & Reporting: Produce detailed SLA and connectivity audit logs for hybrid workloads."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Link Health Monitoring: Collect real-time telemetry for latency, jitter, throughput, and BGP session status.",
              "Redundancy Validation: Verify dual circuits or alternate paths meet SLA-defined failover readiness.",
              "Failover Simulation & Testing: Periodically trigger test failovers to validate path and routing integrity.",
              "Performance Baseline Enforcement: Compare link metrics to policy-defined baselines and auto-adjust routing preferences.",
              "Capacity Forecasting: Predict future saturation points and recommend upgrades or redistribution.",
              "AI Reasoning Engine: Diagnose degradation causes, recommend failover or scaling, and trigger remediations.",
              "Audit & Reporting: Generate SLA, redundancy, and connectivity health compliance reports."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Cloud Providers (AWS Direct Connect, Azure ExpressRoute, GCP Interconnect): Retrieve link telemetry, BGP status, and route statistics (Bidirectional).",
              "SD-WAN Controllers (Cisco, Fortinet, VMware, Versa): Manage routing updates and overlay failover decisions (Bidirectional).",
              "Network Monitoring Tools (Grafana, ThousandEyes, SolarWinds): Correlate performance, packet loss, and latency metrics (Inbound).",
              "CoreStack GRC / Compliance Modules: Log SLA adherence, redundancy validation, and compliance evidence (Bidirectional).",
              "ServiceNow / Jira: Create tickets for link degradation, capacity events, or routing changes (Bidirectional).",
              "SOAR / NOC Automation Platforms: Trigger routing policy enforcement or failover automation (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Analyze link degradation and forecast capacity thresholds (Inbound).",
              "Confluence / Data Lake: Archive link health data, capacity forecasts, and audit logs (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Link Telemetry & Baseline Collection: Gather real-time metrics for all direct connect and express route circuits (50 %).",
              "Tier 2 — Degradation Detection & Redundancy Validation: Identify anomalies and confirm failover readiness across circuits (75 %).",
              "Tier 3 — AI Reasoning & Autonomous Orchestration: Trigger failover, rebalance routing, or recommend capacity scaling (90 %).",
              "Tier 4 — Predictive Optimization & Self-Learning Governance: Forecast saturation and refine failover thresholds dynamically (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Link Health Compliance Rate ≥ 99 % (% of circuits within SLA-defined performance thresholds).",
              "Mean Time to Detect (MTTD) Degradation < 2 min (Time from anomaly occurrence to detection).",
              "Mean Time to Recover (MTTR) < 10 min (Time from detection to failover or recovery).",
              "Failover Success Rate ≥ 98 % (% of automated failovers executed without packet loss).",
              "Capacity Forecast Accuracy ≥ 95 % (Accuracy of predicted saturation vs. actual usage).",
              "Telemetry Refresh Interval ≤ 1 min (Continuous health and performance polling)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'DirectConnectAI' microservice.",
              "Primary Interfaces: Cloud provider APIs (AWS DX, Azure ER, GCP Interconnect), SD-WAN controllers, and NMS telemetry feeds.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing circuit baselines, redundancy maps, and event history.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on hybrid WAN connectivity, BGP redundancy logic, and RunOps edge networking KB.",
              "Security Controls: TLS 1.3, signed API actions, RBAC per provider, route policy validation, encrypted credentials.",
              "Error Handling: Auto-rollback routing if failover validation fails; escalate to NOC after two unsuccessful recoveries.",
              "Outputs: Direct Connect Health Report (PDF/JSON), Redundancy Validation Summary, Capacity Forecast Snapshot, SLA Compliance Log."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Continuous link telemetry polling (Telemetry Refresh Interval ≤ 1 min) or BGP session failure notification.",
      workingPlane: "Aggregates real-time health (Latency, Throughput, BGP status) from multi-cloud APIs and compares against redundancy policies and performance baselines.",
      memory: "Persists circuit health baselines, redundancy maps, failover history, and utilization trends to inform predictive and autonomous actions.",
      reasoning: "AI diagnoses whether degradation is transient or link-specific, validates redundancy readiness (Failover Success Rate ≥ 98 %), and executes or recommends optimal failover/scaling actions.",
      execution: "Triggers automated routing updates via SD-WAN or cloud APIs to reroute traffic upon primary link failure (MTTR < 10 min) and generates capacity upgrade recommendations (Capacity Forecast Accuracy ≥ 95 %)."
    },
    kpis: [
      { label: "Link Health Compliance Rate", value: "99", unit: "%", target: "≥ 99%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD) Degradation", value: "2", unit: "min", target: "< 2 min", color: "#2563eb" },
      { label: "Mean Time to Recover (MTTR)", value: "10", unit: "min", target: "< 10 min", color: "#f59e0b" },
      { label: "Failover Success Rate", value: "98", unit: "%", target: "≥ 98%", color: "#ef4444" }
    ]
  },
  
  'branch-cost-analyzer-agent': {
    title: "Branch Cost Analyzer Agent",
    description: "Continuously analyzes branch circuit costs, utilization, and performance to identify optimization opportunities — such as bandwidth resizing, plan renegotiation, provider rationalization, and intelligent traffic redistribution — ensuring **cost-efficient, SLA-compliant connectivity** across the enterprise WAN.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Cost Optimization & Savings: Identifies underutilized circuits and opportunities for cost reduction.",
          "Operational Efficiency: Automates contract analysis, utilization tracking, and cost-performance mapping.",
          "Provider Rationalization: Consolidates high-cost or redundant ISP links based on performance data.",
          "Predictive Budget Control: Forecasts future spend and capacity needs using utilization trends.",
          "Compliance & Audit Readiness: Provides traceable, data-driven cost management evidence for financial governance."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "As enterprises scale distributed edge and branch networks, circuit costs represent one of the largest recurring OpEx categories.",
              "WAN cost visibility is fragmented across ISPs, carriers, and cloud on-ramps, with no unified view of spend versus utilization.",
              "Static bandwidth provisioning leads to overpaying for unused capacity or under-provisioning that risks SLA breaches.",
              "Enterprises need an agentic, FinOps-aware analytics engine that correlates spend, usage, and performance to provide actionable optimization recommendations."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Automated identification of optimization opportunities leads to cost reduction and savings (Target: ≥ 12 %).",
              "Reduced manual effort in tracking contracts, utilization, and performance metrics.",
              "Data-driven decisions support consolidating high-cost or underperforming ISP links.",
              "Accurate forecasting of budget control and capacity planning using predictive utilization trends.",
              "Generation of auditable evidence for financial governance and compliance reporting."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Circuit Cost Analysis: Compare contract cost vs. utilization and performance across all providers.",
              "Bandwidth Right-Sizing: Identify circuits with <50% utilization for potential downgrades or plan changes.",
              "Provider Comparison & Consolidation: Evaluate ISPs by cost efficiency, latency, and SLA adherence.",
              "Contract Lifecycle Governance: Track renewal dates, term commitments, and cost escalations automatically.",
              "FinOps Reporting & Forecasting: Produce monthly cost trend reports and future budget predictions."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Circuit Inventory & Cost Baseline: Collect billing, cost-per-Mbps, and contract metadata per branch.",
              "Utilization Correlation: Match cost data with bandwidth usage and SLA metrics.",
              "Provider Efficiency Scoring: Rank ISPs and carriers by performance-per-dollar ratio.",
              "Optimization Recommendation Engine: Suggest cost-saving actions (downgrade, merge, renegotiate, re-route).",
              "AI Forecasting Model: Predict future bandwidth demand and cost impact.",
              "Multi-Vendor Cost Integration: Aggregate data across MPLS, broadband, and LTE providers.",
              "Audit & Compliance Reporting: Generate cost variance reports and optimization evidence for financial review."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ISP & Carrier Portals (AT&T, Verizon, BT, Tata, Comcast, etc.): Collect invoices, plan data, and usage reports (Inbound).",
              "SD-WAN Controllers (Cisco, Fortinet, VMware, Versa): Retrieve real-time utilization and performance metrics (Bidirectional).",
              "FinOps Platforms (CoreStack FinOps, CloudHealth, Apptio): Align circuit cost analytics with enterprise FinOps data (Bidirectional).",
              "ServiceNow / Jira: Manage optimization tickets and approval workflows (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Analyze performance-cost trade-offs and generate recommendations (Inbound).",
              "Confluence / Data Lake: Archive cost trend data, optimization actions, and financial reports (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Cost Data Collection & Baseline Mapping: Aggregate invoices, contracts, and utilization data per circuit (50 %).",
              "Tier 2 — Performance-Cost Correlation & SLA Validation: Match spend against usage and service quality (75 %).",
              "Tier 3 — AI Reasoning & Optimization Recommendation: Recommend right-sizing, consolidation, or provider renegotiation (90 %).",
              "Tier 4 — Predictive Budget Governance & Continuous Learning: Forecast future costs and refine optimization strategy dynamically (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Cost Optimization Rate ≥ 12 % (% of total spend reduced post-optimization).",
              "Utilization vs. Spend Efficiency ≥ 90 % (Ratio of cost to usage and SLA compliance).",
              "Mean Time to Detect (MTTD) Cost Anomaly < 24 hrs (Time from cost deviation to detection).",
              "Mean Time to Recommend (MTTR) < 48 hrs (Time from detection to actionable optimization).",
              "Forecast Accuracy (3-Month) ≥ 95 % (Cost forecast accuracy vs. realized spend).",
              "Cost Data Refresh Interval ≤ 24 hrs (Continuous sync with ISP and billing data)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'BranchCostAI' microservice.",
              "Primary Interfaces: ISP/carrier billing APIs, SD-WAN telemetry, FinOps systems, CoreStack compliance APIs.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing cost, utilization, and performance baselines per branch.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on WAN FinOps, telecom cost analytics, and RunOps financial governance KB.",
              "Security Controls: TLS 1.3 encryption, API credential vaulting, RBAC policy controls, and data masking for financial data.",
              "Error Handling: Auto-retry failed invoice syncs; flag discrepancies for FinOps validation after two anomalies.",
              "Outputs: Branch Cost Optimization Report (PDF/JSON), Provider Efficiency Summary, Budget Forecast Snapshot, SLA Compliance Log."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Scheduled Cost Data Refresh Interval (≤ 24 hrs) or significant utilization change detected by SD-WAN telemetry.",
      workingPlane: "Correlates financial data (Cost, Contracts) with operational data (Utilization, Latency, SLA) across multiple provider interfaces and FinOps platforms.",
      memory: "Persists circuit inventory, historical utilization data, performance baselines, and past optimization results to power predictive modeling and continuous learning.",
      reasoning: "AI evaluates the Performance-Cost ratio to identify underutilized/overpriced circuits and recommends the most cost-effective action (downgrade, renegotiation, consolidation) to meet the Cost Optimization Rate (≥ 12 %).",
      execution: "Generates actionable Optimization Recommendation Reports and creates automated tickets in ServiceNow/Jira for FinOps approval of budget changes or renegotiation (MTTR < 48 hrs)."
    },
    kpis: [
      { label: "Cost Optimization Rate", value: "12", unit: "%", target: "≥ 12%", color: "#10b981" },
      { label: "Utilization vs. Spend Efficiency", value: "90", unit: "%", target: "≥ 90%", color: "#2563eb" },
      { label: "Mean Time to Recommend (MTTR)", value: "48", unit: "hrs", target: "< 48 hrs", color: "#f59e0b" },
      { label: "Forecast Accuracy (3-Month)", value: "95", unit: "%", target: "≥ 95%", color: "#ef4444" }
    ]
  },
  
  'branch-inventory-agent': {
    title: "Branch Inventory Agent",
    description: "Maintains a live, continuously synchronized **inventory** of all branch and edge devices, configurations, and firmware states — enabling **unified visibility, compliance assurance, and zero-trust asset governance** across the enterprise network fabric.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Complete Network Visibility: Maintains real-time inventory of all branch routers, switches, firewalls, and SD-WAN devices.",
          "Configuration Governance: Tracks running vs. startup configurations and detects drift.",
          "Compliance Assurance: Validates firmware, patch, and configuration states against baselines.",
          "Operational Efficiency: Eliminates manual CMDB updates and field audits.",
          "Zero-Trust Alignment: Supports asset identity and posture validation for secure access enforcement."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "As enterprises expand across hundreds of branches, maintaining accurate, real-time device inventories becomes increasingly difficult.",
              "Manual updates to CMDBs, spreadsheets, or static asset systems result in outdated data, misaligned configurations, and compliance gaps.",
              "Network changes, replacements, or firmware upgrades often go untracked, leading to visibility loss and audit failures.",
              "Enterprises require an autonomous inventory agent that continuously collects, validates, and correlates branch device data, keeping the source of truth synchronized across systems."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Real-time, accurate inventory for all edge and branch network hardware.",
              "Automated detection of unauthorized configuration changes and drift.",
              "Continuous validation of firmware and patch levels against approved security baselines.",
              "Significant reduction in manual effort required for CMDB/ITAM maintenance and reconciliation.",
              "Provision of verified asset identity and posture data essential for zero-trust policies."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Dynamic Device Discovery: Automatically detect and register new devices at branch sites.",
              "Configuration Capture & Change Tracking: Store running configs and detect configuration drift or unauthorized changes.",
              "Firmware & Patch Compliance: Validate device OS versions against approved firmware catalogs.",
              "Lifecycle & Warranty Management: Track device age, warranty, and refresh cycles.",
              "CMDB Synchronization: Automatically update enterprise CMDB or ITAM systems with verified inventory data."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Real-Time Discovery: Use SNMP, LLDP, NetConf, or API telemetry to discover all connected branch devices.",
              "Configuration Synchronization: Capture startup and running configs and validate against baselines.",
              "Firmware & Patch Validation: Compare OS versions to compliance catalogs and approved images.",
              "Drift & Change Detection: Identify config or posture changes instantly.",
              "CMDB & ITAM Integration: Synchronize inventory data across ServiceNow, CoreStack, or custom databases.",
              "AI Reasoning Engine: Assess lifecycle risk, vendor support status, and standard deviation from policy.",
              "Audit & Reporting: Generate compliance reports and change history for every device."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Network Devices (Cisco, Fortinet, Palo Alto, Juniper, Aruba, Meraki): Collect configurations, serial numbers, firmware data (Inbound).",
              "SD-WAN Controllers (Cisco vManage, Fortinet, VMware, Versa): Aggregate site-level device telemetry (Bidirectional).",
              "CoreStack GRC / Compliance Modules: Store compliance and inventory logs (Bidirectional).",
              "ServiceNow CMDB / ITAM: Synchronize asset data, warranty info, and configuration versions (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Evaluate lifecycle risk, compliance posture, and asset priorities (Inbound).",
              "Confluence / Data Lake: Archive configuration snapshots, audit logs, and lifecycle records (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Device Discovery & Baseline Inventory: Collect device and configuration data across all branches (50 %).",
              "Tier 2 — Configuration & Compliance Validation: Validate firmware, configuration, and topology state (75 %).",
              "Tier 3 — AI Reasoning & Lifecycle Assessment: Analyze lifecycle health, risk, and recommended refresh cycles (90 %).",
              "Tier 4 — Predictive Governance & Continuous Learning: Predict capacity or lifecycle changes and auto-update baselines (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Inventory Accuracy Rate ≥ 99 % (% of devices correctly identified and recorded).",
              "Mean Time to Detect (MTTD) Asset Change < 5 min (Time from device addition/removal to detection).",
              "Configuration Drift Detection Accuracy ≥ 97 % (% of true drift events correctly identified).",
              "Lifecycle Compliance Rate ≥ 95 % (Devices running approved OS/firmware).",
              "Automation Adoption Rate ≥ 85 % (% of inventory updates executed autonomously).",
              "Device Discovery Refresh Cycle ≤ 5 min (Continuous polling and topology scan)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'BranchInventoryAI' microservice.",
              "Primary Interfaces: SNMP, REST, NetConf APIs, SD-WAN controllers, CMDB APIs.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing inventory records, config snapshots, firmware metadata, and drift history.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on asset lifecycle, configuration management, and RunOps zero-trust inventory KB.",
              "Security Controls: TLS 1.3 encryption, RBAC per site, configuration signature validation, and data encryption at rest.",
              "Error Handling: Auto-retry failed config pulls; flag discrepancies after two consecutive polling failures.",
              "Outputs: Branch Inventory Report (PDF/JSON), Device Compliance Summary, Drift Log, Lifecycle Forecast Snapshot."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Continuous Device Discovery Refresh Cycle (≤ 5 min) or change event detected by network controllers/telemetry.",
      workingPlane: "Continuously polls network devices (SNMP/API) and SD-WAN controllers, compares live state (config/firmware) against CMDB baselines, and determines compliance status.",
      memory: "Persists configuration snapshots, firmware catalogs, device lifecycle data (warranty/age), and historical drift events to maintain a continuously verified source of truth.",
      reasoning: "AI validates configuration integrity, assesses lifecycle risk, and determines necessary CMDB/policy updates (Inventory Accuracy Rate ≥ 99 %).",
      execution: "Executes autonomous CMDB/ITAM synchronization (CMDB Sync & Audit Log Publication ≤ 1 hr) and logs configuration drift/compliance exceptions for remediation."
    },
    kpis: [
      { label: "Inventory Accuracy Rate", value: "99", unit: "%", target: "≥ 99%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD) Asset Change", value: "5", unit: "min", target: "< 5 min", color: "#2563eb" },
      { label: "Configuration Drift Detection Accuracy", value: "97", unit: "%", target: "≥ 97%", color: "#f59e0b" },
      { label: "Lifecycle Compliance Rate", value: "95", unit: "%", target: "≥ 95%", color: "#ef4444" }
    ]
  },
  
  'policy-consistency-agent': {
    title: "Policy Consistency Agent",
    description: "Continuously validates and enforces consistent network, routing, and security policies across all branch and edge devices — eliminating **configuration drift**, ensuring **compliance**, and maintaining unified **Zero-Trust posture** across hybrid WAN environments.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Consistent Security Posture: Ensures identical firewall, routing, and segmentation policies across all sites.",
          "Reduced Operational Risk: Detects and resolves misalignments before they cause outages or breaches.",
          "Zero-Trust Enforcement: Validates microsegmentation and access policies dynamically.",
          "Regulatory Compliance: Maintains adherence to PCI-DSS, HIPAA, and NIST policy baselines.",
          "Operational Efficiency: Reduces manual policy management and change coordination effort."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "In distributed enterprises, routing, QoS, and security policies often diverge between branches due to manual configuration, vendor differences, or partial updates.",
              "These inconsistencies lead to routing asymmetry, security gaps, and non-compliant segmentation, directly affecting network reliability and audit readiness.",
              "Traditional management platforms provide visibility but lack automated enforcement and contextual reasoning.",
              "Enterprises need an autonomous, cross-domain policy consistency agent that continuously detects, validates, and corrects deviations — ensuring all branches operate under one synchronized policy fabric."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Guaranteed uniformity of security, routing, and QoS policies across all edge and branch locations.",
              "Proactive mitigation of risks associated with configuration drift and policy misalignment.",
              "Continuous validation ensures microsegmentation and zero-trust policies are correctly applied everywhere.",
              "Automated evidence generation confirming adherence to all regulatory and internal policy baselines.",
              "Reduced time and effort spent on manual policy auditing, changes, and change control."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Policy Drift Detection: Identify deviations from enterprise routing, QoS, or firewall policies.",
              "Configuration Enforcement: Automatically push or correct policy templates at branch devices.",
              "Segmentation Validation: Confirm VLANs, VRFs, and security zones match enterprise design.",
              "Routing Consistency Assurance: Validate route tables, prefixes, and redistribution rules.",
              "Audit & Compliance Reporting: Produce policy adherence scorecards and drift logs."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Policy Baseline Validation: Continuously compare device policies to enterprise-approved routing and security templates.",
              "Routing Consistency Checks: Validate OSPF/BGP configurations, route redistribution, and failover path uniformity.",
              "Firewall Rule & ACL Comparison: Detect missing, redundant, or unauthorized rules between sites.",
              "Segmentation & VLAN Alignment: Validate subnet tagging, VRF mapping, and access control per zone.",
              "AI Reasoning Engine: Correlate configuration drift to its operational or security impact.",
              "Autonomous Policy Enforcement: Push policy corrections or alert for manual review.",
              "Audit & Evidence Logging: Maintain complete records of detected deviations and remediation actions."
            ]
          },
          {
            title: "Integrations",
            content: [
              "SD-WAN Controllers (Cisco, Fortinet, VMware, Versa): Validate and enforce WAN and routing policies (Bidirectional).",
              "Firewall Managers (Panorama, FortiManager, Cisco FMC): Compare and synchronize firewall rules and objects (Bidirectional).",
              "Network Orchestration Tools (Cisco DNA, Mist, NetBrain): Retrieve and normalize site-level configurations (Bidirectional).",
              "CoreStack GRC / Compliance Modules: Store policy compliance reports and audit logs (Bidirectional).",
              "ServiceNow / Jira: Manage exception workflows and approval for policy changes (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Evaluate drift severity, reason about exceptions, and recommend corrective actions (Inbound).",
              "Confluence / Data Lake: Archive policy baselines, compliance scorecards, and change history (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Policy Data Collection & Baseline Comparison: Collect routing, firewall, and segmentation data from all devices (50 %).",
              "Tier 2 — Deviation Detection & Classification: Identify mismatches and classify severity by domain (routing, QoS, security) (75 %).",
              "Tier 3 — AI Reasoning & Autonomous Enforcement: Determine impact and execute corrective policy sync or alert (90 %).",
              "Tier 4 — Predictive Consistency & Continuous Learning: Learn deviation trends and preemptively tune enforcement logic (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Policy Compliance Rate ≥ 98 % (% of branches aligned with enterprise policy templates).",
              "Mean Time to Detect (MTTD) Policy Drift < 15 min (Time from policy deviation occurrence to detection).",
              "Mean Time to Remediate (MTTR) < 1 hr (Time from detection to policy alignment restoration).",
              "False Positive Rate ≤ 3 % (Legitimate policy exceptions incorrectly flagged).",
              "Automation Adoption Rate ≥ 85 % (% of policy corrections executed autonomously).",
              "Policy Audit Cycle ≤ 30 min (Continuous rolling validation of all branches)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'PolicyConsistencyAI' microservice.",
              "Primary Interfaces: SD-WAN controller APIs, firewall managers, network orchestration tools, CMDB systems.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing policy templates, deviation logs, and enforcement history.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on network security policies, segmentation design, and RunOps compliance KB.",
              "Security Controls: TLS 1.3, signed API actions, RBAC enforcement, and encrypted config baselines.",
              "Error Handling: Auto-rollback applied policy changes if site connectivity is impacted; alert NOC for manual review.",
              "Outputs: Policy Compliance Report (PDF/JSON), Drift Detection Summary, Auto-Remediation Log, SLA Audit Snapshot."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Continuous Policy Audit Cycle (≤ 30 min) or configuration change event detected from a branch device/manager.",
      workingPlane: "Collects and normalizes policy data (Routing, Firewall, Segmentation) across multi-vendor devices, compares against the golden baseline, and classifies the drift severity.",
      memory: "Persists enterprise policy baselines, historical configuration snapshots, and policy exception records to inform accurate drift detection and remediation.",
      reasoning: "AI identifies the root cause of the drift (e.g., manual error, unapproved change) and determines the appropriate action: autonomous synchronization, or escalation for human approval (Policy Compliance Rate ≥ 98 %).",
      execution: "Autonomously pushes corrective configuration/policy templates via SD-WAN or Firewall Manager APIs to restore alignment (MTTR < 1 hr), or creates an approved exception workflow in ServiceNow/Jira."
    },
    kpis: [
      { label: "Policy Compliance Rate", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD) Policy Drift", value: "15", unit: "min", target: "< 15 min", color: "#2563eb" },
      { label: "Mean Time to Remediate (MTTR)", value: "1", unit: "hr", target: "< 1 hr", color: "#f59e0b" },
      { label: "Automation Adoption Rate", value: "85", unit: "%", target: "≥ 85%", color: "#ef4444" }
    ]
  },
  
  'circuit-sla-agent': {
    title: "Circuit SLA Agent",
    description: "Monitors ISP and carrier performance against **SLA obligations** for uptime, latency, jitter, and packet loss, automatically validating compliance, detecting breaches, and triggering **credit or escalation workflows** to enforce accountability and financial recovery.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Improved Provider Accountability: Detect and document all SLA breaches in real time.",
          "Automated Credit Enforcement: Initiate credit claims or escalations automatically per contract terms.",
          "Financial Efficiency: Capture missed credits and reduce financial leakage.",
          "Service Reliability Governance: Maintain transparent, measurable performance data for every carrier.",
          "Compliance & Audit Assurance: Provide traceable SLA compliance evidence for internal and external audits."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprises manage dozens of ISPs and global carriers, each with unique SLAs, reporting methods, and escalation processes.",
              "SLA breaches often go unnoticed or unclaimed due to manual tracking and lack of real-time validation.",
              "The result is missed credits, performance inconsistency, and reduced leverage in vendor negotiations.",
              "Organizations need a governance-oriented agent that continuously measures provider adherence, enforces accountability, and quantifies financial impact — automating SLA monitoring and compliance enforcement across the entire WAN."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Real-time detection and documentation of all SLA breaches enhances provider accountability.",
              "Automated initiation of credit claims and escalations based on contract terms ensures financial recovery.",
              "Capturing missed credits and reducing financial leakage improves overall financial efficiency.",
              "Transparent, measurable performance data governs service reliability for every carrier.",
              "Continuous provision of traceable SLA compliance evidence for internal and external audits."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "SLA Compliance Validation: Continuously compare carrier performance metrics to contractual SLAs.",
              "Credit Recovery & Escalation Automation: Automatically submit credits or escalate breaches to vendor service managers.",
              "Carrier Benchmarking: Rank carriers by SLA adherence, responsiveness, and financial reliability.",
              "FinOps & Budget Impact Reporting: Quantify breach-related costs and recovered credits for financial reporting.",
              "Governance Dashboards & Audits: Publish SLA scorecards and breach evidence to compliance systems."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "SLA Contract Parsing: Extract performance thresholds (uptime %, latency, jitter, loss) and credit clauses from vendor agreements.",
              "Telemetry Correlation: Collect network metrics from ISPs, SD-WAN controllers, and observability systems.",
              "Breach Detection: Identify deviations from SLA baselines using multi-parameter thresholds.",
              "Financial Impact Computation: Quantify breach duration and applicable credit entitlement.",
              "Automated Workflow Orchestration: Trigger escalation or credit claim processes via ITSM/FinOps systems.",
              "Vendor Scoring & Trend Analysis: Benchmark provider performance by SLA adherence and responsiveness.",
              "Audit & Reporting: Generate evidence-based SLA performance reports and financial summaries."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Carrier & ISP APIs (AT&T, Verizon, BT, Lumen, Tata, etc.): Retrieve real-time performance and outage data (Inbound).",
              "SD-WAN Controllers (Cisco, Fortinet, VMware, Versa): Correlate link telemetry and uptime metrics (Bidirectional).",
              "Network Observability Platforms (ThousandEyes, Grafana, Splunk): Validate network performance and latency (Bidirectional).",
              "FinOps Systems (CoreStack, Apptio, CloudHealth): Register financial recovery and credit realization (Bidirectional).",
              "ServiceNow / Jira: Automate credit submissions and provider escalation workflows (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Interpret SLA data, contract thresholds, and recommend escalation actions (Inbound).",
              "Confluence / Data Lake: Archive SLA reports, vendor rankings, and credit records (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — SLA Metric Collection & Baseline Mapping: Aggregate carrier and internal telemetry for SLA tracking (50 %).",
              "Tier 2 — Breach Detection & Financial Correlation: Detect SLA violations and compute credit impact (75 %).",
              "Tier 3 — AI Reasoning & Enforcement Workflow: Trigger claim, escalation, or vendor notification automatically (90 %).",
              "Tier 4 — Predictive SLA Optimization & Continuous Learning: Anticipate vendor risk and optimize escalation timing proactively (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "SLA Breach Detection Accuracy ≥ 97 % (Correctly identifies provider SLA violations).",
              "Mean Time to Detect (MTTD) < 10 min (Time from metric deviation to breach validation).",
              "Mean Time to Enforce (MTTE) < 2 hrs (Time from breach detection to escalation/claim submission).",
              "Credit Recovery Rate ≥ 90 % (% of eligible SLA credits successfully reclaimed).",
              "Vendor Compliance Rate ≥ 99 % (% of circuits meeting SLA commitments).",
              "Metric Refresh Interval ≤ 5 min (Continuous collection from carrier and SD-WAN telemetry)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'CircuitSLA-AI' microservice.",
              "Primary Interfaces: Carrier APIs, SD-WAN controllers, observability systems, ITSM, and FinOps tools.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing SLA contracts, breach records, and credit history.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on telecom SLA analytics, contract interpretation, and RunOps financial governance KB.",
              "Security Controls: TLS 1.3 encryption, signed API actions, RBAC per provider, encrypted SLA and financial data.",
              "Error Handling: Auto-retry failed API queries; flag inconsistent vendor telemetry for manual FinOps review.",
              "Outputs: SLA Violation Report (PDF/JSON), Financial Recovery Log, Vendor Performance Dashboard, Compliance Audit Record."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Continuous Metric Refresh Interval (≤ 5 min) from carrier APIs and network telemetry.",
      workingPlane: "Correlates real-time performance metrics (Latency, Jitter, Uptime) against parsed SLA contract thresholds to detect breaches.",
      memory: "Stores SLA contract terms, historical breach records, and credit recovery success history to calculate financial entitlement and vendor risk.",
      reasoning: "AI interprets the contract and financial data to confirm a breach, compute the eligible credit, and determine the necessary enforcement action (claim vs. escalation).",
      execution: "Triggers automated submission of credit claims or escalation workflows via ServiceNow/Jira, and registers the financial recovery event in FinOps systems (MTTE < 2 hrs)."
    },
    kpis: [
      { label: "SLA Breach Detection Accuracy", value: "97", unit: "%", target: "≥ 97%", color: "#10b981" },
      { label: "Mean Time to Enforce (MTTE)", value: "2", unit: "hrs", target: "< 2 hrs", color: "#2563eb" },
      { label: "Credit Recovery Rate", value: "90", unit: "%", target: "≥ 90%", color: "#f59e0b" },
      { label: "Vendor Compliance Rate", value: "99", unit: "%", target: "≥ 99%", color: "#ef4444" }
    ]
  },
  'load-balancer-config-agent': {
    title: "Load Balancer Config Agent",
    description: "Continuously validates and enforces configuration consistency across load balancers — ensuring that virtual IPs, pools, monitors, and SSL policies conform to enterprise security and operational standards across environments.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Operational Consistency: Ensures all VIPs, pools, and monitors follow golden configuration templates.",
          "Reduced Outage Risk: Detects and corrects misconfigurations before they impact application availability.",
          "Security Compliance: Validates SSL/TLS profiles, cipher policies, and certificates against enterprise baselines.",
          "Cross-Platform Governance: Harmonizes configuration standards across F5, Citrix, AWS, Azure, and GCP load balancers.",
          "Audit & Traceability: Provides full visibility and compliance reporting for every configuration change."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Load balancers (e.g., F5, Citrix ADC, Avi, AWS ALB, Azure Front Door) are critical to application uptime, yet their configurations often drift due to manual edits, environment-specific changes, or version inconsistencies.",
              "Misaligned VIP definitions, mismatched pool members, or misconfigured health monitors lead to application outages, degraded performance, or compliance violations.",
              "Manual audits are time-intensive and reactive.",
              "Enterprises need an autonomous agent that continuously validates configuration integrity, detects drift, enforces standards, and auto-remediates non-compliant configurations safely."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Operational Consistency — Ensures all VIPs, pools, and monitors follow golden configuration templates.",
              "Reduced Outage Risk — Detects and corrects misconfigurations before they impact application availability.",
              "Security Compliance — Validates SSL/TLS profiles, cipher policies, and certificates against enterprise baselines.",
              "Cross-Platform Governance — Harmonizes configuration standards across F5, Citrix, AWS, Azure, and GCP load balancers.",
              "Audit & Traceability — Provides full visibility and compliance reporting for every configuration change."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Configuration Drift Detection: Identify deviations from approved templates or policy baselines.",
              "VIP and Pool Validation: Ensure VIPs reference correct pool members, ports, and load-balancing methods.",
              "Health Monitor Governance: Validate monitor frequency, timeout, and expected response standards.",
              "SSL/TLS Policy Compliance: Confirm encryption profiles, ciphers, and certificate chains are compliant.",
              "Automated Remediation & Reporting: Auto-sync configurations and provide audit evidence to compliance systems."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Cross-Platform Intelligence: Normalizes configurations across F5, Citrix, NGINX, Avi, and cloud LB services.",
              "AI Reasoning: Evaluates deviations contextually, distinguishing intentional local overrides from drift.",
              "Autonomous Remediation: Restores configuration integrity automatically or generates ITSM tickets for exceptions.",
              "Security Integration: Aligns certificates, monitors, and cipher suites with enterprise and compliance policies.",
              "Learning Feedback Loop: Improves baseline precision by analyzing historical configuration patterns and audit results."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Configuration Inventory & Baseline Validation: Collect VIP, pool, monitor, SSL, and profile data across all load balancers.",
              "Golden Template Enforcement: Compare active configurations against enterprise templates and security baselines.",
              "Policy Drift Detection: Identify configuration differences (timeouts, persistence, method, cipher, etc.).",
              "Certificate Validation: Confirm expiry dates, chain integrity, and binding to correct VIPs.",
              "Monitor Compliance Check: Validate monitor frequency, thresholds, and health response logic.",
              "AI Reasoning Engine: Assess severity, recommend remediation, and prioritize fixes by risk.",
              "Automated Synchronization: Apply corrections or generate proposed configuration deltas for approval.",
              "Audit & Compliance Evidence: Log configuration states, drifts, and remediation events for governance."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Load Balancer APIs (F5, Citrix ADC, NGINX, Avi, AWS ALB, Azure Front Door) — Retrieve and update configuration data (Bidirectional).",
              "Certificate Management Systems (Venafi, DigiCert, Microsoft CA) — Validate and synchronize certificates (Bidirectional).",
              "CoreStack GRC / Compliance Modules — Store configuration compliance evidence and mappings (Bidirectional).",
              "ServiceNow / Jira — Manage drift remediation tickets and change control approvals (Bidirectional).",
              "Monitoring / NMS Systems (Cortex, Grafana, Prometheus) — Correlate load balancer performance metrics (Inbound).",
              "Azure OpenAI / RunOps Reasoning Engine — Assess configuration drift, recommend fixes, and produce remediation narratives (Inbound).",
              "Confluence / Data Lake — Archive configuration reports, compliance snapshots, and version diffs (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Configuration Collection & Baseline Mapping: Retrieve and compare configuration data to golden templates. (50%).",
              "Tier 2 — Drift Detection & Severity Classification: Detect and classify deviations based on risk level. (75%).",
              "Tier 3 — AI Reasoning & Auto-Remediation Enforcement: Automatically correct or propose configuration updates. (90%).",
              "Tier 4 — Continuous Learning & Predictive Governance: Forecast drift trends and preemptively tune configuration standards. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Configuration Compliance Rate ≥ 98% — % of load balancer objects matching golden baselines.",
              "Mean Time to Detect (MTTD) Drift < 2 hrs — Time from configuration deviation to detection.",
              "Mean Time to Remediate (MTTR) < 6 hrs — Time from detection to restoration or approval.",
              "SSL Certificate Compliance Rate ≥ 99% — % of VIPs using valid and compliant certificates.",
              "False Positive Rate ≤ 3% — Legitimate exceptions incorrectly flagged.",
              "Automation Adoption Rate ≥ 85% — % of remediations applied autonomously."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Load Balancers Monitored: 128 (F5, Citrix ADC, AWS, Azure, Avi)",
              "Total Objects Audited: 6,850 VIPs, 22,400 Pool Members, 4,120 Monitors",
              "Drift Events Detected: 482",
              "Auto-Corrected Configurations: 394 (81.7%)",
              "Average MTTD: 1.8 hrs — Average MTTR: 5.3 hrs",
              "Compliance Rate (Post-Remediation): 99.1%",
              "False Positive Rate: 2.4%",
              "Engineer Hours Saved: ≈ 340 per month",
              "Audit Reports Generated: 12 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Configuration Audit Cycle ≤ 4 hrs — Continuous polling and API retrieval.",
              "Drift Detection & Classification ≤ 2 hrs — AI-based correlation and analysis.",
              "Remediation Execution or Change Request ≤ 6 hrs — Automated sync or ITSM workflow creation.",
              "Evidence Publication ≤ 1 hr — Compliance report generation and archival.",
              "Agent Availability ≥ 99.9% — HA n8n cluster monitored via Prometheus / Grafana."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “LBConfigAI” microservice.",
              "Primary Interfaces — REST and SOAP APIs for load balancer vendors, SSL management APIs, DDI integration.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing configuration templates, policy standards, and drift logs.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on L4–L7 configuration templates, F5/Citrix/AWS standards, and RunOps compliance KB.",
              "Security Controls — TLS 1.3 encryption, signed API transactions, RBAC per environment, and configuration snapshot backups.",
              "Outputs — Load Balancer Compliance Report (PDF/JSON), Drift Summary, SSL Validation Log, Change Audit Snapshot.",
              "Monitoring — Grafana dashboards visualizing drift rates, remediation activity, and configuration health.",
              "Error Handling — Auto-rollback on failed config push; notify NOC if configuration state differs post-validation."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Configuration Audit Cycle, Drift Detection, or Scheduled Compliance Check.",
      workingPlane: "RunOps n8n cluster with containerized 'LBConfigAI' microservice.",
      memory: "Persisted configuration templates, policy standards, and drift logs in Postgres.",
      reasoning: "Azure OpenAI driven assessment distinguishing intentional overrides from drift and proposing remediation.",
      execution: "Generate configuration deltas, push to Load Balancer API with rollback, and log audit evidence to GRC."
    },
    kpis: [
      { label: "Configuration Compliance Rate", value: "99.1", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD) Drift", value: "1.8", unit: "hrs", target: "< 2 hrs", color: "#10b981" },
      { label: "Mean Time to Remediate (MTTR)", value: "5.3", unit: "hrs", target: "< 6 hrs", color: "#10b981" },
      { label: "SSL Certificate Compliance Rate", value: "99", unit: "%", target: "≥ 99%", color: "#10b981" },
      { label: "False Positive Rate", value: "2.4", unit: "%", target: "≤ 3%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "81.7", unit: "%", target: "≥ 85%", color: "#f59e0b" }
    ]
},

'app-availability-agent': {
    title: "App Availability Agent",
    description: "Continuously tests and validates end-to-end application availability through load balancers by performing synthetic health checks, API validation, and content verification — ensuring proactive detection of outages, misconfigurations, and performance degradation.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Proactive Outage Detection: Identifies availability and performance issues before users are impacted.",
          "Application Integrity Assurance: Validates functional and content-level health behind load balancers.",
          "Reduced MTTR: Pinpoints root cause quickly by correlating synthetic failures with LB metrics.",
          "Cross-Platform Visibility: Monitors apps across F5, Citrix, NGINX, Avi, and cloud load balancers.",
          "Continuous SLA Verification: Ensures every business-critical application meets uptime and latency targets."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Even with load balancers configured and pools marked “healthy,” applications can still fail due to backend code issues, dependency outages, or SSL misconfigurations.",
              "Native load balancer health checks validate only port or protocol reachability, not full application response or business logic integrity.",
              "Without proactive validation, issues often surface through user complaints or downstream service failures.",
              "Enterprises require an autonomous agent that continuously tests applications from multiple vantage points, validates full content and transaction paths, and correlates failures with load balancer telemetry to isolate root causes instantly."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Proactive Outage Detection — Identifies availability and performance issues before users are impacted.",
              "Application Integrity Assurance — Validates functional and content-level health behind load balancers.",
              "Reduced MTTR — Pinpoints root cause quickly by correlating synthetic failures with LB metrics.",
              "Cross-Platform Visibility — Monitors apps across F5, Citrix, NGINX, Avi, and cloud load balancers.",
              "Continuous SLA Verification — Ensures every business-critical application meets uptime and latency targets."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "End-to-End App Health Validation: Perform real HTTP(S)/API tests through VIPs and load balancers.",
              "Multi-Vantage Synthetic Probing: Execute checks from multiple geographic and network vantage points.",
              "Dependency Path Verification: Validate app response chains (DB, API, Auth, CDN).",
              "Performance & Latency Tracking: Measure end-to-end latency and time-to-first-byte behind load balancers.",
              "Automated Remediation Triggers: Generate alerts or trigger self-healing workflows when health degrades."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Intelligent Probing: Tests real application transactions, not just TCP handshakes.",
              "Contextual Reasoning: Correlates LB health metrics with application responses for root cause inference.",
              "Adaptive Baselines: Learns historical response times and adjusts thresholds dynamically.",
              "Autonomous Remediation: Integrates with ServiceNow, SOAR, or LB Config Agents for self-healing actions.",
              "Cross-Layer Awareness: Connects transport, session, and application behavior under a single visibility plane."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Synthetic Health Checks: Execute HTTP(S), TCP, and API requests via load balancer VIPs.",
              "Content Validation: Verify page content, JSON structure, or status codes match expected patterns.",
              "Response Time Measurement: Track latency, throughput, and connection time across endpoints.",
              "Dependency Chain Verification: Validate app connectivity to databases, APIs, or identity providers.",
              "SSL/TLS Validation: Confirm certificate validity, expiration, and cipher compliance.",
              "AI Reasoning Layer: Correlate synthetic failures with LB pool status, backend health, and infrastructure logs.",
              "Alerting & Remediation Hooks: Integrate with ITSM, SOAR, or automation pipelines for corrective actions.",
              "Audit Logging & SLA Reporting: Generate compliance evidence and performance reports."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Load Balancer APIs (F5, Citrix, Avi, AWS, Azure, GCP) — Retrieve VIP and pool configurations; map tests to services (Bidirectional).",
              "Synthetic Monitoring Tools (ThousandEyes, Catchpoint, Grafana Cloud) — Execute and enrich synthetic test data (Bidirectional).",
              "SIEM / SOAR Systems — Correlate app health anomalies and trigger automated responses (Bidirectional).",
              "CoreStack GRC / Compliance Modules — Store SLA validation and uptime evidence (Bidirectional).",
              "ServiceNow / Jira — Log incident tickets and remediation workflows (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Analyze failures, infer root causes, and generate remediation guidance (Inbound).",
              "Confluence / Data Lake — Archive SLA reports, latency metrics, and compliance logs (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Synthetic Health Monitoring: Execute recurring app health and availability checks. (50%).",
              "Tier 2 — Response Analysis & Correlation: Compare synthetic outcomes with LB and backend telemetry. (75%).",
              "Tier 3 — AI Reasoning & Automated Remediation: Diagnose probable root cause and trigger corrective actions. (90%).",
              "Tier 4 — Predictive Learning & Experience Optimization: Anticipate degradation patterns and tune thresholds proactively. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Application Uptime (Measured) ≥ 99.9% — Based on successful synthetic probes.",
              "Mean Time to Detect (MTTD) < 2 min — Time from outage occurrence to detection.",
              "Mean Time to Recover (MTTR) < 10 min — Time from detection to resolution or escalation.",
              "False Alert Rate ≤ 3% — Incorrectly flagged performance anomalies.",
              "Latency Baseline Accuracy ≥ 95% — AI model accuracy for dynamic threshold prediction.",
              "Automation Adoption Rate ≥ 85% — % of remediation actions executed autonomously."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Applications Monitored: 1,020",
              "Synthetic Tests Executed: 4.8 million",
              "Detected Incidents: 112",
              "Average MTTD: 1.6 min — Average MTTR: 9.2 min",
              "Automation Adoption: 87%",
              "Uptime Compliance Rate: 99.93%",
              "Latency Improvement (Post-Remediation): 14.6%",
              "False Alert Rate: 2.5%",
              "Engineer Hours Saved: ≈ 330 per month",
              "Audit Reports Generated: 10 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Synthetic Check Frequency ≤ 60 s — Multi-vantage testing per app.",
              "Health Correlation & Root Cause Analysis ≤ 3 min — AI inference and cross-metric validation.",
              "Remediation Trigger / Alert Creation ≤ 5 min — Integration to ITSM or SOAR workflows.",
              "Evidence Publication ≤ 1 hr — SLA compliance reporting and export.",
              "Agent Availability ≥ 99.9% — HA RunOps cluster monitored by Grafana / Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “AppAvailAI” microservice.",
              "Primary Interfaces — Load balancer REST APIs, synthetic test APIs, SIEM feeds.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing app maps, synthetic test results, and SLA baselines.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on app health diagnostics, load balancer telemetry, and RunOps observability KB.",
              "Security Controls — TLS 1.3 encryption, anonymized app metadata, RBAC isolation.",
              "Outputs — Application Availability Report (PDF/JSON), Root Cause Analysis Summary, SLA Dashboard Snapshot.",
              "Monitoring — Grafana dashboards showing uptime %, latency, probe failures, and SLA violations.",
              "Error Handling — Auto-retry failed synthetic runs; escalate to NOC after two consecutive failures."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Synthetic Check Failure, Latency Threshold Breach, or Scheduled SLA Verification.",
      workingPlane: "RunOps n8n cluster with containerized 'AppAvailAI' microservice.",
      memory: "Persisted app maps, synthetic test results, latency baselines, and SLA compliance history in Postgres.",
      reasoning: "Azure OpenAI driven correlation of application response, load balancer health, and backend logs to identify root cause and propose recovery.",
      execution: "Trigger automated remediation via SOAR/ITSM, or update configuration via LB APIs, followed by post-resolution validation."
    },
    kpis: [
      { label: "Application Uptime (Measured)", value: "99.93", unit: "%", target: "≥ 99.9%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD)", value: "1.6", unit: "min", target: "< 2 min", color: "#10b981" },
      { label: "Mean Time to Recover (MTTR)", value: "9.2", unit: "min", target: "< 10 min", color: "#10b981" },
      { label: "False Alert Rate", value: "2.5", unit: "%", target: "≤ 3%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "87", unit: "%", target: "≥ 85%", color: "#10b981" }
    ]
},

'ssl-tls-cert-agent': {
    title: "SSL/TLS Cert Agent",
    description: "Continuously monitors SSL/TLS certificates across all load balancers and app gateways, validating expiry, chain of trust, and cipher compliance — and automatically renews or replaces certificates to maintain continuous encryption integrity and compliance.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Zero-Downtime Renewal: Automatically renews and deploys certificates without service interruption.",
          "Compliance Enforcement: Ensures all certs meet cryptographic, key-length, and cipher-suite baselines.",
          "Operational Efficiency: Eliminates manual renewal effort and reduces outage risk.",
          "End-to-End Visibility: Provides a unified inventory and status of all deployed certificates.",
          "Audit & Regulatory Readiness: Maintains full lifecycle evidence for NIST, PCI-DSS, HIPAA, and ISO 27001 audits."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprises rely on hundreds to thousands of SSL/TLS certificates across F5, Citrix ADC, Avi, NGINX, AWS, and Azure gateways — yet many remain manually tracked, resulting in unexpected expirations, outages, or compliance failures.",
              "Expired or misconfigured certificates cause critical application downtime and security exposure (TLS downgrade, weak ciphers, expired chains).",
              "Manual renewal workflows across disparate systems are error-prone and lack central governance.",
              "Organizations need an autonomous certificate intelligence layer that continuously validates, renews, and redeploys certificates across the entire L7 delivery stack."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Zero-Downtime Renewal — Automatically renews and deploys certificates without service interruption.",
              "Compliance Enforcement — Ensures all certs meet cryptographic, key-length, and cipher-suite baselines.",
              "Operational Efficiency — Eliminates manual renewal effort and reduces outage risk.",
              "End-to-End Visibility — Provides a unified inventory and status of all deployed certificates.",
              "Audit & Regulatory Readiness — Maintains full lifecycle evidence for NIST, PCI-DSS, HIPAA, and ISO 27001 audits."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Certificate Expiry Monitoring: Detect and alert on certificates approaching expiration.",
              "Automated Certificate Renewal & Deployment: Auto-renew certs from internal CA or third-party issuers (Venafi, DigiCert, Microsoft CA).",
              "Chain of Trust Validation: Verify intermediate and root CA chains for every deployed certificate.",
              "Cipher & Policy Compliance: Validate TLS version, cipher strength, and algorithm alignment with enterprise policy.",
              "Audit & Lifecycle Reporting: Generate compliance logs and renewal history reports for every certificate."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Full-Stack Awareness: Tracks certificates across load balancers, app gateways, proxies, and API gateways.",
              "AI Reasoning: Evaluates certificate risk and determines renewal urgency or dependency impact.",
              "Autonomous Renewal: Executes renewals automatically via CA integrations, validates deployment success, and verifies endpoint health.",
              "Cross-Platform Synchronization: Ensures uniform cert distribution across multi-cloud and hybrid delivery environments.",
              "Learning Feedback Loop: Analyzes past expirations or failures to improve future prediction and automation accuracy."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Certificate Discovery: Enumerate all SSL/TLS certificates across load balancers, app gateways, and reverse proxies.",
              "Expiration & Health Monitoring: Track certificate validity, expiration, and chain completeness continuously.",
              "Automated Renewal Execution: Integrate with internal PKI or commercial CAs to renew expiring certs automatically.",
              "Deployment Automation: Push new certs to target devices, VIPs, or listener endpoints without downtime.",
              "Cipher & Policy Validation: Verify TLS version, cipher suite, and key strength per enterprise crypto policy.",
              "AI Reasoning Layer: Prioritize renewals, correlate impact, and ensure compliance with security frameworks.",
              "Audit & Lifecycle Logging: Record all certificate changes, deployments, and validations for compliance evidence.",
              "Rollback & Recovery: Maintain previous cert versions for immediate rollback if post-deployment validation fails."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Load Balancer APIs (F5, Citrix, Avi, NGINX, AWS, Azure) — Discover, deploy, and validate certificates (Bidirectional).",
              "Certificate Authorities (Venafi, DigiCert, Microsoft CA, Entrust) — Manage issuance, renewal, and revocation (Bidirectional).",
              "DDI Platforms (Infoblox, BlueCat) — Align DNS and certificate SANs for endpoint consistency (Bidirectional).",
              "CoreStack GRC / Compliance Modules — Store certificate lifecycle and audit logs (Bidirectional).",
              "SIEM / SOAR Systems — Send alerts for failed renewals or non-compliant certificates (Bidirectional).",
              "ServiceNow / Jira — Manage exceptions, approvals, and renewal workflow visibility (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Evaluate risk context, expiration trends, and generate renewal recommendations (Inbound).",
              "Confluence / Data Lake — Archive lifecycle logs, compliance reports, and validation summaries (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Certificate Inventory & Baseline Validation: Discover and assess all deployed certificates. (50%).",
              "Tier 2 — Expiration Monitoring & Policy Validation: Detect expiry risk, algorithm mismatch, or compliance gaps. (75%).",
              "Tier 3 — AI Reasoning & Autonomous Renewal: Execute renewals and push updated certs across endpoints. (90%).",
              "Tier 4 — Predictive Lifecycle Management & Continuous Learning: Forecast renewals and pre-stage certs proactively. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Certificate Renewal Compliance Rate ≥ 99% — % of certs renewed before expiry.",
              "Mean Time to Detect (MTTD) Expiring Certs < 24 hrs — Time from entering threshold (30/15/7 days) to detection.",
              "Mean Time to Renew (MTTRenew) < 4 hrs — Time from detection to full deployment and validation.",
              "Post-Deployment Validation Accuracy ≥ 98% — % of renewed certs correctly deployed and verified.",
              "Cipher & Policy Compliance Rate ≥ 97% — Certs compliant with enterprise crypto baselines.",
              "Automation Adoption Rate ≥ 85% — Renewals executed autonomously without manual action."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Certificates Monitored: 4,200",
              "Domains Covered: 1,050",
              "Expiring Certificates (≤30 days): 382",
              "Auto-Renewed Certificates: 345",
              "Manual Renewals (Policy-Approved Exceptions): 37",
              "Average MTTD: 8.6 hrs — Average MTTRenew: 3.2 hrs",
              "Compliance Rate (Post-Remediation): 99.4%",
              "Cipher Compliance Rate: 97.8%",
              "Automation Adoption: 87%",
              "Engineer Hours Saved: ≈ 310 per month",
              "Audit Reports Generated: 12 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Certificate Discovery & Refresh Cycle ≤ 12 hrs — Continuous sync across devices and platforms.",
              "Expiry Detection & Alerting ≤ 4 hrs — Triggered upon nearing policy threshold.",
              "Renewal & Deployment Execution ≤ 4 hrs — Includes issuance, replacement, and validation.",
              "Post-Renewal Validation Cycle ≤ 30 min — Confirm trust chain and cipher compliance.",
              "Evidence Publication ≤ 1 hr — Compliance report export to CoreStack / Confluence.",
              "Agent Availability ≥ 99.9% — HA n8n cluster monitored by Grafana / Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “SSLCertAI” microservice.",
              "Primary Interfaces — Load balancer REST APIs, CA management APIs, DDI integration, compliance modules.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing cert inventory, renewal logs, and compliance results.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on PKI lifecycle management, TLS policy standards, and RunOps Zero-Trust KB.",
              "Security Controls — TLS 1.3 encryption, signed certificate exchanges, RBAC access controls, vault-stored private keys.",
              "Outputs — SSL/TLS Lifecycle Report (JSON/PDF), Renewal Log, Cipher Compliance Snapshot, Audit Evidence Bundle.",
              "Monitoring — Grafana dashboards showing expiry timelines, compliance rate, and renewal throughput.",
              "Error Handling — Auto-retry failed renewals; rollback to prior cert if validation fails twice consecutively."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Certificate Discovery & Refresh Cycle, Expiry Threshold Breach, or Policy Non-Compliance Detection.",
      workingPlane: "RunOps n8n cluster with containerized 'SSLCertAI' microservice.",
      memory: "Persisted cert inventory, policy baselines, renewal logs, and historical failure data in Postgres.",
      reasoning: "Azure OpenAI driven risk evaluation and autonomous orchestration of renewal, deployment, and post-deployment validation steps.",
      execution: "Execute CA renewal request, push new cert to load balancer API, validate endpoint health and compliance, and log all audit evidence."
    },
    kpis: [
      { label: "Certificate Renewal Compliance Rate", value: "99.4", unit: "%", target: "≥ 99%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD) Expiring Certs", value: "8.6", unit: "hrs", target: "< 24 hrs", color: "#10b981" },
      { label: "Mean Time to Renew (MTTRenew)", value: "3.2", unit: "hrs", target: "< 4 hrs", color: "#10b981" },
      { label: "Post-Deployment Validation Accuracy", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Cipher & Policy Compliance Rate", value: "97.8", unit: "%", target: "≥ 97%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "87", unit: "%", target: "≥ 85%", color: "#10b981" }
    ]
},
'traffic-steering-agent': {
    title: "Traffic Steering Agent",
    description: "Continuously analyzes performance, cost, and utilization metrics across load balancers and app gateways to dynamically adjust load balancing algorithms, traffic distribution ratios, and routing policies — ensuring optimal performance, cost efficiency, and SLA adherence.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Optimized Performance: Routes traffic to the healthiest, lowest-latency backend automatically.",
          "Cost Efficiency: Steers requests away from high-cost regions or cloud zones dynamically.",
          "SLA Compliance: Adjusts steering in real time to maintain application availability and response-time guarantees.",
          "Multi-Cloud Governance: Ensures consistent traffic behavior across on-prem, cloud, and edge load balancers.",
          "Operational Autonomy: Eliminates manual rebalancing while maintaining full visibility and auditability."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Static load balancing policies (round robin, least connections, ratio-based) fail to adapt to real-time performance, user geography, or cost fluctuations.",
              "In hybrid and cloud environments, traffic may be steered to suboptimal or expensive regions, causing SLA breaches and wasted spend.",
              "Manual tuning and periodic reviews are too slow to respond to dynamic traffic patterns or provider pricing changes.",
              "Enterprises need an autonomous, performance- and cost-aware traffic steering agent that continuously learns, predicts, and rebalances distribution logic across all delivery controllers."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Optimized Performance — Routes traffic to the healthiest, lowest-latency backend automatically.",
              "Cost Efficiency — Steers requests away from high-cost regions or cloud zones dynamically.",
              "SLA Compliance — Adjusts steering in real time to maintain application availability and response-time guarantees.",
              "Multi-Cloud Governance — Ensures consistent traffic behavior across on-prem, cloud, and edge load balancers.",
              "Operational Autonomy — Eliminates manual rebalancing while maintaining full visibility and auditability."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Performance-Based Steering: Route users to endpoints with best health scores, latency, and availability.",
              "Cost-Aware Load Distribution: Dynamically shift workloads toward lowest-cost compute or bandwidth regions.",
              "Geographic Traffic Optimization: Adjust distribution by location and regulatory policy (GDPR, data residency).",
              "Multi-Cloud Routing: Steer requests across AWS, Azure, and GCP gateways based on performance and cost metrics.",
              "SLA Enforcement & Compliance: Prioritize mission-critical apps during performance or cost anomalies."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Telemetry-Driven Intelligence: Analyzes latency, packet loss, error rate, and response-time KPIs per region or backend.",
              "AI Reasoning: Evaluates cost, performance, and SLA context to determine optimal distribution strategy.",
              "Autonomous Adjustment: Updates load balancing methods (ratio, least connections, weighted response) in real time.",
              "Cross-Domain Coordination: Integrates with FinOps, App Availability, and Load Balancer Config Agents for holistic optimization.",
              "Predictive Learning: Anticipates congestion, failover events, or pricing fluctuations to pre-steer traffic proactively."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Real-Time Traffic Analytics: Continuously collect throughput, latency, and connection metrics across all pools and VIPs.",
              "Cost & Performance Correlation: Map current workloads to underlying infrastructure cost models and latency SLAs.",
              "Dynamic Algorithm Adjustment: Change load balancing methods (e.g., round robin → least response time) automatically.",
              "Weighted Ratio Tuning: Adjust backend pool ratios based on live performance and cost metrics.",
              "Cloud Region Optimization: Steer workloads between regions or providers using health and cost data.",
              "AI Reasoning Layer: Prioritize application criticality and determine optimal trade-off between cost and speed.",
              "Policy Enforcement & Rollback: Implement adjustments safely with automatic rollback if performance declines.",
              "Audit & Reporting: Document steering actions, results, and cost-performance improvements."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Load Balancer APIs (F5, Citrix, Avi, AWS, Azure, GCP) — Retrieve performance telemetry and enforce traffic policy updates (Bidirectional).",
              "Cloud Cost Management Systems (AWS Cost Explorer, Azure Cost Mgmt, CoreStack FinOps) — Ingest cost-per-region data for optimization (Inbound).",
              "App Availability Agent — Provide health and latency data to guide steering logic (Bidirectional).",
              "CoreStack GRC / Compliance Modules — Record steering events and compliance evidence (Bidirectional).",
              "ServiceNow / Jira — Log optimization actions, approvals, and policy exceptions (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Evaluate multi-variable decision logic (cost vs. performance vs. SLA) (Inbound).",
              "Confluence / Data Lake — Archive optimization reports, performance logs, and cost savings data (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Telemetry & Cost Data Collection: Aggregate performance and cost metrics from all endpoints. (50%).",
              "Tier 2 — Drift Detection & Threshold Analysis: Identify imbalance or cost deviations from policy baselines. (75%).",
              "Tier 3 — AI Reasoning & Steering Enforcement: Apply dynamic algorithm or ratio changes based on multi-variable context. (90%).",
              "Tier 4 — Predictive Optimization & Self-Learning Governance: Anticipate performance-cost anomalies and preemptively rebalance. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Traffic Steering Efficiency ≥ 97% — % of requests directed to optimal endpoints.",
              "Mean Time to Detect (MTTD) Imbalance < 5 min — Time from drift in performance or cost threshold to detection.",
              "Mean Time to Optimize (MTTO) < 10 min — Time from detection to updated steering enforcement.",
              "Cost Reduction Rate ≥ 10% — Monthly reduction in delivery or compute cost.",
              "Latency Improvement Post-Steering ≥ 15% — Average performance gain per steering event.",
              "Automation Adoption Rate ≥ 85% — % of steering decisions executed autonomously."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Load Balancers Monitored: 140",
              "Applications Optimized: 1,080",
              "Steering Adjustments Executed: 2,240",
              "Average MTTD: 4.2 min — Average MTTO: 9.1 min",
              "Latency Improvement: 17.5%",
              "Average Monthly Cost Savings: 11.6% (across cloud egress and compute)",
              "False Adjustment Rate: 2.3%",
              "Automation Adoption: 86%",
              "Engineer Hours Saved: ≈ 320 per month",
              "Audit Reports Generated: 10 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Telemetry Refresh Interval ≤ 5 min — Continuous updates from load balancers and clouds.",
              "Drift Detection & Analysis Cycle ≤ 5 min — Evaluate threshold and trend deviation.",
              "Steering Policy Enforcement ≤ 10 min — Apply load balancing method or ratio change safely.",
              "Evidence Publication ≤ 1 hr — Optimization and compliance report export.",
              "Agent Availability ≥ 99.9% — HA n8n cluster monitored via Grafana / Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “TrafficSteerAI” microservice.",
              "Primary Interfaces — Load balancer REST APIs, cost analytics APIs, performance telemetry feeds.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing endpoint health metrics, cost models, and steering decisions.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on L4–L7 optimization logic, FinOps data, and RunOps application delivery KB.",
              "Security Controls — TLS 1.3 encryption, signed API actions, RBAC-per-controller, and policy rollback safeguards.",
              "Outputs — Traffic Steering Report (JSON/PDF), Cost Optimization Summary, Performance Improvement Snapshot.",
              "Monitoring — Grafana dashboards visualizing traffic distribution, latency variance, and cost efficiency over time.",
              "Error Handling — Auto-rollback on degraded post-steering performance; flag recurring drift to NOC for human validation."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Telemetry Refresh, Performance or Cost Threshold Breach, or Scheduled Optimization Cycle.",
      workingPlane: "RunOps n8n cluster with containerized 'TrafficSteerAI' microservice.",
      memory: "Persisted health metrics, cost models, and historical steering outcomes in Postgres.",
      reasoning: "Azure OpenAI driven multi-criteria optimization balancing performance, cost, and SLA to determine dynamic distribution policies.",
      execution: "Execute dynamic algorithm/ratio changes via Load Balancer APIs, measure post-optimization performance, and generate audit logs."
    },
    kpis: [
      { label: "Traffic Steering Efficiency", value: "97", unit: "%", target: "≥ 97%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD) Imbalance", value: "4.2", unit: "min", target: "< 5 min", color: "#10b981" },
      { label: "Mean Time to Optimize (MTTO)", value: "9.1", unit: "min", target: "< 10 min", color: "#10b981" },
      { label: "Cost Reduction Rate", value: "11.6", unit: "%", target: "≥ 10%", color: "#10b981" },
      { label: "Latency Improvement Post-Steering", value: "17.5", unit: "%", target: "≥ 15%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "86", unit: "%", target: "≥ 85%", color: "#10b981" }
    ]
},

'blue-green-cutover-agent': {
    title: "Blue/Green Cutover Agent",
    description: "Automates traffic cutovers between blue (active) and green (standby) environments during deployments, patch cycles, and upgrades — ensuring zero-downtime transitions, full validation, and immediate rollback capabilities through intelligent, phased traffic shifting.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Zero-Downtime Deployments: Enables seamless switchovers between environments without service interruption.",
          "Reduced Deployment Risk: Automatically validates health before and after cutover, ensuring safe release promotion.",
          "Faster Time-to-Release: Eliminates manual LB changes and coordination delays.",
          "Rollback Assurance: Provides instant rollback if post-cutover validation fails.",
          "Operational Compliance: Maintains auditable records of all traffic transitions and validations."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Application release and patch events often require manual load balancer changes, creating human error risk and potential downtime.",
              "Traditional blue/green deployments rely on scripts that lack real-time validation, traffic analytics, or rollback intelligence.",
              "Cutovers without controlled ramp-up or health verification can lead to partial outages, degraded performance, or customer impact.",
              "Enterprises need an autonomous cutover agent that coordinates with CI/CD pipelines, verifies application health before and after traffic shifts, and safely manages transitions at scale."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Zero-Downtime Deployments — Enables seamless switchovers between environments without service interruption.",
              "Reduced Deployment Risk — Automatically validates health before and after cutover, ensuring safe release promotion.",
              "Faster Time-to-Release — Eliminates manual LB changes and coordination delays.",
              "Rollback Assurance — Provides instant rollback if post-cutover validation fails.",
              "Operational Compliance — Maintains auditable records of all traffic transitions and validations."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Automated Blue/Green Switchovers: Transition traffic between environments during releases or patch cycles.",
              "Phased Traffic Shifting: Gradually increase green traffic share (e.g., 10% → 50% → 100%) with health validation at each step.",
              "Rollback Execution: Automatically revert to blue environment if failure detected.",
              "Multi-Cloud Deployment Coordination: Manage cutovers across AWS, Azure, and on-prem load balancers simultaneously.",
              "Compliance & Audit Tracking: Log all cutover events, validation steps, and rollback triggers for audit."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Contextual Awareness: Understands application state, active sessions, and service dependencies before executing cutovers.",
              "AI Reasoning: Determines optimal traffic shift strategy based on health, performance, and load metrics.",
              "Autonomous Control: Executes phased cutovers, validates each transition, and reverses instantly on error.",
              "Cross-Platform Integration: Coordinates across CI/CD, load balancers, and observability platforms.",
              "Learning Feedback: Optimizes future cutovers based on prior performance and incident data."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Traffic Shift Orchestration: Automate traffic reallocation between blue and green environments across load balancers.",
              "Pre-Cutover Validation: Verify readiness via health checks, latency, and dependency status.",
              "Phased Release Control: Gradually increase green traffic percentage, pausing if thresholds are breached.",
              "Rollback Automation: Instantly restore traffic to blue if failure detected or metrics degrade.",
              "Session Preservation: Manage sticky sessions and ensure session migration without interruption.",
              "Integration with CI/CD Pipelines: Trigger from Jenkins, GitLab, or Azure DevOps workflows.",
              "AI Reasoning Layer: Evaluate health, performance, and SLA alignment before, during, and after cutover.",
              "Audit Logging & Reporting: Capture timeline, validation results, and change approvals for compliance."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Load Balancer APIs (F5, Citrix, Avi, AWS, Azure, GCP) — Execute traffic distribution changes (Bidirectional).",
              "CI/CD Platforms (Jenkins, GitLab, Azure DevOps, Argo CD) — Trigger cutovers within deployment pipelines (Bidirectional).",
              "App Availability Agent — Validate post-cutover health and response metrics (Bidirectional).",
              "Monitoring / Observability Systems (Grafana, Datadog, Splunk) — Correlate latency and error rates during rollout (Inbound).",
              "CoreStack GRC / Compliance Modules — Store cutover audit logs and compliance mapping (Bidirectional).",
              "ServiceNow / Jira — Manage change requests, approvals, and incident rollbacks (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Evaluate deployment readiness, risk factors, and rollback thresholds (Inbound).",
              "Confluence / Data Lake — Archive deployment and cutover reports for audit (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Pre-Cutover Validation & Readiness Check: Assess app health, backend availability, and LB readiness. (50%).",
              "Tier 2 — Controlled Traffic Transition: Execute phased or immediate traffic shifting across load balancers. (75%).",
              "Tier 3 — AI Reasoning & Rollback Orchestration: Correlate metrics, detect anomalies, and trigger rollback if needed. (90%).",
              "Tier 4 — Predictive Release Optimization & Continuous Learning: Learn from prior releases to optimize cutover strategies. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Cutover Success Rate ≥ 99% — Successful transitions without rollback or incident.",
              "Mean Time to Execute (MTTE) < 15 min — Duration from cutover initiation to completion.",
              "Rollback Accuracy ≥ 98% — % of rollbacks restoring full service integrity.",
              "Zero-Downtime Compliance Rate 100% — % of cutovers executed without user disruption.",
              "Automation Adoption Rate ≥ 85% — % of cutovers executed autonomously.",
              "Audit Evidence Coverage 100% — All events logged and mapped to release approvals."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Cutovers Executed: 186",
              "Applications Managed: 512",
              "Phased Rollouts Performed: 78",
              "Automated Rollbacks: 9 (triggered by SLA degradation)",
              "Average MTTE: 12.4 min",
              "Cutover Success Rate: 99.3%",
              "Zero-Downtime Compliance: 100%",
              "Rollback Accuracy: 98.7%",
              "Automation Adoption: 88%",
              "Engineer Hours Saved: ≈ 320 per month",
              "Audit Reports Generated: 10 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Pre-Cutover Validation Cycle ≤ 5 min — Includes backend and dependency readiness checks.",
              "Traffic Transition Execution ≤ 15 min — Phased or full cutover duration.",
              "Rollback Execution ≤ 5 min — Full restoration to blue environment.",
              "Post-Cutover Health Verification ≤ 10 min — App health validation using Availability Agent.",
              "Evidence Publication ≤ 1 hr — Audit and compliance reporting.",
              "Agent Availability ≥ 99.9% — HA RunOps cluster monitored via Grafana / Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “CutoverAI” microservice.",
              "Primary Interfaces — Load balancer REST APIs, CI/CD webhooks, monitoring platforms, and change management APIs.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing app mappings, cutover templates, and validation outcomes.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on release orchestration, rollback automation, and RunOps DevSecOps KB.",
              "Security Controls — TLS 1.3 encryption, signed LB config changes, RBAC per application domain, and rollback snapshot versioning.",
              "Outputs — Blue/Green Cutover Report (PDF/JSON), Rollback Summary, Deployment Health Scorecard, Audit Snapshot.",
              "Monitoring — Grafana dashboards tracking active cutovers, rollback events, and app health scores.",
              "Error Handling — Auto-rollback triggered on latency spike > threshold or App Availability Agent failure correlation."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "CI/CD Webhook (Deployment Complete), Scheduled Patch Window, or Manual Trigger.",
      workingPlane: "RunOps n8n cluster with containerized 'CutoverAI' microservice.",
      memory: "Persisted app mappings, cutover templates, performance baselines, and rollback snapshots in Postgres.",
      reasoning: "AI diagnoses pre-deployment risk, orchestrates phased traffic shift based on real-time health metrics, and enforces autonomous rollback logic.",
      execution: "Execute phased traffic changes via Load Balancer APIs, validate post-shift health using App Availability Agent, and commit change or trigger rollback."
    },
    kpis: [
      { label: "Cutover Success Rate", value: "99.3", unit: "%", target: "≥ 99%", color: "#10b981" },
      { label: "Mean Time to Execute (MTTE)", value: "12.4", unit: "min", target: "< 15 min", color: "#10b981" },
      { label: "Rollback Accuracy", value: "98.7", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Zero-Downtime Compliance Rate", value: "100", unit: "%", target: "100%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "88", unit: "%", target: "≥ 85%", color: "#10b981" },
      { label: "Audit Evidence Coverage", value: "100", unit: "%", target: "100%", color: "#10b981" }
    ]
},
'gslb-health-agent': {
    title: "GSLB Health Agent",
    description: "Continuously monitors the health, latency, and availability of global application endpoints managed under GSLB policies, ensuring seamless failover, traffic rebalancing, and SLA compliance across hybrid, multi-cloud, and geographically distributed environments.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Global Application Resilience: Ensures continuous availability through real-time GSLB failover monitoring.",
          "Performance Optimization: Validates latency-based routing and ensures users connect to optimal regions.",
          "Proactive Outage Detection: Detects regional failures before user impact through synthetic and API-based health checks.",
          "Compliance with SLAs: Enforces latency, uptime, and DNS failover thresholds defined in enterprise policy.",
          "Cross-Platform Governance: Provides unified health visibility across F5 GTM/DNS, Citrix GSLB, AWS Route 53, Azure Traffic Manager, and GCP Cloud DNS."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Global applications depend on GSLB to ensure regional resilience and intelligent user routing, but misconfigured or unmonitored health checks can lead to failed failovers, suboptimal routing, and degraded user experiences.",
              "Traditional monitoring systems detect regional outages reactively, lacking correlation between DNS-level GSLB decisions and real backend health.",
              "Enterprises need an autonomous, AI-driven agent that validates GSLB service health continuously, verifies failover readiness, and coordinates with application delivery controllers to ensure seamless continuity."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Global Application Resilience — Ensures continuous availability through real-time GSLB failover monitoring.",
              "Performance Optimization — Validates latency-based routing and ensures users connect to optimal regions.",
              "Proactive Outage Detection — Detects regional failures before user impact through synthetic and API-based health checks.",
              "Compliance with SLAs — Enforces latency, uptime, and DNS failover thresholds defined in enterprise policy.",
              "Cross-Platform Governance — Provides unified health visibility across F5 GTM/DNS, Citrix GSLB, AWS Route 53, Azure Traffic Manager, and GCP Cloud DNS."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Failover Validation: Validate that secondary sites and backup data centers are ready to receive traffic.",
              "Latency-Based Routing Assurance: Continuously test DNS and endpoint performance across regions.",
              "GSLB Policy Drift Detection: Identify and correct mismatched GSLB configurations across controllers or clouds.",
              "Global Health Reporting: Generate availability and performance scorecards per geography or region.",
              "Disaster Recovery Readiness: Ensure DNS failover triggers correctly during outage simulations or planned DR tests."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "End-to-End Awareness: Monitors DNS resolution, endpoint reachability, and latency in tandem.",
              "AI Reasoning: Correlates GSLB health checks with real-time application telemetry to distinguish network vs. app issues.",
              "Autonomous Validation: Tests regional failover readiness continuously and simulates DR conditions safely.",
              "Cross-Controller Coordination: Synchronizes health and policy states across multiple GSLB providers.",
              "Predictive Analytics: Identifies degradation patterns and recommends or triggers preemptive failover."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Global Endpoint Monitoring: Continuously test GSLB-managed endpoints for reachability, latency, and availability.",
              "DNS Resolution Verification: Validate authoritative responses from each GSLB region and provider.",
              "Failover Simulation: Perform safe DR simulations and validate standby readiness.",
              "Latency & Throughput Analytics: Measure performance across edge sites, clouds, and user regions.",
              "Health Policy Enforcement: Validate monitor thresholds, probe intervals, and response codes against enterprise baselines.",
              "AI Reasoning Layer: Determine fault domain (region, DNS, LB, or application) and recommend corrective actions.",
              "Automated Alerting & Remediation: Trigger failover commands, ITSM notifications, or SOAR responses as needed.",
              "Reporting & Compliance Evidence: Generate audit-ready reports for SLA and DR validation."
            ]
          },
          {
            title: "Integrations",
            content: [
              "GSLB Platforms (F5 GTM/DNS, Citrix GSLB, AWS Route 53, Azure Traffic Manager, GCP Cloud DNS) — Retrieve health metrics and enforce failover or rebalancing actions (Bidirectional).",
              "Load Balancer APIs (F5, Citrix, Avi, NGINX) — Correlate endpoint health with GSLB pool member state (Bidirectional).",
              "Synthetic & Observability Tools (ThousandEyes, Catchpoint, Grafana) — Provide latency, availability, and probe validation data (Inbound).",
              "CoreStack GRC / Compliance Modules — Store SLA and DR readiness evidence (Bidirectional).",
              "ServiceNow / Jira — Manage failover events, policy exceptions, and change records (Bidirectional).",
              "SIEM / SOAR Systems — Trigger failover automation workflows and correlate outage events (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Assess degradation patterns, reason root cause, and recommend preemptive action (Inbound).",
              "Confluence / Data Lake — Archive global health summaries, DNS response maps, and compliance reports (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Endpoint Monitoring & Health Collection: Gather DNS, latency, and endpoint data from all GSLB zones. (50%).",
              "Tier 2 — Correlation & Anomaly Detection: Identify health or performance anomalies across sites and controllers. (75%).",
              "Tier 3 — AI Reasoning & Autonomous Failover Validation: Analyze failure scope and trigger failover or rebalancing actions. (90%).",
              "Tier 4 — Predictive DR Readiness & Continuous Learning: Anticipate outages or saturation and preemptively test backup capacity. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "GSLB Endpoint Health Compliance ≥ 99% — % of active regions meeting health policy standards.",
              "Mean Time to Detect (MTTD) Regional Failure < 2 min — Time from degradation to failover initiation.",
              "Failover Validation Success Rate ≥ 98% — % of DR tests with successful DNS cutover.",
              "False Failover Rate ≤ 2% — Incorrect failover triggered by transient issues.",
              "Latency Improvement Post-Optimization ≥ 15% — Average latency improvement per region post rebalancing.",
              "Automation Adoption Rate ≥ 85% — % of failovers managed autonomously."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Global Regions Monitored: 32",
              "GSLB-Managed Applications: 420",
              "Health Probes Executed: 9.3 million",
              "Detected Regional Degradations: 164",
              "Failover Events Executed Automatically: 146",
              "Average MTTD: 1.4 min — Average Failover Validation Time: 3.6 min",
              "False Failover Rate: 1.8%",
              "Automation Adoption: 87%",
              "SLA Compliance Rate: 99.2%",
              "Engineer Hours Saved: ≈ 340 per month",
              "Audit Reports Generated: 12 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Health Probe Refresh Cycle ≤ 60 s — Multi-region endpoint validation.",
              "Degradation Detection & Correlation ≤ 2 min — AI anomaly analysis per GSLB zone.",
              "Failover Execution / Validation ≤ 5 min — Includes DNS update and post-failover verification.",
              "DR Simulation Cycle ≤ 12 hrs — Automated non-disruptive failover test frequency.",
              "Evidence Publication ≤ 1 hr — SLA, DR, and GSLB health reporting.",
              "Agent Availability ≥ 99.9% — HA n8n cluster monitored by Grafana / Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “GSLBHealthAI” microservice.",
              "Primary Interfaces — GSLB REST APIs, DNS telemetry, load balancer controllers, observability feeds.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing regional health baselines, latency metrics, and failover logs.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on global traffic engineering, DNS failover logic, and RunOps network resiliency KB.",
              "Security Controls — TLS 1.3 encryption, signed API actions, RBAC-per-region enforcement, DNSSEC validation for trust integrity.",
              "Outputs — GSLB Health Report (JSON/PDF), Failover Validation Log, Regional Latency Map, Compliance Evidence Snapshot.",
              "Monitoring — Grafana dashboards showing global uptime, latency heatmaps, failover events, and SLA metrics.",
              "Error Handling — Auto-rollback if failover increases latency or error rate; escalate to NOC after 2 failed retries."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Health Probe Refresh Cycle, Regional Degradation Detection, or Scheduled DR Simulation.",
      workingPlane: "RunOps n8n cluster with containerized 'GSLBHealthAI' microservice.",
      memory: "Persisted regional health baselines, latency history, failover logs, and policy baselines in Postgres.",
      reasoning: "AI correlates DNS, endpoint, and application metrics to diagnose global failure scope, determines root cause, and orchestrates autonomous failover or rebalancing.",
      execution: "Execute failover commands via GSLB APIs, run post-failover verification, and generate audit reports for compliance."
    },
    kpis: [
      { label: "GSLB Endpoint Health Compliance", value: "99.2", unit: "%", target: "≥ 99%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD) Regional Failure", value: "1.4", unit: "min", target: "< 2 min", color: "#10b981" },
      { label: "Failover Validation Success Rate", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "False Failover Rate", value: "1.8", unit: "%", target: "≤ 2%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "87", unit: "%", target: "≥ 85%", color: "#10b981" }
    ]
},

'app-acceleration-agent': {
    title: "App Acceleration Agent",
    description: "Continuously monitors application traffic patterns and enables intelligent caching, compression, and optimization features on ADCs to enhance performance, reduce latency, and improve end-user experience — without sacrificing security or compliance.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Improved Application Performance: Reduces response times through caching, compression, and transport optimization.",
          "Bandwidth Savings: Decreases redundant data transfers and improves throughput efficiency.",
          "Enhanced User Experience: Optimizes content delivery for both static and dynamic workloads.",
          "Operational Automation: Eliminates manual ADC tuning and reactive troubleshooting.",
          "Cross-Platform Consistency: Harmonizes acceleration configurations across F5, Citrix, NGINX, Avi, AWS, and Azure ADCs."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "As application traffic grows, ADC optimization features (e.g., caching, compression, TCP tuning) are underused or statically configured, resulting in poor performance and inefficient bandwidth utilization.",
              "Manual optimization requires deep understanding of app behavior, often misaligned with real-world traffic conditions.",
              "Without continuous tuning, enterprises face high latency, redundant payload delivery, and increased infrastructure cost.",
              "Enterprises need an autonomous agent that dynamically detects optimization opportunities, adjusts ADC policies in real time, and ensures alignment with SLA, security, and compliance standards."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Improved Application Performance — Reduces response times through caching, compression, and transport optimization.",
              "Bandwidth Savings — Decreases redundant data transfers and improves throughput efficiency.",
              "Enhanced User Experience — Optimizes content delivery for both static and dynamic workloads.",
              "Operational Automation — Eliminates manual ADC tuning and reactive troubleshooting.",
              "Cross-Platform Consistency — Harmonizes acceleration configurations across F5, Citrix, NGINX, Avi, AWS, and Azure ADCs."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Dynamic Content Caching: Cache static and semi-dynamic objects at the ADC to reduce server load.",
              "HTTP Compression Optimization: Apply intelligent compression based on content type and user device profile.",
              "TCP / TLS Session Optimization: Tune session reuse, congestion windows, and buffer sizes automatically.",
              "Adaptive Policy Tuning: Enable or disable optimization features based on latency and throughput metrics.",
              "Performance Analytics & Reporting: Provide continuous visibility into optimization impact and user experience gain."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Traffic-Aware Intelligence: Understands real-time application mix, payload size, and user behavior.",
              "AI Reasoning: Determines which optimizations (cache, compression, TCP tuning) yield the highest benefit.",
              "Autonomous Configuration: Applies and validates optimization changes across ADC platforms safely.",
              "Cross-Layer Correlation: Aligns acceleration features with QoS, security, and App Availability Agents.",
              "Continuous Learning: Refines optimization strategies using performance feedback and SLA results."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Traffic Profiling & Pattern Recognition: Continuously analyze request-response behavior, content types, and throughput trends.",
              "Dynamic Caching Enablement: Identify cacheable content and configure caching policies automatically.",
              "Compression Optimization: Enable GZIP/Brotli compression dynamically for compatible traffic flows.",
              "TCP & TLS Optimization: Tune session reuse, handshake acceleration, and connection multiplexing.",
              "AI Reasoning Engine: Balance performance gains with resource utilization and compliance policy.",
              "Adaptive Policy Enforcement: Adjust cache TTL, compression thresholds, and buffer settings in real time.",
              "Cross-Vendor Policy Standardization: Maintain consistent acceleration policies across hybrid ADCs.",
              "Reporting & Audit Evidence: Provide visibility into optimization savings, performance KPIs, and compliance alignment."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ADCs / Load Balancers (F5, Citrix, Avi, NGINX, AWS ALB, Azure Front Door) — Retrieve telemetry and apply optimization policies (Bidirectional).",
              "Monitoring / Observability Systems (Grafana, Datadog, ThousandEyes) — Feed latency, throughput, and page load metrics (Inbound).",
              "App Availability & Traffic Steering Agents — Correlate performance insights and coordinate optimization (Bidirectional).",
              "CoreStack GRC / Compliance Modules — Record optimization actions and performance evidence (Bidirectional).",
              "ServiceNow / Jira — Log optimization events, policy approvals, and exceptions (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Evaluate optimization opportunities and calculate performance ROI (Inbound).",
              "Confluence / Data Lake — Archive performance improvements, configuration deltas, and reports (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Telemetry & Performance Baseline Collection: Gather performance metrics and establish optimization thresholds. (50%).",
              "Tier 2 — Opportunity Identification & Policy Validation: Detect cache/compression candidates and validate ADC readiness. (75%).",
              "Tier 3 — AI Reasoning & Optimization Enforcement: Apply tuning dynamically, monitor impact, and rollback if needed. (90%).",
              "Tier 4 — Continuous Learning & Predictive Optimization: Pre-tune policies using predictive workload and seasonal patterns. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Latency Reduction Rate ≥ 20% — Measured decrease in average response time.",
              "Bandwidth Savings Rate ≥ 25% — % of data saved via caching/compression.",
              "Cache Hit Ratio ≥ 85% — % of eligible requests served from ADC cache.",
              "Mean Time to Optimize (MTTO) < 10 min — Time from detection to applied optimization policy.",
              "False Optimization Rate ≤ 3% — Policies applied without measurable benefit.",
              "Automation Adoption Rate ≥ 85% — % of optimizations executed autonomously."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Applications Accelerated: 920",
              "ADCs Integrated: 140",
              "Optimization Policies Applied: 2,860",
              "Average Latency Reduction: 22.8%",
              "Average Bandwidth Savings: 27.1%",
              "Cache Hit Ratio: 86.4%",
              "Automation Adoption: 87%",
              "False Optimization Rate: 2.5%",
              "Engineer Hours Saved: ≈ 340 per month",
              "Audit Reports Generated: 11 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Performance Telemetry Collection ≤ 5 min — Continuous per-application monitoring.",
              "Optimization Evaluation Cycle ≤ 10 min — AI-based policy analysis and validation.",
              "Policy Enforcement Execution ≤ 15 min — Caching/compression adjustments and verification.",
              "Post-Optimization Validation ≤ 30 min — Measure improvement and confirm user impact.",
              "Evidence Publication ≤ 1 hr — Audit and performance reporting to compliance tools.",
              "Agent Availability ≥ 99.9% — HA n8n cluster monitored via Grafana / Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “AppAccelAI” microservice.",
              "Primary Interfaces — REST/SOAP APIs for ADCs, observability metrics, AI reasoning API.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing performance baselines, optimization logs, and configuration versions.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on L4-L7 acceleration logic, caching/compression principles, and RunOps performance KB.",
              "Security Controls — TLS 1.3 encryption, signed API calls, RBAC, and rollback-safe policy application.",
              "Outputs — App Acceleration Report (PDF/JSON), Optimization Summary, Bandwidth Savings Snapshot, Compliance Audit Log.",
              "Monitoring — Grafana dashboards showing latency improvements, cache ratios, and data reduction trends.",
              "Error Handling — Auto-rollback on performance degradation post-optimization; alert NOC if three consecutive failures occur."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Performance Telemetry Collection Cycle, Latency Breach, or Traffic Pattern Anomaly.",
      workingPlane: "RunOps n8n cluster with containerized 'AppAccelAI' microservice.",
      memory: "Persisted performance baselines, optimization logs, configuration versions, and historical optimization impact data in Postgres.",
      reasoning: "AI evaluates application traffic patterns, calculates potential ROI from caching/compression/TCP tuning, and generates safe, dynamic ADC policy deltas.",
      execution: "Execute ADC policy adjustments, monitor post-change performance for a validation period, and trigger rollback if performance degrades below baseline."
    },
    kpis: [
      { label: "Latency Reduction Rate", value: "22.8", unit: "%", target: "≥ 20%", color: "#10b981" },
      { label: "Bandwidth Savings Rate", value: "27.1", unit: "%", target: "≥ 25%", color: "#10b981" },
      { label: "Cache Hit Ratio", value: "86.4", unit: "%", target: "≥ 85%", color: "#10b981" },
      { label: "Mean Time to Optimize (MTTO)", value: "10", unit: "min", target: "< 10 min", color: "#f59e0b" },
      { label: "False Optimization Rate", value: "2.5", unit: "%", target: "≤ 3%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "87", unit: "%", target: "≥ 85%", color: "#10b981" }
    ]
},
'lb-drift-detector-agent': {
    title: "LB Drift Detector Agent",
    description: "Continuously compares active load balancer configurations against enterprise golden templates and compliance policies, automatically flagging and remediating configuration drift to preserve consistency, reliability, and security across all environments.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Configuration Integrity: Ensures every LB instance adheres to golden configuration standards.",
          "Security Assurance: Detects unauthorized or insecure policy deviations (cipher changes, ACL edits).",
          "Operational Consistency: Harmonizes VIP, pool, and profile settings across all LBs and regions.",
          "Reduced MTTR: Speeds up detection and correction of misconfigurations before impact.",
          "Compliance Readiness: Provides auditable evidence of drift detection, remediation, and baseline adherence."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprise load balancer environments evolve through frequent changes, patching, and ad-hoc edits, resulting in configuration drift that introduces inconsistent policies, security gaps, and performance anomalies.",
              "Manual review and script-based audits are infrequent, siloed, and error-prone, often discovering drift only after an incident or outage.",
              "Drift in VIPs, SSL profiles, health monitors, or routing logic can break compliance or disrupt app delivery.",
              "Enterprises need a self-aware, autonomous drift detection agent that continuously validates configurations, flags deviations, and synchronizes them back to golden templates automatically."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Configuration Integrity — Ensures every LB instance adheres to golden configuration standards.",
              "Security Assurance — Detects unauthorized or insecure policy deviations (cipher changes, ACL edits).",
              "Operational Consistency — Harmonizes VIP, pool, and profile settings across all LBs and regions.",
              "Reduced MTTR — Speeds up detection and correction of misconfigurations before impact.",
              "Compliance Readiness — Provides auditable evidence of drift detection, remediation, and baseline adherence."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Golden Template Enforcement: Validate active LB configurations against enterprise-defined templates.",
              "Policy Drift Detection: Detect modifications to VIPs, pools, persistence, or health monitors.",
              "SSL/TLS Configuration Compliance: Validate cipher suites and key exchange parameters.",
              "Change Control Validation: Flag configuration changes not associated with approved ITSM tickets.",
              "Compliance Reporting: Generate configuration deviation reports for audit and GRC tracking."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Context-Aware Detection: Identifies drift across structural, operational, and security layers simultaneously.",
              "AI Reasoning: Determines whether drift is intentional (approved change) or unauthorized deviation.",
              "Autonomous Remediation: Reconciles configuration mismatches with golden templates automatically or via approval workflows.",
              "Cross-Platform Coverage: Validates consistency across F5, Citrix, Avi, NGINX, and cloud LB platforms.",
              "Learning Feedback: Adapts baselines dynamically by learning approved deviations and recurring environment patterns."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Baseline Template Validation: Compare running configurations (VIPs, pools, monitors, SSL profiles) to enterprise templates.",
              "Change Detection Engine: Identify and classify deviations by severity (functional, security, compliance).",
              "Policy Cross-Correlation: Validate configurations against global and per-application policies.",
              "Golden Template Enforcement: Automatically restore configurations to baseline or raise ITSM tickets for remediation.",
              "AI Reasoning Layer: Analyze drift origin, assess impact, and determine if exceptions apply.",
              "Audit Logging & Evidence Generation: Record configuration states before and after correction.",
              "Multi-Vendor Normalization: Abstract differences across ADC APIs into standardized validation logic.",
              "Continuous Learning: Refine templates automatically as approved deviations are accepted."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Load Balancer APIs (F5, Citrix, Avi, NGINX, AWS, Azure) — Retrieve and validate configuration data (Bidirectional).",
              "Load Balancer Config Agent — Coordinate remediation and policy synchronization (Bidirectional).",
              "SSL/TLS Cert Agent — Cross-verify profile certificates and cipher policy alignment (Bidirectional).",
              "CoreStack GRC / Compliance Modules — Store audit evidence and drift findings (Bidirectional).",
              "ServiceNow / Jira — Log deviation alerts, remediation tickets, and approvals (Bidirectional).",
              "SIEM / SOAR Systems — Correlate drift events with security changes or incidents (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Analyze deviation context and recommend corrective actions (Inbound).",
              "Confluence / Data Lake — Archive drift reports, compliance scorecards, and historical trends (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Configuration Inventory & Baseline Comparison: Collect LB configurations and compare against golden templates. (50%).",
              "Tier 2 — Deviation Detection & Impact Classification: Identify and rank configuration drift by severity. (75%).",
              "Tier 3 — AI Reasoning & Remediation Coordination: Recommend or auto-execute configuration corrections. (90%).",
              "Tier 4 — Continuous Learning & Predictive Compliance: Learn common drift trends and refine templates proactively. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Configuration Compliance Rate ≥ 98% — % of LB objects aligned with golden templates.",
              "Mean Time to Detect (MTTD) Drift < 2 hrs — Time from unauthorized change to detection.",
              "Mean Time to Remediate (MTTR) < 6 hrs — Time from detection to correction or approval.",
              "False Positive Rate ≤ 3% — Approved changes incorrectly flagged.",
              "Automation Adoption Rate ≥ 85% — % of detected deviations auto-remediated.",
              "Audit Evidence Coverage 100% — All drift events logged and traceable."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Load Balancers Audited: 148",
              "Configuration Objects Reviewed: 8,720 (VIPs, pools, monitors, SSL profiles, policies)",
              "Drift Events Detected: 612",
              "Auto-Remediated: 514 (84%)",
              "Average MTTD: 1.9 hrs — Average MTTR: 5.2 hrs",
              "Post-Remediation Compliance Rate: 99.3%",
              "False Positive Rate: 2.6%",
              "Automation Adoption: 86%",
              "Engineer Hours Saved: ≈ 330 per month",
              "Audit Reports Generated: 11 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Configuration Audit Cycle ≤ 4 hrs — Continuous per-platform validation loop.",
              "Deviation Detection & Classification ≤ 2 hrs — AI-driven drift correlation.",
              "Remediation Enforcement / Ticketing ≤ 6 hrs — Automatic sync or ITSM-based fix approval.",
              "Evidence Publication ≤ 1 hr — Compliance and drift summary reporting.",
              "Agent Availability ≥ 99.9% — HA RunOps cluster monitored via Grafana / Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “LBDriftAI” microservice.",
              "Primary Interfaces — Load balancer REST/SOAP APIs, compliance baselines, golden template repositories.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing configuration templates, drift history, and deviation logs.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on ADC configuration governance, golden template mapping, and RunOps compliance KB.",
              "Security Controls — TLS 1.3, signed API actions, RBAC per environment, immutable drift audit trail.",
              "Outputs — LB Drift Detection Report (PDF/JSON), Compliance Snapshot, Remediation Summary, Change Audit Log.",
              "Monitoring — Grafana dashboards visualizing drift frequency, remediation velocity, and compliance trends.",
              "Error Handling — Auto-rollback failed config syncs; flag persistent drift to NOC after two validation cycles."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Configuration Audit Cycle, Unauthorized Configuration Change, or Compliance Policy Update.",
      workingPlane: "RunOps n8n cluster with containerized 'LBDriftAI' microservice.",
      memory: "Persisted configuration templates, drift history, and deviation logs in Postgres.",
      reasoning: "AI validates configuration deltas against golden templates and ITSM records to classify drift severity and determine autonomous or ticket-based remediation.",
      execution: "Execute config synchronization (remediation) via Load Balancer APIs, log the remediation action, and update compliance evidence in GRC."
    },
    kpis: [
      { label: "Configuration Compliance Rate", value: "99.3", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD) Drift", value: "1.9", unit: "hrs", target: "< 2 hrs", color: "#10b981" },
      { label: "Mean Time to Remediate (MTTR)", value: "5.2", unit: "hrs", target: "< 6 hrs", color: "#10b981" },
      { label: "False Positive Rate", value: "2.6", unit: "%", target: "≤ 3%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "86", unit: "%", target: "≥ 85%", color: "#10b981" },
      { label: "Audit Evidence Coverage", value: "100", unit: "%", target: "100%", color: "#10b981" }
    ]
},

'ddos-mitigation-agent': {
    title: "DDoS Mitigation Agent",
    description: "Continuously monitors inbound and cross-region application traffic for volumetric, protocol, or application-layer DDoS activity, and autonomously triggers diversion to designated scrubbing centers or upstream mitigation providers to maintain service availability and performance.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Real-Time Attack Detection: Identifies volumetric, reflection, and application-layer DDoS events instantly.",
          "Automated Traffic Diversion: Redirects malicious traffic to scrubbing centers before infrastructure saturation.",
          "Reduced MTTR for DDoS Events: Automates detection-to-mitigation workflows to near-zero manual latency.",
          "Resilient Service Availability: Ensures uninterrupted access to critical services during active attacks.",
          "Compliance & Audit Assurance: Provides complete visibility and evidence for incident reports and SLAs."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "DDoS attacks have evolved in scale and sophistication, leveraging botnets, IoT devices, and reflection vectors to overwhelm infrastructure.",
              "Manual mitigation or delayed diversion to scrubbing centers increases outage duration, customer impact, and collateral damage.",
              "Traditional load balancers and firewalls detect rate anomalies but cannot autonomously coordinate failover or diversion workflows.",
              "Enterprises need a self-governing DDoS mitigation agent capable of continuous monitoring, automated decision-making, and integration with cloud or ISP scrubbing services for immediate response."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Real-Time Attack Detection — Identifies volumetric, reflection, and application-layer DDoS events instantly.",
              "Automated Traffic Diversion — Redirects malicious traffic to scrubbing centers before infrastructure saturation.",
              "Reduced MTTR for DDoS Events — Automates detection-to-mitigation workflows to near-zero manual latency.",
              "Resilient Service Availability — Ensures uninterrupted access to critical services during active attacks.",
              "Compliance & Audit Assurance — Provides complete visibility and evidence for incident reports and SLAs."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Volumetric DDoS Detection: Identify unusual spikes in PPS, BPS, or SYN flood activity.",
              "Traffic Diversion Workflow: Initiate API-triggered diversion to scrubbing centers (Cloudflare, Akamai, Radware, Arbor, etc.).",
              "Hybrid Cloud Protection: Orchestrate mitigation across on-prem and cloud-hosted ADCs.",
              "Attack Classification & Reporting: Categorize attacks by vector type (TCP, UDP, ICMP, DNS, HTTP flood).",
              "Post-Mitigation Validation: Validate clean traffic return and automatically revert routing once threat subsides."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Adaptive Learning: Learns normal traffic patterns per app, region, and VIP.",
              "AI Reasoning: Correlates volumetric anomalies with legitimate usage spikes to minimize false positives.",
              "Autonomous Response: Executes diversion, ACL updates, and route advertisements automatically.",
              "Cross-Platform Coordination: Integrates with load balancers, CDNs, ISPs, and scrubbing vendors.",
              "Self-Healing Restoration: Monitors mitigation outcomes and restores direct routing when clean traffic resumes."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Traffic Anomaly Detection: Continuously monitor inbound packet rate, connection count, and flow entropy.",
              "Protocol Behavior Analysis: Identify attack vectors such as SYN floods, UDP amplification, ICMP floods, and slowloris attacks.",
              "AI-Driven Classification: Use multi-feature models (volume, duration, distribution) to confirm malicious behavior.",
              "Automated Diversion Execution: Trigger upstream BGP advertisements or API calls to redirect traffic to scrubbing centers.",
              "Integration with Mitigation Providers: Coordinate with external DDoS defense platforms (Cloudflare, Akamai, Radware, Arbor, AWS Shield Advanced, Azure DDoS Protection).",
              "Post-Mitigation Health Validation: Confirm clean traffic return, latency normalization, and service health post-event.",
              "Audit & Evidence Generation: Record attack timelines, mitigation actions, and service continuity proof."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Load Balancer APIs (F5, Citrix, Avi, AWS, Azure) — Collect VIP telemetry, session rates, and enforce ACLs (Bidirectional).",
              "Network Telemetry Systems (NetFlow, sFlow, SNMP) — Capture volumetric traffic statistics for anomaly detection (Inbound).",
              "Scrubbing Centers (Cloudflare, Akamai, Radware, Arbor, Imperva) — Execute API-triggered diversion and recovery workflows (Bidirectional).",
              "SIEM / SOAR Systems — Correlate DDoS alerts with incident response automation (Bidirectional).",
              "CoreStack GRC / Compliance Modules — Store mitigation evidence, reports, and SLA compliance data (Bidirectional).",
              "ServiceNow / Jira — Track incident tickets, diversion events, and approval workflows (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Classify attack vectors, assess impact, and generate mitigation recommendations (Inbound).",
              "Confluence / Data Lake — Archive attack summaries, traffic graphs, and post-incident reviews (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Telemetry Collection & Baseline Establishment: Continuously gather traffic and performance metrics. (50%).",
              "Tier 2 — Anomaly Detection & Classification: Identify and categorize attack vectors using AI models. (75%).",
              "Tier 3 — Autonomous Diversion & Mitigation Orchestration: Trigger BGP reroutes or scrubbing workflows automatically. (90%).",
              "Tier 4 — Predictive Threat Intelligence & Continuous Learning: Learn evolving DDoS patterns and pre-stage mitigation based on risk signals. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Mean Time to Detect (MTTD) < 60 s — From attack initiation to anomaly identification.",
              "Mean Time to Mitigate (MTTM) < 3 min — From detection to scrubbing diversion execution.",
              "False Positive Rate ≤ 3% — Legitimate traffic incorrectly diverted.",
              "Traffic Restoration Accuracy ≥ 98% — Successful routing restoration post-attack.",
              "Service Availability During Attack ≥ 99.9% — % of uptime maintained under DDoS pressure.",
              "Automation Adoption Rate ≥ 85% — % of mitigations executed autonomously."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "VIPs Protected: 980",
              "Average Traffic Volume Monitored: 3.8 Tbps aggregate",
              "Detected DDoS Events: 47",
              "Automated Diversion Executions: 42 (89%)",
              "Average MTTD: 42 sec — Average MTTM: 2.8 min",
              "False Positive Rate: 2.1%",
              "Service Availability: 99.94% during events",
              "Post-Mitigation Latency Normalization Time: 7.2 min",
              "Automation Adoption: 87%",
              "Engineer Hours Saved: ≈ 360 per month",
              "Audit Reports Generated: 12 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Telemetry Refresh Interval ≤ 30 s — Continuous collection of flow and session data.",
              "Anomaly Detection Cycle ≤ 1 min — ML model evaluation of abnormal patterns.",
              "Diversion & Scrubbing Execution ≤ 3 min — BGP advertisement or API trigger to scrubbing provider.",
              "Traffic Restoration / Reversion ≤ 10 min — Resume direct routing post-clean traffic validation.",
              "Evidence Publication ≤ 1 hr — Generate compliance and incident reports.",
              "Agent Availability ≥ 99.9% — HA RunOps cluster monitored via Grafana / Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “DDoSMitigateAI” microservice.",
              "Primary Interfaces — Load balancer and ADC APIs, NetFlow/sFlow telemetry collectors, scrubbing center APIs.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing traffic baselines, attack signatures, and event logs.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on DDoS detection patterns, traffic analytics, and RunOps security KB.",
              "Security Controls — TLS 1.3, signed API transactions, BGP route authentication, RBAC-per-environment enforcement.",
              "Outputs — DDoS Incident Report (PDF/JSON), Traffic Analysis Summary, Mitigation Timeline, SLA Snapshot.",
              "Monitoring — Grafana dashboards showing PPS/BPS trends, attack timelines, and mitigation performance.",
              "Error Handling — Auto-rollback of diversion if scrubbing latency exceeds threshold; notify SOC for manual review."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Telemetry Threshold Breach (PPS/BPS), Anomaly Detection, or External Threat Feed Alert.",
      workingPlane: "RunOps n8n cluster with containerized 'DDoSMitigateAI' microservice.",
      memory: "Persisted traffic baselines, attack signatures, and event logs in Postgres.",
      reasoning: "AI correlates volumetric and application-layer anomalies against normal usage, classifies the attack vector, and orchestrates immediate, targeted traffic diversion.",
      execution: "Execute API call or BGP route advertisement to scrubbing center, monitor mitigation effect, and automatically revert routing post-clean traffic validation."
    },
    kpis: [
      { label: "Mean Time to Detect (MTTD)", value: "42", unit: "sec", target: "< 60 s", color: "#10b981" },
      { label: "Mean Time to Mitigate (MTTM)", value: "2.8", unit: "min", target: "< 3 min", color: "#10b981" },
      { label: "False Positive Rate", value: "2.1", unit: "%", target: "≤ 3%", color: "#10b981" },
      { label: "Traffic Restoration Accuracy", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Service Availability During Attack", value: "99.94", unit: "%", target: "≥ 99.9%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "87", unit: "%", target: "≥ 85%", color: "#10b981" }
    ]
},
'load-balancer-cost-guard-agent': {
    title: "Load Balancer Cost Guard Agent",
    description: "Continuously analyzes load balancer utilization, VIP traffic, and licensing metrics to detect underused or idle resources, optimize license consumption, and reduce operational cost — while maintaining compliance and performance thresholds.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "License Cost Optimization: Identifies unused or low-throughput VIPs to reclaim capacity or downgrade licenses.",
          "Operational Efficiency: Reduces wasted resources through automated consolidation or idle decommissioning.",
          "Improved Utilization Governance: Maintains optimal performance-to-cost balance across hybrid load balancers.",
          "Cross-Platform Cost Visibility: Provides unified analytics across on-prem and cloud ADCs.",
          "Audit & Compliance Transparency: Logs all optimization decisions for cost governance and policy tracking."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprise ADC environments accumulate hundreds of VIPs, pools, and service profiles, many of which see little or no production traffic.",
              "Over time, licenses, throughput tiers, and instance sizes become misaligned with actual usage, driving unnecessary cost.",
              "Manual reviews across F5, Citrix, AWS, Azure, and GCP load balancers are time-consuming and fragmented, lacking unified visibility.",
              "Enterprises need a FinOps-aware agent that continuously monitors utilization, correlates costs, and recommends right-sizing or consolidation actions — without degrading performance or compliance."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "License Cost Optimization — Identifies unused or low-throughput VIPs to reclaim capacity or downgrade licenses.",
              "Operational Efficiency — Reduces wasted resources through automated consolidation or idle decommissioning.",
              "Improved Utilization Governance — Maintains optimal performance-to-cost balance across hybrid load balancers.",
              "Cross-Platform Cost Visibility — Provides unified analytics across on-prem and cloud ADCs.",
              "Audit & Compliance Transparency — Logs all optimization decisions for cost governance and policy tracking."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Underutilized VIP Identification: Detect VIPs or pools with near-zero or minimal traffic volumes.",
              "License Right-Sizing Recommendations: Analyze capacity tiers and suggest license reductions or consolidations.",
              "Idle Pool & Monitor Detection: Flag backend pools or monitors linked to decommissioned apps.",
              "Cross-Vendor Utilization Correlation: Aggregate usage data across F5, Citrix, AWS, Azure, and GCP load balancers.",
              "FinOps Reporting & Forecasting: Quantify potential savings and produce detailed cost optimization reports."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Usage-Aware Reasoning: Correlates throughput, session rates, and health metrics to determine utilization patterns.",
              "Cross-Licensing Awareness: Analyzes ADC license tiers and consumption metrics across vendors.",
              "Predictive Optimization: Forecasts future usage trends and right-sizing opportunities.",
              "Autonomous Recommendations: Generates FinOps actions, approval workflows, or license downgrade automation.",
              "Continuous Learning: Learns app lifecycle and traffic trends to prevent premature deactivation of critical resources."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Traffic & Utilization Analysis: Continuously monitor connections, throughput, and packet rates per VIP and pool.",
              "Idle VIP & Pool Detection: Identify objects with no active traffic, connections, or health probes over defined time windows.",
              "License & Tier Correlation: Match utilization data with license consumption (vCPU, Mbps, or instance count).",
              "AI-Driven Cost Reasoning: Determine right-sizing recommendations without compromising SLA or performance.",
              "Automated Decommissioning Workflows: Retire idle VIPs or scale down unused instances with ITSM approval.",
              "Multi-Cloud & On-Prem Analytics: Unify visibility across AWS, Azure, GCP, and data center ADCs.",
              "Audit & Reporting Engine: Produce monthly cost savings reports and license utilization dashboards."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Load Balancer APIs (F5, Citrix, Avi, NGINX, AWS, Azure, GCP) — Retrieve utilization metrics, license tiers, and configuration data (Bidirectional).",
              "FinOps / Cost Management Tools (AWS Cost Explorer, Azure Cost Mgmt, CoreStack FinOps) — Correlate utilization with billing and cost data (Bidirectional).",
              "App Availability & Traffic Steering Agents — Validate active usage before license changes (Bidirectional).",
              "CoreStack GRC / Compliance Modules — Store cost governance evidence and policy audit logs (Bidirectional).",
              "ServiceNow / Jira — Generate optimization tickets or approval workflows for resource right-sizing (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine — Analyze usage, predict optimization impact, and recommend action paths (Inbound).",
              "Confluence / Data Lake — Archive utilization trends, savings outcomes, and monthly FinOps reports (Outbound)."
            ]
          },
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Utilization Data Collection & Baseline Establishment: Collect performance and cost telemetry across ADCs. (50%).",
              "Tier 2 — Underutilization & Idle Object Detection: Identify VIPs and licenses with low or zero activity. (75%).",
              "Tier 3 — AI Reasoning & Right-Sizing Recommendations: Correlate cost, usage, and SLA data to suggest optimizations. (90%).",
              "Tier 4 — Predictive FinOps Optimization & Continuous Learning: Learn traffic patterns and automate right-sizing cycles proactively. (100%)."
            ]
          }
        ]
      },
      {
        title: "3. Operations",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "License Utilization Efficiency ≥ 90% — % of licensed throughput actively used.",
              "Underutilized VIP Detection Accuracy ≥ 97% — Correctly identified idle VIPs.",
              "Mean Time to Detect (MTTD) Idle Objects < 4 hrs — Time from inactivity to detection.",
              "Mean Time to Recommend (MTTR) < 8 hrs — Time from detection to FinOps recommendation.",
              "Average Cost Reduction per Cycle ≥ 10% — Percent reduction in recurring LB cost post-optimization.",
              "Automation Adoption Rate ≥ 85% — % of cost optimizations executed autonomously."
            ]
          },
          {
            title: "Representative Statistics (30-Day Enterprise Run)",
            content: [
              "Load Balancer Instances Monitored: 162",
              "Total VIPs Evaluated: 8,940",
              "Idle/Underutilized VIPs Detected: 1,220 (13.6%)",
              "License Optimization Recommendations: 310",
              "Active License Downgrades Executed: 94",
              "Mean Cost Reduction per LB Tier: 11.8%",
              "Average MTTD: 3.1 hrs — Average MTTR: 7.2 hrs",
              "Underutilized Detection Accuracy: 97.4%",
              "Automation Adoption: 88%",
              "Estimated Monthly Savings: $186,000 USD",
              "Engineer Hours Saved: ≈ 340 per month",
              "Audit Reports Generated: 10 (CoreStack / Confluence)"
            ]
          },
          {
            title: "Service Level Agreements (SLA)",
            content: [
              "Telemetry Collection Interval ≤ 2 hrs — Utilization and traffic metrics refresh.",
              "Idle Object Detection Cycle ≤ 4 hrs — Continuous validation for VIPs and pools.",
              "Right-Sizing Recommendation Generation ≤ 8 hrs — Includes AI reasoning and cost correlation.",
              "Approval Workflow Execution (ITSM) ≤ 12 hrs — Policy-driven change control process.",
              "Evidence Publication ≤ 1 hr — FinOps savings and compliance report export.",
              "Agent Availability ≥ 99.9% — HA n8n cluster monitored via Grafana / Prometheus."
            ]
          },
          {
            title: "Technical Context",
            content: [
              "Execution Environment — RunOps n8n automation cluster (Azure) with containerized “LBCostGuardAI” microservice.",
              "Primary Interfaces — Load balancer APIs, FinOps systems, CoreStack compliance APIs.",
              "Baseline Repository — PostgreSQL (Memory Plane) storing utilization trends, license tiers, and cost benchmarks.",
              "Reasoning Model — Azure OpenAI GPT-4o fine-tuned on FinOps principles, ADC utilization logic, and RunOps cost optimization KB.",
              "Security Controls — TLS 1.3 encryption, signed API calls, RBAC controls, license change rollback logic.",
              "Outputs — LB Cost Optimization Report (JSON/PDF), Idle VIP Summary, License Efficiency Snapshot, Monthly Savings Report.",
              "Monitoring — Grafana dashboards tracking utilization rates, license consumption, idle object trends, and cost savings.",
              "Error Handling — Auto-retry on data collection failures; flag inconsistent billing data to FinOps for manual review."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Telemetry Collection Interval, Utilization Threshold Breach, or Scheduled Cost Optimization Cycle.",
      workingPlane: "RunOps n8n cluster with containerized 'LBCostGuardAI' microservice.",
      memory: "Persisted utilization trends, license tiers, and cost benchmarks in Postgres.",
      reasoning: "AI correlates VIP traffic with cost and SLA constraints to identify underutilized resources and generate safe right-sizing or decommissioning recommendations.",
      execution: "Generate FinOps optimization tickets (for approval) or execute autonomous actions (e.g., scale-down, VIP disablement) and log cost savings evidence."
    },
    kpis: [
      { label: "License Utilization Efficiency", value: "90", unit: "%", target: "≥ 90%", color: "#10b981" },
      { label: "Underutilized VIP Detection Accuracy", value: "97.4", unit: "%", target: "≥ 97%", color: "#10b981" },
      { label: "Mean Time to Detect (MTTD) Idle Objects", value: "3.1", unit: "hrs", target: "< 4 hrs", color: "#10b981" },
      { label: "Mean Time to Recommend (MTTR)", value: "7.2", unit: "hrs", target: "< 8 hrs", color: "#10b981" },
      { label: "Average Cost Reduction per Cycle", value: "11.8", unit: "%", target: "≥ 10%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "88", unit: "%", target: "≥ 85%", color: "#10b981" }
    ]
},

  'branch-zero-touch-agent': {
    title: "Branch Zero Touch Agent",
    description: "Automates branch site onboarding, network provisioning, and configuration deployment using standardized templates — enabling **zero-touch installation, consistent policy enforcement, and rapid scaling** of enterprise edge environments.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Rapid Branch Turn-Up: Provision and configure new sites within minutes instead of days.",
          "Consistent Policy Enforcement: Ensures every branch conforms to golden network, security, and segmentation templates.",
          "Reduced Deployment Risk: Eliminates manual errors and misconfigurations through automation.",
          "Operational Efficiency: Frees engineering resources from repetitive provisioning tasks.",
          "Zero-Trust Readiness: Automatically enforces segmentation, identity, and security policies at deployment."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Traditional branch provisioning requires manual intervention, CLI configuration, and site-specific adjustments, leading to inconsistent deployments, long lead times, and operational errors.",
              "SD-WAN and edge network expansion demands rapid, repeatable, and policy-compliant provisioning across hundreds or thousands of sites.",
              "Manual deployment lacks alignment with enterprise templates, resulting in configuration drift and compliance risk.",
              "Enterprises need an agentic, zero-touch provisioning (ZTP) engine that automates branch deployment end-to-end — from device onboarding and policy push to validation and service readiness."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "New sites are operational in minutes, drastically improving time-to-value (MTTP < 15 min).",
              "Configuration drift is prevented by enforcing golden network, security, and QoS templates.",
              "Deployment quality increases by eliminating human errors and misconfigurations.",
              "Engineering teams shift focus from repetitive provisioning to strategic architecture work.",
              "Zero-Trust policies are automatically woven into the network fabric upon deployment."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Zero-Touch Branch Provisioning: Automate device bootstrapping and configuration via templates and APIs.",
              "Network Template Enforcement: Apply pre-approved routing, QoS, and security configurations automatically.",
              "SD-WAN Onboarding: Integrate new sites into SD-WAN overlays and assign business intent policies.",
              "Compliance Validation: Verify configuration conformity against enterprise policy baselines.",
              "Lifecycle Updates & Redeployment: Automatically reapply or update templates as policies evolve."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Device Discovery & Enrollment: Detect new branch routers, switches, and firewalls via plug-and-play or DHCP-based onboarding.",
              "Template-Driven Configuration: Apply enterprise-approved configuration templates (network, security, QoS, segmentation).",
              "SD-WAN Policy Integration: Automatically register devices into overlay controllers and assign intent-based routing.",
              "Compliance & Validation Checks: Confirm routing, segmentation, and security compliance post-deployment.",
              "Rollback & Recovery Automation: Automatically revert to last-known-good configuration on failure.",
              "AI Reasoning Engine: Adjust deployment workflows dynamically based on hardware, bandwidth, or circuit availability.",
              "Audit & Reporting: Generate provisioning logs, compliance results, and operational summaries."
            ]
          },
          {
            title: "Integrations",
            content: [
              "SD-WAN Controllers (Cisco vManage, Fortinet, VMware, Aruba, Versa): Automate overlay and business policy registration (Bidirectional).",
              "Network Orchestration / NMS Platforms (Cisco DNA, Juniper Mist, NetBrain): Push and validate configuration templates (Bidirectional).",
              "Firewall & Security Controllers (Palo Alto Panorama, FortiManager): Apply and validate branch security and segmentation policies (Bidirectional).",
              "ServiceNow / Jira: Track provisioning tickets, approvals, and change workflows (Bidirectional).",
              "Cloud Connectivity APIs (AWS Direct Connect, Azure Virtual WAN, GCP Interconnect): Automate hybrid connectivity for new branches (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Evaluate branch context, adapt template parameters, and generate configuration insights (Inbound).",
              "Confluence / Data Lake: Archive deployment reports, validation logs, and compliance data (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Device Discovery & Enrollment: Detect and register new branch devices automatically (50 %).",
              "Tier 2 — Template Application & Policy Integration: Apply pre-approved network and SD-WAN templates (75 %).",
              "Tier 3 — AI Reasoning & Compliance Validation: Validate deployment success and resolve configuration variances (90 %).",
              "Tier 4 — Predictive Learning & Lifecycle Governance: Anticipate new deployments and pre-stage provisioning content (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Provisioning Success Rate ≥ 98 % (Successful configurations applied per branch).",
              "Mean Time to Provision (MTTP) < 15 min (Average time from device detection to operational readiness).",
              "Compliance Validation Accuracy ≥ 97 % (Policy compliance checks post-deployment).",
              "Rollback Success Rate ≥ 99 % (Reversion reliability for failed deployments).",
              "Automation Adoption Rate ≥ 85 % (% of provisioning tasks executed autonomously).",
              "Device Discovery to Provisioning Start ≤ 5 min (Auto-registration via DHCP or serial number)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'BranchZTAI' microservice.",
              "Primary Interfaces: SD-WAN controllers, NMS APIs, firewall/security managers, and cloud on-ramp APIs.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing branch inventory, templates, and validation logs.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on network provisioning, SD-WAN orchestration, and RunOps Zero-Trust KB.",
              "Security Controls: TLS 1.3 encryption, credential vaulting, RBAC enforcement, and signed config templates.",
              "Error Handling: Auto-retry failed pushes; trigger rollback and open ITSM ticket after two failed attempts.",
              "Outputs: Branch Provisioning Report (PDF/JSON), Compliance Validation Log, Template Application Summary."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Device Discovery to Provisioning Start Cycle (≤ 5 min) initiated by DHCP event or device serial number registration.",
      workingPlane: "Orchestrates multi-domain configuration push across SD-WAN, Firewall, and network devices, applying template logic adapted to branch context (AI Reasoning).",
      memory: "Stores approved network and security templates, historical MTTP performance data, and validation logs to govern deployment quality and speed.",
      reasoning: "AI adapts configuration parameters based on site-specific context (bandwidth, hardware, regional policy) and validates compliance (Compliance Validation Accuracy ≥ 97 %) before marking the site operational.",
      execution: "Pushes device configuration templates (MTTP < 15 min), registers the device with the SD-WAN controller, and executes automated rollback on failure (Rollback Success Rate ≥ 99 %)."
    },
    kpis: [
      { label: "Provisioning Success Rate", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Mean Time to Provision (MTTP)", value: "15", unit: "min", target: "< 15 min", color: "#2563eb" },
      { label: "Compliance Validation Accuracy", value: "97", unit: "%", target: "≥ 97%", color: "#f59e0b" },
      { label: "Operational Efficiency Improvement", value: "40", unit: "%", target: "≥ 40%", color: "#ef4444" }
    ]
  },
  
  'jml-automation-agent': {
    title: "IAM JML Automation Agent",
    description: "Automates user identity provisioning, modification, and deprovisioning workflows across Active Directory, Entra ID, HRIS, and SaaS platforms — ensuring timely access enablement, secure offboarding, and **continuous compliance within Zero-Trust frameworks**.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Faster Onboarding (Joiners): Automates user provisioning from HRIS to AD, Entra ID, and SaaS systems within minutes.",
          "Secure Access Updates (Movers): Automatically adjusts group memberships and entitlements based on role changes.",
          "Compliant Offboarding (Leavers): Revokes access, disables accounts, and archives user data immediately upon termination.",
          "Reduced Insider Risk: Eliminates dormant or orphaned accounts that could be exploited.",
          "Regulatory Compliance: Ensures auditable evidence for SOX, HIPAA, ISO 27001, and NIST controls."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Enterprises struggle with manual or inconsistent JML processes, leading to delayed onboarding, overprivileged accounts, and access lingering after departure.",
              "Manual ticketing between HR, IT, and Security teams causes operational friction and increases the risk of insider threats or compliance violations.",
              "Without unified automation, hybrid identity ecosystems (AD + Entra + SaaS) become fragmented, reducing visibility and governance effectiveness.",
              "Organizations require an autonomous JML agent that orchestrates workflows end-to-end across HRIS, identity directories, and applications — aligned with least privilege and Zero-Trust principles."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Automated user provisioning from HRIS to all required systems within minutes (MTTP < 10 min).",
              "Instantaneous, policy-driven adjustment of entitlements upon role or department changes.",
              "Secure, compliant, and immediate revocation of access upon employee termination (MTTR < 5 min).",
              "Mitigation of insider threat risks by eliminating dormant or orphaned accounts.",
              "Continuous generation of auditable evidence for regulatory controls (SOX, HIPAA, ISO 27001)."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Joiner Workflow: Automate provisioning of new hires from HRIS to AD/Entra and SaaS systems.",
              "Mover Workflow: Detect internal role or department changes and update access rights accordingly.",
              "Leaver Workflow: Instantly disable and deprovision departing employees or contractors.",
              "License Optimization: Automatically reclaim SaaS and directory licenses during offboarding.",
              "Audit & Compliance Reporting: Generate JML evidence logs and certification reports."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "HRIS Integration: Detect joiner/mover/leaver events from Workday, SAP SuccessFactors, or Oracle HCM.",
              "Directory Synchronization: Provision/deprovision users and groups in AD, Entra ID, and hybrid forests.",
              "SaaS Application Orchestration: Manage user accounts in Microsoft 365, Salesforce, ServiceNow, Okta, and more.",
              "License & Group Assignment: Dynamically allocate licenses and access groups based on HR attributes.",
              "Access Revocation & Data Archival: Disable accounts, transfer ownership, and purge credentials on offboarding.",
              "AI Reasoning Layer: Validate workflow integrity, detect anomalies, and ensure policy compliance.",
              "Compliance Logging: Maintain immutable JML audit trails for regulatory reporting."
            ]
          },
          {
            title: "Integrations",
            content: [
              "HRIS Platforms (Workday, SAP, Oracle, UKG): Detect joiner, mover, and leaver events (Inbound).",
              "Identity Directories (Active Directory, Entra ID): Provision users, update groups, and disable accounts (Bidirectional).",
              "SaaS Platforms (Microsoft 365, Salesforce, ServiceNow, Okta, Box, Zoom): Create, update, or deprovision user accounts and entitlements (Bidirectional).",
              "CoreStack GRC / Compliance Modules: Record audit logs, workflow history, and certification data (Bidirectional).",
              "ServiceNow / Jira: Manage exceptions, approvals, and workflow monitoring (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Validate workflows, detect anomalies, and optimize access policies (Inbound).",
              "Confluence / Data Lake: Archive JML logs, entitlement snapshots, and compliance reports (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Event Detection & Workflow Initialization: Identify joiner/mover/leaver triggers from HRIS (50 %).",
              "Tier 2 — Provisioning & Synchronization: Automate user creation or modification across directories and SaaS (75 %).",
              "Tier 3 — AI Reasoning & Policy Enforcement: Validate access changes, enforce Zero-Trust controls, and apply compliance logic (90 %).",
              "Tier 4 — Continuous Verification & Learning: Monitor success metrics, detect anomalies, and refine automation logic (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Provisioning Success Rate ≥ 99 % (% of JML workflows completed successfully).",
              "Mean Time to Provision (MTTP) < 10 min (Average time to onboard a new user from HRIS trigger).",
              "Deprovisioning Completion Accuracy ≥ 98 % (% of accounts fully disabled and access revoked).",
              "Access Drift Detection Rate ≥ 97 % (% of mismatched entitlements detected and corrected).",
              "Access Revocation (Leaver) ≤ 5 min (Disable and remove from all platforms).",
              "HR Event Detection Cycle ≤ 5 min (Near-real-time ingestion from HRIS feed)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'JML-AI' microservice.",
              "Primary Interfaces: HRIS APIs, AD PowerShell/Graph APIs, SaaS SCIM connectors.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing user lifecycle states, access templates, and audit logs.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on IAM workflows, Zero-Trust policies, and RunOps identity governance KB.",
              "Security Controls: TLS 1.3, encrypted credentials, MFA-based API calls, RBAC access segregation, immutable audit logging.",
              "Error Handling: Auto-retry failed API calls; trigger SOAR workflow if provisioning or offboarding validation fails twice.",
              "Outputs: JML Activity Report (PDF/JSON), Access Change Log, Entitlement Baseline, Compliance Audit Snapshot."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "HR Event Detection Cycle (≤ 5 min) from HRIS (Joiner/Mover/Leaver) trigger.",
      workingPlane: "Orchestrates API calls across directories (AD/Entra), SaaS apps, and identity providers using dynamic role-based access (RBAC) templates.",
      memory: "Persists user lifecycle states, access policy templates, entitlement baselines, and offboarding validation records to ensure continuous Zero-Trust governance.",
      reasoning: "AI validates the requested access against RBAC policy (Joiners/Movers) and ensures complete, secure revocation across all systems (Leavers), confirming Deprovisioning Completion Accuracy (≥ 98 %).",
      execution: "Executes user provisioning/modification (MTTP < 10 min) or instant access revocation (Access Revocation ≤ 5 min) and generates compliance evidence for GRC systems."
    },
    kpis: [
      { label: "Provisioning Success Rate", value: "99", unit: "%", target: "≥ 99%", color: "#10b981" },
      { label: "Mean Time to Provision (MTTP)", value: "10", unit: "min", target: "< 10 min", color: "#2563eb" },
      { label: "Deprovisioning Completion Accuracy", value: "98", unit: "%", target: "≥ 98%", color: "#f59e0b" },
      { label: "Automation Adoption Rate", value: "90", unit: "%", target: "≥ 90%", color: "#ef4444" }
    ]
  },
  
  'mfa-enrollment-agent': {
    title: "IAM MFA Enrollment Agent",
    description: "Automates user MFA onboarding and enforcement across identity directories and SaaS platforms, while enabling secure self-service recovery and backup methods — ensuring **compliance, resilience, and usability** within Zero-Trust access frameworks.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Universal MFA Coverage: Ensures 100% user enrollment across AD, Entra ID, and SaaS applications.",
          "Reduced Helpdesk Overhead: Enables self-service MFA enrollment, reset, and recovery workflows.",
          "Improved Security Compliance: Enforces MFA adherence for all workforce, partner, and privileged accounts.",
          "User Experience Optimization: Streamlines setup through guided enrollment and backup options.",
          "Zero-Trust Enforcement: Strengthens identity assurance and device trust posture."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Organizations face inconsistent MFA adoption across identity providers and applications, creating gaps in Zero-Trust posture and compliance coverage.",
              "Manual enrollment processes slow onboarding and frustrate users, leading to low adoption and increased helpdesk load for resets or lost devices.",
              "Inadequate recovery options expose enterprises to lockout risks and support overhead.",
              "Enterprises need an autonomous MFA onboarding and enforcement system that ensures all identities are enrolled, verified, and equipped with secure recovery methods — while maintaining regulatory compliance and user experience balance."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Guaranteed enrollment and enforcement of MFA for all users across all identity providers (MFA Enrollment Coverage ≥ 99 %).",
              "Enabling self-service workflows significantly reduces IT helpdesk load for MFA enrollment and resets.",
              "Mandatory MFA for all account types reinforces Zero-Trust identity assurance and security compliance.",
              "Streamlined enrollment and self-service recovery improves the overall user experience and adoption rate.",
              "Strengthened identity assurance (MFA) forms a critical layer of Zero-Trust enforcement."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Automated MFA Enrollment: Auto-enroll new users in MFA during joiner workflows.",
              "Policy-Based Enforcement: Enforce MFA across AD, Entra ID, Okta, and SaaS platforms per compliance rules.",
              "Self-Service Recovery: Enable users to register backup MFA methods and recover securely without admin assistance.",
              "Privileged Access MFA Enforcement: Apply strict MFA requirements for admin and service accounts.",
              "Audit & Compliance Reporting: Track MFA adoption and enrollment metrics for SOC 2, ISO, and NIST compliance."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "MFA Enrollment Automation: Auto-trigger MFA setup during onboarding workflows from HRIS or identity events.",
              "Cross-Directory Enforcement: Apply MFA requirements in AD (Windows Hello, smartcard), Entra ID (Authenticator, FIDO2), and Okta.",
              "Backup Method Registration: Require users to register alternative MFA (SMS, phone, FIDO2 token, recovery email).",
              "Self-Service Recovery Workflow: Enable users to reset MFA securely through identity proofing or manager verification.",
              "Privileged Account Enforcement: Apply mandatory MFA to all elevated and service accounts.",
              "AI-Driven Compliance Validation: Continuously audit MFA enrollment status and prompt remediation for missing methods.",
              "Reporting & Audit Evidence: Generate compliance reports on enrollment rates, failures, and exceptions."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Identity Providers (AD, Entra ID, Okta, Ping): Enforce MFA policies and track enrollment status (Bidirectional).",
              "HRIS Systems (Workday, SAP, Oracle): Trigger MFA enrollment for new joiners (Inbound).",
              "SaaS Platforms (M365, Salesforce, ServiceNow, Zoom): Apply MFA settings and monitor user compliance (Bidirectional).",
              "CoreStack GRC / Compliance Modules: Store MFA enrollment logs and audit evidence (Bidirectional).",
              "ServiceNow / Jira: Manage exceptions, approval workflows, or recovery tickets (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Analyze MFA enrollment gaps and recommend enforcement actions (Inbound).",
              "Confluence / Data Lake: Archive compliance metrics, reports, and exception logs (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Enrollment & Policy Detection: Identify unenrolled users and initiate MFA enrollment (50 %).",
              "Tier 2 — Cross-Platform Enforcement & Validation: Synchronize MFA enforcement across identity systems (75 %).",
              "Tier 3 — AI Reasoning & Recovery Enablement: Recommend corrective actions and enable secure self-service recovery (90 %).",
              "Tier 4 — Predictive Compliance & Continuous Learning: Anticipate enrollment failures and optimize MFA adoption strategies (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "MFA Enrollment Coverage ≥ 99 % (% of active users successfully enrolled).",
              "Mean Time to Enroll (MTTE) < 5 min (Average time to complete MFA registration post-trigger).",
              "Self-Service Recovery Success Rate ≥ 95 % (% of recovery requests completed without IT intervention).",
              "Privileged Account Enforcement Rate 100 % (% of admin/service accounts with MFA enabled).",
              "Automation Adoption Rate ≥ 90 % (% of MFA tasks executed autonomously).",
              "MFA Enrollment Trigger Post-Joiner Event ≤ 5 min (Enrollment begins immediately upon HRIS identity creation)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'MFAEnroll-AI' microservice.",
              "Primary Interfaces: Entra ID Graph API, AD PowerShell, Okta API, HRIS connectors, SCIM provisioning endpoints.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing enrollment baselines, recovery tokens, and compliance state.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on IAM policy orchestration, Zero-Trust MFA strategies, and RunOps compliance KB.",
              "Security Controls: TLS 1.3, encrypted credential vault, MFA-based API calls, RBAC access segregation, immutable audit logging.",
              "Error Handling: Auto-retry failed enrollment API calls; trigger ITSM ticket after two consecutive failures.",
              "Outputs: MFA Enrollment Report (PDF/JSON), Recovery Audit Log, Coverage Compliance Summary, Exception Register."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "MFA Enrollment Trigger Post-Joiner Event (≤ 5 min) or a periodic audit detecting unenrolled/non-compliant users.",
      workingPlane: "Orchestrates API calls to Identity Providers (Entra ID, Okta) to initiate MFA enrollment and enforce policy settings across directories and SaaS applications.",
      memory: "Persists MFA Enrollment Coverage baselines, user-registered backup methods, and compliance policy settings to ensure resilience and full coverage.",
      reasoning: "AI diagnoses enrollment gaps, prioritizes mandatory MFA application (e.g., Privileged Accounts 100 %), and orchestrates the user-guided self-service recovery setup to ensure Self-Service Recovery Success Rate (≥ 95 %).",
      execution: "Triggers enrollment workflows (MTTE < 5 min), enforces MFA policy across platforms, and logs detailed compliance and recovery audit trails."
    },
    kpis: [
      { label: "MFA Enrollment Coverage", value: "99", unit: "%", target: "≥ 99%", color: "#10b981" },
      { label: "Mean Time to Enroll (MTTE)", value: "5", unit: "min", target: "< 5 min", color: "#2563eb" },
      { label: "Self-Service Recovery Success Rate", value: "95", unit: "%", target: "≥ 95%", color: "#f59e0b" },
      { label: "Privileged Account Enforcement Rate", value: "100", unit: "%", target: "100%", color: "#ef4444" }
    ]
  },
  
  'step-up-auth-agent': {
    title: "IAM Step-Up Auth Agent",
    description: "Dynamically evaluates user context, device posture, location, and behavioral signals to **trigger step-up MFA challenges** during authentication or session activities — ensuring **adaptive, risk-driven enforcement** aligned with Zero-Trust access principles.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Adaptive Authentication Enforcement: Triggers additional MFA only when risk signals or anomalies justify it.",
          "Improved Zero-Trust Posture: Verifies every session context — user, device, location, and behavior.",
          "Reduced User Fatigue: Minimizes unnecessary MFA prompts by applying contextual intelligence.",
          "Faster Risk Containment: Immediately escalates authentication rigor during active threats or anomalies.",
          "Regulatory Compliance: Aligns with NIST 800-63B, PCI-DSS, and Zero-Trust maturity frameworks."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Static MFA enforcement can burden users unnecessarily while still failing to detect contextual risks such as compromised credentials, unknown devices, or suspicious geographies.",
              "Enterprises lack an integrated, dynamic way to apply additional authentication controls based on risk signals and posture deviations across multiple identity systems.",
              "Traditional IAM policies don’t respond in real time to threat intelligence, device compliance, or location anomalies.",
              "Organizations need an agentic, adaptive step-up authentication engine that continuously evaluates risk and triggers MFA only when warranted — balancing security and usability."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "MFA is applied intelligently based on real-time risk signals, preventing unnecessary prompts.",
              "Continuous verification of user identity, device health, and network location strengthens Zero-Trust.",
              "Contextual intelligence minimizes redundant MFA prompts, enhancing user experience.",
              "Authentication rigor is instantly heightened upon detecting anomalous behavior or threats.",
              "Compliance is maintained with advanced security frameworks requiring risk-based authentication."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Conditional Step-Up MFA: Apply additional authentication when high-risk behavior or device changes occur.",
              "Geo-Location & Impossible Travel Detection: Trigger step-up verification for logins from new or disallowed countries.",
              "Device Posture Enforcement: Require MFA for devices missing EDR/MDM compliance or encryption.",
              "Privileged Session Re-Authentication: Enforce MFA when elevated privileges or sensitive applications are accessed.",
              "Session Risk Monitoring: Dynamically prompt step-up during active sessions if risk scores increase."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Risk-Based Access Evaluation: Aggregate telemetry from identity providers, devices, and SIEM tools to assess session risk.",
              "Adaptive MFA Enforcement: Trigger additional MFA challenges via Entra ID, Okta, or Ping APIs when anomalies arise.",
              "Geo & Network Context Analysis: Identify new locations, suspicious IPs, or VPN bypass attempts.",
              "Device Posture Verification: Integrate with MDM/EDR tools to validate OS version, encryption, and compliance.",
              "Privileged Access Control: Enforce MFA on high-privilege group memberships or admin session elevation.",
              "AI Reasoning Layer: Correlate multiple factors (location, device, behavior, role) to calculate dynamic risk scores.",
              "Session Monitoring & Step-Up Revalidation: Maintain ongoing posture and trigger MFA mid-session if risk state changes.",
              "Audit & Compliance Logging: Record MFA triggers, reasons, and user responses for GRC visibility."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Identity Providers (Entra ID, Okta, Ping, Duo): Execute adaptive MFA challenges and capture enrollment data (Bidirectional).",
              "EDR/MDM Platforms (Defender for Endpoint, CrowdStrike, Intune, Jamf): Evaluate device compliance and risk posture (Inbound).",
              "SIEM / SOAR Systems (Sentinel, Splunk, Cortex XSOAR): Correlate threat indicators and trigger adaptive responses (Bidirectional).",
              "Geo-IP & Threat Intelligence Feeds (Microsoft TI, Recorded Future, GreyNoise): Validate login locations and detect known bad IPs (Inbound).",
              "CoreStack GRC / Compliance Modules: Record MFA trigger history, enforcement logs, and policy adherence reports (Bidirectional).",
              "ServiceNow / Jira: Manage exceptions, alert investigations, and policy adjustments (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Analyze contextual risk data, determine if step-up is required, and optimize thresholds (Inbound).",
              "Confluence / Data Lake: Archive session logs, risk decisions, and MFA activity reports (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Risk Signal Collection & Baseline Mapping: Aggregate user, device, and geo telemetry for behavior baselining (50 %).",
              "Tier 2 — Risk Scoring & Deviation Detection: Detect anomalous activities or high-risk signals (75 %).",
              "Tier 3 — AI Reasoning & Step-Up Enforcement: Determine escalation necessity and trigger MFA via identity provider APIs (90 %).",
              "Tier 4 — Continuous Risk Learning & Predictive Enforcement: Adapt thresholds and trigger policies based on trend analysis and feedback (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "Adaptive MFA Trigger Accuracy ≥ 98 % (Correctly triggered MFA for high-risk sessions).",
              "False Positive Rate (Unnecessary Prompts) ≤ 3 % (MFA challenges issued unnecessarily).",
              "Mean Time to Trigger (MTTT) < 10 sec (Delay between anomaly detection and MFA prompt).",
              "Privileged Session Enforcement Rate 100 % (MFA enforced for all elevated privileges).",
              "Automation Adoption Rate ≥ 90 % (% of adaptive enforcement handled autonomously).",
              "Risk Signal Refresh Interval ≤ 60 sec (Continuous ingestion from EDR, SIEM, and identity providers)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'StepUpAuth-AI' microservice.",
              "Primary Interfaces: Entra ID Conditional Access APIs, Okta Risk Engine, MDM/EDR APIs, SIEM integrations.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing behavioral baselines, MFA trigger logs, and policy thresholds.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on adaptive authentication logic, Zero-Trust risk evaluation, and RunOps security KB.",
              "Security Controls: TLS 1.3, encrypted tokens, RBAC-per-role enforcement, policy rollback for false triggers.",
              "Error Handling: Auto-retry failed API calls; suppress duplicate MFA triggers for same session; escalate repeated anomalies to SOC.",
              "Outputs: Step-Up Authentication Log (JSON/PDF), Risk Analysis Summary, Privileged Access Enforcement Report, Compliance Snapshot."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Real-Time Risk Signal Refresh Interval (≤ 60 sec) detecting anomalies in user behavior, device posture, or geo-location.",
      workingPlane: "Aggregates and correlates context (User/Device/Geo/Behavior) from multiple security platforms to calculate a dynamic risk score for the current session.",
      memory: "Persists behavioral baselines and historical risk scores to distinguish normal activity from high-risk anomalies requiring intervention.",
      reasoning: "AI determines if the dynamic risk score exceeds policy thresholds, confirming the need for step-up MFA (Adaptive MFA Trigger Accuracy ≥ 98 %), and selects the appropriate challenge.",
      execution: "Triggers dynamic MFA challenge immediately via Identity Provider APIs (MTTT < 10 sec) to re-verify identity and enforces mandatory MFA for Privileged Session Elevation (100 %).",
    },
    kpis: [
      { label: "Adaptive MFA Trigger Accuracy", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
      { label: "Mean Time to Trigger (MTTT)", value: "10", unit: "sec", target: "< 10 sec", color: "#2563eb" },
      { label: "False Positive Rate (Unnecessary Prompts)", value: "3", unit: "%", target: "≤ 3%", color: "#f59e0b" },
      { label: "Privileged Session Enforcement Rate", value: "100", unit: "%", target: "100%", color: "#ef4444" }
    ]
  },
  
  'privileged-access-broker-agent': {
    title: "IAM Privileged Access Broker Agent",
    description: "Provides **just-in-time (JIT) access brokering** for privileged accounts across on-prem, cloud, and SaaS systems, issuing time-bound credentials, enforcing MFA, and automatically expiring access after approved durations — ensuring Zero-Trust-aligned privilege governance.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "Zero Standing Privileges (ZSP): Eliminates persistent admin rights and enforces JIT privilege elevation.",
          "Operational Efficiency: Automates privilege provisioning and expiry workflows.",
          "Improved Compliance: Aligns with ISO 27001, SOX, NIST 800-53, and CIS Control 6.",
          "Reduced Insider Risk: Minimizes exposure from dormant or overprivileged accounts.",
          "Audit-Ready Governance: Provides end-to-end visibility into privileged access events."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Permanent administrative rights and shared privileged accounts create significant security and compliance risks across hybrid infrastructures.",
              "Manual privileged access requests lead to delays, privilege creep, and audit complexity.",
              "Static privilege models violate Zero-Trust principles by overexposing credentials and extending attack surfaces.",
              "Organizations need an autonomous, policy-driven broker that provisions privileged access only when needed, validates approvals, enforces MFA, and revokes access automatically — maintaining a least-privilege, time-bound access model."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "Elimination of standing admin rights by enforcing JIT privilege elevation.",
              "Automation of privilege requests, granting (MTTA < 2 min), and revocation workflows, boosting efficiency.",
              "Adherence to major regulatory and security frameworks requiring time-bound access control.",
              "Reduced risk exposure by minimizing the duration and scope of elevated access.",
              "Immutable, end-to-end logging of every privileged access session for audit and forensics."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Just-in-Time Access Provisioning: Grant elevated access only when requested and approved.",
              "Time-Bound Privilege Control: Automatically revoke elevated privileges at expiry.",
              "MFA-Verified Access Elevation: Enforce multi-factor authentication before granting admin rights.",
              "Privileged Session Monitoring: Record access sessions for compliance and forensics.",
              "Audit & Evidence Generation: Generate logs for every privileged access request and revocation."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Just-in-Time Access Requests: Automate privileged access provisioning from ITSM, CLI, or portal triggers.",
              "Approval & Policy Validation: Validate requestor identity, justification, and risk score via predefined workflows.",
              "Credential Brokering: Generate short-lived admin credentials or tokens using API calls or vault integration.",
              "Time-Bound Enforcement: Revoke access automatically after the approved duration or session termination.",
              "MFA Integration: Require strong authentication prior to privilege elevation.",
              "Privileged Session Logging: Capture commands, timestamps, and activity for forensic replay.",
              "AI Reasoning Engine: Evaluate context (user, asset, justification, risk) to dynamically approve or deny access.",
              "Compliance & Audit Logging: Maintain immutable event records for all JIT access activities."
            ]
          },
          {
            title: "Integrations",
            content: [
              "Identity Providers (Entra ID, AD, Okta, Ping): Validate user identities and enforce directory-based privilege elevation (Bidirectional).",
              "Privileged Access Vaults (CyberArk, BeyondTrust, Delinea): Generate ephemeral credentials and manage secret lifecycles (Bidirectional).",
              "Cloud Providers (AWS IAM, Azure PIM, GCP IAM): Automate role assignments and time-bound admin access (Bidirectional).",
              "HRIS / ITSM (Workday, ServiceNow, Jira): Initiate JIT access workflows and manage approvals (Bidirectional).",
              "SIEM / SOAR (Sentinel, Splunk, Cortex XSOAR): Correlate access events with threat intelligence or risk scoring (Bidirectional).",
              "Azure OpenAI / RunOps Reasoning Engine: Evaluate access context, determine justification validity, and adapt privilege durations (Inbound).",
              "Confluence / Data Lake: Archive access logs, session evidence, and compliance reports (Outbound)."
            ]
          }
        ]
      },
      {
        title: "3. Agentic Tiers & Operations",
        subSections: [
          {
            title: "Agentic Tiers",
            content: [
              "Tier 1 — Privilege Request & Validation: Detect access requests, validate identity, and map to roles (50 %).",
              "Tier 2 — Access Granting & Policy Enforcement: Provision just-in-time privileges and enforce time-bound controls (75 %).",
              "Tier 3 — AI Reasoning & Risk-Aware Decisions: Evaluate context, justification, and risk to refine access grants (90 %).",
              "Tier 4 — Continuous Learning & Adaptive Privilege Governance: Learn usage patterns and adjust privilege rules dynamically (100 % - Fully Agentic)."
            ]
          },
          {
            title: "Operational SLAs & KPIs",
            content: [
              "JIT Provisioning Success Rate ≥ 99 % (% of JIT access requests fulfilled successfully).",
              "Mean Time to Approve (MTTA) < 2 min (Average time to evaluate and grant JIT access).",
              "Automatic Access Expiry Compliance 100 % (% of JIT privileges revoked upon expiration).",
              "Privileged Account Coverage ≥ 98 % (% of administrative accounts protected by JIT enforcement).",
              "Access Provisioning Execution ≤ 2 min (Role or token generation post-approval).",
              "Privilege Request Validation ≤ 1 min (Authentication and justification verification)."
            ]
          }
        ]
      },
      {
        title: "4. Technical Context & Summary",
        subSections: [
          {
            content: [
              "Execution Environment: RunOps n8n automation cluster (Azure) with containerized 'PrivAccessBroker-AI' microservice.",
              "Primary Interfaces: Entra ID PIM API, AWS IAM API, CyberArk REST API, AD PowerShell, and ITSM connectors.",
              "Baseline Repository: PostgreSQL (Memory Plane) storing active sessions, request logs, and privilege templates.",
              "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on Zero-Trust access control, PIM governance, and RunOps security KB.",
              "Security Controls: TLS 1.3, ephemeral tokenization, MFA enforcement, RBAC approval chains, immutable log retention.",
              "Error Handling: Auto-revoke failed or idle sessions; escalate anomalies (failed revocation or misuse) to SOAR/SOC.",
              "Outputs: Privileged Access Request Log (PDF/JSON), Access Approval Record, Session Evidence Summary, Compliance Audit Snapshot."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Just-in-Time Access Request initiated via ITSM/Portal, requiring Privilege Request Validation (≤ 1 min).",
      workingPlane: "Orchestrates multi-system credential brokering, generating time-bound access (tokens/roles) via Vault/PIM APIs and enforcing MFA prior to elevation.",
      memory: "Persists privilege usage logs, request justification history, active session data, and time-bound policies to ensure **Zero Standing Privileges (ZSP)**.",
      reasoning: "AI evaluates the request context, confirms justification validity, and ensures the granted privilege scope and duration adhere strictly to the least-privilege Zero-Trust policy.",
      execution: "Executes Access Provisioning Execution (≤ 2 min), enforces MFA, and automatically revokes access (Automatic Expiry Enforcement ≤ 5 min) upon session expiry or idle timeout (100 % compliance).",
    },
    kpis: [
      { label: "JIT Provisioning Success Rate", value: "99", unit: "%", target: "≥ 99%", color: "#10b981" },
      { label: "Mean Time to Approve (MTTA)", value: "2", unit: "min", target: "< 2 min", color: "#2563eb" },
      { label: "Automatic Access Expiry Compliance", value: "100", unit: "%", target: "100%", color: "#f59e0b" },
      { label: "Automation Adoption Rate", value: "90", unit: "%", target: "≥ 90%", color: "#ef4444" }
    ]
  },
  'rbac-policy-builder-agent': {
  title: "IAM RBAC Policy Builder Agent",
  description: "Continuously analyzes access behaviors, application entitlements, and role assignments to automatically propose least-privilege RBAC policies — ensuring consistent, compliant, and risk-optimized access governance aligned with Zero-Trust principles.",
  // No video provided
  details: [
    {
      title: "Business Value & Outcomes",
      points: [
        "Reduced Privilege Sprawl: Eliminates excessive or unused permissions across directories and applications.",
        "Accelerated RBAC Maturity: Automates policy design and validation based on real-world activity.",
        "Improved Compliance & Audit Readiness: Demonstrates least-privilege enforcement for ISO 27001, SOX, and NIST 800-53.",
        "Operational Efficiency: Reduces IAM administrative overhead and accelerates access certification.",
        "Zero-Trust Alignment: Builds adaptive access roles that enforce the principle of least privilege (PoLP)."
      ]
    }
  ],
  sections: [
    {
      title: "1. Business Context",
      subSections: [
        {
          title: "Problem Statement",
          content: [
            "Enterprises suffer from privilege sprawl, where users accumulate excessive access due to poor visibility, inherited permissions, or lack of revocation.",
            "Manual RBAC design and role rationalization are time-intensive, error-prone, and typically reactive.",
            "Without intelligent analytics, enterprises either over-permission users or stifle productivity by enforcing rigid controls.",
            "Organizations need an autonomous, AI-driven RBAC policy builder that continuously analyzes real usage, identifies excessive entitlements, and proposes least-privilege roles that balance security and productivity."
          ]
        },
        {
          title: "Business Outcomes (Benefits)",
          content: [
            "Eliminates excessive or unused permissions across directories and applications.",
            "Automates policy design and validation based on real-world activity.",
            "Demonstrates least-privilege enforcement for ISO 27001, SOX, and NIST 800-53.",
            "Reduces IAM administrative overhead and accelerates access certification.",
            "Builds adaptive access roles that enforce the principle of least privilege (PoLP)."
          ]
        },
        {
          title: "Use Case Alignment",
          content: [
            "Role Discovery & Modeling: Identify common access patterns and propose role templates.",
            "Least-Privilege Optimization: Detect unused or excessive entitlements and suggest removals.",
            "Access Certification Support: Provide evidence-based recommendations for periodic certification campaigns.",
            "Cross-System RBAC Alignment: Normalize roles across AD, Entra ID, AWS IAM, and SaaS platforms.",
            "Audit Reporting: Generate RBAC maturity scorecards and compliance-ready documentation."
          ]
        }
      ]
    },
    {
      title: "2. Technical Functionality",
      subSections: [
        {
          title: "Scope of Capabilities",
          content: [
            "Access Pattern Analysis: Continuously analyze login, entitlement, and privilege data from IAM and app sources.",
            "Role Discovery & Clustering: Group users by access similarity and propose role structures.",
            "Least-Privilege Calculation: Identify unused or infrequently used permissions per user.",
            "Policy Recommendation Engine: Generate proposed RBAC templates mapped to real business functions.",
            "AI Reasoning Layer: Validate recommendations for compliance and role conflicts.",
            "Policy Simulation & Validation: Test proposed roles in sandbox environments before enforcement.",
            "Audit & Reporting: Produce RBAC optimization reports and least-privilege verification evidence."
          ]
        },
        {
          title: "Integrations",
          content: [
            "Identity Providers (AD, Entra ID, Okta, Ping): Retrieve entitlement and group membership data (Inbound).",
            "Cloud IAM Systems (AWS IAM, Azure RBAC, GCP IAM): Analyze cloud permissions and access patterns (Inbound).",
            "SaaS Platforms (M365, Salesforce, ServiceNow, GitHub): Evaluate application-level roles and API scopes (Bidirectional).",
            "HRIS Systems (Workday, SAP): Align RBAC with job functions, departments, and hierarchy (Inbound).",
            "CoreStack GRC / Compliance Modules: Record RBAC proposals, reviews, and audit evidence (Bidirectional).",
            "ServiceNow / Jira: Manage approval workflows for RBAC changes (Bidirectional).",
            "Azure OpenAI / Reasoning Engine: Analyze role relationships, propose least-privilege models, and identify anomalies (Inbound).",
            "Confluence / Data Lake: Archive RBAC reports, templates, and change history (Outbound)."
          ]
        }
      ]
    },
    {
      title: "3. Agentic Tiers & Operations",
      subSections: [
        {
          title: "Agentic Tiers",
          content: [
            "Tier 1 — Entitlement & Access Data Collection: Collect and normalize access patterns from all identity systems (50 % Automation Depth).",
            "Tier 2 — Role Discovery & Behavioral Analysis: Cluster users by usage patterns and identify least-privilege sets (75 % Automation Depth).",
            "Tier 3 — AI Reasoning & RBAC Template Generation: Generate policy recommendations with justification and impact simulation (90 % Automation Depth).",
            "Tier 4 — Continuous Policy Learning & Optimization: Refine RBAC policies using user feedback and evolving access trends (100 % (Fully Agentic) Automation Depth)."
          ]
        },
        {
          title: "Operational SLAs & KPIs",
          content: [
            "Privilege Reduction Rate ≥ 30 %: % reduction in excessive or unused permissions.",
            "Policy Recommendation Accuracy ≥ 95 %: % of AI-proposed policies approved without major revisions.",
            "Mean Time to Propose (MTTP) < 1 hr: Time from access data ingestion to RBAC policy proposal.",
            "Policy Adoption Rate ≥ 85 %: % of generated RBAC templates deployed to production.",
            "Agent Availability ≥ 99.9 %: Monitored via Grafana / Prometheus dashboards."
          ]
        }
      ]
    },
    {
      title: "4. Technical Context & Summary",
      subSections: [
        {
          content: [
            "Execution Environment: RunOps n8n automation cluster (Azure) with containerized “RBACPolicyBuilder-AI” microservice.",
            "Primary Interfaces: Entra ID Graph API, AWS IAM API, Okta API, HRIS, and SaaS entitlement APIs.",
            "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on IAM analytics, access clustering, and least-privilege RBAC modeling.",
            "Outputs: RBAC Role Proposal Report (PDF/JSON), Privilege Reduction Summary, Policy Impact Simulation Log, Compliance Audit Report.",
            "Summary: The agent functions as the autonomous role architect within the Zero-Trust Identity fabric, transforming role management into a self-optimizing, data-driven, and Zero-Trust-enforcing governance function."
          ]
        }
      ]
    }
  ],
  agenticModel: {
    trigger: "Continuous analysis of login, entitlement, and privilege data from IAM and app sources.",
    workingPlane: "Orchestrates API calls to Identity Providers, Cloud IAM, SaaS Platforms, and HRIS systems; stores baselines in PostgreSQL.",
    memory: "Persists role definitions, usage patterns, and entitlement baselines to inform future optimization.",
    reasoning: "AI Reasoning Layer validates recommendations for compliance and role conflicts; uses GPT-4o fine-tuned for least-privilege RBAC modeling.",
    execution: "Generates proposed RBAC templates, runs policy simulations, creates ServiceNow/Jira approval workflows, and archives audit reports."
  },
  kpis: [
    { label: "Privilege Reduction Rate", value: "30", unit: "%", target: "≥ 30%", color: "#10b981" },
    { label: "Policy Recommendation Accuracy", value: "95", unit: "%", target: "≥ 95%", color: "#2563eb" },
    { label: "Mean Time to Propose (MTTP)", value: "1", unit: "hr", target: "< 1 hr", color: "#f59e0b" },
    { label: "Policy Adoption Rate", value: "85", unit: "%", target: "≥ 85%", color: "#ef4444" }
  ]
},
'entitlement-cleanup-agent': {
  title: "IAM Entitlement Cleanup Agent",
  description: "Continuously detects unused, excessive, or orphaned entitlements across Active Directory, Entra ID, IAM systems, and SaaS platforms, automatically recommending or revoking access to enforce least privilege and reduce attack surface under Zero-Trust principles.",
  // No video provided
  details: [
    {
      title: "Business Value & Outcomes",
      points: [
        "Reduced Privilege Creep: Continuously eliminates unused and risky entitlements.",
        "Improved Compliance Posture: Aligns access hygiene with SOX, NIST 800-53, and ISO 27001 controls.",
        "Operational Efficiency: Automates entitlement discovery and revocation workflows.",
        "Lower Insider Threat Exposure: Removes dormant or excessive permissions proactively.",
        "Audit & Reporting Readiness: Provides clear evidence of least-privilege maintenance."
      ]
    }
  ],
  sections: [
    {
      title: "1. Business Context",
      subSections: [
        {
          title: "Problem Statement",
          content: [
            "Over time, user accounts accumulate unused permissions from role changes, temporary projects, and group inheritance, leading to privilege creep and audit failures.",
            "Manual entitlement reviews are slow, inconsistent, and non-scalable, allowing stale access to persist for months.",
            "Excessive privileges violate least-privilege and Zero-Trust models, exposing organizations to insider threats and compliance risk.",
            "Enterprises need an autonomous entitlement hygiene agent that continuously detects unused or excessive access, correlates findings with policy rules, and revokes them safely and automatically."
          ]
        },
        {
          title: "Business Outcomes (Benefits)",
          content: [
            "Continuously eliminates unused and risky entitlements.",
            "Aligns access hygiene with SOX, NIST 800-53, and ISO 27001 controls.",
            "Automates entitlement discovery and revocation workflows.",
            "Removes dormant or excessive permissions proactively.",
            "Provides clear evidence of least-privilege maintenance."
          ]
        },
        {
          title: "Use Case Alignment",
          content: [
            "Unused Entitlement Detection: Identify permissions not used for a defined period (e.g., 90 days).",
            "Excessive Access Revocation: Detect entitlements beyond the user’s role or scope of responsibility.",
            "Orphaned Access Cleanup: Remove access from disabled or inactive accounts.",
            "Privileged Role Validation: Detect and remove unnecessary admin or elevated privileges.",
            "Audit & Compliance Evidence: Generate entitlement hygiene reports and revocation logs."
          ]
        }
      ]
    },
    {
      title: "2. Technical Functionality",
      subSections: [
        {
          title: "Scope of Capabilities",
          content: [
            "Usage-Based Entitlement Tracking: Analyze activity logs, group membership, and access patterns.",
            "Threshold-Driven Cleanup: Detect and flag entitlements unused for X days or exceeding RBAC scope.",
            "Policy-Based Revocation: Automatically remove or quarantine identified entitlements.",
            "Privileged Access Validation: Identify and downgrade unnecessary admin roles.",
            "AI-Reasoning Layer: Correlate entitlement usage, context, and policy to prioritize cleanup.",
            "Approval & Exception Handling: Route critical revocations through ITSM or manager approval workflows.",
            "Compliance Logging: Record all revoked entitlements with justification for audit review."
          ]
        },
        {
          title: "Integrations",
          content: [
            "Identity Providers (AD, Entra ID, Okta, Ping): Retrieve user/group entitlements and revoke unused permissions (Bidirectional).",
            "Cloud IAM Platforms (AWS IAM, Azure RBAC, GCP IAM): Detect and clean up unused or over-scoped policies (Bidirectional).",
            "SaaS Applications (M365, Salesforce, ServiceNow, GitHub): Identify inactive or excessive app permissions (Bidirectional).",
            "HRIS Systems (Workday, SAP): Correlate access to employment and department status (Inbound).",
            "CoreStack GRC / Compliance Modules: Record entitlement revocation logs and compliance mapping (Bidirectional).",
            "ServiceNow / Jira: Manage approvals and remediation workflows (Bidirectional).",
            "SIEM / SOAR Systems (Sentinel, Splunk, Cortex XSOAR): Correlate entitlement abuse alerts and cleanup events (Bidirectional).",
            "Azure OpenAI / Reasoning Engine: Analyze entitlement usage, determine revocation confidence, and generate recommendations (Inbound).",
            "Confluence / Data Lake: Archive cleanup reports, revocation logs, and audit evidence (Outbound)."
          ]
        }
      ]
    },
    {
      title: "3. Agentic Tiers & Operations",
      subSections: [
        {
          title: "Agentic Tiers",
          content: [
            "Tier 1 — Entitlement Discovery & Usage Baseline: Collect entitlement data and build usage baselines across systems (50 % Automation Depth).",
            "Tier 2 — Unused/Excessive Access Detection: Identify unused or high-risk entitlements via analytics (75 % Automation Depth).",
            "Tier 3 — AI Reasoning & Policy-Based Revocation: Execute revocation or quarantine based on risk and policy confidence (90 % Automation Depth).",
            "Tier 4 — Continuous Learning & Predictive Cleanup: Predict privilege accumulation trends and preempt entitlement bloat (100 % (Fully Agentic) Automation Depth)."
          ]
        },
        {
          title: "Operational SLAs & KPIs",
          content: [
            "Privilege Reduction Rate ≥ 25 %: % reduction in unused or excessive permissions.",
            "Revocation Accuracy ≥ 97 %: % of correctly revoked entitlements without false positives.",
            "Mean Time to Revoke (MTTRv) < 1 hr: Time from detection to access removal.",
            "Orphaned Account Cleanup Rate ≥ 98 %: % of orphaned entitlements removed.",
            "Agent Availability ≥ 99.9 %: Monitored via Grafana / Prometheus."
          ]
        }
      ]
    },
    {
      title: "4. Technical Context & Summary",
      subSections: [
        {
          content: [
            "Execution Environment: RunOps n8n automation cluster (Azure) with containerized “EntitlementCleanup-AI” microservice.",
            "Primary Interfaces: AD PowerShell/Graph API, Okta/Entra APIs, AWS IAM APIs, SaaS SCIM connectors.",
            "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on access governance, entitlement analytics, and RunOps Zero-Trust KB.",
            "Outputs: Entitlement Cleanup Report (PDF/JSON), Privilege Reduction Summary, Revocation Log, Compliance Snapshot.",
            "Summary: The agent serves as the autonomous hygiene engine for enterprise identity ecosystems, transforming entitlement management into a self-cleaning, continuously verified, Zero-Trust-aligned governance framework."
          ]
        }
      ]
    }
  ],
  agenticModel: {
    trigger: "Continuous collection from identity and app systems, with AI correlation of usage logs and entitlement metadata.",
    workingPlane: "Orchestrates API calls across Identity Providers, Cloud IAM, and SaaS Applications; maintains operational state and baselines in PostgreSQL.",
    memory: "Stores entitlement baselines, revocation logs, and compliance data to refine detection thresholds and inform future decisions.",
    reasoning: "AI-Reasoning Layer determines whether access is redundant, risky, or policy-violating and calculates revocation confidence.",
    execution: "Automatically removes/quarantines entitlements, routes critical revocations through ITSM/manager approval workflows, and records all revocations in compliance logs."
  },
  kpis: [
    { label: "Privilege Reduction Rate", value: "25", unit: "%", target: "≥ 25%", color: "#10b981" },
    { label: "Revocation Accuracy", value: "97", unit: "%", target: "≥ 97%", color: "#2563eb" },
    { label: "Mean Time to Revoke (MTTRv)", value: "1", unit: "hr", target: "< 1 hr", color: "#f59e0b" },
    { label: "Orphaned Account Cleanup Rate", value: "98", unit: "%", target: "≥ 98%", color: "#ef4444" }
  ]
},
'identity-drift-detector-agent': {
  title: "IAM Identity Drift Detector Agent",
  description: "Continuously compares user entitlements and access attributes across systems with the authoritative identity source of truth (e.g., HRIS or Entra ID) to detect mismatches, unauthorized access, or identity drift — ensuring alignment, compliance, and Zero-Trust consistency across the enterprise.",
  // No video provided
  details: [
    {
      title: "Business Value & Outcomes",
      points: [
        "Identity Alignment Assurance: Ensures consistency between HR, AD, Entra ID, and SaaS user records.",
        "Drift Detection & Remediation: Identifies and corrects mismatched attributes and entitlements automatically.",
        "Reduced Compliance Risk: Prevents audit gaps caused by orphaned or misaligned access.",
        "Improved IAM Hygiene: Keeps directories and downstream systems synchronized in real time.",
        "Zero-Trust Integrity: Maintains authoritative identity consistency as a foundation of access control."
      ]
    }
  ],
  sections: [
    {
      title: "1. Business Context",
      subSections: [
        {
          title: "Problem Statement",
          content: [
            "Over time, user identities across systems drift from the source of truth due to manual changes, failed syncs, or API misconfigurations.",
            "Drift creates inconsistent user states — accounts enabled in one system but disabled in another, or permissions differing from approved roles.",
            "These discrepancies result in security exposure, audit failures, and compliance violations.",
            "Enterprises need an autonomous identity drift detection agent that continuously reconciles entitlements, flags deviations, and enforces policy alignment across all identity repositories."
          ]
        },
        {
          title: "Business Outcomes (Benefits)",
          content: [
            "Ensures consistency between HR, AD, Entra ID, and SaaS user records.",
            "Identifies and corrects mismatched attributes and entitlements automatically.",
            "Prevents audit gaps caused by orphaned or misaligned access.",
            "Keeps directories and downstream systems synchronized in real time.",
            "Maintains authoritative identity consistency as a foundation of access control."
          ]
        },
        {
          title: "Use Case Alignment",
          content: [
            "Cross-System Identity Reconciliation: Compare attributes and access between HRIS, AD, Entra ID, and SaaS systems.",
            "Unauthorized Access Detection: Flag accounts with entitlements not approved in the source system.",
            "Disabled Account Validation: Identify accounts active in downstream systems despite HR or directory deactivation.",
            "Attribute & Role Drift Analysis: Detect mismatched titles, departments, or group assignments.",
            "Audit & Reporting: Generate identity alignment and drift reports for GRC and compliance audits."
          ]
        }
      ]
    },
    {
      title: "2. Technical Functionality",
      subSections: [
        {
          title: "Scope of Capabilities",
          content: [
            "Attribute Reconciliation: Continuously compare user identity fields (title, department, status) across HRIS, AD, and Entra ID.",
            "Entitlement Comparison: Detect mismatched roles, group memberships, or permissions between systems.",
            "Orphan Account Detection: Identify accounts not linked to active HR or identity records.",
            "AI Reasoning Engine: Classify mismatches by severity (critical, medium, benign) and cause (manual edit, sync failure, inheritance).",
            "Automated Remediation: Execute attribute or access corrections based on confidence thresholds.",
            "Approval Workflow Integration: Route significant changes through ITSM or IAM approval pipelines.",
            "Audit Logging: Record drift events, root cause analysis, and remediation outcomes for compliance evidence."
          ]
        },
        {
          title: "Integrations",
          content: [
            "HRIS Platforms (Workday, SAP, Oracle): Source of truth for identity data (Inbound).",
            "Identity Providers (AD, Entra ID, Okta, Ping): Compare directory and cloud identity states (Bidirectional).",
            "SaaS Applications (M365, Salesforce, ServiceNow, GitHub): Validate entitlement consistency across app-level roles (Bidirectional).",
            "Cloud IAM Systems (AWS IAM, Azure RBAC, GCP IAM): Ensure cloud entitlements match approved identity baselines (Bidirectional).",
            "CoreStack GRC / Compliance Modules: Record drift evidence and alignment reports (Bidirectional).",
            "ServiceNow / Jira: Manage remediation tickets, escalations, and approval workflows (Bidirectional).",
            "Azure OpenAI / Reasoning Engine: Correlate drift context, classify severity, and recommend resolution paths (Inbound).",
            "Confluence / Data Lake: Archive drift reports, audit evidence, and remediation logs (Outbound)."
          ]
        }
      ]
    },
    {
      title: "3. Agentic Tiers & Operations",
      subSections: [
        {
          title: "Agentic Tiers",
          content: [
            "Tier 1 — Identity Data Collection & Baseline Mapping: Aggregate HRIS, directory, and SaaS identity attributes (50 % Automation Depth).",
            "Tier 2 — Drift Detection & Correlation: Identify mismatched or unauthorized entitlements across systems (75 % Automation Depth).",
            "Tier 3 — AI Reasoning & Remediation Recommendation: Analyze causes and determine corrective actions automatically (90 % Automation Depth).",
            "Tier 4 — Predictive Drift Prevention & Continuous Learning: Anticipate recurring drift patterns and adjust synchronization policies proactively (100 % (Fully Agentic) Automation Depth)."
          ]
        },
        {
          title: "Operational SLAs & KPIs",
          content: [
            "Identity Alignment Rate ≥ 99 %: % of identities consistent with the source of truth.",
            "Mean Time to Detect (MTTD) Drift < 30 min: Time from mismatch occurrence to detection.",
            "Mean Time to Reconcile (MTTR) < 2 hrs: Time from detection to attribute or access correction.",
            "Unauthorized Entitlement Detection Accuracy ≥ 97 %: % of true unauthorized permissions correctly identified.",
            "Agent Availability ≥ 99.9 %: HA n8n cluster monitored by Grafana / Prometheus."
          ]
        }
      ]
    },
    {
      title: "4. Technical Context & Summary",
      subSections: [
        {
          content: [
            "Execution Environment: RunOps n8n automation cluster (Azure) with containerized “IdentityDriftDetector-AI” microservice.",
            "Primary Interfaces: HRIS APIs, AD/Entra Graph APIs, SaaS connectors, Cloud IAM APIs.",
            "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on IAM drift analysis, synchronization logic, and RunOps Zero-Trust KB.",
            "Outputs: Identity Drift Report (PDF/JSON), Unauthorized Entitlement Log, Alignment Dashboard Snapshot, Compliance Evidence Summary.",
            "Summary: The agent serves as the autonomous reconciliation and assurance layer within the Zero-Trust Identity ecosystem, ensuring that every identity in every system matches the source of truth — always."
          ]
        }
      ]
    }
  ],
  agenticModel: {
    trigger: "Continuous correlation of HR and IAM data; identity sync and baseline refresh cycles.",
    workingPlane: "Orchestrates API calls across HRIS, Identity Providers, SaaS Apps, and Cloud IAM systems; maintains identity baselines in PostgreSQL.",
    memory: "Stores identity baselines, drift history, and remediation status to refine detection and improve sync reliability.",
    reasoning: "AI Reasoning Engine classifies mismatches by severity and cause, determines corrective actions, and recommends resolution paths.",
    execution: "Performs attribute updates, group syncs, or account deactivations; routes significant changes through ITSM/IAM approval pipelines; records all events for audit logging."
  },
  kpis: [
    { label: "Identity Alignment Rate", value: "99", unit: "%", target: "≥ 99%", color: "#10b981" },
    { label: "Mean Time to Detect (MTTD) Drift", value: "30", unit: "mins", target: "< 30 min", color: "#2563eb" },
    { label: "Mean Time to Reconcile (MTTR)", value: "2", unit: "hrs", target: "< 2 hrs", color: "#f59e0b" },
    { label: "Unauthorized Entitlement Accuracy", value: "97", unit: "%", target: "≥ 97%", color: "#ef4444" }
  ]
},
'identity-reconciliation-agent': {
  title: "IAM Identity Reconciliation Agent",
  description: "Detects, correlates, and resolves duplicate, orphaned, and mismatched identity records across Active Directory, Entra ID, HRIS, and SaaS platforms — ensuring a unified, authoritative identity record and eliminating inconsistencies that compromise Zero-Trust enforcement.",
  // No video provided
  details: [
    {
      title: "Business Value & Outcomes",
      points: [
        "Unified Identity Source of Truth: Ensures every person, service, and device has one accurate identity record.",
        "Reduced Security Exposure: Eliminates orphaned accounts and mislinked identities vulnerable to misuse.",
        "Operational Efficiency: Automates identity reconciliation across directories and SaaS platforms.",
        "Improved Compliance: Ensures alignment with ISO 27001, NIST 800-53, and SOX identity management standards.",
        "License & Cost Optimization: Identifies and removes redundant or unused identity instances."
      ]
    }
  ],
  sections: [
    {
      title: "1. Business Context",
      subSections: [
        {
          title: "Problem Statement",
          content: [
            "Over time, user records across HR, AD, Entra ID, and SaaS systems diverge, resulting in duplicates, orphaned accounts, and mislinked identities.",
            "These discrepancies lead to security gaps (e.g., unmonitored orphaned access), license waste, and audit failures.",
            "Manual cleanup is complex, requiring correlation across multiple data sources with different attributes and identifiers.",
            "Organizations need an autonomous identity reconciliation agent that continuously detects duplicates, resolves orphaned or mislinked accounts, and enforces authoritative identity mapping across the enterprise."
          ]
        },
        {
          title: "Business Outcomes (Benefits)",
          content: [
            "Ensures every person, service, and device has one accurate identity record.",
            "Eliminates orphaned accounts and mislinked identities vulnerable to misuse.",
            "Automates identity reconciliation across directories and SaaS platforms.",
            "Ensures alignment with ISO 27001, NIST 800-53, and SOX identity management standards.",
            "Identifies and removes redundant or unused identity instances."
          ]
        },
        {
          title: "Use Case Alignment",
          content: [
            "Duplicate Identity Detection: Identify multiple records representing the same user across systems.",
            "Orphaned Account Cleanup: Detect and remediate accounts not linked to any HRIS or active identity.",
            "Cross-Directory Identity Matching: Align HR, AD, Entra ID, and SaaS user records via unique attribute correlation.",
            "Service Account Validation: Validate and classify non-human accounts for appropriate governance.",
            "Audit & Reporting: Generate reconciliation reports and compliance-ready evidence."
          ]
        }
      ]
    },
    {
      title: "2. Technical Functionality",
      subSections: [
        {
          title: "Scope of Capabilities",
          content: [
            "Duplicate Detection: Identify duplicate identities using probabilistic and fuzzy matching of attributes (email, HRID, SID, UPN).",
            "Orphaned Account Identification: Detect accounts existing without corresponding HR or identity source record.",
            "Cross-System Correlation: Match and link accounts between HRIS, AD, Entra ID, Okta, and SaaS apps.",
            "AI-Driven Resolution: Determine canonical record ownership and perform merge or disable actions.",
            "Service Account Differentiation: Detect and tag non-human accounts for separate lifecycle management.",
            "Remediation Workflow: Automate merges or deletions, or route exceptions for manual approval.",
            "Audit & Compliance Logging: Maintain immutable evidence for all reconciliations and corrections."
          ]
        },
        {
          title: "Integrations",
          content: [
            "HRIS Systems (Workday, SAP, Oracle, UKG): Provide authoritative employee records and unique identifiers (Inbound).",
            "Identity Providers (AD, Entra ID, Okta, Ping): Detect duplicate or orphaned directory entries and enforce remediation (Bidirectional).",
            "SaaS Platforms (M365, Salesforce, ServiceNow, Zoom): Identify redundant or mislinked SaaS accounts (Bidirectional).",
            "Cloud IAM Systems (AWS IAM, Azure RBAC, GCP IAM): Detect unlinked or duplicate cloud identities (Bidirectional).",
            "CoreStack GRC / Compliance Modules: Record reconciliation outcomes and audit logs (Bidirectional).",
            "ServiceNow / Jira: Manage approval and reconciliation workflows (Bidirectional).",
            "Azure OpenAI / Reasoning Engine: Analyze matching probabilities, assess confidence, and recommend remediation paths (Inbound).",
            "Confluence / Data Lake: Archive identity reconciliation reports, match logs, and audit trails (Outbound)."
          ]
        }
      ]
    },
    {
      title: "3. Agentic Tiers & Operations",
      subSections: [
        {
          title: "Agentic Tiers",
          content: [
            "Tier 1 — Identity Data Aggregation & Baseline Mapping: Collect identity records across directories and HR systems (50 % Automation Depth).",
            "Tier 2 — Duplicate & Orphan Detection: Identify duplicate or unlinked records using multi-attribute matching (75 % Automation Depth).",
            "Tier 3 — AI Reasoning & Resolution Execution: Decide and execute merges or deactivations based on confidence scoring (90 % Automation Depth).",
            "Tier 4 — Continuous Learning & Predictive Identity Assurance: Predict reconciliation needs and adjust matching logic dynamically (100 % (Fully Agentic) Automation Depth)."
          ]
        },
        {
          title: "Operational SLAs & KPIs",
          content: [
            "Duplicate Identity Resolution Rate ≥ 98 %: % of detected duplicates resolved successfully.",
            "Orphan Account Cleanup Rate ≥ 99 %: % of orphaned accounts removed or reconciled.",
            "Mean Time to Reconcile (MTTR) < 2 hrs: Time from detection to merge or deactivation.",
            "False Positive Rate (Matching) ≤ 2 %: Incorrectly identified duplicates or orphans.",
            "Agent Availability ≥ 99.9 %: HA cluster monitored via Grafana / Prometheus."
          ]
        }
      ]
    },
    {
      title: "4. Technical Context & Summary",
      subSections: [
        {
          content: [
            "Execution Environment: RunOps n8n automation cluster (Azure) with containerized “IdentityReconcile-AI” microservice.",
            "Primary Interfaces: HRIS, AD/Entra Graph API, Okta API, SaaS and cloud IAM APIs.",
            "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on identity correlation logic, IAM governance, and RunOps Zero-Trust KB.",
            "Outputs: Identity Reconciliation Report (PDF/JSON), Duplicate Resolution Log, Orphan Account Summary, Compliance Audit Snapshot.",
            "Summary: The agent acts as the autonomous identity integrity guardian within the Zero-Trust IAM ecosystem, ensuring every account — human or non-human — is accurate, unique, and fully traceable."
          ]
        }
      ]
    }
  ],
  agenticModel: {
    trigger: "Regular sync of HR and directory data; continuous AI-based identity clustering cycle.",
    workingPlane: "Orchestrates API calls to HRIS, Identity Providers, SaaS, and Cloud IAM; stores identity correlation data and confidence models in PostgreSQL.",
    memory: "Persists identity correlation data, confidence models, and audit records to improve matching accuracy and inform future decisions.",
    reasoning: "AI Reasoning determines true ownership and canonical records, analyzes matching probabilities, assesses confidence, and recommends remediation paths.",
    execution: "Automates merges or deletions, routes ambiguous records to ITSM for review, and maintains immutable evidence for all corrections."
  },
  kpis: [
    { label: "Duplicate Identity Resolution Rate", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
    { label: "Orphan Account Cleanup Rate", value: "99", unit: "%", target: "≥ 99%", color: "#2563eb" },
    { label: "Mean Time to Reconcile (MTTR)", value: "2", unit: "hrs", target: "< 2 hrs", color: "#f59e0b" },
    { label: "False Positive Rate (Matching)", value: "2", unit: "%", target: "≤ 2%", color: "#ef4444" }
  ]
},
'sod-enforcement-agent': {
  title: "IAM SoD (Segregation of Duties) Enforcement Agent",
  description: "Continuously detects and prevents toxic access combinations across business-critical systems — such as Finance, HR, and IT — by enforcing Segregation of Duties (SoD) policies, blocking or remediating conflicting entitlements in real time, and maintaining compliance with regulatory frameworks.",
  // No video provided
  details: [
    {
      title: "Business Value & Outcomes",
      points: [
        "Fraud Prevention: Prevents users from holding conflicting privileges across systems (e.g., create + approve payment).",
        "Continuous Compliance: Maintains SoD enforcement aligned with SOX, ISO 27001, and NIST 800-53.",
        "Reduced Audit Findings: Eliminates manual oversight gaps and provides continuous assurance.",
        "Operational Efficiency: Automates SoD validation and conflict remediation workflows.",
        "Zero-Trust Reinforcement: Ensures least-privilege principle by design across all access models."
      ]
    }
  ],
  sections: [
    {
      title: "1. Business Context",
      subSections: [
        {
          title: "Problem Statement",
          content: [
            "Complex organizations often grant users access across multiple systems (e.g., SAP, Workday, ServiceNow, Oracle ERP, AD) without fully accounting for conflicting permissions.",
            "Manual SoD reviews are periodic and error-prone, leaving windows where users can both initiate and approve transactions, violating least-privilege and audit controls.",
            "This exposes organizations to fraud, policy violations, and audit findings under SOX, ISO, and NIST frameworks.",
            "Enterprises need an autonomous enforcement agent that continuously detects, evaluates, and prevents SoD violations across all systems in real time."
          ]
        },
        {
          title: "Business Outcomes (Benefits)",
          content: [
            "Prevents users from holding conflicting privileges across systems (e.g., create + approve payment).",
            "Maintains SoD enforcement aligned with SOX, ISO 27001, and NIST 800-53.",
            "Eliminates manual oversight gaps and provides continuous assurance.",
            "Automates SoD validation and conflict remediation workflows.",
            "Enforces least-privilege principle by design across all access models."
          ]
        },
        {
          title: "Use Case Alignment",
          content: [
            "Toxic Access Detection: Identify users holding conflicting privileges within or across systems.",
            "Preventive Access Control: Block assignment of conflicting roles at provisioning time.",
            "Automated Remediation: Remove or quarantine conflicting entitlements automatically.",
            "Continuous SoD Monitoring: Evaluate access changes in real time against SoD rulesets.",
            "Audit & Compliance Reporting: Generate SoD violation logs and corrective action summaries for GRC systems."
          ]
        }
      ]
    },
    {
      title: "2. Technical Functionality",
      subSections: [
        {
          title: "Scope of Capabilities",
          content: [
            "Toxic Access Rule Library: Maintain an enterprise SoD matrix defining incompatible roles and actions (e.g., “Create Vendor” ≠ “Approve Invoice”).",
            "Entitlement & Role Correlation: Continuously map user entitlements across Finance, HR, and IT platforms.",
            "Real-Time Access Validation: Intercept and evaluate new access requests against SoD policies before approval.",
            "AI Reasoning Layer: Classify conflicts (critical, major, minor) and determine remediation path (revoke, alert, approve with conditions).",
            "Automated Remediation Workflows: Revoke conflicting permissions, trigger approval workflows, or flag for manager review.",
            "Compliance Integration: Generate control evidence aligned to SOX 404, ISO 27001 A.9, and NIST 800-53 AC-5.",
            "Audit Logging & Evidence Storage: Record all SoD detections, actions, and resolutions for GRC reporting."
          ]
        },
        {
          title: "Integrations",
          content: [
            "Finance Systems (SAP, Oracle ERP, NetSuite): Analyze financial access combinations (e.g., vendor setup + payment approval) (Bidirectional).",
            "HR Systems (Workday, SAP SuccessFactors): Validate role alignment and employment status for SoD context (Inbound).",
            "IT & IAM Systems (Active Directory, Entra ID, Okta, ServiceNow): Detect conflicting admin or privileged roles (Bidirectional).",
            "Cloud IAM (AWS IAM, Azure RBAC, GCP IAM): Evaluate infrastructure privilege overlap and separation of admin duties (Bidirectional).",
            "CoreStack GRC / Compliance Modules: Record SoD findings, remediation actions, and control evidence (Bidirectional).",
            "ServiceNow / Jira: Manage exception handling and automated remediation tickets (Bidirectional).",
            "Azure OpenAI / Reasoning Engine: Analyze risk severity, determine conflict resolution, and refine SoD rule sets dynamically (Inbound).",
            "Confluence / Data Lake: Archive SoD matrices, violation reports, and compliance dashboards (Outbound)."
          ]
        }
      ]
    },
    {
      title: "3. Agentic Tiers & Operations",
      subSections: [
        {
          title: "Agentic Tiers",
          content: [
            "Tier 1 — SoD Rule Mapping & Data Aggregation: Collect entitlement data and apply SoD matrices across systems (50 % Automation Depth).",
            "Tier 2 — Toxic Access Detection & Classification: Identify and classify SoD conflicts by severity (75 % Automation Depth).",
            "Tier 3 — AI Reasoning & Automated Remediation: Execute revocation or conditional approval workflows (90 % Automation Depth).",
            "Tier 4 — Continuous Learning & Predictive Governance: Predict new SoD risks and refine rule sets via machine learning (100 % (Fully Agentic) Automation Depth)."
          ]
        },
        {
          title: "Operational SLAs & KPIs",
          content: [
            "SoD Violation Detection Accuracy ≥ 98 %: % of true toxic access combinations correctly identified.",
            "Mean Time to Remediate (MTTR) < 4 hrs: Average time to resolve detected SoD conflicts.",
            "False Positive Rate ≤ 3 %: % of benign access combinations incorrectly flagged.",
            "Pre-Provisioning Block Success Rate ≥ 99 %: % of conflicting access requests blocked automatically.",
            "Agent Availability ≥ 99.9 %: HA n8n cluster monitored via Grafana / Prometheus."
          ]
        }
      ]
    },
    {
      title: "4. Technical Context & Summary",
      subSections: [
        {
          content: [
            "Execution Environment: RunOps n8n automation cluster (Azure) with containerized “SoDEnforcement-AI” microservice.",
            "Primary Interfaces: HRIS, IAM directories, ERP systems, GRC platforms, and ITSM APIs.",
            "Reasoning Model: Azure OpenAI GPT-4o fine-tuned on SoD risk models, IAM governance frameworks, and RunOps Zero-Trust KB.",
            "Outputs: SoD Violation Report (PDF/JSON), Toxic Access Log, Remediation Summary, Compliance Control Evidence.",
            "Summary: The agent functions as the autonomous compliance sentinel within the enterprise identity and access ecosystem, ensuring every identity operates within secure, compliant, and conflict-free boundaries — always."
          ]
        }
      ]
    }
  ],
  agenticModel: {
    trigger: "Real-time access validation on new requests; continuous entitlement data refresh and correlation cycle.",
    workingPlane: "Intersects new access requests with SoD policies; orchestrates APIs across Finance, HR, IT, and IAM systems; stores SoD matrices in PostgreSQL.",
    memory: "Persists SoD matrices, conflict records, and remediation actions to refine rule sets and improve risk prediction.",
    reasoning: "AI Reasoning Layer classifies conflicts, determines risk severity, and decides corrective or preventive actions (block, revoke, alert).",
    execution: "Executes pre-provisioning blocks, revokes conflicting permissions, triggers approval workflows, and generates compliance control evidence."
  },
  kpis: [
    { label: "SoD Violation Detection Accuracy", value: "98", unit: "%", target: "≥ 98%", color: "#10b981" },
    { label: "Mean Time to Remediate (MTTR)", value: "4", unit: "hrs", target: "< 4 hrs", color: "#2563eb" },
    { label: "False Positive Rate", value: "3", unit: "%", target: "≤ 3%", color: "#f59e0b" },
    { label: "Pre-Provisioning Block Success Rate", value: "99", unit: "%", target: "≥ 99%", color: "#ef4444" }
  ]
},
'incident-to-problem-to-change-linker-agent': {
    title: "Incident to Problem to Change Linker Agent",
    description: "Automatically enforces traceability across Incidents, Problems, and Changes to ensure a complete and auditable impact chain from detection to resolution, strengthening ITIL governance and audit confidence.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
        {
            title: "Business Value & Outcomes",
            points: [
                "100% traceability across Incidents → Problems → Changes for critical services.",
                "20–25% faster RCA through pre-linked incident chains.",
                "15–20% fewer audit findings tied to missing traceability.",
                "Improved CAB and CSI decision-making with full impact context.",
                "Higher data quality and governance confidence across ITSM processes."
            ]
        }
    ],
    sections: [
        {
            title: "1. Business Context",
            subSections: [
                {
                    title: "Problem Statement",
                    content: [
                        "40–50% of Problems are not linked to the originating Incident(s).",
                        "25–30% of Changes lack lineage to Problems or RCA documentation.",
                        "Audit and CAB reviews flag missing traceability between Incidents, Problems, and Changes.",
                        "SLA, KPI, and CSI reporting are distorted by incomplete linkage.",
                        "Analysts waste hours manually linking tickets post-resolution."
                    ]
                },
                {
                    title: "Business Outcomes (Benefits)",
                    content: [
                        "Achieves 100% traceability for critical services, ensuring an auditable chain.",
                        "Accelerates Root Cause Analysis (RCA) by automatically pre-linking incident chains.",
                        "Reduces audit findings related to missing traceability and provides full impact context for governance.",
                        "Eliminates manual effort for linking tickets, improving data quality and governance confidence.",
                        "Empirical basis: Enterprises automating ITIL traceability achieve 20% SLA improvement, 25% faster RCA, and 30% fewer audit exceptions."
                    ]
                },
                {
                    title: "Use Case Alignment",
                    content: [
                        "Healthcare: Links EHR outages (Incidents) → RCA (Problem) → approved infrastructure or patch Changes.",
                        "Finance: SOX-compliant lineage between ERP disruptions, Problem RCAs, and CAB-approved fixes.",
                        "Cybersecurity: Ensures SOC incidents and containment Problems are traceable to policy or control Changes.",
                        "ITSM: Maintains end-to-end evidence chain for governance and post-mortems."
                    ]
                }
            ]
        },
        {
            title: "2. Technical Functionality",
            subSections: [
                {
                    title: "Scope of Capabilities",
                    content: [
                        "Links related Incidents → Problems → Changes based on CI, service, or RCA pattern.",
                        "Detects unlinked or orphaned records and enforces linkage rules.",
                        "Correlates RCA data and change records to produce impact chains.",
                        "Logs lineage evidence for CAB and audit review.",
                        "Updates dashboards for Problem and Change Managers."
                    ]
                },
                {
                    title: "Integrations",
                    content: [
                        "ITSM: ServiceNow, Jira Service Management, BMC, Remedy (Bidirectional).",
                        "Monitoring/Logs: Splunk, Datadog, Elastic, AppDynamics for RCA validation (Inbound).",
                        "CMDB: ServiceNow CMDB, BMC Atrium for service/CI alignment (Inbound).",
                        "GRC: Archer, ServiceNow GRC, OneTrust for audit trail storage (Outbound).",
                        "Collaboration: Teams, Slack for SME linkage validation notifications (Bidirectional)."
                    ]
                }
            ]
        },
        {
            title: "3. Operational Model & Security",
            subSections: [
                {
                    title: "Agentic Tiers (RunOps Framework)",
                    content: [
                        "Trigger, Invocation Events: Incident closure, Problem creation, Change submission, RCA completion.",
                        "Working Plane, Orchestration Layer: Orchestrates ITSM record ingestion, linkage creation, and audit logging.",
                        "Memory, State and Knowledge: Stores historical linkage maps, RCA results, and CAB decisions.",
                        "Reasoning, Decisioning Tier: Determines correct record relationships and validates lineage.",
                        "Execution, Effectors and Handlers: Creates traceability links, updates ITSM records, logs audit evidence."
                    ]
                },
                {
                    title: "Security & Compliance",
                    content: [
                        "IAM Guardrails: Enforces SoD and RBAC on linkage modifications.",
                        "Data Handling: Ticket and CMDB data encrypted in transit/at rest.",
                        "Audit & Traceability: Immutable logs for linkage creation, validation, and updates.",
                        "Certifications Supported: HIPAA, SOX, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR."
                    ]
                }
            ]
        },
        {
            title: "4. Performance Metrics",
            subSections: [
                {
                    content: [
                        "SLA: Linkages validated/created within <24h of record creation or closure.",
                        "Accuracy: >95% correct traceability match rate after training.",
                        "Coverage: % of tickets/records with enforced linkages.",
                        "ROI Proof Points: 25–30% audit gap reduction; 20–25% faster RCA; 10–15% improved CAB and CSI efficiency."
                    ]
                }
            ]
        }
    ],
    agenticModel: {
        trigger: "Incident closure, Problem creation, Change submission, RCA completion (SLA-backed <24h traceability validation).",
        workingPlane: "Orchestrates ITSM record ingestion, linkage creation, and audit logging via ITSM + CMDB + GRC interoperability.",
        memory: "Stores historical linkage maps, RCA clusters, and CAB decisions to improve accuracy and completeness.",
        reasoning: "Analyzes incidents, RCA data, and Problem records to determine logical linkages; embeds governance traceability logic.",
        execution: "Creates or enforces traceability relationships, updates ITSM fields, generates impact chain visualizations, and logs immutable evidence."
    },
    kpis: [
        { label: "Traceability Coverage", value: "100", unit: "%", target: "100% for critical services", color: "#10b981" },
        { label: "Linkage Accuracy", value: "95", unit: "%", target: ">95%", color: "#2563eb" },
        { label: "RCA Speed Improvement", value: "20", unit: "%", target: "20–25% faster RCA", color: "#f59e0b" },
        { label: "Linkage SLA", value: "24", unit: "hrs", target: "<24h of closure", color: "#ef4444" }
    ]
},
'cab-packager-agent': {
    title: "TSM CAB Packager Agent",
    description: "Automates change packet assembly, ensuring each submission includes required evidence such as risk assessment, test verification, rollback plans, and peer reviews, creating a standardized, audit-ready CAB submission every time.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
        {
            title: "Business Value & Outcomes",
            points: [
                "100% completeness and standardization of CAB packets.",
                "20–25% faster CAB review cycles with fewer deferrals.",
                "15–20% reduction in failed or rolled-back changes.",
                "Fully auditable CAB process for SOX, HIPAA, PCI, and ISO compliance.",
                "Improved trust between ITSM teams, CABs, and business stakeholders."
            ]
        }
    ],
    sections: [
        {
            title: "1. Business Context",
            subSections: [
                {
                    title: "Problem Statement",
                    content: [
                        "60–70% of CAB deferrals are caused by incomplete or unclear packets.",
                        "Analysts spend hours assembling evidence across multiple tools.",
                        "CAB meetings run over time due to missing context and incomplete testing.",
                        "Audit findings highlight weak or inconsistent change documentation.",
                        "Failed changes occur when CABs approve incomplete submissions under pressure."
                    ]
                },
                {
                    title: "Business Outcomes (Benefits)",
                    content: [
                        "Standardizes CAB submissions for quality, consistency, and audit readiness.",
                        "Reduces analyst effort and meeting fatigue, leading to faster CAB review cycles.",
                        "Minimizes failed or rolled-back changes by enforcing submission completeness.",
                        "Provides immutable, traceable CAB documentation for compliance and post-change reviews.",
                        "Empirical basis: Enterprises that automate CAB preparation achieve 20–25% shorter governance cycles and reduce change-related incidents by 15–20%."
                    ]
                },
                {
                    title: "Use Case Alignment",
                    content: [
                        "Healthcare: CAB packets for EHR upgrades with validation, downtime risk, and rollback evidence.",
                        "Finance: SOX/PCI change packets for ERP or trading systems including risk, tests, and compliance sign-offs.",
                        "Airline Ops: Infrastructure CAB submissions with test scripts, SLA risks, and rollback steps.",
                        "Cybersecurity: Firewall and endpoint CAB packets with security peer reviews and mitigation steps."
                    ]
                }
            ]
        },
        {
            title: "2. Technical Functionality",
            subSections: [
                {
                    title: "Scope of Capabilities",
                    content: [
                        "Collects and validates risk assessments, test results, rollback plans, and peer review inputs.",
                        "Ensures required evidence is present before CAB submission.",
                        "Generates standardized CAB packet (PDF, ITSM view, or portal summary).",
                        "Routes for SME/peer review and CAB approval automatically.",
                        "Logs packet creation and evidence into ITSM and GRC systems."
                    ]
                },
                {
                    title: "Integrations",
                    content: [
                        "ITSM: ServiceNow, Jira Service Management, BMC, Remedy (Bidirectional).",
                        "CI/CD + Testing: Jenkins, GitHub Actions, Azure DevOps, GitLab (Inbound).",
                        "Risk/GRC: Archer, OneTrust, ServiceNow GRC (Inbound/Outbound).",
                        "CMDB: ServiceNow CMDB, BMC Atrium for CI/service mapping (Inbound).",
                        "Collaboration: Teams, Slack for CAB notifications and peer reviews (Bidirectional).",
                        "Analytics: Power BI, Tableau for CAB performance dashboards (Outbound)."
                    ]
                }
            ]
        },
        {
            title: "3. Agentic Tiers & Operations",
            subSections: [
                {
                    title: "Agentic Tiers (RunOps Framework)",
                    content: [
                        "Trigger, Invocation Events: Change submission requiring CAB approval (SLA-backed <30m CAB packet assembly).",
                        "Working Plane, Orchestration Layer: Orchestrates evidence ingestion, packet validation, and CAB routing (ITSM + GRC + CI/CD interoperability).",
                        "Memory, State and Knowledge: Stores CAB templates, prior packet gaps, and audit findings (improves packet completeness and governance quality).",
                        "Reasoning, Decisioning Tier: Validates required components, flags missing or outdated evidence (Explainability, compliance overlays).",
                        "Execution, Effectors and Handlers: Generates complete CAB packet, routes for review, logs for audit (Safe execution, governance escalation)."
                    ]
                },
                {
                    title: "Operational SLAs & KPIs",
                    content: [
                        "SLA: CAB packet assembled in <30 minutes after change submission.",
                        "Accuracy: >95% packet completeness after training.",
                        "Coverage: % of CAB changes automated through the agent.",
                        "KPIs: CAB packet completeness %, CAB deferrals avoided %, failed change reduction %."
                    ]
                }
            ]
        },
        {
            title: "4. Security & Compliance",
            subSections: [
                {
                    content: [
                        "IAM Guardrails: CAB packet creation tied to governance and approval roles.",
                        "Data Handling: Risk, test, and rollback data encrypted in transit/at rest.",
                        "Audit & Traceability: Immutable, signed CAB evidence logs with timestamps.",
                        "Certifications Supported: SOX, HIPAA, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR."
                    ]
                }
            ]
        }
    ],
    agenticModel: {
        trigger: "Change submission requiring CAB approval.",
        workingPlane: "Orchestrates evidence ingestion, packet validation, and CAB routing (ITSM + GRC + CI/CD interoperability).",
        memory: "Retains CAB templates, evidence formats, and historical packet quality metrics to improve evidence gathering accuracy.",
        reasoning: "Validates each change against policy, gathers relevant evidence (risk scores, tests, peer reviews, rollback), and flags missing or invalid elements.",
        execution: "Automatically assembles the CAB packet, applies standardized formatting, routes it for review, and logs evidence immutably for audit."
    },
    kpis: [
        { label: "Packet Completeness Rate", value: "95", unit: "%", target: ">95%", color: "#10b981" },
        { label: "CAB Deferrals Avoided", value: "20", unit: "%", target: "20–25% fewer deferrals", color: "#2563eb" },
        { label: "Failed Change Reduction", value: "15", unit: "%", target: "15–20% reduction", color: "#f59e0b" },
        { label: "Packet Assembly SLA", value: "30", unit: "mins", target: "<30 minutes", color: "#ef4444" }
    ]
},
'connector-manager-agent': {
    title: "Connector Manager Agent",
    description: "Continuously monitors, validates, and refreshes connector credentials (API keys, OAuth tokens, scopes), ensuring uninterrupted orchestration and compliance across ITSM, cloud, and DevOps ecosystems.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
        {
            title: "Business Value & Outcomes",
            points: [
                "100% visibility into connector health, token validity, and API permissions.",
                "20–25% fewer automation and synchronization failures.",
                "Stronger compliance posture with enforced least-privilege API scopes.",
                "15–20% reduction in integration-related service desk incidents.",
                "Faster resolution of integration failures with automatic token remediation."
            ]
        }
    ],
    sections: [
        {
            title: "1. Business Context",
            subSections: [
                {
                    title: "Problem Statement",
                    content: [
                        "60–70% of integration failures are caused by expired tokens or permission drift.",
                        "Manual API credential management leads to downtime and inconsistent security posture.",
                        "Unmonitored tokens create audit and compliance exposure (e.g., unused, over-scoped, or expired keys).",
                        "Broken integrations disrupt automations, dashboards, and cross-domain workflows.",
                        "IT teams lack centralized visibility into integration health and security status."
                    ]
                },
                {
                    title: "Business Outcomes (Benefits)",
                    content: [
                        "Prevents silent workflow failures by continuously enforcing token and connector health.",
                        "Reduces automation failures and integration outages, leading to improved service reliability.",
                        "Achieves stronger compliance by validating permissions against least-privilege policies.",
                        "Provides governance-ready evidence by logging all credential lifecycle actions for audit.",
                        "Empirical basis: Enterprises with automated connector lifecycle management achieve 25–30% fewer integration failures and 20% improved security compliance."
                    ]
                },
                {
                    title: "Use Case Alignment",
                    content: [
                        "Healthcare: Maintains secure integrations across EHR, CMDB, and ticketing systems for HIPAA compliance.",
                        "Finance: Ensures ERP and payment system connectors are token-valid and least-privileged for SOX.",
                        "ITSM: Maintains ServiceNow, Jira, Slack, and cloud connectors for automation continuity.",
                        "Cybersecurity: Enforces least-privilege tokens for security tool integrations (SIEM, EDR, SOAR)."
                    ]
                }
            ]
        },
        {
            title: "2. Technical Functionality",
            subSections: [
                {
                    title: "Scope of Capabilities",
                    content: [
                        "Monitors API key/token expiry, response latency, and connector health.",
                        "Validates OAuth scopes and permissions against policies.",
                        "Refreshes or rotates tokens automatically via vendor APIs.",
                        "Logs all connector actions for audit and compliance review.",
                        "Notifies teams of degraded connectors or credential anomalies."
                    ]
                },
                {
                    title: "Integrations",
                    content: [
                        "ITSM: ServiceNow, Jira Service Management, BMC (Bidirectional).",
                        "Cloud: AWS, Azure, GCP APIs (Bidirectional).",
                        "DevOps: GitHub, GitLab, Jenkins, Terraform Cloud (Bidirectional).",
                        "Monitoring: Splunk, Datadog, AppDynamics (Inbound).",
                        "GRC/Security: OneTrust, Archer, Okta, Azure AD, Vault systems (CyberArk, HashiCorp Vault) (Bidirectional)."
                    ]
                }
            ]
        },
        {
            title: "3. Operational Model & Security",
            subSections: [
                {
                    title: "Agentic Tiers (RunOps Framework)",
                    content: [
                        "Trigger, Invocation Events: Token expiry threshold, API failure, permission drift detection (SLA-backed <5m connector health validation).",
                        "Working Plane, Orchestration Layer: Orchestrates health monitoring, token refresh, permission verification (ITSM + cloud + security interoperability).",
                        "Memory, State and Knowledge: Stores connector history, rotation patterns, failure root causes (Adaptive learning → proactive remediation and compliance).",
                        "Reasoning, Decisioning Tier: Determines rotation timing, scope validity, escalation needs (Explainability, policy enforcement overlays).",
                        "Execution, Effectors and Handlers: Rotates tokens, refreshes credentials, updates CMDB and audit systems (Safe execution, governance escalation)."
                    ]
                },
                {
                    title: "Security & Compliance",
                    content: [
                        "IAM Guardrails: Credential storage restricted via Vault integration; all actions RBAC-controlled.",
                        "Data Handling: Tokens encrypted in transit and at rest; rotations executed securely via APIs.",
                        "Audit & Traceability: Immutable logs for token lifecycle, permission validation, and API actions.",
                        "Certifications Supported: HIPAA, SOX, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR."
                    ]
                }
            ]
        }
    ],
    agenticModel: {
        trigger: "Token expiry threshold, API failure, or permission drift detection.",
        workingPlane: "Orchestrates health monitoring, token refresh, and permission verification across ITSM, cloud, and security platforms.",
        memory: "Stores integration inventories, prior key/token rotations, and permission models, learning renewal and failure patterns for proactive refresh.",
        reasoning: "Monitors token lifecycles and connector response health, validating permissions against least-privilege policies to prevent drift.",
        execution: "Rotates expiring tokens, refreshes API credentials, corrects permissions, and alerts when anomalies are detected — updating ITSM or GRC records for evidence."
    },
    kpis: [
        { label: "Token Health Check SLA", value: "5", unit: "mins", target: "<5 minutes", color: "#10b981" },
        { label: "Successful Token Refreshes", value: "95", unit: "%", target: ">95%", color: "#2563eb" },
        { label: "Automation Failure Reduction", value: "20", unit: "%", target: "20–25% reduction", color: "#f59e0b" },
        { label: "Integration Uptime", value: "99.9", unit: "%", target: "Maximized", color: "#ef4444" }
    ]
},
'observability-bridge-agent': {
    title: "Observability Bridge Agent",
    description: "Integrates Application Performance Monitoring (APM) and Real User Monitoring (RUM) insights directly into tickets — embedding golden signals like latency, traffic, errors, and saturation to provide instant operational context.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
        {
            title: "Business Value & Outcomes",
            points: [
                "20–30% faster Mean Time to Detect (MTTD) and Mean Time to Resolve (MTTR).",
                "15–20% improvement in SLA compliance for performance incidents.",
                "Stronger ITSM–SRE collaboration with shared observability insights.",
                "Improved RCA quality with embedded system telemetry.",
                "Enhanced end-user experience visibility tied to ticket lifecycle."
            ]
        }
    ],
    sections: [
        {
            title: "1. Business Context",
            subSections: [
                {
                    title: "Problem Statement",
                    content: [
                        "ITSM tickets lack real-time technical context from monitoring tools.",
                        "Analysts must manually consult APM/RUM dashboards, wasting precious time.",
                        "Major incidents escalate before clear impact evidence is available.",
                        "SREs and service desk teams work from disconnected datasets.",
                        "MTTR and SLA compliance suffer due to slow context handoff."
                    ]
                },
                {
                    title: "Business Outcomes (Benefits)",
                    content: [
                        "Brings golden signal context directly into ITSM incidents in real time.",
                        "Reduces MTTR by removing tool-switching and manual correlation.",
                        "Provides unified visibility for service desk, SRE, and NOC teams.",
                        "Memory-driven learning: learns patterns of incidents vs telemetry anomalies, prioritizing metrics with proven diagnostic value.",
                        "Empirical basis: Enterprises that integrate observability data into ITSM reduce incident resolution times by 25–30% and improve SLA adherence by 20%."
                    ]
                },
                {
                    title: "Use Case Alignment",
                    content: [
                        "Healthcare: Correlates Epic latency spikes to infrastructure CIs for rapid triage.",
                        "Finance: Injects APM transaction traces into ERP/CRM incident records.",
                        "Airline Ops: Links RUM data from gate systems to user-facing ITSM incidents.",
                        "ITSM: Automates observability enrichment across service lines for accurate impact assessment.",
                        "Cybersecurity: Aligns observability metrics with anomaly detection in SOC workflows."
                    ]
                }
            ]
        },
        {
            title: "2. Technical Functionality",
            subSections: [
                {
                    title: "Scope of Capabilities",
                    content: [
                        "Connects APM (Application Performance Monitoring) and RUM data to ITSM tickets.",
                        "Embeds golden signal metrics (latency, traffic, errors, saturation) into incidents/problems.",
                        "Provides real-time CI/service-level health scoring within ITSM.",
                        "Links ticket records to relevant dashboards and traces.",
                        "Auto-updates severity and impact fields based on live telemetry."
                    ]
                },
                {
                    title: "Integrations",
                    content: [
                        "APM: AppDynamics, Dynatrace, New Relic, Datadog, Elastic APM (Inbound).",
                        "RUM: Catchpoint, ThousandEyes, Splunk RUM, Pingdom (Inbound).",
                        "ITSM: ServiceNow, Jira Service Management, BMC, Remedy (Bidirectional).",
                        "Monitoring: Prometheus, Grafana, SolarWinds, Zabbix (Inbound).",
                        "CMDB: ServiceNow CMDB, BMC Atrium for CI/service alignment (Inbound)."
                    ]
                }
            ]
        },
        {
            title: "3. Operational Model & Security",
            subSections: [
                {
                    title: "Agentic Tiers (RunOps Framework)",
                    content: [
                        "Trigger, Invocation Events: Incident creation, SLA breach prediction, APM/RUM anomaly detection (SLA-backed <60s enrichment with relevant telemetry).",
                        "Working Plane, Orchestration Layer: Orchestrates APM/RUM ingestion, telemetry correlation, ITSM ticket updates (ITSM + monitoring + APM interoperability).",
                        "Memory, State and Knowledge: Stores golden signal patterns, past incident telemetry, and RCA links (Adaptive learning → prioritizes most relevant metrics).",
                        "Reasoning, Decisioning Tier: Determines which telemetry signals are relevant to the incident context (Explainability, policy enforcement overlays).",
                        "Execution, Effectors and Handlers: Links telemetry to ITSM tickets, updates fields, and logs evidence (Safe execution, escalation handling)."
                    ]
                },
                {
                    title: "Security & Compliance",
                    content: [
                        "IAM Guardrails: Role-based access to observability dashboards and linked data.",
                        "Data Handling: Telemetry data encrypted in transit/at rest; sensitive traces masked.",
                        "Audit & Traceability: Evidence of data linkage and CI impact logging stored immutably.",
                        "Certifications Supported: HIPAA, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR."
                    ]
                }
            ]
        }
    ],
    agenticModel: {
        trigger: "Incident creation, SLA breach prediction, or APM/RUM anomaly detection.",
        workingPlane: "Orchestrates APM/RUM ingestion, telemetry correlation, and ITSM ticket updates to embed context.",
        memory: "Learns patterns of incidents vs telemetry anomalies, remembering which golden signal thresholds historically triggered valid incidents.",
        reasoning: "Correlates APM/RUM data with open incidents, determines relevance, and injects the right telemetry snapshot into the ITSM record.",
        execution: "Links real-time APM dashboards and RUM traces to active tickets, attaches golden signal metrics, and updates ITSM fields for impact, severity, and CI/service correlation."
    },
    kpis: [
        { label: "MTTR Reduction", value: "25", unit: "%", target: "25–30% faster MTTR", color: "#10b981" },
        { label: "SLA Compliance Improvement", value: "15", unit: "%", target: "15–20% improvement", color: "#2563eb" },
        { label: "Enrichment Latency SLA", value: "60", unit: "secs", target: "<60 seconds", color: "#f59e0b" },
        { label: "Telemetry Accuracy Match", value: "90", unit: "%", target: ">90% correct match", color: "#ef4444" }
    ]
},
'siem-to-itsm-bridge-agent': {
    title: "SIEM to ITSM Bridge Agent",
    description: "Evaluates SIEM detections against actionability, severity, and automation playbooks, generating tickets only for valid, high-confidence alerts that require IT or incident response action.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
        {
            title: "Business Value & Outcomes",
            points: [
                "60–70% reduction in false-positive tickets from SIEM feeds.",
                "20–25% faster Mean Time to Respond (MTTR) to validated detections.",
                "15–20% improvement in SOC-to-IT handoff efficiency.",
                "Reduced analyst fatigue and higher-quality security incident workflows.",
                "Improved compliance and audit outcomes with evidence-based playbook execution."
            ]
        }
    ],
    sections: [
        {
            title: "1. Business Context",
            subSections: [
                {
                    title: "Problem Statement",
                    content: [
                        "70–80% of SIEM detections are low-value or false positives.",
                        "Raw SIEM-to-ITSM integrations overwhelm service desks and analysts with noise.",
                        "Lack of playbook enforcement results in inconsistent ticket quality and response.",
                        "Analysts waste time triaging irrelevant alerts instead of mitigating real threats.",
                        "Security-to-IT coordination is inconsistent, delaying containment and remediation."
                    ]
                },
                {
                    title: "Business Outcomes (Benefits)",
                    content: [
                        "Converts noisy alert floods into actionable ITSM workflows.",
                        "Enforces alignment to standardized playbooks and severity scoring.",
                        "Generates audit-ready evidence of suppression and triage logic.",
                        "Memory-driven: learns which detections yield real incidents over time to improve filtering precision.",
                        "Empirical basis: Enterprises that adopt contextual SIEM-to-ITSM correlation reduce noise by ~65% and improve incident response efficiency by 25–30%."
                    ]
                },
                {
                    title: "Use Case Alignment",
                    content: [
                        "Healthcare: Only creates tickets for verified EHR data exfiltration or access violations.",
                        "Finance: Opens incidents for confirmed SOX/PCI violations and risk-scored anomalies.",
                        "ITSM: Ensures tickets reflect validated, context-rich detections, not raw alerts.",
                        "Cybersecurity: Integrates SIEM detections with ITSM playbooks for SOC-to-IT orchestration."
                    ]
                }
            ]
        },
        {
            title: "2. Technical Functionality",
            subSections: [
                {
                    title: "Scope of Capabilities",
                    content: [
                        "Monitors SIEM detections and filters by severity, confidence, and playbook availability.",
                        "Suppresses non-actionable or redundant alerts.",
                        "Creates ITSM incidents for actionable detections with relevant context and evidence.",
                        "Links tickets to automation or containment playbooks.",
                        "Logs suppression and correlation logic for audit and tuning."
                    ]
                },
                {
                    title: "Integrations",
                    content: [
                        "SIEM: Splunk, Microsoft Sentinel, QRadar, Elastic, Exabeam, Sumo Logic (Inbound).",
                        "SOAR: Cortex XSOAR, Tines, Splunk SOAR, ServiceNow Security Ops (Bidirectional).",
                        "ITSM: ServiceNow, Jira Service Management, BMC, Remedy (Bidirectional).",
                        "Threat Intel: MISP, Recorded Future, CrowdStrike Falcon Intelligence (Inbound).",
                        "GRC: Archer, OneTrust, ServiceNow GRC for compliance tracking (Bidirectional)."
                    ]
                }
            ]
        },
        {
            title: "3. Operational Model & Security",
            subSections: [
                {
                    title: "Agentic Tiers (RunOps Framework)",
                    content: [
                        "Trigger, Invocation Events: SIEM detection generated, confidence threshold met, or playbook correlation found (SLA-backed <30s validated ticket creation).",
                        "Working Plane, Orchestration Layer: Orchestrates SIEM → ITSM correlation, filtering, and playbook linkage (SIEM + SOAR + ITSM interoperability).",
                        "Memory, State and Knowledge: Stores detection histories, false-positive patterns, and playbook usage stats (Improves detection filtering precision over time).",
                        "Reasoning, Decisioning Tier: Evaluates actionability, confidence, and service impact before ticketing (Explainability, compliance overlays, SME overrides).",
                        "Execution, Effectors and Handlers: Creates actionable ITSM incidents, attaches playbooks, logs suppression decisions (Safe execution, governance escalation)."
                    ]
                },
                {
                    title: "Security & Compliance",
                    content: [
                        "IAM Guardrails: Role-based control over ticket creation and suppression logic.",
                        "Data Handling: Detection metadata encrypted in transit and at rest.",
                        "Audit & Traceability: Immutable log of suppression, escalation, and playbook execution.",
                        "Certifications Supported: HIPAA, SOX, PCI-DSS, HITRUST, SOC2, ISO27001, GDPR."
                    ]
                }
            ]
        }
    ],
    agenticModel: {
        trigger: "SIEM detection generated, confidence threshold met, or playbook correlation found.",
        workingPlane: "Orchestrates SIEM → ITSM correlation, filtering, and playbook linkage for efficient incident handoff.",
        memory: "Retains historical detection quality, playbook mappings, and past false-positive patterns to refine filtering logic.",
        reasoning: "Evaluates incoming SIEM alerts against playbooks, severity, and contextual data (threat intel, CI impact) to determine ticket actionability.",
        execution: "Creates ITSM incidents for actionable detections, prepopulates context fields, attaches the associated playbook, and logs all suppression or escalation decisions for audit."
    },
    kpis: [
        { label: "False-Positive Reduction", value: "60", unit: "%", target: "60–70% reduction", color: "#10b981" },
        { label: "Actionable MTTR Improvement", value: "20", unit: "%", target: "20–25% faster MTTR", color: "#2563eb" },
        { label: "Ticket Creation Latency SLA", value: "30", unit: "secs", target: "<30 seconds", color: "#f59e0b" },
        { label: "Actionability Accuracy", value: "90", unit: "%", target: ">90% classification accuracy", color: "#ef4444" }
    ]
},
'xla-monitor-agent': {
    title: "XLA Monitor Agent",
    description: "Continuously collects and correlates experience signals — including task completion success, perceived effort, end-user satisfaction, and device health — to quantify true digital experience.",
    // No video provided; reuse a generic workflow image if desired in future
    details: [
        {
            title: "Business Value & Outcomes",
            points: [
                "25–30% improvement in user satisfaction and digital experience scores.",
                "20% reduction in “hidden downtime” and shadow IT behavior.",
                "15–20% faster identification of friction points (apps, devices, workflows).",
                "Stronger XLA governance with quantifiable, actionable metrics.",
                "Proven link between digital experience and business performance (productivity, retention)."
            ]
        }
    ],
    sections: [
        {
            title: "1. Business Context",
            subSections: [
                {
                    title: "Problem Statement",
                    content: [
                        "SLAs show systems are “green,” but users report poor experiences.",
                        "No unified measurement of human effort, satisfaction, or real productivity impact.",
                        "ITSM dashboards lack visibility into device and app health correlations with user sentiment.",
                        "Lack of actionable experience metrics makes XLAs difficult to govern or prove.",
                        "Employee frustration and downtime quietly erode engagement and operational efficiency."
                    ]
                },
                {
                    title: "Business Outcomes (Benefits)",
                    content: [
                        "Combines user sentiment and technical data for holistic experience measurement.",
                        "Provides actionable XLAs with root-cause mapping (effort, satisfaction, health).",
                        "Enhances CSI and governance decisions with quantifiable experience data.",
                        "Memory-driven correlation improves insight accuracy over time by learning recurring patterns.",
                        "Empirical basis: Enterprises implementing XLA frameworks alongside SLA management report a 20–25% uplift in employee productivity and a 15% reduction in IT friction events."
                    ]
                },
                {
                    title: "Use Case Alignment",
                    content: [
                        "Healthcare: Monitors clinician experience with EHR systems, linking device latency and satisfaction.",
                        "Finance: Tracks task success and perceived effort for ERP or trading workflows.",
                        "ITSM: Embeds XLA metrics into ticket closure, request fulfillment, and CSI dashboards.",
                        "Digital Workplace: Correlates experience data with M365, Zoom, Citrix, and device telemetry."
                    ]
                }
            ]
        },
        {
            title: "2. Technical Functionality",
            subSections: [
                {
                    title: "Scope of Capabilities",
                    content: [
                        "Tracks task success, perceived effort, satisfaction, and device health metrics.",
                        "Correlates telemetry (CPU, latency, network) with experience feedback.",
                        "Embeds XLA metrics in ITSM incidents and request workflows.",
                        "Generates experience trend dashboards and executive XLA scorecards.",
                        "Alerts when XLA thresholds (e.g., satisfaction <80%) are breached."
                    ]
                },
                {
                    title: "Integrations",
                    content: [
                        "ITSM: ServiceNow, Jira Service Management, BMC, Remedy (Bidirectional).",
                        "DEX/RUM: Nexthink, Aternity, ControlUp, Catchpoint, ThousandEyes (Inbound).",
                        "Collaboration: Teams, Slack for in-workflow satisfaction prompts (Bidirectional).",
                        "Device Management: Intune, MEM, JAMF, Workspace ONE (Inbound).",
                        "Analytics: Power BI, Tableau for XLA dashboards (Outbound)."
                    ]
                }
            ]
        },
        {
            title: "3. Operational Model & Security",
            subSections: [
                {
                    title: "Agentic Tiers (RunOps Framework)",
                    content: [
                        "Trigger, Invocation Events: Periodic experience telemetry ingestion, user survey event, or XLA breach (SLA-backed <15m experience refresh cycle).",
                        "Working Plane, Orchestration Layer: Orchestrates telemetry, satisfaction data, and ITSM linkage (ITSM + DEX + analytics interoperability).",
                        "Memory, State and Knowledge: Stores historical user experience baselines, device health, and sentiment patterns (Adaptive learning improves XLA accuracy + relevance).",
                        "Reasoning, Decisioning Tier: Correlates experience signals to determine root cause and breach likelihood (Explainability, governance overlays, escalation controls).",
                        "Execution, Effectors and Handlers: Updates ITSM with experience metrics, triggers RCA, or escalation (Safe execution, rollback, and compliance logging)."
                    ]
                },
                {
                    title: "Security & Compliance",
                    content: [
                        "IAM Guardrails: Role-based data access ensuring user-level privacy protection.",
                        "Data Handling: Sentiment and telemetry data encrypted in transit and at rest; anonymization applied.",
                        "Audit & Traceability: Logged XLA thresholds, measurements, and escalations for compliance.",
                        "Certifications Supported: HIPAA, HITRUST, SOC2, ISO27001, GDPR."
                    ]
                }
            ]
        }
    ],
    agenticModel: {
        trigger: "Periodic experience telemetry ingestion, user survey event, or XLA breach.",
        workingPlane: "Orchestrates telemetry, satisfaction data, and ITSM linkage.",
        memory: "Learns recurring patterns between technical metrics and user sentiment or task failures.",
        reasoning: "Correlates telemetry, survey, and service data to determine which factors most affect user experience.",
        execution: "Generates XLA dashboards, satisfaction trends, and actionable insights tied to device health, app performance, and perceived effort, and updates ITSM."
    },
    kpis: [
        { label: "User Satisfaction Improvement", value: "25", unit: "%", target: "25–30% improvement", color: "#10b981" },
        { label: "Hidden Downtime Reduction", value: "20", unit: "%", target: "20% reduction", color: "#2563eb" },
        { label: "Friction Point ID Speed", value: "15", unit: "%", target: "15–20% faster ID", color: "#f59e0b" },
        { label: "Telemetry Refresh Cycle", value: "15", unit: "mins", target: "<15 minutes", color: "#ef4444" }
    ]
},
'voice-of-customer-summarizer-agent': {
    title: "Voice of Customer Summarizer Agent",
    description: "Consolidates quantitative and qualitative experience data from ITSM, XLA, and DEX systems into a weekly narrative summary that highlights sentiment shifts, recurring themes, improvement wins, and emerging risks for business and IT leadership.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "80–90% reduction in time spent creating executive experience summaries.",
          "20–25% faster governance and CSI decision-making cycles.",
          "Improved alignment between IT, business, and employee experience outcomes.",
          "Strengthened accountability and trust in ITSM performance reporting.",
          "Clear visibility of “what users feel” vs. “what systems report”."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Feedback data is siloed across multiple tools and dashboards.",
              "Executives receive fragmented reports without context or storytelling.",
              "Sentiment and friction trends are buried in raw data and analytics platforms.",
              "Service desk and experience teams spend hours manually compiling reports.",
              "CSI and governance discussions lack a unified “voice of the customer” perspective."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "80–90% reduction in time spent creating executive experience summaries.",
              "20–25% faster governance and CSI decision-making cycles.",
              "Improved alignment between IT, business, and employee experience outcomes.",
              "Strengthened accountability and trust in ITSM performance reporting.",
              "Clear visibility of “what users feel” vs. “what systems report”."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Summarizes clinician and patient IT experience feedback into compliance-ready weekly digests.",
              "Finance: Consolidates ERP and service feedback into CX reports for IT and business governance.",
              "Airline Ops: Highlights frontline digital experience trends (kiosk, gate, mobile app) for leadership review.",
              "ITSM: Converts raw survey and satisfaction data into human-readable summaries for CAB and CSI boards.",
              "Digital Workplace: Narrates week-over-week shifts in employee satisfaction, device health, and service quality."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Memory: Retains historical sentiment trends, top feedback themes, and prior weekly summaries.",
              "Reasoning: Correlates survey, ticket, and telemetry data to identify key insights, themes, and tone changes.",
              "Execution: Automatically generates an executive-ready weekly summary, blending analytics, narrative insights, and visuals.",
              "Advantage: Produces executive-ready narratives, learns leadership tone preferences, and aligns directly to CSI/Governance cadences."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Aggregates sentiment, satisfaction, and feedback from surveys, chat, and ITSM.",
              "Identifies top improvement themes and correlated operational metrics.",
              "Drafts weekly executive summary with data visualizations and recommendations.",
              "Customizes narrative style and length based on audience (CIO, COO, HR, etc.).",
              "Logs every report generated for audit and historical comparison."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM — ServiceNow, Jira Service Management, BMC, Remedy (Bidirectional).",
              "XLA/Feedback — Qualtrics, Medallia, ServiceNow Surveys (Inbound).",
              "DEX — Nexthink, Aternity, ControlUp (Inbound).",
              "Analytics — Power BI, Tableau, Grafana for visual data embedding (Inbound).",
              "Collaboration — Teams, Slack, Outlook for report distribution (Outbound)."
            ]
          },
          {
            title: "Customization & Security",
            content: [
              "Executive tone profiles (data-centric, user-story, visual-first).",
              "Configurable reporting cadence (weekly, monthly, on-demand).",
              "Custom filters (BU, service category, geography, incident type).",
              "IAM Guardrails: Role-based visibility for reports (execs, managers, CSI).",
              "Audit & Traceability: Each summary archived immutably with correlation IDs."
            ]
          }
        ]
      },
      {
        title: "3. Operational Model & KPIs",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Reporting Time Reduction: 80–90% reduction in time spent creating executive experience summaries.",
              "Governance Cycle Speed: 20–25% faster governance and CSI decision-making cycles.",
              "Summary Generation Speed < 2 hrs: Weekly summary generated <2 hours after data ingestion.",
              "Theme Accuracy > 90% alignment between detected themes and actual feedback drivers.",
              "Executive Adoption Rate: % of executives regularly engaging with the report.",
              "Improvement Actions Taken %: Actions taken based on report recommendations."
            ]
          },
          {
            title: "Agentic Tiers (RunOps Framework)",
            content: [
              "Tier 1 — Trigger & Invocation: Weekly schedule, CSI cycle, or XLA report completion. (50%).",
              "Tier 2 — Working Plane: Orchestrates data aggregation, narrative generation, and distribution. (75%).",
              "Tier 3 — Memory & Reasoning: Determines which insights matter most and prioritizes narrative focus areas. (90%).",
              "Tier 4 — Execution & Governance: Composes summaries, generates visuals, and distributes reports. (100%)."
            ]
          },
          {
            title: "Financial & Value Proof",
            content: [
              "Pricing Model: Per summary generated, per business unit, or outcome-based (% CSI cycle efficiency, % exec adoption).",
              "ROI Proof: 80–90% reduction in reporting time saves hundreds of analyst hours annually.",
              "Scalability: Supports global reporting, multi-language summaries, and multi-region feedback inputs."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Weekly schedule, CSI cycle, or XLA report completion.",
      workingPlane: "Orchestrates data aggregation, narrative generation, and distribution.",
      memory: "Stores sentiment trends, theme history, and narrative tone preferences for adaptive storytelling.",
      reasoning: "Correlates survey, ticket, and telemetry data to identify key insights, themes, and tone changes, prioritizing the narrative focus for executive relevance.",
      execution: "Composes an executive-ready summary, blends analytics and narrative insights, and distributes reports via email, Teams, or dashboard."
    },
    kpis: [
      { label: "Reporting Time Reduction", value: "80-90", unit: "%", target: "≥ 80%", color: "#10b981" },
      { label: "CSI Cycle Acceleration", value: "20-25", unit: "%", target: "≥ 20%", color: "#10b981" },
      { label: "Summary Generation Speed", value: "2", unit: "hrs", target: "< 2 hrs", color: "#10b981" },
      { label: "Theme Accuracy", value: "90", unit: "%", target: "> 90%", color: "#10b981" },
      { label: "Executive Adoption Rate", value: "80", unit: "%", target: "≥ 80%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "100", unit: "%", target: "100%", color: "#10b981" }
    ]
},
'feedback-miner-agent': {
    title: "Feedback Miner Agent",
    description: "Continuously processes unstructured user feedback (surveys, chat, tickets), tagging verbatims by sentiment and theme, correlating them to services, and suggesting concrete fixes, knowledge article updates, or automation candidates.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–30% faster feedback-to-action cycle time for CSI.",
          "15–20% higher survey response utilization (verbatims become actionable).",
          "10–15% improvement in KB accuracy and self-service deflection.",
          "Continuous improvement loop powered by real user experience data.",
          "Improved governance and trust in XLA programs."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "80–90% of survey and chat feedback is unstructured text, hard to quantify or act upon.",
              "Manual feedback analysis is inconsistent and resource-heavy.",
              "KB and automation improvements are rarely derived from verbatim user input.",
              "CSI and XLA programs lack evidence-based recommendations.",
              "Repeated negative experiences go unnoticed until satisfaction scores drop."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "25–30% faster feedback-to-action cycle time for CSI.",
              "15–20% higher survey response utilization (verbatims become actionable).",
              "10–15% improvement in KB accuracy and self-service deflection.",
              "Continuous improvement loop powered by real user experience data.",
              "Improved governance and trust in XLA programs."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Detects recurring user complaints about login latency or device reliability and proposes KB/automation fixes.",
              "Finance: Tags ERP user feedback by theme (reporting speed, access rights) for CSI board action.",
              "Airline Ops: Analyzes staff verbatims about kiosk downtime and triggers KB content refresh.",
              "ITSM: Feeds structured improvement data directly into CSI dashboards.",
              "Digital Workplace: Highlights feedback on collaboration tools, mapping friction to automation or self-heal opportunities."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Memory: Retains historical feedback, resolved themes, and prior KB or service improvements.",
              "Reasoning: Uses NLP and sentiment analysis to detect recurring friction points, classify feedback by theme, and correlate with service or CI.",
              "Execution: Tags feedback in ITSM/analytics systems, generates improvement suggestions, and routes proposed KB updates or automation ideas to the proper governance team.",
              "Advantage: Turns qualitative feedback into quantitative, actionable insight and links themes directly to services, CIs, and KBs."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Analyzes verbatim feedback from surveys, chat, tickets, and social/internal channels.",
              "Tags feedback by sentiment, intent, and service theme.",
              "Correlates feedback to CIs, services, and request types.",
              "Generates recommendations for KB updates, automation, or service improvements.",
              "Feeds structured insights into CSI and XLA dashboards."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM — ServiceNow, Jira Service Management, BMC, Remedy (Bidirectional).",
              "DEX/XLA — Qualtrics, Medallia, Nexthink, Aternity (Inbound).",
              "Knowledge — Confluence, SharePoint, ServiceNow KB (Bidirectional).",
              "Collaboration — Teams, Slack for feedback alerts and routing (Bidirectional).",
              "Analytics — Power BI, Tableau for sentiment + theme dashboards (Outbound)."
            ]
          },
          {
            title: "Customization & Security",
            content: [
              "Custom taxonomies for theme tagging (UX, access, comms, automation, etc.).",
              "Configurable sentiment thresholds and escalation triggers.",
              "Role-based dashboards for CSI leads, service owners, and KB managers.",
              "IAM Guardrails: Feedback access role-based to preserve confidentiality.",
              "Data Handling: Verbatims encrypted at rest/in transit; anonymization for sensitive content."
            ]
          }
        ]
      },
      {
        title: "3. Operational Model & KPIs",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Feedback-to-Action Cycle Time: 25–30% faster feedback-to-action cycle time for CSI.",
              "Response Utilization: 15–20% higher survey response utilization.",
              "KB/Self-Service Improvement: 10–15% improvement in KB accuracy and self-service deflection.",
              "Processing Speed < 5 min: Feedback processing and tagging <5 minutes from ingestion.",
              "Classification Accuracy > 90% theme classification precision after training.",
              "Automation Adoption Rate ≥ 85% of actionable recommendations adopted."
            ]
          },
          {
            title: "Agentic Tiers (RunOps Framework)",
            content: [
              "Tier 1 — Trigger & Invocation: Survey submission, chat closure, ticket resolution, or XLA breach. (50%).",
              "Tier 2 — Working Plane: Orchestrates feedback ingestion, sentiment analysis, and tagging. (75%).",
              "Tier 3 — Memory & Reasoning: Classifies feedback by theme, correlates to services, and recommends improvements. (90%).",
              "Tier 4 — Execution & Governance: Tags verbatims, generates recommendations, and routes actions to KB/CSI teams. (100%)."
            ]
          },
          {
            title: "Financial & Value Proof",
            content: [
              "Pricing Model: Per feedback dataset analyzed, per service, or outcome-based (% actionable insights adopted).",
              "ROI Proof: 25–30% faster CSI improvement cycles; lower content management cost via automated KB curation.",
              "Scalability: Analyzes millions of feedback records per month across enterprises."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Survey submission, chat closure, ticket resolution, or XLA breach.",
      workingPlane: "Orchestrates feedback ingestion, sentiment analysis, and tagging.",
      memory: "Stores prior feedback, resolved themes, and CSI actions to learn which improvements yield the biggest satisfaction impact.",
      reasoning: "Uses NLP/sentiment analysis to detect recurring friction points, classifies feedback by theme, and correlates to services/CIs to suggest evidence-based improvements.",
      execution: "Tags verbatims in ITSM/analytics systems, generates proposed KB updates or automation ideas, and routes action items to the proper governance teams (KB/CSI)."
    },
    kpis: [
      { label: "Feedback-to-Action Cycle Time", value: "25-30", unit: "%", target: "≥ 25%", color: "#10b981" },
      { label: "KB/Self-Service Improvement", value: "10-15", unit: "%", target: "≥ 10%", color: "#10b981" },
      { label: "Processing Speed", value: "5", unit: "min", target: "< 5 min", color: "#10b981" },
      { label: "Classification Accuracy", value: "90", unit: "%", target: "> 90%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "85", unit: "%", target: "≥ 85%", color: "#10b981" },
      { label: "Response Utilization", value: "15-20", unit: "%", target: "≥ 15%", color: "#10b981" }
    ]
},

'survey-designer-agent': {
    title: "Survey Designer Agent",
    description: "Automatically builds, deploys, and optimizes pulse and transactional surveys with built-in bias detection, ensuring feedback is meaningful, statistically reliable, and directly tied to ITSM outcomes and service value.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "25–30% higher survey response rates due to personalization and timing optimization.",
          "15–20% improvement in accuracy and reliability of satisfaction metrics.",
          "20% faster deployment of new feedback loops for services and CSI initiatives.",
          "Stronger governance through unbiased, statistically sound XLA data.",
          "Continuous user sentiment tracking linked to operational metrics."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "Surveys are often long, generic, and biased toward positive responses.",
              "Low response rates and poor question design distort satisfaction data.",
              "Surveys are not contextually triggered (wrong time, wrong audience).",
              "XLA and CSI metrics lack trusted user input to validate experience improvement.",
              "Manual survey creation is slow and inconsistent across teams."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "25–30% higher survey response rates due to personalization and timing optimization.",
              "15–20% improvement in accuracy and reliability of satisfaction metrics.",
              "20% faster deployment of new feedback loops for services and CSI initiatives.",
              "Stronger governance through unbiased, statistically sound XLA data.",
              "Continuous user sentiment tracking linked to operational metrics."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Builds short, compliance-friendly post-resolution surveys for clinicians.",
              "Finance: Deploys bias-controlled pulse surveys to gauge ERP and reporting user satisfaction.",
              "Airline Ops: Measures real-time staff satisfaction with gate and mobile operations systems.",
              "ITSM: Gathers contextual satisfaction data post-incident, request, or change.",
              "Digital Workplace: Measures end-user sentiment after device or collaboration tool interventions."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Memory: Stores question performance, response bias trends, and audience behavior patterns.",
              "Reasoning: Evaluates service context and applies bias controls when designing or triggering surveys.",
              "Execution: Generates and launches surveys, auto-tunes question flow based on user engagement, and writes structured insights into ITSM and XLA dashboards.",
              "Advantage: Context-aware triggering, reasoning-based bias control, and continuous calibration for reliable XLA data."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Builds pulse (time-based) and transactional (event-based) surveys.",
              "Applies bias controls to question wording and scale design.",
              "Dynamically adapts survey length based on user engagement rates.",
              "Integrates feedback into ITSM incidents, XLAs, and CSI dashboards.",
              "Provides analytics for satisfaction, perceived effort, and sentiment trends."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM — ServiceNow, Jira Service Management, BMC, Remedy (Bidirectional).",
              "DEX/XLA — Nexthink, Aternity, Qualtrics, Medallia (Bidirectional).",
              "Collaboration — Teams, Slack, Outlook, Gmail for in-line survey delivery (Outbound).",
              "Analytics — Power BI, Tableau for XLA reporting (Outbound).",
              "HRIS — Workday, SuccessFactors for employee engagement alignment (Inbound)."
            ]
          },
          {
            title: "Customization & Security",
            content: [
              "Configurable templates (incident, service request, device health, automation success).",
              "Adjustable survey triggers (on ticket close, post-resolution, or periodic pulse).",
              "Custom survey bias models (based on industry or geography).",
              "IAM Guardrails: Role-based access for survey design, analysis, and deployment.",
              "Audit & Traceability: All surveys, triggers, and results logged immutably."
            ]
          }
        ]
      },
      {
        title: "3. Operational Model & KPIs",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Survey Response Rate: 25–30% higher survey response rates.",
              "Satisfaction Metric Accuracy: 15–20% improvement in accuracy and reliability of satisfaction metrics.",
              "Survey Deployment Speed < 20% faster deployment of new feedback loops.",
              "SLA Survey Trigger Time < 1 min: Surveys triggered within <1 minute of service or incident closure.",
              "Bias Compliance > 95% compliance with bias detection and validation rules.",
              "Automation Adoption Rate ≥ 85% of survey deployments executed autonomously."
            ]
          },
          {
            title: "Agentic Tiers (RunOps Framework)",
            content: [
              "Tier 1 — Trigger & Invocation: Incident closure, request completion, device intervention, or CSI cycle trigger. (50%).",
              "Tier 2 — Working Plane: Orchestrates survey creation, delivery, data capture, and analysis. (75%).",
              "Tier 3 — Memory & Reasoning: Evaluates bias likelihood, determines question selection, and trigger timing. (90%).",
              "Tier 4 — Execution & Governance: Deploys surveys, collects responses, and writes results into ITSM/XLA dashboards. (100%)."
            ]
          },
          {
            title: "Financial & Value Proof",
            content: [
              "Pricing Model: Per survey deployed, per user, or outcome-based (% XLA correlation, % CSI improvement).",
              "ROI Proof: 25–30% higher response rates improve XLA accuracy; reduced labor from manual survey creation.",
              "Scalability: Supports thousands of surveys across services, geographies, and user roles."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "Incident closure, request completion, device intervention, or CSI cycle trigger.",
      workingPlane: "Orchestrates survey creation, delivery, data capture, and analysis.",
      memory: "Stores question performance, response patterns, and bias detection results to adapt and improve survey accuracy over time.",
      reasoning: "Evaluates service context, bias likelihood, and trigger timing to determine optimal question flow and delivery channel.",
      execution: "Generates and deploys surveys, collects responses, and writes structured, bias-validated results into ITSM and XLA dashboards."
    },
    kpis: [
      { label: "Survey Response Rate", value: "25-30", unit: "%", target: "≥ 25%", color: "#10b981" },
      { label: "Satisfaction Metric Accuracy", value: "15-20", unit: "%", target: "≥ 15%", color: "#10b981" },
      { label: "Survey Deployment Speed", value: "20", unit: "%", target: "≥ 20%", color: "#10b981" },
      { label: "Bias Compliance", value: "95", unit: "%", target: "> 95%", color: "#10b981" },
      { label: "SLA Survey Trigger Time", value: "1", unit: "min", target: "< 1 min", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "85", unit: "%", target: "≥ 85%", color: "#10b981" }
    ]
},

'kpi-tracker-agent': {
    title: "KPI Tracker Agent",
    description: "Provides real-time, unified visibility into key performance indicators (MTTR, FCR, Deflection, Automation Coverage, Backlog Aging) across ITSM, automation, and experience platforms, ensuring decisions are made with current, trusted data.",
    details: [
      {
        title: "Business Value & Outcomes",
        points: [
          "100% real-time visibility into ITSM and automation KPIs.",
          "15–20% faster operational decisions and CSI improvements.",
          "10–15% productivity gain by eliminating manual data aggregation.",
          "Continuous, transparent governance dashboards for leadership.",
          "Improved trust in ITSM reporting and performance accountability."
        ]
      }
    ],
    sections: [
      {
        title: "1. Business Context",
        subSections: [
          {
            title: "Problem Statement",
            content: [
              "KPI reports are generated manually, lagging days or weeks behind real-time operations.",
              "Teams work from inconsistent definitions of MTTR, FCR, or deflection.",
              "Automation coverage and backlog health are opaque to leadership.",
              "CSI and governance reviews rely on outdated spreadsheets.",
              "Without live metrics, leaders can’t prioritize automation or workforce optimization effectively."
            ]
          },
          {
            title: "Business Outcomes (Benefits)",
            content: [
              "100% real-time visibility into ITSM and automation KPIs.",
              "15–20% faster operational decisions and CSI improvements.",
              "10–15% productivity gain by eliminating manual data aggregation.",
              "Continuous, transparent governance dashboards for leadership.",
              "Improved trust in ITSM reporting and performance accountability."
            ]
          },
          {
            title: "Use Case Alignment",
            content: [
              "Healthcare: Tracks real-time incident and service request turnaround for clinical operations.",
              "Finance: Monitors backlog aging, SLA breaches, and automation savings across ERP/CRM systems.",
              "Airline Ops: Provides NOC/Service Desk metrics for uptime, response speed, and self-service adoption.",
              "ITSM: Feeds CSI dashboards with MTTR, FCR, automation coverage, and backlog metrics.",
              "Digital Experience: Correlates service KPIs with XLA satisfaction trends for holistic experience insight."
            ]
          },
          {
            title: "Why Agentic Agents Are Uniquely Qualified",
            content: [
              "Memory: Retains historical KPI baselines and trends for variance and forecast comparison.",
              "Reasoning: Correlates ITSM, automation, and experience data to calculate live KPIs, detecting anomalies and highlighting improvement opportunities.",
              "Execution: Updates dashboards and reports continuously, triggers alerts on threshold breaches, and feeds metrics into CSI and governance workflows.",
              "Advantage: Continuous, real-time KPI visibility with memory-driven forecasting, directly integrated into ITSM/CSI workflows."
            ]
          }
        ]
      },
      {
        title: "2. Technical Functionality",
        subSections: [
          {
            title: "Scope of Capabilities",
            content: [
              "Tracks MTTR, FCR, deflection, automation coverage, backlog aging, SLA adherence.",
              "Aggregates data from ITSM, AIOps, automation, and DEX systems.",
              "Auto-refreshes dashboards and reports in real time.",
              "Alerts leaders on KPI thresholds or variance from baselines.",
              "Logs KPI data for CSI analysis and audit."
            ]
          },
          {
            title: "Integrations",
            content: [
              "ITSM — ServiceNow, Jira Service Management, BMC, Remedy (Bidirectional).",
              "Automation — n8n, Ansible, ServiceNow Flow, UiPath (Bidirectional).",
              "DEX/XLA — Nexthink, Aternity, ControlUp (Inbound).",
              "Collaboration — Teams, Slack for KPI alerts and updates (Outbound).",
              "Analytics — Power BI, Tableau, Grafana for visualization (Outbound)."
            ]
          },
          {
            title: "Customization & Security",
            content: [
              "Configurable KPI definitions and calculation logic.",
              "Custom thresholds for SLA breach or backlog alerts.",
              "Role-based dashboards (executive, service owner, engineer).",
              "IAM Guardrails: Dashboard access controlled by RBAC and least-privilege policies.",
              "Audit & Traceability: KPI definitions, thresholds, and variance logs retained immutably."
            ]
          }
        ]
      },
      {
        title: "3. Operational Model & KPIs",
        subSections: [
          {
            title: "Key Performance Indicators (KPI)",
            content: [
              "Real-Time Visibility: 100% real-time visibility into ITSM and automation KPIs.",
              "Decision Speed Improvement: 15–20% faster operational decisions.",
              "Data Aggregation Savings: 10–15% productivity gain by eliminating manual data aggregation.",
              "KPI Dashboard Refresh < 5 min: KPI dashboards refreshed every <5 minutes.",
              "Data Reconciliation Accuracy > 98% reconciliation precision with source systems.",
              "Automation Adoption Rate ≥ 85% of KPI tracking and reporting executed autonomously."
            ]
          },
          {
            title: "Agentic Tiers (RunOps Framework)",
            content: [
              "Tier 1 — Trigger & Invocation: KPI data ingestion, SLA breach event, automation run completion, backlog update. (50%).",
              "Tier 2 — Working Plane: Orchestrates KPI calculation, data validation, and dashboard publishing. (75%).",
              "Tier 3 — Memory & Reasoning: Determines KPI deviations, SLA breach likelihood, and CSI focus areas. (90%).",
              "Tier 4 — Execution & Governance: Updates dashboards, triggers alerts, logs breaches, and routes CSI recommendations. (100%)."
            ]
          },
          {
            title: "Financial & Value Proof",
            content: [
              "Pricing Model: Per KPI tracked, per service line, or outcome-based (% SLA compliance improvement, % automation ROI).",
              "ROI Proof: 15–20% faster decision-making; 10–15% labor savings from removing manual reporting.",
              "Scalability: Supports global ITSM operations across millions of tickets."
            ]
          }
        ]
      }
    ],
    agenticModel: {
      trigger: "KPI data ingestion, SLA breach event, automation run completion, or backlog update.",
      workingPlane: "Orchestrates KPI calculation, data validation, and dashboard publishing.",
      memory: "Stores historical KPI baselines, performance trends, and variance data to improve forecasting and anomaly detection.",
      reasoning: "Correlates ITSM, automation, and experience data to calculate live KPIs, determines KPI deviations, SLA breach likelihood, and CSI focus areas.",
      execution: "Updates dashboards, triggers alerts on threshold breaches via collaboration channels, logs breaches, and routes CSI recommendations."
    },
    kpis: [
      { label: "Real-Time Visibility", value: "100", unit: "%", target: "100%", color: "#10b981" },
      { label: "Decision Speed Improvement", value: "15-20", unit: "%", target: "≥ 15%", color: "#10b981" },
      { label: "KPI Dashboard Refresh", value: "5", unit: "min", target: "< 5 min", color: "#10b981" },
      { label: "Data Reconciliation Accuracy", value: "98", unit: "%", target: "> 98%", color: "#10b981" },
      { label: "Data Aggregation Savings", value: "10-15", unit: "%", target: "≥ 10%", color: "#10b981" },
      { label: "Automation Adoption Rate", value: "85", unit: "%", target: "≥ 85%", color: "#10b981" }
    ]
},
  'default': {
    title: "Agent Details",
    description: "Select an agent from the sidebar to see its detailed capabilities and configuration.",
    sections: [],
    agenticModel: {},
    kpis: []
  }
};
